import React, { useState, useCallback, useRef, useEffect, useMemo, useLayoutEffect } from 'react';
import { startProCheckout } from '../utils/billing';
import { format, parseISO, isValid } from 'date-fns';
import { useLocation, useNavigate } from 'react-router-dom';
import { SplitButton, Dropdown, Modal, Button } from 'react-bootstrap';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { apifetch } from '../services/interceptors';
import {
  Star, Heart, AlertTriangle, CheckCircle, Info, User, Settings, Menu, Search
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  RadialLinearScale
} from 'chart.js';
import { Bar, Line, Pie, Doughnut, Radar, PolarArea, Bubble, Scatter } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import axios from 'axios';
import { computeDerivedMetrics } from '../utils/derivedMetrics';
import { normalizeScanData } from '../utils/scanDataNormalizer';
import { useExportQueue } from '../contexts/ExportQueueContext';
import { getAvailableFields } from './byor/utils/widgetHelpers';
import { PAGINATED_WIDGETS } from './byor/utils/constants';
import { generateSampleScanData, shouldUseSampleData } from '../utils/sampleDataGenerator';
import { getDefaultVariables, resolveVariables, resolveStyleValue, getColorValue, isUsingGlobalVariable, getVariablesByCategory, extractVariableName } from '../utils/variableResolver';
import GlobalVariablesPanel from './GlobalVariablesPanel';
import PropertiesPanel from './byor/components/PropertiesPanel/PropertiesPanel';
import HeaderElement from './byor/elements/HeaderElement';
import FooterElement from './byor/elements/FooterElement';
import Portal from './common/Portal';
import { useUiVersion } from '../UiVersionContext';
import { useTheme } from '../ThemeContext';
import { TbPlus } from 'react-icons/tb';
import {
  BuilderHeaderV2,
  BuilderRailV2,
  CanvasBarV2,
  InspectorEmptyV2,
  BuilderStatusBarV2,
  LIBRARY_ITEMS
} from './byor/v2/BuilderChromeV2';
import './ReportBuilder.css';
import './GlobalVariables.css';
import {
  FindingsStatusWidget,
  SingleFindingStatusWidget,
  KeyFindingsSummaryWidget,
  OpenPortsWidget,
  extractOpenPortsData,
  FindingsGridWidget,
  OpenPortsFindingsSummaryWidget,
  DarkWebSummaryWidget,
  AccountsFindingsSummaryWidget,
  FirewallFindingsSummaryWidget,
  ServiceRiskSummaryWidget,
  OverallRiskScoreWidget,
  extractKeyFindingsSummary,
  matchesScanType,
  SCAN_TYPE_MAPPING
} from './widgets/FindingsWidgets';
import { InfrastructureScanGridWidget, KeyFindingsAdvancedWidget } from './widgets/KeyFindingsAdvancedWidgetHelper';
import { ManagedServiceScanGridWidget } from './widgets/ManagedServiceExperienceWidgetHelper';
import { SecurityScanGridWidget } from './widgets/SecurityScanGridWidgetHelper';
import { CyberSecurityGridWidget } from './widgets/CyberSecurityGridWidgetHelper';
import { CombinedScanGridWidget } from './widgets/CombinedScanGridWidget';
import { AdvancedScanGridWidget } from './widgets/AdvancedScanGridWidget';
import {
  NestedRectangleFindingsWidget,
  CompanyAtAGlanceWidget,
  CategoryScoreBarWidget,
  FinancialImpactWidget,
  CategoryDescriptionWidget
} from './widgets/AutoFindingsWidgets';
import { resolveCategoryStatusColor } from './widgets/dynamicColorResolver';
import { Section, Row, Column } from './ReportBuilderLayout';
import { calculateAllReportFindings } from './widgets/AllFindingsHelper';
import {
  UserPasswordStatusChart,
  DiskFreeVsUsedChart,
  FirewallStatusChart,
  ServiceStartModesChart,
  AdminPasswordStatusChart,
  OpenPortsChart,
  PasswordStrengthChart,
  HIBPBreachStatusChart,
  DeviceTypeChart,
  NetworkSensitivePortsChart,
  WindowsVersionChart,
  CommonSoftwareStatusChart,
  HIBPStatusChart,
  RDPStatusChart,
  BrowserStatusChart,
  SystemAgeDistributionChart,
  ADObjectTypesChart,
  ADObjectsPieChart,
  FSMORolesChart,
  ActiveInactiveUsersChart,
  PasswordExpiryChart,
  ActiveInactiveComputersChart,
  GroupTypesChart,
  OUMembershipChart,
  GPOTimelineChart,
  NetworkFirewallStatusChart
} from './widgets/ChartWidgets';
import {
  // Active Directory Tables
  ADAllUsersTableWidget,
  ADInactiveUsersTableWidget,
  ADAllComputersTableWidget,
  ADWorkstationAgeTableWidget,
  ADInactiveComputersTableWidget,
  ADGroupsRiskTableWidget,
  ADOUsRiskTableWidget,
  ADFSMORolesTableWidget,
  ADGPOAnalysisTableWidget,
  ADUsersAuditTableWidget,
  ADComputersAuditTableWidget,
  ADDevicesAuditTableWidget,
  ADGroupsAuditTableWidget,
  ADOUsAuditTableWidget,
  ADDomainControllersAuditTableWidget,
  ADGPOPoliciesAuditTableWidget,
  ADEventLogsAuditTableWidget,
  // Network Tables
  NetworkAllUsersPasswordTableWidget,
  NetworkNonExpiringPasswordsTableWidget,
  NetworkAdminDailyWorkTableWidget,
  NetworkDiskUsageTableWidget,
  NetworkBackupStatusTableWidget,
  NetworkDeviceAgeTableWidget,
  NetworkInstalledSoftwareTableWidget,
  NetworkOSVersionsTableWidget,
  NetworkUsersNoExpiringPasswordTableWidget,
  NetworkUsersExpiringPasswordTableWidget,
  NetworkAdminsNoExpiringPasswordTableWidget,
  NetworkAdminsExpiringPasswordTableWidget,
  NetworkAdminsDailyWorkTableWidget,
  NetworkNonDefaultServicesTableWidget,
  NetworkAllOpenPortsTableWidget,
  NetworkSensitiveOpenPortsTableWidget,
  extractSensitiveOpenPortsData,
  NetworkAntivirusStatusTableWidget,
  NetworkEncryptionStatusTableWidget,
  NetworkFirewallStatusTableWidget,
  NetworkDetailedServicesTableWidget,
  NetworkServicesRiskBreakdownTableWidget,
  NetworkHighPrivilegeServicesTableWidget,
  NetworkLogicalDisksTableWidget,
  // Localhost Tables
  LocalhostAllUsersPasswordTableWidget,
  LocalhostNonExpiringPasswordsTableWidget,
  LocalhostDiskUsageTableWidget,
  LocalhostFirewallStatusTableWidget,
  LocalhostOSVersionTableWidget,
  LocalhostSensitiveOpenPortsTableWidget,
  LocalhostServicesRiskBreakdownTableWidget,
  LocalhostHighPrivilegeServicesTableWidget,
  LocalhostInstalledSoftwareTableWidget,
  LocalhostNonDefaultServicesTableWidget,
  LocalhostOpenPortsTableWidget,
  LocalhostBrowserPasswordsTableWidget,
  LocalhostHardwareInformationTableWidget,
  LocalhostSystemInformationTableWidget,
  LocalhostInstalledApplicationsTableWidget,
  LocalhostEventLogsTableWidget,
  LocalhostOpenPortsAuditTableWidget,
  // MS365 Tables
  MS365InactiveUsersTableWidget,
  MS365AllUsersMFATableWidget,
  MS365UsersWithoutMFATableWidget,
  MS365PolicyConfigTableWidget,
  isMS365DataEmpty,
  MS365ScanProofTableWidget,
  MicrosoftPoliciesTableWidget,
  // HIBP/Dark Web Tables
  HIBPBreachedCredentialsTableWidget,
  HIBPExposureByPlatformTableWidget,
  // PII Tables
  PIIDetectedTableWidget,
  PIITypeSummaryTableWidget,
  // Software Inventory
  SoftwareInventoryTableWidget,
  // Local Administrators
  LocalAdministratorsTableWidget,
  // AD Admins Table
  ADAdminsTableWidget
} from './widgets/AllProofTablesWidgets';
import { TableDataMapper, getExtractDataObj } from './widgets/TableDataMapper';
import { applyTableFilters } from './byor/utils/tableFilterUtils';

// Import Summary Findings Widgets
import {
  // Backups Summary
  BackupsSummaryWidget,
  BackupsCostSummaryWidget,
  // Admins Summary
  AdminsPasswordStatusPieChartWidget,
  AdminsFindingSummaryWidget,
  AdminsCostSummaryWidget,
  MSUsersPieChartWidget,
  MSMFAPieChartWidget,
  MSPolicyPieChartWidget,
  InactiveMSUsersSummaryWidget,
  InactiveMSUsersCostSummaryWidget,
  MFASummaryWidget,
  MFACostSummaryWidget,
  PolicyConfigurationCostSummaryWidget,
  // Accounts Summary
  AccountsPasswordStatusPieChartWidget,
  AccountsFindingSummaryWidget,
  AccountsCostSummaryWidget,
  // Firewall Summary
  FirewallFindingSummaryWidget,
  FirewallCostSummaryWidget,
  LocalhostFirewallFindingsSummaryWidget,
  LocalhostFirewallCostSummaryWidget,
  LocalhostServiceRiskSummaryWidget,
  LocalhostServiceCostSummaryWidget,
  EncryptionSummaryWidget,
  EncryptionCostSummaryWidget,
  LocalhostOSCostSummaryWidget,
  LocalhostOpenPortsCostSummaryWidget,
  // Network Firewall Summary
  NetworkFirewallFindingsSummaryWidget,
  NetworkFirewallCostSummaryWidget,
  // OS Summary
  OSRiskSummaryWidget,
  OSCostSummaryWidget,
  // Services Summary
  NetworkServiceRiskSummaryWidget,
  NetworkServiceCostSummaryWidget,
  // Open Ports Summary
  NetworkOpenPortsCostSummaryWidget,
  // AD Computer Cost Summary
  ADComputerCostSummaryWidget,
  ADUsersCostSummaryWidget,
  OUCostSummaryWidget,
  ADGroupsCostSummaryWidget,
  FSMOCostSummaryWidget,
  GPOCostSummaryWidget,
  ADAdminsCostSummaryWidget,
  ADAdminsStatusPieChartWidget,
  // PII
  PIIDetectionPieChartWidget,
  PIIFindingSummaryWidget,
  PIICostSummaryWidget,
  // Total Cost
  TotalCostCalculatorWidget
} from './widgets/SummaryFindingsWidgets';

// Import External Risk Scan Widgets
import {
  ExternalRiskScoreWidget,
  ExternalDomainInfoWidget,
  ExternalRiskOverviewWidget,
  ExternalCategoryBreakdownWidget,
  ExternalAllFindingsTableWidget,
  ExternalWarningsTableWidget,
  ExternalPassedTableWidget,
  ExternalSeverityPieChartWidget,
  ExternalStatusPieChartWidget,
  ExternalCategoryBarChartWidget,
  ExternalComplianceSummaryWidget,
  ExternalComplianceTableWidget,
  ExternalDNSFindingsWidget,
  ExternalEmailFindingsWidget,
  ExternalSSLFindingsWidget,
  ExternalHeadersFindingsWidget,
  ExternalInfrastructureFindingsWidget,
  ExternalBreachFindingsWidget
} from './widgets/ExternalRiskWidgets';
import {
  getWidgetLabels,
  getWidgetStyleTargets,
  hasLabelDefinitions,
  getDefaultLabel,
  getLabelStyle
} from './widgets/widgetLabelHelpers';
import { formatLabelName } from './widgets/widgetLabelDefinitions';
import LabelStyleControls from './byor/components/PropertiesPanel/LabelStyleControls';
import { PALETTE_PRESETS } from '../constants/colorPalettes';
import { detectScanSchemaVersion } from '../utils/scanSchemaVersion';


// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  RadialLinearScale,
  ChartDataLabels
);

// Render charts at a higher backing resolution so they look crisp instead of
// "rastered"/soft — both on screen and when captured into the exported PDF. Chart.js
// defaults to the screen's devicePixelRatio (often 1), which produces fuzzy bars/text;
// pinning a higher ratio (min 2, scaled for retina, capped at 4 for memory) sharpens
// every chart type without touching each widget's options.
ChartJS.defaults.devicePixelRatio = Math.min(4, Math.max(2, (typeof window !== 'undefined' && window.devicePixelRatio ? window.devicePixelRatio : 1) * 2));

// Helper to recursively find a block by ID
const findBlockById = (blocks, id) => {
  if (!blocks || !id) return null;

  for (const block of blocks) {
    if (block.id === id) {
      return block;
    }

    if (block.children && block.children.length > 0) {
      const found = findBlockById(block.children, id);
      if (found) return found;
    }
  }

  return null;
};

const CustomChartLegend = ({ chartRef, config, dataContext, chartType, data }) => {
  const [labels, setLabels] = useState([]);
  const legendStyle = getLabelStyle(config, 'legend', { color: '#666' }, chartType, dataContext);

  useEffect(() => {
    if (!chartRef.current) return;
    const chart = chartRef.current;

    const updateLabels = () => {
      const groups = {};
      chart.data.datasets.forEach((dataset, i) => {
        const dataPoint = dataset.data[0];
        if (!dataPoint) return;
        const key = chartType === 'bubble'
          ? `X: ${dataPoint.x}, Y: ${dataPoint.y}${dataPoint.r ? `, Size: ${dataPoint.r}` : ''}`
          : `X: ${dataPoint.x}, Y: ${dataPoint.y}`;

        if (!groups[key]) {
          groups[key] = {
            nameCounts: {},
            indices: [],
            backgroundColor: dataset.backgroundColor,
            borderColor: dataset.borderColor,
            hidden: !chart.isDatasetVisible(i)
          };
        }
        const name = dataset.label || `Item ${i + 1}`;
        groups[key].nameCounts[name] = (groups[key].nameCounts[name] || 0) + 1;
        groups[key].indices.push(i);
      });

      setLabels(Object.entries(groups).map(([key, group]) => ({
        key,
        ...group
      })));
    };

    updateLabels();

    // We also need to listen for chart updates to sync hidden states
    const originalUpdate = chart.update.bind(chart);
    chart.update = (...args) => {
      originalUpdate(...args);
      updateLabels();
    };

    return () => {
      chart.update = originalUpdate;
    };
  }, [chartRef, config, dataContext, chartType, data]);

  if (config.legendPosition === 'none' || !labels.length) return null;

  const handleToggle = (indices) => {
    if (!chartRef.current) return;
    const chart = chartRef.current;
    const isVisible = chart.isDatasetVisible(indices[0]);
    indices.forEach(idx => chart.setDatasetVisibility(idx, !isVisible));
    chart.update();
  };

  const isTop = config.legendPosition === 'top' || !config.legendPosition;
  const isBottom = config.legendPosition === 'bottom';

  return (
    <div className="custom-chart-legend" style={{
      padding: '12px',
      backgroundColor: legendStyle.backgroundColor || 'transparent',
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: '12px',
      marginTop: isBottom ? '15px' : '0',
      marginBottom: isTop ? '15px' : '0',
      maxHeight: '400px',
      overflowY: 'auto',
      border: (legendStyle.backgroundColor && legendStyle.backgroundColor !== 'transparent') ? '1px solid #eee' : 'none',
      borderRadius: '8px',
      boxShadow: (legendStyle.backgroundColor && legendStyle.backgroundColor !== 'transparent') ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
      width: '100%'
    }}>
      {labels.map((group, idx) => (
        <div
          key={idx}
          onClick={() => handleToggle(group.indices)}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            cursor: 'pointer',
            opacity: group.hidden ? 0.4 : 1,
            transition: 'all 0.2s ease',
            fontSize: legendStyle.fontSize || '12px',
            color: legendStyle.color,
            padding: '10px',
            borderRadius: '6px',
            backgroundColor: 'rgba(0,0,0,0.02)',
            flex: '1 1 calc(33.333% - 12px)',
            minWidth: '220px',
            maxWidth: '100%',
            border: '1px solid rgba(0,0,0,0.04)',
            boxSizing: 'border-box'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.02)'}
        >
          <div style={{
            width: '14px',
            height: '14px',
            backgroundColor: group.backgroundColor,
            border: `1px solid ${group.borderColor}`,
            marginTop: '3px',
            marginRight: '12px',
            flexShrink: 0,
            borderRadius: '2px'
          }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ fontWeight: '700', color: '#333' }}>({group.key})</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
              {Object.entries(group.nameCounts).map(([name, count], nIdx) => (
                <span key={nIdx} style={{ paddingLeft: '8px', color: '#555' }}>
                  • {name} {count > 1 ? <span style={{ fontWeight: 'normal', color: '#888', fontSize: '0.9em' }}>[{count} items]</span> : ''}
                </span>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Helper to recursively update blocks
const recursivelyUpdateBlocks = (blocks, updateFn) => {
  return blocks.map(block => {
    // Apply update logic to current block
    const updatedBlock = updateFn(block);

    // Recursively update children if they exist
    if (updatedBlock.children && updatedBlock.children.length > 0) {
      return {
        ...updatedBlock,
        children: recursivelyUpdateBlocks(updatedBlock.children, updateFn)
      };
    }

    return updatedBlock;
  });
};

// Grid configuration constants
// Grid configuration constants
const PORTRAIT_CONFIG = {
  columns: 18,
  rows: 44,
  cellWidth: 44.11, // 794px (A4 width) / 18 columns
  cellHeight: 25.5, // 1122px (A4 height) / 44 rows
  pageWidth: 794,
  pageHeight: 1122,
  orientation: 'portrait',
  maxWidgetHeight: 1122 - (25.5 * 2)
};

const LANDSCAPE_CONFIG = {
  columns: 25,
  rows: 31,
  cellWidth: 44.88, // 1122px / 25 columns
  cellHeight: 25.61, // 794px / 31 rows
  pageWidth: 1122,
  pageHeight: 794,
  orientation: 'landscape',
  maxWidgetHeight: 794 - (25.61 * 2)
};

// Default for backward compatibility / helpers
const GRID_CONFIG = PORTRAIT_CONFIG;

// Helper: Calculate the Y offset below any visible header for a given page
// Used to position continuation blocks below headers on their page
const getHeaderOffsetForPage = (pageNumber, allPages) => {
  let maxHeaderBottom = 0;
  let relevantHeaderId = null;

  // Check all pages for header blocks that appear on this page number
  allPages.forEach(p => {
    p.blocks.forEach(block => {
      if ((block.type === 'element' || block.type === 'section') && block.elementType === 'header') {
        const fromPage = block.dataConfig?.fromPage ?? block.config?.fromPage ?? 1;
        const toPage = block.dataConfig?.toPage ?? 'last';
        const toPageNumber = toPage === 'last' ? allPages.length : (typeof toPage === 'number' ? toPage : allPages.length);

        if (pageNumber >= fromPage && pageNumber <= toPageNumber) {
          const headerBottom = (block.position?.y || 0) + (block.size?.height || 51);
          if (headerBottom > maxHeaderBottom) {
            maxHeaderBottom = headerBottom;
            relevantHeaderId = block.id;
          }
        }
      }
    });
  });

  if (maxHeaderBottom > 0) {
    console.log(`[Layout Debug] getHeaderOffsetForPage(${pageNumber}) = ${maxHeaderBottom} (from header ${relevantHeaderId})`);
  }

  return maxHeaderBottom;
};

const getFooterOffsetForPage = (pageNumber, allPages) => {
  let maxFooterHeight = 0;
  let relevantFooterId = null;

  allPages.forEach(p => {
    p.blocks.forEach(block => {
      if ((block.type === 'element' || block.type === 'section') && block.elementType === 'footer') {
        const fromPage = block.dataConfig?.fromPage ?? block.config?.fromPage ?? 1;
        const toPage = block.dataConfig?.toPage ?? 'last';
        const toPageNumber = toPage === 'last' ? allPages.length : (typeof toPage === 'number' ? toPage : allPages.length);

        if (pageNumber >= fromPage && pageNumber <= toPageNumber) {
          const footerH = block.size?.height || 39;
          if (footerH > maxFooterHeight) {
            maxFooterHeight = footerH;
            relevantFooterId = block.id;
          }
        }
      }
    });
  });

  return maxFooterHeight || 39; // Default 39px safety margin for standard footers
};



// Maximum widget height for paginated widgets (Default)
const maxWidgetHeight = GRID_CONFIG.maxWidgetHeight;

// Get data by path with intelligent key matching (matches backend logic)
// Handles: exact matches, case-insensitive, category name mapping, fuzzy matching
// Also supports 'internal.' and 'external.' prefixes for combined scan reports
const getDataByPath = (obj, path, customCategoryMap = null) => {
  if (!path || !obj) return null;

  // Parse the path to handle both dot notation and bracket notation
  // e.g., "network_devices[21].system.Administrators" -> ["network_devices", "21", "system", "Administrators"]
  const parsePath = (pathStr) => {
    const parts = [];
    let current = '';
    let inBracket = false;

    for (let i = 0; i < pathStr.length; i++) {
      const char = pathStr[i];

      if (char === '[') {
        if (current) {
          parts.push(current);
          current = '';
        }
        inBracket = true;
      } else if (char === ']') {
        if (current) {
          parts.push(current);
          current = '';
        }
        inBracket = false;
      } else if (char === '.' && !inBracket) {
        if (current) {
          parts.push(current);
          current = '';
        }
      } else {
        current += char;
      }
    }

    if (current) {
      parts.push(current);
    }

    return parts;
  };

  const keys = parsePath(path);
  let current = obj;

  // Mapping of humanized category names to actual scan data keys (same as backend)
  // Also includes 'internal' and 'external' for combined scan data access
  const categoryMap = {
    'Active Directory Scan': 'Ad Scan',
    'Active Directory': 'Ad Scan',
    'Microsoft 365 Scan': 'MS365',
    'Microsoft 365': 'MS365',
    'Network Scan': 'network_devices',
    'Network Devices': 'network_devices',
    'On-Prem Scan': 'Onprem',
    'On Prem': 'Onprem',
    'Onprem': 'Onprem',
    'Localhost Scan': 'localhost',
    'Localhost': 'localhost',
    'Personally Identifiable Information': 'PII',
    // Combined scan data namespaces
    'Internal Scan': 'internal',
    'Internal': 'internal',
    'External Scan': 'external',
    'External': 'external',
    'Domain Scan': 'external',
    // Merge with any custom map from DB
    ...(customCategoryMap || {})
  };

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];

    if (current === null || current === undefined) {
      return null;
    }

    // Handle array index access (numeric key)
    if (/^\d+$/.test(key)) {
      if (Array.isArray(current)) {
        const index = parseInt(key, 10);
        if (index >= 0 && index < current.length) {
          current = current[index];
          continue;
        }
      }
      // Also try as object key
      if (typeof current === 'object' && key in current) {
        current = current[key];
        continue;
      }
      return null;
    }

    if (typeof current !== 'object') {
      return null;
    }

    // Try exact match first
    if (key in current) {
      current = current[key];
      continue;
    }

    // Try category mapping for first key (usually a category name)
    if (i === 0 && categoryMap[key]) {
      const mappedKey = categoryMap[key];
      if (mappedKey in current) {
        current = current[mappedKey];
        continue;
      }
    }

    // Try case-insensitive match
    const foundKey = Object.keys(current).find(k =>
      k.toLowerCase() === key.toLowerCase()
    );
    if (foundKey) {
      current = current[foundKey];
      continue;
    }

    // Try fuzzy match (key contains or is contained by)
    const fuzzyKey = Object.keys(current).find(k =>
      k.toLowerCase().includes(key.toLowerCase()) ||
      key.toLowerCase().includes(k.toLowerCase())
    );
    if (fuzzyKey) {
      current = current[fuzzyKey];
      continue;
    }

    // Try removing spaces and matching
    const normalizedKey = key.replace(/\s+/g, '');
    const normalizedMatch = Object.keys(current).find(k =>
      k.replace(/\s+/g, '').toLowerCase() === normalizedKey.toLowerCase()
    );
    if (normalizedMatch) {
      current = current[normalizedMatch];
      continue;
    }

    // No match found
    return null;
  }

  return current;
};

// Main Report Builder Component

const STATIC_WIDGET_TYPES = [
  // ===== AUDIT TABLES =====
  { type: 'ad-users-audit-table', label: 'Audit AD Users', icon: '📋', description: 'AD users audit table', dataType: 'auto', category: 'Active Directory' },
  { type: 'ad-computers-audit-table', label: 'Audit AD Computers', icon: '💻', description: 'AD computers audit table', dataType: 'auto', category: 'Active Directory' },
  { type: 'ad-devices-audit-table', label: 'Audit AD Devices', icon: '📱', description: 'AD devices audit table', dataType: 'auto', category: 'Active Directory' },
  { type: 'ad-groups-audit-table', label: 'Audit Active Directory Groups', icon: '👥', description: 'AD groups audit table', dataType: 'auto', category: 'Active Directory' },
  { type: 'ad-ous-audit-table', label: 'Audit Organization Units', icon: '📂', description: 'AD OUs audit table', dataType: 'auto', category: 'Active Directory' },
  { type: 'ad-domain-controllers-audit-table', label: 'Audit Domain Controllers', icon: '👑', description: 'AD domain controllers audit table', dataType: 'auto', category: 'Active Directory' },
  { type: 'ad-gpo-policies-audit-table', label: 'Audit GPO Policies', icon: '📜', description: 'AD GPO policies audit table', dataType: 'auto', category: 'Active Directory' },
  { type: 'ad-event-logs-audit-table', label: 'Audit Event Logs', icon: '📝', description: 'AD event logs audit table', dataType: 'auto', category: 'Active Directory' },
  { type: 'ms365-scan-proof-table', label: 'Microsoft 365 Scan Proof', icon: '☁️', description: 'MS365 scan proof table', dataType: 'auto', category: 'Microsoft 365' },
  { type: 'microsoft-policies-table', label: 'Microsoft Policies', icon: '📋', description: 'Microsoft policies table', dataType: 'auto', category: 'Microsoft 365' },
  { type: 'localhost-hardware-information-table', label: 'Localhost Hardware Information', icon: '💾', description: 'Localhost hardware information table', dataType: 'auto', category: 'Localhost' },
  { type: 'localhost-system-information-table', label: 'Localhost System Information', icon: '🖥️', description: 'Localhost system information table', dataType: 'auto', category: 'Localhost' },
  { type: 'localhost-installed-applications-table', label: 'Localhost Installed Applications', icon: '📦', description: 'Localhost installed applications table', dataType: 'auto', category: 'Localhost' },
  { type: 'localhost-event-logs-table', label: 'Localhost Event Logs', icon: '📝', description: 'Localhost event logs table', dataType: 'auto', category: 'Localhost' },
  { type: 'localhost-open-ports-audit-table', label: 'Audit Localhost Open Ports', icon: '🔌', description: 'Localhost open ports audit table', dataType: 'auto', category: 'Localhost' },
  // ===== STANDARD CHARTS =====
  { type: 'numeric', label: 'Numeric', icon: '🔢', description: 'Single value display', dataType: 'number', category: 'Standard Charts' },
  { type: 'bar', label: 'Bar Chart', icon: '📊', description: 'Vertical bars', dataType: 'array', category: 'Standard Charts' },
  { type: 'line', label: 'Line Chart', icon: '📈', description: 'Trend lines', dataType: 'array', category: 'Standard Charts' },
  { type: 'pie', label: 'Pie Chart', icon: '🥧', description: 'Proportions', dataType: 'array', category: 'Standard Charts' },
  { type: 'donut', label: 'Donut', icon: '🍩', description: 'Hollow pie chart', dataType: 'array', category: 'Standard Charts' },
  { type: 'radar', label: 'Radar Chart', icon: '🛰️', description: 'Radar/spider chart', dataType: 'array', category: 'Standard Charts' },
  { type: 'polar', label: 'Polar Area', icon: '❄️', description: 'Polar area chart', dataType: 'array', category: 'Standard Charts' },
  { type: 'bubble', label: 'Bubble Chart', icon: '🫧', description: 'Bubble chart', dataType: 'array', category: 'Standard Charts' },
  // { type: 'scatter', label: 'Scatter Plot', icon: '🔵', description: 'Scatter plot', dataType: 'array', category: 'Standard Charts' },
  { type: 'table', label: 'Table', icon: '📋', description: 'Data table (auto-paginates across pages)', dataType: 'array', category: 'Standard Charts' },
  // { type: 'gauge', label: 'Gauge', icon: '🎯', description: 'Progress gauge', dataType: 'number', category: 'Standard Charts' },
  { type: 'statcard', label: 'Stat Card', icon: '🃏', description: 'Value with label', dataType: 'number', category: 'Standard Charts' },

  // ===== AUTO WIDGETS - TEMPLATE SIGNATURE (NEW!) =====
  // { type: 'nested-rectangle-findings', label: 'Nested Rectangle Findings', icon: '⭐', description: 'Template1 signature visual', dataType: 'auto', category: 'Auto - Template Signature' },
  // { type: 'company-at-a-glance', label: 'Company At A Glance', icon: '🏢', description: 'Overview cards for all scans', dataType: 'auto', category: 'Auto - Template Signature' },
  // { type: 'category-score-bar', label: 'Category Score Bar', icon: '📊', description: 'Horizontal risk bar', dataType: 'auto', category: 'Auto - Template Signature' },
  // { type: 'financial-impact', label: 'Financial Impact', icon: '💰', description: 'Calculated financial risk', dataType: 'auto', category: 'Auto - Template Signature' },
  // { type: 'category-description', label: 'Category Description', icon: '📖', description: 'Why is it important?', dataType: 'auto', category: 'Auto - Template Signature' },

  // ===== AUTO WIDGETS - FINDINGS & SECURITY =====
  { type: 'findings-status', label: 'Findings Status', icon: '🔍', description: 'Security findings grid', dataType: 'auto', category: 'Findings & Security' },
  { type: 'single-finding-status', label: 'Custom Finding Status', icon: '🎯', description: 'Single finding icon callout', dataType: 'auto', category: 'Findings & Security', gridType: 'custom' },
  // { type: 'infrastructure-scan-grid', label: 'Infrastructure Scan Grid', icon: '🏗️', description: '3x3 Grid of Infrastructure Findings', dataType: 'auto', category: 'Findings & Security' },
  // { type: 'managed-service-experience', label: 'Managed IT / Service Experience', icon: '🛠️', description: '3x3 Grid of Service Experience Findings', dataType: 'auto', category: 'Findings & Security' },
  // { type: 'security-scan-grid', label: 'Security Scan Grid', icon: '🛡️', description: '3x3 Grid of Security Findings', dataType: 'auto', category: 'Findings & Security' },
  // { type: 'cyber-security-threat-exposure', label: 'Cyber Security (Threat & Exposure)', icon: '🛡️', description: '3x3 Grid of Cyber Security Findings', dataType: 'auto', category: 'Findings & Security' },
  { type: 'combined-scan-grid', label: 'Basic Scan Grid', icon: '🧩', description: 'Selectable 3x3 Grid of Scan Findings', dataType: 'auto', category: 'Findings & Security', w: 18 },
  { type: 'advanced-scan-grid', label: 'Advanced Scan Grid', icon: '➕', description: 'Selectable 3x3 Grid of Scan Findings from any scan type', dataType: 'auto', category: 'Findings & Security', w: 18 },
  // { type: 'key-findings-advanced', label: 'Key Findings Advanced Summary', icon: '📑', description: 'Advanced audit findings list', dataType: 'auto', category: 'Findings & Security' },
  { type: 'key-findings', label: 'Key Findings Summary', icon: '📑', description: 'Findings table (auto-paginates)', dataType: 'auto', category: 'Findings & Security' },
  // { type: 'open-ports', label: 'Sensitive Open Ports', icon: '🔌', description: 'Sensitive ports list (auto-paginates)', dataType: 'auto', category: 'Findings & Security' },
  { type: 'findings-grid', label: 'Findings Grid', icon: '🗂️', description: 'Findings cards (auto-paginates)', dataType: 'auto', category: 'Findings & Security' },
  { type: 'open-ports-summary', label: 'Open Ports Summary', icon: '📊', description: 'Ports summary table', dataType: 'auto', category: 'Findings & Security' },
  { type: 'darkweb-summary', label: 'Dark Web Summary', icon: '🌐', description: 'Breach data summary', dataType: 'auto', category: 'Findings & Security' },
  // { type: 'localhost-firewall-cost-summary', label: 'Localhost Firewall Cost Summary', icon: '💰', description: 'Localhost firewall remediation cost summary', dataType: 'auto', category: 'Localhost' },
  { type: 'localhost-service-cost-summary', label: 'Localhost Service Cost Summary', icon: '💰', description: 'Localhost service remediation cost summary', dataType: 'auto', category: 'Localhost' },
  { type: 'localhost-os-cost-summary', label: 'Localhost OS Cost Summary', icon: '💰', description: 'Localhost OS remediation cost summary', dataType: 'auto', category: 'Localhost' },
  { type: 'localhost-open-ports-cost-summary', label: 'Localhost Open Ports Cost Summary', icon: '💰', description: 'Localhost open ports remediation cost summary', dataType: 'auto', category: 'Localhost' },
  { type: 'overall-risk-score', label: 'Overall Risk Score', icon: '📈', description: 'Risk rating & breakdown', dataType: 'auto', category: 'Findings & Security' },

  // ===== CHARTS - LOCALHOST =====
  // { type: 'user-password-status', label: 'User Password Status', icon: '🔑', description: 'Password expiry status', dataType: 'auto', category: 'Localhost' },
  // { type: 'disk-free-used', label: 'Disk Free vs Used', icon: '💾', description: 'Disk usage stats', dataType: 'auto', category: 'Localhost' },
  // { type: 'firewall-status', label: 'Firewall Status', icon: '🛡️', description: 'Firewall profile status', dataType: 'auto', category: 'Localhost' },
  // { type: 'service-start-modes', label: 'Service Start Modes', icon: '⚙️', description: 'Service configuration', dataType: 'auto', category: 'Localhost' },
  // { type: 'admin-password-status', label: 'Admin Password Status', icon: '👮', description: 'Admin password expiry', dataType: 'auto', category: 'Localhost' },
  // { type: 'password-strength', label: 'Password Strength', icon: '💪', description: 'Password complexity', dataType: 'auto', category: 'Localhost' },
  // { type: 'hibp-breach-status', label: 'HIBP Breach Status', icon: '🔓', description: 'Breach check results', dataType: 'auto', category: 'Localhost' },
  { type: 'windows-version', label: 'Windows Version', icon: '🪟', description: 'OS version distribution', dataType: 'auto', category: 'Localhost' },
  // { type: 'common-software', label: 'Common Software', icon: '📦', description: 'Installed software', dataType: 'auto', category: 'Localhost' },
  // { type: 'hibp-status', label: 'HIBP Status', icon: '🕵️', description: 'Have I Been Pwned', dataType: 'auto', category: 'Localhost' },
  { type: 'rdp-status', label: 'RDP Status', icon: '🖥️', description: 'Remote Desktop status', dataType: 'auto', category: 'Localhost' },
  // { type: 'browser-status', label: 'Browser Status', icon: '🌐', description: 'Installed browsers', dataType: 'auto', category: 'Localhost' },
  // { type: 'system-age', label: 'System Age', icon: '📅', description: 'System installation age', dataType: 'auto', category: 'Localhost' },

  // ===== CHARTS - NETWORK =====
  // { type: 'open-ports-chart', label: 'Open Ports Chart', icon: '🔌', description: 'Open ports distribution', dataType: 'auto', category: 'Network' },
  // { type: 'open-ports', label: 'Sensitive Open Ports', icon: '🔌', description: 'Sensitive ports list (auto-paginates)', dataType: 'auto', category: 'Network' },
  // { type: 'device-type', label: 'Device Type', icon: '📱', description: 'Network device types', dataType: 'auto', category: 'Network' },
  // { type: 'network-sensitive-ports', label: 'Sensitive Ports', icon: '🚨', description: 'Risky ports found', dataType: 'auto', category: 'Network' },

  // ===== CHARTS - ACTIVE DIRECTORY =====
  // { type: 'ad-object-types', label: 'AD Object Types', icon: '👥', description: 'Users, Groups, Computers', dataType: 'auto', category: 'Active Directory' },
  { type: 'ad-objects-pie-chart', label: 'AD Objects Pie Chart', icon: '🥧', description: 'AD objects distribution pie chart', dataType: 'auto', category: 'Active Directory' },
  // { type: 'fsmo-roles', label: 'FSMO Roles', icon: '👑', description: 'FSMO Role Holders', dataType: 'auto', category: 'Active Directory' },
  // { type: 'active-inactive-users', label: 'Active/Inactive Users', icon: '👤', description: 'User account status', dataType: 'auto', category: 'Active Directory' },
  // { type: 'password-expiry', label: 'Password Expiry', icon: '⏳', description: 'Password expiry settings', dataType: 'auto', category: 'Active Directory' },
  // { type: 'active-inactive-computers', label: 'Active/Inactive Computers', icon: '💻', description: 'Computer account status', dataType: 'auto', category: 'Active Directory' },
  // { type: 'group-types', label: 'Group Types', icon: '👥', description: 'Global, Local, Universal', dataType: 'auto', category: 'Active Directory' },
  // { type: 'ou-membership', label: 'OU Membership', icon: '📂', description: 'Objects per OU', dataType: 'auto', category: 'Active Directory' },
  // { type: 'gpo-timeline', label: 'GPO Timeline', icon: '📜', description: 'GPO Creation Timeline', dataType: 'auto', category: 'Active Directory' },

  // // ===== PROOF TABLES - ACTIVE DIRECTORY =====
  // { type: 'ad-all-users-table', label: 'All AD Users', icon: '📋', description: 'All user accounts table', dataType: 'auto', category: 'Active Directory' },
  { type: 'ad-inactive-users-table', label: 'Inactive AD Users', icon: '❌', description: 'Inactive users table', dataType: 'auto', category: 'Active Directory' },
  { type: 'ad-all-computers-table', label: 'All AD Computers', icon: '💻', description: 'All computers table', dataType: 'auto', category: 'Active Directory' },
  { type: 'ad-workstation-age-table', label: 'Workstation Age', icon: '⏳', description: 'Workstation Age table', dataType: 'auto', category: 'Active Directory' },
  { type: 'ad-inactive-computers-table', label: 'Inactive AD Computers', icon: '🚫', description: 'Inactive computers table', dataType: 'auto', category: 'Active Directory' },
  { type: 'ad-groups-risk-table', label: 'GPO Assessment - Assess Groups Risk', icon: '⚠️', description: 'Groups risk assessment', dataType: 'auto', category: 'Active Directory' },
  { type: 'ad-ous-risk-table', label: 'OU Assessment - Assess Group Risks', icon: '📂', description: 'OUs risk assessment', dataType: 'auto', category: 'Active Directory' },
  { type: 'ad-fsmo-roles-table', label: 'FSMO and Server Role Analysis', icon: '👑', description: 'FSMO roles table', dataType: 'auto', category: 'Active Directory' },
  { type: 'ad-gpo-analysis-table', label: 'GPO Analysis', icon: '📜', description: 'GPO analysis table', dataType: 'auto', category: 'Active Directory' },
  { type: 'ad-admins-table', label: 'Active Directory Privileged Admins', icon: '🔐', description: 'Inventory of privileged AD accounts and groups', dataType: 'auto', category: 'Active Directory' },

  // ===== PROOF TABLES - NETWORK =====
  // { type: 'network-all-users-password-table', label: 'All Users Password Status', icon: '🔑', description: 'Network user passwords', dataType: 'auto', category: 'Network' },
  // { type: 'network-non-expiring-passwords-table', label: 'Non-Expiring Passwords', icon: '⚠️', description: 'Users with non-expiring passwords', dataType: 'auto', category: 'Network' },
  // { type: 'network-admin-daily-work-table', label: 'Admin Daily Work', icon: '👨‍💼', description: 'Admin accounts used daily', dataType: 'auto', category: 'Network' },
  // { type: 'network-disk-usage-table', label: 'Disk Usage', icon: '💾', description: 'Disk usage by device', dataType: 'auto', category: 'Network' },
  { type: 'network-backup-status-table', label: 'Backup Status', icon: '💼', description: 'Backup status by device', dataType: 'auto', category: 'Network' },
  { type: 'network-users-no-expiring-password-table', label: 'Users with no Expiring Password', icon: '🔒', description: 'Users with non-expiring passwords', dataType: 'auto', category: 'Network' },
  { type: 'network-users-expiring-password-table', label: 'Users with Expiring Password', icon: '⏰', description: 'Users with expiring passwords', dataType: 'auto', category: 'Network' },
  // { type: 'network-device-age-table', label: 'Device Age', icon: '📅', description: 'Device age by device', dataType: 'auto', category: 'Network' },
  // { type: 'network-installed-software-table', label: 'Installed Software', icon: '📦', description: 'Software across devices', dataType: 'auto', category: 'Network' },
  { type: 'software-inventory-table', label: 'Software Inventory', icon: '📦', description: 'Software across workstations', dataType: 'auto', category: 'Network' },
  { type: 'local-administrators-table', label: 'Local Administrators', icon: '👤', description: 'Local admin membership per workstation', dataType: 'auto', category: 'Network' },
  { type: 'network-os-versions-table', label: 'OS Versions', icon: '🪟', description: 'OS version distribution', dataType: 'auto', category: 'Network' },
  { type: 'network-admins-no-expiring-password-table', label: 'Admins with no Expiring Password', icon: '🔐', description: 'Admins with non-expiring passwords', dataType: 'auto', category: 'Network' },
  { type: 'network-admins-expiring-password-table', label: 'Admins with Expiring Password', icon: '⏰', description: 'Admins with expiring passwords', dataType: 'auto', category: 'Network' },
  { type: 'network-admins-daily-work-table', label: 'Admins Used for Daily Work', icon: '👨‍💼', description: 'Admin accounts used for daily work', dataType: 'auto', category: 'Network' },
  { type: 'network-non-default-services-table', label: 'Non-Default Services', icon: '⚙️', description: 'Non-default services', dataType: 'auto', category: 'Network' },
  // { type: 'network-all-open-ports-table', label: 'All Open Ports', icon: '🔌', description: 'All open ports table', dataType: 'auto', category: 'Network' },
  // { type: 'network-sensitive-open-ports-table', label: 'Sensitive Open Ports', icon: '🚨', description: 'Sensitive ports only', dataType: 'auto', category: 'Network' },
  { type: 'open-ports', label: 'Sensitive Open Ports', icon: '🚨', description: 'Sensitive ports list (auto-paginates)', dataType: 'auto', category: 'Network' },
  { type: 'open-ports-summary', label: 'Sensitive Open Ports Summary', icon: '📊', description: 'Ports summary table', dataType: 'auto', category: 'Network' },
  { type: 'network-antivirus-status-table', label: 'Antivirus Status', icon: '🛡️', description: 'Antivirus by device', dataType: 'auto', category: 'Network' },
  { type: 'network-encryption-status-table', label: 'Encryption Status', icon: '🔒', description: 'Encryption by device', dataType: 'auto', category: 'Network' },
  { type: 'network-firewall-status-table', label: 'Firewall Status', icon: '🛡️', description: 'Firewall by device', dataType: 'auto', category: 'Network' },
  { type: 'network-firewall-status-chart', label: 'Firewall Status Chart', icon: '📊', description: 'Firewall status distribution pie chart', dataType: 'auto', category: 'Network' },
  { type: 'network-detailed-services-table', label: 'Detailed Non-Default Services', icon: '⚙️', description: 'Detailed non-default services found', dataType: 'auto', category: 'Network' },
  { type: 'network-services-risk-breakdown-table', label: 'Services Risk Breakdown', icon: '⚠️', description: 'Services risk breakdown by state', dataType: 'auto', category: 'Network' },
  { type: 'network-high-privilege-services-table', label: 'High-Privilege Services', icon: '🔐', description: 'High-privilege services', dataType: 'auto', category: 'Network' },
  { type: 'network-logical-disks-table', label: 'Network Logical Disks', icon: '💾', description: 'Logical disks across all devices', dataType: 'auto', category: 'Network' },

  // ===== SUMMARY FINDINGS - LOCALHOST =====
  // { type: 'localhost-firewall-cost-summary', label: 'Localhost Firewall Cost Summary', icon: '💰', description: 'Localhost firewall remediation cost', dataType: 'auto', category: 'Localhost', w: 12 },
  // { type: 'localhost-os-cost-summary', label: 'Localhost OS Cost Summary', icon: '💰', description: 'Localhost OS remediation cost', dataType: 'auto', category: 'Localhost', w: 12 },
  // { type: 'localhost-open-ports-cost-summary', label: 'Localhost Open Ports Cost Summary', icon: '💰', description: 'Localhost open ports remediation cost', dataType: 'auto', category: 'Localhost', w: 12 },
  // { type: 'localhost-service-cost-summary', label: 'Localhost Service Cost Summary', icon: '💰', description: 'Localhost service remediation cost', dataType: 'auto', category: 'Localhost', w: 12 },
  // { type: 'localhost-high-privilege-services-table', label: 'High-Privilege Services', icon: '🔴', description: 'High-privilege services', dataType: 'auto', category: 'Localhost' },
  // { type: 'localhost-installed-software-table', label: 'Installed Software', icon: '📦', description: 'Localhost software', dataType: 'auto', category: 'Localhost' },
  // { type: 'localhost-non-default-services-table', label: 'Non-Default Services', icon: '⚙️', description: 'Localhost services', dataType: 'auto', category: 'Localhost' },
  // { type: 'localhost-open-ports-table', label: 'Open Ports', icon: '🔌', description: 'Localhost open ports', dataType: 'auto', category: 'Localhost' },
  // { type: 'localhost-browser-passwords-table', label: 'Browser Passwords', icon: '🌐', description: 'Stored browser credentials', dataType: 'auto', category: 'Localhost' },

  // ===== CHARTS - MS365 =====
  { type: 'ms365-users-pie-chart', label: 'Entra Users Pie Chart', icon: '🥧', description: 'Active vs Inactive MS365 users', dataType: 'auto', category: 'Microsoft 365' },
  { type: 'ms365-mfa-pie-chart', label: 'MS MFA Pie Chart', icon: '🔐', description: 'MFA Enabled vs Disabled MS365 users', dataType: 'auto', category: 'Microsoft 365' },
  { type: 'ms365-policy-pie-chart', label: 'MS Policy Pie Chart', icon: '📋', description: 'Properly Configured vs Misconfigured MS365 policies', dataType: 'auto', category: 'Microsoft 365' },

  // ===== CHARTS - PII =====
  { type: 'pii-detection-pie-chart', label: 'PII Detection', icon: '🥧', description: 'PII types distribution', dataType: 'auto', category: 'Personally Identifiable Information' },

  // ===== SUMMARY - PII =====
  { type: 'pii-cost-summary', label: 'PII Cost Summary', icon: '💰', description: 'PII remediation cost summary', dataType: 'auto', category: 'Personally Identifiable Information', w: 12 },
  { type: 'inactive-ms-users-cost-summary', label: 'Inactive Entra Users Cost Summary', icon: '💰', description: 'MFA remediation cost summary', dataType: 'auto', category: 'Microsoft 365', w: 12 },
  { type: 'mfa-cost-summary', label: 'MFA Cost Summary', icon: '💰', description: 'MFA remediation cost summary', dataType: 'auto', category: 'Microsoft 365', w: 12 },
  { type: 'policy-configuration-cost-summary', label: 'Policy Configuration Cost Summary', icon: '💰', description: 'Policy configuration remediation cost summary', dataType: 'auto', category: 'Microsoft 365', w: 12 },

  // ===== PROOF TABLES - MS365 =====
  { type: 'ms365-inactive-users-table', label: 'Inactive Users', icon: '❌', description: 'MS365 inactive users', dataType: 'auto', category: 'Microsoft 365' },
  { type: 'ms365-all-users-mfa-table', label: 'All Users MFA Status', icon: '🔐', description: 'MFA status for all users', dataType: 'auto', category: 'Microsoft 365' },
  { type: 'ms365-users-without-mfa-table', label: 'Users Without MFA', icon: '⚠️', description: 'Users without MFA', dataType: 'auto', category: 'Microsoft 365' },
  { type: 'ms365-policy-config-table', label: 'Policy Configuration', icon: '📋', description: 'Policy config status', dataType: 'auto', category: 'Microsoft 365' },

  // ===== PROOF TABLES - HIBP/DARK WEB =====
  // { type: 'hibp-breached-credentials-table', label: 'Breached Credentials', icon: '💀', description: 'Dark web breached creds', dataType: 'auto', category: 'Tables - Dark Web' },
  // { type: 'hibp-exposure-by-platform-table', label: 'Exposure by Platform', icon: '🌐', description: 'Breaches by platform', dataType: 'auto', category: 'Tables - Dark Web' },

  // ===== PROOF TABLES - PII =====
  { type: 'pii-detected-table', label: 'PII Detected', icon: '🔍', description: 'All PII found', dataType: 'auto', category: 'Personally Identifiable Information' },
  // { type: 'pii-type-summary-table', label: 'PII Type Summary', icon: '📊', description: 'PII by type', dataType: 'auto', category: 'Personally Identifiable Information' },

  // ===== SUMMARY FINDINGS - BACKUPS =====
  { type: 'backups-cost-summary', label: 'Backups Cost Summary', icon: '💰', description: 'Backup remediation cost', dataType: 'auto', category: 'Backups', w: 12 },
  { type: 'network-encryption-cost-summary', label: 'Encryption Cost Summary', icon: '💰', description: 'Encryption remediation cost', dataType: 'auto', category: 'Network', w: 12 },

  // ===== SUMMARY FINDINGS - ADMINS =====
  { type: 'admins-password-pie-chart', label: 'Admins Password Pie Chart', icon: '📊', description: 'Admin password expiry pie chart', dataType: 'auto', category: 'Admins' },
  { type: 'admins-cost-summary', label: 'Admins Cost Summary', icon: '💰', description: 'Admin remediation cost', dataType: 'auto', category: 'Admins', w: 12 },

  // ===== SUMMARY FINDINGS - ACCOUNTS =====
  { type: 'accounts-password-pie-chart', label: 'Accounts Password Pie Chart', icon: '📊', description: 'Account password expiry pie chart', dataType: 'auto', category: 'Accounts' },
  { type: 'accounts-cost-summary', label: 'Accounts Cost Summary', icon: '💰', description: 'Account remediation cost', dataType: 'auto', category: 'Accounts', w: 12 },

  // ===== SUMMARY FINDINGS - FIREWALL =====
  // { type: 'firewall-finding-summary', label: 'Firewall Finding Summary', icon: '🛡️', description: 'Firewall status summary', dataType: 'auto', category: 'Firewall' },
  // { type: 'firewall-cost-summary', label: 'Firewall Cost', icon: '💰', description: 'Firewall remediation cost', dataType: 'auto', category: 'Firewall' },
  { type: 'network-firewall-cost-summary', label: 'Network Firewall Cost', icon: '💰', description: 'Network firewall remediation cost', dataType: 'auto', category: 'Firewall', w: 12 },

  // ===== SUMMARY FINDINGS - SERVICES =====
  { type: 'network-service-cost-summary', label: 'Network Service Cost', icon: '💰', description: 'Network service remediation cost', dataType: 'auto', category: 'Services', w: 12 },

  // ===== SUMMARY FINDINGS - OPEN PORTS =====
  { type: 'network-open-ports-cost-summary', label: 'Open Ports Cost Summary', icon: '💰', description: 'Network open ports remediation cost', dataType: 'auto', category: 'Open Ports', w: 12 },

  // ===== SUMMARY FINDINGS - AD COMPUTERS =====
  { type: 'ad-computer-cost-summary', label: 'AD Computer Cost Summary', icon: '💰', description: 'AD computer remediation cost', dataType: 'auto', category: 'Active Directory', w: 12 },
  { type: 'ad-users-cost-summary', label: 'AD Users Cost Summary', icon: '💰', description: 'AD users remediation cost', dataType: 'auto', category: 'Active Directory', w: 12 },
  { type: 'ad-groups-cost-summary', label: 'AD Groups Cost Summary', icon: '💰', description: 'AD groups remediation cost', dataType: 'auto', category: 'Active Directory', w: 12 },
  { type: 'ou-cost-summary', label: 'OU Cost Summary', icon: '💰', description: 'OU remediation cost', dataType: 'auto', category: 'Active Directory', w: 12 },
  { type: 'fsmo-cost-summary', label: 'FSMO Cost Summary', icon: '💰', description: 'FSMO roles remediation cost', dataType: 'auto', category: 'Active Directory', w: 12 },
  { type: 'gpo-cost-summary', label: 'GPO Cost Summary', icon: '💰', description: 'GPO remediation cost', dataType: 'auto', category: 'Active Directory', w: 12 },
  { type: 'ad-admins-cost-summary', label: 'AD Admins Cost Summary', icon: '💰', description: 'AD privileged admins remediation cost summary', dataType: 'auto', category: 'Active Directory', w: 12 },
  // { type: 'ad-admins-status-pie', label: 'AD Admin Status Distribution', icon: '🥧', description: 'Pie chart of active vs stale vs disabled privileged accounts', dataType: 'auto', category: 'Active Directory', w: 12 },

  // ===== SUMMARY FINDINGS - OS =====
  { type: 'os-cost-summary', label: 'OS Cost Summary', icon: '💰', description: 'OS remediation cost', dataType: 'auto', category: 'Operating System', w: 12 },

  // ===== SUMMARY FINDINGS - TOTAL COST =====
  { type: 'total-cost-calculator', label: 'Total Cost Calculator', icon: '💵', description: 'Total remediation cost across all findings', dataType: 'auto', category: 'Cost Analysis', w: 12 },

  // ===== EXTERNAL RISK SCAN =====
  // Summary & Score Widgets
  { type: 'external-risk-score', label: 'External Risk Score', icon: '🎯', description: 'Overall external risk score gauge', dataType: 'auto', category: 'External Risk Scan' },
  { type: 'external-domain-info', label: 'Domain Information', icon: '🌐', description: 'Domain name, scan timestamp, and overview stats', dataType: 'auto', category: 'External Risk Scan' },
  { type: 'external-risk-overview', label: 'Risk Overview Cards', icon: '📊', description: 'Critical/Passed counts and overall risk level', dataType: 'auto', category: 'External Risk Scan' },

  // Category & Findings Widgets
  { type: 'external-category-breakdown', label: 'Category Breakdown', icon: '📋', description: 'All security categories with severity levels', dataType: 'auto', category: 'External Risk Scan' },
  { type: 'external-all-findings-table', label: 'All Findings Table', icon: '📝', description: 'Complete findings across all categories (auto-paginates)', dataType: 'auto', category: 'External Risk Scan' },
  { type: 'external-warnings-table', label: 'Warnings Table', icon: '⚠️', description: 'Only warning/failed findings (auto-paginates)', dataType: 'auto', category: 'External Risk Scan' },
  { type: 'external-passed-table', label: 'Passed Checks Table', icon: '✅', description: 'Only passed checks (auto-paginates)', dataType: 'auto', category: 'External Risk Scan' },

  // Charts
  { type: 'external-severity-pie-chart', label: 'Severity Distribution', icon: '🥧', description: 'Pie chart of finding severities', dataType: 'auto', category: 'External Risk Scan' },
  { type: 'external-status-pie-chart', label: 'Pass/Fail Status', icon: '🍩', description: 'Donut chart of pass vs warning vs fail', dataType: 'auto', category: 'External Risk Scan' },
  { type: 'external-category-bar-chart', label: 'Findings by Category', icon: '📊', description: 'Bar chart of findings count per category', dataType: 'auto', category: 'External Risk Scan' },

  // Compliance & Details
  { type: 'external-compliance-summary', label: 'Compliance Summary', icon: '📜', description: 'All compliance frameworks referenced', dataType: 'auto', category: 'External Risk Scan' },
  { type: 'external-compliance-table', label: 'Compliance Mapping Table', icon: '✅', description: 'Findings mapped to compliance frameworks', dataType: 'auto', category: 'External Risk Scan' },

  // Category-Specific Findings
  { type: 'external-dns-findings', label: 'DNS & Domain Findings', icon: '🔗', description: 'DNS and domain configuration findings', dataType: 'auto', category: 'External Risk Scan' },
  { type: 'external-email-findings', label: 'Email Security Findings', icon: '📧', description: 'SPF, DKIM, DMARC findings', dataType: 'auto', category: 'External Risk Scan' },
  { type: 'external-ssl-findings', label: 'SSL/TLS Findings', icon: '🔒', description: 'SSL/TLS and HTTPS findings', dataType: 'auto', category: 'External Risk Scan' },
  { type: 'external-headers-findings', label: 'Security Headers Findings', icon: '🛡️', description: 'HTTP security headers findings', dataType: 'auto', category: 'External Risk Scan' },
  { type: 'external-infrastructure-findings', label: 'Infrastructure Findings', icon: '☁️', description: 'Hosting and CDN findings', dataType: 'auto', category: 'External Risk Scan' },
  { type: 'external-breach-findings', label: 'Breach Intelligence Findings', icon: '🔓', description: 'Historical breach associations', dataType: 'auto', category: 'External Risk Scan' }
];

// Streamlined preset prompts. The AI designs the report from the actual scan data;
// these just set the focus and audience. The flagship "Executive Security Summary"
// covers the priority risk areas leadership asks about.
const EXEC_SECURITY_SUMMARY_PROMPT = "Create an executive-level security risk summary for leadership. Open with a one-glance scorecard, then one concise finding per risk area — business impact first, a small evidence table only where it adds value. Cover each of these IF the scan data supports it (skip any with no data, never invent): cracked or weak passwords; password re-use across accounts (cite reuse counts); passwords found on dark-web / breach lists; AI tools found in the environment; AI attack-surface exposure (data traffic, cloud storage, and traffic to AI services); unresolved CVEs (count and severity); local administrators; global / domain administrators; and BitLocker / disk-encryption status per device. Keep it executive-length — no padding.";

// Each prompt's "requires" lists the scan-data section keys (matching aiProfile.sections
// keys) it needs SOME data from to be worth recommending. OR semantics — present if ANY
// listed section has data. An empty/absent "requires" means the prompt is generic and
// always shown.
//
// Module-level keys:  'Ad Scan', 'MS365', 'localhost', 'network_devices', 'PII', 'external'
// Localhost detail:   'cve', 'breach', 'browser_passwords', 'extensions',
//                     'browser_history', 'windows_security', 'hardware'
//
// The detail keys come from the dotted-path candidates in the backend profiler
// (utils/aiReportDataProfiler.js) and let a prompt say "I need CVE data" rather than the
// all-or-nothing "I need localhost". Keys are matched by exact string — a typo silently
// hides the prompt forever. Every "prompt" string must also be globally unique, since
// selection state and isPremadePrompt() key off the prompt text, not the label.
const PREMADE_PROMPTS = [
  // ── Executive · Portrait ──────────────────────────────────────────────
  {
    label: "Executive Security Summary",
    scope: "Executive",
    orientation: "portrait",
    prompt: EXEC_SECURITY_SUMMARY_PROMPT,
    description: "Leadership-ready risk summary: cracked/reused/dark-web passwords, AI exposure, CVEs, admins, BitLocker.",
    requires: ["localhost", "Ad Scan"]
  },
  {
    label: "Identity & Credential Risk",
    scope: "Executive",
    orientation: "portrait",
    prompt: "Executive report focused on identity and credential risk. Cover cracked/weak passwords, password re-use, dark-web exposed credentials, accounts with non-expiring passwords, and local vs. global administrators — naming the highest-risk accounts from the scan. Lead with business impact; include a short remediation priority list.",
    description: "Credential and privileged-access risk, with named high-risk accounts.",
    requires: ["localhost", "Ad Scan"]
  },
  {
    label: "AI & Software Exposure",
    scope: "Executive",
    orientation: "portrait",
    prompt: "Executive report on AI usage and software/attack-surface exposure. Cover AI tools found in the environment, AI attack-surface exposure (data traffic, cloud storage, and traffic to AI services), unresolved CVEs, and notable installed software/SaaS. Lead with governance and business risk; cite actual application names and counts from the scan.",
    description: "AI governance, AI traffic exposure, CVEs, and software/SaaS risk.",
    requires: ["localhost"]
  },
  {
    label: "Dark Web & Breach Exposure",
    scope: "Executive",
    orientation: "portrait",
    prompt: "Executive report on breach and dark-web exposure. Cover passwords and accounts found on dark-web / breach lists, password re-use across services, and any externally exposed credentials. Quantify the exposure, name the affected accounts where available, and lead with business impact and remediation priorities.",
    description: "Dark-web / breach-list credential exposure and reuse risk.",
    requires: ["localhost", "external"]
  },
  {
    label: "Overall Risk Posture",
    scope: "Executive",
    orientation: "portrait",
    prompt: "Concise executive posture report: an overall risk score, a one-glance scorecard of each assessed area, and category score bars — followed by the top key findings with business impact and a short remediation priority list. Keep it high-level and board-ready.",
    description: "Overall score, scorecard, category bars, and top findings."
    // No "requires" — this summarizes whatever modules are present, so it's always relevant.
  },
  {
    label: "Microsoft 365 & Identity Security",
    scope: "Executive",
    orientation: "portrait",
    prompt: "Executive report on Microsoft 365 security posture. Cover MFA coverage and gaps, risky or non-compliant policies, inactive/stale user accounts, and privileged admin roles. Lead with business impact — what a compromised M365 account could expose — and a short remediation priority list.",
    description: "MFA gaps, risky policies, inactive users, and M365 admin risk.",
    requires: ["MS365"]
  },
  {
    label: "PII & Data Exposure Risk",
    scope: "Executive",
    orientation: "portrait",
    prompt: "Executive report on personally identifiable information (PII) exposure. Quantify the volume and types of sensitive data discovered, where it was found, and the business/compliance risk it creates. Lead with impact (breach notification, regulatory, reputational) and a short remediation priority list.",
    description: "Volume, type, and business/compliance risk of exposed PII.",
    requires: ["PII"]
  },
  {
    label: "External Attack Surface",
    scope: "Executive",
    orientation: "portrait",
    prompt: "Executive report on external attack surface and internet-facing risk. Cover domain/DNS findings, exposed services, and any compliance or security-posture failures identified by the external scan. Lead with business impact — what an outside attacker could see and exploit — and a short remediation priority list.",
    description: "Internet-facing exposure, domain/DNS risk, and compliance failures.",
    requires: ["external"]
  },
  // ── Executive · Landscape ─────────────────────────────────────────────
  {
    label: "Executive Security Summary",
    scope: "Executive",
    orientation: "landscape",
    prompt: EXEC_SECURITY_SUMMARY_PROMPT,
    description: "Leadership-ready risk summary: cracked/reused/dark-web passwords, AI exposure, CVEs, admins, BitLocker.",
    requires: ["localhost", "Ad Scan"]
  },
  {
    label: "Executive Visual Dashboard",
    scope: "Executive",
    orientation: "landscape",
    prompt: "Wide executive dashboard: place the overall risk score, the area scorecard, and category score bars side by side, followed by the top findings. Use the horizontal space for at-a-glance charts. Business impact first.",
    description: "Side-by-side risk score, scorecard, and category charts."
  },
  {
    label: "Risk Posture & Category Scores",
    scope: "Executive",
    orientation: "landscape",
    prompt: "Landscape executive summary centered on category score bars and an area scorecard, with a short narrative of the top risks and a remediation priorities table. Cite real counts from the scan.",
    description: "Category scores and posture in a wide summary layout."
  },
  {
    label: "Microsoft 365 Security Dashboard",
    scope: "Executive",
    orientation: "landscape",
    prompt: "Wide executive dashboard on Microsoft 365 security: MFA coverage, risky policies, and inactive/privileged accounts side by side, followed by the top M365 findings. Use the horizontal space for at-a-glance charts. Business impact first.",
    description: "Side-by-side MFA, policy, and admin-risk charts for M365.",
    requires: ["MS365"]
  },
  {
    label: "PII Exposure Dashboard",
    scope: "Executive",
    orientation: "landscape",
    prompt: "Wide executive dashboard on PII exposure: a breakdown of sensitive data types and volumes side by side with the highest-risk locations, followed by the top findings. Business impact and compliance risk first.",
    description: "Side-by-side PII volume/type breakdown and top findings.",
    requires: ["PII"]
  },
  {
    label: "External Exposure Dashboard",
    scope: "Executive",
    orientation: "landscape",
    prompt: "Wide executive dashboard on external attack surface: domain/DNS and exposed-service findings side by side with a compliance/severity breakdown, followed by the top findings. Business impact first.",
    description: "Side-by-side external exposure and compliance breakdown.",
    requires: ["external"]
  },
  // ── Technical · Portrait ──────────────────────────────────────────────
  {
    label: "Comprehensive Technical Audit",
    scope: "Technical",
    orientation: "portrait",
    prompt: "Create a detailed technical audit from the scan data: active directory users/computers/admins, network devices and open ports, localhost software and security controls (firewall, antivirus, encryption, backups). Use tables for the detail and cite the real values.",
    description: "Deep technical detail across AD, network, and endpoint.",
    requires: ["Ad Scan", "network_devices", "localhost"]
  },
  {
    label: "Endpoint & Security Controls",
    scope: "Technical",
    orientation: "portrait",
    prompt: "Technical report on endpoint security controls: per-device firewall, antivirus, BitLocker/disk-encryption, RDP, backup status, and notable services. Use status tables and flag the devices that fall short.",
    description: "Per-device firewall, AV, encryption, RDP, and backup status.",
    requires: ["localhost", "network_devices"]
  },
  {
    label: "Identity & Access (AD) Detail",
    scope: "Technical",
    orientation: "portrait",
    prompt: "Technical Active Directory report: domain controllers, privileged/admin accounts, inactive accounts, accounts with non-expiring passwords, and group/OU structure. Use detailed tables citing real usernames and flags.",
    description: "AD domain controllers, admins, inactive accounts, and groups.",
    requires: ["Ad Scan"]
  },
  {
    label: "Microsoft 365 Configuration Detail",
    scope: "Technical",
    orientation: "portrait",
    prompt: "Technical Microsoft 365 report: per-user MFA status, conditional access/policy configuration, inactive accounts, and admin role assignments. Use detailed tables citing real usernames and settings.",
    description: "Per-user MFA, policy config, inactive accounts, and admin roles.",
    requires: ["MS365"]
  },
  {
    label: "PII Discovery Detail",
    scope: "Technical",
    orientation: "portrait",
    prompt: "Technical PII discovery report: detailed table of detected PII items, their type, source location, and count. Group by data type and by location; cite real values from the scan.",
    description: "Detailed PII findings by type, source, and count.",
    requires: ["PII"]
  },
  {
    label: "External Risk Findings Detail",
    scope: "Technical",
    orientation: "portrait",
    prompt: "Technical external risk report: detailed findings from the external/domain scan, including failed checks, exposed services, and DNS/domain configuration issues. Use detailed tables citing real findings and severities.",
    description: "Detailed external scan findings, exposed services, and DNS issues.",
    requires: ["external"]
  },
  // ── Technical · Landscape ─────────────────────────────────────────────
  {
    label: "Network & Endpoint",
    scope: "Technical",
    orientation: "landscape",
    prompt: "Landscape technical report focused on the network and endpoints: a network topology diagram of discovered devices, open-port exposure, antivirus/firewall/encryption status, and localhost hardware/software inventory. Use the wide layout to place related tables side by side.",
    description: "Network topology, open ports, and endpoint controls in wide layout.",
    requires: ["network_devices", "localhost"]
  },
  {
    label: "Network Exposure & Open Ports",
    scope: "Technical",
    orientation: "landscape",
    prompt: "Wide technical report on network exposure: a topology diagram of discovered devices, a table of devices exposing open ports (with the ports and services), and a summary of the most-exposed hosts. Highlight administrative/file-sharing/legacy services.",
    description: "Topology, exposed devices, and open-port detail.",
    requires: ["network_devices"]
  },
  {
    label: "Side-by-Side Audit Grid",
    scope: "Technical",
    orientation: "landscape",
    prompt: "Landscape technical audit using the wide layout to place related tables side by side — e.g. network services next to endpoint controls, or AD groups next to OUs. Dense, detailed, and citing real values.",
    description: "Dense wide-layout tables placed side by side.",
    requires: ["network_devices", "localhost", "Ad Scan"]
  },
  {
    label: "Microsoft 365 Wide Audit",
    scope: "Technical",
    orientation: "landscape",
    prompt: "Landscape technical Microsoft 365 audit: MFA status, policy configuration, and admin roles placed side by side in the wide layout. Dense, detailed, and citing real values.",
    description: "MFA, policy, and admin-role tables side by side.",
    requires: ["MS365"]
  },
  {
    label: "PII Wide Audit",
    scope: "Technical",
    orientation: "landscape",
    prompt: "Landscape technical PII audit: detected PII items grouped by type and source location, placed side by side in the wide layout. Dense, detailed, and citing real values.",
    description: "PII findings by type and location, in wide layout.",
    requires: ["PII"]
  },
  {
    label: "External Risk Wide Audit",
    scope: "Technical",
    orientation: "landscape",
    prompt: "Landscape technical external-risk audit: exposed services, DNS/domain findings, and compliance checks placed side by side in the wide layout. Dense, detailed, and citing real values.",
    description: "External findings, DNS issues, and compliance checks side by side.",
    requires: ["external"]
  },

  // ── Endpoint-focused prompts ──────────────────────────────────────────
  // These gate on the localhost DETAIL section keys ('cve', 'windows_security',
  // 'breach', 'browser_passwords', 'extensions', 'hardware') rather than the
  // all-or-nothing 'localhost'. A localhost-only scan previously surfaced almost
  // nothing under Technical, and the few prompts that did show promised Active
  // Directory and network content the scan did not contain.
  {
    label: "Vulnerability & CVE Analysis",
    scope: "Technical",
    orientation: "portrait",
    prompt: "Technical vulnerability report from the CVE data. Open with total CVE count, the breakdown by severity, and the overall risk level. Then a detailed table of the highest-CVSS vulnerabilities — CVE ID, CVSS score, severity, affected component and version, whether a public exploit exists, and whether a patch/KB is available. Where several machines were scanned, attribute each vulnerability to its device and include a per-device comparison of CVE counts. Close with a remediation sequence that puts exploitable and highest-CVSS issues first. Cite real CVE IDs and counts from the scan.",
    description: "CVE inventory by severity, top vulnerabilities, and a remediation order.",
    requires: ["cve"]
  },
  {
    label: "Vulnerability Exposure (Executive)",
    scope: "Executive",
    orientation: "portrait",
    prompt: "Executive report on vulnerability exposure. Lead with the scale of the problem — how many known vulnerabilities are unresolved, how many are critical or high severity, and what that means commercially in terms of ransomware and breach likelihood. Note how the exposure is distributed across the scanned machines. Avoid CVE-by-CVE detail; give leadership the size of the backlog, the trend it implies about patching process, and a short prioritised remediation plan. Use real counts from the scan.",
    description: "Business-level view of the vulnerability backlog and what it implies.",
    requires: ["cve"]
  },
  {
    label: "Endpoint Security Posture",
    scope: "Technical",
    orientation: "portrait",
    prompt: "Technical report on endpoint protection status for every scanned machine. For each device report: antivirus/Defender enabled state, real-time protection, behaviour monitoring, tamper protection, signature version and age, last scan age, firewall status per profile (domain/private/public), and Windows update status. Present this as a per-device table so gaps are obvious at a glance, then call out specifically which machines are unprotected or running stale definitions. Cite the real device names and values from the scan.",
    description: "Per-device antivirus, firewall, tamper protection, and update status.",
    requires: ["windows_security", "hardware"]
  },
  {
    label: "Credential & Breach Exposure",
    scope: "Technical",
    orientation: "portrait",
    prompt: "Technical report on credential exposure from the endpoint scan. Cover passwords stored in browsers — how many, on which devices, and any weak or re-used credentials with the reuse counts — and email addresses found in known third-party breaches, naming the addresses and the breaches where the data supports it. Separate 'stored insecurely' from 'known breached' clearly, since the remediation differs. Close with concrete remediation: which credentials to rotate first and what to change about password storage practice.",
    description: "Browser-stored passwords, re-use, and known-breach exposure by account.",
    requires: ["breach", "browser_passwords"]
  },
  {
    label: "Browser Extensions & Asset Inventory",
    scope: "Technical",
    orientation: "portrait",
    prompt: "Technical report covering installed browser extensions and endpoint asset inventory. List the extensions found per device, highlighting any with broad permissions or that look like unmanaged/shadow IT. Then give a hardware and operating-system inventory table — manufacturer, model, OS edition and build, memory, CPU, and device age — flagging machines on unsupported or outdated OS builds and any nearing end of serviceable life. Cite real device names, extension names, and OS versions from the scan.",
    description: "Installed extensions plus hardware/OS inventory and ageing devices.",
    requires: ["extensions", "hardware"]
  },
  {
    label: "Multi-Device Fleet Summary",
    scope: "Technical",
    orientation: "landscape",
    prompt: "Landscape technical summary comparing every scanned machine side by side. Build a per-device comparison table covering operating system, vulnerability counts by severity, endpoint protection state, stored-credential count, and browser extension count. Use the wide layout to keep all devices visible in one view, then call out which machines are the weakest links and why. Cite the real device names from the scan.",
    description: "Side-by-side comparison of every scanned device and its weak points.",
    requires: ["localhost"]
  },
  {
    label: "Endpoint Risk Overview",
    scope: "Executive",
    orientation: "landscape",
    prompt: "Wide executive overview of endpoint risk across the scanned machines. Use the horizontal space for at-a-glance charts: vulnerability severity distribution, endpoint protection coverage, and credential exposure. Follow with the top endpoint findings expressed as business risk, and a short prioritised action list. Keep it board-readable and cite real counts.",
    description: "At-a-glance endpoint risk charts with business impact and actions.",
    requires: ["localhost"]
  },

  // ── Always-available fallbacks ────────────────────────────────────────
  // Ungated (no "requires"), so the recommendation list is never empty regardless of
  // what the scan contains. Technical previously had no ungated prompt in either
  // orientation, which is how that quadrant could show "none match this data".
  {
    label: "General Security Assessment",
    scope: "Technical",
    orientation: "portrait",
    prompt: "Produce a technical security assessment from whatever data this scan contains. Begin by stating which areas were assessed, then work through each one that has data — reporting the real findings, values, and affected assets in detailed tables. Explicitly skip any area with no data rather than speculating about it, and note at the end which areas were not covered by this scan so the reader knows the limits of the assessment. Cite real values throughout; never invent findings.",
    description: "Adapts to whatever the scan contains; states what was not assessed.",
  },
  {
    label: "General Security Assessment (Wide)",
    scope: "Technical",
    orientation: "landscape",
    prompt: "Produce a technical security assessment in landscape format from whatever data this scan contains. Use the wide layout to place related tables side by side. Cover every area that has data, reporting real findings and affected assets, and explicitly skip areas with no data instead of speculating. Close by noting which areas this scan did not cover. Cite real values throughout; never invent findings.",
    description: "Wide-format assessment adapting to the available scan data.",
  },
  {
    label: "General Security Assessment (Executive)",
    scope: "Executive",
    orientation: "landscape",
    prompt: "Produce an executive security assessment in landscape format from whatever data this scan contains. Open with an overall posture scorecard, then summarise each assessed area in business-risk terms with supporting charts across the wide layout. Skip areas with no data rather than speculating, and state plainly at the end what this scan did not cover. Keep it board-readable and cite real counts.",
    description: "Board-level wide summary that adapts to the available data.",
  }
];

// Sections that can stand in for a required one. The scanner's multi-device envelope
// surfaces as the top-level key `localhosts`; normalizeScanData merges it into `localhost`
// server-side, but a payload that reaches the profiler un-normalized (e.g. an older
// backend build) would otherwise hide every localhost-gated prompt.
const SECTION_KEY_ALIASES = {
  localhost: ['localhost', 'localhosts']
};

// Does this prompt have real supporting data in the profiled scan? No "requires" (or an
// unprofiled/loading state) means "don't gate it" — always considered available.
function promptHasSupportingData(prompt, profile) {
  if (!prompt.requires || prompt.requires.length === 0) return true;
  if (!profile || !Array.isArray(profile.sections)) return true;
  return prompt.requires.some(sectionKey => {
    const acceptable = SECTION_KEY_ALIASES[sectionKey] || [sectionKey];
    return profile.sections.some(s => acceptable.includes(s.key) && s.hasData);
  });
}

const PipsqueakReportBuilder = ({
  scanData = {},
  filePath = null, // File path for backend export
  onSaveTemplate,
  onExportReport,
  initialTemplate = null,
  initialMetricsData = null,
  mode = null, // 'template' or 'report' - passed from route state
  mspInfo = null, // MSP details from user profile
  clientInfo = null, // Client details from organization
  readOnly = false, // Restrict editing in Report Builder (e.g. for Community users)
  autoOpenAI = false // Auto-open the "Generate with AI" dialog on a blank canvas (from template selection)
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  // Same builder in both interfaces; v2 only swaps the frame around it (BuilderChromeV2).
  const { uiVersion } = useUiVersion();
  const isV2 = uiVersion === 'v2';
  // v2 follows the user's theme for the frame only; the report pages stay white (see
  // BuilderV2.css). v1 never read the theme here and still does not.
  const { isDarkMode } = useTheme();
  // Bootstrap form controls in the panels read Bootstrap's palette, not ours. Set on the
  // panels only, never the root: widgets on the page use Bootstrap too, and must render
  // (and export) light.
  const chromeBsTheme = isV2 ? (isDarkMode ? 'dark' : 'light') : undefined;
  // react-bootstrap modals portal to <body>, outside the builder root, so they need the
  // theme handed to them directly.
  const modalThemeProps = isV2
    ? { className: `rb2-modal v2-tokens${isDarkMode ? ' dark' : ''}`, 'data-bs-theme': chromeBsTheme }
    : {};

  const [pages, setPages] = useState(initialTemplate?.pages || [{ id: 'page-1', name: 'Page 1', blocks: [] }]);
  const [currentTemplateId, setCurrentTemplateId] = useState(initialTemplate?.id || null);
  const [currentPageId, setCurrentPageId] = useState('page-1');
  // Orientation state
  const [orientation, setOrientation] = useState(initialTemplate?.orientation || 'portrait');

  // Derived grid configuration based on orientation
  const gridConfig = useMemo(() => {
    return orientation === 'landscape' ? LANDSCAPE_CONFIG : PORTRAIT_CONFIG;
  }, [orientation]);

  const [selectedBlock, setSelectedBlock] = useState(null);
  const [selectedBlocks, setSelectedBlocks] = useState([]); // Multi-select for custom widget saving

  const [clipboardBlock, setClipboardBlock] = useState(null);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, blockId: null });
  const [propertiesVisible, setPropertiesVisible] = useState(false);
  const [activeSidebarTab, setActiveSidebarTab] = useState('widgets');
  const [showGrid, setShowGrid] = useState(true);
  const [editingBlocks, setEditingBlocks] = useState(new Set());
  const [isExporting, setIsExporting] = useState(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  // Track which placeholder pages the user has manually force-shown (session only, not persisted)
  const [visiblePlaceholderIds, setVisiblePlaceholderIds] = useState(new Set());
  const [savingTemplate, setSavingTemplate] = useState(false);
  // CRITICAL: Initialize templateName from initialTemplate if available
  const [templateName, setTemplateName] = useState(initialTemplate?.name || initialTemplate?.template_name || '');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showAIBuildDialog, setShowAIBuildDialog] = useState(false);
  const [aiBuildMode, setAIBuildMode] = useState(mode === 'template' ? 'template' : 'report');
  const [aiGoal, setAIGoal] = useState('');
  const [aiScope, setAIScope] = useState('Technical');
  const [aiOrientation, setAIOrientation] = useState('portrait');
  // AI Build UX: the user picks one of the Recommended Prompts for the current Scope +
  // Page Orientation (no free-text chatbot box by default). "Create your own prompt"
  // reveals a textarea for a custom goal instead.
  const [aiCustomPromptMode, setAICustomPromptMode] = useState(false);
  // Which recommended-prompt card currently has its full prompt text expanded (keyed by
  // "scope|orientation|label" since labels can repeat across scope/orientation combos).
  const [expandedPromptKey, setExpandedPromptKey] = useState(null);
  const isPremadePrompt = useCallback((text) => PREMADE_PROMPTS.some(p => p.prompt === text), []);
  // Tracks whether the current builder content was produced by AI Build, so the user's
  // single manual save carries the AI tag and the regenerate request. Initialized from a
  // loaded AI template's metadata.
  const [aiReportMeta, setAiReportMeta] = useState(() => {
    const m = initialTemplate?.metadata || initialTemplate?.configuration?.visualTemplate?.metadata;
    return m?.aiGenerated ? { aiGenerated: true, goal: m.goal || '', scope: m.scope || '', orientation: m.orientation || '' } : null;
  });
  const [aiMessages, setAiMessages] = useState([]);
  const [aiMessagesLoading, setAiMessagesLoading] = useState(false);
  const chatBottomRef = useRef(null);

  const [aiAllowFullData, setAIAllowFullData] = useState(true);
  const [selectedPreset, setSelectedPreset] = useState('default');
  const [customPrimary, setCustomPrimary] = useState('#093070');
  const [customSecondary, setCustomSecondary] = useState('#6fca42');
  const [customAccent, setCustomAccent] = useState('#fdbc13');
  // Tracks whether the user manually picked a palette, so we don't override their
  // choice when the tenant's brand colors arrive asynchronously from mspInfo.
  const presetUserPickedRef = useRef(false);

  // Build a "Brand Colors" preset from the tenant's configured brand colors (set in
  // Admin Panel → Tenant). The AI builder uses 3 colors (primary/secondary/accent);
  // the brand palette maps its first three ordered colors to those roles. Extra
  // brand colors are kept for reference but the 3-way palette drives the AI report.
  const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
  const brandPreset = useMemo(() => {
    const colors = Array.isArray(mspInfo?.brand_colors)
      ? mspInfo.brand_colors.filter(c => typeof c === 'string' && HEX_RE.test(c.trim())).map(c => c.trim())
      : [];
    if (colors.length === 0) return null;
    return {
      id: 'brand',
      name: 'Brand Colors (Default)',
      primary: colors[0],
      secondary: colors[1] || colors[0],
      accent: colors[2] || colors[1] || colors[0]
    };
  }, [mspInfo]);

  // The brand preset (if any) is offered first and selected by default.
  const effectivePresets = useMemo(
    () => (brandPreset ? [brandPreset, ...PALETTE_PRESETS] : PALETTE_PRESETS),
    [brandPreset]
  );

  // Default to the tenant's brand palette once it's available, unless the user has
  // already picked a palette themselves.
  useEffect(() => {
    if (brandPreset && !presetUserPickedRef.current) {
      setSelectedPreset('brand');
      setCustomPrimary(brandPreset.primary);
      setCustomSecondary(brandPreset.secondary);
      setCustomAccent(brandPreset.accent);
    }
  }, [brandPreset]);
  const [aiLoading, setAILoading] = useState(false);
  const [aiProfiling, setAIProfiling] = useState(false);
  const [aiProfile, setAIProfile] = useState(null);
  const [aiError, setAIError] = useState('');
  const [aiOmittedSections, setAIOmittedSections] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [exportProgress, setExportProgress] = useState({ current: 0, total: 0 });
  const [showWrittenReportModal, setShowWrittenReportModal] = useState(false);
  const [writtenReportHtml, setWrittenReportHtml] = useState('');
  const [writtenReportLoading, setWrittenReportLoading] = useState(false);
  const [writtenReportError, setWrittenReportError] = useState('');
  const [writtenReportClientName, setWrittenReportClientName] = useState('');
  const [writtenReportAudience, setWrittenReportAudience] = useState('Executive');

  const [aiStatus, setAiStatus] = useState({
    enabled: false,
    model: 'gpt-5.4',
    loading: true,
    isTenantKey: false,
    promptsRemaining: undefined,
    promptLimit: undefined,
    quotaExceeded: false
  });

  useEffect(() => {
    if (!showAIBuildDialog) return;
    let cancelled = false;
    const fetchStatus = async () => {
      setAiStatus(prev => ({ ...prev, loading: true }));
      try {
        const token = localStorage.getItem('token');
        const response = await apifetch('/s3/api/ai-report-builder/status', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();
        // AI Reports is a plan feature (Global Admin → Scan Modules → AI Reports). A plan
        // without it answers 403 feature_locked; left unhandled the dialog sat on
        // "Checking AI configuration..." for ever.
        if (!response.ok && data?.code === 'feature_locked' && !cancelled) {
          setAiStatus({
            enabled: false, planLocked: true, canUpgrade: !!data.upgrade,
            model: 'gpt-5.4', isTenantKey: false, promptsRemaining: undefined,
            promptLimit: undefined, quotaExceeded: false, loading: false
          });
          return;
        }
        if (!response.ok && !cancelled) {
          setAiStatus(prev => ({ ...prev, enabled: false, loading: false }));
          return;
        }
        if (response.ok && !cancelled) {
          setAiStatus({
            enabled: !!data.enabled,
            model: data.model || 'gpt-5.4',
            isTenantKey: !!data.isTenantKey,
            promptsRemaining: data.promptsRemaining,
            promptLimit: data.promptLimit,
            quotaExceeded: !!data.quotaExceeded,
            loading: false
          });
        }
      } catch (err) {
        console.error('Failed to load AI status:', err);
        if (!cancelled) {
          setAiStatus({
            enabled: false,
            model: 'gpt-5.4',
            isTenantKey: false,
            promptsRemaining: undefined,
            promptLimit: undefined,
            quotaExceeded: false,
            loading: false
          });
        }
      }
    };
    fetchStatus();
    return () => {
      cancelled = true;
    };
  }, [showAIBuildDialog]);

  useEffect(() => {
    if (showAIBuildDialog && filePath) {
      const fetchAiConversation = async () => {
        setAiMessagesLoading(true);
        try {
          const token = localStorage.getItem('token');
          const response = await apifetch(`/s3/api/ai-report-builder/conversation?fileUrl=${encodeURIComponent(filePath)}`, {
            method: 'GET',
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            }
          });
          if (response.ok) {
            const data = await response.json();
            setAiMessages(data.messages || []);
          }
        } catch (error) {
          console.error('[AI Conversation] Fetch failed:', error);
        } finally {
          setAiMessagesLoading(false);
        }
      };
      fetchAiConversation();
    }
  }, [showAIBuildDialog, filePath]);

  useEffect(() => {
    if (chatBottomRef.current) {
      chatBottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [aiMessages, aiLoading]);



  // Export queue from global context (persists across page navigation)
  const { addExport, processBackgroundExport: processExportGlobal } = useExportQueue();
  const [theme, setTheme] = useState(initialTemplate?.theme || {
    primaryColor: '#3b82f6',
    secondaryColor: '#1e40af',
    logo: null,
    header: 'Pipsqueak Security Report',
    footer: 'Confidential - Generated by Pipsqueak',
    companyName: '',
    watermark: false,
    statusColors: {
      green: '#40B657',
      yellow: '#FCC419',
      red: '#FF0000'
    },
    // Document-level page numbering. Off by default. format: 'page_x' | 'x_of_y' | 'x';
    // position: 'left' | 'center' | 'right'. The cover page is included in the count.
    pageNumbers: {
      enabled: false,
      format: 'page_x',
      position: 'center'
    }
  });
  const [metricsData, setMetricsData] = useState(initialMetricsData);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState(null);
  const propertiesManuallyClosed = useRef(false);
  const pagesBlockCountRef = useRef(0);

  const [globalVariables, setGlobalVariables] = useState(
    initialTemplate?.global_variables || getDefaultVariables()
  );
  const [showGlobalVariablesPanel, setShowGlobalVariablesPanel] = useState(false);
  const [showStatusColorsModal, setShowStatusColorsModal] = useState(false);

  // Custom Widgets: reusable block snapshots saved per-template
  const [customWidgets, setCustomWidgets] = useState(initialTemplate?.customWidgets || []);
  const [showSaveCustomWidgetModal, setShowSaveCustomWidgetModal] = useState(false);
  const [customWidgetSaveTarget, setCustomWidgetSaveTarget] = useState(null); // blockId being saved

  // Dynamic Scan Groups State
  const [scanGroups, setScanGroups] = useState([]);
  const [scanGroupsLoading, setScanGroupsLoading] = useState(false);

  // Dynamic Categories (Thresholds) Metadata
  const [allCategories, setAllCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // Dynamic Category Map based on Scan Groups from DB
  const dynamicCategoryMap = useMemo(() => {
    const map = {};
    scanGroups.forEach(group => {
      if (group.label && group.key) {
        map[group.label] = group.key;
      }
    });
    return map;
  }, [scanGroups]);

  // Dynamic widgetTypes with updated category names
  const widgetTypes = useMemo(() => {
    return STATIC_WIDGET_TYPES.map(widget => {
      // Find the scan group that matches this widget's category logic
      // We map the static category names to the dynamic labels from DB
      const categoryKeyMap = {
        'Active Directory': 'AD Scan',
        'Microsoft 365': 'MS365 Scan',
        'Localhost': 'Localhost Scan',
        'Network': 'Network Scan',
        'Personally Identifiable Information': 'PII Scan',
        'External Risk Scan': 'External Scan',
        'Infrastructure': 'Infrastructure',
        'Managed IT / Service Experience': 'Managed IT/Service Experience'
      };

      const groupKey = categoryKeyMap[widget.category];
      if (groupKey) {
        const group = scanGroups.find(g => g.key === groupKey);
        if (group) {
          return { ...widget, category: group.label };
        }
      }
      return widget;
    });
  }, [scanGroups]);

  // Fetch scan groups and categories for dynamic labels and configuration
  useEffect(() => {
    const fetchMetadata = async () => {
      setScanGroupsLoading(true);
      setCategoriesLoading(true);
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('Admintoken');
        const [groupsRes, catsRes] = await Promise.all([
          axios.get('/s1/api/thresholds/scan-groups', {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get('/s1/api/thresholds/categories', {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        if (groupsRes.data.success) {
          setScanGroups(groupsRes.data.data);
          console.log('[PipsqueakReportBuilder] Fetched dynamic scan groups:', groupsRes.data.data.length);
        }

        if (catsRes.data.success) {
          setAllCategories(catsRes.data.data);
          console.log('[PipsqueakReportBuilder] Fetched dynamic categories:', catsRes.data.data.length);
        }
      } catch (error) {
        console.error('[PipsqueakReportBuilder] Error fetching metadata:', error);
      } finally {
        setScanGroupsLoading(false);
        setCategoriesLoading(false);
      }
    };

    fetchMetadata();
  }, []);

  // Preview data for template mode - shows example values instead of placeholders
  const previewData = {
    organizationName: 'Acme Corporation',
    scanDate: new Date().toLocaleDateString('en-GB'),
    mspInfo: {
      name: 'John Smith',
      tenant_logo: null, // Will show placeholder in template mode
      logo: null
    },
    clientInfo: {
      name: 'Acme Corporation',
      email: 'contact@acmecorp.com',
      phone: '+1 (555) 123-4567',
      domain: 'acmecorp.com'
    }
  };


  // Widget Groups State - for grouping related widgets that should stay together
  // Format: { groupId: { leaderId, followerIds: [], name } }
  const [widgetGroups, setWidgetGroups] = useState(
    initialTemplate?.widget_groups || {}
  );

  // Builder Mode: 'template' (create reusable template) or 'report' (create one-time report with scan data)
  const [builderMode, setBuilderMode] = useState(() => {
    // Priority 1: Explicit mode from props
    if (mode) return mode;
    // Priority 2: Mode from query param (robust)
    const searchParams = new URLSearchParams(window.location.search); // use window.location directly or parse location.search
    if (searchParams.get('mode')) return searchParams.get('mode');
    // Priority 3: Mode from navigation state (legacy/backup)
    if (location?.state?.mode) return location.state.mode;
    // Priority 4: Mode from initialTemplate metadata
    if (initialTemplate?.metadata?.mode) return initialTemplate.metadata.mode;
    // Priority 3: Detect based on scan data availability
    // If scanData exists and has content, assume report mode
    const hasScanData = scanData && Object.keys(scanData).length > 0;
    return hasScanData ? 'report' : 'template';
  });

  // Sync builderMode state with mode prop if it changes
  useEffect(() => {
    if (mode) {
      setBuilderMode(mode);
    }
  }, [mode]);

  useEffect(() => {
    setAIBuildMode(builderMode === 'template' ? 'template' : 'report');
  }, [builderMode]);

  // CRITICAL: Sync template details when initialTemplate loads/updates
  useEffect(() => {
    if (initialTemplate) {
      if (initialTemplate.id) {
        setCurrentTemplateId(initialTemplate.id);
        console.log('[PipsqueakReportBuilder] Set currentTemplateId:', initialTemplate.id);
      }
      if (initialTemplate.name || initialTemplate.template_name) {
        setTemplateName(initialTemplate.name || initialTemplate.template_name);
      }
    }
  }, [initialTemplate]);

  // Robust derived mode check to ensure UI updates even if state is stale
  // This explicitly handles the "Create Custom Template" case where scanData is empty
  const isTemplateMode = (mode === 'template') ||
    (builderMode === 'template') ||
    (!scanData || Object.keys(scanData).length === 0);

  console.log('[PipsqueakReportBuilder] Mode Debug:', {
    propMode: mode,
    stateMode: builderMode,
    hasScanData: scanData && Object.keys(scanData).length > 0,
    isTemplateMode: isTemplateMode
  });

  // CRITICAL: Inject CSS custom properties for live variable updates
  // This makes variables available globally via CSS var(--variable-name)
  useEffect(() => {
    if (!globalVariables?.variables) return;

    // Get or create style element for variables
    let styleEl = document.getElementById('global-variables-styles');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'global-variables-styles';
      document.head.appendChild(styleEl);
    }

    // Generate CSS custom properties
    const cssVars = globalVariables.variables
      .map(v => `  ${v.name}: ${v.value};`)
      .join('\n');

    // Inject into :root
    styleEl.textContent = `:root {\n${cssVars}\n}`;

    console.log('[GlobalVariables] Injected CSS custom properties:', globalVariables.variables.length);
  }, [globalVariables]);

  // CRITICAL: In template mode, use SAMPLE DATA instead of real scan data
  // This allows users to see how widgets look and customize styling
  // while creating truly reusable templates
  const sampleData = useMemo(() => generateSampleScanData(), []);
  const templateModeScanData = (builderMode === 'template' && (!scanData || Object.keys(scanData).length === 0)) ? sampleData : scanData;


  const pageContainerRefs = useRef({});
  const scrollUpdateTimeoutRef = useRef(null);
  const currentPageIdRef = useRef(currentPageId);
  const [blockCountTrigger, setBlockCountTrigger] = useState(0);

  // ===== ZOOM STATE =====
  const [zoomLevel, setZoomLevel] = useState(1);
  const handleZoomIn = useCallback(() => setZoomLevel(prev => Math.min(prev + 0.1, 2.0)), []);
  const handleZoomOut = useCallback(() => setZoomLevel(prev => Math.max(prev - 0.1, 0.25)), []);
  const handleZoomReset = useCallback(() => setZoomLevel(1), []);

  // ===== WIDGET DATA REQUIREMENTS REGISTRY =====
  // Maps widget types to their required data paths and validation functions
  // Used to show "No Data" fallback when template is loaded with missing scan types
  const WIDGET_DATA_REQUIREMENTS = useMemo(() => {
    // Helper to get dynamic label for a scan type key
    const getLabel = (key, fallback) => {
      const group = scanGroups.find(g => g.key === key);
      return group ? group.label : fallback;
    };

    const AD_LABEL = getLabel('AD Scan', 'Active Directory');
    const NETWORK_LABEL = getLabel('Network Scan', 'Network Scan');
    const MS365_LABEL = getLabel('MS365', 'Microsoft 365');
    const LOCALHOST_LABEL = getLabel('localhost', 'Localhost');
    const PII_LABEL = getLabel('PII', 'Personally Identifiable Information');
    const EXTERNAL_LABEL = getLabel('External Scan', 'External Risk Scan');

    return {
      // ===== ACTIVE DIRECTORY WIDGETS =====
      'ad-all-users-table': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          return scanDataSource?.["Ad Scan"]?.["AD Users"]?.[0] && Array.isArray(scanDataSource["Ad Scan"]["AD Users"][0]);
        },
        fallbackMessage: 'No Active Directory scan data available. This widget requires AD user data.'
      },
      'ad-inactive-users-table': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const adData = scanDataSource?.["Ad Scan"] || {};
          const inactives = adData?.['inactive_accounts'] || [];
          return Array.isArray(inactives) && inactives.length > 0;
        },
        fallbackMessage: 'No Active Directory scan data available. This widget requires inactive AD user accounts.'
      },
      'ad-all-computers-table': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          return scanDataSource?.["Ad Scan"]?.Computers && Array.isArray(scanDataSource["Ad Scan"].Computers);
        },
        fallbackMessage: 'No Active Directory scan data available. This widget requires AD computer data.'
      },
      'ad-workstation-age-table': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          return scanDataSource?.["Ad Scan"]?.Computers && Array.isArray(scanDataSource["Ad Scan"].Computers);
        },
        fallbackMessage: 'No Active Directory scan data available. This widget requires AD workstation age data.'
      },
      'ad-inactive-computers-table': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const adData = scanDataSource?.["Ad Scan"] || {};
          const inactives = adData?.['inactive_computers'] || [];
          return Array.isArray(inactives) && inactives.length > 0;
        },
        fallbackMessage: 'No Active Directory scan data available. This widget requires inactive AD computer accounts.'
      },
      'ad-groups-risk-table': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          return scanDataSource?.["Ad Scan"]?.groups && Array.isArray(scanDataSource["Ad Scan"].groups);
        },
        fallbackMessage: 'No Active Directory scan data available. This widget requires AD group data.'
      },
      'ad-ous-risk-table': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          return scanDataSource?.["Ad Scan"]?.OU_list && Array.isArray(scanDataSource["Ad Scan"].OU_list);
        },
        fallbackMessage: 'No Active Directory scan data available. This widget requires AD OU data.'
      },
      'ad-fsmo-roles-table': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const adData = scanDataSource?.["Ad Scan"] || {};
          // Check for fsmo_roles in various formats
          const fsmo = adData?.fsmo_roles || adData?.FSMO_roles || adData?.fsmoRoles || adData?.FSMORoles || {};
          return fsmo && typeof fsmo === 'object' && Object.keys(fsmo).length > 0;
        },
        fallbackMessage: 'No Active Directory scan data available. This widget requires FSMO role data.'
      },
      'ad-gpo-analysis-table': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const adData = scanDataSource?.["Ad Scan"] || {};
          const gpos = adData?.["GPO Detail"] || adData?.["GPO_Detail"] || adData?.["gpo_detail"] || adData?.["gpoDetail"] || {};
          return gpos && typeof gpos === 'object' && !Array.isArray(gpos) && Object.keys(gpos).length > 0;
        },
        fallbackMessage: 'No Active Directory scan data available. This widget requires GPO data.'
      },
      // Audit tables from template1.js
      'ad-users-audit-table': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          // Match backend: jsondata["Ad Scan"]["AD Users"] - try both direct array and nested [0]
          const adData = scanDataSource?.["Ad Scan"] || scanDataSource?.['Ad Scan'] || {};
          let users = adData?.["AD Users"] || adData?.['AD Users'];

          // If it's an array with first element being an array, use that (matching ADAllUsersTableWidget)
          if (Array.isArray(users) && users.length > 0 && Array.isArray(users[0])) {
            users = users[0];
          }

          // Fallback: if users is not an array or empty, try [0] pattern
          if (!Array.isArray(users) || users.length === 0) {
            users = adData?.["AD Users"]?.[0] || adData?.['AD Users']?.[0] || [];
          }

          return Array.isArray(users) && users.length > 0;
        },
        fallbackMessage: 'No Active Directory users data available.'
      },
      'ad-computers-audit-table': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const adData = scanDataSource?.["Ad Scan"] || scanDataSource?.['Ad Scan'] || {};
          const computers = adData?.Computers || [];
          return Array.isArray(computers) && computers.length > 0;
        },
        fallbackMessage: 'No Active Directory computers data available.'
      },
      'ad-devices-audit-table': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const adData = scanDataSource?.["Ad Scan"] || scanDataSource?.['Ad Scan'] || {};
          const devices = adData?.["ad_devices"] || {};
          return devices && typeof devices === 'object' && Object.keys(devices).length > 0;
        },
        fallbackMessage: 'No Active Directory devices data available.'
      },
      'ad-groups-audit-table': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const adData = scanDataSource?.["Ad Scan"] || scanDataSource?.['Ad Scan'] || {};
          const groups = adData?.groups || [];
          return Array.isArray(groups) && groups.length > 0;
        },
        fallbackMessage: 'No Active Directory groups data available.'
      },
      'ad-ous-audit-table': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const adData = scanDataSource?.["Ad Scan"] || scanDataSource?.['Ad Scan'] || {};
          const ous = adData?.OU_list || [];
          return Array.isArray(ous) && ous.length > 0;
        },
        fallbackMessage: 'No Active Directory organization units data available.'
      },
      'ad-domain-controllers-audit-table': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const adData = scanDataSource?.["Ad Scan"] || scanDataSource?.['Ad Scan'] || {};
          const domainControllers = adData?.domain_controllers || [];
          return Array.isArray(domainControllers) && domainControllers.length > 0;
        },
        fallbackMessage: 'No Active Directory domain controllers data available.'
      },
      'ad-gpo-policies-audit-table': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const adData = scanDataSource?.["Ad Scan"] || scanDataSource?.['Ad Scan'] || {};
          const policies = adData?.gpo_policies || [];
          return Array.isArray(policies) && policies.length > 0;
        },
        fallbackMessage: 'No Active Directory GPO policies data available.'
      },
      'ad-event-logs-audit-table': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const adData = scanDataSource?.["Ad Scan"] || scanDataSource?.['Ad Scan'] || {};
          const event_log = adData?.event_log || [];
          return Array.isArray(event_log) && event_log.length > 0;
        },
        fallbackMessage: 'No Active Directory event logs data available.'
      },
      'ms365-scan-proof-table': {
        scanType: MS365_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const mfaRecords = scanDataSource?.MS365?.MFA;
          return Array.isArray(mfaRecords) && mfaRecords.length > 0;
        },
        fallbackMessage: 'No Microsoft 365 MFA data available.'
      },
      'microsoft-policies-table': {
        scanType: MS365_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const ms = scanDataSource?.MS365 || {};
          return ms && typeof ms === 'object' && Object.keys(ms).length > 0;
        },
        fallbackMessage: 'No Microsoft 365 policy data available.'
      },
      'localhost-hardware-information-table': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const localhost_sys = scanDataSource?.localhost?.['Hardware Informations'];
          return localhost_sys && typeof localhost_sys === 'object' && Object.keys(localhost_sys).length > 0;
        },
        fallbackMessage: 'No localhost hardware information data available.'
      },
      'localhost-system-information-table': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const localhost = scanDataSource?.localhost || {};
          const devices = localhost['External vulnerability']?.length > 0 ? localhost['External vulnerability'][0] : false;
          return Array.isArray(devices) && devices.length > 0;
        },
        fallbackMessage: 'No localhost system information data available.'
      },
      'localhost-installed-applications-table': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const localhost = scanDataSource?.localhost || {};
          let installed = null;
          const externalVuln = localhost['External vulnerability'];
          if (externalVuln) {
            if (Array.isArray(externalVuln) && externalVuln.length > 0) {
              const localhostDevice = externalVuln[0][0];
              installed = localhostDevice?.installed_softwares || localhostDevice?.installed_applications;
            } else if (typeof externalVuln === 'object') {
              installed = externalVuln.installed_applications || externalVuln.installed_softwares;
            }
          }
          if (!installed) {
            installed = localhost.installed_softwares || localhost.installed_applications;
          }
          if (!installed) return false;
          if (Array.isArray(installed)) return installed.length > 0;
          if (typeof installed === 'object') return Object.keys(installed).length > 0;
          return false;
        },
        fallbackMessage: 'No localhost installed applications data available.'
      },
      'localhost-event-logs-table': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const localhost = scanDataSource?.localhost || {};
          const event_logs = localhost.Analyze_event_logs;
          const event_log = (event_logs || []).length > 0 ? event_logs[0] : false;
          return Array.isArray(event_log) && event_log.length > 0;
        },
        fallbackMessage: 'No localhost event logs data available.'
      },
      'localhost-open-ports-audit-table': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const localhost = scanDataSource?.localhost || {};
          if (!localhost || !localhost['External vulnerability'] || localhost['External vulnerability'].length === 0) return false;
          const localhostDevice = localhost['External vulnerability'][0][0];
          const open_ports = localhostDevice?.open_ports || {};
          return typeof open_ports === 'object' && Object.keys(open_ports).length > 0;
        },
        fallbackMessage: 'No localhost open ports data available.'
      },
      'ad-object-types': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          return scanDataSource?.["Ad Scan"] && Object.keys(scanDataSource["Ad Scan"]).length > 0;
        },
        fallbackMessage: 'No Active Directory scan data available.'
      },
      'ad-objects-pie-chart': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          return scanDataSource?.["Ad Scan"] && Object.keys(scanDataSource["Ad Scan"]).length > 0;
        },
        fallbackMessage: 'No Active Directory scan data available.'
      },
      'fsmo-roles': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          return scanDataSource?.["Ad Scan"]?.FSMO_roles;
        },
        fallbackMessage: 'No FSMO roles data available.'
      },
      'active-inactive-users': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          return scanDataSource?.["Ad Scan"]?.["AD Users"]?.[0];
        },
        fallbackMessage: 'No Active Directory users data available.'
      },
      'password-expiry': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          return scanDataSource?.["Ad Scan"]?.["AD Users"]?.[0];
        },
        fallbackMessage: 'No Active Directory users data available.'
      },
      'active-inactive-computers': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          return scanDataSource?.["Ad Scan"]?.Computers;
        },
        fallbackMessage: 'No Active Directory computers data available.'
      },
      'group-types': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          return scanDataSource?.["Ad Scan"]?.groups;
        },
        fallbackMessage: 'No Active Directory groups data available.'
      },
      'ou-membership': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          return scanDataSource?.["Ad Scan"]?.OU_list;
        },
        fallbackMessage: 'No Active Directory OU data available.'
      },
      'gpo-timeline': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          return scanDataSource?.["Ad Scan"]?.GPO_list;
        },
        fallbackMessage: 'No Active Directory GPO data available.'
      },

      // ===== NETWORK SCAN WIDGETS =====
      // Helper function to extract network devices (matches widget extraction logic)
      'network-backup-status-table': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.backups);
        },
        fallbackMessage: 'No network backup data available. Network devices must have backup information.'
      },
      'network-users-no-expiring-password-table': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.['Local Users'] && Array.isArray(d.system['Local Users']));
        },
        fallbackMessage: 'No network user data available. This widget requires local user accounts from network devices.'
      },
      'network-users-expiring-password-table': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.['Local Users'] && Array.isArray(d.system['Local Users']));
        },
        fallbackMessage: 'No network user data available. This widget requires local user accounts from network devices.'
      },
      'network-os-versions-table': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.['OS']);
        },
        fallbackMessage: 'No network OS data available. This widget requires operating system information from network devices.'
      },
      'network-admins-no-expiring-password-table': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.['Administrators'] && Array.isArray(d.system['Administrators']));
        },
        fallbackMessage: 'No network administrator data available. This widget requires administrator accounts from network devices.'
      },
      'network-admins-expiring-password-table': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.['Administrators'] && Array.isArray(d.system['Administrators']));
        },
        fallbackMessage: 'No network administrator data available.'
      },
      'network-admins-daily-work-table': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.['regular_admins'] && Array.isArray(d.system['regular_admins']));
        },
        fallbackMessage: 'No network regular admins data available.'
      },
      'network-non-default-services-table': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.['Non-Default Services'] && Array.isArray(d.system['Non-Default Services']));
        },
        fallbackMessage: 'No network services data available.'
      },
      'network-all-open-ports-table': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.open_ports && Array.isArray(d.open_ports));
        },
        fallbackMessage: 'No network open ports data available.'
      },
      'network-sensitive-open-ports-table': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.open_ports && Array.isArray(d.open_ports));
        },
        fallbackMessage: 'No network open ports data available.'
      },
      'network-antivirus-status-table': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.Antivirus);
        },
        fallbackMessage: 'No network antivirus data available.'
      },
      'network-encryption-status-table': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.['Logical Disks']);
        },
        fallbackMessage: 'No network encryption data available.'
      },
      'network-encryption-cost-summary': {
        scanType: 'Network/Localhost',
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          const hasNetworkEncryption = devices.some(d => d?.system && d.system !== "WMI Error" && (d?.system?.Encryption || d?.system?.encryption));
          const localhost = scanDataSource?.localhost || scanDataSource?.['Localhost Scan'];
          const hasLocalhostEncryption = !!(
            localhost?.System?.Encryption ||
            localhost?.["Hardware Informations"]?.Encryption ||
            localhost?.Encryption ||
            localhost?.encryption ||
            localhost?.System?.encryption ||
            localhost?.["Hardware Informations"]?.encryption
          );
          return hasNetworkEncryption || !!hasLocalhostEncryption;
        },
        fallbackMessage: 'No network or localhost encryption data available.'
      },
      'network-firewall-status-table': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.Firewall && typeof d.system.Firewall === 'object');
        },
        fallbackMessage: 'No network firewall data available.'
      },
      'network-firewall-status-chart': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.Firewall && typeof d.system.Firewall === 'object');
        },
        fallbackMessage: 'No network firewall data available. This widget requires firewall profile information from network devices.'
      },
      'network-detailed-services-table': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.['Non-Default Services'] && Array.isArray(d.system['Non-Default Services']));
        },
        fallbackMessage: 'No network services data available.'
      },
      'network-services-risk-breakdown-table': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.['Non-Default Services'] && Array.isArray(d.system['Non-Default Services']));
        },
        fallbackMessage: 'No network services data available.'
      },
      'network-high-privilege-services-table': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.['Non-Default Services'] && Array.isArray(d.system['Non-Default Services']));
        },
        fallbackMessage: 'No network services data available.'
      },
      'open-ports-chart': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.open_ports && Array.isArray(d.open_ports));
        },
        fallbackMessage: 'No network open ports data available.'
      },
      'device-type': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          return scanDataSource?.network_devices && Array.isArray(scanDataSource.network_devices) && scanDataSource.network_devices.length > 0;
        },
        fallbackMessage: 'No network devices data available.'
      },
      'network-sensitive-ports': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.open_ports && Array.isArray(d.open_ports));
        },
        fallbackMessage: 'No network open ports data available.'
      },

      // ===== LOCALHOST WIDGETS =====
      'localhost-all-users-password-table': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => data?.localhost?.["Hardware Informations"]?.["Local Users"],
        fallbackMessage: 'No localhost user data available.'
      },
      'localhost-non-expiring-passwords-table': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const localhost = scanDataSource?.localhost || {};
          const hardware = localhost?.["Hardware Informations"] || {};
          const users = hardware?.users || [];
          return Array.isArray(users) && users.length > 0;
        },
        fallbackMessage: 'No localhost user data available.'
      },
      'localhost-disk-usage-table': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const localhost = scanDataSource?.localhost || {};
          const hardware = localhost?.["Hardware Informations"] || {};
          return hardware?.["Logical Disks"];
        },
        fallbackMessage: 'No localhost disk data available.'
      },
      'localhost-firewall-status-table': {
        scanType: 'Auto',
        checkFunction: () => true,
        fallbackMessage: null
      },
      'localhost-installed-software-table': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const localhost = scanDataSource?.localhost || {};
          const hardware = localhost?.["Hardware Informations"] || {};
          return hardware?.["Installed Software"];
        },
        fallbackMessage: 'No localhost software data available.'
      },
      'localhost-non-default-services-table': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const localhost = scanDataSource?.localhost || {};
          const hardware = localhost?.["Hardware Informations"] || {};
          return hardware?.["Non-Default Services"];
        },
        fallbackMessage: 'No localhost services data available.'
      },
      'localhost-services-risk-breakdown-table': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const localhost = scanDataSource?.localhost || scanDataSource?.['Localhost Scan'] || scanDataSource || {};
          const hardware = localhost?.['Hardware Informations'] || {};
          const services = hardware?.['Non-Default Services'];
          return Array.isArray(services) && services.length > 0;
        },
        fallbackMessage: 'No localhost non-default services data available.'
      },
      'localhost-high-privilege-services-table': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const localhost = scanDataSource?.localhost || scanDataSource?.['Localhost Scan'] || scanDataSource || {};
          const hardware = localhost?.['Hardware Informations'] || {};
          const services = hardware?.['Non-Default Services'];
          if (!Array.isArray(services)) return false;
          // Check if there are any high-privilege services (Running with Auto start and high privilege account)
          return services.some(service => {
            const account = service['Start Account'] || service['start_account'] || service['StartAccount'];
            const state = service['State'] || service['state'] || service['Status'] || service['status'];
            const startMode = service['Start Mode'] || service['start_mode'] || service['StartMode'];
            return ["localSystem", "Administrator"].includes(account) &&
              state === 'Running' &&
              startMode === 'Auto';
          });
        },
        fallbackMessage: 'No high-privilege services detected.'
      },
      'localhost-open-ports-table': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const localhost = scanDataSource?.localhost || {};
          return localhost?.open_ports || localhost?.["Hardware Informations"]?.["Open Ports"];
        },
        fallbackMessage: 'No localhost open ports data available.'
      },
      'localhost-browser-passwords-table': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const localhost = scanDataSource?.localhost || {};
          return localhost?.Browser_Password_analysis;
        },
        fallbackMessage: 'No localhost browser password data available.'
      },
      'user-password-status': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const localhost = scanDataSource?.localhost || {};
          const hardware = localhost?.["Hardware Informations"] || {};
          return hardware?.["Local Users"] || hardware?.users;
        },
        fallbackMessage: 'No localhost user data available.'
      },
      'disk-free-used': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const localhost = scanDataSource?.localhost || {};
          const hardware = localhost?.["Hardware Informations"] || {};
          return hardware?.["Logical Disks"];
        },
        fallbackMessage: 'No localhost disk data available.'
      },
      'firewall-status': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const localhost = scanDataSource?.localhost || {};
          const hardware = localhost?.["Hardware Informations"] || {};
          return hardware?.Firewall;
        },
        fallbackMessage: 'No localhost firewall data available.'
      },
      'service-start-modes': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const localhost = scanDataSource?.localhost || {};
          const hardware = localhost?.["Hardware Informations"] || {};
          return hardware?.["Non-Default Services"];
        },
        fallbackMessage: 'No localhost services data available.'
      },
      'admin-password-status': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const localhost = scanDataSource?.localhost || {};
          const hardware = localhost?.["Hardware Informations"] || {};
          return hardware?.Administrators;
        },
        fallbackMessage: 'No localhost administrator data available.'
      },
      'password-strength': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const localhost = scanDataSource?.localhost || {};
          const hardware = localhost?.["Hardware Informations"] || {};
          return hardware?.["Local Users"] || hardware?.users;
        },
        fallbackMessage: 'No localhost user data available.'
      },
      'hibp-breach-status': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const localhost = scanDataSource?.localhost || {};
          return localhost?.HIBP_Scan;
        },
        fallbackMessage: 'No HIBP scan data available.'
      },
      'windows-version': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const localhost = scanDataSource?.localhost || {};
          const hardware = localhost?.["Hardware Informations"] || {};
          return hardware?.OS;
        },
        fallbackMessage: 'No localhost OS data available.'
      },
      'common-software': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const localhost = scanDataSource?.localhost || {};
          const hardware = localhost?.["Hardware Informations"] || {};
          return hardware?.["Installed Software"];
        },
        fallbackMessage: 'No localhost software data available.'
      },
      'hibp-status': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const localhost = scanDataSource?.localhost || {};
          return localhost?.HIBP_Scan;
        },
        fallbackMessage: 'No HIBP scan data available.'
      },
      'rdp-status': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const localhost = scanDataSource?.localhost || {};
          const hardware = localhost?.["Hardware Informations"] || {};
          return hardware?.["RDP Enabled"] !== undefined;
        },
        fallbackMessage: 'No localhost RDP data available.'
      },
      'browser-status': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const localhost = scanDataSource?.localhost || {};
          return localhost?.Browser_Password_analysis;
        },
        fallbackMessage: 'No localhost browser data available.'
      },
      'system-age': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const localhost = scanDataSource?.localhost || {};
          const hardware = localhost?.["Hardware Informations"] || {};
          return hardware?.["Install Date"];
        },
        fallbackMessage: 'No localhost install date data available.'
      },

      // ===== MS365 WIDGETS =====
      'ms365-inactive-users-table': {
        scanType: 'MS365 Scan',
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const ms365 = scanDataSource?.MS365 || scanDataSource?.['Microsoft 365 Scan'] || {};
          const inactive = ms365.inactive_accounts || [];
          return Array.isArray(inactive) && inactive.length > 0;
        },
        fallbackMessage: 'No MS365 scan data available. This widget requires MS365 inactive users data.'
      },
      'ms365-all-users-mfa-table': {
        scanType: 'MS365 Scan',
        checkFunction: (data) => data?.MS365 && Object.keys(data.MS365).length > 0,
        fallbackMessage: 'No MS365 scan data available.'
      },
      'ms365-users-without-mfa-table': {
        scanType: 'MS365 Scan',
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const ms365 = scanDataSource?.MS365 || scanDataSource?.['Microsoft 365 Scan'] || {};
          const mfaUsers = ms365.MFA || [];
          if (!Array.isArray(mfaUsers) || mfaUsers.length === 0) return false;
          // Check if there are any users without MFA
          const hasUsersWithoutMFA = mfaUsers.some(user => {
            const mfaStatus = user["MFA Enabled"] !== undefined
              ? user["MFA Enabled"]
              : user.mfaEnabled !== undefined
                ? user.mfaEnabled
                : user.MFAEnabled !== undefined
                  ? user.MFAEnabled
                  : false;
            return mfaStatus === false;
          });
          return hasUsersWithoutMFA;
        },
        fallbackMessage: 'No MS365 MFA scan data available. This widget requires MS365 users without MFA.'
      },
      'ms365-policy-config-table': {
        scanType: 'MS365 Scan',
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const ms365 = scanDataSource?.MS365 || scanDataSource?.['Microsoft 365 Scan'] || {};
          // Check if at least one policy key exists
          const policyKeys = ["password_policies", "legacy_auth_enabled", "conditional_access_policies", "lack_of_dlp_policies", "misconfigured_exchange_forwarding"];
          return policyKeys.some(key => ms365.hasOwnProperty(key));
        },
        fallbackMessage: 'No MS365 policy configuration data available.'
      },
      'ms365-policy-pie-chart': {
        scanType: 'MS365 Scan',
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const ms365 = scanDataSource?.MS365 || scanDataSource?.['Microsoft 365 Scan'] || {};
          // Check if at least one policy key exists
          const policyKeys = ["password_policies", "legacy_auth_enabled", "conditional_access_policies", "lack_of_dlp_policies", "misconfigured_exchange_forwarding"];
          return policyKeys.some(key => ms365.hasOwnProperty(key));
        },
        fallbackMessage: 'No MS365 policy configuration data available.'
      },
      'inactive-ms-users-summary': {
        scanType: 'MS365 Scan',
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const ms365 = scanDataSource?.MS365 || scanDataSource?.['Microsoft 365 Scan'] || {};
          const users = ms365.users || [];
          return Array.isArray(users) && users.length > 0;
        },
        fallbackMessage: 'No MS365 scan data available.'
      },
      'ms365-mfa-pie-chart': {
        scanType: 'MS365 Scan',
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const ms365 = scanDataSource?.MS365 || scanDataSource?.['Microsoft 365 Scan'] || {};
          const mfaUsers = ms365.MFA || [];
          return Array.isArray(mfaUsers) && mfaUsers.length > 0;
        },
        fallbackMessage: 'No MS365 MFA scan data available.'
      },
      'inactive-ms-users-cost-summary': {
        scanType: 'MS365 Scan',
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const ms365 = scanDataSource?.MS365 || scanDataSource?.['Microsoft 365 Scan'] || {};
          const users = ms365.users || [];
          const inactive = ms365.inactive_accounts || [];
          return Array.isArray(users) && users.length > 0 && Array.isArray(inactive) && inactive.length > 0;
        },
        fallbackMessage: 'No MS365 scan data available. This widget requires MS365 inactive users data.'
      },
      'ms365-users-pie-chart': {
        scanType: 'MS365 Scan',
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const ms365 = scanDataSource?.MS365 || scanDataSource?.['Microsoft 365 Scan'] || {};
          const users = ms365.users || [];
          return Array.isArray(users) && users.length > 0;
        },
        fallbackMessage: 'No MS365 scan data available. This widget requires MS365 user data.'
      },

      // ===== PII WIDGETS =====
      'pii-detection-pie-chart': {
        scanType: 'PII Scan',
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const combinedPii = scanDataSource?.pii ||
            scanDataSource?.PII ||
            scanDataSource?.combinedPii ||
            scanDataSource?.['PII Detection'] ||
            scanDataSource?.['PII'] ||
            {};
          return combinedPii && typeof combinedPii === 'object' && Object.keys(combinedPii).length > 0;
        },
        fallbackMessage: 'No PII scan data available. This widget requires PII detection data.'
      },
      'pii-finding-summary': {
        scanType: 'PII Scan',
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const combinedPii = scanDataSource?.PII ||
            scanDataSource?.['PII'] ||
            scanDataSource?.pii ||
            scanDataSource?.combinedPii ||
            scanDataSource?.['PII Detection'] ||
            {};
          return combinedPii && typeof combinedPii === 'object' && Object.keys(combinedPii).length > 0;
        },
        fallbackMessage: 'No PII scan data available. This widget requires PII detection data.'
      },
      'pii-cost-summary': {
        scanType: 'PII Scan',
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const combinedPii = scanDataSource?.PII ||
            scanDataSource?.['PII'] ||
            scanDataSource?.pii ||
            scanDataSource?.combinedPii ||
            scanDataSource?.['PII Detection'] ||
            {};
          return combinedPii && typeof combinedPii === 'object' && Object.keys(combinedPii).length > 0;
        },
        fallbackMessage: 'No PII scan data available. This widget requires PII detection data.'
      },
      'pii-detected-table': {
        scanType: 'PII Scan',
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const combinedPii = scanDataSource?.pii ||
            scanDataSource?.PII ||
            scanDataSource?.combinedPii ||
            scanDataSource?.['PII Detection'] ||
            scanDataSource?.['PII'] ||
            {};
          return combinedPii && typeof combinedPii === 'object' && Object.keys(combinedPii).length > 0;
        },
        fallbackMessage: 'No PII scan data available.'
      },
      'pii-type-summary-table': {
        scanType: 'PII Scan',
        checkFunction: (data) => data?.PII && Object.keys(data.PII).length > 0,
        fallbackMessage: 'No PII scan data available.'
      },

      // ===== DARK WEB WIDGETS =====
      'hibp-breached-credentials-table': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => data?.localhost?.HIBP_Scan,
        fallbackMessage: 'No HIBP breach data available.'
      },
      'hibp-exposure-by-platform-table': {
        scanType: LOCALHOST_LABEL,
        checkFunction: (data) => data?.localhost?.HIBP_Scan,
        fallbackMessage: 'No HIBP breach data available.'
      },

      // ===== SUMMARY WIDGETS =====
      'backups-summary': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.backups);
        },
        fallbackMessage: 'No backup data available in network scan.'
      },
      'backups-cost-summary': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.backups);
        },
        fallbackMessage: 'No backup data available in network scan.'
      },
      'admins-password-pie-chart': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.['Administrators'] && Array.isArray(d.system['Administrators']));
        },
        fallbackMessage: 'No administrator data available in network scan.'
      },
      'admins-finding-summary': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.['Administrators'] && Array.isArray(d.system['Administrators']));
        },
        fallbackMessage: 'No administrator data available in network scan.'
      },
      'admins-cost-summary': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.['Administrators'] && Array.isArray(d.system['Administrators']));
        },
        fallbackMessage: 'No administrator data available in network scan.'
      },
      'accounts-password-pie-chart': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.['Local Users'] && Array.isArray(d.system['Local Users']));
        },
        fallbackMessage: 'No user accounts data available in network scan.'
      },
      'accounts-finding-summary': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.['Local Users'] && Array.isArray(d.system['Local Users']));
        },
        fallbackMessage: 'No user accounts data available in network scan.'
      },
      'accounts-cost-summary': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.['Local Users'] && Array.isArray(d.system['Local Users']));
        },
        fallbackMessage: 'No user accounts data available in network scan.'
      },
      'network-firewall-findings-summary': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.Firewall && typeof d.system.Firewall === 'object');
        },
        fallbackMessage: 'No firewall data available in network scan.'
      },
      'network-firewall-cost-summary': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.Firewall && typeof d.system.Firewall === 'object');
        },
        fallbackMessage: 'No firewall data available in network scan.'
      },
      'network-service-risk-summary': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.['Non-Default Services'] && Array.isArray(d.system['Non-Default Services']));
        },
        fallbackMessage: 'No services data available in network scan.'
      },
      'network-service-cost-summary': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.['Non-Default Services'] && Array.isArray(d.system['Non-Default Services']));
        },
        fallbackMessage: 'No services data available in network scan.'
      },
      'network-open-ports-cost-summary': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          // open_ports is an object, not an array (keys are port numbers)
          return devices.some(d => d?.open_ports && typeof d.open_ports === 'object' && !Array.isArray(d.open_ports) && Object.keys(d.open_ports).length > 0);
        },
        fallbackMessage: 'No open ports data available in network scan.'
      },
      'ad-computer-cost-summary': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const adData = scanDataSource?.["Ad Scan"] || {};
          const inactives = adData?.['inactive_computers'] || [];
          return Array.isArray(inactives) && inactives.length > 0;
        },
        fallbackMessage: 'No Active Directory computer data available.'
      },
      'ad-users-cost-summary': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const adData = scanDataSource?.["Ad Scan"] || {};
          const inactives = adData?.['inactive_accounts'] || [];
          return Array.isArray(inactives) && inactives.length > 0;
        },
        fallbackMessage: 'No Active Directory user data available.'
      },
      'ou-cost-summary': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const adData = scanDataSource?.["Ad Scan"] || {};
          const ous = adData?.['OU_list'] || [];
          return Array.isArray(ous) && ous.length > 0;
        },
        fallbackMessage: 'No Active Directory OU data available.'
      },
      'ad-groups-cost-summary': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const adData = scanDataSource?.["Ad Scan"] || {};
          const groups = adData?.groups || [];
          return Array.isArray(groups) && groups.length > 0;
        },
        fallbackMessage: 'No Active Directory groups data available.'
      },
      'fsmo-cost-summary': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const adData = scanDataSource?.["Ad Scan"] || {};
          const fsmo = adData?.fsmo_roles || adData?.FSMO_roles || adData?.fsmoRoles || adData?.FSMORoles || {};
          return fsmo && typeof fsmo === 'object' && Object.keys(fsmo).length > 0;
        },
        fallbackMessage: 'No Active Directory scan data available. This widget requires FSMO role data.'
      },
      'gpo-cost-summary': {
        scanType: AD_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const adData = scanDataSource?.["Ad Scan"] || {};
          const gpos = adData?.["GPO Detail"] || adData?.["GPO_Detail"] || adData?.["gpo_detail"] || adData?.["gpoDetail"] || {};
          return gpos && typeof gpos === 'object' && !Array.isArray(gpos) && Object.keys(gpos).length > 0;
        },
        fallbackMessage: 'No Active Directory scan data available. This widget requires GPO data.'
      },
      'os-risk-summary': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.['OS']);
        },
        fallbackMessage: 'No operating system data available in network scan.'
      },
      'os-cost-summary': {
        scanType: NETWORK_LABEL,
        checkFunction: (data) => {
          const scanDataSource = data?.scanData || data?.data || data;
          const devices = scanDataSource?.network_devices || [];
          return devices.some(d => d?.system && d.system !== "WMI Error" && d?.system?.['OS']);
        },
        fallbackMessage: 'No operating system data available in network scan.'
      },


      // ===== EXTERNAL RISK SCAN WIDGETS =====
      'external-risk-score': {
        scanType: EXTERNAL_LABEL,
        checkFunction: (data) => !!(data?.external && Object.keys(data.external).length > 0) || !!(data?.externalData && Object.keys(data.externalData).length > 0) || !!(data?.domain && (data?.categories || data?.riskScore)),
        fallbackMessage: 'No external risk scan data available.'
      },
      'external-domain-info': {
        scanType: EXTERNAL_LABEL,
        checkFunction: (data) => !!(data?.external && Object.keys(data.external).length > 0) || !!(data?.externalData && Object.keys(data.externalData).length > 0) || !!(data?.domain && (data?.categories || data?.riskScore)),
        fallbackMessage: 'No external risk scan data available.'
      },
      'external-risk-overview': {
        scanType: EXTERNAL_LABEL,
        checkFunction: (data) => !!(data?.external && Object.keys(data.external).length > 0) || !!(data?.externalData && Object.keys(data.externalData).length > 0) || !!(data?.domain && (data?.categories || data?.riskScore)),
        fallbackMessage: 'No external risk scan data available.'
      },
      'external-category-breakdown': {
        scanType: EXTERNAL_LABEL,
        checkFunction: (data) => !!(data?.external && Object.keys(data.external).length > 0) || !!(data?.externalData && Object.keys(data.externalData).length > 0) || !!(data?.domain && (data?.categories || data?.riskScore)),
        fallbackMessage: 'No external risk scan data available.'
      },
      'external-all-findings-table': {
        scanType: EXTERNAL_LABEL,
        checkFunction: (data) => !!(data?.external && Object.keys(data.external).length > 0) || !!(data?.externalData && Object.keys(data.externalData).length > 0) || !!(data?.domain && (data?.categories || data?.riskScore)),
        fallbackMessage: 'No external risk scan data available.'
      },
      'external-warnings-table': {
        scanType: EXTERNAL_LABEL,
        checkFunction: (data) => !!(data?.external && Object.keys(data.external).length > 0) || !!(data?.externalData && Object.keys(data.externalData).length > 0) || !!(data?.domain && (data?.categories || data?.riskScore)),
        fallbackMessage: 'No external risk scan data available.'
      },
      'external-passed-table': {
        scanType: EXTERNAL_LABEL,
        checkFunction: (data) => !!(data?.external && Object.keys(data.external).length > 0) || !!(data?.externalData && Object.keys(data.externalData).length > 0) || !!(data?.domain && (data?.categories || data?.riskScore)),
        fallbackMessage: 'No external risk scan data available.'
      },
      'external-severity-pie-chart': {
        scanType: EXTERNAL_LABEL,
        checkFunction: (data) => !!(data?.external && Object.keys(data.external).length > 0) || !!(data?.externalData && Object.keys(data.externalData).length > 0) || !!(data?.domain && (data?.categories || data?.riskScore)),
        fallbackMessage: 'No external risk scan data available.'
      },
      'external-status-pie-chart': {
        scanType: EXTERNAL_LABEL,
        checkFunction: (data) => !!(data?.external && Object.keys(data.external).length > 0) || !!(data?.externalData && Object.keys(data.externalData).length > 0) || !!(data?.domain && (data?.categories || data?.riskScore)),
        fallbackMessage: 'No external risk scan data available.'
      },
      'external-category-bar-chart': {
        scanType: EXTERNAL_LABEL,
        checkFunction: (data) => !!(data?.external && Object.keys(data.external).length > 0) || !!(data?.externalData && Object.keys(data.externalData).length > 0) || !!(data?.domain && (data?.categories || data?.riskScore)),
        fallbackMessage: 'No external risk scan data available.'
      },
      'external-compliance-summary': {
        scanType: EXTERNAL_LABEL,
        checkFunction: (data) => !!(data?.external && Object.keys(data.external).length > 0) || !!(data?.externalData && Object.keys(data.externalData).length > 0) || !!(data?.domain && (data?.categories || data?.riskScore)),
        fallbackMessage: 'No external risk scan data available.'
      },
      'external-compliance-table': {
        scanType: EXTERNAL_LABEL,
        checkFunction: (data) => !!(data?.external && Object.keys(data.external).length > 0) || !!(data?.externalData && Object.keys(data.externalData).length > 0) || !!(data?.domain && (data?.categories || data?.riskScore)),
        fallbackMessage: 'No external risk scan data available.'
      },
      'external-dns-findings': {
        scanType: EXTERNAL_LABEL,
        checkFunction: (data) => !!(data?.external && Object.keys(data.external).length > 0) || !!(data?.externalData && Object.keys(data.externalData).length > 0) || !!(data?.domain && (data?.categories || data?.riskScore)),
        fallbackMessage: 'No external risk scan data available.'
      },
      'external-email-findings': {
        scanType: EXTERNAL_LABEL,
        checkFunction: (data) => !!(data?.external && Object.keys(data.external).length > 0) || !!(data?.externalData && Object.keys(data.externalData).length > 0) || !!(data?.domain && (data?.categories || data?.riskScore)),
        fallbackMessage: 'No external risk scan data available.'
      },
      'external-ssl-findings': {
        scanType: EXTERNAL_LABEL,
        checkFunction: (data) => !!(data?.external && Object.keys(data.external).length > 0) || !!(data?.externalData && Object.keys(data.externalData).length > 0) || !!(data?.domain && (data?.categories || data?.riskScore)),
        fallbackMessage: 'No external risk scan data available.'
      },
      'external-headers-findings': {
        scanType: EXTERNAL_LABEL,
        checkFunction: (data) => !!(data?.external && Object.keys(data.external).length > 0) || !!(data?.externalData && Object.keys(data.externalData).length > 0) || !!(data?.domain && (data?.categories || data?.riskScore)),
        fallbackMessage: 'No external risk scan data available.'
      },
      'external-infrastructure-findings': {
        scanType: EXTERNAL_LABEL,
        checkFunction: (data) => !!(data?.external && Object.keys(data.external).length > 0) || !!(data?.externalData && Object.keys(data.externalData).length > 0) || !!(data?.domain && (data?.categories || data?.riskScore)),
        fallbackMessage: 'No external risk scan data available.'
      },
      'external-breach-findings': {
        scanType: EXTERNAL_LABEL,
        checkFunction: (data) => !!(data?.external && Object.keys(data.external).length > 0) || !!(data?.externalData && Object.keys(data.externalData).length > 0) || !!(data?.domain && (data?.categories || data?.riskScore)),
        fallbackMessage: 'No external risk scan data available.'
      },

      // ===== AUTO WIDGETS (always available - receive full data context) =====
      'findings-status': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'single-finding-status': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'key-findings': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'open-ports': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'findings-grid': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'open-ports-summary': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'darkweb-summary': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'accounts-summary': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'accounts-admin-summary': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'firewall-summary': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'localhost-firewall-findings-summary': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'localhost-firewall-cost-summary': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'localhost-service-risk-summary': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'localhost-service-cost-summary': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'localhost-os-cost-summary': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'localhost-open-ports-cost-summary': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'service-risk-summary': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'overall-risk-score': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'total-cost-calculator': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'infrastructure-scan-grid': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'security-scan-grid': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },

      // ===== GENERIC CHARTS (always available when dataConfig is set) =====
      'numeric': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'bar': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'line': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'pie': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'donut': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'radar': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'polar': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'bubble': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'scatter': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'table': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'gauge': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null },
      'statcard': { scanType: 'Auto', checkFunction: () => true, fallbackMessage: null }
    };
  }, [scanGroups]);

  // Helper function to check if widget has required data available
  const checkWidgetDataAvailability = useCallback((widgetType, data) => {
    const requirements = WIDGET_DATA_REQUIREMENTS[widgetType];

    // If no requirements defined, assume data is available
    if (!requirements) {
      console.log(`[checkWidgetDataAvailability] No requirements defined for: ${widgetType}, assuming available`);
      return { available: true, message: null };
    }

    // Auto widgets always have data
    if (requirements.scanType === 'Auto') {
      return { available: true, message: null };
    }

    // Check if data exists using custom check function
    try {
      const available = requirements.checkFunction(data);
      return {
        available,
        scanType: requirements.scanType,
        message: available ? null : requirements.fallbackMessage
      };
    } catch (error) {
      console.error(`[checkWidgetDataAvailability] Error checking ${widgetType}:`, error);
      return {
        available: false,
        scanType: requirements.scanType,
        message: requirements.fallbackMessage || 'Data unavailable'
      };
    }
  }, [WIDGET_DATA_REQUIREMENTS]);

  useEffect(() => {
    if (initialMetricsData && initialMetricsData !== metricsData) {
      setMetricsData(initialMetricsData);
      setMetricsError(null);
      setMetricsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMetricsData]);

  // Use ref to track last fetched filePath to prevent infinite loops
  const lastFetchedFilePathRef = useRef(null);

  useEffect(() => {
    let isCancelled = false;

    if (!filePath) {
      if (!initialMetricsData) {
        setMetricsData(null);
      }
      setMetricsError(null);
      setMetricsLoading(false);
      lastFetchedFilePathRef.current = null;
      return;
    }

    // Skip if we already fetched for this filePath
    if (lastFetchedFilePathRef.current === filePath) {
      return;
    }

    // Skip if metricsData already exists and matches the filePath
    if (metricsData && metricsData.fileUrl === filePath) {
      lastFetchedFilePathRef.current = filePath;
      return;
    }

    const fetchMetrics = async () => {
      setMetricsLoading(true);
      setMetricsError(null);

      try {
        const token = localStorage.getItem('token');

        // const testFile = '/media/mattiyoo/matthew/Dev-Storage/btsg/Pipsqueak-Network-Scanner/Website/backend/Report Management Service/routes/uploads/15-12-2025_1765826488888_891_9-12-2025-encrypted.json';
        const response = await apifetch('/s3/api/report-metrics', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ fileUrl: filePath })
        });

        let payload = null;
        try {
          payload = await response.json();
        } catch (parseError) {
          payload = null;
        }

        if (!response.ok) {
          const message = payload?.error || 'Failed to load report metrics';
          throw new Error(message);
        }

        if (!isCancelled) {
          setMetricsData(payload);
          lastFetchedFilePathRef.current = filePath;
        }
      } catch (error) {
        if (!isCancelled) {
          setMetricsError(error.message || 'Failed to load report metrics');
          setMetricsData(null);
          lastFetchedFilePathRef.current = null;
        }
      } finally {
        if (!isCancelled) {
          setMetricsLoading(false);
        }
      }
    };

    fetchMetrics();

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filePath, initialMetricsData]); // Removed metricsData from dependencies to prevent infinite loop

  // Get the raw data from the API response or fallback to scanData prop
  // The new simplified API returns { data, fileUrl } where data is the raw JSON
  // CRITICAL: In template mode, use sample data instead of real scan data
  // CRITICAL: Must merge external scan data from scanData.external
  const effectiveScanData = useMemo(() => {
    let baseData = {};

    // PRIORITY 1: Template mode - use sample data (only if no scanData is loaded)
    if (builderMode === 'template' && sampleData && Object.keys(sampleData).length > 0 && (!scanData || Object.keys(scanData).length === 0)) {
      console.log('[ReportBuilder] Using sample data for template mode');
      return sampleData;
    }

    // PRIORITY 2: New API format - metricsData.data contains the raw JSON
    if (metricsData && metricsData.data && typeof metricsData.data === 'object') {
      baseData = { ...metricsData.data };
    }
    // PRIORITY 3: Legacy support - metricsData.scanData
    else if (metricsData && metricsData.scanData && typeof metricsData.scanData === 'object') {
      baseData = { ...metricsData.scanData };
    }
    // PRIORITY 4: Fallback to scanData prop
    else if (scanData && typeof scanData === 'object') {
      baseData = { ...scanData };
    }

    // CRITICAL: Always merge external scan data from scanData.external if it exists
    // This is where ReportBuilderPage stores external risk scan data
    // We must ensure this merge happens LAST and is not overwritten
    if (scanData && scanData.external) {
      console.log('[ReportBuilder] ✅ Merging external scan data from scanData.external (Priority Merge)');
      // If baseData.external already exists (e.g. from metricsData), we merge deeply or overwrite?
      // For now, scanData.external (from file upload) should probably take precedence or be merged
      baseData.external = {
        ...(baseData.external || {}),
        ...scanData.external
      };
    }

    // Also check metricsData.externalData (alternative location)
    if (metricsData && metricsData.externalData && typeof metricsData.externalData === 'object') {
      console.log('[ReportBuilder] ✅ Merging external scan data from metricsData.externalData');
      baseData.external = metricsData.externalData;
    }

    // Collapse the scanner's multi-device envelope ({ localhosts: [...] }) into the same
    // superset shape the backend uses (adds `localhost`, `network_devices`, `_fleet` while
    // keeping every original key). The AI report builder writes its tokens, table
    // sourcePaths, and bindings against the NORMALIZED tree, so rendering against the raw
    // envelope made every live path miss — tokens rendered "N/A"/"0", bound tables and
    // charts rendered "No data to render", and the topology showed "No devices to diagram".
    // Pass-through for payloads that are neither schema.
    const normalized = normalizeScanData(baseData);

    console.log('[ReportBuilder] effectiveScanData keys:', Object.keys(normalized).slice(0, 15));
    console.log('[ReportBuilder] Has external data:', !!normalized.external);

    return normalized;
  }, [builderMode, sampleData, metricsData, scanData]);

  // Effect: Remove widgets if their filters are invalid (referencing fields missing in data)
  // This handles the user request: if a widget relies on a filter (e.g., "system.Antivirus"),
  // and that field is missing in the current scan data, remove the entire widget instead of just ignoring the filter.
  useEffect(() => {
    // Only run in Report Mode with real data
    if (builderMode !== 'report' || !effectiveScanData || Object.keys(effectiveScanData).length === 0) return;

    setPages((prevPages) => {
      let hasChanges = false;
      const newPages = prevPages.map((page) => {
        const initialCount = page.blocks.length;
        // Filter out blocks with invalid filters or missing data dependencies
        const validBlocks = page.blocks.filter((block) => {
          // Standard Chart Types (from lines 592-603)
          const standardChartTypes = [
            'numeric', 'bar', 'line', 'pie', 'donut', 'radar',
            'polar', 'bubble', 'scatter', 'table', 'gauge', 'statcard'
          ];

          // Check Standard Chart widgets for missing data dependencies
          if (
            block.type === 'widget' &&
            standardChartTypes.includes(block.widgetType) &&
            block.dataConfig?.metricPath
          ) {
            // Get available fields for this metric path in current data
            const availableFields = getAvailableFields(
              effectiveScanData,
              block.dataConfig.metricPath
            );

            // CHECK 1: Device Filters
            if (block.dataConfig.deviceFilters?.length > 0) {
              const filtersValid = block.dataConfig.deviceFilters.every((filter) =>
                availableFields.includes(filter.fieldPath)
              );
              if (!filtersValid) {
                console.warn(`[ReportBuilder] Removing block ${block.id} (${block.widgetType}) due to missing device filter fields.`);
                hasChanges = true;
                return false;
              }
            }

            // CHECK 2: Group By / Filter Field (Category)
            // Used in 'count', 'sum', 'average', or explicit filter/group mode
            const usesFilterField =
              ['count', 'sum', 'average'].includes(block.dataConfig.aggregation) ||
              ['group', 'filter'].includes(block.dataConfig.filterMode);

            if (usesFilterField && block.dataConfig.filterField) {
              if (!availableFields.includes(block.dataConfig.filterField)) {
                console.warn(`[ReportBuilder] Removing block ${block.id} (${block.widgetType}) due to missing GroupBy/Filter field: ${block.dataConfig.filterField}`);
                hasChanges = true;
                return false;
              }
            }

            // CHECK 3: Value Field (Y-axis logic)
            // Used in 'sum', 'average', 'direct'
            const usesValueField = ['sum', 'average', 'direct'].includes(block.dataConfig.aggregation);
            if (usesValueField && block.dataConfig.valueField) {
              if (!availableFields.includes(block.dataConfig.valueField)) {
                console.warn(`[ReportBuilder] Removing block ${block.id} (${block.widgetType}) due to missing Value field: ${block.dataConfig.valueField}`);
                hasChanges = true;
                return false;
              }
            }

            // CHECK 4: Label Field (X-axis logic for Direct mode)
            if (block.dataConfig.aggregation === 'direct' && block.dataConfig.labelField) {
              if (!availableFields.includes(block.dataConfig.labelField)) {
                console.warn(`[ReportBuilder] Removing block ${block.id} (${block.widgetType}) due to missing Label field: ${block.dataConfig.labelField}`);
                hasChanges = true;
                return false;
              }
            }

            // CHECK 5: Segments (count_segments)
            if (block.dataConfig.aggregation === 'count_segments' && block.dataConfig.segments?.length > 0) {
              const segmentsValid = block.dataConfig.segments.every(segment =>
                (segment.filters || []).every(f => availableFields.includes(f.fieldPath))
              );
              if (!segmentsValid) {
                console.warn(`[ReportBuilder] Removing block ${block.id} (${block.widgetType}) due to missing Segment filter fields.`);
                hasChanges = true;
                return false;
              }
            }
          }
          return true; // Keep block
        });

        if (validBlocks.length !== initialCount) {
          return { ...page, blocks: validBlocks };
        }
        return page;
      });

      // Only update state if changes occurred to avoid infinite loop
      return hasChanges ? newPages : prevPages;
    });
  }, [effectiveScanData, builderMode]);

  // Filter widgets based on available data when in report mode
  const filteredWidgetTypes = useMemo(() => {
    // We no longer filter widgets based on data availability.
    // Empty states handle missing data gracefully, so all widgets should be selectable.
    return widgetTypes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builderMode, effectiveScanData, widgetTypes]);

  // Compute derived metrics from scan data
  const derivedMetrics = useMemo(() => {
    return computeDerivedMetrics(effectiveScanData);
  }, [effectiveScanData]);

  // Simplified dataContext - just use the raw data from the file
  // The report builder will dynamically discover and use whatever structure is present
  // CRITICAL: Update pages when initialTemplate changes (for async template loading)
  // This ensures that when templates are loaded asynchronously from TemplateSelector,
  // the pages state is updated with the normalized template (including continuation page flags)
  const initialTemplateLoadedRef = useRef(false);
  useEffect(() => {
    // Only update pages if:
    // 1. initialTemplate has pages
    // 2. We haven't loaded it yet (to prevent overwriting user edits)
    if (initialTemplate && initialTemplate.pages && Array.isArray(initialTemplate.pages) && !initialTemplateLoadedRef.current) {
      // Show loading state while patching template
      setIsLoadingTemplate(true);

      // Ensure all continuation pages are properly marked
      const normalizedPages = initialTemplate.pages.map(page => {
        // Check if this page has cloned blocks - if so, it MUST be a continuation page
        const hasClonedBlocks = Array.isArray(page.blocks) && page.blocks.some(b => b.isClonedBlock);
        // Also check page name as fallback indicator (some templates may have continuation pages without proper flags)
        const hasContinuationName = page.name && page.name.toLowerCase().includes('continuation');

        // CRITICAL: If page has cloned blocks OR name indicates continuation, mark as continuation page
        // This ensures continuation pages from templates are always properly marked
        const isContinuationPage = hasClonedBlocks ? true : (page.isContinuationPage || hasContinuationName || false);

        // Find original block ID from cloned blocks if continuationOf is not set
        let continuationOf = page.continuationOf;
        if (isContinuationPage && !continuationOf && hasClonedBlocks) {
          const clonedBlock = page.blocks.find(b => b.isClonedBlock);
          continuationOf = clonedBlock?.originalBlockId || null;
        }

        return {
          ...page,
          // CRITICAL: Always mark as continuation if it has cloned blocks
          isContinuationPage: isContinuationPage,
          continuationOf: continuationOf,
          // Preserve all other properties
          blocks: page.blocks || []
        };
      });

      // CRITICAL: Strip continuation pages from the saved template.
      // They are dynamically regenerated by the pagination logic once the current scan data loads.
      // Only PLACEHOLDER pages (user-designed templates for continuation layout) should be kept.
      // Without this, stale continuation pages cause the placeholder to auto-show even when data doesn't overflow.
      const pagesWithoutStaleContinuations = normalizedPages.filter(page => {
        if (page.isContinuationPage && !page.isPlaceholder) {
          // Check if any cloned block on this page was manually resized by the user
          // If they explicitly resized it on the auto-generated page, we should preserve their effort!
          const hasManualResize = page.blocks.some(b => (b.isClonedBlock || b.isStaticClone) && b.manualResize);
          if (hasManualResize) {
            console.log(`[ReportBuilder] Keeping manually resized continuation page: ${page.name}`);
            return true;
          }
          console.log(`[ReportBuilder] Stripping stale continuation page on load: ${page.name} (${page.id})`);
          return false;
        }
        return true;
      });

      // We no longer filter out widgets that don't have data.
      // The user can now manually include/exclude pages via the export toggle UI instead.
      let finalPages = pagesWithoutStaleContinuations;

      console.log('[ReportBuilder] ✅ Updating pages from initialTemplate:', {
        pageCount: finalPages.length,
        continuationPages: finalPages.filter(p => p.isContinuationPage).length,
        placeholderPages: finalPages.filter(p => p.isPlaceholder).length,
        pages: finalPages.map(p => ({
          id: p.id,
          name: p.name,
          isContinuationPage: p.isContinuationPage,
          isPlaceholder: p.isPlaceholder,
          placeholderForPage: p.placeholderForPage,
          hasClonedBlocks: p.blocks?.some(b => b.isClonedBlock),
          hasPlaceholderWidgets: p.blocks?.some(b => b.isPlaceholderWidget),
          continuationOf: p.continuationOf,
          clonedBlocksCount: p.blocks?.filter(b => b.isClonedBlock).length || 0
        }))
      });

      setPages(finalPages);

      // CRITICAL: Sync other template-level data when the template is loaded
      if (initialTemplate.theme) {
        setTheme(initialTemplate.theme);
        console.log('[ReportBuilder] ✅ Syncing theme from initialTemplate');
      }

      if (initialTemplate.global_variables) {
        setGlobalVariables(initialTemplate.global_variables);
        console.log('[ReportBuilder] ✅ Syncing global variables from initialTemplate');
      }

      if (initialTemplate.widget_groups) {
        setWidgetGroups(initialTemplate.widget_groups);
        console.log('[ReportBuilder] ✅ Syncing widget groups from initialTemplate');
      }

      if (initialTemplate.orientation) {
        setOrientation(initialTemplate.orientation);
        console.log('[ReportBuilder] ✅ Syncing orientation from initialTemplate');
      }

      if (initialTemplate.customWidgets) {
        setCustomWidgets(initialTemplate.customWidgets);
        console.log('[ReportBuilder] ✅ Syncing customWidgets from initialTemplate');
      }

      initialTemplateLoadedRef.current = true;

      // Set current page to first non-continuation page
      const firstRegularPage = finalPages.find(p => !p.isContinuationPage);
      if (firstRegularPage) {
        setCurrentPageId(firstRegularPage.id);
      }

      // Hide loading state after template is patched
      setIsLoadingTemplate(false);
    }
  }, [initialTemplate, builderMode, effectiveScanData, checkWidgetDataAvailability]);

  // Filter widgets from pages when scan data becomes available in report mode
  // This is a SECOND PASS that runs after metrics are fetched
  const secondFilterDoneRef = useRef(false);
  useEffect(() => {
    // Only filter if we're in report mode, have scan data, and pages are already loaded
    // AND we haven't done the second filter yet
    if (builderMode === 'report' && effectiveScanData && pages.length > 0 && initialTemplateLoadedRef.current && !secondFilterDoneRef.current) {
      setPages(prevPages => {
        secondFilterDoneRef.current = true;
        return prevPages;
      });
    }
  }, [builderMode, effectiveScanData, checkWidgetDataAvailability]);

  // Data context for widgets - combines raw scan data with derived metrics
  // This is what gets passed to widgets as the `data` prop
  const dataContext = useMemo(() => {
    const coverDataObj = derivedMetrics.coverData || {};

    // Build context matching backend structure EXACTLY:
    // - Raw scan data (keys: "Ad Scan", "MS365", etc.)
    // - coverData with humanized category names (keys: "Active Directory Scan", etc.)
    // - Top-level access to humanized category names for direct paths
    // - exportData and totalPII
    // - __derived.chartData and __derived.chartMeta
    const context = {
      ...effectiveScanData, // Raw scan data - includes network_devices, "Ad Scan", etc.
      scanData: effectiveScanData, // Alias for widgets
      data: effectiveScanData, // Alias for widgets
    };

    // Add exportData and findings from metricsData if available
    // CRITICAL: In template mode, metricsData is null, so fallback to effectiveScanData
    if (metricsData) {
      if (metricsData.exportData) {
        context.exportData = metricsData.exportData;
      }
      if (metricsData.findings) {
        context.findings = metricsData.findings;
      }
      if (metricsData.totalPII !== undefined) {
        context.totalPII = metricsData.totalPII;
      }
      if (metricsData.coverData) {
        context.coverData = metricsData.coverData;
      }
      if (metricsData.category_thresholds) {
        context.category_thresholds = metricsData.category_thresholds;
      }
      if (metricsData.acceptable_score_threshold !== undefined) {
        context.acceptable_score_threshold = metricsData.acceptable_score_threshold;
      }
    }


    // TEMPLATE MODE FIX: If metricsData doesn't have findings but effectiveScanData does, use it
    // This ensures sample data works in template mode
    if (!context.findings && effectiveScanData?.findings) {
      context.findings = effectiveScanData.findings;
    }

    // Also add top-level access to humanized category names (CRITICAL for path resolution)
    // This allows paths like "Active Directory Scan.Disk" to work directly
    if (coverDataObj && Object.keys(coverDataObj).length > 0) {
      Object.keys(coverDataObj).forEach(categoryName => {
        // Only add if it doesn't conflict with existing keys
        if (!(categoryName in context)) {
          context[categoryName] = coverDataObj[categoryName];
        }
      });
    }

    return context;
  }, [effectiveScanData, derivedMetrics, metricsData, builderMode]);

  const availableMetrics = useMemo(
    () => processScanDataForMetrics(dataContext, derivedMetrics),
    [dataContext, derivedMetrics]
  );
  const currentPage = pages.find(page => page.id === currentPageId);

  // Stable block lookup for the properties panel to prevent unmounting/focus loss during state updates
  const selectedBlockData = useMemo(() => {
    if (!selectedBlock) return null;
    const blockId = selectedBlock.includes('.') ? selectedBlock.split('.')[0] : selectedBlock;

    // 1. Search current page
    let foundBlock = findBlockById(currentPage?.blocks, blockId);

    // 2. Search global headers/footers
    if (!foundBlock && window.__headerElements) {
      foundBlock = findBlockById(window.__headerElements, blockId);
    }
    if (!foundBlock && window.__footerElements) {
      foundBlock = findBlockById(window.__footerElements, blockId);
    }

    // 3. Fallback: Search all pages
    if (!foundBlock && pages) {
      for (const page of pages) {
        foundBlock = findBlockById(page.blocks, blockId);
        if (foundBlock) break;
      }
    }

    return foundBlock;
  }, [selectedBlock, currentPage?.blocks, pages]);

  // Keep ref in sync with state
  useEffect(() => {
    currentPageIdRef.current = currentPageId;
  }, [currentPageId]);


  // Automatically open/close properties panel based on block selection
  // But respect manual closing
  useEffect(() => {
    if (propertiesManuallyClosed.current) {
      // Don't auto-open if user manually closed it
      if (!selectedBlock) {
        propertiesManuallyClosed.current = false; // Reset when block is deselected
      }
      return;
    }

    // Helper to check if block is on current page (including nested)
    const isBlockOnPage = (blocks) => {
      if (!blocks) return false;

      // Use selectedBlockData.id if available because the original block ID might've morphed (e.g., clone ID changes during layout sync)
      const targetId = selectedBlockData?.id || selectedBlock;
      if (!targetId) return false;

      for (const block of blocks) {
        if (block.id === targetId || block.id === selectedBlock || block.originalBlockId === selectedBlock || block.placeholderForWidget === selectedBlock) return true;
        if (block.children && block.children.length > 0) {
          if (isBlockOnPage(block.children)) return true;
        }
      }
      return false;
    };

    if (selectedBlock && isBlockOnPage(currentPage?.blocks) && !readOnly) {
      // Block is selected - open properties panel
      setPropertiesVisible(true);
    } else if (!selectedBlock) {
      // Only auto-close if NO block is manually selected anymore
      setPropertiesVisible(false);
    }
  }, [selectedBlock, selectedBlockData, currentPage, readOnly]);

  useEffect(() => {
    if (initialMetricsData && initialMetricsData !== metricsData) {
      setMetricsData(initialMetricsData);
      setMetricsError(null);
      setMetricsLoading(false);
    }
  }, [initialMetricsData]);

  useEffect(() => {
    let isCancelled = false;

    if (!filePath) {
      if (!initialMetricsData) {
        setMetricsData(null);
      }
      setMetricsError(null);
      setMetricsLoading(false);
      lastFetchedFilePathRef.current = null;
      return;
    }

    // Skip if we already fetched for this filePath
    if (lastFetchedFilePathRef.current === filePath) {
      return;
    }

    // Skip if metricsData already exists and matches the filePath
    if (metricsData && metricsData.fileUrl === filePath) {
      lastFetchedFilePathRef.current = filePath;
      return;
    }

    const fetchMetrics = async () => {
      setMetricsLoading(true);
      setMetricsError(null);

      try {
        const token = localStorage.getItem('token');

        // const testFile = '/media/mattiyoo/matthew/Dev-Storage/btsg/Pipsqueak-Network-Scanner/Website/backend/Report Management Service/routes/uploads/15-12-2025_1765826488888_891_9-12-2025-encrypted.json';
        const response = await apifetch('/s3/api/report-metrics', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ fileUrl: filePath })
        });

        let payload = null;
        try {
          payload = await response.json();
        } catch (parseError) {
          payload = null;
        }

        if (!response.ok) {
          const message = payload?.error || 'Failed to load report metrics';
          throw new Error(message);
        }

        if (!isCancelled) {
          setMetricsData(payload);
          lastFetchedFilePathRef.current = filePath;
        }
      } catch (error) {
        if (!isCancelled) {
          setMetricsError(error.message || 'Failed to load report metrics');
          setMetricsData(null);
          lastFetchedFilePathRef.current = null;
        }
      } finally {
        if (!isCancelled) {
          setMetricsLoading(false);
        }
      }
    };

    fetchMetrics();

    return () => {
      isCancelled = true;
    };
  }, [filePath, initialMetricsData]); // Removed metricsData from dependencies to prevent infinite loop

  const getBlockData = useCallback((block) => {
    if (block.type !== 'widget') return null;

    const { metricPath, aggregation, filters, tableColumns, chartAggregationField } = block.dataConfig;
    const widgetType = block.widgetType;
    const isChartWidget = ['bar', 'line', 'pie', 'donut', 'radar', 'polar', 'bubble', 'scatter'].includes(widgetType);
    const isNumericWidget = ['numeric', 'statcard', 'gauge'].includes(widgetType);

    // Computed binding (AI-generated "recipe"): recompute chart values from the live scan
    // data so saved AI reports update their charts/scores on different scan data — no AI.
    const cb = block.dataConfig?.computedBinding;
    if (cb && isChartWidget) {
      const scan = effectiveScanData || dataContext;
      if (cb.kind === 'scoreItems' && Array.isArray(cb.items)) {
        const labels = cb.items.map(it => it.label);
        const values = cb.items.map(it => {
          const s = it.binding ? evalBindingScore(it.binding, scan) : null;
          return s !== null && s !== undefined ? s : (Number(it.frozenValue) || 0);
        });
        const colors = cb.scoreColored
          ? values.map(v => scoreToStatus(v).bar)
          : (cb.items.every(it => it.color) ? cb.items.map(it => it.color) : undefined);
        return { labels, values, ...(colors ? { colors } : {}), seriesLabel: block.customData?.seriesLabel || block.config?.title || 'Score' };
      }
      if (cb.kind === 'categoryCounts') {
        const res = evalCategoryCounts(cb, scan);
        if (res && res.values.some(v => v > 0)) {
          return { labels: res.labels, values: res.values, ...(cb.frozen?.colors ? { colors: cb.frozen.colors } : {}), seriesLabel: block.config?.title || 'Count' };
        }
        // Fall back to the frozen mix if nothing matched on this scan.
        if (cb.frozen) return { labels: cb.frozen.labels, values: cb.frozen.values, ...(cb.frozen.colors ? { colors: cb.frozen.colors } : {}) };
      }
    }

    // For numeric widgets: NEVER use value from customData - always get it from scan data
    // For other widgets: if customData exists, use it instead of scan data
    if (block.customData && Object.keys(block.customData).length > 0) {
      if (isNumericWidget) {
        // For numeric widgets, we need to get the value from scan data and merge with customData label
        // Skip returning customData directly - we'll merge it below after getting scan data
      } else {
        // For chart widgets or widgets with full customData, use customData directly
        return block.customData;
      }
    }

    // Static data support: AI-computed data injected directly into the block.
    // A block that ALSO carries a metricPath is live-bound: the path wins and these rows
    // are only the fallback used further below when the path resolves to nothing.
    if (block.dataConfig?.staticData && Array.isArray(block.dataConfig.staticData) &&
        !(metricPath && block.widgetType === 'table')) {
      if (block.widgetType === 'table' && tableColumns && tableColumns.length > 0) {
        return processTableData(block.dataConfig.staticData, tableColumns, 'staticData', block.dataConfig.tableSort, null, true);
      }
      if (isChartWidget) {
        // staticData for charts: [{label:'X', value:45}, ...]
        const sd = block.dataConfig.staticData;
        const labelKey = block.dataConfig.filterField || block.dataConfig.labelField || 'label';
        const valueKey = block.dataConfig.valueField || 'value';
        return {
          labels: sd.map(r => String(r[labelKey] ?? r.label ?? r.name ?? '')),
          values: sd.map(r => Number(r[valueKey] ?? r.value ?? r.score ?? 0)),
          colors: sd.map(r => r.color || undefined).filter(Boolean).length === sd.length
            ? sd.map(r => r.color)
            : undefined,
          seriesLabel: block.config?.title || 'Score'
        };
      }
      return block.dataConfig.staticData;
    }

    // Special handling for table widgets with column mappings
    if (block.widgetType === 'table' && tableColumns && tableColumns.length > 0 && metricPath) {
      let data = getDataByPath(dataContext, metricPath, dynamicCategoryMap);

      // AI-generated evidence tables carry both a live metricPath and the rows the AI
      // computed from the scan. If the live path resolves to nothing here, render those
      // rows instead of an empty table — the evidence is real, only the binding missed.
      const isEmptyResolved = !data || (Array.isArray(data) && data.length === 0);
      if (isEmptyResolved && Array.isArray(block.dataConfig?.staticData) && block.dataConfig.staticData.length > 0) {
        return processTableData(block.dataConfig.staticData, tableColumns, 'staticData', block.dataConfig.tableSort, null, true);
      }

      // CRITICAL FIX: If in report mode and data is missing, return NULL to trigger removal/hiding
      // Do NOT return sample data if we are in report mode
      if (!data) {
        if (builderMode === 'report') return null;
        return getSampleData(block.widgetType);
      }

      // Normalize object data to array of {key, value} for filtering and display
      // This allows filtering on "Property" (key) and "Value" (value)
      const isObjectData = data && typeof data === 'object' && !Array.isArray(data);
      if (isObjectData) {
        const TABLE_INTERNAL_FIELDS = ['_parent', '_parentIp', '_parentHostname', '__isPlaceholder__'];
        data = Object.entries(data)
          .filter(([key]) => !TABLE_INTERNAL_FIELDS.includes(key) && !key.startsWith('_'))
          .map(([key, value]) => ({ key, value }));
      }

      // Apply device/parent filters - run BEFORE flatten if no flattenField, or AFTER if flatten is active
      const hasFlatten = !!block.dataConfig.flattenField;
      if (!hasFlatten && block.dataConfig.deviceFilters && block.dataConfig.deviceFilters.length > 0) {
        data = filterTableData(data, block.dataConfig.deviceFilters);
      }

      // CRITICAL FIX: If data becomes empty after filtering...
      // For tables, we MUST let it fall through to processTableData so it can generate headers
      // returning [] here causes it to bypass processTableData and we lose the headers
      if (!data || (Array.isArray(data) && data.length === 0)) {
        if (block.widgetType === 'table') {
          // Let it continue to processTableData which handles empty data by returning { headers: [...], rows: [] }
          // Do nothing here, just don't return early
        } else {
          return [];
        }
      }

      // Apply flatten logic: expand nested array/object fields into individual rows
      const flattenField = block.dataConfig.flattenField;
      if (flattenField && Array.isArray(data)) {
        const flattenedData = [];
        const MAX_FLATTEN_ROWS = 10000; // Safety cap to prevent browser freeze
        let hitLimit = false;
        for (let pi = 0; pi < data.length && !hitLimit; pi++) {
          const parentItem = data[pi];
          const nestedValue = getNestedField(parentItem, flattenField);
          if (nestedValue && typeof nestedValue === 'object') {
            if (Array.isArray(nestedValue)) {
              // Array flatten: each array element becomes a row
              for (let ci = 0; ci < nestedValue.length && !hitLimit; ci++) {
                const childItem = nestedValue[ci];
                if (typeof childItem === 'object' && childItem !== null) {
                  flattenedData.push({ ...parentItem, _flattenedItem: childItem, _flattenedKey: null, _flattenedValue: null });
                } else {
                  flattenedData.push({ ...parentItem, _flattenedItem: null, _flattenedKey: String(childItem), _flattenedValue: childItem });
                }
                if (flattenedData.length >= MAX_FLATTEN_ROWS) { hitLimit = true; }
              }
            } else {
              // Object flatten: each key-value pair becomes a row
              const entries = Object.entries(nestedValue).filter(([k]) => !k.startsWith('_'));
              for (let ei = 0; ei < entries.length && !hitLimit; ei++) {
                const [key, value] = entries[ei];
                flattenedData.push({ ...parentItem, _flattenedItem: typeof value === 'object' ? value : null, _flattenedKey: key, _flattenedValue: value });
                if (flattenedData.length >= MAX_FLATTEN_ROWS) { hitLimit = true; }
              }
            }
          }
          // Skip items with no/empty nested value (they have nothing to flatten)
        }
        data = flattenedData;
        if (hitLimit) {
          console.warn('[getBlockData] Flatten row limit reached:', MAX_FLATTEN_ROWS, 'for field:', flattenField);
        }
        console.log('[getBlockData] Flattened data by field:', flattenField, 'Result:', data.length, 'rows');
      }

      // Apply device filters AFTER flatten (so _flattenedKey/_flattenedValue can be filtered)
      if (hasFlatten && block.dataConfig.deviceFilters && block.dataConfig.deviceFilters.length > 0) {
        data = filterTableData(data, block.dataConfig.deviceFilters);
      }

      // CRITICAL FIX: If data becomes empty after flatten/filtering...
      // For tables, we MUST let it fall through to processTableData so it can generate headers
      if (!data || (Array.isArray(data) && data.length === 0)) {
        if (block.widgetType === 'table') {
          // Let it continue to processTableData
        } else {
          return [];
        }
      }

      // Process table data with column mappings
      // Always pass true for returnEmptyIfNoData to avoid sample data fallback when metricPath is set
      return processTableData(data, tableColumns, metricPath, block.dataConfig.tableSort, block.dataConfig.flattenField, true);
    }

    // Auto widgets (open-ports, key-findings, etc.) need full dataContext, not sample data
    // Include all auto widgets that don't need a data source selection
    const autoWidgets = [
      // Signature Widgets
      'nested-rectangle-findings', 'company-at-a-glance', 'category-score-bar', 'financial-impact', 'category-description',
      // Findings & Security widgets
      'findings-status', 'single-finding-status', 'key-findings', 'open-ports', 'findings-grid',
      'open-ports-summary', 'darkweb-summary', 'accounts-summary',
      'accounts-admin-summary', 'firewall-summary', 'localhost-firewall-findings-summary', 'localhost-firewall-cost-summary', 'localhost-service-risk-summary', 'localhost-service-cost-summary', 'localhost-os-cost-summary', 'localhost-open-ports-cost-summary', 'service-risk-summary',
      'overall-risk-score', 'total-cost-calculator', 'infrastructure-scan-grid',
      'managed-service-experience', 'security-scan-grid', 'cyber-security-threat-exposure', 'combined-scan-grid',
      'advanced-scan-grid',
      // Chart widgets for scans (Localhost, Network, Active Directory)
      'user-password-status', 'disk-free-used', 'firewall-status',
      'service-start-modes', 'admin-password-status', 'password-strength',
      'hibp-breach-status', 'windows-version', 'common-software',
      'hibp-status', 'rdp-status', 'browser-status', 'system-age',
      'open-ports-chart', 'device-type', 'network-sensitive-ports',
      'ad-object-types', 'ad-objects-pie-chart', 'fsmo-roles',
      'active-inactive-users', 'password-expiry', 'active-inactive-computers',
      'group-types', 'ou-membership', 'gpo-timeline',
      // Summary widgets
      'backups-summary', 'backups-cost-summary',
      'admins-password-pie-chart', 'admins-finding-summary', 'admins-cost-summary',
      'accounts-password-pie-chart', 'accounts-finding-summary', 'accounts-cost-summary',
      'network-firewall-findings-summary', 'network-firewall-cost-summary',
      'network-encryption-cost-summary',
      'network-service-risk-summary', 'network-service-cost-summary',
      'network-open-ports-cost-summary',
      'ad-computer-cost-summary', 'ad-users-cost-summary', 'ad-groups-cost-summary', 'ou-cost-summary', 'fsmo-cost-summary', 'gpo-cost-summary',
      'os-risk-summary', 'os-cost-summary',
      // Proof tables (these also don't need data source)
      'ad-all-users-table', 'ad-inactive-users-table', 'ad-all-computers-table', 'ad-workstation-age-table',
      'ad-inactive-computers-table', 'ad-groups-risk-table', 'ad-ous-risk-table',
      'ad-fsmo-roles-table', 'ad-gpo-analysis-table', 'ad-admins-table',
      // Audit tables from template1.js
      'ad-users-audit-table', 'ad-computers-audit-table', 'ad-devices-audit-table',
      'ad-groups-audit-table', 'ad-ous-audit-table', 'ad-domain-controllers-audit-table',
      'ad-gpo-policies-audit-table', 'ad-event-logs-audit-table',
      'ms365-scan-proof-table', 'microsoft-policies-table',
      'localhost-hardware-information-table', 'localhost-system-information-table',
      'localhost-installed-applications-table', 'localhost-event-logs-table',
      'localhost-open-ports-audit-table',
      'network-backup-status-table', 'network-users-no-expiring-password-table',
      'network-users-expiring-password-table', 'network-os-versions-table',
      'network-admins-no-expiring-password-table', 'network-admins-expiring-password-table',
      'network-admins-daily-work-table', 'network-non-default-services-table',
      'network-all-open-ports-table', 'network-sensitive-open-ports-table',
      'network-antivirus-status-table', 'network-encryption-status-table',
      'network-firewall-status-table', 'network-firewall-status-chart', 'network-detailed-services-table',
      'network-services-risk-breakdown-table', 'network-high-privilege-services-table',
      'network-logical-disks-table',
      'software-inventory-table', 'local-administrators-table',
      'localhost-all-users-password-table', 'localhost-non-expiring-passwords-table',
      'localhost-disk-usage-table', 'localhost-firewall-status-table', 'localhost-os-version-table', 'localhost-sensitive-open-ports-table', 'localhost-services-risk-breakdown-table', 'localhost-high-privilege-services-table', 'pii-detected-table',
      'localhost-installed-software-table', 'localhost-non-default-services-table',
      'localhost-open-ports-table', 'localhost-browser-passwords-table',
      'ms365-inactive-users-table', 'ms365-all-users-mfa-table',
      'ms365-users-without-mfa-table', 'ms365-policy-config-table', 'ms365-users-pie-chart',
      'ms365-mfa-pie-chart', 'ms365-policy-pie-chart',
      'inactive-ms-users-summary', 'inactive-ms-users-cost-summary', 'mfa-summary', 'mfa-cost-summary',
      'hibp-breached-credentials-table', 'hibp-exposure-by-platform-table',
      'pii-detection-pie-chart', 'pii-detected-table', 'pii-type-summary-table', 'pii-finding-summary', 'pii-cost-summary',
      // External Risk Scan widgets
      'external-risk-score', 'external-domain-info', 'external-risk-overview',
      'external-category-breakdown', 'external-all-findings-table', 'external-warnings-table',
      'external-passed-table', 'external-severity-pie-chart', 'external-status-pie-chart',
      'external-category-bar-chart', 'external-compliance-summary', 'external-compliance-table',
      'external-dns-findings', 'external-email-findings', 'external-ssl-findings',
      'external-headers-findings', 'external-infrastructure-findings', 'external-breach-findings'
    ]

      ;

    if (!metricPath) {
      if (autoWidgets.includes(block.widgetType)) {
        // Return full dataContext for auto widgets
        return dataContext;
      }
      if (builderMode === 'report') {
        if (block.widgetType === 'table') return [];
        if (isChartWidget) return { labels: [], values: [] };
        return null;
      }
      return getSampleData(block.widgetType);
    }


    // Get data from context (same structure as backend)
    let data = getDataByPath(dataContext, metricPath, dynamicCategoryMap);

    // Filter/Group Logic for charts: Apply filters BEFORE transforming to chart format
    // This allows excluding specific data points e.g. "Unknown"
    // Also skip this if filterMode is 'group' (grouping is handled below/separately or via manual grouping)
    const isGrouping = block.dataConfig?.filterMode === 'group' || (block.dataConfig?.aggregation && block.dataConfig.aggregation !== 'direct');
    // Filter/Group Logic for charts: Apply filters BEFORE transforming to chart format
    // This allows excluding specific data points e.g. "Unknown"
    // Apply filters even if grouping, so we can exclude items before counting
    if (isChartWidget && Array.isArray(data) && block.dataConfig?.deviceFilters?.length > 0) {
      // Reuse the existing filterTableData function which handles complex filters including exclusion
      data = filterTableData(data, block.dataConfig.deviceFilters);
    }

    // For chart widgets, transform array data to chart format (same as backend)
    if (isChartWidget && Array.isArray(data) && data.length > 0) {
      // Handle Count Segments Aggregation (Conditional Counting)
      if (block.dataConfig?.aggregation === 'count_segments' && block.dataConfig.segments?.length > 0) {
        const segments = block.dataConfig.segments;
        const segmentResults = segments.map(segment => {
          const filters = segment.filters || [];
          let matchCount = 0;
          if (filters.length === 0) {
            matchCount = data.length;
          } else {
            const filtered = filterTableData(data, filters);
            matchCount = filtered.length;
          }
          return {
            label: segment.label || `Segment ${segment.id}`,
            value: matchCount,
            color: segment.color || '#3b82f6'
          };
        });

        // Check if all segments have 0 value
        const totalValue = segmentResults.reduce((sum, s) => sum + s.value, 0);

        // CRITICAL FIX: If total value is 0 and we are hiding zero values OR in ANY mode
        // Return structured empty data to show empty chart state
        if (totalValue === 0) {
          console.log('[getBlockData] Returning empty segment chart because total value is 0');
          return {
            labels: segmentResults.map(s => s.label),
            values: segmentResults.map(s => 0),
            colors: segmentResults.map(s => s.color),
            seriesLabel: 'Count'
          };
        }

        return {
          labels: segmentResults.map(s => s.label),
          values: segmentResults.map(s => s.value),
          colors: segmentResults.map(s => s.color),
          seriesLabel: 'Count'
        };
      }

      // Handle Grouping Mode (Count by Category, or Sum/Average of field by Category)
      if (isGrouping || chartAggregationField) {
        const groupField = block.dataConfig.filterField || (chartAggregationField ? chartAggregationField.path : null);
        if (!groupField) {
          // If no group field but grouping is requested, fallback to legacy plotting if possible
          // or continue to next check
        } else {
          const valueField = block.dataConfig?.valueField; // Optional: field to aggregate
          const aggType = block.dataConfig?.aggregation || 'count'; // 'count', 'sum', 'average'

          const groupResults = {}; // { label: { sum: 0, count: 0 } }

          data.forEach(item => {
            if (!item || typeof item !== 'object') return;

            let categories = getNestedField(item, groupField);

            // Handle missing values
            if (categories === undefined || categories === null) {
              categories = ['(Empty)'];
            } else if (!Array.isArray(categories)) {
              categories = [categories];
            }

            // Get value to aggregate
            let valToAgg = 1;
            if (valueField && aggType !== 'count') {
              const rawVal = getNestedField(item, valueField);
              valToAgg = typeof rawVal === 'number' ? rawVal : (parseFloat(String(rawVal)) || 0);
            }

            // Apply to each category (handles multi-select fields if they exist)
            categories.forEach(cat => {
              // Format label: handle booleans and empty strings
              let label = cat;
              if (typeof cat === 'boolean') label = cat ? 'Yes' : 'No';
              else if (cat === '' || cat === null || cat === undefined) label = '(Empty)';
              else if (String(cat) === '[object Object]') label = '(Not Empty/Has Value)';
              else label = String(cat).trim();

              if (!groupResults[label]) {
                groupResults[label] = { sum: 0, count: 0 };
              }
              groupResults[label].sum += valToAgg;
              groupResults[label].count += 1;
            });
          });

          // Convert to arrays and apply final aggregation logic
          let processedData = Object.entries(groupResults).map(([label, stats]) => {
            let value = stats.count; // Default for count
            if (aggType === 'sum') value = stats.sum;
            else if (aggType === 'average') value = stats.count > 0 ? stats.sum / stats.count : 0;

            return { label, value };
          });

          // Filter out 0 values if requested
          if (block.dataConfig?.hideZeroValues) {
            processedData = processedData.filter(d => d.value !== 0);
          }

          // Sort by value (descending)
          processedData.sort((a, b) => b.value - a.value);

          // CRITICAL FIX: If no data remains after processing, return empty structure in ANY mode
          // Do not return null (shows removal), do not return sample data
          if (processedData.length === 0) {
            return {
              labels: [],
              values: [],
              seriesLabel: aggType.charAt(0).toUpperCase() + aggType.slice(1)
            };
          }

          // Slice to top 20 to avoid visual clutter
          const topData = processedData.slice(0, 20);

          return {
            labels: topData.map(d => d.label),
            values: topData.map(d => d.value),
            seriesLabel: aggType.charAt(0).toUpperCase() + aggType.slice(1)
          };
        }
      }

      if (typeof data[0] === 'object' && data[0] !== null) {
        // Transform array of objects into chart format
        const labels = [];
        const values = [];

        // Analyze structure to find label/value keys
        const sampleItems = data.slice(0, Math.min(5, data.length));
        const allKeys = new Set();
        sampleItems.forEach(item => {
          if (typeof item === 'object' && item !== null) {
            Object.keys(item).forEach(key => allKeys.add(key));
          }
        });

        // Smart Field Detection: Prioritize user config, then common keys
        const userLabelField = block.dataConfig?.labelField; // From DataTab
        const userValueField = block.dataConfig?.valueField; // From DataTab

        const possibleLabelKeys = ['risk', 'label', 'name', 'key', 'category', 'level', 'type', 'status', 'severity', 'severity_level', 'severityLevel'];
        const possibleValueKeys = ['count', 'value', 'amount', 'total', 'number', 'quantity', 'score', 'sum'];

        let labelKey = userLabelField || null;
        let valueKey = userValueField || null;

        // Find label key if not set by user
        if (!labelKey) {
          for (const key of possibleLabelKeys) {
            const matches = sampleItems.filter(item =>
              item && typeof item === 'object' &&
              (key in item) && item[key] !== null && item[key] !== undefined
            ).length;
            if (matches >= Math.ceil(sampleItems.length * 0.8)) {
              labelKey = key;
              break;
            }
          }
        }

        // Find value key if not set by user
        if (!valueKey) {
          for (const key of possibleValueKeys) {
            const matches = sampleItems.filter(item => {
              if (!item || typeof item !== 'object' || !(key in item)) return false;
              const val = item[key];
              return val !== null && val !== undefined && (typeof val === 'number' || Array.isArray(val));
            }).length;
            if (matches >= Math.ceil(sampleItems.length * 0.8)) {
              valueKey = key;
              break;
            }
          }
        }


        // Special logic for Bubble and Scatter charts (Direct plotting with multiple axes)
        if (['bubble', 'scatter'].includes(widgetType)) {
          const xField = block.dataConfig?.xField;
          const yField = block.dataConfig?.yField || block.dataConfig?.valueField;
          const rField = block.dataConfig?.radiusField;
          const labelField = block.dataConfig?.labelField || 'hostname';

          const chartValues = [];
          const chartLabels = [];

          // Helper to convert to numeric value, handling objects/arrays by counting them
          const toNumeric = (val, defaultVal = 0) => {
            if (typeof val === 'number') return val;
            if (val === null || val === undefined) return defaultVal;
            if (Array.isArray(val)) return val.length;
            if (typeof val === 'object') return Object.keys(val).length;
            const parsed = parseFloat(String(val));
            return isNaN(parsed) ? defaultVal : parsed;
          };

          data.forEach(item => {
            if (!item || typeof item !== 'object') return;

            const xRaw = xField ? getNestedField(item, xField) : (item.index || 0);
            const yRaw = yField ? getNestedField(item, yField) : 0;
            const rRaw = rField ? getNestedField(item, rField) : 10;
            const labelRaw = labelField ? getNestedField(item, labelField) : '';

            const x = toNumeric(xRaw);
            const y = toNumeric(yRaw);
            const r = widgetType === 'bubble' ? toNumeric(rRaw, 10) : undefined;

            chartValues.push(widgetType === 'bubble' ? { x, y, r } : { x, y });
            chartLabels.push(String(labelRaw || ''));
          });

          return {
            labels: chartLabels,
            values: chartValues,
            seriesLabel: yField || 'Data'
          };
        }

        // Extract data
        data.forEach((item, index) => {
          if (typeof item === 'object' && item !== null) {
            // Use getNestedField to support depth
            const labelRaw = labelKey ? getNestedField(item, labelKey) : null;
            let label = labelRaw;

            // Format label
            if (label === null || label === undefined || label === '') {
              label = '(Empty)';
            } else if (typeof label === 'boolean') {
              label = label ? 'Yes' : 'No';
            } else if (String(label) === '[object Object]') {
              label = '(Not Empty/Has Value)';
            } else {
              label = String(label);
            }

            let value = valueKey ? getNestedField(item, valueKey) : null;

            // Fallback to legacy detection if no value found via keys
            if (value === null || value === undefined) {
              value = (item.count || item.value || item.amount || item.total ||
                item.number || item.quantity || item.score || item.sum || 0);
            }

            // Handle array values
            if (Array.isArray(value) && value.length > 0) {
              const firstVal = value[0];
              value = typeof firstVal === 'number' ? firstVal : (typeof firstVal === 'string' ? parseFloat(firstVal) || 0 : Number(firstVal) || 0);
            }

            // Convert to number
            if (typeof value === 'string') {
              const cleaned = value.replace('%', '').trim();
              value = parseFloat(cleaned) || 0;
            } else if (typeof value === 'boolean') {
              value = value ? 1 : 0;
            }

            if (typeof value === 'number' && !isNaN(value) && isFinite(value)) {
              // Hide Zero Values logic
              if (block.dataConfig?.hideZeroValues && value === 0) {
                // Skip 0 values if configured
                return;
              }
              labels.push(label);
              values.push(value);
            }
          }
        });

        // SPECIAL HANDLING: Scatter and Bubble charts need coordinate objects {x, y, r}
        if (['scatter', 'bubble'].includes(widgetType) && (block.dataConfig.labelField || block.dataConfig.valueField)) {
          const xField = block.dataConfig.labelField; // Use labelField as X-axis
          const yField = block.dataConfig.valueField; // Use valueField as Y-axis
          const rField = block.dataConfig.radiusField; // Only for bubble

          const coordinates = data.map(item => {
            if (!item || typeof item !== 'object') return null;

            const rawX = getNestedField(item, xField);
            const rawY = getNestedField(item, yField);

            // Convert to numbers, giving 0 for invalid values
            const x = typeof rawX === 'number' ? rawX : (parseFloat(String(rawX)) || 0);
            const y = typeof rawY === 'number' ? rawY : (parseFloat(String(rawY)) || 0);

            if (widgetType === 'bubble') {
              const rawR = rField ? getNestedField(item, rField) : null;
              // Default radius to 10 if not provided or invalid
              const r = typeof rawR === 'number' ? rawR : (parseFloat(String(rawR)) || 10);
              return { x, y, r };
            }

            return { x, y };
          }).filter(coord => coord !== null);

          if (coordinates.length > 0) {
            return {
              values: coordinates,
              seriesLabel: yField
            };
          }
        }

        if (labels.length > 0 && values.length > 0 && labels.length === values.length) {
          return {
            labels,
            values,
            seriesLabel: valueKey // Pass the field name being plotted (e.g. "bad_password_count")
          };
        }

        // Return empty structure if we couldn't find ANY valid data to plot
        // This triggers NoDataFallback in renderContent
        return { labels: [], values: [], seriesLabel: valueKey };
      }
    }

    // For numeric widgets or if data is already in correct format
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      // Already in chart format
      if (data.labels && Array.isArray(data.labels) && data.values && Array.isArray(data.values)) {
        return data;
      }

      // Transform object data for chart widgets
      if (isChartWidget && !data.labels) {
        // ... (existing logic for object transformation)
        // If successful, returns inside loop
        // Fallback below if not successful
      }
    }

    // Apply aggregation for numeric widgets
    let finalData;
    if (isNumericWidget && aggregation && data) {
      finalData = aggregateData(data, aggregation, filters);
    } else {
      if (!data) {
        // Return empty structure based on widget type
        if (isChartWidget) return { labels: [], values: [] };
        if (block.widgetType === 'table') return [];
        return null; // For numeric?
      }
      finalData = data;
      // Note: Removed getSampleData fallback. If data exists but is improper, checks below handle it.
      // If data is just missing, we handled it above.
    }

    // For chart widgets, verify we have valid data format
    if (isChartWidget && finalData) {
      const hasValidChartData = (finalData.labels?.length > 0 && finalData.values?.length > 0) ||
        finalData.datasets?.length > 0;
      if (!hasValidChartData) {
        return { labels: [], values: [] };
      }
    }

    // For numeric widgets: always merge scan data value with customData label (if customData exists)
    // NEVER use value from customData - it should always come from scan data
    if (isNumericWidget && block.customData && Object.keys(block.customData).length > 0) {
      return {
        ...finalData,
        // Explicitly ensure value comes from scan data (finalData), not customData
        value: finalData?.value,
        // Use label and description from customData if provided
        label: block.customData.label !== undefined ? block.customData.label : finalData?.label,
        description: block.customData.description !== undefined ? block.customData.description : finalData?.description
      };
    }

    return finalData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataContext, pages, effectiveScanData]);

  // Helper: count the number of data items a specific widget block resolves to
  const getWidgetDataCount = useCallback((blockId) => {
    for (const page of pages) {
      const block = page.blocks.find(b => b.id === blockId);
      if (block) {
        // Special case: Table Widgets
        // Because custom table widgets use complex nested objects, we must use the Data Mapper
        // to extract the true array of rows to count them accurately for dependencies.
        if (TableDataMapper.hasWidgetType(block.widgetType)) {
          const rawData = effectiveScanData || metricsData;
          if (!rawData) return 0;
          try {
            const tableData = TableDataMapper.extractData(block.widgetType, rawData);
            if (Array.isArray(tableData)) {
              return tableData.length;
            }
          } catch (e) {
            console.error('[getWidgetDataCount] Error extracting table data for dependency:', e);
            return 0;
          }
        }

        // Standard case for charts and basic widgets
        const data = getBlockData(block);
        if (!data) return 0;

        // Explicitly check for empty array forms
        if (Array.isArray(data)) return data.length;
        if (data.rows && Array.isArray(data.rows)) return data.rows.length;
        if (data.labels && Array.isArray(data.labels)) {
          // Some charts might have labels but 0 values, meaning empty.
          if (data.values && Array.isArray(data.values) && data.values.length === 0) return 0;
          return data.labels.length;
        }

        // If it's an object but has 0 keys, consider it empty data
        if (typeof data === 'object' && data !== null) {
          if (Object.keys(data).length === 0) return 0;
          return 1;
        }

        // Scalar value
        return 1;
      }
    }
    return 0;
  }, [pages, getBlockData, effectiveScanData, metricsData]);

  const getWidgetItems = useCallback((widgetType, block) => {
    if (!dataContext) return [];
    const config = block.config || {};
    
    if (widgetType === 'table') {
      const widgetData = getBlockData(block);
      const rows = (widgetData && widgetData.rows && Array.isArray(widgetData.rows))
        ? widgetData.rows
        : (Array.isArray(widgetData) ? widgetData : []);
      const mergedConfig = { ...block.config, ...block.dataConfig };
      return applyTableFilters(rows, mergedConfig?.tableFilters);
    }
    
    if (widgetType === 'key-findings') {
      const { scanType = 'All', hideNoData = false, customThresholdNotes } = config;
      const summaries = extractKeyFindingsSummary(dataContext, customThresholdNotes);
      let filteredSummaries = summaries;
      if (scanType && scanType !== 'All') {
        filteredSummaries = summaries.filter(s => matchesScanType(s.category, scanType));
      }
      if (hideNoData) {
        filteredSummaries = filteredSummaries.filter(s => s.statusColor !== 'gray');
      }
      return filteredSummaries;
    }
    
    if (TableDataMapper[widgetType]) {
      const entry = TableDataMapper[widgetType];
      const mergedConfig = { ...block.config, ...block.dataConfig };
      const extractedData = entry.extractor(dataContext, mergedConfig);
      return applyTableFilters(extractedData, mergedConfig?.tableFilters);
    }
    
    if (widgetType === 'open-ports' || widgetType === 'network-sensitive-open-ports-table') {
      const extractedData = extractOpenPortsData(dataContext);
      return extractedData?.all || [];
    }
    
    if (widgetType === 'findings-grid') {
      const dataItems = [];
      const exportDataSource = dataContext.exportData || dataContext;
      if (exportDataSource && typeof exportDataSource === 'object') {
        Object.values(exportDataSource).forEach(categoryData => {
          if (Array.isArray(categoryData) && categoryData.length >= 1) {
            const rectangles = categoryData[0];
            if (Array.isArray(rectangles)) {
              dataItems.push(...rectangles);
            }
          }
        });
      }
      return dataItems;
    }
    
    if (widgetType.startsWith('external-')) {
      const getExternalData = (ctx) => {
        if (ctx.external && (ctx.external.domain || ctx.external.categories)) return ctx.external;
        if (ctx.externalData && (ctx.externalData.domain || ctx.externalData.categories)) return ctx.externalData;
        if (ctx.domain && ctx.categories && Array.isArray(ctx.categories)) return ctx;
        if (ctx.data?.external) return ctx.data.external;
        if (ctx.scanData?.external) return ctx.scanData.external;
        if (ctx.metricsData?.external) return ctx.metricsData.external;
        return null;
      };

      const externalData = getExternalData(dataContext);
      if (!externalData || !Array.isArray(externalData.categories)) return [];

      const dataItems = [];
      if (widgetType === 'external-all-findings-table') {
        externalData.categories.forEach(cat => {
          if (Array.isArray(cat.findings)) dataItems.push(...cat.findings);
        });
      } else if (widgetType === 'external-warnings-table') {
        externalData.categories.forEach(cat => {
          if (Array.isArray(cat.findings)) {
            dataItems.push(...cat.findings.filter(f => f.status === 'warning' || f.status === 'fail'));
          }
        });
      } else if (widgetType === 'external-passed-table') {
        externalData.categories.forEach(cat => {
          if (Array.isArray(cat.findings)) {
            dataItems.push(...cat.findings.filter(f => f.status === 'pass'));
          }
        });
      } else if (widgetType === 'external-compliance-table') {
        externalData.categories.forEach(cat => {
          if (Array.isArray(cat.findings)) {
            cat.findings.forEach(f => {
              if (Array.isArray(f.compliance)) dataItems.push(...f.compliance);
            });
          }
        });
      } else if (widgetType === 'external-dns-findings' ||
        widgetType === 'external-email-findings' ||
        widgetType === 'external-ssl-findings' ||
        widgetType === 'external-headers-findings' ||
        widgetType === 'external-infrastructure-findings' ||
        widgetType === 'external-breach-findings') {
        const typeMap = {
          'external-dns-findings': 'DNS',
          'external-email-findings': 'Email',
          'external-ssl-findings': 'SSL',
          'external-headers-findings': 'Headers',
          'external-infrastructure-findings': 'Infrastructure',
          'external-breach-findings': 'Breach'
        };
        const targetType = typeMap[widgetType];
        const category = externalData.categories.find(c => c.name && c.name.includes(targetType));
        if (category && Array.isArray(category.findings)) {
          return category.findings;
        }
      }
      return dataItems;
    }

    return [];
  }, [dataContext, getBlockData]);

  const estimateWidgetRowHeights = useCallback((widgetType, block, items) => {
    if (!Array.isArray(items) || items.length === 0) return [];
    
    if (widgetType === 'key-findings') {
      return items.map(item => {
        const notes = item.notes || '';
        const notesLength = notes.length;
        const lines = Math.max(1, Math.ceil(notesLength / 35));
        const height = 50 + (lines - 1) * 18;
        return height + 8; // Row height + gap
      });
    }

    if (widgetType === 'findings-grid') {
      return items.map(() => 120 + 16); // Card height + gap
    }

    // Default/generic tables
    return items.map(item => {
      let maxStrLength = 0;
      Object.keys(item).forEach(key => {
        if (key === 'no' || key === 'no.' || key === 'id' || key === 'statusColor') return;
        const val = item[key];
        if (typeof val === 'string') {
          maxStrLength = Math.max(maxStrLength, val.length);
        }
      });
      const lines = Math.max(1, Math.ceil(maxStrLength / 30));
      const height = 39 + (lines - 1) * 15;
      return height + 2; // Row height + buffer
    });
  }, []);

  const calculateItemsThatFit = useCallback((rowHeights, usableHeight) => {
    if (rowHeights.length === 0) return 0;
    let currentHeight = 0;
    let count = 0;
    for (let i = 0; i < rowHeights.length; i++) {
      const h = rowHeights[i];
      if (currentHeight + h > usableHeight) {
        if (count === 0) return 1;
        break;
      }
      currentHeight += h;
      count++;
    }
    return count;
  }, []);

  const calculateSafeItemsPerPage = useCallback((rowHeights, startIndex, usableHeight) => {
    const remainingHeights = rowHeights.slice(startIndex);
    if (remainingHeights.length === 0) return 15; // default fallback
    
    let low = 1;
    let high = remainingHeights.length;
    let bestK = 1;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      let fitsAll = true;
      for (let i = 0; i <= remainingHeights.length - mid; i++) {
        let sum = 0;
        for (let j = 0; j < mid; j++) {
          sum += remainingHeights[i + j];
        }
        if (sum > usableHeight) {
          fitsAll = false;
          break;
        }
      }
      if (fitsAll) {
        bestK = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }
    return bestK;
  }, []);

  const getWidgetOverhead = useCallback((widgetType) => {
    if (widgetType === 'findings-grid') {
      return 70;
    }
    return 114;
  }, []);

  // Debug: Track pages state changes for table columns
  useEffect(() => {
    if (currentPage) {
      const tableBlocks = currentPage.blocks.filter(b => b.widgetType === 'table');
      tableBlocks.forEach(block => {
        console.log('[Main] Table block state:', {
          blockId: block.id,
          tableColumnsCount: block.dataConfig?.tableColumns?.length || 0,
          tableColumns: block.dataConfig?.tableColumns
        });
      });
    }
  }, [currentPage]);

  // Track block count changes to trigger pagination recalculation
  useEffect(() => {
    const totalBlocks = pages.reduce((sum, page) => sum + page.blocks.length, 0);
    if (totalBlocks !== pagesBlockCountRef.current) {
      pagesBlockCountRef.current = totalBlocks;
      // Trigger pagination recalculation by updating trigger state
      setBlockCountTrigger(prev => prev + 1);
    }
  }, [pages]);

  // Scroll detection to update current page indicator
  useEffect(() => {
    const observerOptions = {
      root: null, // Use viewport as root
      rootMargin: '-10% 0px -10% 0px', // Trigger when page is 10% visible from top and bottom
      threshold: [0, 0.1, 0.3, 0.5, 0.7, 1.0]
    };

    const handleIntersection = (entries) => {
      // Clear any pending updates
      if (scrollUpdateTimeoutRef.current) {
        clearTimeout(scrollUpdateTimeoutRef.current);
      }

      // Find the most visible page
      let mostVisibleEntry = null;
      let maxVisibility = 0;

      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const visibility = entry.intersectionRatio;
          if (visibility > maxVisibility) {
            maxVisibility = visibility;
            mostVisibleEntry = entry;
          }
        }
      });

      // Update current page if we found a visible page
      if (mostVisibleEntry && mostVisibleEntry.intersectionRatio > 0.05) {
        const pageId = mostVisibleEntry.target.dataset.pageId;
        if (pageId) {
          // Debounce the update to avoid rapid changes
          scrollUpdateTimeoutRef.current = setTimeout(() => {
            setCurrentPageId(prevPageId => {
              // Only update if different to avoid unnecessary re-renders
              return pageId !== prevPageId ? pageId : prevPageId;
            });
          }, 100);
        }
      }
    };

    const observer = new IntersectionObserver(handleIntersection, observerOptions);

    // Helper function to find the most visible page
    const findMostVisiblePage = () => {
      let mostVisiblePage = null;
      let maxVisibility = 0;
      const viewportHeight = window.innerHeight;
      const viewportCenter = window.scrollY + viewportHeight / 2;

      pages.forEach(page => {
        const ref = pageContainerRefs.current[page.id];
        if (ref) {
          const rect = ref.getBoundingClientRect();
          const pageTop = window.scrollY + rect.top;
          const pageBottom = pageTop + rect.height;
          const pageCenter = pageTop + rect.height / 2;

          // Calculate how much of the page is visible in viewport
          const visibleTop = Math.max(0, viewportCenter - pageTop);
          const visibleBottom = Math.max(0, pageBottom - viewportCenter);
          const visibleHeight = Math.min(visibleTop, visibleBottom) * 2;
          const visibility = Math.min(1, visibleHeight / viewportHeight);

          if (visibility > maxVisibility) {
            maxVisibility = visibility;
            mostVisiblePage = page.id;
          }
        }
      });

      return mostVisiblePage;
    };

    // Scroll handler as fallback
    const handleScroll = () => {
      if (scrollUpdateTimeoutRef.current) {
        clearTimeout(scrollUpdateTimeoutRef.current);
      }

      scrollUpdateTimeoutRef.current = setTimeout(() => {
        const visiblePageId = findMostVisiblePage();
        if (visiblePageId && visiblePageId !== currentPageIdRef.current) {
          setCurrentPageId(visiblePageId);
        }
      }, 150);
    };

    // Observe all page containers - use a small delay to ensure refs are set
    const observePages = () => {
      // Clear old refs that no longer exist
      const currentPageIds = new Set(pages.map(p => p.id));
      Object.keys(pageContainerRefs.current).forEach(pageId => {
        if (!currentPageIds.has(pageId)) {
          delete pageContainerRefs.current[pageId];
        }
      });

      // Observe all current page containers
      pages.forEach(page => {
        const ref = pageContainerRefs.current[page.id];
        if (ref) {
          observer.observe(ref);
        }
      });
    };

    // Try immediately and also after a short delay to catch late-rendered pages
    observePages();
    const timeoutId = setTimeout(observePages, 100);

    // Add scroll listener as fallback
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
      if (scrollUpdateTimeoutRef.current) {
        clearTimeout(scrollUpdateTimeoutRef.current);
      }
    };
  }, [pages]); // Only depend on pages to avoid recreating observer too often

  // Auto-size widgets when data changes (handles pagination)
  useEffect(() => {
    // Only run if we have data context
    if (!dataContext || Object.keys(dataContext).length === 0) {
      return;
    }

    // Check if there are any paginated widgets to process
    const hasPaginatedWidgets = pages.some(page =>
      !page.isContinuationPage &&
      page.blocks.some(block => block.type === 'widget' && PAGINATED_WIDGETS.includes(block.widgetType))
    );

    if (!hasPaginatedWidgets) {
      return;
    }

    // Calculate pagination and create continuation pages
    setPages(prev => {
      // Deep clone pages and blocks to prevent direct state mutation
      const newPages = prev.map(p => ({
        ...p,
        blocks: p.blocks.map(b => ({ ...b })) // Clone blocks to allow mutation of block properties
      }));
      let hasChanges = false;

      // FIRST: Comprehensive cleanup of incorrectly structured pages before processing
      // This prevents nested continuations and duplicates from being processed
      const pagesToRemove = [];

      // Track all original block IDs to identify which continuation pages are valid
      const originalBlockIds = new Set();
      newPages.forEach(page => {
        if (!page.isContinuationPage) {
          page.blocks.forEach(block => {
            if (!block.isClonedBlock) {
              originalBlockIds.add(block.id);
            }
          });
        }
      });

      // ORPHAN CLEANUP: Remove continuation pages whose original blocks no longer exist
      // This prevents infinite loops where orphaned continuation pages keep triggering hasChanges
      const orphanedPageIndices = [];
      newPages.forEach((page, pageIdx) => {
        if (!page.isContinuationPage) return;
        const clonedBlocks = page.blocks.filter(b => b.isClonedBlock);
        if (clonedBlocks.length === 0) return;
        // Check if ALL cloned blocks on this page are orphaned (original no longer exists)
        const allOrphaned = clonedBlocks.every(b => !originalBlockIds.has(b.originalBlockId));
        if (allOrphaned) {
          orphanedPageIndices.push(pageIdx);
        }
      });
      if (orphanedPageIndices.length > 0) {
        // Remove in reverse order to preserve indices
        [...orphanedPageIndices].sort((a, b) => b - a).forEach(idx => {
          console.log('[Pagination] Removing orphaned continuation page:', {
            pageId: newPages[idx].id,
            pageName: newPages[idx].name
          });
          newPages.splice(idx, 1);
        });
        hasChanges = true;
      }

      newPages.forEach((page, pageIdx) => {
        const hasClonedBlocks = page.blocks.some(b => b.isClonedBlock);
        const isMarkedAsContinuation = page.isContinuationPage;

        // CRITICAL: If a page has cloned blocks, it MUST be marked as continuation page
        // This ensures continuation pages from templates are always properly marked
        if (hasClonedBlocks && !isMarkedAsContinuation) {
          const hasOriginalBlock = page.blocks.some(b => !b.isClonedBlock);
          if (!hasOriginalBlock) {
            // Check if the original block still exists before marking
            const clonedBlock = page.blocks.find(b => b.isClonedBlock);
            if (clonedBlock && originalBlockIds.has(clonedBlock.originalBlockId)) {
              // Page has only cloned blocks and original exists - mark it as continuation
              page.isContinuationPage = true;
              page.continuationOf = clonedBlock.originalBlockId;
              hasChanges = true;
              console.log('[Pagination] Auto-marked page as continuation:', {
                pageId: page.id,
                pageName: page.name,
                continuationOf: page.continuationOf
              });
            } else {
              // Original block no longer exists - orphaned page, remove it
              pagesToRemove.push(pageIdx);
            }
          } else {
            // Mixed page - this shouldn't happen, remove it
            pagesToRemove.push(pageIdx);
          }
        }

        // CRITICAL: Remove pages marked as continuation but have no cloned blocks (invalid state)
        // These are "fake" continuation pages that should be removed, UNLESS they are explicitly user placeholders
        if (isMarkedAsContinuation && !hasClonedBlocks && !page.isPlaceholder) {
          console.log('[Pagination] Removing page marked as continuation but has no cloned blocks:', {
            pageId: page.id,
            pageName: page.name,
            pageIdx: pageIdx
          });
          pagesToRemove.push(pageIdx);
        }

        // CRITICAL: Ensure pages WITH cloned blocks are ALWAYS marked as continuation
        // This prevents them from appearing as normal pages with remove buttons
        if (hasClonedBlocks && !isMarkedAsContinuation) {
          const hasOriginalBlock = page.blocks.some(b => !b.isClonedBlock);
          if (!hasOriginalBlock) {
            // Page has only cloned blocks - it MUST be a continuation page
            // Mark it as continuation immediately to prevent it from appearing as a normal page
            page.isContinuationPage = true;
            const clonedBlock = page.blocks.find(b => b.isClonedBlock);
            if (clonedBlock && clonedBlock.originalBlockId) {
              page.continuationOf = clonedBlock.originalBlockId;
            }
            hasChanges = true;
            console.log('[Pagination] Auto-marked page with cloned blocks as continuation (early cleanup):', {
              pageId: page.id,
              pageName: page.name,
              continuationOf: page.continuationOf,
              pageIdx: pageIdx
            });
          } else {
            // Mixed page - this shouldn't happen, remove it
            console.log('[Pagination] Removing mixed page (has both original and cloned blocks):', {
              pageId: page.id,
              pageName: page.name,
              pageIdx: pageIdx
            });
            pagesToRemove.push(pageIdx);
          }
        }

        // Remove nested continuation pages (continuations of continuations)
        if (isMarkedAsContinuation && page.continuationOf) {
          const pageBlock = page.blocks.find(b => b.isClonedBlock);
          if (pageBlock) {
            // Check if this is a nested continuation (block ID has multiple -continuation- parts)
            const continuationParts = pageBlock.id.split('-continuation-');
            if (continuationParts.length > 2) {
              pagesToRemove.push(pageIdx);
            }

            // Check if continuationOf points to a cloned block (nested continuation)
            if (!originalBlockIds.has(page.continuationOf)) {
              pagesToRemove.push(pageIdx);
            }
          }
        }
      });

      // Remove marked pages in reverse order to maintain indices
      const uniquePagesToRemove = [...new Set(pagesToRemove)].sort((a, b) => b - a);
      uniquePagesToRemove.forEach(idx => {
        newPages.splice(idx, 1);
        console.log('[Pagination Loop] hasChanges = true at line 3870 (delete broken structural page)');
        hasChanges = true;
      });

      newPages.forEach((page, pageIdx) => {
        // Skip continuation pages - they're for rendering continuation content
        // Skip placeholder pages - they're for template editing, not for pagination
        if (page.isContinuationPage || page.isPlaceholder) {
          return;
        }

        page.blocks.forEach(block => {
          // Skip cloned blocks - they're handled by their original block
          if (block.isClonedBlock) {
            return;
          }

          // Skip placeholder widgets - they should not be paginated
          if (block.isPlaceholderWidget) {
            return;
          }

          // CRITICAL SAFEGUARD: Never process blocks that are continuations
          // Check if block ID contains "-continuation-" which indicates it's a continuation block
          if (block.id.includes('-continuation-')) {
            return;
          }

          // Also skip blocks whose IDs contain 'placeholder' — they are placeholder artifacts
          if (block.id.includes('placeholder')) {
            return;
          }

          // Process paginated widgets
          if (block.type === 'widget' && PAGINATED_WIDGETS.includes(block.widgetType)) {

            // Get widget data to calculate content height
            const widgetData = getBlockData(block);

            // Calculate number of items based on widget type
            let totalItems = 0;
            if (block.widgetType === 'table') {
              // For table widget, count rows after applying filters
              const rows = (widgetData && widgetData.rows && Array.isArray(widgetData.rows))
                ? widgetData.rows
                : (Array.isArray(widgetData) ? widgetData : []);

              const mergedConfig = { ...block.config, ...block.dataConfig };
              const filteredRows = applyTableFilters(rows, mergedConfig?.tableFilters);
              totalItems = filteredRows.length;
            } else if (block.widgetType === 'key-findings') {
              // For key findings, use the EXACT SAME logic as the widget
              const summaries = extractKeyFindingsSummary(dataContext, block.config?.customThresholdNotes);

              // Apply scanType filtering (matches widget logic)
              const scanType = block.config?.scanType || 'All';
              let filteredSummaries = summaries;

              if (scanType && scanType !== 'All') {
                filteredSummaries = summaries.filter(s => matchesScanType(s.category, scanType));
              }

              // Apply hideNoData filtering (matches widget logic)
              const hideNoData = block.config?.hideNoData || false;
              if (hideNoData) {
                filteredSummaries = filteredSummaries.filter(s => s.statusColor !== 'gray');
              }

              totalItems = filteredSummaries.length;

            } else if (TableDataMapper[block.widgetType]) {
              // NEW: Use TableDataMapper for centrally managed Proof Tables
              // This automatically applies Table Filters to the pagination count
              const entry = TableDataMapper[block.widgetType];
              const mergedConfig = { ...block.config, ...block.dataConfig };
              const extractedData = entry.extractor(dataContext, mergedConfig);
              const unfilteredCount = extractedData.length;
              const filteredData = applyTableFilters(extractedData, mergedConfig?.tableFilters);
              totalItems = filteredData.length;

              if (mergedConfig?.tableFilters?.filters?.length > 0) {
                console.log(`[Pagination] Mapper-based calculation for ${block.widgetType}:`, {
                  unfilteredCount,
                  filteredCount: totalItems,
                  filters: mergedConfig.tableFilters.filters
                });
              }

              console.log('[Pagination] Mapper-based calculation:', {
                widgetType: block.widgetType,
                unfilteredCount: extractedData.length,
                filteredCount: filteredData.length,
                totalItems: totalItems
              });
            } else if (block.widgetType === 'open-ports') {
              // CRITICAL: Use the EXACT SAME extraction function as OpenPortsWidget
              // OpenPortsWidget uses extractOpenPortsData which returns { all: [...], sensitive: [...], ... }
              // The widget displays allPortsData.all which contains ONLY sensitive ports
              const extractedData = extractOpenPortsData(dataContext);
              totalItems = extractedData.all.length; // Use .all array which contains sensitive ports

              console.log('[Pagination Calculation] Open Ports (Sensitive):', {
                widgetType: block.widgetType,
                blockId: block.id,
                totalItems: totalItems,
                allArrayLength: extractedData.all.length,
                sensitiveArrayLength: extractedData.sensitive.length
              });
            } else if (block.widgetType === 'findings-grid') {
              // For findings grid, count all findings rectangles
              const exportDataSource = dataContext.exportData || dataContext;
              if (exportDataSource && typeof exportDataSource === 'object') {
                Object.values(exportDataSource).forEach(categoryData => {
                  if (Array.isArray(categoryData) && categoryData.length >= 1) {
                    const rectangles = categoryData[0];
                    if (Array.isArray(rectangles)) {
                      totalItems += rectangles.length;
                    }
                  }
                });
              }
            } else if (block.widgetType.startsWith('external-')) {
              // EXTERNAL RISK SCAN PAGINATION LOGIC
              // Must match logic in ExternalRiskWidgets.jsx helpers
              const getExternalData = (ctx) => {
                // Check all possible locations for external data
                if (ctx.external && (ctx.external.domain || ctx.external.categories)) return ctx.external;
                if (ctx.externalData && (ctx.externalData.domain || ctx.externalData.categories)) return ctx.externalData;
                if (ctx.domain && ctx.categories && Array.isArray(ctx.categories)) return ctx;
                // Check common wrappers
                if (ctx.data?.external) return ctx.data.external;
                if (ctx.scanData?.external) return ctx.scanData.external;
                if (ctx.metricsData?.external) return ctx.metricsData.external;
                return null;
              };

              const externalData = getExternalData(dataContext);

              if (externalData && Array.isArray(externalData.categories)) {
                if (block.widgetType === 'external-all-findings-table') {
                  // Count ALL findings across all categories
                  externalData.categories.forEach(cat => {
                    if (Array.isArray(cat.findings)) {
                      totalItems += cat.findings.length;
                    }
                  });
                } else if (block.widgetType === 'external-warnings-table') {
                  // Count ONLY warnings/failures
                  externalData.categories.forEach(cat => {
                    if (Array.isArray(cat.findings)) {
                      totalItems += cat.findings.filter(f => f.status === 'warning' || f.status === 'fail').length;
                    }
                  });
                } else if (block.widgetType === 'external-passed-table') {
                  // Count ONLY passed checks
                  externalData.categories.forEach(cat => {
                    if (Array.isArray(cat.findings)) {
                      totalItems += cat.findings.filter(f => f.status === 'pass').length;
                    }
                  });
                } else if (block.widgetType === 'external-compliance-table') {
                  // Count compliance mappings
                  externalData.categories.forEach(cat => {
                    if (Array.isArray(cat.findings)) {
                      cat.findings.forEach(f => {
                        if (Array.isArray(f.compliance)) {
                          totalItems += f.compliance.length;
                        }
                      });
                    }
                  });
                } else if (block.widgetType === 'external-dns-findings' ||
                  block.widgetType === 'external-email-findings' ||
                  block.widgetType === 'external-ssl-findings' ||
                  block.widgetType === 'external-headers-findings' ||
                  block.widgetType === 'external-infrastructure-findings' ||
                  block.widgetType === 'external-breach-findings') {
                  // Specific category findings
                  // Map widget type to category name part
                  const typeMap = {
                    'external-dns-findings': 'DNS',
                    'external-email-findings': 'Email',
                    'external-ssl-findings': 'SSL',
                    'external-headers-findings': 'Headers',
                    'external-infrastructure-findings': 'Infrastructure', // Might need fuzzy match
                    'external-breach-findings': 'Breach'
                  };

                  const targetType = typeMap[block.widgetType];
                  const category = externalData.categories.find(c => c.name && c.name.includes(targetType));
                  if (category && Array.isArray(category.findings)) {
                    totalItems = category.findings.length;
                  }
                }
              }
            }


            if (totalItems === 0) {
              // Remove pagination if no items
              if (block.pagination) {
                block.pagination = undefined;
                hasChanges = true;

                // Remove continuation clones of this block from ANY continuation page
                newPages.forEach(p => {
                  if (p.isContinuationPage) {
                    const cloneIdx = p.blocks.findIndex(b => b.isClonedBlock && b.originalBlockId === block.id);
                    if (cloneIdx !== -1) {
                      p.blocks.splice(cloneIdx, 1);
                      hasChanges = true;
                    }
                  }
                });

                // Then remove any continuation pages that are now empty (or only have placeholder aesthetic blocks)
                const emptyContinuationPages = newPages.filter(p =>
                  p.isContinuationPage && !p.blocks.some(b => b.isClonedBlock)
                );

                emptyContinuationPages.forEach(cp => {
                  const index = newPages.indexOf(cp);
                  if (index !== -1) {
                    newPages.splice(index, 1);
                    console.log(`[Pagination] Deleted empty shared continuation page due to zero items for block ${block.id}`);
                    hasChanges = true;
                  }
                });
              }
              // Early return - skip rest of pagination logic
              return;
            }

            let itemsPerPageFirstPage;
            let itemsPerPageContinuation;

            const isAutoFirstPage = block.dataConfig?.itemsPerPageFirstPage === 'auto' || block.dataConfig?.itemsPerPageFirstPage === undefined;
            const isAutoContinuation = block.dataConfig?.itemsPerPageContinuation === 'auto' || block.dataConfig?.itemsPerPageContinuation === undefined;

            const userSetFirstPage = !isAutoFirstPage && block.dataConfig?.itemsPerPageFirstPage && typeof block.dataConfig.itemsPerPageFirstPage === 'number';
            const userSetLegacy = block.dataConfig?.itemsPerPage && typeof block.dataConfig.itemsPerPage === 'number';

            const itemsForRows = getWidgetItems(block.widgetType, block);
            const rowHeights = estimateWidgetRowHeights(block.widgetType, block, itemsForRows);
            const overhead = getWidgetOverhead(block.widgetType);

            if (isAutoFirstPage) {
              const blockHeight = block.size?.height || 400;
              const usableHeight = Math.max(0, blockHeight - overhead);
              itemsPerPageFirstPage = calculateItemsThatFit(rowHeights, usableHeight);
            } else if (userSetFirstPage) {
              itemsPerPageFirstPage = block.dataConfig.itemsPerPageFirstPage;
            } else if (userSetLegacy) {
              itemsPerPageFirstPage = block.dataConfig.itemsPerPage;
            }

            const userSetContinuation = !isAutoContinuation && block.dataConfig?.itemsPerPageContinuation && typeof block.dataConfig.itemsPerPageContinuation === 'number';
            if (isAutoContinuation || (isAutoFirstPage && !userSetContinuation)) {
              const pageHeight = gridConfig?.pageHeight || 1056;
              const usableContHeight = Math.max(0, pageHeight - overhead);
              itemsPerPageContinuation = calculateSafeItemsPerPage(rowHeights, itemsPerPageFirstPage || 0, usableContHeight);
            } else if (userSetContinuation) {
              itemsPerPageContinuation = block.dataConfig.itemsPerPageContinuation;
            } else if (itemsPerPageFirstPage !== undefined) {
              itemsPerPageContinuation = itemsPerPageFirstPage;
            }

            if (itemsPerPageFirstPage === undefined) {
              if (block.widgetType === 'table') {
                // Table widget: Each row is ~40px + borders + spacing
                itemsPerPageFirstPage = 15; // SAFE TESTED VALUE
              } else if (block.widgetType === 'key-findings') {
                // Key Findings: Each row is ~50px + spacing + borders
                // User tested: only 15 items fit on one page
                itemsPerPageFirstPage = 15; // SAFE TESTED VALUE (user confirmed)
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 15;
                }
              } else if (block.widgetType === 'open-ports' || block.widgetType === 'network-sensitive-open-ports-table') {
                // Sensitive Open Ports: 3 columns (IP, Port, Service)
                // Smaller rows than Key Findings (~35px vs 50px)
                // Can fit 20 items comfortably on one page
                itemsPerPageFirstPage = 20; // SAFE TESTED VALUE (user confirmed)
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 20;
                }
              } else if (block.widgetType === 'network-backup-status-table') {
                // Backup Status: 4 columns (Device, Last Backup, Status, Notes)
                // Similar row height to Open Ports (~35px)
                // Can fit 20 items comfortably on one page
                itemsPerPageFirstPage = 20; // SAFE TESTED VALUE
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 20;
                }
              } else if (block.widgetType === 'ad-all-computers-table' || block.widgetType === 'ad-workstation-age-table') {
                // AD All Computers: 6 columns (No, Name, Host, OS, OU, Age)
                // Similar row height to other tables (~35px)
                itemsPerPageFirstPage = 20;
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 20;
                }
              } else if (block.widgetType === 'network-antivirus-status-table') {
                // Antivirus Status: 4 columns (Device, Antivirus, Path, Status)
                // Similar row height to Backup Status (~35px)
                itemsPerPageFirstPage = 20;
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 20;
                }
              } else if (block.widgetType === 'ms365-all-users-mfa-table') {
                // MS365 All Users MFA: 5 columns (No, Display Name, MFA Status, Num Methods, Auth Methods)
                // Similar row height to other tables (~35px)
                itemsPerPageFirstPage = 20;
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 20;
                }
              } else if (block.widgetType === 'network-os-versions-table' ||
                block.widgetType === 'localhost-os-version-table' ||
                block.widgetType === 'localhost-os-versions-table') {
                // OS Versions: 3 columns (Device, OS, Risk)
                // Similar row height to Open Ports (~35px)
                // Can fit 20 items comfortably on one page
                itemsPerPageFirstPage = 20; // SAFE TESTED VALUE
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 20;
                }
              } else if (block.widgetType === 'network-firewall-status-table') {
                // Firewall Status: 3 columns (Device, Firewall Profiles, Status)
                // Similar row height to Open Ports (~35px)
                // Set to 10 items per page as requested
                itemsPerPageFirstPage = 10; // USER REQUESTED VALUE
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 10;
                }
              } else if (block.widgetType === 'network-detailed-services-table') {
                // Detailed Services: 6 columns (IP, Name, Display Name, Reason, State, Start Mode)
                // Similar row height to Open Ports (~35px)
                // Can fit 20 items comfortably on one page
                itemsPerPageFirstPage = 20; // SAFE TESTED VALUE
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 20;
                }
              } else if (block.widgetType === 'network-non-default-services-table') {
                // Non-Default Services Risk Breakdown: 4 columns (State, Total Services, Severity, Explanation)
                // Similar row height to Open Ports (~35px)
                // Can fit 20 items comfortably on one page
                itemsPerPageFirstPage = 20; // SAFE TESTED VALUE
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 20;
                }
              } else if (block.widgetType === 'network-services-risk-breakdown-table') {
                // Services Risk Breakdown: 4 columns (State, Total Services, Severity, Explanation)
                // Similar row height to Open Ports (~35px)
                // Can fit 20 items comfortably on one page
                itemsPerPageFirstPage = 20; // SAFE TESTED VALUE
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 20;
                }
              } else if (block.widgetType === 'network-high-privilege-services-table') {
                // High-Privilege Services: 6 columns (IP, Name, Display Name, Reason, Risk, Note)
                // Similar row height to Open Ports (~35px)
                // Can fit 20 items comfortably on one page
                itemsPerPageFirstPage = 10; // SAFE TESTED VALUE
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 10;
                }
              } else if (block.widgetType === 'network-users-no-expiring-password-table') {
                // Users No Expiring Password: 4 columns (Computer IP, Username, Account Type, Password Expires)
                // Similar row height to Open Ports (~35px)
                // Can fit 20 items comfortably on one page
                itemsPerPageFirstPage = 20; // SAFE TESTED VALUE
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 20;
                }
              } else if (block.widgetType === 'network-users-expiring-password-table') {
                // Users Expiring Password: 4 columns (Computer IP, Username, Account Type, Password Expires)
                // Similar row height to Open Ports (~35px)
                // Can fit 20 items comfortably on one page
                itemsPerPageFirstPage = 20; // SAFE TESTED VALUE
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 20;
                }
              } else if (block.widgetType === 'network-admins-no-expiring-password-table') {
                // Admins No Expiring Password: 4 columns (Computer IP, Username, Account Type, Password Expires)
                // Similar row height to Open Ports (~35px)
                // Can fit 20 items comfortably on one page
                itemsPerPageFirstPage = 20; // SAFE TESTED VALUE
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 20;
                }
              } else if (block.widgetType === 'network-admins-expiring-password-table') {
                // Admins Expiring Password: 4 columns (Computer IP, Username, Account Type, Password Expires)
                // Similar row height to Open Ports (~35px)
                // Can fit 20 items comfortably on one page
                itemsPerPageFirstPage = 20; // SAFE TESTED VALUE
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 20;
                }
              } else if (block.widgetType === 'network-admins-daily-work-table') {
                // Admins Daily Work: 3 columns (Computer IP, Group, Account)
                // Similar row height to Open Ports (~35px)
                // Can fit 20 items comfortably on one page
                itemsPerPageFirstPage = 20; // SAFE TESTED VALUE
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 20;
                }
              } else if (block.widgetType === 'ad-groups-risk-table') {
                // AD Groups Risk: 7 columns (Name, Description, Members, Scope, Type, Created, Risk)
                // Similar row height to Open Ports (~35px)
                // Set to 5 items per page as requested
                itemsPerPageFirstPage = 5; // USER REQUESTED VALUE
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 5;
                }
              } else if (block.widgetType === 'ad-ous-risk-table') {
                // AD OUs Risk: 4 columns (Name, DN, Members, Risk)
                // Similar row height to Open Ports (~35px)
                // Can fit 20 items comfortably on one page
                itemsPerPageFirstPage = 20; // SAFE TESTED VALUE
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 20;
                }
              } else if (block.widgetType === 'ad-fsmo-roles-table') {
                // FSMO Roles: 4 columns (Role, DC Name, Risk, Note)
                // Similar row height to Open Ports (~35px)
                // Set to 7 items per page as requested
                itemsPerPageFirstPage = 7; // USER REQUESTED VALUE
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 7;
                }
              } else if (block.widgetType === 'ad-gpo-analysis-table') {
                // GPO Analysis: 7 columns (GPO Name, Created Date, Last Modified, Linked Scopes, Policies, Risk, Note)
                // Similar row height to Open Ports (~35px)
                // Can fit 20 items comfortably on one page
                itemsPerPageFirstPage = 20; // SAFE TESTED VALUE
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 20;
                }
              } else if (block.widgetType === 'ad-admins-table') {
                // AD Admins: 6 columns (No, Account Name, Account Type, Privileged Group, Enabled Status, Last Logon)
                // Similar row height to other AD tables (~35px)
                itemsPerPageFirstPage = 15;
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 15;
                }
              } else if (block.widgetType === 'ms365-inactive-users-table') {
                // MS365 Inactive Users: 2 columns (User Name, User ID)
                // Similar row height to Open Ports (~35px)
                // Can fit 20 items comfortably on one page
                itemsPerPageFirstPage = 20; // SAFE TESTED VALUE
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 20;
                }
              } else if (block.widgetType === 'ad-users-audit-table' ||
                block.widgetType === 'ad-computers-audit-table' ||
                block.widgetType === 'ad-devices-audit-table' ||
                block.widgetType === 'ad-groups-audit-table' ||
                block.widgetType === 'ad-ous-audit-table' ||
                block.widgetType === 'ad-domain-controllers-audit-table' ||
                block.widgetType === 'ad-gpo-policies-audit-table' ||
                block.widgetType === 'ad-event-logs-audit-table' ||
                block.widgetType === 'ms365-scan-proof-table' ||
                block.widgetType === 'microsoft-policies-table' ||
                block.widgetType === 'localhost-hardware-information-table' ||
                block.widgetType === 'localhost-system-information-table' ||
                block.widgetType === 'localhost-installed-applications-table' ||
                block.widgetType === 'localhost-event-logs-table' ||
                block.widgetType === 'localhost-open-ports-audit-table') {
                // All audit tables: Similar row height to other tables (~35px)
                // Default to 25 items per page (same as other tables like ad-inactive-users-table)
                itemsPerPageFirstPage = 25; // DEFAULT VALUE for audit tables
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 25;
                }
              } else if (block.widgetType === 'pii-detected-table') {
                // PII Detected: 3 columns (Type, Value, Source)
                // Similar row height to Open Ports (~35px)
                // Can fit 20 items comfortably on one page
                itemsPerPageFirstPage = 10; // SAFE TESTED VALUE
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 10;
                }
              } else if (block.widgetType === 'findings-grid') {
                // Findings Grid: Large cards ~150-200px each
                // Much fewer items per page
                itemsPerPageFirstPage = 5; // SAFE TESTED VALUE (3 columns, 2 rows max)
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 5;
                }
              } else {
                // Fallback for any other paginated widgets
                itemsPerPageFirstPage = 15; // SAFE DEFAULT
                if (!userSetContinuation) {
                  itemsPerPageContinuation = 15;
                }
              }
            }

            // Ensure itemsPerPageContinuation is set if it's still undefined
            if (itemsPerPageContinuation === undefined) {
              itemsPerPageContinuation = itemsPerPageFirstPage || 15;
            }

            // Ensure itemsPerPageFirstPage is set if it's still undefined
            if (itemsPerPageFirstPage === undefined) {
              itemsPerPageFirstPage = 15; // Final fallback
            }

            // Calculate total pages needed considering different itemsPerPage for first vs continuation pages
            // If totalItems <= itemsPerPageFirstPage: only 1 page needed
            // Otherwise: 1 first page + continuation pages
            let totalPagesNeeded;
            if (totalItems <= itemsPerPageFirstPage) {
              totalPagesNeeded = 1;
            } else {
              const remainingItems = totalItems - itemsPerPageFirstPage;
              const continuationPagesNeeded = Math.ceil(remainingItems / itemsPerPageContinuation);
              totalPagesNeeded = 1 + continuationPagesNeeded;
            }

            // EXECUTIVE REPORT PAGINATION CAP: AI-generated summary tables read every row
            // from the scan data (so charts/counts stay accurate against the real dataset),
            // but the table itself must NOT sprawl across dozens of continuation pages.
            // dataConfig.maxPages caps how many pages this table renders; the remaining
            // rows are simply not paginated. totalItems is left as the true count.
            const maxPages = Number(block.dataConfig?.maxPages);
            if (maxPages > 0 && totalPagesNeeded > maxPages) {
              totalPagesNeeded = maxPages;
            }

            console.log('[Pagination Calculation]', {
              blockId: block.id,
              widgetType: block.widgetType,
              totalItems: totalItems,
              itemsPerPageFirstPage: itemsPerPageFirstPage,
              itemsPerPageContinuation: itemsPerPageContinuation,
              remainingItems: totalItems > itemsPerPageFirstPage ? totalItems - itemsPerPageFirstPage : 0,
              continuationPagesNeeded: totalItems > itemsPerPageFirstPage ? Math.ceil((totalItems - itemsPerPageFirstPage) / itemsPerPageContinuation) : 0,
              totalPagesNeeded: totalPagesNeeded,
              expectedDistribution: totalPagesNeeded > 1
                ? `Page 1: items 1-${itemsPerPageFirstPage} (indices 0-${itemsPerPageFirstPage - 1}), ` +
                `Page 2+: items ${itemsPerPageFirstPage + 1}-${totalItems} (indices ${itemsPerPageFirstPage}-${totalItems - 1})`
                : `Page 1: items 1-${totalItems} (indices 0-${totalItems - 1})`
            });

            // Update block pagination info
            const newPagination = {
              spansMultiplePages: totalPagesNeeded > 1,
              itemsPerPage: itemsPerPageFirstPage, // For first page (currentPage = 0)
              itemsPerPageFirstPage: itemsPerPageFirstPage,
              itemsPerPageContinuation: itemsPerPageContinuation,
              totalItems: totalItems,
              totalPages: totalPagesNeeded,
              currentPage: 0 // Original block is always page 0
            };

            const paginationChanged = !block.pagination ||
              JSON.stringify(block.pagination) !== JSON.stringify(newPagination);

            if (paginationChanged) {
              block.pagination = newPagination;
              console.log('[Pagination Loop] hasChanges = true at line 4886 (paginationChanged)');
              hasChanges = true;
              console.log('[Pagination] ✓ Set block.pagination:', {
                widgetType: block.widgetType,
                blockId: block.id,
                totalItems: newPagination.totalItems,
                totalPages: newPagination.totalPages,
                itemsPerPageFirstPage: newPagination.itemsPerPageFirstPage,
                itemsPerPageContinuation: newPagination.itemsPerPageContinuation,
                spansMultiplePages: newPagination.spansMultiplePages
              });
            }

            // Create or remove continuation pages
            // CRITICAL: Find continuation pages that are DIRECT continuations of the original block
            // Include pages with cloned blocks even if not marked as continuation (they should be)
            const existingContinuationPages = newPages.filter((p, pageIdx) => {
              // Must have cloned blocks OR be marked as continuation page
              const hasClonedBlocks = p.blocks.some(b => b.isClonedBlock && b.originalBlockId === block.id);
              if (!hasClonedBlocks && !p.isContinuationPage) return false;

              // Must be continuation of this specific block (or have cloned blocks from this block)
              if (p.continuationOf && p.continuationOf !== block.id) return false;

              // Ensure this is a direct continuation, not a nested one
              // Check that the block on this page is a direct clone (not a clone of a clone)
              const pageBlock = p.blocks.find(b => b.isClonedBlock && b.originalBlockId === block.id);
              if (!pageBlock) {
                return false;
              }

              // Block ID should be exactly: blockId-continuation-N (not nested like continuation-1-continuation-1)
              // Escape special regex characters in block.id
              const escapedBlockId = block.id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              const expectedPattern = new RegExp(`^${escapedBlockId}-continuation-\\d+$`);
              const isValidDirectContinuation = expectedPattern.test(pageBlock.id);

              if (!isValidDirectContinuation) {
                return false;
              }

              // CRITICAL: Fix incorrectly marked pages - ensure continuation pages are ALWAYS marked
              // This is essential for continuation pages loaded from templates
              if (!p.isContinuationPage && hasClonedBlocks) {
                p.isContinuationPage = true;
                p.continuationOf = block.id;
                console.log('[Pagination Loop] hasChanges = true at line 4931 (add isContinuationPage flag)');
                hasChanges = true;
                console.log('[Pagination] 🔒 Fixed continuation page marking:', {
                  pageId: p.id,
                  pageName: p.name,
                  continuationOf: block.id
                });
              }

              // Also check if marked as continuation but missing continuationOf
              if (p.isContinuationPage && !p.continuationOf && hasClonedBlocks) {
                p.continuationOf = block.id;
                console.log('[Pagination Loop] hasChanges = true at line 4943 (add continuationOf field)');
                hasChanges = true;
              }

              return true;
            });

            // Sort existing continuation pages by their page index
            existingContinuationPages.sort((a, b) => {
              const aBlock = a.blocks.find(b => b.originalBlockId === block.id);
              const bBlock = b.blocks.find(b => b.originalBlockId === block.id);
              const aIndex = aBlock?.pagination?.currentPage || 0;
              const bIndex = bBlock?.pagination?.currentPage || 0;
              return aIndex - bIndex;
            });

            // First, update blocks on existing continuation pages to match original block
            // SIMPLE: Each continuation page gets currentPage = 1, 2, 3... (not 0)
            // CRITICAL: Always update continuation pages with latest pagination values
            existingContinuationPages.forEach((contPage, idx) => {
              // Find the cloned block on this continuation page
              const existingClonedBlock = contPage.blocks.find(b =>
                b.isClonedBlock && b.originalBlockId === block.id
              );

              if (existingClonedBlock) {
                // CRITICAL: Preserve the original pageIndex from the block's pagination or continuationPageIndex
                // This ensures template-loaded continuation pages keep their original index
                // Use continuationPageIndex if available, otherwise use currentPage from pagination, otherwise use array index + 1
                let pageIndex = contPage.continuationPageIndex;
                if (pageIndex === undefined || pageIndex === null) {
                  pageIndex = existingClonedBlock.pagination?.currentPage;
                }
                if (pageIndex === undefined || pageIndex === null || pageIndex === 0) {
                  pageIndex = idx + 1;
                }
                const clonedBlockId = `${block.id}-continuation-${pageIndex}`;

                // Update existing cloned block with latest config/style from original
                existingClonedBlock.id = clonedBlockId;
                existingClonedBlock.isClonedBlock = true;
                existingClonedBlock.originalBlockId = block.id;

                // Update all other properties from original block (including dataConfig)
                Object.keys(block).forEach(key => {
                  // Fix: Protection for independent resizing.
                  // If the clone was manually resized by the user, DO NOT overwrite its size with the original block's size.
                  if ((key === 'size' || key === 'manualResize') && existingClonedBlock.manualResize) {
                    return;
                  }

                  if (key !== 'id' && key !== 'isClonedBlock' && key !== 'originalBlockId' && key !== 'pagination' && key !== 'position') {
                    existingClonedBlock[key] = block[key];
                  }
                });

                // Set initial position or ensure block is not behind a header
                // RULE: If block is a cloned block on a continuation page, trust its template position
                // UNLESS it's at Y=0 or overlapping a header.
                const contPageNumber = newPages.indexOf(contPage) + 1;
                const headerOffset = getHeaderOffsetForPage(contPageNumber, newPages);

                if (!existingClonedBlock.position) {
                  console.log(`[Layout Debug] Setting initial position for new cloned block ${existingClonedBlock.id} on Page ${contPageNumber}`);
                  existingClonedBlock.position = {
                    x: block.position?.x || 0,
                    y: headerOffset
                  };
                } else {
                  // Existing block on continuation page (likely from template)
                  const currentY = existingClonedBlock.position.y || 0;

                  // If it's at 0 or behind the header, push it down
                  if (currentY < headerOffset) {
                    console.log(`[Layout Debug] Pushing continuation block ${existingClonedBlock.id} below header on Page ${contPageNumber}: ${currentY}px -> ${headerOffset}px`);
                    existingClonedBlock.position = {
                      ...existingClonedBlock.position,
                      y: headerOffset
                    };
                  } else {
                    // It's already below the header (or no header exists)
                    // Trust the saved position from the template
                    console.log(`[Layout Debug] Trusting template position for continuation block ${existingClonedBlock.id} on Page ${contPageNumber}: Y=${currentY}px`);
                  }
                }

                // CRITICAL: Always update pagination with latest values from original block
                // Read from original block's dataConfig first (most up-to-date), then fall back to pagination
                // This ensures changes to itemsPerPageContinuation are immediately reflected
                const continuationItemsPerPage =
                  block.dataConfig?.itemsPerPageContinuation ||
                  block.pagination?.itemsPerPageContinuation ||
                  block.pagination?.itemsPerPage ||
                  20;
                const firstPageItemsPerPage =
                  block.dataConfig?.itemsPerPageFirstPage ||
                  block.dataConfig?.itemsPerPage ||
                  block.pagination?.itemsPerPageFirstPage ||
                  block.pagination?.itemsPerPage ||
                  20;

                // Always update pagination to ensure continuation pages have latest values
                // This is critical when user changes itemsPerPageFirstPage or itemsPerPageContinuation
                const newPagination = {
                  spansMultiplePages: block.pagination?.spansMultiplePages || false,
                  itemsPerPage: continuationItemsPerPage, // Use continuation itemsPerPage for continuation pages
                  itemsPerPageFirstPage: firstPageItemsPerPage, // Keep reference to first page value for startIndex calculation
                  itemsPerPageContinuation: continuationItemsPerPage,
                  totalItems: block.pagination?.totalItems || 0,
                  totalPages: block.pagination?.totalPages || 1,
                  currentPage: pageIndex // Page 1, 2, 3, etc.
                };

                // Check if pagination values have changed
                const paginationChanged = !existingClonedBlock.pagination ||
                  JSON.stringify(existingClonedBlock.pagination) !== JSON.stringify(newPagination);

                if (paginationChanged) {
                  existingClonedBlock.pagination = newPagination;
                  console.log('[Pagination Loop] hasChanges = true at line 5072 (clone paginationChanged)');
                  hasChanges = true;
                }

                // CRITICAL: Ensure page's continuationPageIndex matches the block's currentPage
                // This ensures consistency and prevents duplicate detection issues
                if (contPage.continuationPageIndex !== pageIndex) {
                  contPage.continuationPageIndex = pageIndex;
                  console.log('[Pagination Loop] hasChanges = true at line 5079 (fix continuationPageIndex)');
                  hasChanges = true;
                }

                // CRITICAL: Ensure page is ALWAYS marked as continuation (cannot be unmarked)
                // This is essential for continuation pages from templates
                if (!contPage.isContinuationPage) {
                  contPage.isContinuationPage = true;
                  console.log('[Pagination Loop] hasChanges = true at line 5086 (reinstate isContinuationPage)');
                  hasChanges = true;
                  console.log('[Pagination] 🔒 Re-marked continuation page:', {
                    pageId: contPage.id,
                    pageName: contPage.name,
                    continuationOf: block.id
                  });
                }

                // Ensure continuationOf is set correctly
                if (contPage.continuationOf !== block.id) {
                  contPage.continuationOf = block.id;
                  console.log('[Pagination Loop] hasChanges = true at line 5097 (reinstate continuationOf)');
                  hasChanges = true;
                }

                // CRITICAL: Ensure continuationForPage is set so the 'for' loop (new system) can find these pages
                // Without this, the new system creates duplicate pages with overlapping data
                if (contPage.continuationForPage !== page.id) {
                  contPage.continuationForPage = page.id;
                  hasChanges = true;
                }

                // CRITICAL: Double-check that the page still has cloned blocks
                // If it doesn't, something went wrong - log a warning
                const stillHasClonedBlocks = contPage.blocks.some(b => b.isClonedBlock && b.originalBlockId === block.id);
                if (!stillHasClonedBlocks) {
                  console.error('[Pagination] ⚠️ Continuation page lost its cloned blocks!', {
                    pageId: contPage.id,
                    pageName: contPage.name,
                    blocks: contPage.blocks.map(b => ({ id: b.id, isClonedBlock: b.isClonedBlock, originalBlockId: b.originalBlockId }))
                  });
                }
              } else {
                // No block found - this shouldn't happen, but create one
                const pageIndex = contPage.continuationPageIndex || 1;
                const clonedBlockId = `${block.id}-continuation-${pageIndex}`;
                contPage.blocks = [{
                  ...block,
                  id: clonedBlockId,
                  isClonedBlock: true,
                  originalBlockId: block.id,
                  pagination: {
                    ...block.pagination,
                    currentPage: pageIndex,
                    itemsPerPage: block.pagination?.itemsPerPageContinuation || block.pagination?.itemsPerPage || 20,
                    itemsPerPageFirstPage: block.pagination?.itemsPerPageFirstPage || block.pagination?.itemsPerPage || 20,
                    itemsPerPageContinuation: block.pagination?.itemsPerPageContinuation || block.pagination?.itemsPerPage || 20,
                    totalItems: block.pagination?.totalItems || 0,
                    totalPages: block.pagination?.totalPages || 1
                  }
                }];
                hasChanges = true;
              }
            });

            const pagesNeeded = totalPagesNeeded > 1 ? totalPagesNeeded - 1 : 0;

            console.log(`[Pagination] Block ${block.id} needs ${pagesNeeded} continuation pages.`);

            // 1. Ensure `block` has a clone on exactly Page 1 to `pagesNeeded`
            for (let pageIndex = 1; pageIndex <= pagesNeeded; pageIndex++) {
              // Find existing shared continuation page for this pageIndex
              let sharedPage = newPages.find(p =>
                p.isContinuationPage &&
                p.continuationForPage === page.id &&
                p.continuationPageIndex === pageIndex
              );

              const continuationItemsPerPage = block.dataConfig?.itemsPerPageContinuation || block.pagination?.itemsPerPageContinuation || block.pagination?.itemsPerPage || 20;
              const firstPageItemsPerPage = block.dataConfig?.itemsPerPageFirstPage || block.dataConfig?.itemsPerPage || block.pagination?.itemsPerPageFirstPage || block.pagination?.itemsPerPage || 20;
              const calculatedStartIndex = firstPageItemsPerPage + (pageIndex - 1) * continuationItemsPerPage;
              const calculatedEndIndex = calculatedStartIndex + continuationItemsPerPage;

              const clonedBlockId = `${block.id}-continuation-${pageIndex}`;

              // Check if an existing clone already exists with user-set dimensions
              const existingClone = newPages.reduce((found, p) => {
                if (found) return found;
                return p.blocks?.find(b => b.id === clonedBlockId);
              }, null);

              // Preserve user-set height from the existing clone or original block
              const preserveHeight = existingClone?.manualResize
                ? existingClone.size?.height
                : (block.manualResize ? block.size?.height : undefined);
              const preserveManualResize = existingClone?.manualResize || block.manualResize || false;

              const clonedBlock = {
                ...block, // Copy all properties from original block (including updated dataConfig)
                id: clonedBlockId, // Unique ID for clone
                isClonedBlock: true, // Mark as cloned
                originalBlockId: block.id, // Reference to original
                manualResize: preserveManualResize,
                size: { ...(block.size || {}), height: preserveHeight },
                style: { ...(block.style || {}), height: preserveHeight },
                position: { x: block.position?.x || 0, y: getHeaderOffsetForPage(pageIndex + 1, newPages) },
                pagination: {
                  spansMultiplePages: block.pagination?.spansMultiplePages || false,
                  itemsPerPage: continuationItemsPerPage,
                  itemsPerPageFirstPage: firstPageItemsPerPage,
                  itemsPerPageContinuation: continuationItemsPerPage,
                  totalItems: block.pagination?.totalItems || 0,
                  totalPages: block.pagination?.totalPages || 1,
                  currentPage: pageIndex // Page 1, 2, 3, etc.
                }
              };

              if (sharedPage) {
                // Ensure it has this table
                if (!sharedPage.blocks.some(b => b.isClonedBlock && b.originalBlockId === block.id)) {
                  console.log(`[Pagination] Sharing existing continuation page ${pageIndex} for page ${page.id}`);

                  // Inherit placeholder dummy geometry if available
                  const placeholderPage = newPages.find(p => p.isPlaceholder && p.placeholderForPage === page.id);
                  if (placeholderPage) {
                    const dummyWidget = placeholderPage.blocks.find(pb => pb.isPlaceholderWidget && pb.placeholderForWidget === block.id);
                    if (dummyWidget) {
                      clonedBlock.style = {
                        ...clonedBlock.style,
                        x: dummyWidget.position?.x ?? dummyWidget.style?.x,
                        y: dummyWidget.position?.y ?? dummyWidget.style?.y,
                        width: dummyWidget.size?.width ?? dummyWidget.style?.width,
                        height: dummyWidget.size?.height ?? dummyWidget.style?.height
                      };
                      clonedBlock.position = {
                        x: dummyWidget.position?.x ?? dummyWidget.style?.x ?? clonedBlock.position?.x ?? 0,
                        y: dummyWidget.position?.y ?? dummyWidget.style?.y ?? clonedBlock.position?.y ?? 0
                      };
                      clonedBlock.size = {
                        width: dummyWidget.size?.width ?? dummyWidget.style?.width ?? clonedBlock.size?.width ?? 200,
                        height: dummyWidget.size?.height ?? dummyWidget.style?.height ?? clonedBlock.size?.height ?? 150
                      };
                      clonedBlock.manualResize = dummyWidget.manualResize;
                    }
                  }

                  sharedPage.blocks.push(clonedBlock);
                  console.log('[Pagination Loop] hasChanges = true at line 5214 (push new cloned block to shared page)');
                  hasChanges = true;
                }
              } else {
                // Create entirely new shared page!
                const placeholderPage = newPages.find(p => p.isPlaceholder && p.placeholderForPage === page.id);
                const aestheticBlocks = [];
                if (placeholderPage) {
                  placeholderPage.blocks.forEach(pb => {
                    if (pb.isPlaceholderWidget) {
                      if (pb.placeholderForWidget === block.id) {
                        // Handle dummy geometry for THIS table
                        clonedBlock.style = {
                          ...clonedBlock.style,
                          x: pb.position?.x ?? pb.style?.x,
                          y: pb.position?.y ?? pb.style?.y,
                          width: pb.size?.width ?? pb.style?.width,
                          height: pb.size?.height ?? pb.style?.height
                        };
                        clonedBlock.position = {
                          x: pb.position?.x ?? pb.style?.x ?? clonedBlock.position?.x ?? 0,
                          y: pb.position?.y ?? pb.style?.y ?? clonedBlock.position?.y ?? 0
                        };
                        clonedBlock.size = {
                          width: pb.size?.width ?? pb.style?.width ?? clonedBlock.size?.width ?? 200,
                          height: pb.size?.height ?? pb.style?.height ?? clonedBlock.size?.height ?? 150
                        };
                        clonedBlock.manualResize = pb.manualResize;
                      } else {
                        // Handle static clones for OTHER widgets
                        const originalOtherBlock = page.blocks.find(b => b.id === pb.placeholderForWidget);
                        if (originalOtherBlock) {
                          aestheticBlocks.push({
                            ...originalOtherBlock,
                            id: `${originalOtherBlock.id}-static-clone-${Date.now()}-${pageIndex}-${Math.random().toString(36).substr(2, 5)}`,
                            isStaticClone: true,
                            originalBlockId: originalOtherBlock.id,
                            position: { ...pb.position },
                            size: { ...pb.size },
                            style: {
                              ...originalOtherBlock.style,
                              x: pb.position?.x ?? pb.style?.x,
                              y: pb.position?.y ?? pb.style?.y,
                              width: pb.size?.width ?? pb.style?.width,
                              height: pb.size?.height ?? pb.style?.height
                            },
                            manualResize: pb.manualResize
                          });
                        }
                      }
                    } else {
                      // Copy placeholder aesthetic blocks natively
                      aestheticBlocks.push({
                        ...pb,
                        id: `${pb.id}-clone-${Date.now()}-${pageIndex}-${Math.random().toString(36).substr(2, 5)}`
                      });
                    }
                  });
                } else {
                  // No placeholder page — auto-generated continuation pages only
                  // include the paginated table widget itself, NOT other elements/widgets
                  // (elements/widgets from the parent page are intentionally excluded)
                }

                // Determine insertIndex for NEW page
                const widgetPageIndex = newPages.findIndex(p => p.id === page.id);
                let insertIndex = widgetPageIndex;
                for (let j = widgetPageIndex + 1; j < newPages.length; j++) {
                  if (newPages[j].isContinuationPage && newPages[j].continuationForPage === page.id) {
                    insertIndex = j;
                  } else {
                    break;
                  }
                }

                // Create the page
                const continuationPage = {
                  ...(placeholderPage || {}),
                  id: `page-continuation-${page.id}-${pageIndex}-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
                  name: `Continuation`,
                  blocks: [...aestheticBlocks, clonedBlock],
                  isContinuationPage: true,
                  continuationOf: block.id,
                  continuationForPage: page.id,
                  continuationPageIndex: pageIndex,
                  isPlaceholder: false,
                  placeholderForPage: null
                };

                console.log(`[Pagination] Inserting continuation page ${pageIndex} at index ${insertIndex + 1}`);
                newPages.splice(insertIndex + 1, 0, continuationPage);
                console.log('[Pagination Loop] hasChanges = true at line 5186 (splice new shared continuation page)');
                hasChanges = true;
              }
            }

            // 2. Cleanup: Remove clones mapping to THIS block on pages with pageIndex > pagesNeeded
            const excessPages = newPages.filter(p =>
              p.isContinuationPage &&
              p.continuationForPage === page.id &&
              p.continuationPageIndex > pagesNeeded &&
              p.blocks.some(b => b.isClonedBlock && b.originalBlockId === block.id)
            );

            if (excessPages.length > 0) {
              console.log(`[Pagination] Cleaning up ${excessPages.length} excess continuation pages for block ${block.id}`);
              excessPages.forEach(pageItem => {
                const cloneIdx = pageItem.blocks.findIndex(b => b.isClonedBlock && b.originalBlockId === block.id);
                if (cloneIdx >= 0) {
                  pageItem.blocks.splice(cloneIdx, 1);
                  console.log('[Pagination Loop] hasChanges = true at line 5129 (removed excess cloned block from page)');
                  hasChanges = true;
                }
              });
            }

            // 3. Cleanup: If ANY continuation page is now completely empty of cloned blocks, delete it and sync aesthetic blocks to Placeholder
            const emptyPageIndices = [];
            newPages.forEach((pageItem, idx) => {
              if (pageItem.isContinuationPage && pageItem.continuationForPage === page.id && !pageItem.blocks.some(b => b.isClonedBlock)) {
                emptyPageIndices.push(idx);
              }
            });

            if (emptyPageIndices.length > 0) {
              emptyPageIndices.sort((a, b) => b - a).forEach(idx => {
                const targetPage = newPages[idx];
                const placeholderForSource = newPages.find(ph => ph.isPlaceholder && ph.placeholderForPage === targetPage.continuationForPage);
                if (placeholderForSource) {
                  const aestheticBlocks = targetPage.blocks.filter(b => !b.isClonedBlock && !b.isPlaceholderWidget && !b.isStaticClone);
                  if (aestheticBlocks.length > 0) {
                    const placeholderWidgets = placeholderForSource.blocks.filter(b => b.isPlaceholderWidget || b.isStaticClone);
                    placeholderForSource.blocks = [
                      ...placeholderWidgets,
                      ...aestheticBlocks.map(b => ({ ...b, id: b.id.includes('-clone-') ? b.id.split('-clone-')[0] : b.id }))
                    ];
                    console.log(`[Pagination] ✅ Synced ${aestheticBlocks.length} aesthetic blocks back to placeholder before removal`);
                  }
                }
                newPages.splice(idx, 1);
                console.log('[Pagination Loop] hasChanges = true at line 5291 (deleted empty shared continuation page)');
                hasChanges = true;
                console.log(`[Pagination] Deleted empty shared continuation page at index ${idx}`);
              });
            }

            // Note: Excess page removal is now handled BEFORE creating new pages (see above)
            // This ensures proper cleanup when itemsPerPageContinuation increases

            // CRITICAL: Clean up any incorrectly nested continuation pages
            // Remove any continuation pages that are continuations of cloned blocks (nested continuations)
            const incorrectlyNestedPages = newPages.filter((p, idx) => {
              if (!p.isContinuationPage || p.continuationOf !== block.id) return false;

              const pageBlock = p.blocks.find(b => b.originalBlockId === block.id);
              if (!pageBlock) return false;

              // Check if this is a nested continuation (e.g., continuation-1-continuation-1)
              const blockIdParts = pageBlock.id.split('-continuation-');
              if (blockIdParts.length > 2) {
                console.log(`[Pagination] 🗑️ Removing incorrectly nested continuation page: ${pageBlock.id}`);
                return true;
              }

              return false;
            });

            // Remove incorrectly nested pages
            incorrectlyNestedPages.forEach(nestedPage => {
              const indexToRemove = newPages.indexOf(nestedPage);
              if (indexToRemove !== -1) {
                console.log(`[Pagination] 🗑️ Removing nested continuation page at index ${indexToRemove}`);
                newPages.splice(indexToRemove, 1);
                console.log('[Pagination Loop] hasChanges = true at line 5317 (removed incorrectly nested page)');
                hasChanges = true;
              }
            });

            // CRITICAL: Remove duplicate continuation pages
            // Find all continuation pages for this block and remove duplicates
            const allContinuationPagesForBlock = newPages
              .map((p, idx) => ({ page: p, index: idx }))
              .filter(({ page }) => page.continuationOf === block.id);

            // Group by page index to find duplicates
            const pagesByIndex = new Map();
            allContinuationPagesForBlock.forEach(({ page, index }) => {
              const pageBlock = page.blocks.find(b => b.originalBlockId === block.id);
              const pageIndex = pageBlock?.pagination?.currentPage || 0;

              if (!pagesByIndex.has(pageIndex)) {
                pagesByIndex.set(pageIndex, []);
              }
              pagesByIndex.get(pageIndex).push({ page, index });
            });

            // Remove duplicates (keep the first one, remove the rest)
            pagesByIndex.forEach((pagesWithSameIndex, pageIndex) => {
              if (pagesWithSameIndex.length > 1) {
                console.log(`[Pagination] 🗑️ Found ${pagesWithSameIndex.length} duplicate pages for page index ${pageIndex}, removing ${pagesWithSameIndex.length - 1}`);
                // Keep the first one, remove the rest (in reverse order to maintain indices)
                for (let i = pagesWithSameIndex.length - 1; i > 0; i--) {
                  const { index } = pagesWithSameIndex[i];
                  console.log(`[Pagination] 🗑️ Removing duplicate continuation page at index ${index}`);
                  newPages.splice(index, 1);
                  console.log('[Pagination Loop] hasChanges = true at line 5347 (removed duplicate continuation page)');
                  hasChanges = true;
                }
              }
            });
          }
        });
      });

      // FINAL CLEANUP: Remove duplicate continuation pages and invalid pages
      // This is CRITICAL - any page with cloned blocks MUST be marked as continuation
      const finalPagesToRemove = [];
      const seenBlockIds = new Map(); // Track block IDs we've seen to identify duplicates

      // First pass: Mark pages with cloned blocks as continuation (don't remove them - they're valid)
      // Then identify pages marked as continuation but with no cloned blocks (these are invalid and should be removed)
      newPages.forEach((page, pageIdx) => {
        const hasClonedBlocks = page.blocks.some(b => b.isClonedBlock);
        const hasOriginalBlocks = page.blocks.some(b => !b.isClonedBlock);

        // CRITICAL: If a page has cloned blocks but isn't marked as continuation, MARK IT (don't remove)
        // This ensures continuation pages from templates are properly marked
        if (hasClonedBlocks && !page.isContinuationPage) {
          if (!hasOriginalBlocks) {
            // Before auto-marking, check if the original block still exists
            const clonedBlock = page.blocks.find(b => b.isClonedBlock);
            const originalStillExists = clonedBlock && originalBlockIds.has(clonedBlock.originalBlockId);
            if (originalStillExists) {
              // Page has only cloned blocks and original exists - mark it as continuation
              page.isContinuationPage = true;
              if (clonedBlock && clonedBlock.originalBlockId && !page.continuationOf) {
                page.continuationOf = clonedBlock.originalBlockId;
              }
              console.log('[Pagination Loop] hasChanges = true at line 5379 (auto-marked page with cloned blocks as continuation)');
              hasChanges = true;
              console.log('[Pagination] FINAL CLEANUP: Auto-marked page with cloned blocks as continuation:', {
                pageId: page.id,
                pageName: page.name,
                continuationOf: page.continuationOf,
                pageIdx: pageIdx
              });
            } else {
              // Original block no longer exists - this is an orphaned page, remove it
              console.log('[Pagination] FINAL CLEANUP: Removing orphaned page (original block gone):', {
                pageId: page.id,
                pageName: page.name
              });
              if (!finalPagesToRemove.includes(pageIdx)) {
                finalPagesToRemove.push(pageIdx);
              }
            }
          } else {
            // Mixed page (has both original and cloned blocks) - this is invalid, remove it
            if (!finalPagesToRemove.includes(pageIdx)) {
              finalPagesToRemove.push(pageIdx);
            }
          }
        }

        // Remove pages that are marked as continuation but have no cloned blocks (these are invalid)
        if (page.isContinuationPage && !hasClonedBlocks) {
          console.log('[Pagination] FINAL CLEANUP: Removing page marked as continuation but has no cloned blocks:', {
            pageId: page.id,
            pageName: page.name,
            pageIdx: pageIdx
          });
          if (!finalPagesToRemove.includes(pageIdx)) {
            finalPagesToRemove.push(pageIdx);
          }
        }
      });

      // Second pass: track block IDs to find duplicates (only for properly marked continuation pages)
      newPages.forEach((page, pageIdx) => {
        // Skip pages already marked for removal
        if (finalPagesToRemove.includes(pageIdx)) return;

        // Track block IDs to find duplicates
        page.blocks.forEach(block => {
          if (block.isClonedBlock) {
            const blockId = block.id;
            if (seenBlockIds.has(blockId)) {
              // This block ID already exists in another page - it's a duplicate
              const existingPageIdx = seenBlockIds.get(blockId);
              const existingPage = newPages[existingPageIdx];

              // Always remove the page that's NOT properly marked as continuation
              // If both are continuation pages, remove the later one
              if (!page.isContinuationPage) {
                if (!finalPagesToRemove.includes(pageIdx)) {
                  finalPagesToRemove.push(pageIdx);
                }
              } else if (!existingPage.isContinuationPage) {
                if (!finalPagesToRemove.includes(existingPageIdx)) {
                  finalPagesToRemove.push(existingPageIdx);
                }
              } else if (pageIdx > existingPageIdx) {
                // Both are continuation pages, remove the later one
                if (!finalPagesToRemove.includes(pageIdx)) {
                  finalPagesToRemove.push(pageIdx);
                }
              } else {
                if (!finalPagesToRemove.includes(existingPageIdx)) {
                  finalPagesToRemove.push(existingPageIdx);
                }
              }
            } else {
              seenBlockIds.set(blockId, pageIdx);
            }
          }
        });
      });

      // Remove pages marked for removal (in reverse order to maintain indices)
      const uniqueFinalPagesToRemove = [...new Set(finalPagesToRemove)].sort((a, b) => b - a);
      uniqueFinalPagesToRemove.forEach(idx => {
        newPages.splice(idx, 1);
        console.log('[Pagination Loop] hasChanges = true at line 5447 (removed page from finalPagesToRemove)');
        hasChanges = true;
      });

      // AGGRESSIVE CLEANUP: Run multiple passes to ensure all duplicates and invalid pages are removed
      let cleanupPasses = 0;
      const maxCleanupPasses = 5; // Increased passes to ensure thorough cleanup
      let foundIssues = true;

      while (foundIssues && cleanupPasses < maxCleanupPasses) {
        cleanupPasses++;
        foundIssues = false;
        const blockIdToPageMap = new Map();
        const pagesToRemoveThisPass = [];

        newPages.forEach((page, pageIdx) => {
          const hasClonedBlocks = page.blocks.some(b => b.isClonedBlock);

          // CRITICAL: Remove ANY page with cloned blocks that isn't marked as continuation
          if (hasClonedBlocks && !page.isContinuationPage) {
            if (!pagesToRemoveThisPass.includes(pageIdx)) {
              pagesToRemoveThisPass.push(pageIdx);
              foundIssues = true;
            }
            return; // Skip processing blocks on this invalid page
          }

          // 2. Remove continuation pages that somehow lost their cloned blocks
          // EXCEPT if they are user-defined placeholders
          if (page.isContinuationPage && !hasClonedBlocks && !page.isPlaceholder) {
            if (!pagesToRemoveThisPass.includes(pageIdx)) {
              pagesToRemoveThisPass.push(pageIdx);
              foundIssues = true;
            }
            return;
          }

          // Track block IDs to find duplicates
          page.blocks.forEach(block => {
            if (block.isClonedBlock) {
              const blockId = block.id;

              if (blockIdToPageMap.has(blockId)) {
                const existingPageIdx = blockIdToPageMap.get(blockId);

                // Always remove the one that's not properly marked, or the later one
                if (!page.isContinuationPage) {
                  if (!pagesToRemoveThisPass.includes(pageIdx)) {
                    pagesToRemoveThisPass.push(pageIdx);
                    foundIssues = true;
                  }
                } else if (pageIdx > existingPageIdx) {
                  if (!pagesToRemoveThisPass.includes(pageIdx)) {
                    pagesToRemoveThisPass.push(pageIdx);
                    foundIssues = true;
                  }
                } else {
                  if (!pagesToRemoveThisPass.includes(existingPageIdx)) {
                    pagesToRemoveThisPass.push(existingPageIdx);
                    foundIssues = true;
                  }
                }
              } else {
                blockIdToPageMap.set(blockId, pageIdx);
              }
            }
          });
        });

        // Remove pages found in this pass (in reverse order to maintain indices)
        pagesToRemoveThisPass.sort((a, b) => b - a).forEach(idx => {
          newPages.splice(idx, 1);
          console.log('[Pagination Loop] hasChanges = true at line 5520 (removed page during aggressive cleanup)');
          hasChanges = true;
        });
      }

      // FINAL VERIFICATION: Ensure ALL pages with cloned blocks are marked as continuation
      // This is CRITICAL for continuation pages loaded from templates
      // BUT only if the original block still exists (otherwise it's an orphaned page)
      newPages.forEach((page, pageIdx) => {
        const hasClonedBlocks = page.blocks.some(b => b.isClonedBlock);
        if (hasClonedBlocks && !page.isContinuationPage) {
          // Check that the original block still exists
          const clonedBlock = page.blocks.find(b => b.isClonedBlock);
          if (clonedBlock && originalBlockIds.has(clonedBlock.originalBlockId)) {
            // CRITICAL: Auto-mark as continuation page (don't remove - it might be from a template)
            page.isContinuationPage = true;
            if (clonedBlock.originalBlockId && !page.continuationOf) {
              page.continuationOf = clonedBlock.originalBlockId;
            }
            hasChanges = true;
            console.log('[Pagination] ✅ FINAL: Auto-marked page as continuation:', {
              pageId: page.id,
              pageName: page.name,
              continuationOf: page.continuationOf,
              hasClonedBlocks: true
            });
          }
          // If original doesn't exist, the orphan cleanup above already handles it
        }

        // CRITICAL: Ensure continuation pages cannot be unmarked
        // If a page is marked as continuation, ensure it has cloned blocks or remove the flag
        if (page.isContinuationPage && !hasClonedBlocks) {
          console.warn('[Pagination] ⚠️ Page marked as continuation but has no cloned blocks:', {
            pageId: page.id,
            pageName: page.name
          });
        }
      });

      // FINAL SAFEGUARD: Before returning, ensure ALL pages with cloned blocks are marked as continuation
      // This is the last line of defense to prevent continuation pages from being unmarked
      // BUT only if the original block still exists (prevents infinite loops from orphaned pages)
      let finalHasChanges = hasChanges;
      newPages.forEach(page => {
        const hasClonedBlocks = page.blocks?.some(b => b.isClonedBlock) || false;
        if (hasClonedBlocks && !page.isContinuationPage) {
          const clonedBlock = page.blocks.find(b => b.isClonedBlock);
          // Only mark if the original block still exists
          if (clonedBlock && originalBlockIds.has(clonedBlock.originalBlockId)) {
            console.error('[Pagination] 🚨 FINAL SAFEGUARD: Auto-marking page as continuation before return', {
              pageId: page.id,
              pageName: page.name,
              clonedBlocksCount: page.blocks.filter(b => b.isClonedBlock).length
            });
            page.isContinuationPage = true;
            if (clonedBlock?.originalBlockId && !page.continuationOf) {
              page.continuationOf = clonedBlock.originalBlockId;
            }
            finalHasChanges = true;
          }
        }
      });

      // ... (code omitted up to ALREADY_FIXED area to avoid massive diff)

      // EXPLICIT SORT: Ensure all continuation pages always appear in the correct sequential order
      // right after their parent page.
      if (hasChanges) {
        // Group pages: parent page -> [continuation pages ordered by index]
        const reorderedPages = [];
        const processedPageIds = new Set();

        newPages.forEach(page => {
          if (processedPageIds.has(page.id)) return;

          if (!page.isContinuationPage) {
            // This is a normal/parent page or a placeholder page
            reorderedPages.push(page);
            processedPageIds.add(page.id);

            // Find all continuation pages for this parent page
            const myContinuations = newPages.filter(p =>
              p.isContinuationPage &&
              p.continuationForPage === page.id
            );

            // Sort them by continuationPageIndex ascending
            myContinuations.sort((a, b) => {
              const diff = (a.continuationPageIndex || 0) - (b.continuationPageIndex || 0);
              if (diff !== 0) return diff;
              // Tie breaker if index is somehow the same
              return (newPages.indexOf(a) - newPages.indexOf(b));
            });

            // Append them immediately after the parent page
            myContinuations.forEach(cont => {
              reorderedPages.push(cont);
              processedPageIds.add(cont.id);
            });
          } else if (!page.continuationForPage) {
            // Edge case: continuation page with no parent link (orphaned but survived cleanup)
            reorderedPages.push(page);
            processedPageIds.add(page.id);
          }
        });

        // Pick up any stragglers
        newPages.forEach(page => {
          if (!processedPageIds.has(page.id)) {
            reorderedPages.push(page);
          }
        });

        return reorderedPages;
      }

      return finalHasChanges ? newPages : prev;
    });
  }, [dataContext, getBlockData, blockCountTrigger]); // Use blockCountTrigger instead of pages to prevent infinite loop

  // ========== DIRECTIONAL SYNC: ACTIVE PAGE → COUNTERPART ==========
  // Auto-Generated Continuation Pages and Continuation Placeholder Pages are the SAME concept.
  // Edits on the active one must immediately overwrite the layout on the hidden one.
  const lastSyncRef = useRef(null);
  useEffect(() => {
    if (!currentPageId) return;

    const activePage = pages.find(p => p.id === currentPageId);
    if (!activePage) return;

    // Determine the type of the active page
    const isContinuation = activePage.isContinuationPage && !activePage.isPlaceholder;
    const isPlaceholder = activePage.isPlaceholder;
    const hasPlaceholder = pages.some(p => p.isPlaceholder && p.placeholderForPage === currentPageId);
    const hasContinuations = pages.some(p => p.isContinuationPage && !p.isPlaceholder && p.continuationForPage === currentPageId);
    const isParentPage = !isContinuation && !isPlaceholder && (hasPlaceholder || hasContinuations);

    if (!isContinuation && !isPlaceholder && !isParentPage) return;

    // Helper to get a string representation of relevant properties for comparison
    const getPropStr = (block) => {
      // NOTE: Intentionally omits geometry (position/size) so we only sync non-layout properties between Parent <-> Clones
      return JSON.stringify({
        c: block.config,
        dc: block.dataConfig,
        cd: { ...block.customData, __isPlaceholder__: undefined, reason: undefined },
        s: { ...block.style, x: 0, y: 0, width: 0, height: 0 },
        t: block.text
      });
    };

    // Helper to get raw structural fingerprint with geometry
    const getGeometryStr = (block) => {
      return `${block.position?.x},${block.position?.y},${block.size?.width},${block.size?.height},${block.manualResize}`;
    };

    // Helper to sync non-geometry properties from source to target
    const syncPropsOnly = (target, source) => {
      const cd = source.customData ? JSON.parse(JSON.stringify(source.customData)) : null;
      if (cd) {
        if (target.customData?.__isPlaceholder__) cd.__isPlaceholder__ = true;
        if (target.customData?.reason) cd.reason = target.customData.reason;
      }
      return {
        ...target,
        config: source.config ? JSON.parse(JSON.stringify(source.config)) : target.config,
        dataConfig: source.dataConfig ? JSON.parse(JSON.stringify(source.dataConfig)) : target.dataConfig,
        customData: cd || target.customData,
        text: source.text !== undefined ? source.text : target.text,
        style: { ...source.style, x: target.style?.x, y: target.style?.y, width: target.style?.width, height: target.style?.height }
      };
    };

    // Fingerprint includes geometry for Placeholder and Continuation, because they sync layout.
    // For Parent, we only sync Properties to its clones, but let's include its geometry so mutations are detected.
    const fingerprint = `${currentPageId}::${activePage.blocks.map(b =>
      `${b.id}:${getPropStr(b)}:${getGeometryStr(b)}`
    ).join('|')}`;

    if (lastSyncRef.current === fingerprint) return;
    lastSyncRef.current = fingerprint;

    let needsUpdate = false;

    setPages(prev => {
      const updated = prev.map(p => ({ ...p, blocks: [...p.blocks] }));
      const current = updated.find(p => p.id === currentPageId);
      if (!current) return prev;

      if (isParentPage) {
        // Active is Parent Page -> Sync Properties TO Placeholder AND TO Continuations
        const placeholder = updated.find(p => p.isPlaceholder && p.placeholderForPage === current.id);
        const continuations = updated.filter(p => p.isContinuationPage && !p.isPlaceholder && p.continuationForPage === current.id);

        if (placeholder) {
          let phDirty = false;
          const newPhBlocks = placeholder.blocks.map(pb => {
            if (pb.isPlaceholderWidget) {
              const match = current.blocks.find(sb => sb.id === pb.placeholderForWidget);
              // Only sync if properties differ
              if (match && getPropStr(pb) !== getPropStr(match)) {
                phDirty = true;
                return syncPropsOnly(pb, match);
              }
            }
            return pb;
          });
          if (phDirty) {
            placeholder.blocks = newPhBlocks;
            needsUpdate = true;
          }
        }

        if (continuations.length > 0) {
          continuations.forEach(contPage => {
            let contDirty = false;
            // Map over continuation blocks so we don't accidentally drop unmatched blocks if they were aesthetic
            const newContBlocks = contPage.blocks.map(cb => {
              if (cb.isClonedBlock || cb.isStaticClone) {
                const match = current.blocks.find(sb => sb.id === cb.originalBlockId);
                if (match && getPropStr(cb) !== getPropStr(match)) {
                  contDirty = true;
                  return syncPropsOnly(cb, match);
                }
              }
              return cb;
            });
            if (contDirty) {
              contPage.blocks = newContBlocks;
              needsUpdate = true;
            }
          });
        }
      } else if (isContinuation && current.continuationForPage) {
        // Active is Continuation -> Sync Layout TO Placeholder
        let placeholder = updated.find(p => p.isPlaceholder && p.placeholderForPage === current.continuationForPage);

        // Auto-create Placeholder page if it doesn't exist so user's edits persist to the template
        if (!placeholder) {
          const sourcePage = updated.find(p => p.id === current.continuationForPage);
          if (sourcePage) {
            const newPlaceholderId = `placeholder-page-${Date.now()}`;

            const activeCloneIds = new Set(
              current.blocks
                .filter(b => b.isClonedBlock || b.isStaticClone)
                .map(b => b.originalBlockId)
            );

            const dummyWidgets = sourcePage.blocks
              .filter(b => b.type === 'widget' && activeCloneIds.has(b.id))
              .map(w => {
                const dummyWidgetId = `widget-placeholder-${w.id}-${Date.now()}`;
                let customData = { ...w.customData, __isPlaceholder__: true, reason: 'Placeholder Sample' };
                // Snap geometry from counterpart immediately
                const counterpart = current.blocks.find(cb => cb.originalBlockId === w.id);
                return {
                  ...w,
                  id: dummyWidgetId,
                  isPlaceholderWidget: true,
                  placeholderForWidget: w.id,
                  customData,
                  position: counterpart ? { ...counterpart.position } : { ...w.position },
                  size: counterpart ? { ...counterpart.size } : { ...w.size },
                  style: {
                    ...w.style,
                    x: counterpart ? counterpart.position?.x : w.position?.x,
                    y: counterpart ? counterpart.position?.y : w.position?.y
                  }
                };
              });

            placeholder = {
              id: newPlaceholderId,
              name: `Placeholder: ${sourcePage.name}`,
              isPlaceholder: true,
              placeholderForPage: sourcePage.id,
              blocks: dummyWidgets,
              config: {}
            };

            const sourceIndex = updated.findIndex(p => p.id === sourcePage.id);
            if (sourceIndex >= 0) {
              updated.splice(sourceIndex + 1, 0, placeholder);
            } else {
              updated.push(placeholder);
            }
            needsUpdate = true;
          }
        }

        if (placeholder) {
          // 1. Get current aesthetic blocks (non-widget) from the active Continuation Page
          const contAesthetic = current.blocks.filter(b => !b.isClonedBlock && !b.isStaticClone && !b.isPlaceholderWidget);

          // 2. Get all cloned parent-associated widgets
          const clonedWidgets = current.blocks.filter(b => b.isClonedBlock || b.isStaticClone);

          // 3. Keep original placeholder layout structure, update geometry
          const newPlaceholderBlocks = placeholder.blocks.filter(b => b.isPlaceholderWidget).map(dummy => {
            const correspondingClone = clonedWidgets.find(cw => cw.originalBlockId === dummy.placeholderForWidget);
            if (correspondingClone) {
              return {
                ...dummy,
                position: { ...correspondingClone.position },
                size: { ...correspondingClone.size },
                manualResize: correspondingClone.manualResize,
                style: {
                  ...dummy.style,
                  x: correspondingClone.position?.x,
                  y: correspondingClone.position?.y,
                  width: correspondingClone.size?.width,
                  height: correspondingClone.size?.height
                }
              };
            }
            return dummy;
          });

          // 4. Overwrite aesthetic blocks and static clones that have no matching dummy widget
          const finalBlocks = [
            ...newPlaceholderBlocks,
            ...contAesthetic.map(b => ({
              ...b,
              id: b.id.includes('-clone-') ? b.id.split('-clone-')[0] : b.id
            })),
            // PRESERVE static clones that didn't have a matching dummy widget
            ...clonedWidgets.filter(cw => !newPlaceholderBlocks.some(pb => pb.placeholderForWidget === cw.originalBlockId))
          ];

          if (JSON.stringify(placeholder.blocks) !== JSON.stringify(finalBlocks)) {
            placeholder.blocks = finalBlocks;
            needsUpdate = true;
          }
        }

      } else if (isPlaceholder && current.placeholderForPage) {
        // Active is Placeholder -> Sync Layout TO all Continuations for this source page
        const continuations = updated.filter(p => p.isContinuationPage && !p.isPlaceholder && p.continuationForPage === current.placeholderForPage);

        if (continuations.length > 0) {
          // 1. Get current aesthetic blocks (non-widget)
          const phAesthetic = current.blocks.filter(b => !b.isPlaceholderWidget);

          continuations.forEach(contPage => {
            // 2. Keep cloned widget, update its geometry from matching dummy widget natively
            const newContBlocks = contPage.blocks.filter(b => b.isClonedBlock || b.isStaticClone).map(cloned => {
              const dummy = current.blocks.find(b => b.isPlaceholderWidget && b.placeholderForWidget === cloned.originalBlockId);
              if (dummy) {
                return {
                  ...cloned,
                  position: { ...dummy.position },
                  size: { ...dummy.size },
                  manualResize: dummy.manualResize,
                  style: {
                    ...cloned.style,
                    x: dummy.position?.x,
                    y: dummy.position?.y,
                    width: dummy.size?.width,
                    height: dummy.size?.height
                  }
                };
              }
              return cloned;
            });

            // 3. Overwrite aesthetic blocks AND preserve static clones without dummy widget match
            const finalBlocks = [
              ...newContBlocks,
              ...phAesthetic.map(b => ({
                ...b,
                id: `${b.id}-clone-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
              })),
              // Filter unmatched static clones that were on the continuation page already
              ...contPage.blocks.filter(b => b.isStaticClone && !newContBlocks.find(nc => nc.originalBlockId === b.originalBlockId))
            ];

            const currGeom = contPage.blocks.map(b => `${b.position?.x},${b.position?.y},${b.size?.width},${b.size?.height}`).join('|');
            const newGeom = finalBlocks.map(b => `${b.position?.x},${b.position?.y},${b.size?.width},${b.size?.height}`).join('|');

            if (contPage.blocks.length !== finalBlocks.length || currGeom !== newGeom) {
              contPage.blocks = finalBlocks;
              needsUpdate = true;
            }
          });
        }
      }

      return needsUpdate ? updated : prev;
    });
  }, [pages, currentPageId]);

  // ========== WIDGET OVERLAP PREVENTION LOGIC ==========
  // Prevent widgets from overlapping each other
  // When a widget (like a table) expands, other widgets below it are pushed down
  // RULE: Widgets cannot overlap other widgets (but elements CAN overlap widgets)

  // Helper function to calculate actual content height for a widget based on its data
  const calculateActualWidgetHeight = useCallback((block) => {
    if (!block) return 100;

    // Fix: Respect manual resizing. If user set a fixed size, do not override it with content height.
    if (block.manualResize && block.size?.height) {
      return block.size.height;
    }

    const widgetType = block.widgetType || block.type;
    const ROW_HEIGHT = 35; // Approximate row height for tables
    const HEADER_HEIGHT = 50; // Table header height
    const WIDGET_PADDING = 40; // Widget padding (top + bottom)
    const TITLE_HEIGHT = 30; // Title height if present

    // For table-based widgets, calculate based on data items
    if (widgetType === 'backup-status-table' ||
      widgetType === 'table' ||
      widgetType?.includes('table')) {
      // Get the data for this block
      const blockData = getBlockData(block);
      let itemCount = 0;

      if (Array.isArray(blockData)) {
        itemCount = blockData.length;
      } else if (blockData?.data && Array.isArray(blockData.data)) {
        itemCount = blockData.data.length;
      } else if (block.pagination?.totalItems) {
        // Use pagination info if available - items on THIS page
        itemCount = Math.min(
          block.pagination.itemsPerPage || 20,
          block.pagination.totalItems
        );
      }

      // Calculate height: header + (rows * rowHeight) + padding + title
      const calculatedHeight = HEADER_HEIGHT + (itemCount * ROW_HEIGHT) + WIDGET_PADDING + TITLE_HEIGHT;

      // Use the larger of calculated or stored height
      return Math.max(calculatedHeight, block.size?.height || 100);
    }

    // For other widget types, use stored height or reasonable defaults
    if (widgetType === 'backups-summary' || widgetType === 'backups-cost-summary') {
      return Math.max(block.size?.height || 150, 150);
    }

    // For elements (text, title, etc.), use stored height or reasonable default
    if (block.type === 'element') {
      return block.size?.height || 50;
    }

    // Default: use stored height or minimum
    return block.size?.height || 100;
  }, [getBlockData]);

  // Helper function to check if two rectangles overlap
  const doBlocksOverlap = useCallback((block1, block2) => {
    const rect1 = {
      left: block1.position.x,
      right: block1.position.x + (block1.size?.width || 200),
      top: block1.position.y,
      bottom: block1.position.y + calculateActualWidgetHeight(block1)
    };

    const rect2 = {
      left: block2.position.x,
      right: block2.position.x + (block2.size?.width || 200),
      top: block2.position.y,
      bottom: block2.position.y + calculateActualWidgetHeight(block2)
    };

    // Check for horizontal overlap
    const horizontalOverlap = rect1.left < rect2.right && rect1.right > rect2.left;
    // Check for vertical overlap
    const verticalOverlap = rect1.top < rect2.bottom && rect1.bottom > rect2.top;

    return horizontalOverlap && verticalOverlap;
  }, [calculateActualWidgetHeight]);

  // ========== DYNAMIC DOCUMENT REFLOW SYSTEM ==========
  // This system reflows widgets across pages based on actual content height
  // Template pages define the ORDER of widgets, not their final page numbers
  // 
  // Example: Template has Page 1 (Backup Status, Summary) and Page 2 (Key Findings)
  // With real data, Backup Status might span 3 pages, so Key Findings ends up on Page 4+

  const documentReflowRunRef = useRef(false);

  // Create a stable key to detect when data is ready
  const dataContextKey = useMemo(() => {
    if (!dataContext || typeof dataContext !== 'object') return '';
    const keys = Object.keys(dataContext);
    if (keys.length === 0) return '';
    return `data-${keys.length}-${keys.slice(0, 3).join('-')}`;
  }, [dataContext]);

  // Document reflow effect - runs once after data is loaded and pagination is complete
  useEffect(() => {
    // Skip if already run
    if (documentReflowRunRef.current) {
      return;
    }

    // CRITICAL: Skip document reflow for Custom Templates in report mode
    // Smart Layout Adjustment handles this instead (preserves page structure)
    // CRITICAL: Skip document reflow for Custom Templates in report OR template (edit) mode
    // Smart Layout Adjustment handles this instead (preserves page structure)
    const isCustomTemplateReport = (builderMode === 'report' || builderMode === 'template') && initialTemplate && initialTemplate.pages && initialTemplate.pages.length > 0;

    if (isCustomTemplateReport) {
      console.log('[DocumentReflow] Skipping reflow for Custom Template (Smart Layout Adjustment will handle it)');
      return;
    }


    // Skip if no data context yet (template mode or initial load)
    if (!dataContextKey) {
      return;
    }

    // Delay to ensure table pagination has completed first
    const timeoutId = setTimeout(() => {
      // Double-check we haven't run already
      if (documentReflowRunRef.current) {
        return;
      }

      console.log('[DocumentReflow] Starting widget reflow across pages...');

      // Mark as run BEFORE making changes
      documentReflowRunRef.current = true;

      setPages(prevPages => {
        // Step 1: Collect ALL blocks (widgets + elements) from ALL pages in order
        // Sort by: original page index, then Y position
        // This ensures elements flow with their nearby widgets
        // Skip cloned blocks - they are handled with continuation pages

        const allBlocksRaw = [];

        // Track which blocks came from which template page for ordering
        prevPages.forEach((page, pageIdx) => {
          // Only collect from non-continuation pages
          if (!page.isContinuationPage) {
            const pageBlocks = page.blocks
              .filter(b => !b.isClonedBlock) // Skip cloned blocks
              .map(b => ({
                ...b,
                _originalPageIdx: pageIdx,
                _originalY: b.position.y,
                _isWidget: b.type === 'widget',
                _isElement: b.type === 'element'
              }))
              .sort((a, b) => a.position.y - b.position.y); // Sort by Y within page

            allBlocksRaw.push(...pageBlocks);
          }
        });

        // Step 1.5: Filter out WIDGETS that have NO DATA
        // Elements are always kept (they don't have data requirements)
        const allBlocks = allBlocksRaw.filter(block => {
          // Elements are always kept
          if (block._isElement) {
            return true;
          }

          // For widgets, check if they have data
          const widgetType = block.widgetType || block.type;
          const blockData = getBlockData(block);

          // Check if widget has data - BE CONSERVATIVE
          let hasData = true;

          if (blockData === null || blockData === undefined) {
            if (!block.dataBindingPath) {
              hasData = false;
            }
          } else if (Array.isArray(blockData) && blockData.length === 0) {
            hasData = false;
          }

          if (!hasData) {
            console.log('[DocumentReflow] Removing widget with no data:', {
              widget: widgetType || block.id,
              dataBindingPath: block.dataBindingPath
            });
          }

          return hasData;
        });

        // Separate widgets for continuation tracking (elements don't have continuations)
        const allWidgets = allBlocks.filter(b => b._isWidget);
        const allElements = allBlocks.filter(b => b._isElement);

        // Step 2: Group continuation pages by their original widget ID
        // We need to MOVE these pages with their parent widget
        const continuationPagesByWidget = new Map();
        const continuationPagesData = new Map();

        prevPages.forEach((page, pageIdx) => {
          if (page.isContinuationPage && page.continuationOf) {
            if (!continuationPagesByWidget.has(page.continuationOf)) {
              continuationPagesByWidget.set(page.continuationOf, []);
              continuationPagesData.set(page.continuationOf, []);
            }
            continuationPagesByWidget.get(page.continuationOf).push(pageIdx);
            continuationPagesData.get(page.continuationOf).push({
              ...page,
              _originalPageIdx: pageIdx
            });
          }
        });

        console.log('[DocumentReflow] Collected blocks:', {
          totalBlocksRaw: allBlocksRaw.length,
          totalBlocksWithData: allBlocks.length,
          widgets: allWidgets.length,
          elements: allElements.length,
          widgetsWithContinuations: Array.from(continuationPagesByWidget.keys()).length,
          blocks: allBlocks.map(b => ({
            id: b.id,
            type: b._isWidget ? (b.widgetType || 'widget') : 'element',
            elementType: b._isElement ? b.elementType : undefined,
            originalPage: b._originalPageIdx + 1,
            originalY: b._originalY,
            hasContinuations: b._isWidget && continuationPagesByWidget.has(b.id)
          }))
        });

        if (allBlocks.length === 0) {
          console.log('[DocumentReflow] No blocks to reflow');
          return prevPages;
        }

        // Step 3: Build new pages structure with properly reflowed widgets
        const PAGE_HEIGHT = GRID_CONFIG?.pageHeight || 1122;
        const WIDGET_SPACING = 0;
        const PAGE_TOP_MARGIN = 0; // Start at top of page (no extra margin)
        const PAGE_BOTTOM_MARGIN = 0;

        // Start fresh - only keep non-continuation pages as shells
        let newPages = [];
        let pageCounter = 0;

        // Track current position for placing widgets
        // For first widget on each page, use its original Y position if it's the first on that template page
        let currentY = PAGE_TOP_MARGIN;
        let hasChanges = false;

        // Helper to create a new regular page
        const createNewPage = () => {
          const newPageId = `reflow-page-${Date.now()}-${pageCounter}`;
          const newPage = {
            id: newPageId,
            name: `Page ${pageCounter + 1}`,
            blocks: [],
            isContinuationPage: false
          };
          newPages.push(newPage);
          pageCounter++;
          console.log('[DocumentReflow] Created new page:', newPageId);
          hasChanges = true;
          return newPage;
        };

        // Create first page
        createNewPage();

        // Process ALL blocks (widgets + elements) in order - they should appear in sequence
        // This ensures elements stay with their nearby widgets
        for (const block of allBlocks) {
          const blockHeight = block._isWidget
            ? calculateActualWidgetHeight(block)
            : (block.size?.height || 30); // Elements have simpler height

          // Check if this is a widget with continuation pages
          const widgetContinuationPages = block._isWidget
            ? (continuationPagesData.get(block.id) || [])
            : [];
          const hasContinuationPages = widgetContinuationPages.length > 0;

          // Find which page this block should go on
          // If it won't fit on current page, move to next page
          const currentPageIdx = newPages.length - 1;
          if (currentY + blockHeight > PAGE_HEIGHT - PAGE_BOTTOM_MARGIN && currentY > PAGE_TOP_MARGIN) {
            // Move to next page
            createNewPage();
            currentY = PAGE_TOP_MARGIN;
          }

          // Place the block on the current page
          const actualPageIdx = newPages.length - 1;
          const newPosition = { x: block.position?.x || 0, y: currentY };

          // Check if position changed
          const positionChanged =
            block._originalPageIdx !== actualPageIdx ||
            (block.position?.y || 0) !== currentY;

          if (positionChanged) {
            hasChanges = true;
            const blockType = block._isWidget ? (block.widgetType || 'widget') : 'element';
            console.log(`[DocumentReflow] Repositioning ${blockType}:`, {
              id: block.id,
              from: { page: block._originalPageIdx + 1, y: block._originalY },
              to: { page: actualPageIdx + 1, y: currentY }
            });
          }

          // Add block to page (without the temporary tracking properties)
          const cleanBlock = { ...block, position: newPosition };
          delete cleanBlock._originalPageIdx;
          delete cleanBlock._originalY;
          delete cleanBlock._isWidget;
          delete cleanBlock._isElement;
          newPages[actualPageIdx].blocks.push(cleanBlock);

          // Update currentY for next block
          currentY += blockHeight + WIDGET_SPACING;

          // If this is a widget with continuation pages, ADD THEM RIGHT AFTER THIS PAGE
          // Elements don't have continuation pages
          if (hasContinuationPages) {
            console.log(`[DocumentReflow] Adding ${widgetContinuationPages.length} continuation pages for widget:`, {
              widget: block.widgetType || block.id,
              afterPage: actualPageIdx + 1
            });

            // Add each continuation page right after the current page
            for (const contPage of widgetContinuationPages) {
              const newContPageId = `reflow-cont-${Date.now()}-${pageCounter}`;
              const newContPage = {
                ...contPage,
                id: newContPageId,
                name: `Page ${pageCounter + 1}`,
                // Keep the blocks as-is - they contain the cloned table data
              };
              delete newContPage._originalPageIdx;

              newPages.push(newContPage);
              pageCounter++;
              hasChanges = true;

              console.log('[DocumentReflow] Added continuation page:', {
                pageId: newContPageId,
                blockCount: newContPage.blocks?.length || 0,
                continuationOf: newContPage.continuationOf
              });
            }

            // Next block starts on fresh page after all continuations
            createNewPage();
            currentY = PAGE_TOP_MARGIN;
          }
        }

        // Step 5: Remove empty continuation pages. KEEP original empty pages.
        newPages = newPages.filter((page, idx) => idx === 0 || page.blocks.length > 0 || !page.isContinuationPage);

        // Step 6: Renumber pages
        newPages = newPages.map((page, idx) => ({
          ...page,
          name: `Page ${idx + 1}`
        }));

        if (hasChanges) {
          console.log('[DocumentReflow] ✅ Reflow complete:', {
            totalPages: newPages.length,
            regularPages: newPages.filter(p => !p.isContinuationPage).length,
            continuationPages: newPages.filter(p => p.isContinuationPage).length,
            pagesWithWidgets: newPages.filter(p => p.blocks.some(b => b.type === 'widget')).length
          });
        } else {
          console.log('[DocumentReflow] No changes needed');
        }

        return hasChanges ? newPages : prevPages;
      });
    }, 1000); // 1 second delay to ensure table pagination is complete

    return () => clearTimeout(timeoutId);
  }, [calculateActualWidgetHeight, getBlockData, dataContextKey]);

  // Smart Layout Adjustment for Custom Templates
  // This runs INSTEAD of document reflow for Custom Templates in report mode
  // It preserves page structure while auto-adjusting widget heights and preventing overlaps
  const smartLayoutAdjustmentRunRef = useRef(false);
  useEffect(() => {
    // Only run for Custom Templates in report OR template (edit) mode
    const isCustomTemplateReport = (builderMode === 'report' || builderMode === 'template') && initialTemplate && initialTemplate.pages && initialTemplate.pages.length > 0;

    if (!isCustomTemplateReport) {
      return;
    }

    // Skip if already run
    if (smartLayoutAdjustmentRunRef.current) {
      return;
    }

    // Skip if no data context yet
    if (!dataContextKey) {
      return;
    }

    // Delay to ensure widgets have rendered with data
    const timeoutId = setTimeout(() => {
      if (smartLayoutAdjustmentRunRef.current) {
        return;
      }

      console.log('[SmartLayoutAdjustment] Starting layout adjustment for Custom Template...');
      smartLayoutAdjustmentRunRef.current = true;

      setPages(prevPages => {
        const newPages = [];

        // Process each page independently to preserve page boundaries
        prevPages.forEach((page, pageIdx) => {
          // Skip continuation pages
          if (page.isContinuationPage) {
            newPages.push(page);
            return;
          }

          // ========== SEPARATE WIDGETS AND ELEMENTS ==========
          // Elements can overlap and maintain their absolute positions
          // Widgets stack vertically without overlapping
          const widgets = page.blocks.filter(block => block.type === 'widget').map(block => {
            // Fix: For legacy Custom Templates, if a block has a size but no manualResize flag,
            // assume it WAS manually resized/set by the user in the past and lock it.
            // This prevents "auto-size based on content" from ruining existing layouts.
            if (!block.manualResize && block.size && block.size.height) {
              return { ...block, manualResize: true };
            }
            return block;
          });
          const elements = page.blocks.filter(block => block.type === 'element');

          // Sort widgets by Y position for vertical stacking
          const sortedWidgets = [...widgets].sort((a, b) => a.position.y - b.position.y);

          let currentPageBlocks = [];
          let currentPageY = 0;
          let overflowBlocks = [];
          let firstBlockY = null; // Track first widget's original Y position

          // ========== PROCESS WIDGETS (VERTICAL STACKING) ==========
          sortedWidgets.forEach((block) => {
            // Calculate actual content height for widgets
            let actualHeight = block.size.height;

            const calculatedHeight = calculateActualWidgetHeight(block);
            if (calculatedHeight > 0) {
              actualHeight = Math.max(calculatedHeight, GRID_CONFIG.cellHeight);
            }

            // LEAST-MOVEMENT POSITIONING:
            // Start with the original Y position.
            // Only push down if we overlap with a previously placed widget on this page.
            let targetY = block.position.y || 0;

            // DISABLE SMART LAYOUT ADJUSTMENT (COLLISION DETECTION)
            // User requirement: Prevent widgets from shifting when a template is applied.
            // We want to preserve the exact saved positions, even if they overlap.
            /* 
            // Check against already placed WIDGETS on the current page
            let needsShift = true;
            let safetyCounter = 0;
            
            while (needsShift && safetyCounter < 50) {
              needsShift = false;
              safetyCounter++;
              
              const currentRect = {
                position: { ...block.position, y: targetY },
                size: { ...block.size, height: actualHeight }
              };

              for (const placedBlock of currentPageBlocks) {
                if (placedBlock.type !== 'widget') continue;
                
                if (doBlocksOverlap(currentRect, placedBlock)) {
                  // If we overlap, push below the offending block
                  const overlapBottom = placedBlock.position.y + (placedBlock.size.height || 0);
                  if (overlapBottom > targetY) {
                    targetY = overlapBottom;
                    needsShift = true;
                    console.log(`[SmartLayoutAdjustment] Page ${pageIdx + 1}, Overlap detected with ${placedBlock.id}. Shifting ${block.widgetType} to Y=${targetY}`);
                  }
                }
              }
            }
            */

            if (targetY !== block.position.y) {
              console.log(`[SmartLayoutAdjustment] Page ${pageIdx + 1}, FINAL SHIFT for ${block.widgetType}: ${block.position.y}px -> ${targetY}px`);
            } else {
              console.log(`[SmartLayoutAdjustment] Page ${pageIdx + 1}, Keeping ${block.widgetType} at original Y: ${targetY}px`);
            }

            // Check if widget fits on current page
            const blockBottom = targetY + actualHeight;
            const pageHasSpace = blockBottom <= GRID_CONFIG.pageHeight;

            if (pageHasSpace) {
              // Widget fits on current page
              currentPageBlocks.push({
                ...block,
                position: {
                  ...block.position,
                  y: targetY
                },
                size: {
                  ...block.size,
                  height: actualHeight
                }
              });
            } else {
              // Widget doesn't fit, add to overflow
              console.log(`[SmartLayoutAdjustment] Page ${pageIdx + 1} full (Bottom=${blockBottom}), widget ${block.widgetType} will overflow to next page`);
              overflowBlocks.push({
                ...block,
                size: {
                  ...block.size,
                  height: actualHeight
                }
              });
            }
          });

          // ========== ADD ELEMENTS (PRESERVE POSITIONS, ALLOW OVERLAP) ==========
          // Elements maintain their original positions and can overlap
          elements.forEach((element) => {
            currentPageBlocks.push({
              ...element,
              // Keep original position - no adjustment
              position: element.position,
              size: element.size
            });
            console.log(`[SmartLayoutAdjustment] Page ${pageIdx + 1}, Element ${element.elementType || 'unknown'} preserved at Y=${element.position.y} (allows overlap)`);
          });

          // Add the current page
          newPages.push({
            ...page,
            blocks: currentPageBlocks
          });

          // Create overflow pages if needed (for widgets only)
          if (overflowBlocks.length > 0) {
            let overflowPageBlocks = [];
            let overflowPageY = 0;
            let overflowPageCounter = 1;

            overflowBlocks.forEach((block) => {
              const blockBottom = overflowPageY + block.size.height;
              const pageHasSpace = blockBottom <= GRID_CONFIG.pageHeight;

              if (!pageHasSpace && overflowPageBlocks.length > 0) {
                // Create new overflow page
                newPages.push({
                  id: `page-overflow-${pageIdx}-${overflowPageCounter}-${Date.now()}`,
                  name: `${page.name} (Overflow ${overflowPageCounter})`,
                  blocks: overflowPageBlocks,
                  isContinuationPage: false
                });

                overflowPageBlocks = [];
                overflowPageY = 0;
                overflowPageCounter++;
              }

              overflowPageBlocks.push({
                ...block,
                position: {
                  ...block.position,
                  y: overflowPageY
                }
              });
              overflowPageY += block.size.height;
            });

            // Add final overflow page
            if (overflowPageBlocks.length > 0) {
              newPages.push({
                id: `page-overflow-${pageIdx}-${overflowPageCounter}-${Date.now()}`,
                name: `${page.name} (Overflow ${overflowPageCounter})`,
                blocks: overflowPageBlocks,
                isContinuationPage: false
              });
            }
          }
        });

        // Ensure at least one page exists
        if (newPages.length === 0) {
          newPages.push({
            id: `page-${Date.now()}`,
            name: 'Page 1',
            blocks: [],
            isContinuationPage: false
          });
        }

        // Filter out empty continuation pages, but KEEP original template pages
        const nonEmptyPages = newPages.filter(page => (page.blocks && page.blocks.length > 0) || !page.isContinuationPage);

        // If all pages are empty, keep at least one empty page
        const finalPages = nonEmptyPages.length > 0 ? nonEmptyPages : [newPages[0]];

        console.log(`[SmartLayoutAdjustment] ✅ Layout adjustment complete: ${prevPages.length} pages -> ${newPages.length} pages (${finalPages.length} non-empty)`);
        return finalPages;
      });
    }, 1500); // 1.5 second delay to ensure widgets have rendered

    return () => clearTimeout(timeoutId);
  }, [builderMode, initialTemplate, dataContextKey, calculateActualWidgetHeight]);

  // Grid helper functions
  const snapToGrid = (x, y) => {
    const gridX = Math.round(x / gridConfig.cellWidth) * gridConfig.cellWidth;
    const gridY = Math.round(y / gridConfig.cellHeight) * gridConfig.cellHeight;
    return {
      x: Math.max(0, Math.min(gridX, gridConfig.pageWidth - gridConfig.cellWidth)),
      y: Math.max(0, Math.min(gridY, gridConfig.pageHeight - gridConfig.cellHeight))
    };
  };

  // Grid position helper - used by snapToGrid
  // const getGridPosition = (x, y) => ({
  //   column: Math.round(x / GRID_CONFIG.cellWidth),
  //   row: Math.round(y / GRID_CONFIG.cellHeight)
  // });

  const snapSizeToGrid = (width, height) => {
    const gridWidth = Math.max(1, Math.round(width / gridConfig.cellWidth)) * gridConfig.cellWidth;
    const gridHeight = Math.max(1, Math.round(height / gridConfig.cellHeight)) * gridConfig.cellHeight;
    return { width: gridWidth, height: gridHeight };
  };

  // Get group info for a block
  const getBlockGroupInfo = useCallback((blockId) => {
    for (const [groupId, group] of Object.entries(widgetGroups)) {
      if (group.leaderId === blockId) {
        return { groupId, role: 'leader', groupName: group.name, followerIds: group.followerIds };
      }
      if (group.followerIds.includes(blockId)) {
        return { groupId, role: 'follower', groupName: group.name, leaderId: group.leaderId };
      }
    }
    return null;
  }, [widgetGroups]);

  // Select block handler
  const handleBlockSelect = useCallback((blockId, event) => {
    if (!blockId) {
      setSelectedBlock(null);
      setSelectedBlocks([]);
      return;
    }

    // Determine blocks to select if part of a group
    let blocksToSelect = [blockId];
    const groupInfo = getBlockGroupInfo(blockId);
    if (groupInfo && groupInfo.groupId) {
      const g = widgetGroups[groupInfo.groupId];
      if (g) {
        blocksToSelect = [g.leaderId, ...g.followerIds];
      }
    }

    // Ctrl+Click, Cmd+Click, or Shift+Click: toggle multi-select
    if (event && (event.ctrlKey || event.metaKey || event.shiftKey)) {
      setSelectedBlocks(prev => {
        const updated = [...prev];
        if (prev.includes(blockId)) {
          // Remove from multi-select
          return updated.filter(id => !blocksToSelect.includes(id));
        } else {
          // Add to multi-select (also include current selectedBlock if not already in list)
          if (updated.length === 0 && selectedBlock && !blocksToSelect.includes(selectedBlock)) {
            updated.push(selectedBlock);
          }
          blocksToSelect.forEach(id => {
            if (!updated.includes(id)) updated.push(id);
          });
          return updated;
        }
      });
      // Keep the primary selection on the last clicked block
      setSelectedBlock(blockId);
      return;
    }

    // Normal click: single selection or group selection
    if (blocksToSelect.length > 1) {
      setSelectedBlocks(blocksToSelect);
      setSelectedBlock(blockId);
    } else {
      setSelectedBlocks([]);
      setSelectedBlock(blockId);
    }
  }, [pages, currentPageId, getBlockGroupInfo, widgetGroups, selectedBlock]);

  // Add new page
  const addPage = useCallback(() => {
    const newId = `page-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setPages(prev => {
      const pageNumber = prev.length + 1;
      const newPage = {
        id: newId,
        name: `Page ${pageNumber}`,
        blocks: []
      };
      return [...prev, newPage];
    });

    // Update current page strictly outside of the setPages updater
    setCurrentPageId(newId);

    setTimeout(() => {
      if (newId) {
        const pageContainer = document.querySelector(`[data-page-id="${newId}"]`);
        if (pageContainer) {
          pageContainer.scrollIntoView({ behavior: 'auto', block: 'start' });
        }
      }
    }, 200);
  }, []);

  // Insert a new page at a specific index
  const insertPage = useCallback((index) => {
    const newId = `page-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setPages(prev => {
      const newPages = [...prev];
      const newPage = {
        id: newId,
        name: `Page ${newPages.length + 1}`,
        blocks: []
      };
      // Insert at the specified index
      newPages.splice(index, 0, newPage);

      // Re-number subsequent pages for clarity if they use the default naming scheme
      for (let i = 0; i < newPages.length; i++) {
        if (newPages[i].name.startsWith('Page ')) {
          newPages[i].name = `Page ${i + 1}`;
        }
      }
      return newPages;
    });

    setCurrentPageId(newId);

    setTimeout(() => {
      const pageContainer = document.querySelector(`[data-page-id="${newId}"]`);
      if (pageContainer) {
        pageContainer.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    }, 200);
  }, []);

  // Move a page up or down
  const movePage = useCallback((fromIndex, direction) => {
    setPages(prev => {
      const toIndex = fromIndex + direction;

      // Prevent moving out of bounds
      if (toIndex < 0 || toIndex >= prev.length) return prev;

      const newPages = [...prev];
      const [movedPage] = newPages.splice(fromIndex, 1);
      newPages.splice(toIndex, 0, movedPage);

      // Re-number pages if they use the default naming scheme
      for (let i = 0; i < newPages.length; i++) {
        if (newPages[i].name.startsWith('Page ')) {
          newPages[i].name = `Page ${i + 1}`;
        }
      }

      return newPages;
    });
  }, []);

  // Reorder page: accepts PAGE IDs (not indices) to avoid filtered-vs-state index mismatch.
  // The Toolbar receives a filtered pages array, so its indices don't match the state array.
  const reorderPage = useCallback((fromPageId, toPageId) => {
    console.log('[reorderPage] Called with fromPageId:', fromPageId, 'toPageId:', toPageId);

    setPages(prev => {
      // Resolve the actual indices from page IDs
      const fromIndex = prev.findIndex(p => p.id === fromPageId);
      const toIndex = prev.findIndex(p => p.id === toPageId);

      console.log('[reorderPage] Resolved indices: fromIndex:', fromIndex, 'toIndex:', toIndex);

      // Prevent invalid bounds
      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
        console.log('[reorderPage] Rejected: page not found or same position');
        return prev;
      }

      // Helper: returns true ONLY for continuation/dependent pages (NOT base pages)
      const isContinuationType = (page) => {
        if (!page) return false;
        const hasClonedBlocks = Array.isArray(page.blocks) && page.blocks.some(b => b.isClonedBlock);
        return page.isContinuationPage || !!page.continuationOf || hasClonedBlocks || page.isPlaceholder;
      };

      const fromPage = prev[fromIndex];
      console.log('[reorderPage] fromPage:', fromPage?.name, 'isContinuation:', isContinuationType(fromPage));

      // Reject dragging a continuation page directly
      if (isContinuationType(fromPage)) {
        console.warn('[reorderPage] Rejected: cannot drag a continuation page');
        return prev;
      }

      // Count how many continuation pages follow the dragged base page
      let sequenceLength = 1;
      while (fromIndex + sequenceLength < prev.length && isContinuationType(prev[fromIndex + sequenceLength])) {
        sequenceLength++;
      }
      console.log('[reorderPage] sequenceLength:', sequenceLength);

      // If dropping onto a continuation page, snap to the boundary of its parent group
      let targetIndex = toIndex;
      if (isContinuationType(prev[targetIndex])) {
        let leaderIdx = targetIndex;
        while (leaderIdx > 0 && isContinuationType(prev[leaderIdx])) {
          leaderIdx--;
        }
        let endIdx = leaderIdx;
        while (endIdx + 1 < prev.length && isContinuationType(prev[endIdx + 1])) {
          endIdx++;
        }

        if (fromIndex < leaderIdx) {
          targetIndex = endIdx;
        } else {
          targetIndex = leaderIdx;
        }
        console.log('[reorderPage] Snapped targetIndex to:', targetIndex);
      }

      if (fromIndex === targetIndex) {
        console.log('[reorderPage] Rejected: same position after snap');
        return prev;
      }

      // --- Standard array reorder ---
      const newPages = [...prev];
      const movedChunk = newPages.splice(fromIndex, sequenceLength);

      let insertAt;
      if (fromIndex < targetIndex) {
        insertAt = targetIndex - sequenceLength + 1;
      } else {
        insertAt = targetIndex;
      }
      insertAt = Math.max(0, Math.min(insertAt, newPages.length));

      console.log('[reorderPage] Moving', movedChunk.length, 'pages from index', fromIndex, 'to insertAt:', insertAt);

      newPages.splice(insertAt, 0, ...movedChunk);
      return newPages;
    });
  }, []);

  // Remove page
  const removePage = useCallback((pageId) => {
    if (pages.length <= 1) return;

    const pageToRemove = pages.find(p => p.id === pageId);

    if (!pageToRemove) {
      console.warn('[ReportBuilder] Page not found:', pageId);
      return;
    }

    // Allow removing continuation pages - user can now delete them if needed
    // Log for debugging purposes
    const hasClonedBlocks = Array.isArray(pageToRemove.blocks) && pageToRemove.blocks.some(b => b.isClonedBlock);
    const hasContinuationName = pageToRemove.name && pageToRemove.name.toLowerCase().includes('continuation');
    const isContinuationPage = pageToRemove.isContinuationPage ||
      hasClonedBlocks ||
      !!pageToRemove.continuationOf ||
      hasContinuationName;

    if (isContinuationPage) {
      console.log('[ReportBuilder] Removing continuation page:', {
        pageId,
        pageName: pageToRemove.name,
        isContinuationPage: pageToRemove.isContinuationPage,
        hasClonedBlocks: hasClonedBlocks,
        continuationOf: pageToRemove.continuationOf,
        hasContinuationName: hasContinuationName
      });
    }

    setPages(prev => {
      const finalCheckPage = prev.find(p => p.id === pageId);
      if (!finalCheckPage) {
        return prev; // Page not found, return previous state
      }

      // Get all block IDs from the page being removed
      const blockIdsToRemove = new Set();
      pageToRemove?.blocks.forEach(block => {
        if (!block.isClonedBlock) {
          blockIdsToRemove.add(block.id);
        }
      });

      // Find all continuation pages that are continuations of blocks on this page
      const continuationPageIdsToRemove = new Set();
      prev.forEach(page => {
        // CRITICAL: Also check by cloned blocks to catch any continuation pages that might not be marked
        const pageHasClonedBlocks = page.blocks?.some(b => b.isClonedBlock);
        const isContinuationPage = page.isContinuationPage || pageHasClonedBlocks || !!page.continuationOf;

        if (isContinuationPage && page.continuationOf) {
          // If this continuation page is for a block on the page being removed, mark it for deletion
          if (blockIdsToRemove.has(page.continuationOf)) {
            continuationPageIdsToRemove.add(page.id);
          }
        }
      });

      // Remove the page and all its continuation pages
      const pagesToRemove = [pageId, ...Array.from(continuationPageIdsToRemove)];
      const newPages = prev.filter(page => !pagesToRemove.includes(page.id));

      // CRITICAL: Ensure any remaining pages with cloned blocks are marked as continuation
      // This prevents continuation pages from losing their flags
      newPages.forEach(page => {
        const hasClonedBlocks = page.blocks?.some(b => b.isClonedBlock);
        if (hasClonedBlocks && !page.isContinuationPage) {
          page.isContinuationPage = true;
          const clonedBlock = page.blocks.find(b => b.isClonedBlock);
          if (clonedBlock?.originalBlockId && !page.continuationOf) {
            page.continuationOf = clonedBlock.originalBlockId;
          }
        }
      });

      // Renumber pages (only non-continuation pages)
      let pageNumber = 1;
      newPages.forEach(page => {
        if (!page.isContinuationPage) {
          page.name = `Page ${pageNumber}`;
          pageNumber++;
        }
      });

      // Update current page if the deleted page or any of its continuations was the current page
      if (pagesToRemove.includes(currentPageId) && newPages.length > 0) {
        // Find the first non-continuation page
        const firstRegularPage = newPages.find(p => !p.isContinuationPage);
        if (firstRegularPage) {
          setCurrentPageId(firstRegularPage.id);
        } else {
          setCurrentPageId(newPages[0].id);
        }
      }

      return newPages;
    });
  }, [pages.length, currentPageId, pages]);

  // Toggle Page Export Override
  const togglePageExport = useCallback((pageId, isIncluded) => {
    setPages(prev => prev.map(page => {
      if (page.id === pageId) {
        return {
          ...page,
          config: {
            ...(page.config || {}),
            exportIncluded: isIncluded
          }
        };
      }
      return page;
    }));
  }, []);

  // Add header/footer section as a freeform container
  const addLayoutSection = useCallback((sectionType) => { // 'header' or 'footer'
    const timestamp = Date.now();
    const newSection = {
      id: `${sectionType}-section-${timestamp}`,
      type: 'element', // Changed from 'section' to allow freeform children
      elementType: sectionType,
      position: { x: 0, y: 0 },
      size: { width: gridConfig.pageWidth, height: 100 },
      style: {
        backgroundColor: 'rgba(0,0,0,0.02)',
        borderRadius: '0px',
        border: 'none',
        minHeight: '40px'
      },
      children: [], // Start with empty children for freeform positioning
      config: {
        layout: {
          type: 'freeform',
          pageRange: { startPage: 1, endPage: 'last' }
        }
      }
    };

    setPages(prevPages => {
      const targetPageId = currentPageId || prevPages[0]?.id;
      return prevPages.map(page => {
        if (page.id === targetPageId) {
          return {
            ...page,
            blocks: [...page.blocks, newSection]
          };
        }
        return page;
      });
    });

    handleBlockSelect(newSection.id);
  }, [currentPageId, gridConfig, setPages, handleBlockSelect]);

  const addWidget = useCallback((widgetType, options = {}) => {
    // Allow adding widgets to continuation pages, but with content-based height

    // Widgets that should be full width and auto-size based on content
    const autoSizeWidgetTypes = ['table', 'key-findings', 'open-ports', 'findings-grid', 'ad-inactive-users-table', 'ad-all-computers-table', 'ad-workstation-age-table', 'ad-inactive-computers-table', 'ad-groups-risk-table', 'ad-ous-risk-table', 'ad-fsmo-roles-table', 'ad-gpo-analysis-table', 'ad-users-audit-table', 'ad-computers-audit-table', 'ad-devices-audit-table', 'ad-groups-audit-table', 'ad-ous-audit-table', 'ad-domain-controllers-audit-table', 'ad-gpo-policies-audit-table', 'ad-event-logs-audit-table', 'ms365-scan-proof-table', 'microsoft-policies-table', 'localhost-hardware-information-table', 'localhost-system-information-table', 'localhost-installed-applications-table', 'localhost-event-logs-table', 'localhost-open-ports-audit-table', 'network-backup-status-table', 'network-antivirus-status-table', 'network-os-versions-table', 'network-firewall-status-table', 'network-detailed-services-table', 'network-non-default-services-table', 'network-services-risk-breakdown-table', 'network-high-privilege-services-table', 'network-users-no-expiring-password-table', 'network-users-expiring-password-table', 'network-admins-no-expiring-password-table', 'network-admins-expiring-password-table', 'network-admins-daily-work-table', 'network-sensitive-open-ports-table', 'network-logical-disks-table', 'network-all-open-ports-table', 'ms365-inactive-users-table', 'ms365-all-users-mfa-table', 'ms365-users-without-mfa-table', 'ms365-policy-config-table', 'localhost-os-version-table', 'localhost-os-versions-table', 'localhost-sensitive-open-ports-table', 'localhost-services-risk-breakdown-table', 'localhost-non-default-services-table', 'localhost-open-ports-table', 'localhost-browser-passwords-table', 'localhost-all-users-password-table', 'localhost-non-expiring-passwords-table', 'localhost-disk-usage-table', 'localhost-installed-software-table', 'localhost-high-privilege-services-table', 'pii-detected-table', 'cyber-security-threat-exposure', 'software-inventory-table', 'local-administrators-table', 'ad-admins-table', 'infrastructure-scan-grid', 'managed-service-experience', 'security-scan-grid', 'combined-scan-grid'];
    const isAutoSizeWidget = autoSizeWidgetTypes.includes(widgetType) || widgetType.endsWith('-cost-summary') || widgetType === 'total-cost-calculator';

    // For continuation pages, always use content-based height (not full page height)
    const isContinuationPage = currentPage.isContinuationPage;

    // Find the next available grid position
    const findNextGridPosition = () => {
      const existingBlocks = currentPage.blocks;
      let defaultSize = getDefaultWidgetSize(widgetType, gridConfig);

      // For auto-size widgets, make them full width
      if (isAutoSizeWidget) {
        defaultSize = {
          width: gridConfig.pageWidth,
          // On continuation pages, use smaller initial height (content-based)
          // On regular pages, use full page height (will be auto-adjusted)
          height: isContinuationPage ? gridConfig.cellHeight * 5 : gridConfig.pageHeight
        };
      }

      const gridCols = Math.ceil(defaultSize.width / gridConfig.cellWidth);
      const gridRows = Math.ceil(defaultSize.height / gridConfig.cellHeight);

      // For full-width widgets, always place at top (x=0)
      if (isAutoSizeWidget) {
        // Find the highest y position of existing blocks
        const maxY = existingBlocks.length > 0
          ? Math.max(...existingBlocks.map(b => b.position.y + b.size.height))
          : 0;
        return { x: 0, y: maxY };
      }

      // Try to find an empty spot for regular widgets
      for (let row = 0; row <= gridConfig.rows - gridRows; row++) {
        for (let col = 0; col <= gridConfig.columns - gridCols; col++) {
          const x = col * gridConfig.cellWidth;
          const y = row * gridConfig.cellHeight;

          // Check if this position is occupied
          const isOccupied = existingBlocks.some(block => {
            if (!block.position) return false;
            const blockCol = Math.floor(block.position.x / gridConfig.cellWidth);
            const blockRow = Math.floor(block.position.y / gridConfig.cellHeight);
            const blockCols = Math.ceil(block.size.width / gridConfig.cellWidth);
            const blockRows = Math.ceil(block.size.height / gridConfig.cellHeight);

            return !(col + gridCols <= blockCol || col >= blockCol + blockCols ||
              row + gridRows <= blockRow || row >= blockRow + blockRows);
          });

          if (!isOccupied) {
            return { x, y };
          }
        }
      }

      // If no empty spot found, place at top-left
      return { x: 0, y: 0 };
    };

    const {
      config: initialConfig = {},
      dataConfig: initialDataConfig = {},
      style: initialStyle = {},
      customData: initialCustomData
    } = options || {};

    const defaultStyle = {
      backgroundColor: '#ffffff',
      borderColor: '#e5e7eb',
      textColor: '#000000',
      fontSize: 'medium',
      valueFontSize: 24, // Font size for numeric values (in pixels)
      labelFontSize: 14, // Font size for labels (in pixels)
      valueColor: '#000000', // Color for numeric values
      labelColor: '#666666', // Color for labels
      chartColors: [], // Custom colors for chart indicators (optional, empty = use default)
      borderRadius: 8,
      borderWidth: 0, // No border by default - user must explicitly set borderWidth > 0
      padding: 16,
      shadow: 'medium'
    };

    const defaultDataConfig = {
      metricPath: '',
      aggregation: 'count',
      filters: [],
      tableColumns: widgetType === 'table' ? [] : undefined,
      // Field selection for array-based metrics
      labelField: '', // Field to use as labels (for charts)
      valueField: '', // Field to use as values (for charts)
      // Filter/Group configuration for array-based metrics
      filterField: '', // Field to filter/group by (e.g., "Disabled")
      filterMode: 'none', // 'none', 'filter', 'group'
      filterValue: '', // Value to filter for (when filterMode is 'filter')
      // Device-level filters - filter parent array (e.g., network_devices) by child properties
      deviceFilters: [], // Array of { id, fieldPath, operator, value, logic }
      // Table sorting configuration
      tableSort: null, // { fieldPath: string, direction: 'asc' | 'desc' } or null
      // Flatten nested array/object into individual rows (e.g., 'open_ports' expands 1 device → N rows)
      flattenField: '' // Field path to flatten (e.g., 'open_ports')
    };

    const mergedDataConfig = {
      ...defaultDataConfig,
      ...initialDataConfig
    };

    if (widgetType !== 'table') {
      delete mergedDataConfig.tableColumns;
    } else if (!Array.isArray(mergedDataConfig.tableColumns)) {
      mergedDataConfig.tableColumns = [];
    }

    // Auto-detect chart fields if metricPath is preset but fields are missing (e.g. from presets)
    // This allows charts to work immediately without needing to open Properties panel
    const isChartWidget = ['bar', 'line', 'pie', 'donut', 'radar', 'polar', 'bubble', 'scatter'].includes(widgetType);
    if (isChartWidget && mergedDataConfig.metricPath && (!mergedDataConfig.labelField || !mergedDataConfig.valueField)) {
      try {
        const data = getDataByPath(dataContext, mergedDataConfig.metricPath, dynamicCategoryMap);
        if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
          // Use dataContext since scanData might not be in scope here
          const availableFields = getAvailableFields(dataContext, mergedDataConfig.metricPath);

          const numericFields = availableFields.filter(field => {
            if (!data || data.length === 0) return false;
            const sample = data[0];
            const value = sample[field];
            // STRICTER CHECK: Use Number() instead of parseFloat() to avoid "2025-01-01" becoming 2025
            // Also explicitly check for date-like strings
            if (typeof value === 'number') return true;
            if (typeof value === 'string') {
              // Reject if it looks like a date (ISO format or common separators)
              if (value.includes('-') || value.includes(':') || value.includes('T')) return false;
              return !isNaN(Number(value)) && isFinite(Number(value)) && value.trim() !== '';
            }
            return false;
          });

          const textFields = availableFields.filter(field => {
            if (!data || data.length === 0) return false;
            const sample = data[0];
            const value = sample[field];
            // Allow ANY string that is not a strict number, or is explicitly a date-like string
            if (typeof value === 'string') {
              // If it's a date-like string (has separators), it's definitely text/label
              if (value.includes('-') || value.includes(':') || value.includes('T')) return true;

              // Otherwise, if it's not a valid strict number, it's text
              return isNaN(Number(value)) || !isFinite(Number(value)) || value.trim() === '';
            }
            return false;
          });

          if (!mergedDataConfig.labelField) {
            const defaultLabel = textFields.length > 0 ? textFields[0] : (availableFields.length > 0 ? availableFields[0] : '');
            if (defaultLabel) mergedDataConfig.labelField = defaultLabel;
          }

          if (!mergedDataConfig.valueField) {
            const defaultValue = numericFields.length > 0 ? numericFields[0] : (availableFields.length > 0 ? availableFields[0] : '');
            if (defaultValue) mergedDataConfig.valueField = defaultValue;
          }
          console.log('[ReportBuilder] Auto-configured chart fields on creation:', {
            widgetType,
            metricPath: mergedDataConfig.metricPath,
            label: mergedDataConfig.labelField,
            value: mergedDataConfig.valueField
          });
        }
      } catch (err) {
        console.error('[ReportBuilder] Error auto-configuring chart fields:', err);
      }
    }

    // Calculate initial size - full width for auto-size widgets
    let initialSize = getDefaultWidgetSize(widgetType, gridConfig);
    if (isAutoSizeWidget) {
      initialSize = {
        width: gridConfig.pageWidth,
        // Use a small initial height - will be auto-adjusted based on content
        height: gridConfig.cellHeight * 3 // Small initial height, will grow with content
      };
    }

    const newBlock = {
      id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'widget',
      widgetType,
      position: findNextGridPosition(),
      size: initialSize,
      config: {
        ...getDefaultWidgetConfig(widgetType),
        ...initialConfig
      },
      style: {
        ...defaultStyle,
        showDataLabels: widgetType === 'bubble' ? false : true,
        ...initialStyle
      },
      dataConfig: mergedDataConfig,
      // Track pagination info for paginated widgets
      pagination: isAutoSizeWidget ? {
        spansMultiplePages: false,
        currentPage: 0,
        itemsPerPage: 15, // Default items per page
        itemsPerPageFirstPage: 15, // Default for first page
        itemsPerPageContinuation: 25, // Default for continuation pages
        totalItems: null,
        totalPages: 1
      } : undefined
    };

    if (initialCustomData && Object.keys(initialCustomData).length > 0) {
      newBlock.customData = { ...initialCustomData };
    }

    // Try adding to selected container (Column, Row, Section)
    if (selectedBlock) {
      const targetContainerId = resolveTargetContainer(pages, selectedBlock);

      if (targetContainerId) {
        let addedToContainer = false;
        const updatedPages = pages.map(page => {
          if (addedToContainer) return page;
          const { updatedBlocks, success } = addBlockToContainer(page.blocks, targetContainerId, newBlock);
          if (success) {
            addedToContainer = true;
            return { ...page, blocks: updatedBlocks };
          }
          return page;
        });

        if (addedToContainer) {
          setPages(updatedPages);
          setSelectedBlock(newBlock.id);
          setSidebarVisible(false);
          return;
        }
      }
    }

    setPages(prev => prev.map(page =>
      page.id === currentPageId
        ? { ...page, blocks: [...page.blocks, newBlock] }
        : page
    ));
    setSelectedBlock(newBlock.id);
    setSidebarVisible(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageId, currentPage, gridConfig, pages, selectedBlock]);

  // Add element to current page
  const addElement = useCallback((inputElementType, initialConfig = {}) => {
    // Intercept Header/Footer to use new Section structure
    if (inputElementType === 'header' || inputElementType === 'footer') {
      addLayoutSection(inputElementType);
      return;
    }

    let elementType = inputElementType;

    // Handle special MSP Logo types
    if (elementType === 'msp_logo') {
      elementType = 'image';
      initialConfig = {
        ...initialConfig,
        src: '{{ MSP.logo }}',
        alt: 'MSP Logo'
      };
    } else if (elementType === 'msp_large_logo') {
      elementType = 'image';
      initialConfig = {
        ...initialConfig,
        src: '{{ MSP.large_logo }}',
        alt: 'MSP Large Logo',
        imageWidth: '300px',
        imageHeight: '100px'
      };
    }

    // Allow adding elements to continuation pages (same as widgets)

    // Find the next available grid position
    const findNextGridPosition = () => {
      const currentPage = pages.find(p => p.id === currentPageId);
      const existingBlocks = currentPage?.blocks || [];
      const defaultSize = getDefaultElementSize(elementType, gridConfig);
      const gridCols = Math.ceil(defaultSize.width / gridConfig.cellWidth);
      const gridRows = Math.ceil(defaultSize.height / gridConfig.cellHeight);

      // Special handling for footer elements - position at bottom
      if (elementType === 'footer') {
        const footerY = gridConfig.pageHeight - gridConfig.cellHeight;
        return { x: 0, y: footerY };
      }

      // Special handling for header elements - position at top
      if (elementType === 'header' || elementType === 'title') {
        return { x: 0, y: 0 };
      }

      // Try to find an empty spot for other elements
      for (let row = 0; row <= gridConfig.rows - gridRows; row++) {
        for (let col = 0; col <= gridConfig.columns - gridCols; col++) {
          const x = col * gridConfig.cellWidth;
          const y = row * gridConfig.cellHeight;

          // Check if this position is occupied
          const isOccupied = existingBlocks.some(block => {
            if (!block.position) return false;
            const blockCol = Math.floor(block.position.x / gridConfig.cellWidth);
            const blockRow = Math.floor(block.position.y / gridConfig.cellHeight);
            const blockCols = Math.ceil(block.size.width / gridConfig.cellWidth);
            const blockRows = Math.ceil(block.size.height / gridConfig.cellHeight);

            return !(col + gridCols <= blockCol || col >= blockCol + blockCols ||
              row + gridRows <= blockRow || row >= blockRow + blockRows);
          });

          if (!isOccupied) {
            return { x, y };
          }
        }
      }

      // If no empty spot found, place at top-left
      return { x: 0, y: 0 };
    };

    const newBlock = {
      id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'element',
      elementType,
      position: findNextGridPosition(),
      size: getDefaultElementSize(elementType, gridConfig),
      config: {
        ...getDefaultElementConfig(elementType),
        ...initialConfig
      },
      style: {
        backgroundColor: (elementType === 'shape' || elementType === 'icon') ? '#CBD5E1' : 'transparent',
        borderColor: 'transparent',
        textColor: '#000000',
        fontSize: 'medium',
        valueFontSize: 24,
        labelFontSize: elementType === 'title' ? 24 : 14,
        fontWeight: elementType === 'title' ? '700' : '400',
        valueColor: '#000000',
        labelColor: '#666666',
        chartColors: [],
        borderRadius: elementType === 'shape' ? 8 : (elementType === 'title' || elementType === 'text' ? 4 : 0),
        borderWidth: 0,
        padding: (elementType === 'title' || elementType === 'text') ? 10 : 8,
        shadow: 'none'
      }
    };

    // Try adding to selected container (Column, Row, Section)
    if (selectedBlock) {
      const targetContainerId = resolveTargetContainer(pages, selectedBlock);

      if (targetContainerId) {
        let addedToContainer = false;
        const updatedPages = pages.map(page => {
          if (addedToContainer) return page;
          const { updatedBlocks, success } = addBlockToContainer(page.blocks, targetContainerId, newBlock);
          if (success) {
            addedToContainer = true;
            return { ...page, blocks: updatedBlocks };
          }
          return page;
        });

        if (addedToContainer) {
          setPages(updatedPages);
          setSelectedBlock(newBlock.id);
          setSidebarVisible(false);
          return;
        }
      }
    }

    setPages(prev => prev.map(page =>
      page.id === currentPageId
        ? { ...page, blocks: [...page.blocks, newBlock] }
        : page
    ));
    setSelectedBlock(newBlock.id);
    setSidebarVisible(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageId, gridConfig, pages, selectedBlock]);

  // ===== CUSTOM WIDGETS: Save / Add / Delete =====

  // Helper: deep-clone a block and assign new unique IDs to it and all children
  const cloneBlockWithNewIds = (block) => {
    const newId = `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    return {
      ...JSON.parse(JSON.stringify(block)), // deep clone
      id: newId,
      children: Array.isArray(block.children)
        ? block.children.map(child => cloneBlockWithNewIds(child))
        : []
    };
  };

  // Save one or more blocks as a custom widget (triggered from context menu)
  const saveAsCustomWidget = useCallback((blockIds, name, icon = '📦') => {
    // Normalize to array
    const ids = Array.isArray(blockIds) ? blockIds : [blockIds];

    // Helper to find a block across all pages and headers/footers
    const findBlockForCW = (id) => {
      for (const page of pages) {
        const search = (blocks) => {
          for (const b of blocks) {
            if (b.id === id) return b;
            if (Array.isArray(b.children)) {
              const found = search(b.children);
              if (found) return found;
            }
          }
          return null;
        };
        const found = search(page.blocks);
        if (found) return found;
      }
      if (window.__headerElements) {
        const found = window.__headerElements.find(b => b.id === id);
        if (found) return found;
      }
      if (window.__footerElements) {
        const found = window.__footerElements.find(b => b.id === id);
        if (found) return found;
      }
      return null;
    };

    // Collect all target blocks
    const targetBlocks = ids.map(id => findBlockForCW(id)).filter(Boolean);
    if (targetBlocks.length === 0) {
      console.warn('[CustomWidgets] No blocks found for IDs:', ids);
      return;
    }

    // Deep-clone each block's data
    const blockSnapshots = targetBlocks.map(block => {
      const snapshot = JSON.parse(JSON.stringify(block));
      delete snapshot.id;
      return {
        type: snapshot.type,
        elementType: snapshot.elementType,
        widgetType: snapshot.widgetType,
        position: snapshot.position || { x: 0, y: 0 },
        size: snapshot.size,
        config: snapshot.config || {},
        style: snapshot.style || {},
        customData: snapshot.customData || {},
        dataConfig: snapshot.dataConfig || {},
        children: snapshot.children || []
      };
    });

    // Normalize positions relative to top-left of the group
    const minX = Math.min(...blockSnapshots.map(b => b.position?.x || 0));
    const minY = Math.min(...blockSnapshots.map(b => b.position?.y || 0));
    blockSnapshots.forEach(b => {
      b.position = {
        x: (b.position?.x || 0) - minX,
        y: (b.position?.y || 0) - minY
      };
    });

    const customWidget = {
      id: `cw-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: name.trim(),
      icon: icon || '📦',
      createdAt: new Date().toISOString(),
      isMultiBlock: blockSnapshots.length > 1,
      blockData: blockSnapshots.length === 1
        ? blockSnapshots[0]
        : { blocks: blockSnapshots }
    };

    setCustomWidgets(prev => [...prev, customWidget]);
    setSelectedBlocks([]); // Clear multi-select after save
    console.log('[CustomWidgets] Saved custom widget:', customWidget.name, `(${blockSnapshots.length} block(s))`);
  }, [pages]);

  // Insert a saved custom widget onto the current page
  const addCustomWidget = useCallback((customWidgetId) => {
    const cw = customWidgets.find(w => w.id === customWidgetId);
    if (!cw) return;

    // Determine if multi-block or single-block
    const blockDatas = cw.blockData.blocks
      ? cw.blockData.blocks // multi-block
      : [cw.blockData]; // single-block

    // Find placement position
    const currentPageObj = pages.find(p => p.id === currentPageId);
    const existingBlocks = currentPageObj?.blocks || [];
    const baseY = existingBlocks.length > 0
      ? Math.max(...existingBlocks.map(b => (b.position?.y || 0) + (b.size?.height || 0)))
      : 0;

    const isMultiBlock = blockDatas.length > 1;
    const groupId = isMultiBlock ? `group-${Date.now()}` : null;
    let leaderId = null;

    const newBlocks = blockDatas.map((blockData, index) => {
      const newBlockId = `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      if (index === 0) leaderId = newBlockId;

      return {
        id: newBlockId,
        type: blockData.type || 'element',
        elementType: blockData.elementType,
        widgetType: blockData.widgetType,
        position: {
          x: (blockData.position?.x || 0),
          y: Math.min(baseY + (blockData.position?.y || 0), gridConfig.pageHeight - 50)
        },
        size: { ...blockData.size },
        config: JSON.parse(JSON.stringify(blockData.config || {})),
        style: JSON.parse(JSON.stringify(blockData.style || {})),
        customData: JSON.parse(JSON.stringify(blockData.customData || {})),
        dataConfig: JSON.parse(JSON.stringify(blockData.dataConfig || {})),
        children: Array.isArray(blockData.children)
          ? blockData.children.map(child => cloneBlockWithNewIds(child))
          : [],
        ...(isMultiBlock ? {
          groupId,
          groupRole: index === 0 ? 'leader' : 'follower',
          groupOrder: index,
          groupLeaderId: index === 0 ? null : leaderId
        } : {})
      };
    });

    setPages(prev => prev.map(page =>
      page.id === currentPageId
        ? { ...page, blocks: [...page.blocks, ...newBlocks] }
        : page
    ));

    if (isMultiBlock) {
      setWidgetGroups(prev => ({
        ...prev,
        [groupId]: {
          leaderId: newBlocks[0].id,
          followerIds: newBlocks.slice(1).map(b => b.id),
          name: cw.name || 'Custom Widget Group'
        }
      }));
      setSelectedBlocks(newBlocks.map(b => b.id));
      setSelectedBlock(newBlocks[0].id);
    } else if (newBlocks.length > 0) {
      setSelectedBlock(newBlocks[0].id);
      setSelectedBlocks([]);
    }

    setSidebarVisible(false);
  }, [customWidgets, currentPageId, pages, gridConfig]);

  // Delete a custom widget from the saved list
  const deleteCustomWidget = useCallback((customWidgetId) => {
    setCustomWidgets(prev => prev.filter(w => w.id !== customWidgetId));
  }, []);

  const moveBlock = useCallback((blockId, newPosition, targetPageId = null) => {
    console.log('[Layout Debug] moveBlock called:', { blockId, newPosition, targetPageId });

    const snappedPosition = snapToGrid(newPosition.x, newPosition.y);

    setPages(prev => {
      // Find the source page and block recursively
      let sourcePageId = null;
      let blockToMove = null;
      let parentContainer = null;

      const findBlockRecursive = (blocks, pageId, parent = null) => {
        for (const block of blocks) {
          if (block.id === blockId) {
            sourcePageId = pageId;
            blockToMove = block;
            parentContainer = parent;
            return true;
          }
          if (block.children && block.children.length > 0) {
            if (findBlockRecursive(block.children, pageId, block)) return true;
          }
        }
        return false;
      };

      for (const page of prev) {
        if (findBlockRecursive(page.blocks, page.id)) break;
      }

      if (!blockToMove) {
        console.warn('[ReportBuilder] Block not found:', blockId);
        return prev;
      }

      // Determine parent boundaries for constraints
      // For headers/footers, we allow moving within their own bounds
      const isParentHeaderFooter = parentContainer && (parentContainer.type === 'element' || parentContainer.type === 'section') &&
        (parentContainer.elementType === 'header' || parentContainer.elementType === 'footer');

      // Determine target page
      // FIX: If it's a Header/Footer element, it must stay on its current page logically (synced across pages)
      const finalTargetPageId = isParentHeaderFooter ? sourcePageId : (targetPageId || sourcePageId);

      const parentWidth = parentContainer ? (parentContainer.size?.width || gridConfig.pageWidth) : gridConfig.pageWidth;
      const parentHeight = parentContainer ? (parentContainer.size?.height || gridConfig.pageHeight) : gridConfig.pageHeight;

      const blockWidth = blockToMove.size?.width || gridConfig.cellWidth;
      const blockHeight = blockToMove.size?.height || gridConfig.cellHeight;

      const maxX = parentWidth - blockWidth;
      const maxY = parentHeight - blockHeight;

      const constrainedPosition = {
        x: Math.max(0, Math.min(snappedPosition.x, maxX)),
        y: Math.max(0, Math.min(snappedPosition.y, maxY))
      };

      // Gather all blocks to move in group
      const deltaX = snappedPosition.x - (blockToMove.position?.x || 0);
      const deltaY = snappedPosition.y - (blockToMove.position?.y || 0);
      const groupIdToMove = blockToMove.groupId;
      const groupBlocks = [];

      if (groupIdToMove) {
        const collectGroupBlocks = (blocks) => {
          for (const b of blocks) {
            if (b.id !== blockId && b.groupId === groupIdToMove) {
              groupBlocks.push(b);
            }
            if (b.children) collectGroupBlocks(b.children);
          }
        };
        const sourcePage = prev.find(p => p.id === sourcePageId);
        if (sourcePage) collectGroupBlocks(sourcePage.blocks);
      }

      // Moving within the same page
      if (sourcePageId === finalTargetPageId) {
        const updateBlocks = (blocks) => {
          return blocks.map(b => {
            if (b.id === blockId) {
              return { ...b, position: constrainedPosition, manualPosition: true };
            }
            if (groupIdToMove && b.groupId === groupIdToMove) {
              const bWidth = b.size?.width || gridConfig.cellWidth;
              const bHeight = b.size?.height || gridConfig.cellHeight;
              const pWidth = parentContainer ? (parentContainer.size?.width || gridConfig.pageWidth) : gridConfig.pageWidth;
              const pHeight = parentContainer ? (parentContainer.size?.height || gridConfig.pageHeight) : gridConfig.pageHeight;
              return {
                ...b,
                position: {
                  x: Math.max(0, Math.min((b.position?.x || 0) + deltaX, pWidth - bWidth)),
                  y: Math.max(0, Math.min((b.position?.y || 0) + deltaY, pHeight - bHeight))
                },
                manualPosition: true
              };
            }
            if (b.children && b.children.length > 0) {
              return { ...b, children: updateBlocks(b.children) };
            }
            return b;
          });
        };

        return prev.map(page => {
          // FIX: If moving an element within a Header/Footer, update it on ALL pages to keep them in sync
          if (isParentHeaderFooter || page.id === sourcePageId) {
            return {
              ...page,
              blocks: updateBlocks(page.blocks)
            };
          }
          return page;
        });
      }

      // Moving between pages
      return prev.map(page => {
        if (page.id === sourcePageId) {
          const removeBlockRecursive = (blocks) => {
            return blocks
              .filter(b => b.id !== blockId && !(groupIdToMove && b.groupId === groupIdToMove))
              .map(b => b.children ? { ...b, children: removeBlockRecursive(b.children) } : b);
          };
          return { ...page, blocks: removeBlockRecursive(page.blocks) };
        }
        if (page.id === finalTargetPageId) {
          const blocksToAdd = [
            { ...blockToMove, position: constrainedPosition, manualPosition: true },
            ...groupBlocks.map(b => {
              const bWidth = b.size?.width || gridConfig.cellWidth;
              const bHeight = b.size?.height || gridConfig.cellHeight;
              const pWidth = parentContainer ? (parentContainer.size?.width || gridConfig.pageWidth) : gridConfig.pageWidth;
              const pHeight = parentContainer ? (parentContainer.size?.height || gridConfig.pageHeight) : gridConfig.pageHeight;
              return {
                ...b,
                position: {
                  x: Math.max(0, Math.min((b.position?.x || 0) + deltaX, pWidth - bWidth)),
                  y: Math.max(0, Math.min((b.position?.y || 0) + deltaY, pHeight - bHeight))
                },
                manualPosition: true
              };
            })
          ];
          return {
            ...page,
            blocks: [
              ...page.blocks.filter(b => b.id !== blockId && !(groupIdToMove && b.groupId === groupIdToMove)),
              ...blocksToAdd
            ]
          };
        }
        return page;
      });
    });

    if (targetPageId && targetPageId !== currentPageId) {
      setCurrentPageId(targetPageId);
    }
  }, [currentPageId, gridConfig, setPages]);

  // Resize block with grid snapping (allows resizing cloned blocks for content-based height)
  const resizeBlock = useCallback((blockId, newSize, pageId = null, options = {}) => {
    console.log('[Layout Debug] resizeBlock called:', { blockId, newSize, pageId, options });
    const { skipSnapping = false, skipConstraints = false } = options;
    const snappedSize = skipSnapping ? newSize : snapSizeToGrid(newSize.width, newSize.height);

    let shouldTriggerPagination = false;

    setPages(prev => {
      // Find current block recursively
      let currentBlock = null;
      let parentContainer = null;

      const findBlockRecursive = (blocks, parent = null) => {
        for (const block of blocks) {
          if (block.id === blockId) {
            currentBlock = block;
            parentContainer = parent;
            return true;
          }
          if (block.children && block.children.length > 0) {
            if (findBlockRecursive(block.children, block)) return true;
          }
        }
        return false;
      };

      for (const page of prev) {
        if (findBlockRecursive(page.blocks)) break;
      }

      if (!currentBlock) return prev;

      // Check if this block or any of its children are paginated widgets
      const checkContainsPaginatedWidgets = (b) => {
        if (b.type === 'widget' && PAGINATED_WIDGETS.includes(b.widgetType)) return true;
        if (b.children && b.children.length > 0) {
          return b.children.some(checkContainsPaginatedWidgets);
        }
        return false;
      };

      if (checkContainsPaginatedWidgets(currentBlock)) {
        shouldTriggerPagination = true;
      }

      const isHeaderFooter = (currentBlock.type === 'element' || currentBlock.type === 'section') && (currentBlock.elementType === 'header' || currentBlock.elementType === 'footer');

      const isParentHeaderFooter = parentContainer && (parentContainer.type === 'element' || parentContainer.type === 'section') &&
        (parentContainer.elementType === 'header' || parentContainer.elementType === 'footer');

      const parentWidth = parentContainer ? (parentContainer.size?.width || gridConfig.pageWidth) : gridConfig.pageWidth;
      const parentHeight = parentContainer ? (parentContainer.size?.height || gridConfig.pageHeight) : gridConfig.pageHeight;

      const maxWidth = parentWidth - (currentBlock.position?.x || 0);
      const maxHeight = parentHeight - (currentBlock.position?.y || 0);

      // Apply constraints unless skipped (e.g. property panel resize)
      // Relax constraints for isParentHeaderFooter to strictly ignore parent bounds if they are problematic
      const constrainedSize = skipConstraints ? snappedSize : {
        width: (isHeaderFooter || isParentHeaderFooter) ? snappedSize.width : Math.max(gridConfig.cellWidth, Math.min(snappedSize.width, maxWidth)),
        height: (isHeaderFooter || isParentHeaderFooter) ? snappedSize.height : Math.max(gridConfig.cellHeight, Math.min(snappedSize.height, maxHeight))
      };

      // Log if we're constraining the size
      if (constrainedSize.width !== snappedSize.width || constrainedSize.height !== snappedSize.height) {
        console.log(`[resizeBlock] Constraining block ${blockId} to page boundaries:`, {
          requested: snappedSize,
          constrained: constrainedSize,
          position: currentBlock.position,
          maxAllowed: { maxWidth, maxHeight }
        });
      }

      // Helper function for recursive update
      const updateBlocksRecursive = (blocks) => {
        return blocks.map(b => {
          // 1. Header/Footer sync across all pages
          if (isHeaderFooter && b.type === 'element' && b.elementType === currentBlock.elementType) {
            return { ...b, size: constrainedSize, manualResize: true };
          }

          // 2. Target block update
          if (b.id === blockId) {
            return { ...b, size: constrainedSize, manualResize: true };
          }

          // 3. Recursive children update
          if (b.children && b.children.length > 0) {
            return { ...b, children: updateBlocksRecursive(b.children) };
          }
          return b;
        });
      };

      // Resolve the original block ID for clone sync
      let originalBlockId = blockId;
      if (currentBlock && (currentBlock.isClonedBlock || currentBlock.isStaticClone) && currentBlock.originalBlockId) {
        originalBlockId = currentBlock.originalBlockId;
      }
      const isCloneResize = originalBlockId !== blockId;

      return prev.map(page => {
        // If specific pageId was provided (for continuation clones), only update that page
        // UNLESS it's a clone resize — then update ALL pages to keep clones in sync
        // CRITICAL FIX: If we are resizing a child of a Header/Footer (isParentHeaderFooter),
        // we MUST update it on all pages to keep them in sync, just like the Header/Footer itself.
        if (pageId && page.id !== pageId && !isHeaderFooter && !isParentHeaderFooter && !isCloneResize) {
          return page;
        }

        const updateBlocksRecursive = (blocks) => {
          return blocks.map(b => {
            // 1. Header/Footer sync across all pages
            if (isHeaderFooter && b.type === 'element' && b.elementType === currentBlock.elementType) {
              // Force manualResize to true to prevent auto-sizing
              // Use constrainedSize which is already calculated
              return { ...b, size: constrainedSize, manualResize: true };
            }

            // 2. Target block update (the block being resized)
            if (b.id === blockId) {
              return { ...b, size: constrainedSize, manualResize: true };
            }

            // 3. Clone sync: also update the original block and all other clones
            if (isCloneResize) {
              if (b.id === originalBlockId ||
                ((b.isClonedBlock || b.isStaticClone) && b.originalBlockId === originalBlockId)) {
                return { ...b, size: constrainedSize, manualResize: true };
              }
            }

            // 4. Child blocks recursion
            if (b.children && b.children.length > 0) {
              return { ...b, children: updateBlocksRecursive(b.children) };
            }

            return b;
          });
        };

        return {
          ...page,
          blocks: updateBlocksRecursive(page.blocks)
        };
      });
    });

    if (shouldTriggerPagination) {
      console.log('[Pagination] Triggering recalculation due to block resize for block:', blockId);
      setBlockCountTrigger(prev => prev + 1);
    }
  }, [gridConfig, setPages, setBlockCountTrigger]);

  // Helper to find the true parent ID of a block if it's a clone or placeholder dummy
  const resolveOriginalBlockId = useCallback((blockId, pagesList) => {
    for (const p of pagesList) {
      const found = findBlockById(p.blocks, blockId);
      if (found) {
        if ((found.isClonedBlock || found.isStaticClone) && found.originalBlockId) return found.originalBlockId;
        if (found.isPlaceholderWidget && found.placeholderForWidget) return found.placeholderForWidget;
        return blockId;
      }
    }
    return blockId;
  }, []);

  // Update block configuration
  const updateBlockConfig = useCallback((blockId, newConfig) => {
    // Check if this is a paginated widget - if so, trigger pagination recalculation
    let shouldTriggerPagination = false;

    setPages(prev => {
      // Find the type of the block being updated to check if it's a header or footer
      let targetType = null;
      let targetWidgetType = null;

      // Find block recursively to get its type
      for (const page of prev) {
        const b = findBlockById(page.blocks, blockId);
        if (b) {
          if ((b.type === 'element' || b.type === 'section') && (b.elementType === 'header' || b.elementType === 'footer')) {
            targetType = b.elementType;
          }
          if (b.type === 'widget') {
            targetWidgetType = b.widgetType;
          }
          break;
        }
      }

      // If it's a paginated widget, any config change might affect content length
      if (targetWidgetType && PAGINATED_WIDGETS.includes(targetWidgetType)) {
        shouldTriggerPagination = true;
      }

      const originalBlockId = resolveOriginalBlockId(blockId, prev);

      const updateFn = (block) => {
        // If it's a header/footer sync, update all matching elements
        if (targetType && (block.type === 'element' || block.type === 'section') && block.elementType === targetType) {
          return { ...block, config: { ...block.config, ...newConfig } };
        }
        // Otherwise, update the specific block or its clones/parent
        if (block.id === blockId || block.id === originalBlockId ||
          ((block.isClonedBlock || block.isStaticClone) && block.originalBlockId === originalBlockId) ||
          (block.isPlaceholderWidget && block.placeholderForWidget === originalBlockId)) {
          return { ...block, config: { ...block.config, ...newConfig } };
        }
        return block;
      };

      const updatedPages = prev.map(page => ({
        ...page,
        blocks: recursivelyUpdateBlocks(page.blocks, updateFn)
      }));

      return updatedPages;
    });

    if (shouldTriggerPagination) {
      console.log('[Pagination] Triggering recalculation due to config update for block:', blockId);
      setBlockCountTrigger(prev => prev + 1);
    }
  }, []);

  // Update block style
  const updateBlockStyle = useCallback((blockId, newStyle) => {
    setPages(prev => {
      // Find the block being updated to check for special synchronization needs
      let targetType = null;
      let isFindingWidget = false;

      // Recursive find
      for (const page of prev) {
        const b = findBlockById(page.blocks, blockId);
        if (b) {
          if ((b.type === 'element' || b.type === 'section') && (b.elementType === 'header' || b.elementType === 'footer')) {
            targetType = b.elementType;
          } else if (b.type === 'widget' && (b.widgetType === 'findings-status' || b.widgetType === 'single-finding-status')) {
            isFindingWidget = true;
          }
          break;
        }
      }

      const originalBlockId = resolveOriginalBlockId(blockId, prev);

      const updateFn = (block) => {
        // 1. Header/Footer sync
        if (targetType && (block.type === 'element' || block.type === 'section') && block.elementType === targetType) {
          return { ...block, style: { ...block.style, ...newStyle } };
        }

        const isTargetBlock = block.id === blockId || block.id === originalBlockId ||
          ((block.isClonedBlock || block.isStaticClone) && block.originalBlockId === originalBlockId) ||
          (block.isPlaceholderWidget && block.placeholderForWidget === originalBlockId);

        // 2. Finding Widgets Synchronization (Finding Overrides / Custom Icons)
        const isFindingStyleSync = 'findingOverrides' in newStyle || 'customIcon' in newStyle;
        if (isFindingWidget && isFindingStyleSync &&
          block.type === 'widget' &&
          (block.widgetType === 'findings-status' || block.widgetType === 'single-finding-status')) {

          const syncStyles = {};
          if ('findingOverrides' in newStyle) syncStyles.findingOverrides = newStyle.findingOverrides;
          if ('customIcon' in newStyle) syncStyles.customIcon = newStyle.customIcon;

          return {
            ...block,
            style: {
              ...block.style,
              // If this is the specific block being updated, apply all new styles.
              // Otherwise, only sync the findings-related styles.
              ...(isTargetBlock ? newStyle : syncStyles)
            }
          };
        }

        // 3. Standard specific block update (including clones/parent)
        if (isTargetBlock) {
          return { ...block, style: { ...block.style, ...newStyle } };
        }
        return block;
      };

      return prev.map(page => ({
        ...page,
        blocks: recursivelyUpdateBlocks(page.blocks, updateFn)
      }));
    });
  }, []);

  // Update block data configuration
  const updateBlockDataConfig = useCallback((blockId, newDataConfig) => {
    console.log('[PipsqueakReportBuilder] updateBlockDataConfig CALLED:', { blockId, newDataConfig });

    // Check if itemsPerPage is being updated - if so, trigger pagination recalculation
    // Also check for itemsPerPageFirstPage and itemsPerPageContinuation
    const shouldTriggerPagination = 'itemsPerPage' in newDataConfig ||
      'itemsPerPageFirstPage' in newDataConfig ||
      'itemsPerPageContinuation' in newDataConfig ||
      'tableFilters' in newDataConfig ||
      'deviceFilters' in newDataConfig;

    setPages(prev => {
      // Find the current block to log before/after - search ALL pages
      let blockBeforeUpdate = null;
      for (const p of prev) {
        // Recursive find
        const found = findBlockById(p.blocks, blockId);
        if (found) {
          blockBeforeUpdate = found;
          break;
        }
      }
      console.log('[PipsqueakReportBuilder] BEFORE update - tableColumns:', blockBeforeUpdate?.dataConfig?.tableColumns?.length);

      const originalBlockId = resolveOriginalBlockId(blockId, prev);

      const updateFn = (block) => {
        if (block.id === blockId || block.id === originalBlockId ||
          ((block.isClonedBlock || block.isStaticClone) && block.originalBlockId === originalBlockId) ||
          (block.isPlaceholderWidget && block.placeholderForWidget === originalBlockId)) {
          return { ...block, dataConfig: { ...block.dataConfig, ...newDataConfig } };
        }
        return block;
      };

      const newPages = prev.map(page => ({
        ...page,
        blocks: recursivelyUpdateBlocks(page.blocks, updateFn)
      }));

      // Log after - search ALL pages (recursive)
      let updatedBlock = null;
      for (const p of newPages) {
        const found = findBlockById(p.blocks, blockId);
        if (found) {
          updatedBlock = found;
          break;
        }
      }
      console.log('[PipsqueakReportBuilder] AFTER update - tableColumns:', updatedBlock?.dataConfig?.tableColumns?.length);

      return newPages;
    });

    // Trigger pagination recalculation if itemsPerPage changed
    // This runs after setPages completes, ensuring the state is updated
    if (shouldTriggerPagination) {
      setBlockCountTrigger(prev => prev + 1);
    }
  }, [currentPageId]);

  // Toggle edit mode for a block
  const toggleEditMode = useCallback((blockId) => {
    setEditingBlocks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(blockId)) {
        newSet.delete(blockId);
      } else {
        newSet.add(blockId);
      }
      return newSet;
    });
  }, []);

  // Update block custom data
  const updateBlockCustomData = useCallback((blockId, customData) => {
    setPages(prev => {
      // Find the block to check for special synchronization (like header/footer)
      let targetType = null;
      for (const page of prev) {
        const b = findBlockById(page.blocks, blockId);
        if (b) {
          if (b.type === 'element' && (b.elementType === 'header' || b.elementType === 'footer')) {
            targetType = b.elementType;
          }
          break;
        }
      }

      const updateFn = (block) => {
        // 1. Header/Footer sync
        if (targetType && block.type === 'element' && block.elementType === targetType) {
          return { ...block, customData: { ...block.customData, ...customData } };
        }

        const originalBlockId = resolveOriginalBlockId(blockId, prev);

        // 2. Standard specific block update
        if (block.id === blockId || block.id === originalBlockId ||
          ((block.isClonedBlock || block.isStaticClone) && block.originalBlockId === originalBlockId) ||
          (block.isPlaceholderWidget && block.placeholderForWidget === originalBlockId)) {
          return { ...block, customData: { ...block.customData, ...customData } };
        }
        return block;
      };

      return prev.map(page => ({
        ...page,
        blocks: recursivelyUpdateBlocks(page.blocks, updateFn)
      }));
    });
  }, []);

  // ========== WIDGET GROUP MANAGEMENT ==========

  // Create a new group with the selected widget as leader
  const createWidgetGroup = useCallback((leaderId, groupName = 'Unnamed Group') => {
    const groupId = `group-${Date.now()}`;

    // Update the leader block with group properties
    setPages(prevPages => prevPages.map(page => ({
      ...page,
      blocks: page.blocks.map(block =>
        block.id === leaderId
          ? {
            ...block,
            groupId,
            groupRole: 'leader',
            groupOrder: 0,
            groupLeaderId: null
          }
          : block
      )
    })));

    // Add to widgetGroups state
    setWidgetGroups(prev => ({
      ...prev,
      [groupId]: {
        leaderId,
        followerIds: [],
        name: groupName
      }
    }));

    return groupId;
  }, []);

  // Add a widget as a follower to an existing group
  const addToWidgetGroup = useCallback((blockId, groupId) => {
    const group = widgetGroups[groupId];
    if (!group) return;

    // Get current follower count for order
    const followerOrder = group.followerIds.length + 1;

    // Update the block with group properties
    setPages(prevPages => prevPages.map(page => ({
      ...page,
      blocks: page.blocks.map(block =>
        block.id === blockId
          ? {
            ...block,
            groupId,
            groupRole: 'follower',
            groupOrder: followerOrder,
            groupLeaderId: group.leaderId
          }
          : block
      )
    })));

    // Update widgetGroups state
    setWidgetGroups(prev => ({
      ...prev,
      [groupId]: {
        ...prev[groupId],
        followerIds: [...prev[groupId].followerIds, blockId]
      }
    }));
  }, [widgetGroups]);

  // Remove a widget from its group
  const removeFromWidgetGroup = useCallback((blockId) => {
    // Find which group this block belongs to
    let groupId = null;
    let isLeader = false;

    for (const [gId, group] of Object.entries(widgetGroups)) {
      if (group.leaderId === blockId) {
        groupId = gId;
        isLeader = true;
        break;
      }
      if (group.followerIds.includes(blockId)) {
        groupId = gId;
        break;
      }
    }

    if (!groupId) return;

    if (isLeader) {
      // If removing leader, dissolve the entire group
      const group = widgetGroups[groupId];
      const allBlockIds = [group.leaderId, ...group.followerIds];

      // Clear group properties from all blocks
      setPages(prevPages => prevPages.map(page => ({
        ...page,
        blocks: page.blocks.map(block =>
          allBlockIds.includes(block.id)
            ? {
              ...block,
              groupId: null,
              groupRole: null,
              groupOrder: null,
              groupLeaderId: null
            }
            : block
        )
      })));

      // Remove group from state
      setWidgetGroups(prev => {
        const newGroups = { ...prev };
        delete newGroups[groupId];
        return newGroups;
      });
    } else {
      // Just remove the follower
      setPages(prevPages => prevPages.map(page => ({
        ...page,
        blocks: page.blocks.map(block =>
          block.id === blockId
            ? {
              ...block,
              groupId: null,
              groupRole: null,
              groupOrder: null,
              groupLeaderId: null
            }
            : block
        )
      })));

      // Update widgetGroups to remove follower
      setWidgetGroups(prev => ({
        ...prev,
        [groupId]: {
          ...prev[groupId],
          followerIds: prev[groupId].followerIds.filter(id => id !== blockId)
        }
      }));
    }
  }, [widgetGroups]);

  // Remove block
  const removeBlock = useCallback((blockId) => {
    // Check if block is a cloned block
    const block = currentPage.blocks.find(b => b.id === blockId);
    if (block?.isClonedBlock) {
      console.warn('[ReportBuilder] Cannot remove cloned blocks on continuation pages');
      alert('Cannot remove widgets from continuation pages. Remove the original widget instead.');
      return;
    }

    setPages(prev => {
      // Recursive function to find all blocks to remove (including dependents AND children)
      const findBlocksToRemove = (targetId, allPages) => {
        const idsToRemove = new Set([targetId]);

        // Helper to find block and its children recursively
        const addChildrenToSet = (blocks, targetId) => {
          const block = blocks.find(b => b.id === targetId);
          if (block && block.children) {
            block.children.forEach(child => {
              idsToRemove.add(child.id);
              addChildrenToSet(block.children, child.id);
            });
          } else if (!block) {
            // Try searching children of other blocks
            for (const b of blocks) {
              if (b.children) addChildrenToSet(b.children, targetId);
            }
          }
        };

        allPages.forEach(p => addChildrenToSet(p.blocks, targetId));

        // Find blocks dependent on any of the current idsToRemove
        let foundNewDependent = true;
        while (foundNewDependent) {
          foundNewDependent = false;
          allPages.forEach(p => {
            // We need to traverse recursively to find dependents that might be nested
            const checkDependents = (blocks) => {
              blocks.forEach(b => {
                if (b.config?.dependentOn && idsToRemove.has(b.config.dependentOn) && !idsToRemove.has(b.id)) {
                  idsToRemove.add(b.id);
                  foundNewDependent = true;
                }
                if (b.children) checkDependents(b.children);
              });
            };
            checkDependents(p.blocks);
          });
        }

        return idsToRemove;
      };

      const idsToRemove = findBlocksToRemove(blockId, prev);

      // ALSO find all cloned blocks and static clones that reference any removed block
      // These are on continuation pages and must be removed too
      prev.forEach(p => {
        p.blocks.forEach(b => {
          if ((b.isClonedBlock || b.isStaticClone) && b.originalBlockId && idsToRemove.has(b.originalBlockId)) {
            idsToRemove.add(b.id);
          }
        });
      });

      // Recursive function to remove block from a list of blocks (handles nesting)
      const removeBlockFromList = (blocks, idsToRemove) => {
        return blocks.reduce((acc, block) => {
          // If this block is to be removed, skip it
          if (idsToRemove.has(block.id)) {
            return acc;
          }

          // If block has children, recursively filter them
          const updatedChildren = block.children && block.children.length > 0
            ? removeBlockFromList(block.children, idsToRemove)
            : block.children;

          return [...acc, {
            ...block,
            children: updatedChildren
          }];
        }, []);
      };

      // Remove the blocks from all pages
      const updatedPages = prev.map(page => ({
        ...page,
        blocks: removeBlockFromList(page.blocks, idsToRemove)
      }));

      // Also remove any continuation pages for these blocks
      // Check BOTH continuationOf AND continuationForPage since the codebase uses both field names
      // Also remove pages where ALL remaining blocks are gone (empty after block removal)
      const pagesWithoutContinuations = updatedPages.filter(page => {
        if (!page.isContinuationPage) return true; // Keep non-continuation pages
        // Remove if continuationOf matches a removed block
        if (page.continuationOf && idsToRemove.has(page.continuationOf)) return false;
        // Remove if ALL cloned blocks on this page reference removed originals
        const clonedBlocks = page.blocks.filter(b => b.isClonedBlock);
        if (clonedBlocks.length === 0 && page.isContinuationPage) return false; // Empty continuation page
        const allClonedBlocksOrphaned = clonedBlocks.length > 0 && clonedBlocks.every(b => idsToRemove.has(b.originalBlockId) || !b.originalBlockId);
        if (allClonedBlocksOrphaned) return false;
        return true;
      });
      // Also remove any placeholder pages for source pages that no longer have paginated widgets
      const pagesWithoutOrphanedPlaceholders = pagesWithoutContinuations.filter(page => {
        if (!page.isPlaceholder || !page.placeholderForPage) return true; // Keep non-placeholder pages
        // Find the source page this placeholder is for
        const sourcePage = pagesWithoutContinuations.find(p => p.id === page.placeholderForPage);
        if (!sourcePage) return false; // Source page doesn't exist anymore — remove placeholder
        // Check if source page still has any paginated widgets
        const hasPaginatedWidgets = sourcePage.blocks.some(b =>
          b.type === 'widget' && PAGINATED_WIDGETS.includes(b.widgetType) && !b.isClonedBlock && !b.isStaticClone
        );
        if (!hasPaginatedWidgets) return false; // No paginated widgets left — remove placeholder
        // Check if all placeholder widgets reference removed blocks
        const placeholderWidgets = page.blocks.filter(b => b.isPlaceholderWidget);
        if (placeholderWidgets.length > 0) {
          const allPlaceholderWidgetsOrphaned = placeholderWidgets.every(b =>
            b.placeholderForWidget && idsToRemove.has(b.placeholderForWidget)
          );
          if (allPlaceholderWidgetsOrphaned) return false; // All placeholder widgets are orphaned
        }
        return true;
      });

      return pagesWithoutOrphanedPlaceholders;
    });

    // Also remove from global headers/footers if present
    if (window.__headerElements) {
      // We need a way to update the global header elements. 
      // Since they are not in state here (except via whatever mechanism updates them),
      // we might need to trigger an update event or similar.
      // However, for now, let's assume they might be in the 'pages' state context if we unify them.
      // BUT, headerElements are usually passed down or managed via a separate context/state.
      // If they are managed in the parent or via a separate hook, we need that setter.
      // Looking at the code, 'headerElements' seem to be passed as props or globals?
      // Actually lines 13046 show 'headerElements' being used directly.
      // If they are strictly global `window.__headerElements`, we can mutate them (ugly but works for now as per immediate previous patterns)
      // Better: If we have setHeaderElements/setFooterElements available?
      // The file scan didn't show them.
      // Let's assume we need to update blocks recursively in pages, which we did.

      // Note: The prompt implies these might be inside the page structure now due to "nested" changes.
      // If they are in `pages` (as Sections), the recursive remover handles it.
      // If they are separate, we might need to handle them. 
      // Given the "addLayoutSection" adds them to `pages`, they ARE in pages now! 
      // So the recursive `removeBlockFromList` above SHOULD handle it.
    }

    if (selectedBlock === blockId) {
      setSelectedBlock(null);
    }
  }, [currentPageId, selectedBlock, currentPage]);

  // Toggle Continuation Placeholder page
  const handleTogglePlaceholder = useCallback((blockId) => {
    // Find the page index that contains the original widget
    const sourcePageIndex = pages.findIndex(p => p.blocks.some(b => b.id === blockId));
    if (sourcePageIndex === -1) return;

    const sourcePage = pages[sourcePageIndex];

    // Find if placeholder already exists for THIS PAGE
    const existingPlaceholderIndex = pages.findIndex(p => p.isPlaceholder && p.placeholderForPage === sourcePage.id);

    if (existingPlaceholderIndex >= 0) {
      // Remove existing placeholder and clean up its visible state
      setPages(prevPages => {
        const newPages = [...prevPages];
        const idx = newPages.findIndex(p => p.isPlaceholder && p.placeholderForPage === sourcePage.id);
        if (idx >= 0) {
          const removedId = newPages[idx].id;
          // Clean up manual visibility flag for this placeholder
          setVisiblePlaceholderIds(prev => {
            const next = new Set(prev);
            next.delete(removedId);
            return next;
          });
          newPages.splice(idx, 1);
        }
        return newPages;
      });
      console.log(`[Placeholder] Removed placeholder for page: ${sourcePage.id}`);
      return;
    }

    // Create dummy clones of ALL widgets on the source page to serve as visual placeholders
    const dummyWidgets = sourcePage.blocks
      .filter(b => b.type === 'widget')
      .map(w => {
        const dummyWidgetId = `widget-placeholder-${w.id}-${Date.now()}`;

        let customData = { ...w.customData };
        if (w.widgetType === 'table') {
          const headers = w.dataConfig?.tableColumns?.map(col => col.header || col.fieldPath || 'Column') || ['Column 1', 'Column 2'];
          const sampleRow = headers.map(() => 'Sample Data');
          customData = {
            headers,
            rows: [sampleRow, sampleRow, sampleRow], // 3 rows of dummy data
            __isPlaceholder__: true
          };
        } else {
          customData = { __isPlaceholder__: true, reason: 'Placeholder Sample' };
        }

        return {
          ...w,
          id: dummyWidgetId,
          isPlaceholderWidget: true,
          placeholderForWidget: w.id, // Map it back to original widget
          customData,
          style: {
            ...w.style,
            y: w.style.y > 0 ? w.style.y : 150
          }
        };
      });

    const pageLabel = sourcePage.name || `Page ${sourcePageIndex + 1}`;

    // Add new placeholder immediately following the page
    const newPlaceholderId = `placeholder-page-${Date.now()}`;
    const newPlaceholderPage = {
      id: newPlaceholderId,
      name: `Placeholder: ${pageLabel}`,
      isPlaceholder: true,
      placeholderForPage: sourcePage.id,
      blocks: dummyWidgets, // Inject all dummy widgets!
      config: {}
    };

    setPages(prevPages => {
      const idx = prevPages.findIndex(p => p.id === sourcePage.id);
      if (idx === -1) return prevPages;
      const newPages = [...prevPages];
      newPages.splice(idx + 1, 0, newPlaceholderPage);
      return newPages;
    });

    // Make the new placeholder immediately visible (so the filter doesn't hide it)
    setVisiblePlaceholderIds(prev => new Set([...prev, newPlaceholderId]));

    console.log(`[Placeholder] Created placeholder for page: ${sourcePage.id}`);

    // Automatically switch to the new placeholder page and scroll to it
    setCurrentPageId(newPlaceholderId);
    setTimeout(() => {
      const pageContainer = document.querySelector(`[data-page-id="${newPlaceholderId}"]`);
      if (pageContainer) {
        pageContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }, [pages]);

  // Bring block to front (highest z-index)
  const bringToFront = useCallback((blockId) => {
    setPages(prev => prev.map(page => {
      if (page.id !== currentPageId) return page;

      // Get all blocks with their current z-index values
      const blocksWithZIndex = page.blocks.map(b => {
        const z = b.style?.zIndex;
        // Normalize: convert 'auto', undefined, or invalid to 1
        const normalizedZ = (typeof z === 'number' && z > 0) ? z : 1;
        return { block: b, zIndex: normalizedZ };
      });

      // Find the actual maximum z-index currently in use
      const maxZIndex = blocksWithZIndex.length > 0
        ? Math.max(...blocksWithZIndex.map(b => b.zIndex))
        : 1;

      // Update the target block to be in front
      return {
        ...page,
        blocks: page.blocks.map(block => {
          if (block.id === blockId) {
            return {
              ...block,
              style: {
                ...block.style,
                zIndex: maxZIndex + 1
              }
            };
          }
          return block;
        })
      };
    }));
    setContextMenu({ visible: false, x: 0, y: 0, blockId: null });
  }, [currentPageId]);

  // Send block to back (lowest z-index, but still visible)
  const sendToBack = useCallback((blockId) => {
    setPages(prev => prev.map(page => {
      if (page.id !== currentPageId) return page;

      // Get all blocks with their current z-index values (excluding the target block)
      const otherBlocks = page.blocks.filter(b => b.id !== blockId);
      const blocksWithZIndex = otherBlocks.map(b => {
        const z = b.style?.zIndex;
        // Normalize: convert 'auto', undefined, or invalid to 1
        return (typeof z === 'number' && z > 0) ? z : 1;
      });

      // Find the actual minimum z-index currently in use by other blocks
      const minZIndex = blocksWithZIndex.length > 0
        ? Math.min(...blocksWithZIndex)
        : 1;

      // Set target block to be below all others (minZIndex - 1, but at least 1)
      const targetZIndex = Math.max(1, minZIndex - 1);

      return {
        ...page,
        blocks: page.blocks.map(block => {
          if (block.id === blockId) {
            return {
              ...block,
              style: {
                ...block.style,
                zIndex: targetZIndex
              }
            };
          }
          return block;
        })
      };
    }));
    setContextMenu({ visible: false, x: 0, y: 0, blockId: null });
  }, [currentPageId]);

  // Copy/Paste Widget Logic
  // Create global lists of all header/footer elements for context menu availability
  const globalHeaderElements = useMemo(() => {
    const headers = [];
    pages.forEach(p => {
      p.blocks.forEach(block => {
        if ((block.type === 'element' || block.type === 'section') && block.elementType === 'header') {
          if (!headers.find(h => h.id === block.id)) {
            headers.push(block);
          }
        }
      });
    });
    return headers;
  }, [pages]);

  const globalFooterElements = useMemo(() => {
    const footers = [];
    pages.forEach(p => {
      p.blocks.forEach(block => {
        if ((block.type === 'element' || block.type === 'section') && block.elementType === 'footer') {
          if (!footers.find(f => f.id === block.id)) {
            footers.push(block);
          }
        }
      });
    });
    return footers;
  }, [pages]);

  // Store in window for access outside this scope (temporary solution kept for other usages if any)
  useEffect(() => {
    window.__headerElements = globalHeaderElements;
    window.__footerElements = globalFooterElements;
  }, [globalHeaderElements, globalFooterElements]);

  // ===== UNDO / REDO STATE & LOGIC =====
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoingRedoing = useRef(false);

  // Take a debounced snapshot of visual state (pages, global headers, global footers)
  useEffect(() => {
    // If the change was triggered *by* an undo/redo action, don't save a new snapshot
    if (isUndoingRedoing.current) {
      isUndoingRedoing.current = false;
      return;
    }

    const timer = setTimeout(() => {
      setHistory(prevHistory => {
        // Build the current snapshot containing EVERYTHING needed to restore layout
        const snapshot = JSON.stringify({
          pages,
          globalHeaderElements,
          globalFooterElements
        });

        // Prevent recording consecutive identical snapshots
        if (prevHistory.length > 0 && historyIndex >= 0) {
          if (prevHistory[historyIndex] === snapshot) {
            return prevHistory;
          }
        }

        // Standard Undo/Redo pattern: if we Undid something then made a NEW edit,
        // we must erase the "future" Redo states.
        const newHistory = prevHistory.slice(0, historyIndex + 1);
        newHistory.push(snapshot);

        // Optional: Cap history size to prevent enormous memory leaks on heavy editing
        if (newHistory.length > 30) {
          newHistory.shift();
          setHistoryIndex(newHistory.length - 1); // Maintain the pointer
          return newHistory;
        }

        setHistoryIndex(newHistory.length - 1);
        return newHistory;
      });
    }, 600); // 600ms debounce allows rapid drag-and-drop to settle as one single move

    return () => clearTimeout(timer);
  }, [pages, globalHeaderElements, globalFooterElements]); // Note: DO NOT include history or historyIndex to avoid loops

  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      isUndoingRedoing.current = true;
      const previousIndex = historyIndex - 1;
      const previousState = JSON.parse(history[previousIndex]);

      setPages(previousState.pages);
      // Global headers/footers rebuild naturally as they are derived from pages,
      // but we restore them if they're needed globally outside of page syncs.

      setHistoryIndex(previousIndex);
      console.log('[ReportBuilder] Undid to history index:', previousIndex);
    }
  }, [history, historyIndex]);

  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      isUndoingRedoing.current = true;
      const nextIndex = historyIndex + 1;
      const nextState = JSON.parse(history[nextIndex]);

      setPages(nextState.pages);

      setHistoryIndex(nextIndex);
      console.log('[ReportBuilder] Redid to history index:', nextIndex);
    }
  }, [history, historyIndex]);
  // ===================================
  const handleCopyBlock = useCallback((blockId) => {
    // Find the block in current page or other pages
    let blockToCopy = null;

    // First check current page (most likely)
    const page = pages.find(p => p.id === currentPageId);
    if (page) {
      blockToCopy = page.blocks.find(b => b.id === blockId);
    }

    // If not found, search all pages
    if (!blockToCopy) {
      for (const p of pages) {
        const found = p.blocks.find(b => b.id === blockId);
        if (found) {
          blockToCopy = found;
          break;
        }
      }
    }

    if (blockToCopy) {
      // Deep clone to detach references
      const clonedBlock = JSON.parse(JSON.stringify(blockToCopy));
      // Store original page ID for comparison logic later
      clonedBlock.pageId = currentPageId;
      setClipboardBlock(clonedBlock);
      console.log('[ReportBuilder] Widget copied to clipboard:', clonedBlock);
      // Optional: Add toast notification here
    }

    // Close context menu
    setContextMenu({ visible: false, x: 0, y: 0, blockId: null });
  }, [pages, currentPageId]);

  const handlePasteBlock = useCallback((targetPageId, x, y) => {
    if (!clipboardBlock) return;

    console.log('[ReportBuilder] Pasting widget:', clipboardBlock, 'at', x, y);

    setPages(prevPages => prevPages.map(page => {
      if (page.id !== targetPageId) return page;

      // Generate new ID for the pasted block
      // Format: type-timestamp-random
      const newId = `${clipboardBlock.widgetType || clipboardBlock.elementType || 'block'}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

      // Determine position
      // If x,y provided (from context menu click), use them (adjusted for canvas offset if needed)
      // Otherwise, offset from original position
      let position = { ...clipboardBlock.position };

      // If we have mouse coordinates from context menu, we need to convert them to canvas coordinates
      // But contextMenu.x/y are likely screen/client coordinates.
      // For simplicity in V1, let's just offset from the original position slightly to make it visible
      // Or if copying to a new page, use original position

      // Logic: 
      // 1. If pasting via Context Menu (mouse coordinates provided) -> Paste at mouse location
      // 2. If pasting via Keyboard (no mouse coordinates) -> Offset from original position

      if (x !== undefined && y !== undefined && x !== 0 && y !== 0) {
        // Find the target page element to get its bounding rect
        const pageElement = document.querySelector(`.page-background[data-page-id="${targetPageId}"]`);

        if (pageElement) {
          const rect = pageElement.getBoundingClientRect();

          // Calculate relative position directly from the mouse click
          // We want the CLICK point to be the top-left of the widget? Or center?
          // User said: "paste it to where the mouse cursor has been pointing so user will not be confused where it has been pasted"
          // Usually pasting places the top-left corner at the cursor.

          let relativeX = x - rect.left;
          let relativeY = y - rect.top;

          // Adjust for scroll if needed? getBoundingClientRect accounts for viewport, clientX/Y also viewport.
          // So relativeX/Y should be correct relative to the element.

          // Optional: Snap to grid
          // relativeX = Math.round(relativeX / gridConfig.cellWidth) * gridConfig.cellWidth;
          // relativeY = Math.round(relativeY / gridConfig.cellHeight) * gridConfig.cellHeight;

          // Ensure positive coordinates
          position.x = Math.max(0, relativeX);
          position.y = Math.max(0, relativeY);

          console.log('[ReportBuilder] Calculated paste position:', position.x, position.y);
        }
      } else {
        // Fallback: Offset logic
        if (clipboardBlock.pageId === targetPageId) {
          position.x += gridConfig.cellWidth;
          position.y += gridConfig.cellHeight;
        }
      }

      // Ensure it stays within bounds? (Optional)

      const newBlock = {
        ...clipboardBlock,
        id: newId,
        position: position,
        // Reset internal state if needed
        groupId: null, // Don't copy group membership initially
        groupRole: null
      };

      return {
        ...page,
        blocks: [...page.blocks, newBlock]
      };
    }));

    // Close context menu
    setContextMenu({ visible: false, x: 0, y: 0, blockId: null });
  }, [clipboardBlock, gridConfig]);

  // Keyboard Shortcuts for Copy/Paste (Global)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger if user is typing in an input or textarea
      if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) {
        return;
      }

      // Check for Ctrl/Command + C (Copy)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
        if (selectedBlock) {
          console.log('[ReportBuilder] Keyboard Shortcut: Copying block', selectedBlock);
          handleCopyBlock(selectedBlock);
        }
      }

      // Check for Ctrl/Command + V (Paste)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
        if (clipboardBlock) {
          console.log('[ReportBuilder] Keyboard Shortcut: Pasting block to page', currentPageId);
          handlePasteBlock(currentPageId);
        }
      }

      // Check for Delete or Backspace key
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const blocksToDelete = selectedBlocks?.length > 0
          ? selectedBlocks
          : (selectedBlock ? [selectedBlock] : []);

        if (blocksToDelete.length > 0) {
          console.log('[ReportBuilder] Keyboard Shortcut: Deleting blocks', blocksToDelete);
          e.preventDefault();
          blocksToDelete.forEach(id => removeBlock(id));
        }
      }

      // Check for Undo (Ctrl/Cmd + Z)
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        handleUndo();
      }

      // Check for Redo (Ctrl/Cmd + Shift + Z or Ctrl/Cmd + Y)
      if ((e.ctrlKey || e.metaKey) && ((e.shiftKey && e.key.toLowerCase() === 'z') || e.key.toLowerCase() === 'y')) {
        e.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedBlock, selectedBlocks, clipboardBlock, currentPageId, handleCopyBlock, handlePasteBlock, removeBlock, handleUndo, handleRedo]);


  // Handle adding a block to a specific Header
  // Handle adding a block to a specific Header - UPDATED TO SYNC ACROSS ALL PAGES
  const handleAddToHeader = useCallback((sourceBlockId, headerId) => {
    // 1. Find source block
    let sourceBlock = null;

    // Helper to find block
    const findSource = (blocks) => {
      for (const b of blocks) {
        if (b.id === sourceBlockId) {
          sourceBlock = b;
          return true;
        }
        if (b.children && findSource(b.children)) return true;
      }
      return false;
    };

    for (const p of pages) {
      if (findSource(p.blocks)) break;
    }

    // Also check global lists just in case
    if (!sourceBlock) {
      sourceBlock = globalHeaderElements.find(h => h.id === sourceBlockId) || globalFooterElements.find(f => f.id === sourceBlockId);
    }

    if (!sourceBlock) return;

    // 2. Clone the block
    const newBlock = JSON.parse(JSON.stringify(sourceBlock));
    // CRITICAL: Generate ID ONCE and use it for all instances to allow synchronization
    newBlock.id = `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    // Reset position for the container (relative to header 0,0)
    newBlock.position = { x: 0, y: 0 };
    // Ensure absolute positioning
    newBlock.style = {
      ...newBlock.style,
      position: 'absolute',
      // Reset potentially problematic layout props from flex parents
      flex: undefined,
      width: sourceBlock.size?.width || 200,
      height: sourceBlock.size?.height || 50
    };

    // 3. Add to ALL Headers across ALL pages (Synchronization)
    setPages(prev => {
      const updateHeader = (blocks) => {
        return blocks.map(b => {
          // Update ANY header element
          if ((b.type === 'element' || b.type === 'section') && b.elementType === 'header') {
            return {
              ...b,
              children: [...(b.children || []), newBlock]
            };
          }
          if (b.children) {
            return {
              ...b,
              children: updateHeader(b.children)
            };
          }
          return b;
        });
      };
      return prev.map(p => ({
        ...p,
        blocks: updateHeader(p.blocks)
      }));
    });

    // Switch to the page where the header starts (or stay if visible)
    // Optional: Select the new block
    setTimeout(() => setSelectedBlock(newBlock.id), 100);

  }, [pages, globalHeaderElements, globalFooterElements]);

  // Handle adding a block to a specific Footer - UPDATED TO SYNC ACROSS ALL PAGES
  const handleAddToFooter = useCallback((sourceBlockId, footerId) => {
    // 1. Find source block
    let sourceBlock = null;

    const findSource = (blocks) => {
      for (const b of blocks) {
        if (b.id === sourceBlockId) {
          sourceBlock = b;
          return true;
        }
        if (b.children && findSource(b.children)) return true;
      }
      return false;
    };

    for (const p of pages) {
      if (findSource(p.blocks)) break;
    }

    if (!sourceBlock) {
      sourceBlock = globalHeaderElements.find(h => h.id === sourceBlockId) || globalFooterElements.find(f => f.id === sourceBlockId);
    }

    if (!sourceBlock) return;

    // 2. Clone the block
    const newBlock = JSON.parse(JSON.stringify(sourceBlock));
    // CRITICAL: Generate ID ONCE and use it for all instances to allow synchronization
    newBlock.id = `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    newBlock.position = { x: 0, y: 0 };
    newBlock.style = {
      ...newBlock.style,
      position: 'absolute',
      flex: undefined,
      width: sourceBlock.size?.width || 200,
      height: sourceBlock.size?.height || 50
    };

    // 3. Add to ALL Footers across ALL pages (Synchronization)
    setPages(prev => {
      const updateFooter = (blocks) => {
        return blocks.map(b => {
          // Update ANY footer element
          if ((b.type === 'element' || b.type === 'section') && b.elementType === 'footer') {
            return {
              ...b,
              children: [...(b.children || []), newBlock]
            };
          }
          if (b.children) {
            return {
              ...b,
              children: updateFooter(b.children)
            };
          }
          return b;
        });
      };
      return prev.map(p => ({
        ...p,
        blocks: updateFooter(p.blocks)
      }));
    });

    setTimeout(() => setSelectedBlock(newBlock.id), 100);
  }, [pages, globalHeaderElements, globalFooterElements]);

  // Handle context menu
  const handleContextMenu = useCallback((e, blockId) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      blockId: blockId // Null if right-clicking canvas
    });
  }, []);

  // Close context menu
  const closeContextMenu = useCallback(() => {
    setContextMenu({ visible: false, x: 0, y: 0, blockId: null });
  }, []);

  // Handle orientation toggle with widget rescaling
  const handleOrientationToggle = useCallback(() => {
    const newOrientation = orientation === 'portrait' ? 'landscape' : 'portrait';
    const oldColumns = orientation === 'portrait' ? PORTRAIT_CONFIG.columns : LANDSCAPE_CONFIG.columns;
    const newColumns = newOrientation === 'portrait' ? PORTRAIT_CONFIG.columns : LANDSCAPE_CONFIG.columns;
    const newPageWidth = newOrientation === 'portrait' ? PORTRAIT_CONFIG.pageWidth : LANDSCAPE_CONFIG.pageWidth;

    // Scale factor for width and x-position
    // e.g. Portrait -> Landscape: 25/18 = 1.38 (Grow)
    // e.g. Landscape -> Portrait: 18/25 = 0.72 (Shrink)
    const ratio = newColumns / oldColumns;

    console.log(`[handleOrientationToggle] Switching to ${newOrientation}`);
    console.log(`[handleOrientationToggle] Rescaling widgets by ratio: ${ratio.toFixed(3)} (${oldColumns} -> ${newColumns} cols)`);

    // Create deep copy of pages to avoid mutation
    const updatedPages = pages.map(page => ({
      ...page,
      blocks: page.blocks.map(block => {
        // FORCE FULL WIDTH FOR HEADERS AND FOOTERS
        // This ensures they always look correct when switching orientations
        const isHeaderFooter = block.type === 'element' && (block.elementType === 'header' || block.elementType === 'footer');

        if (isHeaderFooter) {
          return {
            ...block,
            x: 0,
            w: newColumns,
            position: {
              ...block.position,
              x: 0
            },
            size: {
              ...block.size,
              width: newPageWidth
            }
          };
        }

        // Calculate new width
        let newW = Math.round(block.w * ratio);
        // Ensure minimum width of 2 columns so it doesn't disappear
        newW = Math.max(2, newW);
        // Ensure it doesn't exceed total columns
        newW = Math.min(newW, newColumns);

        // Calculate new x position
        let newX = Math.round(block.x * ratio);

        // Constraint: Ensure block fits within the page width
        if (newX + newW > newColumns) {
          // If it overflows, try to shift X left
          if (newW <= newColumns) {
            newX = newColumns - newW;
          } else {
            // Should not happen due to min(newW, newColumns) above, but safety check
            newX = 0;
            newW = newColumns;
          }
        }

        return {
          ...block,
          x: newX,
          w: newW
          // We generally preserve 'y' and 'h' as vertical units are roughly consistent
          // logic-wise (rows) even if pixels change.
          // This prevents distortions for text and simple stacked layouts.
        };
      })
    }));

    setOrientation(newOrientation);
    setPages(updatedPages);
  }, [orientation, pages]);

  // Layout Correction Effect
  // Automatically enforces full width for headers/footers whenever orientation changes
  // This acts as a safety net to ensure they are always correct, even if they were added in a different mode or resized incorrectly
  useEffect(() => {
    const targetConfig = orientation === 'landscape' ? LANDSCAPE_CONFIG : PORTRAIT_CONFIG;

    setPages(prevPages => prevPages.map(page => ({
      ...page,
      blocks: page.blocks.map(block => {
        const isHeaderFooter = block.type === 'element' && (block.elementType === 'header' || block.elementType === 'footer');
        if (isHeaderFooter) {
          // Force exact dimensions for the current orientation
          return {
            ...block,
            x: 0,
            w: targetConfig.columns,
            position: {
              ...block.position,
              x: 0
            },
            size: {
              ...block.size,
              width: targetConfig.pageWidth
            }
          };
        }
        return block;
      })
    })));
  }, [orientation]);

  const exportToPDF = useCallback(async () => {
    try {
      // EMERGENCY KILL SWITCH: NEVER allow client-side export for 100+ pages
      // This will ALWAYS crash the browser - force server-side instead
      if (pages.length >= 100) {
        console.error(`[exportToPDF] ❌ BLOCKED: Cannot use client-side export for ${pages.length} pages. This will crash the browser.`);
        throw new Error(`Client-side export is not supported for templates with ${pages.length} pages. Please use server-side export.`);
      }

      // Note: isExporting state is managed by the calling function (downloadPDF or viewPDFInBrowser)
      const pdf = new jsPDF(gridConfig.orientation === 'portrait' ? 'p' : 'l', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Store the original current page
      const originalCurrentPageId = currentPageId;

      // PERFORMANCE: Use requestAnimationFrame for better timing (faster than setTimeout)
      const waitForRender = () => new Promise(resolve => requestAnimationFrame(resolve));

      // PERFORMANCE: For large templates (50+ pages), use batching and memory cleanup
      const isLargeExport = pages.length > 30;
      const isExtraLargeExport = pages.length > 100; // 100+ pages = ULTRA mode

      // ULTRA aggressive batching for 100+ pages to prevent crashes
      // 100-200 pages: batch of 3
      // 200+ pages: batch of 2 (safest for 263 pages)
      let BATCH_SIZE;
      if (isExtraLargeExport) {
        BATCH_SIZE = pages.length > 200 ? 2 : 3;
      } else if (isLargeExport) {
        BATCH_SIZE = 5;
      } else {
        BATCH_SIZE = pages.length;
      }

      console.log(`[exportToPDF] Starting export of ${pages.length} pages. isLargeExport: ${isLargeExport}, isExtraLargeExport: ${isExtraLargeExport}, BATCH_SIZE: ${BATCH_SIZE}`);

      setExportProgress({ current: 0, total: pages.length });

      // Process pages in batches to avoid memory issues
      for (let batchStart = 0; batchStart < pages.length; batchStart += BATCH_SIZE) {
        const batchEnd = Math.min(batchStart + BATCH_SIZE, pages.length);
        console.log(`[exportToPDF] Processing batch: pages ${batchStart + 1} to ${batchEnd}`);

        for (let i = batchStart; i < batchEnd; i++) {
          if (i > 0) pdf.addPage();

          const page = pages[i];
          setExportProgress({ current: i + 1, total: pages.length });

          // Switch to the page we want to capture
          setCurrentPageId(page.id);

          // PERFORMANCE: Optimized wait time based on export size
          // Large exports: 1 frame (~16ms) - faster but still allows render
          // Small exports: 2 frames (~33ms) - ensures high quality
          await waitForRender();
          if (!isLargeExport) {
            await waitForRender();
          }

          // Force chart re-rendering for PDF export
          const chartElements = document.querySelectorAll('canvas');
          chartElements.forEach(canvas => {
            // Trigger chart resize to ensure proper rendering
            const chartInstance = ChartJS.getChart(canvas);
            if (chartInstance) {
              chartInstance.resize();
            }
          });

          // PERFORMANCE: Skip extra wait for large exports - charts are already rendered from page switch
          if (!isLargeExport) {
            await waitForRender();
          }

          // Capture the visual content of the page
          if (page.blocks.length > 0) {
            // CRITICAL FIX: Find the SPECIFIC page canvas element using data-page-id attribute
            const pageCanvas = document.querySelector(`.page-background[data-page-id="${page.id}"]`);

            // Verification: check if we're capturing the right page
            if (pageCanvas) {
              const pageBlocks = pageCanvas.querySelectorAll('.block');
              console.log(`[exportToPDF] ✓ Found page canvas for page ID ${page.id} with ${pageBlocks.length} blocks (expected ${page.blocks.length} blocks)`);

              // Double-check we're not capturing the same page multiple times
              const parentCanvas = pageCanvas.closest('.page-canvas');
              if (parentCanvas) {
                const parentPageId = parentCanvas.getAttribute('data-page-id');
                if (parentPageId !== page.id) {
                  console.error(`[exportToPDF] ERROR: Parent page-canvas ID mismatch! Expected ${page.id}, got ${parentPageId}`);
                }
              }
            } else {
              console.error(`[exportToPDF] ERROR: No page canvas found for page ${i + 1} with ID ${page.id}`);
            }
            if (pageCanvas) {
              // Temporarily hide selection indicators and remove buttons
              const originalStyles = [];
              const blocks = pageCanvas.querySelectorAll('.block');

              // Also temporarily deselect any selected blocks
              const selectedBlocks = pageCanvas.querySelectorAll('.block.selected');
              selectedBlocks.forEach(block => {
                block.classList.remove('selected');
              });

              blocks.forEach((block, index) => {
                // Store original styles
                originalStyles[index] = {
                  border: block.style.border,
                  boxShadow: block.style.boxShadow,
                  position: block.style.position,
                  classList: Array.from(block.classList)
                };

                // Hide selection indicators and remove buttons
                block.style.border = 'none';
                block.style.boxShadow = 'none';

                // Hide resize handles and remove buttons
                const resizeHandles = block.querySelectorAll('.resize-handle');
                const removeButtons = block.querySelectorAll('.block-remove-btn');

                resizeHandles.forEach(handle => {
                  handle.style.display = 'none';
                });

                removeButtons.forEach(button => {
                  button.style.display = 'none';
                });
              });

              // Hide grid overlay if visible
              const gridOverlay = pageCanvas.querySelector('.grid-overlay');
              if (gridOverlay) {
                gridOverlay.style.display = 'none';
              }

              // Hide any empty canvas state
              const emptyCanvas = pageCanvas.querySelector('.empty-canvas');
              if (emptyCanvas) {
                emptyCanvas.style.display = 'none';
              }

              try {
                // PERFORMANCE: Use lower scale for large exports to reduce memory usage
                // scale: 1.5 provides good quality while being 44% less memory than scale: 2
                // scale: 1.2 for 100-200 pages (65% less memory than scale: 2)
                // scale: 1.0 for 200+ pages (75% less memory than scale: 2) - prevents crashes
                let canvasScale;
                if (isExtraLargeExport) {
                  canvasScale = pages.length > 200 ? 1.0 : 1.2;
                } else if (isLargeExport) {
                  canvasScale = 1.5;
                } else {
                  canvasScale = 2;
                }

                // Capture the canvas
                const canvas = await html2canvas(pageCanvas, {
                  scale: canvasScale, // Optimized resolution based on export size
                  useCORS: true,
                  allowTaint: false,
                  backgroundColor: '#ffffff',
                  width: 794, // A4 width in pixels
                  height: 1122, // A4 height in pixels
                  scrollX: 0,
                  scrollY: 0,
                  logging: false, // Disable console logging
                  removeContainer: false
                });

                // Restore original styles
                blocks.forEach((block, index) => {
                  if (originalStyles[index]) {
                    block.style.border = originalStyles[index].border;
                    block.style.boxShadow = originalStyles[index].boxShadow;
                    block.style.position = originalStyles[index].position;
                  }

                  // Show resize handles and remove buttons again
                  const resizeHandles = block.querySelectorAll('.resize-handle');
                  const removeButtons = block.querySelectorAll('.block-remove-btn');

                  resizeHandles.forEach(handle => {
                    handle.style.display = '';
                  });

                  removeButtons.forEach(button => {
                    button.style.display = '';
                  });
                });

                // Restore selected state
                selectedBlocks.forEach(block => {
                  block.classList.add('selected');
                });

                // Show grid overlay again if it was visible
                if (gridOverlay) {
                  gridOverlay.style.display = '';
                }

                // Show empty canvas state again if it was visible
                if (emptyCanvas) {
                  emptyCanvas.style.display = '';
                }

                // Add the captured image to PDF
                // PERFORMANCE: Use JPEG for large exports to reduce memory and file size (80% smaller)
                // PNG for small exports to maintain perfect quality
                // Lower JPEG quality for 200+ pages to reduce memory even more
                const imageFormat = isLargeExport ? 'image/jpeg' : 'image/png';
                let imageQuality;
                if (isExtraLargeExport) {
                  imageQuality = pages.length > 200 ? 0.85 : 0.90; // Lower quality for 200+ pages
                } else if (isLargeExport) {
                  imageQuality = 0.92;
                } else {
                  imageQuality = 1.0;
                }

                const imgData = canvas.toDataURL(imageFormat, imageQuality);
                const imgWidth = pageWidth; // Full width
                const imgHeight = (canvas.height * imgWidth) / canvas.width;

                // Position the image at the top of the page
                const startY = 0; // Start at the top
                const availableHeight = pageHeight; // Full page height

                const pdfImageFormat = isLargeExport ? 'JPEG' : 'PNG';

                if (imgHeight <= availableHeight) {
                  pdf.addImage(imgData, pdfImageFormat, 0, startY, imgWidth, imgHeight);
                  console.log(`[exportToPDF] ✓ Added page ${i + 1} to PDF (${pdfImageFormat}, full size: ${imgWidth}mm x ${imgHeight.toFixed(2)}mm)`);
                } else {
                  // Scale down if too tall
                  const scaledHeight = availableHeight;
                  const scaledWidth = (canvas.width * scaledHeight) / canvas.height;
                  pdf.addImage(imgData, pdfImageFormat, 0, startY, scaledWidth, scaledHeight);
                  console.log(`[exportToPDF] ✓ Added page ${i + 1} to PDF (${pdfImageFormat}, scaled: ${scaledWidth.toFixed(2)}mm x ${scaledHeight}mm)`);
                }
              } catch (canvasError) {
                console.warn('Failed to capture canvas, leaving page blank:', canvasError);
                // Leave page blank if capture fails
              }
            } else {
              // Leave page blank if canvas not found
            }
          } else {
            // No blocks on this page - leave it blank
          }
        }

        // PERFORMANCE: Memory cleanup after each batch for large exports
        if (isLargeExport && batchEnd < pages.length) {
          console.log(`[exportToPDF] Batch ${Math.floor(batchStart / BATCH_SIZE) + 1} complete. Cleaning up memory...`);

          // Force garbage collection by clearing references
          // This helps prevent memory buildup when processing large templates
          // ULTRA mode: Longer pause for 200+ pages to ensure GC runs
          const gcPauseTime = isExtraLargeExport && pages.length > 200 ? 200 : 100;
          await new Promise(resolve => setTimeout(resolve, gcPauseTime));

          // Allow UI to update between batches
          await waitForRender();

          // Extra render frame for ultra large exports to ensure browser stays responsive
          if (isExtraLargeExport) {
            await waitForRender();
          }
        }
      }

      // Restore the original current page
      setCurrentPageId(originalCurrentPageId);

      const filename = `pipsqueak-security-report-${new Date().getTime()}.pdf`;

      // Return the PDF blob for viewing in browser or downloading
      return { pdf, filename };
    } catch (error) {
      console.error('PDF export failed:', error);
      throw error; // Re-throw to be handled by calling function
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages, theme, currentPageId, gridConfig]);

  // View PDF in browser
  const viewPDFInBrowser = useCallback(async () => {
    try {
      setIsExporting(true);
      setExportError(null);
      setExportSuccess(false);

      // Wait for React to re-render with isExporting=true before capturing PDF
      await new Promise(resolve => setTimeout(resolve, 100));

      const result = await exportToPDF();

      if (result && result.pdf) {
        // Use data URL instead of blob URL for better compatibility
        // This ensures the PDF opens in the browser without issues
        const pdfDataUrl = result.pdf.output('dataurlstring');

        // Open PDF in new tab (same approach as PastScans.jsx)
        const newWindow = window.open(pdfDataUrl, '_blank');

        if (newWindow) {
          // Successfully opened
          setExportSuccess(true);

          // Close modal after showing success message
          setTimeout(() => {
            setShowExportModal(false);
            setExportSuccess(false);
          }, 1500);
        } else {
          // If popup was blocked, show error
          setExportError('Popup was blocked. Please allow popups for this site to view PDF in browser.');
        }
      } else {
        setExportError('Failed to generate PDF. Please try again.');
      }
    } catch (error) {
      console.error('Failed to view PDF:', error);
      setExportError('Failed to generate PDF for viewing. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [exportToPDF]);

  // Helper function to normalize file paths from different environments
  const normalizePath = useCallback((filepath) => {
    if (!filepath) return filepath;

    // Return the filepath as-is since it should already be the correct absolute path
    // stored in the database from the backend services
    return filepath;
  }, []);

  /**
   * Capture rendered HTML from all pages in the ReportBuilder
   * Option A: Client-side HTML capture for 100% visual fidelity
   */
  const captureRenderedHTML = useCallback(() => {
    console.log('[captureRenderedHTML] Starting HTML capture from DOM...');

    // Get all page containers
    const pageContainers = document.querySelectorAll('.page-container');

    if (!pageContainers || pageContainers.length === 0) {
      console.error('[captureRenderedHTML] No page containers found!');
      return null;
    }

    console.log(`[captureRenderedHTML] Found ${pageContainers.length} page containers`);

    // Capture HTML from each page
    const capturedPages = Array.from(pageContainers).map((pageContainer, index) => {
      // Get the page-canvas element which contains the actual rendered content
      const pageCanvas = pageContainer.querySelector('.page-canvas');

      if (!pageCanvas) {
        console.warn(`[captureRenderedHTML] No page-canvas found in page ${index + 1}`);
        return null;
      }

      // Clone the page-canvas to avoid modifying the original
      const clonedCanvas = pageCanvas.cloneNode(true);

      // CRITICAL: Inline every <img> as a data URI, for the same reason the canvases
      // above are converted.
      //
      // The captured HTML is rendered by Puppeteer via page.setContent(), which gives the
      // document NO base URL — so a relative src ("/s1/api/tenant/logo", "/uploads/...")
      // resolves against about:blank and simply never loads, and a blob: URL from this
      // origin is meaningless in another browser entirely. The server's resolveImagesInHTML()
      // only rewrites the bundled "/s3/images/" and "/images/" assets, so a tenant's own
      // uploaded logo was always dropped: present in the builder, absent in the PDF.
      //
      // Here the image is already decoded in the DOM and the session that is allowed to
      // read it is this one, so it costs a canvas draw and no network at all.
      //
      // MUST run before the canvas->img conversion below: that step ADDS <img> elements to
      // the clone only, so afterwards the two trees no longer line up index for index.
      const originalImages = pageCanvas.querySelectorAll('img');
      const clonedImages = clonedCanvas.querySelectorAll('img');

      originalImages.forEach((originalImg, imgIndex) => {
        const clonedImg = clonedImages[imgIndex];
        if (!clonedImg) return;

        const src = originalImg.getAttribute('src') || '';
        if (!src || src.startsWith('data:')) return;

        try {
          if (!originalImg.complete || !originalImg.naturalWidth) {
            throw new Error('image not decoded');
          }
          const c = document.createElement('canvas');
          c.width = originalImg.naturalWidth;
          c.height = originalImg.naturalHeight;
          c.getContext('2d').drawImage(originalImg, 0, 0);
          // Throws for a cross-origin image served without CORS headers — the canvas is
          // tainted and cannot be read. That is why the fallback below exists.
          clonedImg.setAttribute('src', c.toDataURL('image/png'));
        } catch (error) {
          // Best effort: an absolute URL at least gives Puppeteer something it can try to
          // fetch, where a relative one had no chance.
          try {
            clonedImg.setAttribute('src', new URL(src, window.location.origin).href);
          } catch (e) { /* leave the original src alone */ }
          console.warn(`[captureRenderedHTML] Could not inline image ${imgIndex} on page ${index + 1}:`, error.message);
        }
      });

      // CRITICAL: Convert all canvas elements (charts) to images
      // Canvas elements don't transfer in HTML - we need to convert them to <img> tags
      const originalCanvases = pageCanvas.querySelectorAll('canvas');
      const clonedCanvases = clonedCanvas.querySelectorAll('canvas');

      originalCanvases.forEach((originalCanvas, canvasIndex) => {
        if (clonedCanvases[canvasIndex]) {
          try {
            // Convert canvas to data URL (base64 image)
            const dataURL = originalCanvas.toDataURL('image/png');

            // Create an img element to replace the canvas
            const img = document.createElement('img');
            img.src = dataURL;
            img.style.width = originalCanvas.style.width || `${originalCanvas.width}px`;
            img.style.height = originalCanvas.style.height || `${originalCanvas.height}px`;
            img.style.maxWidth = '100%';
            img.style.display = originalCanvas.style.display || 'block';

            // Copy any classes or attributes
            if (originalCanvas.className) {
              img.className = originalCanvas.className;
            }

            // Replace the canvas with the image in the cloned tree
            clonedCanvases[canvasIndex].parentNode.replaceChild(img, clonedCanvases[canvasIndex]);

            console.log(`[captureRenderedHTML] Converted canvas ${canvasIndex} to image in page ${index + 1}`);
          } catch (error) {
            console.error(`[captureRenderedHTML] Failed to convert canvas ${canvasIndex}:`, error);
          }
        }
      });

      // Remove grid overlay if present (we don't want it in the PDF)
      const gridOverlay = clonedCanvas.querySelector('.grid-overlay');
      if (gridOverlay) {
        gridOverlay.remove();
      }

      // Remove block controls (resize handles, remove buttons, etc.)
      const resizeHandles = clonedCanvas.querySelectorAll('.resize-handle');
      resizeHandles.forEach(handle => handle.remove());

      const removeButtons = clonedCanvas.querySelectorAll('.block-remove-btn');
      removeButtons.forEach(btn => btn.remove());

      const editButtons = clonedCanvas.querySelectorAll('.block-edit-btn');
      editButtons.forEach(btn => btn.remove());

      // Interactive table chrome. These used to be hidden by the @media print block in
      // DataTableWidget.css, which no longer applies now that the export renders in
      // screen media (see /export-puppeteer in the Report Generation Service). Removing
      // them here keeps the exported output the same as before while making the rest of
      // the page match the builder — a page-size control in a PDF is not a control.
      const tableControls = clonedCanvas.querySelectorAll('.table-controls, .pagination-container');
      tableControls.forEach(el => el.remove());

      // CRITICAL: Remove ALL selection-related elements and styles
      const selectedIndicators = clonedCanvas.querySelectorAll('.selected');
      selectedIndicators.forEach(indicator => {
        indicator.classList.remove('selected');
      });

      // Remove any blocks with selection styling
      const allBlocks = clonedCanvas.querySelectorAll('.block');
      allBlocks.forEach(block => {
        // Remove selected class
        block.classList.remove('selected');

        // Remove any inline styles that might indicate selection
        if (block.style.outline) block.style.outline = '';
        if (block.style.border && block.style.border.includes('blue')) block.style.border = '';
        if (block.style.boxShadow && block.style.boxShadow.includes('blue')) block.style.boxShadow = '';
      });

      // EXPORT FIX: Strip all builder-only visual styles from .page-background
      // These styles (border-radius, box-shadow, border) are cosmetic wrappers that
      // make the builder look nice but cause "double container" artifacts in the PDF export.
      const pageBackground = clonedCanvas.querySelector('.page-background');
      if (pageBackground) {
        pageBackground.style.borderRadius = '0';
        pageBackground.style.boxShadow = 'none';
        pageBackground.style.border = 'none';
        // Ensure no extra margin/padding that would offset the content
        pageBackground.style.margin = '0';
        pageBackground.style.padding = '0';
      }

      // EXPORT FIX: Strip flex centering from .page-canvas itself to prevent side whitespace.
      // The outer .page-canvas uses display:flex + justify-content:center which creates
      // horizontal centering white space. For export we want it to be a plain block wrapper.
      clonedCanvas.style.display = 'block';
      clonedCanvas.style.justifyContent = '';
      clonedCanvas.style.alignItems = '';
      clonedCanvas.style.padding = '0';
      clonedCanvas.style.margin = '0';

      // Also remove any "add section" UI buttons that may have been captured
      const addSectionBtns = clonedCanvas.querySelectorAll('.add-section-btn-container');
      addSectionBtns.forEach(btn => btn.remove());

      // Get the outer HTML of the cleaned page
      const pageHTML = clonedCanvas.outerHTML;

      console.log(`[captureRenderedHTML] Page ${index + 1} HTML captured, length: ${pageHTML.length} chars`);

      return pageHTML;
    }).filter(html => html !== null);

    console.log(`[captureRenderedHTML] Successfully captured ${capturedPages.length} pages`);

    return capturedPages;
  }, []);

  /**
   * Extract all CSS styles from the document
   * This includes both inline styles and stylesheets
   */
  const extractCSS = useCallback(() => {
    console.log('[extractCSS] Extracting CSS from document...');

    let cssText = '';

    // Extract from all stylesheets
    const styleSheets = Array.from(document.styleSheets);

    styleSheets.forEach((sheet, index) => {
      try {
        // Only process stylesheets from the same origin
        if (sheet.cssRules) {
          const rules = Array.from(sheet.cssRules);
          rules.forEach(rule => {
            cssText += rule.cssText + '\n';
          });
        }
      } catch (e) {
        // CORS restrictions prevent reading some stylesheets
        console.warn(`[extractCSS] Could not access stylesheet ${index}:`, e.message);
      }
    });

    // Also capture inline styles from <style> tags
    const styleTags = document.querySelectorAll('style');
    styleTags.forEach(tag => {
      cssText += tag.textContent + '\n';
    });

    console.log(`[extractCSS] Total CSS extracted: ${cssText.length} chars`);

    return cssText;
  }, []);

  // Backend export function - Now uses background processing with GLOBAL context
  // MUST be defined BEFORE downloadPDF since downloadPDF uses it
  const exportToBackend = useCallback(async (format = 'pdf') => {
    console.log('🔥🔥🔥 [exportToBackend] FUNCTION CALLED - START OF EXPORT 🔥🔥🔥');
    console.log(`[exportToBackend] Format: ${format}, FilePath exists: ${!!filePath}`);
    console.log(`[exportToBackend] 🚀 Using Chunked Puppeteer for 100% visual fidelity`);
    console.log(`[exportToBackend] 📋 Using BACKGROUND EXPORT with progress tracker`);

    if (!filePath) {
      alert('File path not available. Cannot export via backend.');
      return;
    }

    try {
      const token = localStorage.getItem('token');

      // CRITICAL: Deselect any selected blocks before capturing HTML
      const previouslySelectedBlock = selectedBlock;
      if (selectedBlock) {
        console.log(`[exportToBackend] Deselecting block: ${selectedBlock}`);
        setSelectedBlock(null);
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Capture rendered HTML from the browser for 100% visual fidelity
      console.log(`[exportToBackend] Step 1: Capturing rendered HTML from ${pages.length} pages...`);
      const capturedHTML = captureRenderedHTML();

      if (!capturedHTML || capturedHTML.length === 0) {
        throw new Error('Failed to capture HTML from pages');
      }

      console.log(`[exportToBackend] Step 2: Extracting CSS styles...`);
      const css = extractCSS();

      // Restore the previously selected block
      if (previouslySelectedBlock) {
        setSelectedBlock(previouslySelectedBlock);
      }

      // Create export entry for the queue
      const exportId = `export-${Date.now()}`;
      const exportEntry = {
        id: exportId,
        name: templateName || 'Custom Report',
        pageCount: pages.length,
        format: format,
        status: 'processing',
        progress: 0,
        stage: 'Preparing export...',
        timestamp: new Date()
      };

      console.log(`[exportToBackend] Step 3: Adding export to GLOBAL queue...`);
      console.log(`[exportToBackend] Export ID: ${exportId}`);
      console.log(`[exportToBackend] Pages: ${pages.length}`);

      // Add to GLOBAL export queue (persists across navigation)
      addExport(exportEntry);

      // Close export modal - user can continue working
      setShowExportModal(false);

      // Determine export orientation from what the builder is ACTUALLY rendering, using
      // every reliable signal so it can never be wrong:
      //   A) the real DOM width of the page element (.page-background is set to
      //      gridConfig.pageWidth → 1122 landscape / 794 portrait) — the definitive signal
      //   B) the orientation state / gridConfig
      //   C) the widest block in state (landscape content reaches ~1032px right edge)
      let domPageWidth = 0;
      try {
        document.querySelectorAll('.page-background, .page-content, .page-canvas').forEach(el => {
          const w = Math.max(el.offsetWidth || 0, (el.getBoundingClientRect && el.getBoundingClientRect().width) || 0);
          domPageWidth = Math.max(domPageWidth, w);
        });
      } catch (e) { /* DOM not available */ }
      let maxRight = 0;
      for (const p of pages) {
        for (const b of (p.blocks || [])) {
          maxRight = Math.max(maxRight, (b.position?.x || 0) + (b.size?.width || 0));
        }
      }
      const stateIsLandscape = gridConfig.orientation === 'landscape' || orientation === 'landscape';
      const looksLandscape = domPageWidth > 900 || maxRight > 820 || stateIsLandscape;
      const exportOrientation = looksLandscape ? 'landscape' : 'portrait';
      console.log(`[exportToBackend] Export orientation -> ${exportOrientation} (domPageWidth=${domPageWidth.toFixed(0)}px, maxRight=${maxRight.toFixed(0)}px, state=${gridConfig.orientation}/${orientation})`);

      // Stamp the page's ACTUAL rendered size, measured off .page-background.
      //
      // Orientation is a lossy way to say "how big is the page": the server turns the word
      // back into 1123x794 or 794x1122 from a lookup, so every disagreement between the
      // builder and that table shows up as content laid out for one width being rendered
      // into another — content that is proportionally too large and clipped on the right,
      // which is exactly the reported symptom. Sending the measurement removes the guess:
      // whatever the builder drew is what the PDF page is.
      //
      // offsetWidth is unaffected by the zoom control's transform (it reports layout size,
      // not painted size), so this is the same number at any zoom level.
      //
      // .page-background specifically, not the max over .page-canvas too: .page-canvas is
      // the centring flex wrapper and stretches to the builder viewport, so on a wide
      // screen it measures far wider than the page it contains.
      let pageSizeMarker = '';
      try {
        const pageEl = document.querySelector('.page-background');
        const w = Math.round(pageEl?.offsetWidth || 0);
        const h = Math.round(pageEl?.offsetHeight || 0);
        // Sanity bounds: a plausible page, not a collapsed or detached element.
        if (w >= 300 && w <= 4000 && h >= 300 && h <= 4000) {
          pageSizeMarker = `<!--REPORT_PAGE_SIZE:${w}x${h}-->`;
          console.log(`[exportToBackend] Measured page size -> ${w}x${h}px`);
        } else {
          console.warn(`[exportToBackend] Page size not usable (${w}x${h}), falling back to orientation.`);
        }
      } catch (e) { /* fall back to the orientation marker below */ }

      // Belt-and-suspenders: stamp the chosen orientation directly into the captured HTML
      // as an explicit marker the backend reads first (non-heuristic, can't be lost).
      // Kept as the fallback for when the measurement above is unavailable.
      const stampedHTML = Array.isArray(capturedHTML)
        ? capturedHTML.map((pg, i) => i === 0 ? `${pageSizeMarker}<!--REPORT_ORIENTATION:${exportOrientation}-->${pg}` : pg)
        : capturedHTML;

      // Start background processing using GLOBAL context (non-blocking)
      // New Signature: (exportEntryId, htmlPages, css, format, token, fileUrl, templateName)
      processExportGlobal(exportId, stampedHTML, css, format, token, filePath, templateName, exportOrientation);

      console.log(`[exportToBackend] ✅ Export added to GLOBAL queue successfully!`);
      console.log(`[exportToBackend] 📋 Export will continue even if you navigate away!`);

    } catch (error) {
      console.error('[exportToBackend] Failed to start background export:', error);
      alert(`Failed to start export: ${error.message}`);
    }
  }, [filePath, pages, templateName, selectedBlock, captureRenderedHTML, extractCSS, addExport, processExportGlobal, gridConfig, orientation]);

  // Download PDF
  const downloadPDF = useCallback(async () => {
    try {
      setShowExportModal(true);
      setIsExporting(true);
      setExportError(null);
      setExportSuccess(false);

      // Wait for React to re-render with isExporting=true before capturing PDF
      await new Promise(resolve => setTimeout(resolve, 100));

      // UNIFIED EXPORT: Always use Puppeteer-based background export for all reports
      // This provides consistent progress tracking and reliability for all page counts
      console.log(`[downloadPDF] Exporting ${pages.length} pages using Puppeteer background export with progress tracking...`);

      // Use server-side Puppeteer export with progress tracking
      await exportToBackend('pdf');

      setExportSuccess(true);
      // Close modal after showing success message
      setTimeout(() => {
        setShowExportModal(false);
        setExportSuccess(false);
      }, 1500);
    } catch (error) {
      console.error('Failed to download PDF:', error);
      setExportError('Failed to generate PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [exportToBackend, pages.length, gridConfig]);

  // Handle export button click - replaced by direct downloadPDF call

  // Helper to prepare template data
  const prepareTemplatePayload = () => {
    // ===== ANALYZE TEMPLATE DATA REQUIREMENTS =====
    // Track which scan types this template needs to function properly
    const templateDataRequirements = new Set();
    let totalBlocks = 0;

    // Recursive helper to map blocks for saving (ensures children are also mapped)
    const mapBlockForSaving = (block) => {
      const savedBlock = {
        // Core identification
        id: block.id,
        type: block.type || 'widget', // 'widget' or 'element'

        // Layout properties (CRITICAL for proper rendering and reloading)
        position: {
          x: block.position?.x || 0,
          y: block.position?.y || 0
        },
        size: {
          width: block.size?.width || 200,
          height: block.size?.height || 150
        },

        // Type-specific properties
        widgetType: block.widgetType,  // Preserve actual widget type
        elementType: block.elementType,  // Preserve actual element type

        // Configuration (title, content, etc.)
        config: {
          ...(block.config || {}),
          widgetType: block.widgetType
        },

        // Styling (colors, borders, fonts, etc.)
        style: block.style || {},
        dataConfig: block.dataConfig || {},
        customData: block.customData || {},

        // Nested children
        children: Array.isArray(block.children) ? block.children.map(mapBlockForSaving) : [],

        // Placeholder & Clone metadata
        // CRITICAL: When saving as a template, we MUST strip clone/continuation markers
        // so these widgets are treated as source widgets upon reload, allowing
        // them to be correctly identified as page dependency targets.
        isPlaceholderWidget: block.isPlaceholderWidget,
        placeholderForWidget: block.placeholderForWidget,
        isClonedBlock: false, // Force false in templates
        isStaticClone: false, // Force false in templates
        originalBlockId: null, // Force null in templates
        pagination: null, // Reset pagination state in templates

        // Layout control flags
        manualResize: block.manualResize
      };

      // Remove undefined values to keep JSON clean
      if (!savedBlock.widgetType) delete savedBlock.widgetType;
      if (!savedBlock.elementType) delete savedBlock.elementType;

      return savedBlock;
    };

    // Map pages with explicit properties
    // CRITICAL: Filter out any continuation pages that were generated during a report scan.
    // Templates should only contain the "source" pages.
    const mappedPages = pages
      .filter(page => !page.isContinuationPage || page.isPlaceholder)
      .map(page => {
        // Create a clean copy of config and strip the temporary export override
        const cleanConfig = { ...(page.config || {}) };
        delete cleanConfig.exportIncluded;

        return {
          id: page.id,
          name: page.name || `Page ${page.id}`,
          blocks: page.blocks.map(block => {
            totalBlocks++;
            if (block.type === 'widget') {
              const requirements = WIDGET_DATA_REQUIREMENTS[block.widgetType];
              if (requirements && requirements.scanType !== 'Auto') {
                templateDataRequirements.add(requirements.scanType);
              }
            }
            return mapBlockForSaving(block);
          }),
          // Page-level configuration (Page Dependency settings, etc.)
          config: cleanConfig,
          // Explicitly map these booleans so the loader doesn't lose track of placeholder status
          isPlaceholder: page.isPlaceholder || false,
          placeholderForPage: page.placeholderForPage || null,
          isContinuationPage: page.isContinuationPage || false,
          continuationOf: page.continuationOf || null,
          continuationForPage: page.continuationForPage || null,
          continuationPageIndex: page.continuationPageIndex || 0
        };
      });

    console.log('[prepareTemplatePayload] Template requires scan types:', Array.from(templateDataRequirements));
    console.log(`[prepareTemplatePayload] Builder mode: ${builderMode}`);

    // Prepare template data with metadata
    return {
      name: templateName.trim(),
      pages: mappedPages,
      theme: theme,
      orientation: orientation,
      customWidgets: customWidgets,
      metadata: {
        mode: builderMode, // 'template' or 'report'
        requiredScanTypes: Array.from(templateDataRequirements),
        createdAt: new Date().toISOString(),
        totalBlocks: totalBlocks,
        totalPages: mappedPages.length,
        version: '1.0.0',
        // Carry the AI provenance so the saved template shows the AI tag and can be
        // "Regenerated with AI" on other scan data.
        ...(aiReportMeta?.aiGenerated ? {
          aiGenerated: true,
          goal: aiReportMeta.goal || '',
          scope: aiReportMeta.scope || '',
          orientation: aiReportMeta.orientation || orientation
        } : {}),
        // Add scan file path if in report mode
        ...(builderMode === 'report' && filePath ? { scanFilePath: filePath } : {})
      }
    };
  };

  const handleUpdateTemplate = async () => {
    if (!currentTemplateId) {
      alert('No template ID found to update.');
      return;
    }
    // Name check removed to allow "Save Changes" without prompt.
    // If name is empty, backend will preserve existing name.
    // if (!templateName.trim()) { ... }

    setSavingTemplate(true);
    try {
      const token = localStorage.getItem('token');
      const template = prepareTemplatePayload();

      // Call the onSaveTemplate callback if provided (for parent tracking)
      if (onSaveTemplate) {
        // We might just pass the updated template
        // await onSaveTemplate(template);
      }

      const response = await apifetch(`/s2/api/custom-templates/${currentTemplateId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...template,
          global_variables: globalVariables,
          widget_groups: widgetGroups,
          // template_mode, is_template, scan_file_path etc are usually fixed or updated via template object
          // But let's send them to be sure
          scan_file_path: builderMode === 'report' ? filePath : null,
          description: `Custom ${builderMode}: ${templateName.trim()}`
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to update template');
      }

      const result = await response.json();
      console.log(`[handleUpdateTemplate] Updated template ${currentTemplateId}`);
      alert('Template updated successfully!');

    } catch (error) {
      console.error('Error updating template:', error);
      alert('Failed to update template: ' + error.message);
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!currentTemplateId) return;
    if (!window.confirm('Are you sure you want to delete this template? This cannot be undone.')) return;

    setSavingTemplate(true); // Use saving flag to block UI
    try {
      const token = localStorage.getItem('token');
      const response = await apifetch(`/s2/api/custom-templates/${currentTemplateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete template');
      }

      console.log(`[handleDeleteTemplate] Deleted template ${currentTemplateId}`);
      alert('Template deleted successfully.');

      // Navigate back
      navigate('/dashboard');

    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template: ' + error.message);
      setSavingTemplate(false);
    }
  };

  // Save template function
  const saveTemplate = useCallback(async () => {
    if (!templateName.trim()) {
      alert('Please enter a template name');
      return;
    }

    setSavingTemplate(true);

    try {
      const token = localStorage.getItem('token');

      const template = prepareTemplatePayload();

      // Call the onSaveTemplate callback if provided
      if (onSaveTemplate) {
        await onSaveTemplate(template);
      }

      // Save through the Report Management Service. It persists ReportBuilder templates
      // into report_templates.configuration.visualTemplate so the builder and export
      // paths share one source of truth.
      const customTemplateResponse = await apifetch('/s2/api/custom-templates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...template,
          global_variables: globalVariables,  // Include global variables
          widget_groups: widgetGroups,  // Include widget groups for dynamic layout
          template_mode: builderMode,
          is_template: builderMode === 'template',
          scan_file_path: builderMode === 'report' ? filePath : null,
          // The scan format this layout was built against; it is only offered for scans of
          // the same scanner major version. Template mode builds on sample data.
          scan_schema_version: detectScanSchemaVersion(templateModeScanData),
          // Prefix "AI-generated" so the gallery shows the AI tag for AI-built reports.
          description: `${aiReportMeta?.aiGenerated ? 'AI-generated' : 'Custom'} ${builderMode}: ${templateName.trim()}`
        }),
      });

      if (!customTemplateResponse.ok) {
        throw new Error('Failed to save template');
      }

      const customTemplateResult = await customTemplateResponse.json();
      console.log(`[saveTemplate] Saved report template (ID: ${customTemplateResult.templateId})`);

      setShowSaveDialog(false);
      setTemplateName('');

      const successMessage = builderMode === 'template'
        ? 'Template saved successfully! It will now appear in template selection and export options.'
        : 'Report saved successfully!';
      alert(successMessage);
    } catch (error) {
      console.error('[saveTemplate] Failed to save:', error);
      alert(`Failed to save ${builderMode}: ${error.message}`);
    } finally {
      setSavingTemplate(false);
    }
  }, [templateName, pages, theme, onSaveTemplate, builderMode, filePath, aiReportMeta, globalVariables, widgetGroups]);

  const profileAIReportData = useCallback(async () => {
    setAIProfiling(true);
    setAIError('');
    try {
      const token = localStorage.getItem('token');
      const response = await apifetch('/s3/api/ai-report-builder/profile-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          scanData: effectiveScanData,
          fileUrl: filePath
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || data.details || 'Failed to profile scan data');
      }
      setAIProfile(data.profile || null);
      return data.profile || null;
    } catch (error) {
      console.error('[AI Build] Profile failed:', error);
      setAIError(error.message);
      return null;
    } finally {
      setAIProfiling(false);
    }
  }, [effectiveScanData, filePath]);

  const openAIBuildDialog = useCallback(() => {
    setAIBuildMode(builderMode === 'template' ? 'template' : 'report');
    setAIError('');
    setAIOmittedSections([]);
    // If this is a saved AI template, pre-fill the request from its stored metadata so
    // clicking Generate re-runs the SAME analysis on the CURRENTLY selected scan data
    // (a true "Regenerate with AI" — refreshes charts, scorecards, scores, and reviews).
    const meta = aiReportMeta || initialTemplate?.metadata || initialTemplate?.configuration?.visualTemplate?.metadata;
    if (meta?.aiGenerated) {
      if (meta.scope) setAIScope(meta.scope);
      if (meta.orientation) setAIOrientation(meta.orientation);
      if (meta.goal) {
        setAIGoal(meta.goal);
        setAICustomPromptMode(!isPremadePrompt(meta.goal));
      }
    } else {
      setAIGoal('');
      setAICustomPromptMode(false);
    }
    setShowAIBuildDialog(true);
    setTimeout(() => {
      profileAIReportData();
    }, 0);
  }, [builderMode, profileAIReportData, initialTemplate, aiReportMeta, isPremadePrompt]);

  // When launched from the "Generate with AI" card on the template selection page,
  // automatically open the AI Build dialog once the builder has mounted.
  const autoOpenedAIRef = useRef(false);
  useEffect(() => {
    if (autoOpenAI && !autoOpenedAIRef.current) {
      autoOpenedAIRef.current = true;
      openAIBuildDialog();
    }
  }, [autoOpenAI, openAIBuildDialog]);

  const handleApplyBlocks = useCallback((aiResponse, startFresh = false) => {
    if (!aiResponse) return;
    
    // Check if aiResponse already has hydrated pages (e.g. from history)
    const isAlreadyHydrated = !startFresh && aiResponse.pages && aiResponse.pages.some(p => 
      p.blocks && p.blocks.some(b => (b.type === 'widget' || b.type === 'element') && b.position)
    );
    
    if (isAlreadyHydrated) {
      setPages(aiResponse.pages);
      return aiResponse.pages;
    }
    
    // ═══════════════════════════════════════════════════════════════════
    // SMART LAYOUT ENGINE
    // The AI only specifies WHAT to render and in what ORDER.
    // This engine calculates exact pixel heights, auto-splits across pages,
    // and creates continuation clones — using the same math as the
    // dynamic sizing engine.
    // ═══════════════════════════════════════════════════════════════════

    // --- Helper: Get the exact data item count for a widget type ---
    const getDataItemCount = (widgetType, block = {}) => {
      if (!dataContext) return 0;
      
      const config = block.config || {};
      const dataConfig = block.dataConfig || {};
      
      let count = 0;

      if (widgetType === 'key-findings') {
        const { scanType = 'All', hideNoData = false, customThresholdNotes } = config;
        let summaries = extractKeyFindingsSummary(dataContext, customThresholdNotes);
        
        // Apply scanType filtering
        if (scanType && scanType !== 'All') {
          summaries = summaries.filter(summary => matchesScanType(summary.category, scanType));
        }
        
        // Apply hideNoData filtering
        if (hideNoData) {
          summaries = summaries.filter(summary => summary.statusColor !== 'gray');
        }
        count = summaries.length;
      }
      else if (widgetType === 'open-ports' || widgetType === 'network-sensitive-open-ports-table') {
        const data = extractOpenPortsData(dataContext);
        count = data?.all?.length || 0;
      }
      else if (widgetType === 'findings-grid') {
        const exportDataSource = dataContext.exportData || dataContext;
        if (exportDataSource && typeof exportDataSource === 'object') {
          Object.values(exportDataSource).forEach(categoryData => {
            if (Array.isArray(categoryData) && categoryData.length >= 1) {
              const rectangles = categoryData[0];
              if (Array.isArray(rectangles)) count += rectangles.length;
            }
          });
        }
      }
      else if (widgetType === 'findings-status') {
        const findings = Array.isArray(dataContext)
          ? dataContext
          : (dataContext?.findings || dataContext?.scanData?.findings || []);
        count = findings.length;
      }
      // For TableDataMapper-based widgets (AD tables, network tables, etc.)
      else if (TableDataMapper[widgetType]) {
        try {
          const entry = TableDataMapper[widgetType];
          let extractedData = entry.extractor(dataContext, config);
          
          if (Array.isArray(extractedData)) {
            // Apply common config filters
            if (config.ageThreshold && typeof config.ageThreshold === 'number') {
              extractedData = extractedData.filter(row => {
                const age = parseInt(row['Age (Months)'] || row['age'] || '0', 10);
                return !isNaN(age) && age > config.ageThreshold;
              });
            }
            if (config.hideMicrosoft) {
              extractedData = extractedData.filter(row => {
                const os = (row['Operating System'] || row['os'] || '').toLowerCase();
                return !os.includes('microsoft') && !os.includes('windows');
              });
            }
            if (widgetType === 'ad-admins-table') {
               if (config.showOnlyEnabled) extractedData = extractedData.filter(r => (r['Status'] || '').toLowerCase() === 'enabled');
               if (config.showOnlyDisabled) extractedData = extractedData.filter(r => (r['Status'] || '').toLowerCase() === 'disabled');
               if (config.showOnlyUsers) extractedData = extractedData.filter(r => (r['Type'] || '').toLowerCase() === 'user');
               if (config.showOnlyGroups) extractedData = extractedData.filter(r => (r['Type'] || '').toLowerCase() === 'group');
            }
            count = extractedData.length;
          }
        } catch (e) {
          console.warn(`[Layout Engine] Could not extract data for ${widgetType}:`, e);
          count = 0;
        }
      }
      
      // External tables
      if (widgetType.startsWith('external-')) {
        try {
          const getExternalData = (ctx) => {
            if (ctx.external && (ctx.external.domain || ctx.external.categories)) return ctx.external;
            if (ctx.externalData && (ctx.externalData.domain || ctx.externalData.categories)) return ctx.externalData;
            if (ctx.domain && ctx.categories && Array.isArray(ctx.categories)) return ctx;
            if (ctx.data?.external) return ctx.data.external;
            if (ctx.scanData?.external) return ctx.scanData.external;
            return null;
          };
          const externalData = getExternalData(dataContext);
          if (!externalData) return 0;
          
          if (widgetType === 'external-all-findings-table') {
            let count = 0;
            if (externalData.categories) {
              externalData.categories.forEach(c => { count += (c.findings?.length || 0); });
            }
            return count;
          }
          if (widgetType === 'external-warnings-table') {
            let count = 0;
            if (externalData.categories) {
              externalData.categories.forEach(c => {
                if (c.findings) count += c.findings.filter(f => f.severity === 'warning' || f.result === 'warning').length;
              });
            }
            return count;
          }
          // Category-specific external findings
          const typeMap = {
            'external-dns-findings': 'DNS', 'external-email-findings': 'Email',
            'external-ssl-findings': 'SSL', 'external-headers-findings': 'Headers',
            'external-infrastructure-findings': 'Infrastructure', 'external-breach-findings': 'Breach'
          };
          if (typeMap[widgetType] && externalData.categories) {
            const cat = externalData.categories.find(c => c.name?.includes(typeMap[widgetType]));
            return cat?.findings?.length || 0;
          }
        } catch (e) {
          return 0;
        }
      }
      return count;
    };

    // --- Helper: Compute exact pixel height for a widget with N data items ---
    const computeWidgetHeight = (widgetType, itemCount, availableHeightPx, block = {}) => {
      const config = block.config || {};
      
      if (widgetType === 'overall-risk-score') {
        return { heightPx: 300, itemsPerPage: 1, needsContinuation: false, remainingItems: 0 };
      } else if (widgetType === 'external-risk-score' || widgetType === 'external-severity-pie-chart') {
        return { heightPx: 350, itemsPerPage: 1, needsContinuation: false, remainingItems: 0 };
      } else if (widgetType === 'company-at-a-glance') {
        return { heightPx: 650, itemsPerPage: 1, needsContinuation: false, remainingItems: 0 };
      } else if (widgetType === 'findings-status') {
        const columns = config.gridColumns || (itemCount > 5 ? 4 : (itemCount <= 2 ? 2 : 3));
        const rows = Math.ceil(itemCount / columns) || 1;
        const height = 90 + rows * 110;
        return { heightPx: height, itemsPerPage: 1, needsContinuation: false, remainingItems: 0 };
      } else if (widgetType === 'category-score-bar' || widgetType === 'financial-impact' || widgetType === 'category-description') {
        return { heightPx: 250, itemsPerPage: 1, needsContinuation: false, remainingItems: 0 };
      } else if (widgetType === 'nested-rectangle-findings') {
        return { heightPx: 900, itemsPerPage: 1, needsContinuation: false, remainingItems: 0 };
      } else if (['pie', 'donut', 'bar', 'line', 'radar', 'polar', 'gauge', 'bubble', 'scatter'].includes(widgetType)) {
        return { heightPx: 350, itemsPerPage: 1, needsContinuation: false, remainingItems: 0 };
      } else if (['statcard', 'numeric'].includes(widgetType)) {
        return { heightPx: 200, itemsPerPage: 1, needsContinuation: false, remainingItems: 0 };
      }

      const itemsForRows = getWidgetItems(widgetType, block);
      const rowHeights = estimateWidgetRowHeights(widgetType, block, itemsForRows);
      const overhead = getWidgetOverhead(widgetType);

      // Sum of all items row heights
      let totalRowHeight = 0;
      for (let i = 0; i < itemCount && i < rowHeights.length; i++) {
        totalRowHeight += rowHeights[i];
      }
      // If there are more items than row heights estimated, pad with average row height
      if (itemCount > rowHeights.length) {
        const avgHeight = rowHeights.length > 0 ? (rowHeights.reduce((a, b) => a + b, 0) / rowHeights.length) : (widgetType === 'key-findings' ? 50 : widgetType === 'findings-grid' ? 120 : 39);
        totalRowHeight += (itemCount - rowHeights.length) * avgHeight;
      }

      const totalHeightNeeded = overhead + totalRowHeight;
      const maxAvailable = availableHeightPx || gridConfig.pageHeight;
      
      if (totalHeightNeeded <= maxAvailable) {
        // Fits entirely — size to exact content height
        let itemsPerPage = itemCount;
        if (itemsPerPage === 0) {
          itemsPerPage = calculateItemsThatFit(rowHeights, Math.max(0, maxAvailable - overhead));
        }
        return { 
          heightPx: totalHeightNeeded, 
          itemsPerPage: itemsPerPage, 
          needsContinuation: false, 
          remainingItems: 0 
        };
      }
      
      // Doesn't fit — calculate how many items fit on this page
      const usableHeight = Math.max(0, maxAvailable - overhead);
      const itemsThatFit = calculateItemsThatFit(rowHeights, usableHeight);
      
      // Calculate height for fitting items
      let fittingRowHeight = 0;
      for (let i = 0; i < itemsThatFit && i < rowHeights.length; i++) {
        fittingRowHeight += rowHeights[i];
      }
      if (itemsThatFit > rowHeights.length) {
        const avgHeight = rowHeights.length > 0 ? (rowHeights.reduce((a, b) => a + b, 0) / rowHeights.length) : (widgetType === 'key-findings' ? 50 : widgetType === 'findings-grid' ? 120 : 39);
        fittingRowHeight += (itemsThatFit - rowHeights.length) * avgHeight;
      }
      const heightForFitting = overhead + fittingRowHeight;
      
      return {
        heightPx: heightForFitting,
        itemsPerPage: itemsThatFit,
        needsContinuation: itemCount > itemsThatFit,
        remainingItems: Math.max(0, itemCount - itemsThatFit)
      };
    };

    // --- Helper: Normalize and validate widget types from AI ---
    const normalizeWidgetType = (rawType) => {
      if (!rawType || typeof rawType !== 'string') return null;
      
      const cleanType = rawType.trim().toLowerCase();
      
      // 1. Check direct match first
      const validTypes = STATIC_WIDGET_TYPES.map(w => w.type);
      if (validTypes.includes(cleanType)) {
        return cleanType;
      }
      
      // 2. Direct mapping for common AI near-misses
      const commonMappings = {
        'key-findings-widget': 'key-findings',
        'key-finding-widget': 'key-findings',
        'findings-widget': 'key-findings',
        'findings': 'key-findings',
        'key-findings-summary': 'key-findings',
        'key-finding-summary': 'key-findings',
        'key-findings-table': 'key-findings',
        'key-finding': 'key-findings',
        'key_findings': 'key-findings',
        'keyfindings': 'key-findings',
        
        'open-ports-widget': 'open-ports',
        'open-ports-table': 'open-ports',
        'open-port-widget': 'open-ports',
        'open-port': 'open-ports',
        'open-ports-summary-widget': 'open-ports',
        'open-ports-summary-table': 'open-ports',
        'sensitive-open-ports': 'open-ports',
        'open_ports': 'open-ports',
        'openports': 'open-ports',
        
        'overall-risk': 'overall-risk-score',
        'risk-score': 'overall-risk-score',
        'overall-risk-score-widget': 'overall-risk-score',
        
        'findings-grid-widget': 'findings-grid',
        
        'ad-computers': 'ad-all-computers-table',
        'ad-all-computers': 'ad-all-computers-table',
        'ad-computers-table': 'ad-all-computers-table',
        
        'ad-groups': 'ad-groups-risk-table',
        'ad-groups-risk': 'ad-groups-risk-table',
        'ad-groups-table': 'ad-groups-risk-table',
        
        'ad-ous': 'ad-ous-risk-table',
        'ad-ous-risk': 'ad-ous-risk-table',
        'ad-ous-table': 'ad-ous-risk-table',
        
        'ad-fsmo': 'ad-fsmo-roles-table',
        'ad-fsmo-roles': 'ad-fsmo-roles-table',
        'ad-fsmo-table': 'ad-fsmo-roles-table',
        
        'ad-admins': 'ad-admins-table',
        'ad-privileged-admins': 'ad-admins-table',
        'ad-admins-table': 'ad-admins-table',
        
        'network-backup': 'network-backup-status-table',
        'backup-status': 'network-backup-status-table',
        'backup-status-table': 'network-backup-status-table',
        
        'network-antivirus': 'network-antivirus-status-table',
        'antivirus-status': 'network-antivirus-status-table',
        'antivirus-status-table': 'network-antivirus-status-table',
        
        'network-os': 'network-os-versions-table',
        'os-versions': 'network-os-versions-table',
        'os-versions-table': 'network-os-versions-table',
        
        'network-services': 'network-services-risk-breakdown-table',
        'services-risk': 'network-services-risk-breakdown-table',
        'services-risk-table': 'network-services-risk-breakdown-table'
      };
      
      if (commonMappings[cleanType]) {
        console.log(`[Layout Engine] Mapped invalid widget type "${rawType}" -> "${commonMappings[cleanType]}"`);
        return commonMappings[cleanType];
      }
      
      // 3. Fuzzy search based on cleaning special chars
      const simplify = (str) => str.replace(/[^a-z0-9]/g, '');
      const simplifiedClean = simplify(cleanType);
      
      for (const valid of validTypes) {
        if (simplify(valid) === simplifiedClean) {
          console.log(`[Layout Engine] Fuzzy mapped invalid widget type "${rawType}" -> "${valid}"`);
          return valid;
        }
      }
      
      // 4. Try matching against label or description
      for (const widget of STATIC_WIDGET_TYPES) {
        const labelClean = simplify(widget.label.toLowerCase());
        const descClean = simplify(widget.description.toLowerCase());
        if (simplifiedClean.includes(labelClean) || labelClean.includes(simplifiedClean) ||
            simplifiedClean.includes(descClean) || descClean.includes(simplifiedClean)) {
          console.log(`[Layout Engine] Label/Desc mapped invalid widget type "${rawType}" -> "${widget.type}"`);
          return widget.type;
        }
      }
      
      console.warn(`[Layout Engine] Failed to normalize widget type: "${rawType}"`);
      return null;
    };

    // --- Parse AI response into flat block list ---
    let aiPages = [];
    if (aiResponse.pages && Array.isArray(aiResponse.pages) && aiResponse.pages.length > 0) {
      aiPages = aiResponse.pages;
    } else if (aiResponse.blocks && Array.isArray(aiResponse.blocks) && aiResponse.blocks.length > 0) {
      aiPages = [aiResponse];
    } else if (Array.isArray(aiResponse) && aiResponse.length > 0) {
      aiPages = [{ blocks: aiResponse }];
    }
    
    if (aiPages.length === 0) return;

    // ═══════════════════════════════════════════════════════════════════
    // BACKEND-NORMALIZED PATH
    // If the template came from the backend normalizeTemplate(), blocks
    // already have grid positions with overlap prevention applied.
    // Trust those positions and only add data-aware pagination + continuation.
    // ═══════════════════════════════════════════════════════════════════
    const hasBackendGrid = aiPages.some(p =>
      (p.blocks || []).some(b => b.gridX !== undefined && b.gridY !== undefined)
    );

    if (hasBackendGrid && startFresh) {
      const backendPageHeight = gridConfig.pageHeight;
      const finalPages = [];

      for (const aiPage of aiPages) {
        const pageBlocks = [];
        const continuationQueue = [];

        for (const block of aiPage.blocks || []) {
          let widgetType = block.widgetType;
          if (block.type === 'widget' && widgetType) {
            widgetType = normalizeWidgetType(widgetType) || widgetType;
          }

          const newBlock = {
            id: block.id || `ai-block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            type: block.type || 'widget',
            ...(block.type === 'element' ? { elementType: block.elementType || 'text' } : {}),
            ...(widgetType ? { widgetType } : {}),
            position: { ...(block.position || { x: 0, y: 0 }) },
            size: { ...(block.size || { width: 300, height: 200 }) },
            gridX: block.gridX,
            gridY: block.gridY,
            gridW: block.gridW,
            gridH: block.gridH,
            config: {
              ...(block.config || {}),
              ...(widgetType ? { widgetType } : {}),
              title: block.config?.title || (widgetType ? widgetType.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : ''),
              showTitle: block.config?.showTitle !== false
            },
            style: {
              backgroundColor: 'transparent',
              textColor: '#2C3E50',
              borderColor: 'transparent',
              ...(block.style || {})
            },
            dataConfig: { ...(block.dataConfig || {}) },
            customData: block.customData || {},
            children: block.children || [],
            isClonedBlock: block.isClonedBlock || false,
            isStaticClone: block.isStaticClone || false,
            originalBlockId: block.originalBlockId || null,
            pagination: null
          };

          // For data widgets, compute pagination and detect continuation needs
          if (block.type === 'widget' && widgetType && PAGINATED_WIDGETS.includes(widgetType)) {
            const totalDataItems = getDataItemCount(widgetType, block);

            if (totalDataItems > 0) {
              const blockHeight = block.size?.height || 400;
              const sizing = computeWidgetHeight(widgetType, totalDataItems, blockHeight, block);
              if (sizing && sizing.heightPx) {
                newBlock.size.height = sizing.heightPx;
                newBlock.gridH = Math.ceil(sizing.heightPx / gridConfig.cellHeight);
              }
              const userFirstPage = block.dataConfig?.itemsPerPageFirstPage;
              const isAutoFirst = userFirstPage === 'auto' || userFirstPage === undefined;
              const itemsPerPage = isAutoFirst ? sizing.itemsPerPage : userFirstPage;

              const itemsForRows = getWidgetItems(widgetType, block);
              const rowHeights = estimateWidgetRowHeights(widgetType, block, itemsForRows);
              const overhead = getWidgetOverhead(widgetType);

              const userCont = block.dataConfig?.itemsPerPageContinuation;
              const isAutoCont = userCont === 'auto' || userCont === undefined;
              const continuationItemsPerPage = isAutoCont ?
                calculateSafeItemsPerPage(rowHeights, itemsPerPage || 0, Math.max(0, backendPageHeight - overhead)) : userCont;

              const remainingAfterFirst = Math.max(0, totalDataItems - itemsPerPage);
              const contPages = remainingAfterFirst > 0 ? Math.ceil(remainingAfterFirst / continuationItemsPerPage) : 0;

              newBlock.dataConfig = {
                ...newBlock.dataConfig,
                itemsPerPage,
                itemsPerPageFirstPage: itemsPerPage,
                itemsPerPageContinuation: continuationItemsPerPage
              };

              newBlock.pagination = {
                spansMultiplePages: contPages > 0,
                itemsPerPage,
                itemsPerPageFirstPage: itemsPerPage,
                itemsPerPageContinuation: continuationItemsPerPage,
                totalItems: totalDataItems,
                totalPages: 1 + contPages,
                currentPage: 0
              };

              if (contPages > 0) {
                continuationQueue.push({
                  primaryBlock: newBlock,
                  totalDataItems,
                  itemsPerPage,
                  continuationItemsPerPage,
                  widgetType,
                  rowHeight: widgetType === 'key-findings' ? 50 : widgetType === 'findings-grid' ? 120 : 39,
                  overhead,
                  rowHeights
                });
              }
            }
          }

          pageBlocks.push(newBlock);
        }

        finalPages.push({
          id: aiPage.id || `page-${finalPages.length + 1}`,
          name: aiPage.name || `Page ${finalPages.length + 1}`,
          blocks: pageBlocks,
          config: aiPage.config || {},
          isPlaceholder: false,
          isContinuationPage: false
        });

        // Create continuation pages for widgets with more data than fits
        for (const item of continuationQueue) {
          let itemsRemaining = item.totalDataItems - item.itemsPerPage;
          let contNum = 1;

          while (itemsRemaining > 0) {
            const itemsOnThisPage = Math.min(itemsRemaining, item.continuationItemsPerPage);
            const startIdx = item.itemsPerPage + (contNum - 1) * item.continuationItemsPerPage;
            const endIdx = startIdx + itemsOnThisPage;
            const pageRowHeights = (item.rowHeights || []).slice(startIdx, endIdx);
            
            let fittingRowHeight = 0;
            for (let i = 0; i < pageRowHeights.length; i++) {
              fittingRowHeight += pageRowHeights[i];
            }
            if (itemsOnThisPage > pageRowHeights.length) {
              const avgHeight = pageRowHeights.length > 0 ? (pageRowHeights.reduce((a, b) => a + b, 0) / pageRowHeights.length) : item.rowHeight;
              fittingRowHeight += (itemsOnThisPage - pageRowHeights.length) * avgHeight;
            }
            const contHeight = item.overhead + fittingRowHeight;
            const contPageId = `page-cont-${item.primaryBlock.id}-${contNum}-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;

            finalPages.push({
              id: contPageId,
              name: `Page ${finalPages.length + 1}`,
              blocks: [{
                ...item.primaryBlock,
                id: `${item.primaryBlock.id}-continuation-${contNum}`,
                isClonedBlock: true,
                originalBlockId: item.primaryBlock.id,
                position: { x: item.primaryBlock.position.x, y: 0 },
                size: { width: item.primaryBlock.size.width, height: contHeight },
                dataConfig: {
                  ...item.primaryBlock.dataConfig,
                  itemsPerPage: itemsOnThisPage,
                  itemsPerPageContinuation: item.continuationItemsPerPage
                },
                pagination: {
                  spansMultiplePages: true,
                  itemsPerPage: itemsOnThisPage,
                  itemsPerPageFirstPage: item.itemsPerPage,
                  itemsPerPageContinuation: item.continuationItemsPerPage,
                  totalItems: item.totalDataItems,
                  totalPages: item.primaryBlock.pagination.totalPages,
                  currentPage: contNum
                }
              }],
              isPlaceholder: false,
              isContinuationPage: true,
              continuationOf: item.primaryBlock.id,
              continuationForPage: aiPage.id,
              continuationPageIndex: contNum
            });

            itemsRemaining -= itemsOnThisPage;
            contNum++;
          }
        }
      }

      // Renumber pages
      finalPages.forEach((p, idx) => p.name = `Page ${idx + 1}`);

      setPages(finalPages);

      // Update history
      setHistory(prev => {
        const newHistory = prev.slice(0, historyIndex + 1);
        newHistory.push({ pages: finalPages, orientation });
        if (newHistory.length > 50) newHistory.shift();
        return newHistory;
      });
      setHistoryIndex(prev => prev + 1);

      return finalPages;
    }

    // --- Start with current pages (clone to avoid mutation) or fresh slate ---
    const newPages = startFresh ? [{
      id: 'page-1',
      name: 'Page 1',
      blocks: [],
      isPlaceholder: false
    }] : pages.map(page => ({
      ...page,
      blocks: [...page.blocks]
    }));
    
    let currentTargetPageId = startFresh ? newPages[0].id : currentPageId;
    const pageHeight = gridConfig.pageHeight;
    const GAP = gridConfig.cellHeight; // Gap between blocks (~25px)

    aiPages.forEach((aiPage, pageIdx) => {
      const blocksToApply = aiPage.blocks || [];
      if (blocksToApply.length === 0) return;

      // Find or create target page
      let targetPage = newPages.find(p => p.id === currentTargetPageId);
      if (!targetPage) targetPage = newPages[0];

      // For subsequent AI pages, start on a fresh page
      if (pageIdx > 0) {
        const newPageId = `page-ai-sep-${Date.now()}-${pageIdx}`;
        const freshPage = {
          id: newPageId,
          name: `Page ${newPages.length + 1}`,
          blocks: [],
          isPlaceholder: false
        };
        newPages.push(freshPage);
        targetPage = freshPage;
        currentTargetPageId = newPageId;
      }

      // Calculate starting Y position (below existing content)
      let currentY = targetPage.blocks.reduce((max, block) => {
        const blockY = block.position?.y || 0;
        const blockH = block.size?.height || 0;
        return Math.max(max, blockY + blockH);
      }, 0);
      if (currentY > 0) currentY += GAP; // Add gap below existing content

      blocksToApply.forEach(b => {
        // Handle true page break blocks from AI
        if (b.type === 'page-break') {
          const newPageId = `page-ai-pb-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
          const freshPage = {
            id: newPageId,
            name: `Page ${newPages.length + 1}`,
            blocks: [],
            isPlaceholder: false
          };
          newPages.push(freshPage);
          targetPage = freshPage;
          currentTargetPageId = newPageId;
          currentY = getHeaderOffsetForPage(newPages.length, newPages);
          return;
        }

        const isElement = b.type === 'element' || ['title', 'text', 'heading', 'paragraph', 'image', 'spacer', 
                          'horizontal-line', 'divider', 'shape', 'icon',
                          'header', 'footer'].includes(b.type);
        
        let elementType = b.elementType;
        if (!elementType) {
          if (b.type === 'heading' || b.type === 'title') elementType = 'title';
          else if (b.type === 'paragraph' || b.type === 'text') elementType = 'text';
          else if (b.type === 'spacer') elementType = 'shape';
          else if (b.type === 'horizontal-line') elementType = 'divider';
          else if (b.type === 'header') elementType = 'header';
          else if (b.type === 'footer') elementType = 'footer';
          else if (isElement) elementType = b.type;
        }

        const blockW = b.size?.width || (b.w || (gridConfig.orientation === 'landscape' ? 25 : 18)) * gridConfig.cellWidth;
        
        // ═══════════════════════════════════════════════════════
        // ELEMENT HANDLING: Simple height from h value or defaults
        // ═══════════════════════════════════════════════════════
        if (isElement) {
          const blockH = b.size?.height || (b.h || (elementType === 'title' ? 3 : elementType === 'shape' ? 1 : elementType === 'divider' ? 1 : 4)) * gridConfig.cellHeight;
          
          let targetPageNum = newPages.indexOf(targetPage) + 1;
          let footerOffset = getFooterOffsetForPage(targetPageNum, newPages);
          let headerOffset = getHeaderOffsetForPage(targetPageNum, newPages);

          // If element doesn't fit, move to new page
          if (currentY + blockH > pageHeight - footerOffset && currentY > headerOffset) {
            const newPageId = `page-ai-overflow-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
            const freshPage = {
              id: newPageId,
              name: `Page ${newPages.length + 1}`,
              blocks: [],
              isPlaceholder: false
            };
            newPages.push(freshPage);
            targetPage = freshPage;
            currentTargetPageId = newPageId;
            currentY = getHeaderOffsetForPage(newPages.length, newPages);
          }

          const newBlock = {
            id: b.id || `ai-elem-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            type: 'element',
            elementType: elementType,
            position: { x: b.position?.x || 0, y: currentY },
            size: { width: blockW, height: blockH },
            config: {
              content: b.content || b.config?.content || '',
              title: b.title || b.config?.title || b.content || b.config?.content || '',
              showTitle: b.showTitle !== false,
              align: b.config?.align || 'left',
              ...(b.config || {})
            },
            style: {
              backgroundColor: 'transparent',
              textColor: '#2C3E50',
              borderColor: 'transparent',
              ...(b.style || {})
            },
            dataConfig: {
              ...(b.dataConfig || {})
            }
          };

          targetPage.blocks.push(newBlock);
          currentY += blockH + GAP;
          return; // Done with this element
        }

        // ═══════════════════════════════════════════════════════
        // WIDGET HANDLING: Smart height calculation from data
        // ═══════════════════════════════════════════════════════
        const widgetType = normalizeWidgetType(b.widgetType || b.type);
        if (!widgetType) {
          console.warn(`[Layout Engine] Skipping unknown/unmappable widget type: "${b.type}"`);
          return; // Skip unknown widgets
        }
        
        // Calculate total items (now correctly matching applied filters)
        const totalDataItems = getDataItemCount(widgetType, b);
        
        let targetPageNum = newPages.indexOf(targetPage) + 1;
        let headerOffset = getHeaderOffsetForPage(targetPageNum, newPages);
        let footerOffset = getFooterOffsetForPage(targetPageNum, newPages);
        if (currentY < headerOffset) {
          currentY = headerOffset;
        }
        
        const availableOnCurrentPage = Math.max(0, pageHeight - currentY - footerOffset);

        console.log(`[Layout Engine] Widget "${widgetType}": ${totalDataItems} data items, ${Math.round(availableOnCurrentPage)}px available on current page`);

        // Calculate how much height the widget needs if it were placed on a completely fresh page
        const freshPageSizing = computeWidgetHeight(widgetType, totalDataItems, pageHeight - headerOffset - footerOffset, b);
        
        const itemsForRows = getWidgetItems(widgetType, b);
        const rowHeights = estimateWidgetRowHeights(widgetType, b, itemsForRows);
        const overhead = getWidgetOverhead(widgetType);
        
        let minUsefulHeight;
        if (freshPageSizing.itemsPerPage === 1 && !freshPageSizing.needsContinuation) {
          // Fixed height widget (e.g. chart, statcard, company-at-a-glance). Needs its full height.
          minUsefulHeight = freshPageSizing.heightPx;
        } else {
          // Paginated widget (e.g. table, grid). Needs at least overhead + 3 rows to look decent.
          let threeRowsHeight = 0;
          for (let i = 0; i < 3 && i < rowHeights.length; i++) {
            threeRowsHeight += rowHeights[i];
          }
          if (rowHeights.length < 3) {
            const defaultRowHeight = widgetType === 'key-findings' ? 50 : widgetType === 'findings-grid' ? 120 : 39;
            threeRowsHeight += (3 - rowHeights.length) * defaultRowHeight;
          }
          minUsefulHeight = overhead + threeRowsHeight;
        }

        // If not enough room for even a minimal widget, move to new page first
        if (availableOnCurrentPage < minUsefulHeight && currentY > headerOffset) {
          const newPageId = `page-ai-newpg-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
          const freshPage = {
            id: newPageId,
            name: `Page ${newPages.length + 1}`,
            blocks: [],
            isPlaceholder: false
          };
          newPages.push(freshPage);
          targetPage = freshPage;
          currentTargetPageId = newPageId;
          
          targetPageNum = newPages.indexOf(targetPage) + 1;
          headerOffset = getHeaderOffsetForPage(targetPageNum, newPages);
          footerOffset = getFooterOffsetForPage(targetPageNum, newPages);
          currentY = headerOffset;
        }

        // Recalculate after possible page change
        const effectiveAvailable = Math.max(0, pageHeight - currentY - footerOffset);
        const finalSizing = computeWidgetHeight(widgetType, totalDataItems, effectiveAvailable, b);

        // --- Place the primary widget block ---
        const blockId = b.id || `ai-widget-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const primaryBlock = {
          id: blockId,
          type: 'widget',
          widgetType: widgetType,
          position: { x: b.position?.x || 0, y: currentY },
          size: { width: blockW, height: finalSizing.heightPx },
          config: {
            content: b.content || b.config?.content || '',
            title: b.title || b.config?.title || widgetType.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            showTitle: b.showTitle !== false,
            widgetType: widgetType,
            ...(b.config || {})
          },
          style: {
            backgroundColor: 'transparent',
            textColor: '#2C3E50',
            borderColor: 'transparent',
            ...(b.style || {})
          },
          dataConfig: {
            metricPath: '',
            aggregation: 'count',
            filters: [],
            tableColumns: [],
            chartAggregationField: null,
            ...(b.dataConfig || {}),
            // Set computed itemsPerPage — this drives the rendering, but respect custom AI/user settings if provided
            itemsPerPage: b.dataConfig?.itemsPerPage !== undefined ? b.dataConfig.itemsPerPage : finalSizing.itemsPerPage,
            itemsPerPageFirstPage: b.dataConfig?.itemsPerPageFirstPage !== undefined ? b.dataConfig.itemsPerPageFirstPage : finalSizing.itemsPerPage
          }
        };

        // Add pagination info if this widget has data
        if (totalDataItems > 0 && PAGINATED_WIDGETS.includes(widgetType)) {
          const itemsPerPageFirstPage = b.dataConfig?.itemsPerPageFirstPage !== undefined ? b.dataConfig.itemsPerPageFirstPage : finalSizing.itemsPerPage;
          const continuationItemsPerPage = b.dataConfig?.itemsPerPageContinuation !== undefined ? b.dataConfig.itemsPerPageContinuation : calculateSafeItemsPerPage(rowHeights, itemsPerPageFirstPage || 0, Math.max(0, pageHeight - overhead));
          const remainingAfterFirst = Math.max(0, totalDataItems - itemsPerPageFirstPage);
          const continuationPagesNeeded = remainingAfterFirst > 0 ? Math.ceil(remainingAfterFirst / continuationItemsPerPage) : 0;
          
          primaryBlock.pagination = {
            spansMultiplePages: continuationPagesNeeded > 0,
            itemsPerPage: itemsPerPageFirstPage,
            itemsPerPageFirstPage: itemsPerPageFirstPage,
            itemsPerPageContinuation: continuationItemsPerPage,
            totalItems: totalDataItems,
            totalPages: 1 + continuationPagesNeeded,
            currentPage: 0
          };
          primaryBlock.dataConfig.itemsPerPageContinuation = continuationItemsPerPage;
        }

        targetPage.blocks.push(primaryBlock);
        currentY += finalSizing.heightPx + GAP;

        console.log(`[Layout Engine] Placed "${widgetType}" on page: height=${finalSizing.heightPx}px, items=${finalSizing.itemsPerPage}/${totalDataItems}, needsContinuation=${finalSizing.needsContinuation}`);

        // --- Create continuation pages if needed ---
        if (PAGINATED_WIDGETS.includes(widgetType) && finalSizing.needsContinuation && totalDataItems > finalSizing.itemsPerPage) {
          let itemsRemaining = totalDataItems - finalSizing.itemsPerPage;
          let continuationPageNum = 1;

          while (itemsRemaining > 0) {
            const contPageNumInReport = newPages.length + 1;
            const headerOffsetCont = getHeaderOffsetForPage(contPageNumInReport, newPages);
            const footerOffsetCont = getFooterOffsetForPage(contPageNumInReport, newPages);
            const usableContHeight = Math.max(0, pageHeight - headerOffsetCont - footerOffsetCont - overhead);
            const startIdx = totalDataItems - itemsRemaining;
            const continuationItemsPerPage = calculateSafeItemsPerPage(rowHeights, startIdx, usableContHeight);

            const itemsOnThisContPage = Math.min(itemsRemaining, continuationItemsPerPage);
            const endIdx = startIdx + itemsOnThisContPage;
            const pageRowHeights = rowHeights.slice(startIdx, endIdx);
            
            let fittingRowHeight = 0;
            for (let i = 0; i < pageRowHeights.length; i++) {
              fittingRowHeight += pageRowHeights[i];
            }
            if (itemsOnThisContPage > pageRowHeights.length) {
              const defaultRowHeight = widgetType === 'key-findings' ? 50 : widgetType === 'findings-grid' ? 120 : 39;
              const avgHeight = pageRowHeights.length > 0 ? (pageRowHeights.reduce((a, b) => a + b, 0) / pageRowHeights.length) : defaultRowHeight;
              fittingRowHeight += (itemsOnThisContPage - pageRowHeights.length) * avgHeight;
            }
            const contHeight = overhead + fittingRowHeight;
            
            const contPageId = `page-continuation-${targetPage.id}-${continuationPageNum}-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
            const contPage = {
              id: contPageId,
              name: `Page ${newPages.length + 1}`,
              blocks: [],
              isPlaceholder: false,
              isContinuationPage: true,
              continuationOf: blockId,
              continuationForPage: targetPage.id,
              continuationPageIndex: continuationPageNum
            };

            const cloneBlock = {
              id: `${blockId}-continuation-${continuationPageNum}`,
              type: 'widget',
              widgetType: widgetType,
              isClonedBlock: true,
              originalBlockId: blockId,
              position: { x: b.position?.x || 0, y: headerOffsetCont },
              size: { width: blockW, height: contHeight },
              config: { ...primaryBlock.config },
              style: { ...primaryBlock.style },
              dataConfig: {
                ...primaryBlock.dataConfig,
                itemsPerPage: itemsOnThisContPage,
                itemsPerPageContinuation: continuationItemsPerPage
              },
              pagination: {
                spansMultiplePages: true,
                itemsPerPage: itemsOnThisContPage,
                itemsPerPageFirstPage: finalSizing.itemsPerPage,
                itemsPerPageContinuation: continuationItemsPerPage,
                totalItems: totalDataItems,
                totalPages: primaryBlock.pagination.totalPages,
                currentPage: continuationPageNum
              }
            };

            contPage.blocks.push(cloneBlock);
            newPages.push(contPage);

            console.log(`[Layout Engine] Created continuation page ${continuationPageNum} for "${widgetType}": ${itemsOnThisContPage} items, height=${contHeight}px`);

            itemsRemaining -= itemsOnThisContPage;
            continuationPageNum++;
          }
          
          // After continuation pages, next widget goes on a new page
          const nextPageId = `page-ai-after-cont-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
          const nextPage = {
            id: nextPageId,
            name: `Page ${newPages.length + 1}`,
            blocks: [],
            isPlaceholder: false
          };
          newPages.push(nextPage);
          targetPage = nextPage;
          currentTargetPageId = nextPageId;
          currentY = getHeaderOffsetForPage(newPages.length, newPages);
        }
      });
    });

    // Clean up any empty pages (except the first one if the whole report is empty)
    const cleanedPages = newPages.filter((page, index) => {
      if (index === 0) return true; // Always keep the first page
      return page.blocks && page.blocks.length > 0;
    });
    
    // Update page names after cleanup
    cleanedPages.forEach((p, idx) => p.name = `Page ${idx + 1}`);

    setPages(cleanedPages);

    // Update history
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push({ pages: cleanedPages, orientation });
      if (newHistory.length > 50) newHistory.shift();
      return newHistory;
    });
    setHistoryIndex(prev => prev + 1);

    return cleanedPages;
  }, [pages, dataContext, gridConfig, currentPageId, setPages, setHistory, historyIndex, setHistoryIndex, orientation]);

  const applyGeneratedTemplate = useCallback((visualTemplate) => {
    try {
      if (!visualTemplate?.pages || !Array.isArray(visualTemplate.pages)) {
        throw new Error('AI returned an invalid template');
      }

      // Run our Smart Layout Engine to cleanly layout the pages, avoiding overlaps and overflows
      const hydratedPages = handleApplyBlocks(visualTemplate, true);

      setCurrentPageId(hydratedPages[0]?.id || 'page-1');
      if (visualTemplate.theme) setTheme(prev => ({ ...prev, ...visualTemplate.theme }));
      if (visualTemplate.orientation) setOrientation(visualTemplate.orientation);
      if (visualTemplate.name) setTemplateName(visualTemplate.name);
      // Remember this report was AI-built so the user's manual save gets the AI tag.
      const m = visualTemplate.metadata || {};
      setAiReportMeta({ aiGenerated: true, goal: m.goal || '', scope: m.scope || '', orientation: m.orientation || visualTemplate.orientation || '' });
      setBuilderMode('report');
      setShowAIBuildDialog(false);
      setSidebarVisible(false);
      setPropertiesVisible(false);
    } catch (error) {
      console.error('[AI Apply] Apply failed:', error);
      setAIError(error.message);
    }
  }, [handleApplyBlocks]);

  const generateAIReportLayout = useCallback(async (opts = {}) => {
    // Allow callers (e.g. one-click Regenerate) to pass the stored request directly,
    // since React state updates aren't applied synchronously before this runs.
    const goalToUse = opts.goal != null ? opts.goal : aiGoal;
    const scopeToUse = opts.scope != null ? opts.scope : aiScope;
    const orientationToUse = opts.orientation != null ? opts.orientation : aiOrientation;
    setAILoading(true);
    setAIError('');
    setAIOmittedSections([]);
    try {
      const token = localStorage.getItem('token');
      const currentPresetData = effectivePresets.find(p => p.id === selectedPreset) || effectivePresets[0];
      const primaryColor = selectedPreset === 'custom' ? customPrimary : currentPresetData.primary;
      const secondaryColor = selectedPreset === 'custom' ? customSecondary : currentPresetData.secondary;
      const accentColor = selectedPreset === 'custom' ? customAccent : currentPresetData.accent;

      const response = await apifetch('/s3/api/ai-report-builder/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          mode: 'report',
          goal: goalToUse,
          scope: scopeToUse,
          allowFullData: aiAllowFullData,
          scanData: effectiveScanData,
          fileUrl: filePath,
          currentPages: pages,
          orientation: orientationToUse,
          colorPalette: {
            name: currentPresetData.name,
            primary: primaryColor,
            secondary: secondaryColor,
            accent: accentColor
          }
        })
      });

      let data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || data.details || 'Failed to start AI report layout generation');
      }

      if (data.jobId) {
        setAIError('');
        const jobId = data.jobId;
        let jobComplete = false;
        
        while (!jobComplete) {
            await new Promise(resolve => setTimeout(resolve, 4000)); // Poll every 4 seconds
            
            const statusRes = await apifetch(`/s3/api/ai-report-builder/job/${jobId}`, {
                headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
            });
            const statusData = await statusRes.json().catch(() => ({}));
            
            if (statusData.status === 'complete') {
                jobComplete = true;
                data = statusData; // Inherit the result
            } else if (statusData.status === 'error') {
                throw new Error(statusData.error || 'Failed to generate AI report layout');
            } else if (!statusRes.ok) {
                throw new Error('Failed to check job status');
            }
        }
      }

      if (data.template) {
        applyGeneratedTemplate(data.template);
      }
      setAIGoal('');
      setAICustomPromptMode(false);
    } catch (error) {
      console.error('[AI Build] Generate failed:', error);
      setAIError(error.message);
    } finally {
      setAILoading(false);
    }
  }, [aiScope, aiGoal, aiAllowFullData, effectiveScanData, filePath, pages, aiOrientation, applyGeneratedTemplate, selectedPreset, customPrimary, customSecondary, customAccent, effectivePresets]);

  // "Regenerate with AI": open the AI Build dialog with the previous goal/scope/orientation
  // pre-filled from the saved metadata, so the user can review and tweak settings before
  // clicking "Generate". This replaces the old one-click auto-generate behavior.
  const regenerateWithAI = useCallback(() => {
    const meta = aiReportMeta || {};
    setAIBuildMode('report');
    setAIError('');
    setAIOmittedSections([]);
    if (meta.scope) setAIScope(meta.scope);
    if (meta.orientation) setAIOrientation(meta.orientation);
    if (meta.goal) {
      setAIGoal(meta.goal);
      setAICustomPromptMode(!isPremadePrompt(meta.goal));
    }
    setShowAIBuildDialog(true);
    // Profile scan data in background so the dialog shows data insights
    setTimeout(() => {
      profileAIReportData();
    }, 0);
  }, [aiReportMeta, profileAIReportData, isPremadePrompt]);

  const generateWrittenReport = useCallback(async () => {
    if (!filePath) {
      setWrittenReportError('No scan file is selected. Please open a scan before generating a report.');
      setShowWrittenReportModal(true);
      return;
    }
    setWrittenReportLoading(true);
    setWrittenReportError('');
    setWrittenReportHtml('');
    setShowWrittenReportModal(true);
    try {
      const token = localStorage.getItem('token');
      const response = await apifetch('/s3/api/ai-report-builder/generate-written-report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          fileUrl: filePath,
          clientName: writtenReportClientName || undefined,
          audience: writtenReportAudience
        })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate report');
      }
      setWrittenReportHtml(data.html || '');
    } catch (err) {
      console.error('[Written Report] Generate failed:', err);
      setWrittenReportError(err.message || 'Failed to generate report');
    } finally {
      setWrittenReportLoading(false);
    }
  }, [filePath, writtenReportClientName, writtenReportAudience]);

  // --- Page Dependency Evaluation ---
  // We evaluate page dependencies ahead of render so we can grey them out in canvas and toolbar,
  // instead of silently removing them (except during export).
  const evaluatedPages = useMemo(() => {
    return pages.map(page => {
      let isDependencyUnmet = false;
      if (builderMode === 'report' && page.config?.dependentBlockId && !page.isContinuationPage && !page.isPlaceholder) {
        const count = getWidgetDataCount(page.config.dependentBlockId);
        const operator = page.config?.dependencyOperator || 'has_data';
        const threshold = page.config?.dependencyThreshold ?? 0;
        let conditionMet = true;
        switch (operator) {
          case 'has_data': conditionMet = count > 0; break;
          case '==': conditionMet = count === threshold; break;
          case '!=': conditionMet = count !== threshold; break;
          case '>': conditionMet = count > threshold; break;
          case '>=': conditionMet = count >= threshold; break;
          case '<': conditionMet = count < threshold; break;
          case '<=': conditionMet = count <= threshold; break;
          default: conditionMet = count > 0; break;
        }
        isDependencyUnmet = !conditionMet;
      }

      let isEmptyPage = false;
      if (builderMode === 'report' && (!page.blocks || page.blocks.length === 0) && !page.isPlaceholder && !page.isContinuationPage) {
        isEmptyPage = true;
      }

      // Page Suppression Logic: Hide if the page ONLY contains an empty M365 Policy Config widget
      if (!isEmptyPage && builderMode === 'report' && page.blocks?.length === 1 && !page.isPlaceholder && !page.isContinuationPage) {
        const solelyContainsM365Widget = page.blocks[0]?.type === 'ms365-policy-config-table';
        if (solelyContainsM365Widget && isMS365DataEmpty(dataContext)) {
          isEmptyPage = true;
        }
      }

      let isExcluded = isDependencyUnmet || isEmptyPage;

      // Allow manual override
      if (page.config?.exportIncluded !== undefined) {
        isExcluded = !page.config.exportIncluded;
      }

      return { ...page, isDependencyUnmet, isEmptyPage, isExcluded, originalIsExcluded: isDependencyUnmet || isEmptyPage };
    });
  }, [pages, builderMode, getWidgetDataCount]);

  const isAiTemplate = !!(aiReportMeta?.aiGenerated || initialTemplate?.metadata?.aiGenerated || initialTemplate?.configuration?.visualTemplate?.metadata?.aiGenerated);

  return (
    <DndProvider backend={HTML5Backend}>
      <div className={`report-builder${isV2 ? ` rb-v2 v2-tokens${isDarkMode ? ' dark' : ''}${readOnly ? ' rb2-readonly' : ''}` : ''}`}>
        {isV2 && (
          <>
            <BuilderHeaderV2
              section={builderMode === 'template' ? 'Report templates' : 'Reports'}
              title={templateName || (builderMode === 'template' ? 'Untitled template' : 'Untitled report')}
              backLabel={location.state?.fromTemplateHub ? 'Back to report templates' : 'Back'}
              onBack={() => {
                if (location.state?.fromTemplateHub) navigate('/templates');
                else if (window.history.length > 1) navigate(-1);
                else navigate('/dashboard');
              }}
              isTemplateMode={builderMode === 'template'}
              readOnly={readOnly}
              canUndo={historyIndex > 0}
              canRedo={historyIndex < history.length - 1}
              onUndo={handleUndo}
              onRedo={handleRedo}
              onOpenSettings={() => setShowStatusColorsModal(true)}
              onAI={isAiTemplate && regenerateWithAI ? regenerateWithAI : openAIBuildDialog}
              aiLabel={isAiTemplate ? 'Regenerate' : 'Build with AI'}
              showSave
              currentTemplateId={currentTemplateId}
              saving={savingTemplate}
              onSave={handleUpdateTemplate}
              onSaveAs={() => setShowSaveDialog(true)}
              onDelete={handleDeleteTemplate}
              onExport={builderMode === 'report' ? downloadPDF : null}
            />
            {!readOnly && (
              <BuilderRailV2
                items={LIBRARY_ITEMS
                  .filter(item => item.key !== 'custom' || customWidgets.length > 0)
                  .map(item => item.key === 'custom' ? { ...item, count: customWidgets.length } : item)}
                activeKey={activeSidebarTab}
                open={sidebarVisible}
                onSelect={(key) => {
                  if (sidebarVisible && activeSidebarTab === key) {
                    setSidebarVisible(false);
                  } else {
                    setActiveSidebarTab(key);
                    setSidebarVisible(true);
                  }
                }}
              />
            )}
          </>
        )}
        {/* Loading Overlay - shown while template is being patched */}
        {isLoadingTemplate && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}>
            <div className="spinner-border text-light" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="sr-only">Loading...</span>
            </div>
            <p style={{ color: 'white', marginTop: '20px', fontSize: '18px' }}>
              Preparing Custom Report...
            </p>
            <p style={{ color: '#aaa', marginTop: '10px', fontSize: '14px' }}>
              Patching template with scan data
            </p>
          </div>
        )}

        {/* Sidebar */}
        <div className={`sidebar-container ${(sidebarVisible && !readOnly) ? 'visible' : 'hidden'}`} data-bs-theme={chromeBsTheme}>
          <Sidebar
            activeTab={activeSidebarTab}
            onTabChange={setActiveSidebarTab}
            availableMetrics={availableMetrics}
            onAddWidget={addWidget}
            onAddElement={addElement}
            onToggle={() => setSidebarVisible(false)}
            isVisible={sidebarVisible}
            metricsLoading={metricsLoading}
            metricsError={metricsError}
            dataContext={dataContext}
            currentPage={currentPage}
            filteredWidgetTypes={filteredWidgetTypes}
            mspInfo={mspInfo}
            customWidgets={customWidgets}
            onAddCustomWidget={addCustomWidget}
            onDeleteCustomWidget={deleteCustomWidget}
            readOnly={readOnly}
            dynamicCategoryMap={dynamicCategoryMap}
            widgetTypes={widgetTypes}
            scanGroups={scanGroups}
            variant={isV2 ? 'v2' : 'v1'}
          />
        </div>

        {/* Main Content Area */}
        <div className={`main-content ${(!sidebarVisible || readOnly) ? 'sidebar-hidden' : ''} ${(!propertiesVisible || readOnly) ? 'properties-hidden' : ''}`}>
          {/* Independent Top Action Bar for Undo/Redo (De-congesting Toolbar) */}
          {/* v2 carries these in BuilderHeaderV2. */}
          {!readOnly && !isV2 && (
            <div
              className="builder-top-actions"
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '8px 16px',
                background: '#ffffff',
                borderBottom: '1px solid #e2e8f0',
                gap: '16px',
                alignItems: 'center'
              }}
            >
              {/* Zoom Controls */}
              {/* <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRight: '1px solid #e2e8f0', paddingRight: '16px' }}>
                <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>Zoom</span>
                <button
                  className="toolbar-btn secondary icon-only"
                  onClick={handleZoomOut}
                  title="Zoom Out"
                  style={{ padding: '4px 8px' }}
                >
                  −
                </button>
                <button
                  className="toolbar-btn secondary"
                  onClick={handleZoomReset}
                  title="Reset Zoom to 100%"
                  style={{ padding: '4px 8px', minWidth: '55px', fontSize: '12px', textAlign: 'center' }}
                >
                  {Math.round(zoomLevel * 100)}%
                </button>
                <button
                  className="toolbar-btn secondary icon-only"
                  onClick={handleZoomIn}
                  title="Zoom In"
                  style={{ padding: '4px 8px' }}
                >
                  +
                </button>
              </div> */}

              <button
                className="toolbar-btn"
                onClick={() => setShowStatusColorsModal(true)}
                title="Report Settings (status colors, page numbers)"
              >
                <span className="btn-icon">🎨</span>
                <span className="btn-text">Report Settings</span>
              </button>

              {/* Undo / Redo */}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  className="toolbar-btn secondary"
                  onClick={handleUndo}
                  disabled={historyIndex <= 0}
                  title="Undo (Ctrl+Z)"
                  style={{ opacity: historyIndex > 0 ? 1 : 0.5, padding: '4px 12px' }}
                >
                  <span style={{ marginRight: '4px' }}>↶</span> Undo
                </button>
                <button
                  className="toolbar-btn secondary"
                  onClick={handleRedo}
                  disabled={historyIndex >= history.length - 1}
                  title="Redo (Ctrl+Shift+Z)"
                  style={{ opacity: historyIndex < history.length - 1 ? 1 : 0.5, padding: '4px 12px' }}
                >
                  <span style={{ marginRight: '4px' }}>↷</span> Redo
                </button>
              </div>
            </div>
          )}

          {/* Top Toolbar */}
          <Toolbar
            pages={evaluatedPages.filter(page => {
              if (page.isPlaceholder) {
                // CRITICAL: If continuation pages exist for this source, ALWAYS hide the placeholder
                // (even if user explicitly toggled it — continuation pages take absolute priority)
                const hasContinuationPages = evaluatedPages.some(p =>
                  p.isContinuationPage &&
                  !p.isPlaceholder &&
                  p.continuationForPage === page.placeholderForPage
                );
                if (hasContinuationPages) return false;
                // Only show if user explicitly toggled via "Edit Placeholder" button
                return visiblePlaceholderIds.has(page.id);
              }
              // ONLY FILTER OUT EXCLUDED PAGES IF EXPORTING
              if (isExporting && page.isExcluded) {
                return false;
              }
              return true;
            })}
            currentPageId={currentPageId}
            onPageChange={(pageId) => {
              setCurrentPageId(pageId);
              // Scroll to the page container
              setTimeout(() => {
                const pageContainer = document.querySelector(`[data-page-id="${pageId}"]`);
                if (pageContainer) {
                  pageContainer.scrollIntoView({ behavior: 'auto', block: 'start' });
                }
              }, 100);
            }}
            onAddPage={addPage}
            onInsertPage={insertPage}
            onMovePage={movePage}
            onReorderPage={reorderPage}
            onRemovePage={removePage}
            onTogglePageExport={togglePageExport}
            onToggleSidebar={() => setSidebarVisible(!sidebarVisible)}
            onToggleProperties={() => setPropertiesVisible(!propertiesVisible)}
            sidebarVisible={sidebarVisible}
            propertiesVisible={propertiesVisible}
            theme={theme}
            onThemeChange={setTheme}
            onExportPDF={downloadPDF}
            filePath={filePath}
            showGrid={showGrid}
            onToggleGrid={() => setShowGrid(!showGrid)}
            onSaveTemplate={() => setShowSaveDialog(true)}
            savingTemplate={savingTemplate}
            currentTemplateId={currentTemplateId}
            readOnly={readOnly}
            handleUpdateTemplate={handleUpdateTemplate}
            handleDeleteTemplate={handleDeleteTemplate}
            onAIBuild={openAIBuildDialog}
            onRegenerateWithAI={regenerateWithAI}
            isAiTemplate={isAiTemplate}
            onGenerateWrittenReport={() => setShowWrittenReportModal(true)}
            builderMode={builderMode}
            showGlobalVariablesPanel={showGlobalVariablesPanel}
            setShowGlobalVariablesPanel={setShowGlobalVariablesPanel}
            setShowStatusColorsModal={setShowStatusColorsModal}
            orientation={orientation}
            handleOrientationToggle={handleOrientationToggle}
            handleUndo={handleUndo}
            handleRedo={handleRedo}
            canUndo={historyIndex > 0}
            canRedo={historyIndex < history.length - 1}
            variant={isV2 ? 'v2' : 'v1'}
            zoomLevel={zoomLevel}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onZoomReset={handleZoomReset}
          />

          {/* Template Mode Indicator - Show when using sample data */}
          {/* v2 shows this as a badge in the header instead of a banner over the canvas. */}
          {builderMode === 'template' && !isV2 && (
            <div style={{
              position: 'fixed',
              top: '0',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 9999,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '8px 24px',
              borderRadius: '0 0 8px 8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '18px' }}>📝</span>
              <span>Template Mode - Using Sample Data</span>
              <span style={{
                background: 'rgba(255,255,255,0.2)',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '12px'
              }}>
                Design your layout & styling
              </span>
            </div>
          )}

          {/* Zoom Container - Provides scroll boundaries for scaled content */}
          <div
            className="zoom-container"
            style={{
              flex: 1,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              background: 'var(--background)',
              position: 'relative'
            }}
          >
            {/* Page Canvas - Vertical Stacking */}
            <div
              className="page-canvas-wrapper"
              style={{
                transform: `scale(${zoomLevel})`,
                transformOrigin: 'top center',
                transition: 'transform 0.2s ease-out',
                /* Remove paddingBottom to further reduce space */
                marginBottom: `calc(-100% * (1 - ${zoomLevel}))`,
                paddingBottom: '0px'
              }}
              onClick={(e) => {
                // Deselect block when clicking on the wrapper (outside any page)
                const target = e.target;
                if (target.classList.contains('page-canvas-wrapper')) {
                  setSelectedBlock(null);
                }
              }}
            >
              {/* Header/Footer elements are now calculated via useMemo at top level */}

              {(() => {
                // Calculate which pages are actually visible in the builder/report
                const visiblePages = evaluatedPages.filter(page => {
                  // Never show placeholder pages on export
                  if (isExporting && page.isPlaceholder) {
                    return false;
                  }
                  // Placeholder pages: hidden when continuation pages exist (they take priority).
                  // Only show when user explicitly toggled AND no continuation pages exist.
                  if (page.isPlaceholder) {
                    const hasContinuationPages = evaluatedPages.some(p =>
                      p.isContinuationPage &&
                      !p.isPlaceholder &&
                      p.continuationForPage === page.placeholderForPage
                    );
                    if (hasContinuationPages) return false;
                    return visiblePlaceholderIds.has(page.id);
                  }
                  // ONLY FILTER OUT EXCLUDED PAGES IF EXPORTING
                  if (isExporting && page.isExcluded) {
                    return false;
                  }
                  return true;
                });

                const availableWidgets = visiblePages.flatMap((p, pageIndex) =>
                  p.blocks
                    .filter(b => b.type === 'widget' && !b.isContinuation && !b.isClonedBlock)
                    .map(w => ({
                      id: w.id,
                      label: `[Page ${pageIndex + 1}] ` + (w.config?.title ? `${w.widgetType} - ${w.config.title}` : `${w.widgetType} - ${w.dataConfig?.systemName || w.id.substring(0, 8)}`)
                    }))
                );


                return evaluatedPages
                  .filter(page => {
                    // Never show placeholder pages on export
                    if (isExporting && page.isPlaceholder) {
                      return false;
                    }
                    // Placeholder pages: hidden when continuation pages exist (they take priority).
                    // Only show when user explicitly toggled AND no continuation pages exist.
                    if (page.isPlaceholder) {
                      const hasContinuationPages = evaluatedPages.some(p =>
                        p.isContinuationPage &&
                        !p.isPlaceholder &&
                        p.continuationForPage === page.placeholderForPage
                      );
                      if (hasContinuationPages) return false;
                      return visiblePlaceholderIds.has(page.id);
                    }
                    // ONLY FILTER OUT EXCLUDED PAGES IF EXPORTING
                    if (isExporting && page.isExcluded) {
                      return false;
                    }
                    return true;
                  })
                  .map((page, pageIndex) => {
                    // Find Header and Footer elements that should appear on this page
                    // Header/Footer appear on all pages starting from their startPage
                    const currentPageNumber = pageIndex + 1;

                    // Find all Header elements across all pages that should appear on this page
                    const headerElements = [];
                    // Find all Footer elements across all pages that should appear on this page
                    const footerElements = [];

                    // Get Header/Footer blocks that are directly on this page
                    const pageHeaderFooterBlocks = page.blocks.filter(
                      block => (block.type === 'element' || block.type === 'section') && (block.elementType === 'header' || block.elementType === 'footer')
                    );
                    const pageHeaderFooterIds = new Set(pageHeaderFooterBlocks.map(block => block.id));

                    // First, add headers/footers that are directly on this page
                    pageHeaderFooterBlocks.forEach(block => {
                      if (block.elementType === 'header') {
                        headerElements.push(block);
                      } else if (block.elementType === 'footer') {
                        footerElements.push(block);
                      }
                    });

                    // Then, add headers/footers from other pages that should appear on this page
                    pages.forEach(p => {
                      p.blocks.forEach(block => {
                        if ((block.type === 'element' || block.type === 'section') && (block.elementType === 'header' || block.elementType === 'footer')) {
                          // Skip if already added (from current page)
                          if (pageHeaderFooterIds.has(block.id)) {
                            return;
                          }

                          // Get fromPage (support both old startPage and new fromPage)
                          const fromPage = block.dataConfig?.fromPage ?? block.dataConfig?.startPage ?? block.config?.fromPage ?? block.config?.startPage ?? 1;
                          // Get toPage (default to 'last' if not specified)
                          const toPage = block.dataConfig?.toPage ?? 'last';
                          const totalPages = pages.length;
                          const toPageNumber = toPage === 'last' ? totalPages : (typeof toPage === 'number' ? toPage : totalPages);

                          // Check if current page is within the range
                          const isInRange = currentPageNumber >= fromPage && currentPageNumber <= toPageNumber;

                          // Include if current page is in range
                          if (isInRange) {
                            if (block.elementType === 'header') {
                              headerElements.push(block);
                            } else if (block.elementType === 'footer') {
                              footerElements.push(block);
                            }
                          }
                        }
                      });
                    });

                    return (
                      <div
                        key={page.id}
                        className={`page-container ${page.isExcluded ? 'page-dependency-unmet' : ''}`}
                        ref={el => pageContainerRefs.current[page.id] = el}
                        data-page-id={page.id}
                      >
                        {/* Banner moved to Toolbar */}
                        <div className="page-label" style={{ display: 'flex', alignItems: 'center' }}>
                          <span className="page-number">Page {pageIndex + 1}</span>
                          {page.isContinuationPage && (
                            <span className="continuation-badge">
                              Continuation (Auto-generated)
                            </span>
                          )}
                          {page.isPlaceholder && (
                            <span style={{
                              marginLeft: '8px',
                              padding: '2px 8px',
                              borderRadius: '10px',
                              background: 'rgba(139, 92, 246, 0.2)',
                              border: '1px solid rgba(139, 92, 246, 0.4)',
                              color: '#c4b5fd',
                              fontSize: '10px',
                              fontWeight: '700',
                              letterSpacing: '0.5px',
                              textTransform: 'uppercase',
                            }}>
                              📐 Placeholder
                            </span>
                          )}
                          <span className="page-name">{`Page ${pageIndex + 1}`}</span>

                          {/* Edit Placeholder button — only shown when NO auto-generated continuation page exists.
                        If a continuation page already exists, the user edits that directly (they're the same). */}
                          {!page.isContinuationPage && !page.isPlaceholder && (() => {
                            // Check if auto-generated continuation pages already exist for this source page
                            const hasAutoGeneratedContinuation = pages.some(p =>
                              p.isContinuationPage &&
                              !p.isPlaceholder &&
                              p.continuationForPage === page.id
                            );
                            // If continuation pages exist, user edits those directly — no need for placeholder button
                            if (hasAutoGeneratedContinuation) return null;

                            // Lookup by isPlaceholder flag and placeholderForPage reference (reliable, ID-based)
                            const placeholderForThisPage = pages.find(p => p.isPlaceholder && p.placeholderForPage === page.id);
                            if (!placeholderForThisPage) return null;
                            const isManuallyShown = visiblePlaceholderIds.has(placeholderForThisPage.id);
                            return (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isManuallyShown) {
                                    // Hide it again
                                    setVisiblePlaceholderIds(prev => {
                                      const next = new Set(prev);
                                      next.delete(placeholderForThisPage.id);
                                      return next;
                                    });
                                  } else {
                                    // Show it and scroll to it
                                    setVisiblePlaceholderIds(prev => new Set([...prev, placeholderForThisPage.id]));
                                    setTimeout(() => {
                                      const el = pageContainerRefs.current[placeholderForThisPage.id];
                                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }, 80);
                                  }
                                }}
                                title={isManuallyShown ? 'Hide the Placeholder Page' : 'Reveal the Placeholder Page for editing'}
                                style={{
                                  marginLeft: '10px',
                                  padding: '3px 10px',
                                  borderRadius: '12px',
                                  background: isManuallyShown ? 'rgba(139, 92, 246, 0.3)' : 'rgba(139, 92, 246, 0.15)',
                                  border: '1px solid rgba(139, 92, 246, 0.5)',
                                  color: '#c4b5fd',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                📐 {isManuallyShown ? 'Hide Placeholder' : 'Edit Placeholder'}
                              </button>
                            );
                          })()}


                          {availableWidgets.length > 0 && !page.isContinuationPage && !page.isPlaceholder && !readOnly && (
                            <div
                              className="page-dependency-container"
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                marginLeft: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                background: page.config?.dependentBlockId ? 'rgba(59, 130, 246, 0.15)' : 'rgba(255, 255, 255, 0.1)',
                                padding: '4px 10px',
                                borderRadius: '20px',
                                border: `1px solid ${page.config?.dependentBlockId ? 'rgba(59, 130, 246, 0.3)' : 'rgba(255, 255, 255, 0.15)'}`,
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={page.config?.dependentBlockId ? '#60a5fa' : 'rgba(255,255,255,0.7)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                              </svg>
                              <span style={{
                                color: page.config?.dependentBlockId ? '#93c5fd' : 'rgba(255,255,255,0.8)',
                                fontSize: '11px',
                                fontWeight: '600',
                                letterSpacing: '0.5px',
                                textTransform: 'uppercase',
                                mixBlendMode: 'difference',
                              }}>
                                Page Dependency:
                              </span>
                              <select
                                value={page.config?.dependentBlockId || ''}
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  setPages(prev => prev.map(p =>
                                    p.id === page.id
                                      ? { ...p, config: { ...(p.config || {}), dependentBlockId: newValue } }
                                      : p
                                  ));
                                }}
                                title="Hide this page in report if selected widget has no data"
                                style={{
                                  padding: '0px 16px 0px 4px',
                                  backgroundColor: 'transparent',
                                  color: page.config?.dependentBlockId ? '#fff' : '#d1d5db',
                                  border: 'none',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  minWidth: '130px',
                                  cursor: 'pointer',
                                  outline: 'none',
                                  appearance: 'none',
                                  lineHeight: '1',
                                  backgroundImage: `url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23${page.config?.dependentBlockId ? 'ffffff' : '9ca3af'}%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")`,
                                  backgroundRepeat: 'no-repeat',
                                  backgroundPosition: 'right 0px top 50%',
                                  backgroundSize: '8px auto',
                                  marginBottom: '0px',
                                  mixBlendMode: 'difference',
                                }}
                              >
                                <option value="" style={{ color: '#000', mixBlendMode: 'difference' }}>None (Always Visible)</option>
                                {availableWidgets.map(w => (
                                  <option key={w.id} value={w.id} style={{ color: '#000', mixBlendMode: 'difference' }}>
                                    {w.label}
                                  </option>
                                ))}
                              </select>

                              {/* Condition operator + threshold — visible when a widget is selected */}
                              {page.config?.dependentBlockId && (
                                <>
                                  <select
                                    value={page.config?.dependencyOperator || 'has_data'}
                                    onChange={(e) => {
                                      const newOp = e.target.value;
                                      setPages(prev => prev.map(p =>
                                        p.id === page.id
                                          ? { ...p, config: { ...(p.config || {}), dependencyOperator: newOp } }
                                          : p
                                      ));
                                    }}
                                    title="Condition operator for page visibility"
                                    style={{
                                      padding: '0px 14px 0px 4px',
                                      backgroundColor: 'transparent',
                                      color: '#fff',
                                      border: 'none',
                                      fontSize: '11px',
                                      fontWeight: '500',
                                      minWidth: '50px',
                                      cursor: 'pointer',
                                      outline: 'none',
                                      appearance: 'none',
                                      lineHeight: '1',
                                      backgroundImage: `url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23ffffff%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")`,
                                      backgroundRepeat: 'no-repeat',
                                      backgroundPosition: 'right 0px top 50%',
                                      backgroundSize: '7px auto',
                                      mixBlendMode: 'difference',
                                      marginBottom: '0px',
                                    }}
                                  >
                                    <option value="has_data" style={{ color: '#000' }}>Has Data</option>
                                    <option value="==" style={{ color: '#000' }}>==</option>
                                    <option value="!=" style={{ color: '#000' }}>!=</option>
                                    <option value=">" style={{ color: '#000' }}>&gt;</option>
                                    <option value=">=" style={{ color: '#000' }}>&gt;=</option>
                                    <option value="<" style={{ color: '#000' }}>&lt;</option>
                                    <option value="<=" style={{ color: '#000' }}>&lt;=</option>
                                  </select>

                                  {/* Threshold input — hidden for 'has_data' since it doesn't use a threshold */}
                                  {(page.config?.dependencyOperator && page.config.dependencyOperator !== 'has_data') && (
                                    <input
                                      type="number"
                                      min="0"
                                      value={page.config?.dependencyThreshold ?? 0}
                                      onChange={(e) => {
                                        const val = parseInt(e.target.value, 10);
                                        setPages(prev => prev.map(p =>
                                          p.id === page.id
                                            ? { ...p, config: { ...(p.config || {}), dependencyThreshold: isNaN(val) ? 0 : val } }
                                            : p
                                        ));
                                      }}
                                      title="Data count threshold"
                                      style={{
                                        width: '42px',
                                        padding: '1px 4px',
                                        backgroundColor: 'rgba(255,255,255,0.1)',
                                        color: '#fff',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        borderRadius: '4px',
                                        fontSize: '11px',
                                        fontWeight: '500',
                                        textAlign: 'center',
                                        outline: 'none',
                                        mixBlendMode: 'difference',
                                      }}
                                    />
                                  )}
                                </>
                              )}
                            </div>
                          )}

                          {/* Manual Export Inclusion Toggle */}
                          {!page.isContinuationPage && (
                            <label
                              className="export-toggle-checkbox"
                              style={{
                                marginLeft: 'auto',
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                fontSize: '11px',
                                color: page.isExcluded ? '#ef4444' : '#10b981',
                                userSelect: 'none',
                                background: page.isExcluded ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                border: `1px solid ${page.isExcluded ? 'rgba(239, 68, 68, 0.3)' : 'rgba(16, 185, 129, 0.3)'}`,
                                transition: 'all 0.2s ease'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={!page.isExcluded}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  togglePageExport(page.id, e.target.checked);
                                }}
                                style={{ marginRight: '6px', cursor: 'pointer' }}
                              />
                              {page.isExcluded ? 'Exclude from Export' : 'Include in Export'}
                              {page.originalIsExcluded && !page.isExcluded && ' (Manual Override)'}
                              {!page.originalIsExcluded && page.isExcluded && ' (Manual Override)'}
                              {page.originalIsExcluded && page.isExcluded && ' (No Data)'}
                            </label>
                          )}
                        </div>

                        <PageCanvas
                          page={page}
                          pageIndex={pageIndex}
                          totalPages={pages.length}
                          selectedBlock={selectedBlock}
                          selectedBlocks={selectedBlocks}
                          onBlockSelect={handleBlockSelect}
                          onBlockMove={moveBlock}
                          onBlockResize={resizeBlock}
                          onBlockRemove={removeBlock}
                          getBlockData={getBlockData}
                          pages={pages}
                          currentPageId={currentPageId}
                          handleContextMenu={handleContextMenu}
                          mspInfo={mspInfo}
                          clientInfo={clientInfo}
                          previewData={previewData}
                          orientation={orientation}
                          scanGroups={scanGroups}
                          allCategories={allCategories}

                          onPageChange={setCurrentPageId}
                          showGrid={showGrid}
                          gridConfig={gridConfig}
                          editingBlocks={editingBlocks}
                          onToggleEditMode={toggleEditMode}
                          onUpdateCustomData={updateBlockCustomData}
                          onDataConfigUpdate={updateBlockDataConfig}
                          onConfigUpdate={updateBlockConfig}
                          isExporting={isExporting}
                          readOnly={readOnly}
                          checkWidgetDataAvailability={checkWidgetDataAvailability}
                          metricsData={metricsData}
                          builderMode={builderMode}
                          effectiveScanData={effectiveScanData}
                          headerElements={headerElements}
                          footerElements={footerElements}
                          onHeaderFooterSelect={handleBlockSelect}
                          onAddSection={addLayoutSection}
                          onHeaderFooterMove={moveBlock}
                          onHeaderFooterResize={resizeBlock}
                          onHeaderFooterRemove={removeBlock}
                          theme={theme}
                        />
                      </div>
                    );
                  });
              })()}
              {/* Minimized bottom spacer for a more compact experience */}
              {!isExporting && <div style={{ minHeight: '50px', width: '100%', pointerEvents: 'none' }} />}
            </div>
          </div>
        </div>

        {/* Context Menu */}
        {contextMenu.visible && !readOnly && (
          <ContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            blockId={contextMenu.blockId}
            block={
              currentPage?.blocks?.find(b => b.id === contextMenu.blockId) ||
              window.__headerElements?.find(b => b.id === contextMenu.blockId) ||
              window.__footerElements?.find(b => b.id === contextMenu.blockId)
            }
            onEdit={() => {
              if (contextMenu.blockId) {
                handleBlockSelect(contextMenu.blockId);
                setPropertiesVisible(true);
                // Don't toggle edit mode - element should only be editable in Properties panel
              }
              closeContextMenu();
            }}
            onBringToFront={() => bringToFront(contextMenu.blockId)}
            onSendToBack={() => sendToBack(contextMenu.blockId)}
            onDelete={() => {
              if (contextMenu.blockId) {
                removeBlock(contextMenu.blockId);
              }
              closeContextMenu();
            }}
            onClose={closeContextMenu}

            // Copy/Paste handlers
            onCopy={() => handleCopyBlock(contextMenu.blockId)}
            onPaste={() => handlePasteBlock(currentPageId, contextMenu.x, contextMenu.y)}
            canPaste={!!clipboardBlock}

            // Save as Custom Widget
            onSaveAsCustomWidget={(blockId) => {
              // Use selectedBlocks if multiple are selected, otherwise just this block
              const blockIdsToSave = selectedBlocks.length > 1
                ? selectedBlocks
                : (selectedBlocks.length === 1 ? selectedBlocks : [blockId]);
              setCustomWidgetSaveTarget(blockIdsToSave);
              setShowSaveCustomWidgetModal(true);
              closeContextMenu();
            }}
            selectedBlocksCount={selectedBlocks.length}

            // Group management props
            onCreateGroup={createWidgetGroup}
            onAddToGroup={addToWidgetGroup}
            onRemoveFromGroup={removeFromWidgetGroup}
            widgetGroups={widgetGroups}
            blockGroupInfo={contextMenu.blockId ? getBlockGroupInfo(contextMenu.blockId) : null}
            onAddWidget={() => {
              if (contextMenu.blockId) {
                setSelectedBlock(contextMenu.blockId);
                setSidebarVisible(true);
              }
              closeContextMenu();
            }}
            availableHeaders={globalHeaderElements}
            availableFooters={globalFooterElements}
            onAddToHeader={handleAddToHeader}
            onAddToFooter={handleAddToFooter}
            hasPlaceholder={(() => {
              const targetPage = pages.find(page => page.blocks.some(b => b.id === contextMenu.blockId));
              return pages.some(p => p.isPlaceholder && p.placeholderForPage === targetPage?.id);
            })()}
            isContinuationContext={(() => {
              const targetPage = pages.find(page => page.blocks.some(b => b.id === contextMenu.blockId));
              if (!targetPage) return false;
              if (targetPage.isContinuationPage) return true;
              return pages.some(p => p.isContinuationPage && !p.isPlaceholder && p.continuationForPage === targetPage.id);
            })()}
            onTogglePlaceholder={handleTogglePlaceholder}
          />
        )}


        {/* Properties Panel */}
        <div className={`properties-container ${(propertiesVisible && !readOnly) ? 'visible' : 'hidden'}`} data-bs-theme={chromeBsTheme}>
          {(propertiesVisible && !readOnly) && selectedBlockData && (
            <PropertiesPanel
              block={selectedBlockData}
              onConfigUpdate={updateBlockConfig}
              onStyleUpdate={updateBlockStyle}
              onResize={resizeBlock}
              onDataConfigUpdate={updateBlockDataConfig}
              onRemove={removeBlock}
              availableMetrics={availableMetrics}
              scanData={templateModeScanData}
              isTemplateMode={isTemplateMode}
              dataContext={dataContext}
              pages={pages}
              onToggle={() => {
                setPropertiesVisible(false);
                propertiesManuallyClosed.current = true;
              }}
              isVisible={propertiesVisible}
              editingBlocks={editingBlocks}
              onToggleEditMode={toggleEditMode}
              onUpdateCustomData={updateBlockCustomData}
              globalVariables={globalVariables}
              selectedElementPath={selectedBlock}
              scanGroups={scanGroups}
              allCategories={allCategories}
            />
          )}
          {isV2 && propertiesVisible && !readOnly && !selectedBlockData && (
            <InspectorEmptyV2
              onClose={() => {
                setPropertiesVisible(false);
                propertiesManuallyClosed.current = true;
              }}
            />
          )}
        </div>

        {isV2 && (
          <BuilderStatusBarV2
            left={[
              builderMode === 'template'
                ? <><i className="rb2-dot rb2-dot--accent" /> Template · sample data</>
                : <><i className="rb2-dot" /> Live scan data</>,
              selectedBlockData && `Selected: ${selectedBlockData.config?.title || String(selectedBlockData.widgetType || selectedBlockData.elementType || selectedBlockData.type || '').replace(/[-_]/g, ' ').replace(/^\w/, c => c.toUpperCase())}`
            ]}
            right={[
              `${pages.filter(p => !p.isPlaceholder).length} ${pages.filter(p => !p.isPlaceholder).length === 1 ? 'page' : 'pages'}`,
              orientation === 'landscape' ? 'Landscape' : 'Portrait',
              `${Math.round(zoomLevel * 100)}%`
            ]}
          />
        )}

        {/* Overlay when sidebar/properties are open on mobile */}
        {(sidebarVisible || propertiesVisible) && (
          <div
            className="overlay"
            onClick={() => {
              setSidebarVisible(false);
              setPropertiesVisible(false);
            }}
          />
        )}

        {/* Export Modal */}
        {showExportModal && (
          <div className="modal-overlay" onClick={() => !isExporting && setShowExportModal(false)}>
            <div className="modal-content export-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
              <div className="modal-header">
                <h3>Export Report</h3>
                <button
                  className="close-btn"
                  onClick={() => !isExporting && setShowExportModal(false)}
                  disabled={isExporting}
                >
                  ✕
                </button>
              </div>

              <div className="modal-body">

                {isExporting && (
                  <div className="export-loading text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">
                      Generating your report... {exportProgress.total > 0 && (
                        <span>({exportProgress.current} / {exportProgress.total} pages)</span>
                      )}
                    </p>
                    {exportProgress.total > 0 && (
                      <div className="progress mt-3" style={{ height: '20px', maxWidth: '400px', margin: '0 auto' }}>
                        <div
                          className="progress-bar progress-bar-striped progress-bar-animated"
                          role="progressbar"
                          style={{ width: `${(exportProgress.current / exportProgress.total) * 100}%` }}
                          aria-valuenow={exportProgress.current}
                          aria-valuemin="0"
                          aria-valuemax={exportProgress.total}
                        >
                          {Math.round((exportProgress.current / exportProgress.total) * 100)}%
                        </div>
                      </div>
                    )}
                    {pages.length > 50 && pages.length < 100 && (
                      <p className="text-muted small mt-2">
                        ⚠️ Large template detected ({pages.length} pages). This may take 2-3 minutes. Please wait...
                      </p>
                    )}
                    {pages.length >= 100 && (
                      <div className="alert alert-info mt-3" style={{ fontSize: '0.9rem', padding: '0.75rem' }}>
                        <strong>🚀 Server-Side Processing Enabled</strong>
                        <br />
                        Your template has {pages.length} pages. For optimal performance and reliability,
                        we're using server-side PDF generation.
                        <br />
                        <strong>Estimated time: {pages.length >= 200 ? '3-5' : '1-3'} minutes</strong>
                        <br />
                        Please keep this tab open while we generate your report.
                      </div>
                    )}
                  </div>
                )}

                {exportSuccess && (
                  <div className="alert alert-success text-center">
                    <strong>✓ Report generated successfully!</strong>
                  </div>
                )}

                {exportError && (
                  <div className="alert alert-danger">
                    <strong>Error:</strong> {exportError}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}


        {/* Status Colors Modal */}
        <Modal
          show={showStatusColorsModal}
          onHide={() => setShowStatusColorsModal(false)}
          backdrop="static"
          centered
          style={{ zIndex: 11000 }} // Ensure it is above selected blocks (1000) and context menus (10000)
          enforceFocus={false}
          {...modalThemeProps}
        >
          <Modal.Header closeButton>
            <Modal.Title>Report Settings</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* ── Page Numbers ─────────────────────────────────────────────── */}
            <div style={{ marginBottom: '18px', paddingBottom: '18px', borderBottom: '1px solid #e5e7eb' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold', marginBottom: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!theme?.pageNumbers?.enabled}
                  onChange={(e) => setTheme(prev => ({ ...prev, pageNumbers: { ...(prev.pageNumbers || {}), enabled: e.target.checked } }))}
                />
                Show page numbers
              </label>
              <p style={{ fontSize: '13px', color: '#666', margin: '0 0 10px' }}>
                Numbers every page (including the cover) in the footer area — updates automatically as pages are added, removed, or auto-generated.
              </p>
              {theme?.pageNumbers?.enabled && (
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '140px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Format</label>
                    <select
                      value={theme?.pageNumbers?.format || 'page_x'}
                      onChange={(e) => setTheme(prev => ({ ...prev, pageNumbers: { ...(prev.pageNumbers || {}), format: e.target.value } }))}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    >
                      <option value="page_x">Page X</option>
                      <option value="x_of_y">X of Y</option>
                      <option value="x">X</option>
                    </select>
                  </div>
                  <div style={{ flex: 1, minWidth: '140px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Position</label>
                    <select
                      value={theme?.pageNumbers?.position || 'center'}
                      onChange={(e) => setTheme(prev => ({ ...prev, pageNumbers: { ...(prev.pageNumbers || {}), position: e.target.value } }))}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    >
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>Status Colors</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>Green (Satisfactory)</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={theme?.statusColors?.green || '#40B657'}
                    onChange={(e) => setTheme(prev => ({ ...prev, statusColors: { ...(prev.statusColors || {}), green: e.target.value } }))}
                    style={{ width: '50px', height: '40px', padding: '0', cursor: 'pointer' }}
                  />
                  <input
                    type="text"
                    value={theme?.statusColors?.green || '#40B657'}
                    onChange={(e) => setTheme(prev => ({ ...prev, statusColors: { ...(prev.statusColors || {}), green: e.target.value } }))}
                    style={{ flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>Yellow (Needs Improvement)</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={theme?.statusColors?.yellow || '#FCC419'}
                    onChange={(e) => setTheme(prev => ({ ...prev, statusColors: { ...(prev.statusColors || {}), yellow: e.target.value } }))}
                    style={{ width: '50px', height: '40px', padding: '0', cursor: 'pointer' }}
                  />
                  <input
                    type="text"
                    value={theme?.statusColors?.yellow || '#FCC419'}
                    onChange={(e) => setTheme(prev => ({ ...prev, statusColors: { ...(prev.statusColors || {}), yellow: e.target.value } }))}
                    style={{ flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>Red (Immediate Attention)</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={theme?.statusColors?.red || '#FF0000'}
                    onChange={(e) => setTheme(prev => ({ ...prev, statusColors: { ...(prev.statusColors || {}), red: e.target.value } }))}
                    style={{ width: '50px', height: '40px', padding: '0', cursor: 'pointer' }}
                  />
                  <input
                    type="text"
                    value={theme?.statusColors?.red || '#FF0000'}
                    onChange={(e) => setTheme(prev => ({ ...prev, statusColors: { ...(prev.statusColors || {}), red: e.target.value } }))}
                    style={{ flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
              </div>
              <p style={{ fontSize: '13px', color: '#666', marginTop: '10px' }}>
                These colors will be used across all widgets displaying finding statuses in this template.
              </p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={() => setShowStatusColorsModal(false)}>
              Done
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Save as Custom Widget Modal */}
        <Modal
          show={showSaveCustomWidgetModal}
          onHide={() => { setShowSaveCustomWidgetModal(false); setCustomWidgetSaveTarget(null); }}
          centered
          style={{ zIndex: 11000 }}
          {...modalThemeProps}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {Array.isArray(customWidgetSaveTarget) && customWidgetSaveTarget.length > 1
                ? `Save ${customWidgetSaveTarget.length} Blocks as Custom Widget`
                : 'Save as Custom Widget'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ fontWeight: 600, marginBottom: '4px', display: 'block' }}>Widget Name</label>
                <input
                  id="custom-widget-name-input"
                  type="text"
                  placeholder="e.g. My Title Block"
                  defaultValue=""
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const name = e.target.value.trim();
                      const iconInput = document.getElementById('custom-widget-icon-input');
                      const icon = iconInput?.value || '📦';
                      if (name && customWidgetSaveTarget) {
                        saveAsCustomWidget(customWidgetSaveTarget, name, icon);
                        setShowSaveCustomWidgetModal(false);
                        setCustomWidgetSaveTarget(null);
                      }
                    }
                  }}
                />
              </div>
              <div>
                <label style={{ fontWeight: 600, marginBottom: '4px', display: 'block' }}>Icon (emoji)</label>
                <input
                  id="custom-widget-icon-input"
                  type="text"
                  defaultValue="📦"
                  style={{ width: '80px', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '20px', textAlign: 'center' }}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowSaveCustomWidgetModal(false); setCustomWidgetSaveTarget(null); }}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => {
              const nameInput = document.getElementById('custom-widget-name-input');
              const iconInput = document.getElementById('custom-widget-icon-input');
              const name = nameInput?.value?.trim();
              const icon = iconInput?.value || '📦';
              if (name && customWidgetSaveTarget) {
                saveAsCustomWidget(customWidgetSaveTarget, name, icon);
                setShowSaveCustomWidgetModal(false);
                setCustomWidgetSaveTarget(null);
              }
            }}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>

        {/* AI Written Report Modal */}
        <Modal show={showWrittenReportModal} onHide={() => { setShowWrittenReportModal(false); setWrittenReportHtml(''); setWrittenReportError(''); }} size="xl" centered enforceFocus={false} {...modalThemeProps}>
          <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)', color: '#fff' }}>
            <Modal.Title style={{ color: '#fff' }}>✨ AI Written Report</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: '24px', maxHeight: '80vh', overflowY: 'auto' }}>
            {!writtenReportHtml && !writtenReportLoading && !writtenReportError && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <p style={{ color: '#374151', marginBottom: '8px' }}>
                  Generate a comprehensive, professional cybersecurity assessment report from the selected scan data using AI. The report will include an executive summary, detailed findings, risk ratings, and remediation recommendations.
                </p>
                <div>
                  <label style={{ fontWeight: 600, marginBottom: '4px', display: 'block', color: '#374151' }}>Client / Organization Name (optional)</label>
                  <input
                    type="text"
                    value={writtenReportClientName}
                    onChange={e => setWrittenReportClientName(e.target.value)}
                    placeholder="e.g. Acme Corporation"
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                  />
                </div>
                <div>
                  <label style={{ fontWeight: 600, marginBottom: '4px', display: 'block', color: '#374151' }}>Report Audience</label>
                  <select
                    value={writtenReportAudience}
                    onChange={e => setWrittenReportAudience(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                  >
                    <option value="Executive">Executive (CEO, CIO, CTO) — business risk focus</option>
                    <option value="Technical">Technical (IT Staff) — detailed remediation steps</option>
                  </select>
                </div>
                {!filePath && (
                  <div className="alert alert-warning" role="alert">
                    No scan file is currently selected. Please open a scan from Past Scans before generating the AI report.
                  </div>
                )}
              </div>
            )}
            {writtenReportError && (
              <div className="alert alert-danger" role="alert">
                <strong>Error:</strong> {writtenReportError}
              </div>
            )}
            {writtenReportLoading && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px', gap: '16px' }}>
                <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }} />
                <div style={{ color: '#374151', fontWeight: 600, fontSize: '16px' }}>Analyzing scan data and generating your report...</div>
                <div style={{ color: '#6b7280', fontSize: '13px' }}>This may take 15–30 seconds.</div>
              </div>
            )}
            {writtenReportHtml && !writtenReportLoading && (
              <div
                dangerouslySetInnerHTML={{ __html: writtenReportHtml }}
                style={{ fontFamily: 'Arial, sans-serif', fontSize: '14px', lineHeight: '1.6' }}
              />
            )}
          </Modal.Body>
          <Modal.Footer style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              {writtenReportHtml && (
                <>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      const blob = new Blob([`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Security Report</title></head><body>${writtenReportHtml}</body></html>`], { type: 'text/html' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `security-report-${new Date().toISOString().slice(0,10)}.html`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                  >
                    Download HTML
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      const tmp = document.createElement('div');
                      tmp.innerHTML = writtenReportHtml;
                      navigator.clipboard.writeText(tmp.innerText).then(() => alert('Report text copied to clipboard'));
                    }}
                  >
                    Copy Text
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => {
                      setWrittenReportHtml('');
                      setWrittenReportError('');
                    }}
                  >
                    Regenerate
                  </Button>
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button variant="secondary" onClick={() => { setShowWrittenReportModal(false); setWrittenReportHtml(''); setWrittenReportError(''); }}>
                Close
              </Button>
              {!writtenReportHtml && !writtenReportLoading && (
                <Button
                  variant="primary"
                  onClick={generateWrittenReport}
                  disabled={!filePath}
                  style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)', border: 'none' }}
                >
                  ✨ Generate Report
                </Button>
              )}
            </div>
          </Modal.Footer>
        </Modal>

        <Modal show={showAIBuildDialog} onHide={() => setShowAIBuildDialog(false)} size="lg" centered enforceFocus={false} {...modalThemeProps}>
          <Modal.Header closeButton>
            <Modal.Title>AI Build Report</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {aiError && (
              <div className="alert alert-danger" role="alert">
                {aiError}
              </div>
            )}

            {aiStatus.loading ? (
              <div className="text-center py-4 text-muted">
                <div className="spinner-border spinner-border-sm me-2" role="status" />
                Checking AI configuration...
              </div>
            ) : !aiStatus.enabled ? (
              aiStatus.planLocked ? (
                <div className="alert alert-info d-flex flex-wrap align-items-center gap-2" role="alert">
                  <span>AI Reports is not included in your plan.</span>
                  {aiStatus.canUpgrade ? (
                    <button type="button" className="btn btn-primary btn-sm ms-auto" onClick={() => startProCheckout()}>
                      Upgrade to Pro
                    </button>
                  ) : (
                    <span>Contact your Pipsqueak administrator to have it enabled.</span>
                  )}
                </div>
              ) : aiStatus.quotaExceeded ? (
                <div className="alert alert-danger" role="alert">
                  You have used all 5 trial prompts. A tenant admin must configure their own OpenAI API key under the Admin Panel -> Tenant tab to continue using the AI Report Builder.
                </div>
              ) : (
                <div className="alert alert-warning" role="alert">
                  AI Report Builder is not enabled. A tenant admin or platform admin must configure an OpenAI API key first.
                </div>
              )
            ) : (
              <>
                {!aiStatus.isTenantKey && aiStatus.promptsRemaining !== undefined && (
                  <div className="alert alert-warning d-flex align-items-center gap-2" role="alert">
                    <div>
                      <strong>Free Trial Mode:</strong> Using the system OpenAI key. You have <strong>{aiStatus.promptsRemaining} of {aiStatus.promptLimit}</strong> remaining prompts. Please add your own API key under Admin Panel -> Tenant tab for unlimited generation.
                    </div>
                  </div>
                )}

                {/* AI Disclaimer Alert */}
                <div className="alert alert-warning mb-0 border-0 shadow-sm p-3 d-flex align-items-start gap-3" style={{ backgroundColor: '#fff3cd', borderLeft: '4px solid #ffc107', borderRadius: '6px' }}>
                  <span style={{ fontSize: '20px', lineHeight: '1' }}>⚠️</span>
                  <div style={{ fontSize: '14px', color: '#664d03' }}>
                    <strong className="d-block mb-1" style={{ fontSize: '15px' }}>Review AI-Generated Output</strong>
                    AI models can make formatting mistakes or layout misalignments. It is better to verify all pages and widgets before sharing or exporting this report.
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Scope</label>
                    <select
                      className="form-select"
                      value={aiScope}
                      onChange={(e) => {
                        setAIScope(e.target.value);
                        // Recommended prompts are scoped to Scope + Orientation — once
                        // either changes, clear any prior selection so the user picks
                        // from the newly-relevant list (custom prompts are left as-is).
                        if (!aiCustomPromptMode) setAIGoal('');
                      }}
                      disabled={aiLoading}
                    >
                      <option value="Technical">Technical Report</option>
                      <option value="Executive">Executive Report</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Page Orientation</label>
                    <select
                      className="form-select"
                      value={aiOrientation}
                      onChange={(e) => {
                        setAIOrientation(e.target.value);
                        if (!aiCustomPromptMode) setAIGoal('');
                      }}
                      disabled={aiLoading}
                    >
                      <option value="portrait">Portrait</option>
                      <option value="landscape">Landscape</option>
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">Color Palette Preset</label>
                    <select 
                      className="form-select"
                      value={selectedPreset}
                      onChange={(e) => {
                        const val = e.target.value;
                        presetUserPickedRef.current = true;
                        setSelectedPreset(val);
                        if (val !== 'custom') {
                          const preset = effectivePresets.find(p => p.id === val);
                          if (preset) {
                            setCustomPrimary(preset.primary);
                            setCustomSecondary(preset.secondary);
                            setCustomAccent(preset.accent);
                          }
                        }
                      }}
                      disabled={aiLoading}
                    >
                      {effectivePresets.map(preset => (
                        <option key={preset.id} value={preset.id}>{preset.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12">
                    <div className="p-3 border rounded bg-light" style={{ borderColor: '#e2e8f0' }}>
                      <div className="fw-semibold mb-2 small text-muted">Customize 3-Way Colors</div>
                      <div className="row g-3">
                        <div className="col-4">
                          <div className="d-flex flex-column gap-1">
                            <span className="small text-muted" style={{ fontSize: '11px' }}>Primary Color</span>
                            <div className="d-flex align-items-center gap-2">
                              <input
                                type="color"
                                value={customPrimary}
                                onChange={(e) => {
                                  setCustomPrimary(e.target.value);
                                  setSelectedPreset('custom');
                                }}
                                disabled={aiLoading}
                                title="Choose primary color"
                                style={{ width: '36px', height: '36px', padding: '2px', border: '1px solid #cbd5e1', borderRadius: '4px', cursor: 'pointer' }}
                              />
                              <input
                                type="text"
                                value={customPrimary}
                                onChange={(e) => {
                                  setCustomPrimary(e.target.value);
                                  setSelectedPreset('custom');
                                }}
                                disabled={aiLoading}
                                maxLength={7}
                                className="form-control form-control-sm text-monospace"
                                style={{ fontSize: '12px', fontFamily: 'monospace', minWidth: '70px' }}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="d-flex flex-column gap-1">
                            <span className="small text-muted" style={{ fontSize: '11px' }}>Secondary Color</span>
                            <div className="d-flex align-items-center gap-2">
                              <input
                                type="color"
                                value={customSecondary}
                                onChange={(e) => {
                                  setCustomSecondary(e.target.value);
                                  setSelectedPreset('custom');
                                }}
                                disabled={aiLoading}
                                title="Choose secondary color"
                                style={{ width: '36px', height: '36px', padding: '2px', border: '1px solid #cbd5e1', borderRadius: '4px', cursor: 'pointer' }}
                              />
                              <input
                                type="text"
                                value={customSecondary}
                                onChange={(e) => {
                                  setCustomSecondary(e.target.value);
                                  setSelectedPreset('custom');
                                }}
                                disabled={aiLoading}
                                maxLength={7}
                                className="form-control form-control-sm text-monospace"
                                style={{ fontSize: '12px', fontFamily: 'monospace', minWidth: '70px' }}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="d-flex flex-column gap-1">
                            <span className="small text-muted" style={{ fontSize: '11px' }}>Accent Color</span>
                            <div className="d-flex align-items-center gap-2">
                              <input
                                type="color"
                                value={customAccent}
                                onChange={(e) => {
                                  setCustomAccent(e.target.value);
                                  setSelectedPreset('custom');
                                }}
                                disabled={aiLoading}
                                title="Choose accent color"
                                style={{ width: '36px', height: '36px', padding: '2px', border: '1px solid #cbd5e1', borderRadius: '4px', cursor: 'pointer' }}
                              />
                              <input
                                type="text"
                                value={customAccent}
                                onChange={(e) => {
                                  setCustomAccent(e.target.value);
                                  setSelectedPreset('custom');
                                }}
                                disabled={aiLoading}
                                maxLength={7}
                                className="form-control form-control-sm text-monospace"
                                style={{ fontSize: '12px', fontFamily: 'monospace', minWidth: '70px' }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="form-label fw-semibold mb-0">
                        {aiCustomPromptMode
                          ? 'Your Prompt'
                          : `Recommended Prompts for ${aiScope} (${aiOrientation === 'portrait' ? 'Portrait' : 'Landscape'})`}
                      </label>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => {
                          const next = !aiCustomPromptMode;
                          setAICustomPromptMode(next);
                          // Switching modes starts the goal fresh so a stale selection
                          // from the other mode can't be submitted by accident.
                          setAIGoal('');
                        }}
                        disabled={aiLoading}
                      >
                        {aiCustomPromptMode ? '← Choose a recommended prompt' : '+ Create your own prompt'}
                      </button>
                    </div>

                    {aiCustomPromptMode ? (
                      <textarea
                        className="form-control"
                        rows={3}
                        value={aiGoal}
                        onChange={(e) => setAIGoal(e.target.value)}
                        placeholder="Describe what this report should emphasize."
                        disabled={aiLoading}
                        autoFocus
                      />
                    ) : (() => {
                      const candidatePrompts = PREMADE_PROMPTS.filter(p => p.scope === aiScope && p.orientation === aiOrientation);
                      // Only recommend prompts the scan data actually supports (e.g. don't
                      // suggest an AD-detail report when there's no Active Directory data).
                      const availablePrompts = candidatePrompts.filter(p => promptHasSupportingData(p, aiProfile));
                      return (
                        <>
                          {availablePrompts.length > 0 ? (
                            <div className="row g-2">
                              {availablePrompts.map((p, idx) => {
                                const isSelected = aiGoal === p.prompt;
                                const promptKey = `${p.scope}|${p.orientation}|${p.label}`;
                                const isExpanded = expandedPromptKey === promptKey;
                                return (
                                  <div key={idx} className="col-md-6">
                                    <div
                                      className="card p-2 h-100"
                                      role="button"
                                      tabIndex={0}
                                      style={{
                                        cursor: aiLoading ? 'default' : 'pointer',
                                        opacity: aiLoading && !isSelected ? 0.6 : 1,
                                        backgroundColor: isSelected ? '#eaf2ff' : '#f8f9fa',
                                        borderColor: isSelected ? '#0d6efd' : '#e2e8f0',
                                        borderWidth: isSelected ? '2px' : '1px',
                                        borderStyle: 'solid',
                                        borderRadius: '6px',
                                        transition: 'all 0.2s ease-in-out'
                                      }}
                                      onClick={() => {
                                        if (aiLoading) return;
                                        // Selecting a recommended prompt immediately builds the
                                        // report with it — no separate "Generate" step.
                                        setAIGoal(p.prompt);
                                        generateAIReportLayout({ goal: p.prompt });
                                      }}
                                      onKeyDown={(e) => {
                                        if ((e.key === 'Enter' || e.key === ' ') && !aiLoading) {
                                          e.preventDefault();
                                          setAIGoal(p.prompt);
                                          generateAIReportLayout({ goal: p.prompt });
                                        }
                                      }}
                                    >
                                      <div className="d-flex justify-content-between align-items-center mb-1">
                                        <span className="fw-bold text-primary" style={{ fontSize: '11px' }}>{p.label}</span>
                                        {isSelected ? (
                                          <span className="badge bg-primary border-0" style={{ fontSize: '9px', padding: '2px 5px' }}>✓ Selected</span>
                                        ) : (
                                          <span className="badge bg-primary-subtle text-primary border-0" style={{ fontSize: '9px', padding: '2px 5px' }}>Recommended</span>
                                        )}
                                      </div>
                                      <p className="text-muted mb-1" style={{ fontSize: '11px', lineHeight: '1.3' }}>{p.description}</p>
                                      <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0"
                                        style={{ fontSize: '10px', textDecoration: 'none' }}
                                        onClick={(e) => {
                                          // Only toggles the preview — must not select/generate.
                                          e.stopPropagation();
                                          setExpandedPromptKey(isExpanded ? null : promptKey);
                                        }}
                                      >
                                        {isExpanded ? '▲ Hide full prompt' : '▼ Show full prompt'}
                                      </button>
                                      {isExpanded && (
                                        <div
                                          className="mt-2 p-2"
                                          style={{
                                            fontSize: '11px',
                                            lineHeight: '1.4',
                                            color: '#334155',
                                            backgroundColor: '#ffffff',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '4px',
                                            whiteSpace: 'pre-wrap'
                                          }}
                                          // Prevent a click inside the text from bubbling up
                                          // to the card's select/generate handler.
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          {p.prompt}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="alert alert-secondary py-2 mb-0" style={{ fontSize: '13px' }}>
                              {aiProfiling
                                ? 'Checking which report types fit your scan data…'
                                : candidatePrompts.length > 0
                                  ? `None of the recommended ${aiScope} (${aiOrientation === 'portrait' ? 'Portrait' : 'Landscape'}) prompts match the data in this scan. Try a different Scope/Orientation, or create your own prompt.`
                                  : `No recommended prompts for ${aiScope} (${aiOrientation === 'portrait' ? 'Portrait' : 'Landscape'}) yet. Create your own prompt instead.`}
                            </div>
                          )}
                          {!aiGoal.trim() && availablePrompts.length > 0 && (
                            <div className="form-text mt-2">Click a recommended prompt to generate the report immediately, or create your own.</div>
                          )}
                        </>
                      );
                    })()}

                    {aiLoading && (
                      <div className="mt-3">
                        <AIGeneratingIndicator />
                      </div>
                    )}
                  </div>
                  <div className="col-12">
                    <div className="form-check">
                      <input
                        id="ai-allow-full-data"
                        className="form-check-input"
                        type="checkbox"
                        checked={aiAllowFullData}
                        onChange={(e) => setAIAllowFullData(e.target.checked)}
                        disabled={aiLoading}
                      />
                      <label className="form-check-label text-muted" htmlFor="ai-allow-full-data">
                        Allow sending raw scan data to AI (required for deep contextual writing)
                      </label>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">Detected Data Coverage</h6>
                    <Button variant="outline-secondary" size="sm" onClick={profileAIReportData} disabled={aiProfiling || aiLoading}>
                      {aiProfiling ? 'Profiling...' : 'Refresh Profile'}
                    </Button>
                  </div>
                  {!aiProfile ? (
                    <div className="text-muted small">{aiProfiling ? 'Inspecting scan data...' : 'No profile loaded yet.'}</div>
                  ) : (
                    <>
                      <div className="d-flex flex-wrap gap-2 mb-3">
                        {(aiProfile.sections || []).map(section => (
                          <span
                            key={`${section.key}-${section.path}`}
                            className={`badge ${section.hasData ? 'bg-success' : 'bg-secondary'}`}
                            title={section.path}
                          >
                            {section.label}: {section.hasData ? `${section.count} ${section.type}` : 'missing'}
                          </span>
                        ))}
                      </div>
                      {(aiProfile.warnings || []).length > 0 && (
                        <div className="alert alert-warning py-2 mb-0">
                          <strong>Incomplete data:</strong>
                          <ul className="mb-0">
                            {aiProfile.warnings.slice(0, 6).map((warning, index) => (
                              <li key={index}>{warning}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {aiOmittedSections.length > 0 && (
                  <div className="alert alert-info mt-3 py-2">
                    <strong>AI skipped or removed:</strong>
                    <ul className="mb-0">
                      {aiOmittedSections.slice(0, 8).map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAIBuildDialog(false)} disabled={aiLoading}>
              Close
            </Button>
            <Button variant="primary" onClick={generateAIReportLayout} disabled={aiLoading || aiProfiling || !aiStatus.enabled || !aiGoal.trim()} title={!aiGoal.trim() ? 'Select a recommended prompt or write your own first' : undefined}>
              {aiLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Generating…
                </>
              ) : 'Generate Layout'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Save Template Dialog */}
        {showSaveDialog && (
          <div className="modal-overlay" onClick={() => setShowSaveDialog(false)}>
            <div className="modal-content save-template-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Save Template</h3>
                <button className="close-btn" onClick={() => setShowSaveDialog(false)} aria-label="Close modal">×</button>
              </div>
              <div className="modal-body">
                <p>Enter a name for this template:</p>
                <input
                  type="text"
                  className="form-control mb-3"
                  placeholder="Template name"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveTemplate();
                    if (e.key === 'Escape') setShowSaveDialog(false);
                  }}
                />
              </div>
              <div className="modal-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowSaveDialog(false);
                    setTemplateName('');
                  }}
                  disabled={savingTemplate}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={saveTemplate}
                  disabled={savingTemplate || !templateName.trim()}
                >
                  {savingTemplate ? 'Saving...' : 'Save Template'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Export Progress Tracker is now GLOBAL - shown via App.js */}
        {/* No need for local tracker - progress persists across all pages */}

        {/* Global Variables Panel */}
        {showGlobalVariablesPanel && (
          <GlobalVariablesPanel
            variables={globalVariables}
            onUpdate={setGlobalVariables}
            onClose={() => setShowGlobalVariablesPanel(false)}
          />
        )}

      </div>
    </DndProvider>
  );
};

// =============================================================================
// DATA SOURCE BROWSER COMPONENT
// A hierarchical data browser that allows users to navigate through objects and arrays
// =============================================================================
// =============================================================================
// DATA SOURCE BROWSER COMPONENT (NEW)
// =============================================================================
const DataSourceBrowser = ({
  dataContext,
  onSelect,
  selectedPath = '',
  widgetType = null,
  onCancel
}) => {
  // Navigation state
  const [currentPath, setCurrentPath] = useState([]);

  // Unified search term for both objects and arrays
  const [searchTerm, setSearchTerm] = useState('');

  // Filter settings for arrays
  const [arrayFilter, setArrayFilter] = useState({ field: '', operator: 'equals', value: '' });


  // Pagination for large arrays
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  // Reset pagination and search when path changes
  useEffect(() => {
    setCurrentPage(1);
    setSearchTerm('');
    setArrayFilter({ field: '', operator: 'equals', value: '' });
  }, [currentPath]);

  // Get the data at the current path
  const getCurrentData = useCallback(() => {
    if (currentPath.length === 0) return dataContext;

    let data = dataContext;
    for (const segment of currentPath) {
      if (segment.type === 'key') {
        data = data?.[segment.key];
      } else if (segment.type === 'index') {
        data = data?.[segment.index];
      }
      if (data === undefined || data === null) return null;
    }
    return data;
  }, [dataContext, currentPath]);

  // Get the full path string
  const getPathString = useCallback(() => {
    if (currentPath.length === 0) return '';
    let path = '';
    for (let i = 0; i < currentPath.length; i++) {
      const segment = currentPath[i];
      if (segment.type === 'key') {
        if (path) path += '.';
        path += segment.key;
      } else if (segment.type === 'index') {
        path += `[${segment.index}]`;
      }
    }
    return path;
  }, [currentPath]);

  // Analyze data type
  const getDataType = (data) => {
    if (data === null || data === undefined) return 'null';
    if (Array.isArray(data)) return 'array';
    if (typeof data === 'object') return 'object';
    if (typeof data === 'number') return 'number';
    if (typeof data === 'boolean') return 'boolean';
    if (typeof data === 'string') {
      if (!isNaN(parseFloat(data)) && isFinite(data)) return 'numeric-string';
      return 'string';
    }
    return 'unknown';
  };

  // Get type icon/label
  const getTypeInfo = (type, data) => {
    switch (type) {
      case 'array': return { icon: '📋', label: 'Array', count: data?.length || 0 };
      case 'object': return { icon: '📦', label: 'Object', count: Object.keys(data || {}).length };
      case 'number': return { icon: '🔢', label: 'Number' };
      case 'numeric-string': return { icon: '🔢', label: 'Num Str' };
      case 'boolean': return { icon: data ? '✅' : '❌', label: 'Bool' };
      case 'string': return { icon: '📝', label: 'String' };
      case 'null': return { icon: '⭕', label: 'Null' };
      default: return { icon: '❓', label: 'Unknown' };
    }
  };

  // Breadcrumbs
  const breadcrumbs = useMemo(() => {
    const crumbs = [{ label: 'Root', path: [] }];
    let pathSoFar = [];
    for (const segment of currentPath) {
      pathSoFar = [...pathSoFar, segment];
      crumbs.push({
        label: segment.type === 'key' ? segment.key : `[${segment.index}]`,
        path: [...pathSoFar]
      });
    }
    return crumbs;
  }, [currentPath]);

  // Navigation handlers
  const navigateTo = (newPath) => setCurrentPath(newPath);
  const drillInto = (segment) => setCurrentPath([...currentPath, segment]);

  // Helper: Get label for array item
  const getArrayItemLabel = (item, index) => {
    if (!item || typeof item !== 'object') return String(item).substring(0, 50);
    const identifiers = ['name', 'Name', 'title', 'Title', 'label', 'Label', 'hostname', 'ip', 'id', 'ID', 'username'];
    for (const id of identifiers) if (item[id]) return String(item[id]);
    const firstString = Object.entries(item).find(([k, v]) => typeof v === 'string' && v.length < 50 && !k.startsWith('_'));
    return firstString ? firstString[1] : `Item ${index}`;
  };

  // Get Items to Display (Filtered & Paginated)
  const { filteredItems, currentType, currentData } = useMemo(() => {
    const data = getCurrentData();
    const type = getDataType(data);
    let items = [];

    // 1. Convert data to list of items
    if (type === 'object') {
      items = Object.entries(data).map(([key, value]) => ({
        key,
        value,
        type: getDataType(value),
        segment: { type: 'key', key },
        label: key
      }));

      // --- NEW: Filter root-level items for Standard Widgets ---
      const isStandardWidget = [
        'numeric', 'gauge', 'statcard', 'bar', 'line', 'pie', 'donut', 'radar', 'polar', 'bubble', 'scatter', 'table'
      ].includes(widgetType);

      if (isStandardWidget && currentPath.length === 0) {
        const allowedKeys = [
          'Internet Speed',
          'Pipsqueak',
          'Pisqueak',
          'Localhost Scan', 'localhost',
          'Network Scan', 'network_devices', 'network_scan',
          'AD Scan', 'ad_scan',
          'MS365 Scan', 'ms365_scan',
          'PII Scan', 'pii_scan'
        ];
        const labelMap = {
          'localhost': 'Localhost Scan',
          'network_devices': 'Network Scan',
          'network_scan': 'Network Scan',
          'ad_scan': 'AD Scan',
          'ms365_scan': 'MS365 Scan',
          'pii_scan': 'PII Scan',
          'Pisqueak': 'Pipsqueak'
        };

        items = items
          .filter(item => {
            const key = item.key;
            const type = item.type;

            // Specifically for Network Scan, the user ONLY wants the array version (network_devices)
            // If this is the 'network_scan' object, hide it.
            if ((key === 'Network Scan' || key === 'network_scan') && type === 'object') {
              return false;
            }

            const lowerKey = key.toLowerCase();
            return allowedKeys.some(allowed => {
              const lowerAllowed = allowed.toLowerCase();
              const lowerAllowedNoScan = lowerAllowed.replace(/\s+scan/g, '');

              return lowerKey === lowerAllowed ||
                lowerKey === lowerAllowedNoScan ||
                (allowed === 'Network Scan' && lowerKey === 'network_devices');
            });

          })
          .map(item => ({
            ...item,
            label: labelMap[item.key] || item.label
          }));
      }
    } else if (type === 'array') {
      items = data.map((item, index) => ({
        key: index,
        value: item,
        type: getDataType(item),
        segment: { type: 'index', index },
        label: getArrayItemLabel(item, index),
        originalIndex: index
      }));
    }

    // 2. Filter using Unified Search Term
    if (searchTerm) {
      const lowerTerm = searchTerm.toLowerCase();
      items = items.filter(item => {
        // Search in Label/Key
        if (String(item.label).toLowerCase().includes(lowerTerm)) return true;

        // Search in Value (primitive)
        if (!['object', 'array'].includes(item.type) && String(item.value).toLowerCase().includes(lowerTerm)) return true;

        // Deep search for objects (one level)
        if (item.type === 'object' && item.value) {
          return Object.keys(item.value).some(k => k.toLowerCase().includes(lowerTerm));
        }
        return false;
      });
    }

    return { filteredItems: items, currentType: type, currentData: data };
  }, [getCurrentData, searchTerm, arrayFilter]);

  // Pagination Logic
  const paginatedItems = useMemo(() => {
    if (currentType !== 'array') return filteredItems;
    const start = (currentPage - 1) * itemsPerPage;
    return filteredItems.slice(start, start + itemsPerPage);
  }, [filteredItems, currentPage, currentType]);

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  // Confirm Selection
  const handleConfirm = () => {
    const pathStr = getPathString();
    onSelect({
      path: pathStr,
      dataType: currentType,
      data: currentData,
      itemCount: Array.isArray(currentData) ? currentData.length : (typeof currentData === 'object' ? Object.keys(currentData).length : 1)
    });
  };

  // Render Styles
  const listItemStyle = {
    background: 'var(--background-secondary)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius)',
    padding: '8px 12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    minHeight: '40px',
    position: 'relative',
    overflow: 'hidden'
  };

  return (
    <div className="dsb-container" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, gap: '0.5rem' }}>

      {/* HEADER: Breadcrumbs & Search */}
      <div className="dsb-header" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {/* Breadcrumbs */}
        <div className="dsb-breadcrumbs" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '5px', fontSize: '0.9rem' }}>
          {breadcrumbs.map((crumb, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <span style={{ color: 'var(--text-tertiary)' }}>/</span>}
              <button
                onClick={() => navigateTo(crumb.path)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: idx === breadcrumbs.length - 1 ? 'var(--primary)' : 'var(--text-secondary)',
                  fontWeight: idx === breadcrumbs.length - 1 ? '600' : 'normal',
                  padding: '2px 6px',
                  cursor: 'pointer',
                  borderRadius: '4px'
                }}
                className="hover-bg-secondary"
              >
                {crumb.label}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Search Bar */}
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder="Search data keys or values..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              paddingLeft: '35px',
              borderRadius: 'var(--radius)',
              border: '1px solid var(--border)',
              background: 'var(--background-primary)',
              color: 'var(--text-primary)'
            }}
          />
          <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', opacity: 0.5 }}>🔍</span>
        </div>
      </div>

      {/* CONTENT: Grid / List */}
      <div className="dsb-content" style={{ flex: 1, overflowY: 'auto', paddingRight: '5px' }}>

        {/* Helper info */}
        <div style={{ marginBottom: '10px', fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'flex', justifyContent: 'space-between' }}>
          <span>Showing {filteredItems.length} items</span>
          <span>{getTypeInfo(currentType, currentData).icon} {getTypeInfo(currentType, currentData).label}</span>
        </div>

        {filteredItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>
            <div style={{ fontSize: '2rem', marginBottom: '10px' }}>🕵️</div>
            No items found matching "{searchTerm}"
          </div>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            {paginatedItems.map((item, idx) => {
              const info = getTypeInfo(item.type, item.value);
              const isComplex = ['object', 'array'].includes(item.type);

              return (
                <div
                  key={item.key}
                  style={listItemStyle}
                  className="dsb-item hover-bg-tertiary"
                  onClick={() => isComplex ? drillInto(item.segment) : null}
                >
                  {/* Icon */}
                  <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{info.icon}</span>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div style={{ fontWeight: '500', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.9rem' }} title={item.label}>
                      {item.label}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      {isComplex ? `${info.count || 0} items` : String(item.value).substring(0, 30)}
                    </div>
                  </div>

                  {/* Type Badge / Action */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '0.65rem', background: 'var(--background-tertiary)', padding: '2px 6px', borderRadius: '4px', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
                      {item.type}
                    </span>
                    {isComplex && (
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)' }}>›</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} className="btn btn-sm btn-outline-secondary">Previous</button>
            <span style={{ alignSelf: 'center', fontSize: '0.9rem' }}>Page {currentPage} of {totalPages}</span>
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)} className="btn btn-sm btn-outline-secondary">Next</button>
          </div>
        )}
      </div>

      {/* FOOTER: Action Bar */}
      <div className="dsb-footer" style={{ borderTop: '1px solid var(--border)', paddingTop: '15px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '10px' }}>
        <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
          Selected Path: <code>{getPathString() || 'Root'}</code>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={onCancel} className="btn btn-light">Cancel</button>
          <button onClick={handleConfirm} className="btn btn-primary">
            Select {currentType === 'array' ? 'All Items' : 'This Data'}
          </button>
        </div>
      </div>
    </div>
  );
};


// Sidebar Component with Tabs and Wizard Modal
const Sidebar = ({ activeTab, onTabChange, availableMetrics, onAddWidget, onAddElement, onToggle, isVisible, metricsLoading = false, metricsError = null, dataContext = {}, currentPage = null, filteredWidgetTypes = [], mspInfo = null, customWidgets = [], onAddCustomWidget, onDeleteCustomWidget, readOnly = false, dynamicCategoryMap = {}, widgetTypes = [], scanGroups = [], variant = 'v1' }) => {
  // Helper to resolve static category names to dynamic database labels
  const getDynamicCategoryName = (staticCategory) => {
    const categoryKeyMap = {
      'Active Directory': 'AD Scan',
      'Microsoft 365': 'MS365 Scan',
      'Localhost': 'Localhost Scan',
      'Network': 'Network Scan',
      'Personally Identifiable Information': 'PII Scan',
      'External Risk Scan': 'External Scan',
      'Infrastructure': 'Infrastructure',
      'Managed IT / Service Experience': 'Managed IT/Service Experience'
    };
    const groupKey = categoryKeyMap[staticCategory];
    if (groupKey) {
      const group = scanGroups.find(g => g.key === groupKey);
      if (group) {
        return group.label;
      }
    }
    return staticCategory;
  };

  // Wizard state
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [selectedWidgetType, setSelectedWidgetType] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState(null);

  // NEW: Data source browser state
  const [dataSourceSelection, setDataSourceSelection] = useState(null);
  // dataSourceSelection = { path, dataType, filter, selectedIndex, data, itemCount }

  const [fieldConfig, setFieldConfig] = useState({
    labelField: '',
    valueField: '',
    filterMode: 'none',
    filterField: '',
    filterValue: '',
    title: ''
  });

  // Search and collapsible state
  const [widgetSearchQuery, setWidgetSearchQuery] = useState('');
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());


  // Auto-expand categories when search query matches widgets
  useEffect(() => {
    if (widgetSearchQuery.trim()) {
      const query = widgetSearchQuery.toLowerCase().trim();
      const categoriesToExpand = new Set();

      // Find all categories that have matching widgets
      filteredWidgetTypes.forEach(widget => {
        const matches =
          widget.label.toLowerCase().includes(query) ||
          widget.description?.toLowerCase().includes(query) ||
          widget.type.toLowerCase().includes(query);

        if (matches && widget.category) {
          categoriesToExpand.add(widget.category);
        }
      });

      // Expand matching categories
      setCollapsedCategories(prev => {
        const newSet = new Set(prev);
        categoriesToExpand.forEach(cat => newSet.delete(cat));
        return newSet;
      });
    }
  }, [widgetSearchQuery]);

  const elementTypes = [
    { type: 'title', label: 'Title', icon: '🏷️', description: 'Large heading text' },
    { type: 'text', label: 'Text', icon: '📝', description: 'Rich text content' },
    { type: 'image', label: 'Image', icon: '🖼️', description: 'Image from URL' },
    { type: 'divider', label: 'Divider', icon: '➖', description: 'Horizontal line' },
    { type: 'embed', label: 'Embed', icon: '🔗', description: 'Embed external content' },
    { type: 'shape', label: 'Shape', icon: '⬜', description: 'Geometric shapes' },
    { type: 'icon', label: 'Icon', icon: '⭐', description: 'Icons and symbols' },
    { type: 'header', label: 'Header', icon: '📄', description: 'Page header (appears on every page)' },
    { type: 'footer', label: 'Footer', icon: '📄', description: 'Page footer (appears on every page)' }
  ];

  // Add MSP Logos if available
  // Always add MSP Logo elements now, but they will use the fallback if missing
  // This allows users to place the elements even if they haven't uploaded a logo yet
  if (true) { // mspInfo?.tenant_logo || mspInfo?.logo
    // Add after Image (index 2 + 1 = 3)
    elementTypes.splice(3, 0, { type: 'msp_logo', label: 'MSP Logo', icon: '🏢', description: 'MSP Logo Image (fallback if missing)' });
    elementTypes.splice(4, 0, { type: 'msp_large_logo', label: 'MSP Large Logo', icon: '🏢', description: 'MSP Large Logo Image (fallback to regular)' });
  }
  // Get chart compatibility score and recommendation
  const getChartCompatibility = (widgetType, metricAnalysis) => {
    if (!metricAnalysis) return { score: 0, rating: 'unknown', message: 'Unable to analyze data' };

    const { type, itemCount, hasNumericValues, hasBooleanFields, categoryFieldCount, isObjectArray } = metricAnalysis;

    // Numeric widgets (numeric, gauge, statcard)
    if (['numeric', 'gauge', 'statcard'].includes(widgetType)) {
      if (type === 'number') {
        return { score: 100, rating: 'perfect', message: 'Perfect match for single values' };
      }
      if (type === 'array') {
        return { score: 70, rating: 'good', message: `Shows count (${itemCount} items)` };
      }
      return { score: 30, rating: 'poor', message: 'Not ideal for this data type' };
    }

    // Table - works well with arrays of objects
    if (widgetType === 'table') {
      if (type === 'array' && isObjectArray) {
        return { score: 100, rating: 'perfect', message: 'Ideal for tabular data' };
      }
      if (type === 'array') {
        return { score: 80, rating: 'good', message: 'Can display as list' };
      }
      return { score: 40, rating: 'poor', message: 'Tables work best with arrays' };
    }

    // Pie/Donut - best for categorical data with few categories
    if (['pie', 'donut'].includes(widgetType)) {
      if (type === 'array' && isObjectArray) {
        if (hasBooleanFields || categoryFieldCount > 0) {
          if (itemCount <= 10) {
            return { score: 100, rating: 'perfect', message: 'Great for showing proportions' };
          }
          return { score: 80, rating: 'good', message: 'Use grouping for best results' };
        }
        if (hasNumericValues) {
          return { score: 70, rating: 'good', message: 'Can show value distribution' };
        }
        return { score: 50, rating: 'fair', message: 'Use "Group By" for meaningful chart' };
      }
      return { score: 30, rating: 'poor', message: 'Needs array data with categories' };
    }

    // Bar Chart - very versatile
    if (widgetType === 'bar') {
      if (type === 'array' && isObjectArray) {
        if (hasNumericValues) {
          return { score: 100, rating: 'perfect', message: 'Excellent for comparisons' };
        }
        if (hasBooleanFields || categoryFieldCount > 0) {
          return { score: 90, rating: 'perfect', message: 'Great with grouping' };
        }
        return { score: 60, rating: 'fair', message: 'Use "Group By" to create bars' };
      }
      if (type === 'array') {
        return { score: 70, rating: 'good', message: 'Can show item counts' };
      }
      return { score: 30, rating: 'poor', message: 'Needs array data' };
    }

    // Line Chart - best for trends/time series
    if (widgetType === 'line') {
      if (type === 'array' && isObjectArray && hasNumericValues) {
        return { score: 90, rating: 'perfect', message: 'Good for trends over time' };
      }
      if (type === 'array') {
        return { score: 50, rating: 'fair', message: 'Better with numeric values' };
      }
      return { score: 30, rating: 'poor', message: 'Needs sequential numeric data' };
    }

    // Radar - best for multi-dimensional comparison
    if (widgetType === 'radar') {
      if (type === 'array' && isObjectArray && hasNumericValues && itemCount >= 3 && itemCount <= 10) {
        return { score: 100, rating: 'perfect', message: 'Ideal for multi-metric comparison' };
      }
      if (type === 'array' && isObjectArray) {
        return { score: 60, rating: 'fair', message: 'Works best with 3-10 items' };
      }
      return { score: 30, rating: 'poor', message: 'Needs numeric multi-dimensional data' };
    }

    // Polar Area - similar to pie but with magnitude
    if (widgetType === 'polar') {
      if (type === 'array' && isObjectArray && hasNumericValues) {
        return { score: 90, rating: 'perfect', message: 'Shows magnitude and proportion' };
      }
      if (type === 'array' && (hasBooleanFields || categoryFieldCount > 0)) {
        return { score: 70, rating: 'good', message: 'Use grouping for counts' };
      }
      return { score: 40, rating: 'poor', message: 'Needs numeric values' };
    }

    // Scatter/Bubble - needs x,y coordinates
    if (['scatter', 'bubble'].includes(widgetType)) {
      if (type === 'array' && isObjectArray && metricAnalysis.numericFieldCount >= 2) {
        return { score: 100, rating: 'perfect', message: 'Great for correlations' };
      }
      if (type === 'array' && isObjectArray && hasNumericValues) {
        return { score: 60, rating: 'fair', message: 'Needs 2+ numeric fields' };
      }
      return { score: 20, rating: 'poor', message: 'Needs multiple numeric fields' };
    }

    return { score: 50, rating: 'fair', message: 'May need configuration' };
  };

  // Get metrics with compatibility scores
  const getMetricsWithCompatibility = (widgetType) => {
    return availableMetrics.map(metric => {
      const analysis = analyzeMetricData(metric);
      const compatibility = getChartCompatibility(widgetType, analysis);
      return {
        ...metric,
        analysis,
        compatibility
      };
    }).sort((a, b) => b.compatibility.score - a.compatibility.score);
  };

  // Filter metrics based on selected widget type (only show compatible ones)
  const getFilteredMetrics = (widgetType) => {
    const metricsWithCompat = getMetricsWithCompatibility(widgetType);
    // Filter by compatibility score and remove duplicates by label
    const filtered = metricsWithCompat.filter(m => m.compatibility.score >= 30);

    // Deduplicate by label - keep the one with highest compatibility score
    const seen = new Map();
    filtered.forEach(metric => {
      const key = metric.label;
      if (!seen.has(key) || seen.get(key).compatibility.score < metric.compatibility.score) {
        seen.set(key, metric);
      }
    });

    return Array.from(seen.values());
  };

  // Get data for selected metric
  const getMetricData = () => {
    if (!selectedMetric || !dataContext) return null;
    return getDataByPath(dataContext, selectedMetric.path, dynamicCategoryMap);
  };

  // Extract all nested field paths from an object (e.g., "system.Manufacturer", "audit_dns.ip")
  const extractNestedPaths = (obj, prefix = '', maxDepth = 3, currentDepth = 0) => {
    const paths = [];
    if (currentDepth >= maxDepth || !obj || typeof obj !== 'object') return paths;

    Object.keys(obj).forEach(key => {
      const fullPath = prefix ? `${prefix}.${key}` : key;
      const value = obj[key];

      if (value === null || value === undefined) {
        paths.push({ path: fullPath, type: 'null' });
      } else if (Array.isArray(value)) {
        paths.push({ path: fullPath, type: 'array', itemCount: value.length });
        // Don't recurse into arrays for now
      } else if (typeof value === 'object') {
        paths.push({ path: fullPath, type: 'object' });
        // Recurse into nested objects
        paths.push(...extractNestedPaths(value, fullPath, maxDepth, currentDepth + 1));
      } else {
        paths.push({ path: fullPath, type: typeof value, sampleValue: value });
      }
    });

    return paths;
  };

  // Get value at a nested path (handles bracket notation like "array[0].key")
  const getNestedValue = (obj, path) => {
    if (!obj || !path) return undefined;
    // Parse path to handle bracket notation
    const parts = [];
    let current = '';
    let inBracket = false;
    for (let i = 0; i < path.length; i++) {
      const char = path[i];
      if (char === '[') { if (current) { parts.push(current); current = ''; } inBracket = true; }
      else if (char === ']') { if (current) { parts.push(current); current = ''; } inBracket = false; }
      else if (char === '.' && !inBracket) { if (current) { parts.push(current); current = ''; } }
      else { current += char; }
    }
    if (current) parts.push(current);

    let result = obj;
    for (const key of parts) {
      if (result === null || result === undefined) return undefined;
      if (/^\d+$/.test(key) && Array.isArray(result)) {
        result = result[parseInt(key, 10)];
      } else if (typeof result === 'object') {
        result = result[key];
      } else {
        return undefined;
      }
    }
    return result;
  };

  // Get all unique values for a nested field path from array data
  const getUniqueValuesForPath = (data, fieldPath) => {
    if (!Array.isArray(data)) return [];
    const uniqueValues = [];
    const valueSet = new Set();

    data.forEach(item => {
      const val = getNestedValue(item, fieldPath);
      if (val !== undefined && val !== null) {
        const valStr = String(val);
        if (!valueSet.has(valStr)) {
          valueSet.add(valStr);
          uniqueValues.push({ value: val, display: valStr });
        }
      }
    });

    return uniqueValues.sort((a, b) => a.display.localeCompare(b.display));
  };

  // Analyze all available fields including nested ones
  const analyzeAllFields = () => {
    const data = getMetricData();
    if (!Array.isArray(data) || data.length === 0) {
      return { allPaths: [], groupedPaths: {} };
    }

    // Sample items to analyze structure
    const sampleItems = data.slice(0, 5);
    const pathsMap = new Map();

    sampleItems.forEach(item => {
      if (typeof item === 'object' && item !== null) {
        const paths = extractNestedPaths(item);
        paths.forEach(pathInfo => {
          if (!pathsMap.has(pathInfo.path)) {
            pathsMap.set(pathInfo.path, pathInfo);
          }
        });
      }
    });

    const allPaths = Array.from(pathsMap.values());

    // Group paths by their root key for better organization
    const groupedPaths = {};
    allPaths.forEach(pathInfo => {
      const rootKey = pathInfo.path.split('.')[0];
      if (!groupedPaths[rootKey]) {
        groupedPaths[rootKey] = [];
      }
      groupedPaths[rootKey].push(pathInfo);
    });

    return { allPaths, groupedPaths };
  };

  // Analyze fields from selected metric data
  const analyzeFields = () => {
    const data = getMetricData();
    if (!Array.isArray(data) || data.length === 0 || typeof data[0] !== 'object') {
      return { numericFields: [], textFields: [], booleanFields: [], categoryFields: [], nestedFields: [] };
    }

    const sampleItems = data.slice(0, 10);
    const allKeys = new Set();
    sampleItems.forEach(item => {
      if (typeof item === 'object' && item !== null) {
        Object.keys(item).forEach(key => allKeys.add(key));
      }
    });

    const numericFields = [];
    const textFields = [];
    const booleanFields = [];
    const categoryFields = [];
    const nestedFields = []; // Fields that are objects with nested data

    allKeys.forEach(key => {
      const uniqueValues = new Set();
      let numericCount = 0;
      let booleanCount = 0;
      let textCount = 0;
      let objectCount = 0;

      sampleItems.forEach(item => {
        if (item && typeof item === 'object' && item !== null && key in item) {
          const val = item[key];
          if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
            objectCount++;
          } else {
            uniqueValues.add(String(val));
            if (typeof val === 'number') numericCount++;
            else if (typeof val === 'boolean') booleanCount++;
            else if (typeof val === 'string') {
              if (!isNaN(parseFloat(val)) && isFinite(parseFloat(val))) {
                numericCount++;
              } else {
                textCount++;
              }
            }
          }
        }
      });

      const total = sampleItems.length;
      if (objectCount / total > 0.5) {
        // This is a nested object field
        nestedFields.push(key);
      } else if (numericCount / total > 0.7) numericFields.push(key);
      else if (booleanCount / total > 0.7) booleanFields.push(key);
      else if (textCount / total > 0.7) textFields.push(key);

      if (uniqueValues.size >= 2 && uniqueValues.size <= 10) {
        categoryFields.push({ key, uniqueCount: uniqueValues.size });
      }
    });

    return { numericFields, textFields, booleanFields, categoryFields, nestedFields };
  };

  // Get unique values for a field
  const getUniqueValues = (fieldName) => {
    const data = getMetricData();
    if (!Array.isArray(data)) return [];

    const uniqueValues = [];
    const valueSet = new Set();
    data.forEach(item => {
      if (item && typeof item === 'object' && fieldName in item) {
        const val = item[fieldName];
        const valStr = String(val);
        if (!valueSet.has(valStr)) {
          valueSet.add(valStr);
          uniqueValues.push(val);
        }
      }
    });
    return uniqueValues;
  };

  // ============================================
  // HIERARCHICAL DRILL-DOWN SYSTEM
  // ============================================

  // State for drill-down navigation
  // eslint-disable-next-line no-unused-vars
  const [drillDownPath, setDrillDownPath] = useState([]);
  const [drillDownFilters, setDrillDownFilters] = useState([]);
  const [selectedNestedArray, setSelectedNestedArray] = useState(null);
  const [nestedFieldFilter, setNestedFieldFilter] = useState(null); // { fieldPath, operator, value } for filtering nested array items
  const [nestedFieldSearch, setNestedFieldSearch] = useState(''); // Search query for filtering nested fields
  // Device selection mode: 'all' = use all devices, 'specific' = single device, 'compare' = compare field across devices
  const [deviceMode, setDeviceMode] = useState('all');
  const [selectedDeviceIndex, setSelectedDeviceIndex] = useState(null); // null = all, number = specific device
  const [comparisonField, setComparisonField] = useState(null); // Field to compare across devices (e.g., 'system.RAM Used')
  const [chartAggregationField, setChartAggregationField] = useState(null); // For chart widgets: which field to aggregate/count

  // Nested array aggregation mode: 'combine' = merge all, 'single' = first only
  const [nestedAggMode, setNestedAggMode] = useState('combine');

  // Discover all nested fields within items of an array (arrays, objects, primitives)
  const discoverNestedArrays = (data, maxDepth = 5, currentPath = '') => {
    const nestedFields = [];

    if (!Array.isArray(data) || data.length === 0) return nestedFields;

    // Smart sampling: Take items from different parts of the array to discover all possible structures
    // Some devices may have minimal data while others have full data
    const sampleSize = Math.min(30, data.length);
    const indices = new Set();

    // Take first few
    for (let i = 0; i < Math.min(5, data.length); i++) indices.add(i);
    // Take some from middle
    const midStart = Math.floor(data.length / 3);
    for (let i = midStart; i < Math.min(midStart + 5, data.length); i++) indices.add(i);
    // Take some from later (where devices with full data may be)
    const lateStart = Math.floor(data.length * 2 / 3);
    for (let i = lateStart; i < Math.min(lateStart + 10, data.length); i++) indices.add(i);
    // Also add some random indices
    while (indices.size < sampleSize && indices.size < data.length) {
      indices.add(Math.floor(Math.random() * data.length));
    }

    const sampleItems = Array.from(indices).map(i => data[i]).filter(Boolean);

    const exploreObject = (obj, path, depth, visitedPaths = new Set()) => {
      if (depth >= maxDepth || !obj || typeof obj !== 'object' || Array.isArray(obj)) return;

      Object.keys(obj).forEach(key => {
        const value = obj[key];
        const fullPath = path ? `${path}.${key}` : key;

        // Skip if we've already visited this path
        if (visitedPaths.has(fullPath)) return;
        visitedPaths.add(fullPath);

        if (Array.isArray(value) && value.length > 0) {
          // Found a nested array
          const sampleItem = value[0];
          const isObjectArray = sampleItem && typeof sampleItem === 'object' && !Array.isArray(sampleItem);

          // Create a human-readable label from the path
          const pathParts = fullPath.split('.');
          const label = pathParts[pathParts.length - 1]
            .replace(/([A-Z])/g, ' $1')
            .replace(/_/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();

          nestedFields.push({
            path: fullPath,
            label: label,
            fullLabel: pathParts.map(p => p.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()).join(' → '),
            itemCount: value.length,
            fieldType: 'array',
            isObjectArray,
            sampleFields: isObjectArray ? Object.keys(sampleItem).filter(k => !k.startsWith('_')).slice(0, 6) : [],
            sampleItem: isObjectArray ? sampleItem : null
          });

          // Recursively explore inside the nested array's items
          if (isObjectArray && sampleItem) {
            exploreObject(sampleItem, fullPath + '[]', depth + 1, new Set());
          }
        } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          // Found a nested object - also add it as an option
          const pathParts = fullPath.split('.');
          const label = pathParts[pathParts.length - 1]
            .replace(/([A-Z])/g, ' $1')
            .replace(/_/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();

          const objectKeys = Object.keys(value).filter(k => !k.startsWith('_')).slice(0, 6);

          nestedFields.push({
            path: fullPath,
            label: label,
            fullLabel: pathParts.map(p => p.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()).join(' → '),
            itemCount: 1,
            fieldType: 'object',
            isObjectArray: false,
            sampleFields: objectKeys,
            sampleItem: value
          });

          // Recurse into nested objects
          exploreObject(value, fullPath, depth + 1, visitedPaths);
        } else {
          // Primitive field (string, number, boolean)
          const pathParts = fullPath.split('.');
          const label = pathParts[pathParts.length - 1]
            .replace(/([A-Z])/g, ' $1')
            .replace(/_/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();

          nestedFields.push({
            path: fullPath,
            label: label,
            fullLabel: pathParts.map(p => p.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()).join(' → '),
            itemCount: 1,
            fieldType: typeof value,
            isObjectArray: false,
            sampleFields: [],
            sampleValue: value
          });
        }
      });
    };

    // Explore the structure of sample items
    sampleItems.forEach(item => {
      if (typeof item === 'object' && item !== null) {
        exploreObject(item, currentPath, 0, new Set());
      }
    });

    // Remove duplicates and sort by path
    const uniquePaths = new Map();
    nestedFields.forEach(field => {
      const existing = uniquePaths.get(field.path);
      if (!existing || field.itemCount > existing.itemCount) {
        uniquePaths.set(field.path, field);
      }
    });

    // Sort by field type and label for better UX (arrays first, then objects, then primitives)
    const typeOrder = { array: 0, object: 1, string: 2, number: 3, boolean: 4 };
    return Array.from(uniquePaths.values()).sort((a, b) => {
      const typeCompare = (typeOrder[a.fieldType] || 99) - (typeOrder[b.fieldType] || 99);
      return typeCompare !== 0 ? typeCompare : a.label.localeCompare(b.label);
    });
  };

  // Get list of available devices/items from the root array
  const getAvailableDevices = () => {
    if (!selectedMetric || !dataContext) return [];

    const data = getDataByPath(dataContext, selectedMetric.path, dynamicCategoryMap);
    if (!Array.isArray(data)) return [];

    return data.map((item, index) => {
      // Try to find a good identifier for each device
      const ip = item.ip || item.IP || '';
      const hostname = item.hostname || item.Hostname || '';
      const computerName = getNestedValue(item, 'system.Computer Name') || '';
      const manufacturer = getNestedValue(item, 'system.Manufacturer') || '';
      const status = item.status || '';

      // Create a display label
      let label = computerName || hostname || ip || `Device ${index + 1}`;
      let sublabel = '';
      if (ip && label !== ip) sublabel = ip;
      if (manufacturer) sublabel = sublabel ? `${sublabel} - ${manufacturer}` : manufacturer;

      return {
        index,
        label,
        sublabel,
        ip,
        hostname,
        computerName,
        manufacturer,
        status,
        hasSystemData: !!getNestedValue(item, 'system.Manufacturer')
      };
    });
  };

  // Get all fields available for cross-device comparison (numeric, boolean, string, nested)
  const getComparisonFields = () => {
    if (!selectedMetric || !dataContext) return [];

    const data = getDataByPath(dataContext, selectedMetric.path, dynamicCategoryMap);
    if (!Array.isArray(data) || data.length === 0) return [];

    const numericFields = [];
    const booleanFields = [];
    const categoryFields = [];
    const sampleItems = data.slice(0, 20).filter(item => item && typeof item === 'object');

    // Track unique values for category fields
    const fieldValueCounts = new Map();

    // Look for all useful field types at various levels
    const explored = new Set();

    const exploreAllFields = (obj, prefix = '') => {
      if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return;

      Object.keys(obj).forEach(key => {
        const fullPath = prefix ? `${prefix}.${key}` : key;
        if (explored.has(fullPath)) return;
        explored.add(fullPath);

        const value = obj[key];

        // Skip internal fields
        if (key.startsWith('_')) return;

        const label = fullPath.split('.').pop()
          .replace(/([A-Z])/g, ' $1')
          .replace(/_/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();

        // Check for numeric values
        if (typeof value === 'number' || (typeof value === 'string' && !isNaN(parseFloat(value)) && value.match(/^[\d.%]+$/))) {
          numericFields.push({
            path: fullPath,
            label,
            fullLabel: fullPath.replace(/\./g, ' → '),
            sampleValue: value,
            type: 'numeric',
            category: '📊 Numeric Values'
          });
        }
        // Check for boolean values
        else if (typeof value === 'boolean') {
          booleanFields.push({
            path: fullPath,
            label,
            fullLabel: fullPath.replace(/\./g, ' → '),
            sampleValue: value ? 'true' : 'false',
            type: 'boolean',
            category: '🔘 Yes/No Fields'
          });
        }
        // Check for string values that could be categories
        else if (typeof value === 'string' && value.length > 0 && value.length < 100) {
          // Count unique values across sample items
          if (!fieldValueCounts.has(fullPath)) {
            fieldValueCounts.set(fullPath, new Set());
          }
          fieldValueCounts.get(fullPath).add(value);

          // Only add if we haven't already
          if (!categoryFields.some(f => f.path === fullPath)) {
            categoryFields.push({
              path: fullPath,
              label,
              fullLabel: fullPath.replace(/\./g, ' → '),
              sampleValue: value,
              type: 'category',
              category: '📝 Text/Status Fields'
            });
          }
        }
        // Recurse into nested objects
        else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          exploreAllFields(value, fullPath);
        }
      });
    };

    // Explore all sample items
    sampleItems.forEach(item => exploreAllFields(item));

    // Filter category fields to only include those with reasonable number of unique values
    const filteredCategoryFields = categoryFields.filter(f => {
      const uniqueCount = fieldValueCounts.get(f.path)?.size || 0;
      return uniqueCount >= 2 && uniqueCount <= 20; // Only include if 2-20 unique values
    }).map(f => ({
      ...f,
      uniqueValues: fieldValueCounts.get(f.path)?.size || 0
    }));

    // Combine all fields with category headers
    const allFields = [
      ...numericFields,
      ...booleanFields,
      ...filteredCategoryFields
    ];

    // Remove duplicates and sort by category then label
    const unique = new Map();
    allFields.forEach(f => {
      if (!unique.has(f.path)) unique.set(f.path, f);
    });

    return Array.from(unique.values()).sort((a, b) => {
      // Sort by category first, then by label
      if (a.category !== b.category) {
        const categoryOrder = { '📊 Numeric Values': 0, '🔘 Yes/No Fields': 1, '📝 Text/Status Fields': 2 };
        return (categoryOrder[a.category] || 99) - (categoryOrder[b.category] || 99);
      }
      return a.label.localeCompare(b.label);
    });
  };

  // Get data at the current drill-down level
  const getDrillDownData = () => {
    if (!selectedMetric || !dataContext) return null;

    // Start with the root data
    let data = getDataByPath(dataContext, selectedMetric.path, dynamicCategoryMap);
    if (!Array.isArray(data)) return null;

    // Apply any drill-down filters
    if (drillDownFilters.length > 0) {
      drillDownFilters.forEach(filter => {
        if (filter.fieldPath && filter.value !== '') {
          data = data.filter(item => {
            const val = getNestedValue(item, filter.fieldPath);
            if (filter.operator === 'equals') {
              return String(val).toLowerCase() === String(filter.value).toLowerCase();
            }
            return true;
          });
        }
      });
    }

    // Handle "compare" mode - get a specific field from each device
    if (deviceMode === 'compare' && comparisonField) {
      // Determine field type
      let fieldType = 'numeric';
      data.forEach(item => {
        const value = getNestedValue(item, comparisonField);
        if (typeof value === 'boolean') fieldType = 'boolean';
        else if (typeof value === 'string' && isNaN(parseFloat(value))) fieldType = 'category';
      });

      // For numeric: return device-value pairs
      if (fieldType === 'numeric') {
        const comparisonData = [];
        data.forEach((item, idx) => {
          const value = getNestedValue(item, comparisonField);
          if (value !== undefined && value !== null) {
            const computerName = getNestedValue(item, 'system.Computer Name') || item.hostname || item.ip || `Device ${idx + 1}`;
            const parsedValue = typeof value === 'string' ? parseFloat(value.replace('%', '')) : value;

            comparisonData.push({
              label: computerName,
              value: parsedValue,
              ip: item.ip || '',
              _index: idx
            });
          }
        });
        return comparisonData;
      }

      // For boolean/category: return grouped counts
      const counts = {};
      data.forEach(item => {
        const value = getNestedValue(item, comparisonField);
        if (value !== undefined && value !== null) {
          const strValue = typeof value === 'boolean'
            ? (value ? 'Enabled / Yes' : 'Disabled / No')
            : String(value);
          counts[strValue] = (counts[strValue] || 0) + 1;
        }
      });

      return Object.entries(counts).map(([label, value]) => ({ label, value }));
    }

    // Handle "specific" mode - select only one device FIRST before any other processing
    if (deviceMode === 'specific' && selectedDeviceIndex !== null) {
      data = data.filter((_, idx) => idx === selectedDeviceIndex);
    }

    // If a nested array is selected, extract and aggregate it
    if (selectedNestedArray) {
      const nestedData = [];

      data.forEach((item, itemIndex) => {
        const nestedValue = getNestedValue(item, selectedNestedArray.path);
        if (Array.isArray(nestedValue)) {
          // Get parent device information
          const parentIp = item.ip || item.IP || '';
          const parentHostname = item.hostname || item.Hostname || getNestedValue(item, 'system.Computer Name') || '';

          // Include both internal tracking fields and user-friendly fields
          const parentInfo = {
            _parentIndex: itemIndex,
            _parentIp: parentIp,
            _parentHostname: parentHostname,
            // User-friendly fields that can be selected as columns
            'IP': parentIp,
            'Hostname': parentHostname,
            'Device IP': parentIp,
            'Device Hostname': parentHostname
          };

          if (nestedAggMode === 'combine') {
            // Combine nested arrays (or single device's array)
            nestedValue.forEach(nestedItem => {
              if (typeof nestedItem === 'object') {
                nestedData.push({ ...nestedItem, ...parentInfo });
              } else {
                nestedData.push({ value: nestedItem, ...parentInfo });
              }
            });
          }
        }
      });

      return nestedData;
    }

    return data;
  };

  // Get available nested arrays based on current selection
  const getAvailableNestedArrays = () => {
    if (!selectedMetric || !dataContext) return [];

    const data = getDataByPath(dataContext, selectedMetric.path, dynamicCategoryMap);
    if (!Array.isArray(data)) return [];

    return discoverNestedArrays(data);
  };

  // Get preview of drill-down results
  const getDrillDownPreview = () => {
    const data = getDrillDownData();
    if (!Array.isArray(data)) return { count: 0, sample: null };

    return {
      count: data.length,
      sample: data.slice(0, 3)
    };
  };

  // Start wizard - optionally with a pre-selected widget type (skips to step 2)
  const startWizard = (preSelectedWidgetType = null) => {
    setShowWizard(true);
    setSelectedMetric(null);
    setDrillDownPath([]);
    setDrillDownFilters([]);
    setSelectedNestedArray(null);
    setDeviceMode('all');
    setNestedAggMode('combine');
    setSelectedDeviceIndex(null);
    setComparisonField(null);
    setFieldConfig({
      labelField: '',
      valueField: '',
      filterMode: 'none',
      filterField: '',
      filterValue: '',
      title: '',
      // Advanced query builder fields
      advancedFilters: [],
      showAdvancedQuery: false,
      // Drill-down fields
      nestedArrayPath: null,
      parentFilters: [],
      // Device selection
      deviceMode: 'all',
      selectedDeviceIndex: null,
      comparisonField: null,
      // Device-level filters
      deviceFilters: []
    });

    if (preSelectedWidgetType) {
      // Skip to step 2 with pre-selected widget type
      setSelectedWidgetType(preSelectedWidgetType);
      setWizardStep(2);
    } else {
      // Start from step 1
      setSelectedWidgetType(null);
      setWizardStep(1);
    }
  };

  // Close wizard
  const closeWizard = () => {
    setShowWizard(false);
    setWizardStep(1);
    setSelectedWidgetType(null);
    setSelectedMetric(null);
    setDataSourceSelection(null); // Reset new browser selection
    setDrillDownPath([]);
    setDrillDownFilters([]);
    setSelectedNestedArray(null);
    setDeviceMode('all');
    setNestedAggMode('combine');
    setSelectedDeviceIndex(null);
    setComparisonField(null);
    setFieldConfig({
      labelField: '',
      valueField: '',
      filterMode: 'none',
      filterField: '',
      filterValue: '',
      title: '',
      advancedFilters: [],
      showAdvancedQuery: false,
      nestedArrayPath: null,
      parentFilters: [],
      deviceMode: 'all',
      selectedDeviceIndex: null,
      comparisonField: null,
      deviceFilters: []
    });
  };

  // Add drill-down filter
  const addDrillDownFilter = () => {
    setDrillDownFilters(prev => [
      ...prev,
      { id: Date.now(), fieldPath: '', operator: 'equals', value: '' }
    ]);
  };

  // Update drill-down filter
  const updateDrillDownFilter = (filterId, updates) => {
    setDrillDownFilters(prev =>
      prev.map(f => f.id === filterId ? { ...f, ...updates } : f)
    );
  };

  // Remove drill-down filter
  const removeDrillDownFilter = (filterId) => {
    setDrillDownFilters(prev => prev.filter(f => f.id !== filterId));
  };

  // Select a nested array to drill into
  const selectNestedArray = (nestedArray) => {
    setSelectedNestedArray(nestedArray);
    // Auto-analyze the nested array fields
    const data = getDrillDownData();
    if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
      const fields = analyzeFields();
      // Auto-suggest grouping for boolean fields
      if (fields.booleanFields.length > 0) {
        setFieldConfig(prev => ({
          ...prev,
          filterMode: 'group',
          filterField: fields.booleanFields[0]
        }));
      }
    }
  };

  // Clear nested array selection - kept for future drill-down UI
  // const clearNestedArraySelection = () => {
  //   setSelectedNestedArray(null);
  // };

  // Add a new advanced filter condition
  const addAdvancedFilter = () => {
    setFieldConfig(prev => ({
      ...prev,
      advancedFilters: [
        ...prev.advancedFilters,
        { id: Date.now(), fieldPath: '', operator: 'equals', value: '', logic: 'AND' }
      ]
    }));
  };

  // Update an advanced filter condition
  const updateAdvancedFilter = (filterId, updates) => {
    setFieldConfig(prev => ({
      ...prev,
      advancedFilters: prev.advancedFilters.map(f =>
        f.id === filterId ? { ...f, ...updates } : f
      )
    }));
  };

  // Remove an advanced filter condition
  const removeAdvancedFilter = (filterId) => {
    setFieldConfig(prev => ({
      ...prev,
      advancedFilters: prev.advancedFilters.filter(f => f.id !== filterId)
    }));
  };

  // Apply advanced filters to data
  const applyAdvancedFilters = (data, filters) => {
    if (!Array.isArray(data) || !filters || filters.length === 0) return data;

    return data.filter(item => {
      let result = true;

      for (let i = 0; i < filters.length; i++) {
        const filter = filters[i];
        if (!filter.fieldPath || filter.value === '') continue;

        const itemValue = getNestedValue(item, filter.fieldPath);
        const filterValue = filter.value;
        let matches = false;

        // Handle different operators
        switch (filter.operator) {
          case 'equals':
            matches = String(itemValue).toLowerCase() === String(filterValue).toLowerCase();
            break;
          case 'not_equals':
            matches = String(itemValue).toLowerCase() !== String(filterValue).toLowerCase();
            break;
          case 'contains':
            matches = String(itemValue).toLowerCase().includes(String(filterValue).toLowerCase());
            break;
          case 'not_contains':
            matches = !String(itemValue).toLowerCase().includes(String(filterValue).toLowerCase());
            break;
          case 'starts_with':
            matches = String(itemValue).toLowerCase().startsWith(String(filterValue).toLowerCase());
            break;
          case 'ends_with':
            matches = String(itemValue).toLowerCase().endsWith(String(filterValue).toLowerCase());
            break;
          case 'greater_than':
            matches = parseFloat(itemValue) > parseFloat(filterValue);
            break;
          case 'less_than':
            matches = parseFloat(itemValue) < parseFloat(filterValue);
            break;
          case 'is_empty':
            matches = itemValue === undefined || itemValue === null || itemValue === '' || (Array.isArray(itemValue) && itemValue.length === 0) || (typeof itemValue === 'object' && itemValue !== null && !Array.isArray(itemValue) && Object.keys(itemValue).length === 0);
            break;
          case 'is_not_empty':
            matches = itemValue !== undefined && itemValue !== null && itemValue !== '' && !(Array.isArray(itemValue) && itemValue.length === 0) && !(typeof itemValue === 'object' && itemValue !== null && !Array.isArray(itemValue) && Object.keys(itemValue).length === 0);
            break;
          case 'in_list': {
            const predefined = PREDEFINED_LISTS[filterValue];
            const listVals = predefined ? predefined.values : String(filterValue).split(',').map(v => v.trim());
            matches = listVals.includes(String(itemValue));
            break;
          }
          case 'not_in_list': {
            const predefined2 = PREDEFINED_LISTS[filterValue];
            const listVals2 = predefined2 ? predefined2.values : String(filterValue).split(',').map(v => v.trim());
            matches = !listVals2.includes(String(itemValue));
            break;
          }
          case 'is_true':
            matches = itemValue === true || itemValue === 'true' || itemValue === 'True';
            break;
          case 'is_false':
            matches = itemValue === false || itemValue === 'false' || itemValue === 'False';
            break;
          default:
            matches = String(itemValue) === String(filterValue);
        }

        // Apply logic (AND/OR)
        if (i === 0) {
          result = matches;
        } else if (filter.logic === 'AND') {
          result = result && matches;
        } else {
          result = result || matches;
        }
      }

      return result;
    });
  };

  // Handle widget type selection in wizard
  const handleWizardWidgetSelect = (widgetType) => {
    const widget = widgetTypes.find(w => w.type === widgetType);

    // For 'auto' type widgets (Findings widgets), skip wizard and add directly
    if (widget?.dataType === 'auto') {
      // Close wizard
      setShowWizard(false);

      // Add widget with empty dataConfig - it will use full context automatically
      onAddWidget(widgetType, {
        metricPath: '', // Empty path = use full context
        title: widget.label || getDefaultTitle(widgetType),
        // No field configuration needed - auto widgets handle their own data extraction
      });

      // Reset wizard state
      setSelectedWidgetType(null);
      setSelectedMetric(null);
      setWizardStep(1);
      setFieldConfig({
        labelField: '',
        valueField: '',
        filterMode: 'none',
        filterField: '',
        filterValue: '',
        title: ''
      });

      return;
    }

    // For regular widgets, proceed to data selection step
    setSelectedWidgetType(widgetType);
    setWizardStep(2);
  };

  // Handle widget button click from sidebar - auto widgets add directly, others open wizard
  const handleWidgetButtonClick = (widgetType) => {
    const widget = widgetTypes.find(w => w.type === widgetType);

    // For 'auto' type widgets, add directly without wizard
    if (widget?.dataType === 'auto') {
      onAddWidget(widgetType, {
        metricPath: '', // Empty path = use full context
        title: widget.label || getDefaultTitle(widgetType),
        // No field configuration needed - auto widgets handle their own data extraction
      });
      return;
    }

    // For regular widgets, open the wizard
    startWizard(widgetType);
  };

  // Handle metric selection in wizard with smart auto-configuration
  // eslint-disable-next-line no-unused-vars
  const handleWizardMetricSelect = (metric) => {
    setSelectedMetric(metric);
    setSelectedNestedArray(null); // Reset nested array selection
    setDrillDownFilters([]); // Reset filters

    // Analyze the data to provide smart defaults
    const analysis = metric.analysis || analyzeMetricData(metric);
    const widget = widgetTypes.find(w => w.type === selectedWidgetType);

    // Smart default configuration based on data and chart type
    let smartConfig = {
      title: metric.label,
      labelField: '',
      valueField: '',
      filterMode: 'none',
      filterField: '',
      filterValue: '',
      nestedArrayPath: null,
      parentFilters: []
    };

    // Skip field config for numeric widgets - go directly to preview
    if (widget?.dataType === 'number') {
      setFieldConfig(smartConfig);
      setWizardStep(4); // Go directly to preview
      return;
    }

    // Check if this metric has nested arrays that can be drilled into
    const data = getDataByPath(dataContext, metric.path, dynamicCategoryMap);
    if (Array.isArray(data) && data.length > 0) {
      const nestedArrs = discoverNestedArrays(data);
      if (nestedArrs.length > 0) {
        // Stay on step 2 to show drill-down options
        setFieldConfig(smartConfig);
        return; // Don't auto-advance - let user explore drill-down
      }
    }

    // For array data without numeric values, suggest grouping
    if (analysis?.type === 'array' && analysis?.isObjectArray) {
      // If no numeric values but has boolean/category fields, auto-suggest grouping
      if (!analysis.hasNumericValues && (analysis.hasBooleanFields || analysis.categoryFieldCount > 0)) {
        smartConfig.filterMode = 'group';

        // Try to find the best field to group by
        const fieldAnalysisResult = analyzeFieldsForMetric(metric);
        if (fieldAnalysisResult.booleanFields.length > 0) {
          smartConfig.filterField = fieldAnalysisResult.booleanFields[0];
        } else if (fieldAnalysisResult.categoryFields.length > 0) {
          smartConfig.filterField = fieldAnalysisResult.categoryFields[0].key;
        }
      }
      // If has numeric values, suggest using them
      else if (analysis.hasNumericValues) {
        const fieldAnalysisResult = analyzeFieldsForMetric(metric);
        if (fieldAnalysisResult.numericFields.length > 0) {
          smartConfig.valueField = fieldAnalysisResult.numericFields[0];
        }
        if (fieldAnalysisResult.textFields.length > 0) {
          smartConfig.labelField = fieldAnalysisResult.textFields[0];
        }
      }
    }

    setFieldConfig(smartConfig);
    // Don't auto-advance to step 3 - let user see drill-down panel
    // User must click "Use This Data" to proceed
  };

  // Analyze fields for a specific metric
  const analyzeFieldsForMetric = (metric) => {
    const data = getDataByPath(dataContext, metric.path, dynamicCategoryMap);
    if (!Array.isArray(data) || data.length === 0 || typeof data[0] !== 'object') {
      return { numericFields: [], textFields: [], booleanFields: [], categoryFields: [] };
    }

    const sampleItems = data.slice(0, 10);
    const allKeys = new Set();
    sampleItems.forEach(item => {
      if (typeof item === 'object' && item !== null) {
        Object.keys(item).forEach(key => allKeys.add(key));
      }
    });

    const numericFields = [];
    const textFields = [];
    const booleanFields = [];
    const categoryFields = [];

    allKeys.forEach(key => {
      const uniqueValues = new Set();
      let numericCount = 0;
      let booleanCount = 0;
      let textCount = 0;

      sampleItems.forEach(item => {
        if (item && typeof item === 'object' && item !== null && key in item) {
          const val = item[key];
          uniqueValues.add(String(val));
          if (typeof val === 'number') numericCount++;
          else if (typeof val === 'boolean') booleanCount++;
          else if (typeof val === 'string') {
            if (!isNaN(parseFloat(val)) && isFinite(parseFloat(val))) {
              numericCount++;
            } else {
              textCount++;
            }
          }
        }
      });

      const total = sampleItems.length;
      if (numericCount / total > 0.7) numericFields.push(key);
      else if (booleanCount / total > 0.7) booleanFields.push(key);
      else if (textCount / total > 0.7) textFields.push(key);

      if (uniqueValues.size >= 2 && uniqueValues.size <= 10) {
        categoryFields.push({ key, uniqueCount: uniqueValues.size });
      }
    });

    return { numericFields, textFields, booleanFields, categoryFields };
  };

  // Add widget from wizard
  const addWidgetFromWizard = () => {
    const widgetInfo = widgetTypes.find(w => w.type === selectedWidgetType);

    // Build title based on selection
    let autoTitle = widgetInfo?.label || 'New Widget';

    // Use new DataSourceBrowser selection if available
    if (dataSourceSelection) {
      // Use the path for the title
      const pathParts = (dataSourceSelection.path || '').split('.');
      autoTitle = pathParts[pathParts.length - 1] || 'Data';

      // Make it more descriptive
      if (dataSourceSelection.dataType === 'array') {
        if (dataSourceSelection.selectedIndex !== null && dataSourceSelection.selectedIndex !== undefined) {
          autoTitle = `${autoTitle} [${dataSourceSelection.selectedIndex}]`;
        } else if (dataSourceSelection.filter) {
          autoTitle = `${autoTitle} (Filtered)`;
        }
      }
    } else if (deviceMode === 'compare' && comparisonField) {
      // For comparison mode, use the comparison field label
      const fieldLabel = comparisonField.split('.').pop().replace(/([A-Z])/g, ' $1').trim();
      autoTitle = `${fieldLabel} by Device`;
    } else if (deviceMode === 'specific' && selectedDeviceIndex !== null) {
      const devices = getAvailableDevices();
      const device = devices[selectedDeviceIndex];
      if (device) {
        autoTitle = device.label;
        if (selectedNestedArray) {
          autoTitle += ` - ${selectedNestedArray.label}`;
          if (fieldConfig.filterMode === 'group' && fieldConfig.filterField) {
            autoTitle += ` by ${fieldConfig.filterField}`;
          }
        }
      }
    } else if (selectedNestedArray) {
      autoTitle = selectedNestedArray.label;
      if (fieldConfig.filterMode === 'group' && fieldConfig.filterField) {
        autoTitle += ` by ${fieldConfig.filterField}`;
      }
    } else if (selectedMetric) {
      autoTitle = selectedMetric.label;
    }

    // Build dataConfig based on selection source
    const dataConfig = {
      metricPath: dataSourceSelection?.path || selectedMetric?.path || '',
      aggregation: 'count',
      labelField: fieldConfig.labelField,
      valueField: fieldConfig.valueField,
      filterMode: fieldConfig.filterMode,
      filterField: chartAggregationField?.path || fieldConfig.filterField,
      filterValue: fieldConfig.filterValue,
      advancedFilters: fieldConfig.advancedFilters || [],
      // Drill-down configuration
      nestedArrayPath: selectedNestedArray?.path || null,
      nestedFieldFilter: nestedFieldFilter && nestedFieldFilter.fieldPath ? nestedFieldFilter : null,
      parentFilters: drillDownFilters.filter(f => f.fieldPath && f.value),
      // Device selection mode
      deviceMode: deviceMode,
      nestedAggMode: nestedAggMode,
      // Device/item selection
      selectedDeviceIndex: dataSourceSelection?.selectedIndex ?? (deviceMode === 'specific' ? selectedDeviceIndex : null),
      comparisonField: deviceMode === 'compare' ? comparisonField : null,
      // NEW: Store data source browser selection info
      browserSelection: dataSourceSelection ? {
        path: dataSourceSelection.path,
        dataType: dataSourceSelection.dataType,
        filter: dataSourceSelection.filter,
        selectedIndex: dataSourceSelection.selectedIndex,
        itemCount: dataSourceSelection.itemCount
      } : null,
      // Chart aggregation field for chart widgets
      chartAggregationField: chartAggregationField ? {
        path: chartAggregationField.path,
        label: chartAggregationField.label,
        fieldType: chartAggregationField.fieldType,
        isObjectArray: chartAggregationField.isObjectArray,
        sampleFields: chartAggregationField.sampleFields
      } : null,
      // Auto-configure table columns for nested arrays
      tableColumns: (() => {
        // For table widgets with nested array, auto-configure columns
        if (selectedWidgetType === 'table' && selectedNestedArray?.path) {
          const metricPath = dataSourceSelection?.path || selectedMetric?.path || '';
          const data = getDataByPath(dataContext, metricPath, dynamicCategoryMap);

          if (Array.isArray(data) && data.length > 0) {
            // Get first device that has the nested array
            let sampleDevice = null;
            for (const item of data) {
              const nestedValue = getNestedValue(item, selectedNestedArray.path);
              if (Array.isArray(nestedValue) && nestedValue.length > 0 && typeof nestedValue[0] === 'object') {
                sampleDevice = nestedValue[0];
                break;
              }
            }

            if (sampleDevice) {
              // Get nested item fields
              const nestedFields = Object.keys(sampleDevice).filter(f => !TABLE_INTERNAL_FIELDS.includes(f));
              // Create columns: IP first, then nested item fields
              const timestamp = Date.now();
              return [
                {
                  id: `col-${timestamp}-ip`,
                  header: 'IP',
                  fieldPath: 'IP'
                },
                ...nestedFields.map((field, idx) => ({
                  id: `col-${timestamp}-${idx}`,
                  header: field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                  fieldPath: field
                }))
              ];
            }
          }
        }
        return null; // Let user configure manually or use auto-detect
      })()
    };

    onAddWidget(selectedWidgetType, {
      config: {
        title: fieldConfig.title || autoTitle
      },
      dataConfig
    });

    closeWizard();
  };

  // eslint-disable-next-line no-unused-vars
  const filteredMetrics = selectedWidgetType ? getFilteredMetrics(selectedWidgetType) : availableMetrics;
  const nestedArrays = selectedMetric ? getAvailableNestedArrays() : [];
  const drillDownPreview = getDrillDownPreview();

  // Use drill-down data for field analysis if nested array is selected
  const effectiveData = selectedNestedArray ? getDrillDownData() : getMetricData();
  const fieldAnalysis = selectedMetric ? (() => {
    if (!Array.isArray(effectiveData) || effectiveData.length === 0 || typeof effectiveData[0] !== 'object') {
      return { numericFields: [], textFields: [], booleanFields: [], categoryFields: [], nestedFields: [] };
    }

    const sampleItems = effectiveData.slice(0, 10);
    const allKeys = new Set();
    sampleItems.forEach(item => {
      if (typeof item === 'object' && item !== null) {
        Object.keys(item).forEach(key => allKeys.add(key));
      }
    });

    const numericFields = [];
    const textFields = [];
    const booleanFields = [];
    const categoryFields = [];
    const nestedFields = [];

    allKeys.forEach(key => {
      // Skip parent info fields
      if (key.startsWith('_parent')) return;

      const uniqueValues = new Set();
      let numericCount = 0;
      let booleanCount = 0;
      let textCount = 0;
      let objectCount = 0;

      sampleItems.forEach(item => {
        if (item && typeof item === 'object' && item !== null && key in item) {
          const val = item[key];
          if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
            objectCount++;
          } else {
            uniqueValues.add(String(val));
            if (typeof val === 'number') numericCount++;
            else if (typeof val === 'boolean') booleanCount++;
            else if (typeof val === 'string') {
              if (!isNaN(parseFloat(val)) && isFinite(parseFloat(val))) {
                numericCount++;
              } else {
                textCount++;
              }
            }
          }
        }
      });

      const total = sampleItems.length;
      if (objectCount / total > 0.5) nestedFields.push(key);
      else if (numericCount / total > 0.7) numericFields.push(key);
      else if (booleanCount / total > 0.7) booleanFields.push(key);
      else if (textCount / total > 0.7) textFields.push(key);

      if (uniqueValues.size >= 2 && uniqueValues.size <= 15) {
        categoryFields.push({ key, uniqueCount: uniqueValues.size });
      }
    });

    return { numericFields, textFields, booleanFields, categoryFields, nestedFields };
  })() : null;

  const isChartWidget = selectedWidgetType && ['bar', 'line', 'pie', 'donut', 'radar', 'polar', 'bubble', 'scatter'].includes(selectedWidgetType);
  const hasNoNumericValues = fieldAnalysis && fieldAnalysis.numericFields.length === 0;

  return (
    <div className="sidebar" style={{ display: readOnly ? 'none' : undefined }}>
      <div className="sidebar-header">
        {/* In v2 the rail chooses the tab, so the panel only needs to say which it is. */}
        {variant === 'v2' ? (
          <span className="rb2-sidebar-title">
            {{ widgets: 'Widgets', elements: 'Elements', custom: 'Saved widgets' }[activeTab] || 'Library'}
          </span>
        ) : (
        <div className="sidebar-tabs">
          <button
            className={`tab-btn ${activeTab === 'widgets' ? 'active' : ''}`}
            onClick={() => onTabChange('widgets')}
          >
            Widgets
          </button>
          <button
            className={`tab-btn ${activeTab === 'elements' ? 'active' : ''}`}
            onClick={() => onTabChange('elements')}
          >
            Elements
          </button>
          {customWidgets.length > 0 && (
            <button
              className={`tab-btn ${activeTab === 'custom' ? 'active' : ''}`}
              onClick={() => onTabChange('custom')}
            >
              Custom ({customWidgets.length})
            </button>
          )}
        </div>
        )}
        <button className="close-btn" onClick={onToggle} aria-label="Close panel">
          ✕
        </button>
      </div>

      <div className="sidebar-content">
        {/* Wizard Mode - Inline in Sidebar */}
        {showWizard ? (
          <div className="sidebar-wizard" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Step Indicator - Compact for sidebar */}
            <div className="sidebar-wizard-steps">
              {[1, 2, 3, 4].map(step => (
                <div
                  key={step}
                  className={`sidebar-wizard-step ${wizardStep === step ? 'active' : ''} ${wizardStep > step ? 'completed' : ''}`}
                  onClick={() => wizardStep > step && step !== 1 && setWizardStep(step)}
                >
                  <div className="step-dot">{wizardStep > step ? '✓' : step}</div>
                  <div className="step-name">
                    {step === 1 && 'Type'}
                    {step === 2 && 'Data'}
                    {step === 3 && 'Config'}
                    {step === 4 && 'Add'}
                  </div>
                </div>
              ))}
            </div>

            {/* Wizard Content */}
            <div className="sidebar-wizard-content" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              {/* Step 1: Select Chart Type */}
              {wizardStep === 1 && (
                <div className="sidebar-wizard-step-content">
                  <div className="step-header">
                    <h4>Select Chart Type</h4>
                    <p>Choose your visualization</p>
                  </div>
                  <div className="sidebar-widget-grid">
                    {filteredWidgetTypes.map(widget => (
                      <button
                        key={widget.type}
                        className={`sidebar-widget-btn ${selectedWidgetType === widget.type ? 'selected' : ''}`}
                        onClick={() => handleWizardWidgetSelect(widget.type)}
                      >
                        <span className="widget-icon">{widget.icon}</span>
                        <span className="widget-label">{widget.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2: Select Data Source with New Browser */}
              {wizardStep === 2 && (
                <div className="sidebar-wizard-step-content data-source-step" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                  <div className="step-header">
                    <h4>{!dataSourceSelection ? 'Browse Data' : 'Configure Data'}</h4>
                    <p>{!dataSourceSelection
                      ? `Navigate and select data for your ${filteredWidgetTypes.find(w => w.type === selectedWidgetType)?.label}`
                      : 'Refine your selection or expand field fields below.'}
                    </p>
                  </div>

                  {!dataSourceSelection ? (
                    <DataSourceBrowser
                      dataContext={dataContext}
                      widgetType={selectedWidgetType}
                      onSelect={(selection) => {
                        // Store the selection
                        setDataSourceSelection(selection);
                        // Create a metric-like object for compatibility with existing code
                        setSelectedMetric({
                          path: selection.path,
                          label: selection.path || 'Root Data',
                          category: 'Custom Selection',
                          dataType: selection.dataType
                        });
                        // Configure field settings based on selection
                        if (selection.filter) {
                          setFieldConfig(prev => ({
                            ...prev,
                            filterMode: 'query',
                            advancedFilters: [{
                              id: Date.now(),
                              field: selection.filter.field,
                              operator: selection.filter.operator,
                              value: selection.filter.value,
                              logic: 'AND'
                            }]
                          }));
                        }
                        if (selection.selectedIndex !== null) {
                          setDeviceMode('specific');
                          setSelectedDeviceIndex(selection.selectedIndex);
                        }

                        // For table widgets, check if there are nested arrays to drill into
                        if (selectedWidgetType === 'table') {
                          const data = getDataByPath(dataContext, selection.path, dynamicCategoryMap);
                          if (Array.isArray(data) && data.length > 0) {
                            const nestedArrs = discoverNestedArrays(data);
                            if (nestedArrs.length > 0) {
                              // Stay on step 2 to show nested array selection
                              setSelectedNestedArray(null); // Reset nested array selection
                              return; // Don't advance to preview yet
                            }
                          }
                        }

                        // For chart widgets, check if data is nested array that needs aggregation
                        const isChartWidget = ['bar', 'line', 'pie', 'donut', 'radar', 'polar'].includes(selectedWidgetType);
                        if (isChartWidget) {
                          const data = getDataByPath(dataContext, selection.path, dynamicCategoryMap);
                          if (Array.isArray(data) && data.length > 0) {
                            const nestedArrs = discoverNestedArrays(data);
                            if (nestedArrs.length > 0) {
                              // Stay on step 2 to show aggregation field selection for charts
                              setSelectedNestedArray(null); // Reset for reuse
                              setChartAggregationField(null); // Reset aggregation
                              return; // Don't advance to preview yet
                            }
                          }
                        }

                        // Move to preview step
                        setWizardStep(4);
                      }}
                      onCancel={() => closeWizard()}
                    />
                  ) : (
                    <div className="selection-summary-banner" style={{
                      padding: '10px 15px',
                      background: 'var(--background-secondary)',
                      border: '2px solid var(--primary)',
                      borderRadius: 'var(--radius)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '15px',
                      flexShrink: 0
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: '600', textTransform: 'uppercase' }}>Selected Data</span>
                        <code style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>{dataSourceSelection.path || 'Root'}</code>
                      </div>
                      <button
                        onClick={() => {
                          setDataSourceSelection(null);
                          setSelectedNestedArray(null);
                          setChartAggregationField(null);
                        }}
                        className="btn btn-xs btn-outline-secondary"
                        style={{ padding: '2px 8px', fontSize: '0.75rem' }}
                      >
                        Change
                      </button>
                    </div>
                  )}

                  <div className="step-2-detail-container" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

                    {/* Nested Array Selection for Table Widgets */}
                    {selectedWidgetType === 'table' && selectedMetric && !selectedNestedArray && dataSourceSelection && (
                      <div className="nested-array-selection-panel" style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        background: 'var(--background-secondary)',
                        borderRadius: 'var(--radius)',
                        border: '1px solid var(--border)',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0, // Critical for flex scrolling
                        overflow: 'hidden'
                      }}>
                        <div style={{ flexShrink: 0 }}>
                          <div className="section-header" style={{
                            marginBottom: '0.75rem',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            color: 'var(--text-primary)'
                          }}>
                            📂 Filter & Expand Fields (Optional)
                          </div>
                          <div style={{
                            fontSize: '0.8125rem',
                            color: 'var(--text-secondary)',
                            marginBottom: '0.75rem'
                          }}>
                            Select a field to filter and expand. Only devices with this field will be shown. For arrays, each item becomes a separate row.
                          </div>

                          {/* Search input */}
                          <input
                            type="text"
                            value={nestedFieldSearch}
                            onChange={(e) => setNestedFieldSearch(e.target.value)}
                            placeholder="🔍 Search fields..."
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              marginBottom: '0.75rem',
                              borderRadius: 'var(--radius-sm)',
                              border: '1px solid var(--border)',
                              fontSize: '0.8125rem'
                            }}
                          />
                        </div>

                        {(() => {
                          const data = getDataByPath(dataContext, selectedMetric.path, dynamicCategoryMap);
                          if (!Array.isArray(data) || data.length === 0) return null;

                          const allNestedFields = discoverNestedArrays(data);

                          // Filter fields based on search query
                          const nestedFields = nestedFieldSearch
                            ? allNestedFields.filter(field =>
                              field.label.toLowerCase().includes(nestedFieldSearch.toLowerCase()) ||
                              field.path.toLowerCase().includes(nestedFieldSearch.toLowerCase()) ||
                              field.fullLabel.toLowerCase().includes(nestedFieldSearch.toLowerCase())
                            )
                            : allNestedFields;

                          if (allNestedFields.length === 0) {
                            return (
                              <div style={{
                                padding: '0.75rem',
                                background: 'var(--background)',
                                borderRadius: 'var(--radius-sm)',
                                fontSize: '0.8125rem',
                                color: 'var(--text-secondary)',
                                textAlign: 'center'
                              }}>
                                No nested fields found in this data source
                              </div>
                            );
                          }

                          if (nestedFields.length === 0 && nestedFieldSearch) {
                            return (
                              <div style={{
                                padding: '0.75rem',
                                background: 'var(--background)',
                                borderRadius: 'var(--radius-sm)',
                                fontSize: '0.8125rem',
                                color: 'var(--text-secondary)',
                                textAlign: 'center'
                              }}>
                                No fields match "{nestedFieldSearch}"
                              </div>
                            );
                          }

                          // Helper to get icon for field type
                          const getFieldIcon = (fieldType) => {
                            switch (fieldType) {
                              case 'array': return '📋';
                              case 'object': return '📦';
                              case 'string': return '📝';
                              case 'number': return '🔢';
                              case 'boolean': return '✓';
                              default: return '•';
                            }
                          };

                          return (
                            <>
                              <div className="nested-arrays-list" style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.5rem',
                                marginBottom: '0.75rem',
                                flex: 1, // Fill available space
                                overflowY: 'auto', // Scroll internally
                                paddingRight: '0.5rem',
                                minHeight: 0
                              }}>
                                {nestedFields.map(field => (
                                  <button
                                    key={field.path}
                                    className="nested-array-option"
                                    onClick={() => {
                                      setSelectedNestedArray(field);
                                    }}
                                    style={{
                                      padding: '0.75rem',
                                      background: 'var(--background)',
                                      border: '1px solid var(--border)',
                                      borderRadius: 'var(--radius-sm)',
                                      cursor: 'pointer',
                                      textAlign: 'left',
                                      transition: 'all 0.2s',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '0.25rem',
                                      flexShrink: 0 // Don't squash items
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = 'var(--background-hover)';
                                      e.currentTarget.style.borderColor = 'var(--primary)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = 'var(--background)';
                                      e.currentTarget.style.borderColor = 'var(--border)';
                                    }}
                                  >
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.5rem',
                                      fontWeight: '500',
                                      fontSize: '0.875rem'
                                    }}>
                                      <span>{getFieldIcon(field.fieldType)}</span>
                                      <span>{field.label}</span>
                                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 'normal' }}>
                                        ({field.fieldType})
                                      </span>
                                    </div>
                                    {field.isObjectArray && field.sampleFields.length > 0 && (
                                      <div style={{
                                        fontSize: '0.75rem',
                                        color: 'var(--text-secondary)',
                                        marginLeft: '1.75rem'
                                      }}>
                                        Fields: {field.sampleFields.slice(0, 4).join(', ')}
                                        {field.sampleFields.length > 4 && '...'}
                                      </div>
                                    )}
                                    {field.fieldType === 'object' && field.sampleFields.length > 0 && (
                                      <div style={{
                                        fontSize: '0.75rem',
                                        color: 'var(--text-secondary)',
                                        marginLeft: '1.75rem'
                                      }}>
                                        Fields: {field.sampleFields.slice(0, 4).join(', ')}
                                        {field.sampleFields.length > 4 && '...'}
                                      </div>
                                    )}
                                    {(field.fieldType === 'string' || field.fieldType === 'number' || field.fieldType === 'boolean') && field.sampleValue !== undefined && (
                                      <div style={{
                                        fontSize: '0.75rem',
                                        color: 'var(--text-secondary)',
                                        marginLeft: '1.75rem'
                                      }}>
                                        Sample: {String(field.sampleValue)}
                                      </div>
                                    )}
                                  </button>
                                ))}
                              </div>

                              <div className="nested-array-actions" style={{
                                display: 'flex',
                                gap: '0.5rem',
                                marginTop: 'auto', // Push to bottom
                                flexShrink: 0
                              }}>
                                <button
                                  className="btn-secondary"
                                  onClick={() => setDataSourceSelection(null)}
                                  style={{
                                    padding: '0.5rem 1.25rem',
                                    background: 'var(--background)',
                                    color: 'var(--text-primary)',
                                    border: '1px solid var(--border)',
                                    borderRadius: 'var(--radius-sm)',
                                    cursor: 'pointer',
                                    fontWeight: '500'
                                  }}
                                >
                                  ← Back
                                </button>
                                <button
                                  className="btn-primary"
                                  onClick={() => {
                                    // Use data without nested array expansion
                                    setWizardStep(4);
                                  }}
                                  style={{
                                    flex: 1,
                                    padding: '0.5rem 1rem',
                                    background: 'var(--primary)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: 'var(--radius-sm)',
                                    cursor: 'pointer',
                                    fontWeight: '500'
                                  }}
                                >
                                  Use Whole Data →
                                </button>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    )}

                    {/* Show selected nested array and proceed option */}
                    {selectedWidgetType === 'table' && selectedMetric && selectedNestedArray && dataSourceSelection && (
                      <div className="nested-array-selected-panel" style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        background: 'var(--background-secondary)',
                        borderRadius: 'var(--radius)',
                        border: '2px solid var(--primary)'
                      }}>
                        <div className="section-header" style={{
                          marginBottom: '0.75rem',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          color: 'var(--text-primary)'
                        }}>
                          ✓ Selected: {selectedNestedArray.label}
                        </div>
                        <div style={{
                          fontSize: '0.8125rem',
                          color: 'var(--text-secondary)',
                          marginBottom: '0.75rem'
                        }}>
                          Each item in <code style={{ background: 'var(--background)', padding: '0.125rem 0.25rem', borderRadius: '2px' }}>{selectedNestedArray.path}</code> will become a separate row.
                          Parent device information (IP, Hostname) will be included in each row.
                        </div>

                        {/* Optional filter for nested array items (only for arrays of objects) */}
                        {selectedNestedArray.fieldType === 'array' && selectedNestedArray.isObjectArray && selectedNestedArray.sampleFields.length > 0 && (
                          <div style={{
                            padding: '0.75rem',
                            background: 'var(--background)',
                            borderRadius: 'var(--radius-sm)',
                            marginBottom: '0.75rem',
                            border: '1px solid var(--border)'
                          }}>
                            <div style={{ fontSize: '0.8125rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                              🔍 Filter Array Items (Optional)
                            </div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                              Further filter which items to include (e.g., only items where Status = "OK")
                            </div>

                            {!nestedFieldFilter ? (
                              <button
                                onClick={() => setNestedFieldFilter({ fieldPath: '', operator: 'equals', value: '' })}
                                style={{
                                  width: '100%',
                                  padding: '0.5rem',
                                  background: 'var(--background-secondary)',
                                  border: '1px dashed var(--border)',
                                  borderRadius: 'var(--radius-sm)',
                                  cursor: 'pointer',
                                  fontSize: '0.8125rem',
                                  color: 'var(--text-primary)'
                                }}
                              >
                                + Add Filter Condition
                              </button>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                  <select
                                    value={nestedFieldFilter.fieldPath}
                                    onChange={(e) => setNestedFieldFilter({ ...nestedFieldFilter, fieldPath: e.target.value })}
                                    style={{
                                      flex: 1,
                                      padding: '0.375rem',
                                      fontSize: '0.8125rem',
                                      borderRadius: 'var(--radius-sm)',
                                      border: '1px solid var(--border)'
                                    }}
                                  >
                                    <option value="">Select field...</option>
                                    {selectedNestedArray.sampleFields.map(field => (
                                      <option key={field} value={field}>{field}</option>
                                    ))}
                                  </select>
                                  <select
                                    value={nestedFieldFilter.operator}
                                    onChange={(e) => setNestedFieldFilter({ ...nestedFieldFilter, operator: e.target.value })}
                                    style={{
                                      padding: '0.375rem',
                                      fontSize: '0.8125rem',
                                      borderRadius: 'var(--radius-sm)',
                                      border: '1px solid var(--border)'
                                    }}
                                  >
                                    <option value="equals">Equals</option>
                                    <option value="not_equals">Not Equals</option>
                                    <option value="contains">Contains</option>
                                    <option value="starts_with">Starts With</option>
                                    <option value="greater_than">Greater Than</option>
                                    <option value="less_than">Less Than</option>
                                    <option value="is_empty">Is Empty</option>
                                    <option value="is_not_empty">Is Not Empty</option>
                                  </select>
                                </div>
                                {!['is_empty', 'is_not_empty'].includes(nestedFieldFilter.operator) && (
                                  <input
                                    type="text"
                                    value={nestedFieldFilter.value}
                                    onChange={(e) => setNestedFieldFilter({ ...nestedFieldFilter, value: e.target.value })}
                                    placeholder="Enter value..."
                                    style={{
                                      padding: '0.375rem',
                                      fontSize: '0.8125rem',
                                      borderRadius: 'var(--radius-sm)',
                                      border: '1px solid var(--border)'
                                    }}
                                  />
                                )}
                                <button
                                  onClick={() => setNestedFieldFilter(null)}
                                  style={{
                                    padding: '0.25rem 0.5rem',
                                    fontSize: '0.75rem',
                                    background: 'var(--error)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: 'var(--radius-sm)',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Remove Filter
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="nested-array-actions" style={{
                          display: 'flex',
                          gap: '0.5rem'
                        }}>
                          <button
                            className="btn-secondary"
                            onClick={() => {
                              setSelectedNestedArray(null);
                            }}
                            style={{
                              padding: '0.5rem 1rem',
                              background: 'var(--background)',
                              color: 'var(--text-primary)',
                              border: '1px solid var(--border)',
                              borderRadius: 'var(--radius-sm)',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                          >
                            Change Selection
                          </button>
                          <button
                            className="btn-primary"
                            onClick={() => {
                              setWizardStep(4);
                            }}
                            style={{
                              flex: 1,
                              padding: '0.5rem 1rem',
                              background: 'var(--primary)',
                              color: 'white',
                              border: 'none',
                              borderRadius: 'var(--radius-sm)',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                          >
                            Continue with {selectedNestedArray.label} →
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Chart Aggregation Field Selection - shown for chart widgets when nested arrays are available */}
                    {['bar', 'line', 'pie', 'donut', 'radar', 'polar'].includes(selectedWidgetType) && selectedMetric && !chartAggregationField && dataSourceSelection && (
                      (() => {
                        const data = getDataByPath(dataContext, dataSourceSelection.path, dynamicCategoryMap);
                        if (!Array.isArray(data) || data.length === 0) return null;

                        const nestedArrs = discoverNestedArrays(data);
                        if (nestedArrs.length === 0) return null;

                        // Filter to show only suitable fields for chart aggregation
                        const aggregatableFields = nestedArrs.filter(field =>
                          field.fieldType === 'array' ||
                          field.fieldType === 'boolean' ||
                          field.fieldType === 'string' ||
                          (field.fieldType === 'object' && field.sampleFields.length > 0)
                        );

                        if (aggregatableFields.length === 0) return null;

                        return (
                          <div className="chart-aggregation-panel" style={{
                            marginTop: '1rem',
                            padding: '1rem',
                            background: 'var(--background-secondary)',
                            borderRadius: 'var(--radius)',
                            border: '1px solid var(--border)'
                          }}>
                            <div className="section-header" style={{
                              marginBottom: '0.75rem',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              color: 'var(--text-primary)'
                            }}>
                              📊 Select Field to Aggregate
                            </div>
                            <div style={{
                              fontSize: '0.8125rem',
                              color: 'var(--text-secondary)',
                              marginBottom: '0.75rem'
                            }}>
                              Choose a field to count/aggregate for the chart. The chart will show the distribution of values.
                            </div>

                            {/* Search input */}
                            <input
                              type="text"
                              value={nestedFieldSearch}
                              onChange={(e) => setNestedFieldSearch(e.target.value)}
                              placeholder="🔍 Search fields..."
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                marginBottom: '0.75rem',
                                borderRadius: 'var(--radius-sm)',
                                border: '1px solid var(--border)',
                                fontSize: '0.8125rem'
                              }}
                            />

                            <div className="aggregatable-fields-list" style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '0.5rem',
                              marginBottom: '0.75rem',
                              maxHeight: '300px',
                              overflowY: 'auto'
                            }}>
                              {aggregatableFields
                                .filter(field => !nestedFieldSearch || field.label.toLowerCase().includes(nestedFieldSearch.toLowerCase()))
                                .map(field => (
                                  <button
                                    key={field.path}
                                    className="aggregation-field-option"
                                    onClick={() => {
                                      setChartAggregationField(field);
                                    }}
                                    style={{
                                      padding: '0.75rem',
                                      background: 'var(--background)',
                                      border: '1px solid var(--border)',
                                      borderRadius: 'var(--radius-sm)',
                                      cursor: 'pointer',
                                      textAlign: 'left',
                                      transition: 'all 0.2s',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '0.25rem'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = 'var(--background-hover)';
                                      e.currentTarget.style.borderColor = 'var(--primary)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = 'var(--background)';
                                      e.currentTarget.style.borderColor = 'var(--border)';
                                    }}
                                  >
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.5rem',
                                      fontWeight: '500',
                                      fontSize: '0.875rem'
                                    }}>
                                      <span>{field.fieldType === 'array' ? '📋' : field.fieldType === 'boolean' ? '✓' : '📝'}</span>
                                      <span>{field.label}</span>
                                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 'normal' }}>
                                        ({field.fieldType})
                                      </span>
                                    </div>
                                    {field.fieldType === 'array' && field.isObjectArray && field.sampleFields.length > 0 && (
                                      <div style={{
                                        fontSize: '0.75rem',
                                        color: 'var(--text-secondary)',
                                        marginLeft: '1.75rem'
                                      }}>
                                        Array of objects with: {field.sampleFields.slice(0, 4).join(', ')}
                                        {field.sampleFields.length > 4 && '...'}
                                      </div>
                                    )}
                                    {(field.fieldType === 'string' || field.fieldType === 'boolean') && field.sampleValue !== undefined && (
                                      <div style={{
                                        fontSize: '0.75rem',
                                        color: 'var(--text-secondary)',
                                        marginLeft: '1.75rem'
                                      }}>
                                        Sample: {String(field.sampleValue)}
                                      </div>
                                    )}
                                  </button>
                                ))}
                            </div>

                            <div className="aggregation-actions" style={{
                              display: 'flex',
                              gap: '0.5rem'
                            }}>
                              <button
                                className="btn-primary"
                                onClick={() => {
                                  // Skip aggregation and use data as-is
                                  setWizardStep(4);
                                }}
                                style={{
                                  flex: 1,
                                  padding: '0.5rem 1rem',
                                  background: 'var(--primary)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: 'var(--radius-sm)',
                                  cursor: 'pointer',
                                  fontWeight: '500'
                                }}
                              >
                                Skip Aggregation →
                              </button>
                            </div>
                          </div>
                        );
                      })()
                    )}

                    {/* Show selected chart aggregation field and proceed */}
                    {['bar', 'line', 'pie', 'donut', 'radar', 'polar'].includes(selectedWidgetType) && selectedMetric && chartAggregationField && dataSourceSelection && (
                      <div className="chart-aggregation-selected-panel" style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        background: 'var(--background-secondary)',
                        borderRadius: 'var(--radius)',
                        border: '2px solid var(--primary)'
                      }}>
                        <div className="section-header" style={{
                          marginBottom: '0.75rem',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          color: 'var(--text-primary)'
                        }}>
                          ✓ Aggregating: {chartAggregationField.label}
                        </div>
                        <div style={{
                          fontSize: '0.8125rem',
                          color: 'var(--text-secondary)',
                          marginBottom: '0.75rem'
                        }}>
                          {chartAggregationField.fieldType === 'array' && chartAggregationField.isObjectArray
                            ? `Will drill into ${chartAggregationField.label} and count items by their fields.`
                            : `Chart will show the distribution of ${chartAggregationField.label} values.`}
                        </div>

                        <div className="chart-aggregation-actions" style={{
                          display: 'flex',
                          gap: '0.5rem'
                        }}>
                          <button
                            className="btn-secondary"
                            onClick={() => {
                              setDataSourceSelection(null);
                              setSelectedNestedArray(null);
                              setChartAggregationField(null);
                            }}
                            style={{
                              padding: '0.5rem 1.25rem',
                              background: 'var(--background)',
                              color: 'var(--text-primary)',
                              border: '1px solid var(--border)',
                              borderRadius: 'var(--radius-sm)',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                          >
                            ← Back
                          </button>
                          <button
                            className="btn-primary"
                            onClick={() => {
                              setWizardStep(4);
                            }}
                            style={{
                              flex: 1,
                              padding: '0.5rem 1rem',
                              background: 'var(--primary)',
                              color: 'white',
                              border: 'none',
                              borderRadius: 'var(--radius-sm)',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                          >
                            Continue with {chartAggregationField.label} →
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Transition to next steps... */}
                  </div>

                  {/* Wizard Footer - Fixed at bottom - Hide on Step 2 as it has its own footer */}
                  {wizardStep !== 2 && (
                    <div className="sidebar-wizard-footer">

                      {/* Quick Filter Presets - show for network_devices and similar */}
                      {(() => {
                        const presets = getFilterPresetsForMetric(selectedMetric.path, selectedNestedArray?.path);
                        if (presets.length === 0) return null;

                        return (
                          <div className="filter-presets-section">
                            <div className="section-label">⚡ Quick Filters</div>
                            <div className="filter-presets-grid">
                              {presets.slice(0, 8).map(preset => (
                                <button
                                  key={preset.id}
                                  className="filter-preset-btn"
                                  onClick={() => {
                                    // Apply preset configuration
                                    if (preset.compareMode) {
                                      // Comparison preset
                                      setDeviceMode('compare');
                                      setComparisonField(preset.comparisonField);
                                      setSelectedDeviceIndex(null);
                                    } else if (preset.filterMode === 'group') {
                                      // Group by preset
                                      setFieldConfig(prev => ({
                                        ...prev,
                                        filterMode: 'group',
                                        filterField: preset.filterField || preset.groupByField,
                                        filterValue: '',
                                        advancedFilters: []
                                      }));
                                      // Go directly to step 3 for configuration
                                      setWizardStep(3);
                                    } else if (preset.filterMode === 'query' && preset.advancedFilters) {
                                      // Query preset
                                      setFieldConfig(prev => ({
                                        ...prev,
                                        filterMode: 'query',
                                        advancedFilters: preset.advancedFilters.map(f => {
                                          return { ...f, id: Date.now() + Math.random() };
                                        }),
                                        filterField: '',
                                        filterValue: ''
                                      }));
                                      // Go directly to step 3 for configuration
                                      setWizardStep(3);
                                    }
                                  }}
                                  title={preset.description}
                                >
                                  <span className="preset-icon">{preset.icon}</span>
                                  <span className="preset-name">{preset.name}</span>
                                </button>
                              ))}
                            </div>
                            {presets.length > 8 && (
                              <div className="presets-more">+ {presets.length - 8} more presets available in Configure step</div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Device/Item Selection Mode */}
                      <div className="device-selection-section">
                        <div className="section-label">🖥️ Device Selection</div>
                        <div className="device-mode-btns">
                          <button
                            className={`device-mode-btn ${deviceMode === 'all' ? 'active' : ''}`}
                            onClick={() => {
                              setDeviceMode('all');
                              setSelectedDeviceIndex(null);
                              setComparisonField(null);
                            }}
                          >
                            <span className="mode-icon">🔗</span>
                            <span className="mode-label">All Devices</span>
                          </button>
                          <button
                            className={`device-mode-btn ${deviceMode === 'specific' ? 'active' : ''}`}
                            onClick={() => {
                              setDeviceMode('specific');
                              setComparisonField(null);
                            }}
                          >
                            <span className="mode-icon">📍</span>
                            <span className="mode-label">Specific Device</span>
                          </button>
                          <button
                            className={`device-mode-btn ${deviceMode === 'compare' ? 'active' : ''}`}
                            onClick={() => {
                              setDeviceMode('compare');
                              setSelectedDeviceIndex(null);
                            }}
                          >
                            <span className="mode-icon">📊</span>
                            <span className="mode-label">Compare</span>
                          </button>
                        </div>
                      </div>

                      {/* Specific Device Selector */}
                      {deviceMode === 'specific' && (
                        <div className="device-selector-section">
                          <div className="section-label">📍 Select Device</div>
                          <div className="device-list">
                            {getAvailableDevices().filter(d => d.hasSystemData).slice(0, 20).map(device => (
                              <button
                                key={device.index}
                                className={`device-item ${selectedDeviceIndex === device.index ? 'selected' : ''}`}
                                onClick={() => setSelectedDeviceIndex(device.index)}
                              >
                                <span className="device-status">{device.status === 'Up' ? '🟢' : '⚪'}</span>
                                <div className="device-details">
                                  <span className="device-name">{device.label}</span>
                                  {device.sublabel && <span className="device-sub">{device.sublabel}</span>}
                                </div>
                              </button>
                            ))}
                            {getAvailableDevices().filter(d => d.hasSystemData).length === 0 && (
                              <div className="no-devices">No devices with full system data</div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Comparison Field Selector */}
                      {deviceMode === 'compare' && (
                        <div className="comparison-section">
                          <div className="section-label">📊 Compare/Analyze Field Across Devices</div>
                          <select
                            className="comparison-field-select"
                            value={comparisonField || ''}
                            onChange={(e) => setComparisonField(e.target.value || null)}
                            style={{ marginBottom: '0.5rem' }}
                          >
                            <option value="">Select a field to compare...</option>
                            {(() => {
                              const fields = getComparisonFields();
                              let currentCategory = '';
                              const options = [];

                              fields.forEach((field, idx) => {
                                // Add category header
                                if (field.category !== currentCategory) {
                                  currentCategory = field.category;
                                  options.push(
                                    <option key={`cat-${idx}`} disabled style={{ fontWeight: 'bold', backgroundColor: '#f0f0f0' }}>
                                      ── {field.category} ──
                                    </option>
                                  );
                                }
                                options.push(
                                  <option key={field.path} value={field.path}>
                                    {field.fullLabel} {field.type === 'boolean' ? `(${field.sampleValue})` : field.type === 'category' ? `(${field.uniqueValues || '?'} values)` : `(e.g., ${field.sampleValue})`}
                                  </option>
                                );
                              });
                              return options;
                            })()}
                          </select>

                          {comparisonField && (
                            <div className="comparison-info" style={{
                              padding: '0.75rem',
                              background: 'var(--background)',
                              borderRadius: 'var(--radius)',
                              marginTop: '0.5rem'
                            }}>
                              {(() => {
                                const data = getDrillDownData();
                                const fields = getComparisonFields();
                                const selectedField = fields.find(f => f.path === comparisonField);

                                if (!Array.isArray(data) || !selectedField) return 'No data';

                                if (selectedField.type === 'numeric') {
                                  return (
                                    <div>
                                      <strong>📊 Value Comparison</strong>
                                      <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                                        Shows {selectedField.label} value for each device as bars
                                      </div>
                                      <div style={{ marginTop: '0.5rem' }}>{data.length} devices with this field</div>
                                    </div>
                                  );
                                } else if (selectedField.type === 'boolean') {
                                  // Count true vs false
                                  let trueCount = 0, falseCount = 0;
                                  data.forEach(item => {
                                    const val = getNestedValue(item, comparisonField);
                                    if (val === true) trueCount++;
                                    else if (val === false) falseCount++;
                                  });
                                  return (
                                    <div>
                                      <strong>🔘 Yes/No Breakdown</strong>
                                      <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                                        Groups devices by {selectedField.label} status
                                      </div>
                                      <div style={{ marginTop: '0.5rem' }}>
                                        ✅ True: {trueCount} | ❌ False: {falseCount}
                                      </div>
                                    </div>
                                  );
                                } else if (selectedField.type === 'category') {
                                  // Count by category
                                  const counts = {};
                                  data.forEach(item => {
                                    const val = getNestedValue(item, comparisonField);
                                    if (val !== null && val !== undefined) {
                                      counts[val] = (counts[val] || 0) + 1;
                                    }
                                  });
                                  const entries = Object.entries(counts).slice(0, 5);
                                  return (
                                    <div>
                                      <strong>📝 Category Breakdown</strong>
                                      <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                                        Groups devices by {selectedField.label} value
                                      </div>
                                      <div style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                                        {entries.map(([val, count]) => (
                                          <div key={val}>• {val}: {count} devices</div>
                                        ))}
                                        {Object.keys(counts).length > 5 && <div>... and more</div>}
                                      </div>
                                    </div>
                                  );
                                }
                                return `${data.length} devices with this field`;
                              })()}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Filter current data (optional) */}
                      {drillDownFilters.length > 0 && (
                        <div className="drill-down-filters">
                          <div className="section-label">🔍 Filters</div>
                          {drillDownFilters.map((filter, index) => {
                            const { allPaths } = analyzeAllFields();
                            const uniqueValues = filter.fieldPath
                              ? getUniqueValuesForPath(getDataByPath(dataContext, selectedMetric.path, dynamicCategoryMap), filter.fieldPath)
                              : [];

                            return (
                              <div key={filter.id} className="filter-row">
                                <select
                                  value={filter.fieldPath}
                                  onChange={(e) => updateDrillDownFilter(filter.id, { fieldPath: e.target.value, value: '' })}
                                  className="filter-field-select"
                                >
                                  <option value="">Field...</option>
                                  {allPaths.filter(p => p.type !== 'object' && p.type !== 'array').map(p => (
                                    <option key={p.path} value={p.path}>{p.path}</option>
                                  ))}
                                </select>
                                <span>=</span>
                                {uniqueValues.length > 0 && uniqueValues.length <= 30 ? (
                                  <select
                                    value={filter.value}
                                    onChange={(e) => updateDrillDownFilter(filter.id, { value: e.target.value })}
                                    className="filter-value-select"
                                  >
                                    <option value="">Value...</option>
                                    {uniqueValues.map((uv, idx) => (
                                      <option key={idx} value={uv.display}>{uv.display}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <input
                                    type="text"
                                    value={filter.value}
                                    onChange={(e) => updateDrillDownFilter(filter.id, { value: e.target.value })}
                                    placeholder="Value..."
                                    className="filter-value-input"
                                  />
                                )}
                                <button
                                  className="filter-remove-btn"
                                  onClick={() => removeDrillDownFilter(filter.id)}
                                >✕</button>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <button className="add-filter-btn" onClick={addDrillDownFilter}>
                        + Add Filter
                      </button>

                      {/* Nested Arrays - Drill Down Options (only for all/specific modes) */}
                      {deviceMode !== 'compare' && nestedArrays.length > 0 && (
                        <div className="nested-arrays-section">
                          <div className="section-label">📂 Drill Down Into</div>
                          <div className="nested-arrays-grid">
                            {nestedArrays.map(arr => (
                              <button
                                key={arr.path}
                                className="nested-array-btn"
                                onClick={() => selectNestedArray(arr)}
                              >
                                <div className="nested-array-header">
                                  <span className="nested-array-icon">📋</span>
                                  <span className="nested-array-name">{arr.label}</span>
                                </div>
                                <div className="nested-array-meta">
                                  {arr.isObjectArray && (
                                    <span className="nested-array-fields">
                                      {arr.sampleFields.slice(0, 3).join(', ')}
                                      {arr.sampleFields.length > 3 && '...'}
                                    </span>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Use This Data Button */}
                      <div className="drill-down-actions">
                        <button
                          className="use-data-btn primary"
                          onClick={() => setWizardStep(3)}
                          disabled={deviceMode === 'specific' && selectedDeviceIndex === null}
                        >
                          {deviceMode === 'compare' && comparisonField
                            ? 'Compare Devices →'
                            : deviceMode === 'specific' && selectedDeviceIndex !== null
                              ? `Use ${getAvailableDevices()[selectedDeviceIndex]?.label || 'Device'} →`
                              : 'Use This Data →'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Nested Array Selected - Configure Aggregation */}
                  {selectedMetric && selectedNestedArray && (
                    <div className="drill-down-panel">
                      <div className="drill-down-info">
                        <div className="info-label">📋 Nested Array:</div>
                        <div className="info-value">{selectedNestedArray.label}</div>
                        <div className="info-count">{drillDownPreview.count} total items</div>
                        {deviceMode === 'specific' && selectedDeviceIndex !== null && (
                          <div className="info-device">📍 From: {getAvailableDevices()[selectedDeviceIndex]?.label || 'Selected Device'}</div>
                        )}
                      </div>

                      {/* Quick Filter Presets for Nested Arrays */}
                      {(() => {
                        const presets = getFilterPresetsForMetric(selectedMetric.path, selectedNestedArray?.path);
                        if (presets.length === 0) return null;

                        return (
                          <div className="filter-presets-section">
                            <div className="section-label">⚡ Quick Filters</div>
                            <div className="filter-presets-grid">
                              {presets.map(preset => (
                                <button
                                  key={preset.id}
                                  className="filter-preset-btn"
                                  onClick={() => {
                                    if (preset.filterMode === 'group') {
                                      setFieldConfig(prev => ({
                                        ...prev,
                                        filterMode: 'group',
                                        filterField: preset.filterField,
                                        filterValue: '',
                                        advancedFilters: []
                                      }));
                                      setWizardStep(3);
                                    } else if (preset.filterMode === 'query' && preset.advancedFilters) {
                                      setFieldConfig(prev => ({
                                        ...prev,
                                        filterMode: 'query',
                                        advancedFilters: preset.advancedFilters.map(f => ({ ...f, id: Date.now() + Math.random() })),
                                        filterField: '',
                                        filterValue: ''
                                      }));
                                      setWizardStep(3);
                                    }
                                  }}
                                  title={preset.description}
                                >
                                  <span className="preset-icon">{preset.icon}</span>
                                  <span className="preset-name">{preset.name}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Aggregation Mode - Only show if using all devices */}
                      {deviceMode !== 'specific' && (
                        <div className="aggregation-section">
                          <div className="section-label">📊 Aggregation</div>
                          <div className="aggregation-options">
                            <button
                              className={`agg-btn ${nestedAggMode === 'combine' ? 'active' : ''}`}
                              onClick={() => setNestedAggMode('combine')}
                            >
                              <span className="agg-icon">🔗</span>
                              <span className="agg-label">Combine All</span>
                              <span className="agg-desc">Merge from all devices</span>
                            </button>
                            <button
                              className={`agg-btn ${nestedAggMode === 'single' ? 'active' : ''}`}
                              onClick={() => setNestedAggMode('single')}
                            >
                              <span className="agg-icon">1️⃣</span>
                              <span className="agg-label">First Only</span>
                              <span className="agg-desc">Use first device only</span>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Preview of sample data */}
                      {drillDownPreview.sample && drillDownPreview.sample.length > 0 && (
                        <div className="preview-section">
                          <div className="section-label">👁 Preview</div>
                          <div className="preview-items">
                            {drillDownPreview.sample.map((item, idx) => (
                              <div key={idx} className="preview-item">
                                {Object.entries(item)
                                  .filter(([k]) => !k.startsWith('_parent'))
                                  .slice(0, 3)
                                  .map(([k, v]) => (
                                    <span key={k} className="preview-field">
                                      <strong>{k}:</strong> {typeof v === 'boolean' ? (v ? '✓' : '✗') : String(v).substring(0, 20)}
                                    </span>
                                  ))
                                }
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Continue Button */}
                      <div className="drill-down-actions">
                        <button
                          className="use-data-btn secondary"
                          onClick={() => {
                            setSelectedNestedArray(null);
                          }}
                        >
                          ← Back
                        </button>
                        <button
                          className="use-data-btn primary"
                          onClick={() => setWizardStep(3)}
                        >
                          Configure →
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Configure Fields */}
              {wizardStep === 3 && selectedMetric && (
                <div className="sidebar-wizard-step-content">
                  <div className="step-header">
                    <h4>Configure</h4>
                    <p>Customize your widget</p>
                  </div>

                  {/* Widget Title */}
                  <div className="sidebar-field">
                    <label>Title</label>
                    <input
                      type="text"
                      value={fieldConfig.title}
                      onChange={(e) => setFieldConfig(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Widget title"
                    />
                  </div>

                  {isChartWidget && fieldAnalysis && (
                    <>
                      {/* No numeric values warning */}
                      {hasNoNumericValues && (
                        <div className="sidebar-tip">
                          💡 Use "Group By" to create counts from this data
                        </div>
                      )}

                      {/* Filter/Group Mode */}
                      <div className="sidebar-field">
                        <label>Data Mode</label>
                        <div className="sidebar-mode-btns">
                          <button
                            className={`mode-btn ${fieldConfig.filterMode === 'none' ? 'active' : ''}`}
                            onClick={() => setFieldConfig(prev => ({ ...prev, filterMode: 'none', filterField: '', filterValue: '', advancedFilters: [] }))}
                          >
                            📋 All
                          </button>
                          <button
                            className={`mode-btn ${fieldConfig.filterMode === 'group' ? 'active' : ''}`}
                            onClick={() => setFieldConfig(prev => ({ ...prev, filterMode: 'group', filterValue: '' }))}
                          >
                            📊 Group
                          </button>
                          <button
                            className={`mode-btn ${fieldConfig.filterMode === 'filter' ? 'active' : ''}`}
                            onClick={() => setFieldConfig(prev => ({ ...prev, filterMode: 'filter' }))}
                          >
                            🔍 Filter
                          </button>
                          <button
                            className={`mode-btn ${fieldConfig.filterMode === 'query' ? 'active' : ''}`}
                            onClick={() => {
                              setFieldConfig(prev => ({
                                ...prev,
                                filterMode: 'query',
                                advancedFilters: prev.advancedFilters.length ? prev.advancedFilters : [{ id: Date.now(), fieldPath: '', operator: 'equals', value: '', logic: 'AND' }]
                              }));
                            }}
                          >
                            🔎 Query
                          </button>
                        </div>
                      </div>

                      {/* Advanced Query Builder */}
                      {fieldConfig.filterMode === 'query' && (
                        <div className="sidebar-query-builder">
                          <div className="query-builder-header">
                            <span className="query-label">🔎 Advanced Query</span>
                            <span className="query-hint">Filter by nested fields</span>
                          </div>

                          {/* Query Conditions */}
                          <div className="query-conditions">
                            {fieldConfig.advancedFilters.map((filter, index) => {
                              const { allPaths } = analyzeAllFields();
                              const selectedPath = filter.fieldPath;
                              const uniqueValues = selectedPath ? getUniqueValuesForPath(getMetricData(), selectedPath) : [];

                              return (
                                <div key={filter.id} className="query-condition">
                                  {index > 0 && (
                                    <div className="query-logic">
                                      <select
                                        value={filter.logic}
                                        onChange={(e) => updateAdvancedFilter(filter.id, { logic: e.target.value })}
                                        className="logic-select"
                                      >
                                        <option value="AND">AND</option>
                                        <option value="OR">OR</option>
                                      </select>
                                    </div>
                                  )}

                                  <div className="condition-row">
                                    {/* Field Path Selector */}
                                    <select
                                      value={filter.fieldPath}
                                      onChange={(e) => updateAdvancedFilter(filter.id, { fieldPath: e.target.value, value: '' })}
                                      className="field-select"
                                    >
                                      <option value="">Select field...</option>
                                      {allPaths.filter(p => p.type !== 'object' && p.type !== 'array').map(pathInfo => (
                                        <option key={pathInfo.path} value={pathInfo.path}>
                                          {pathInfo.path}
                                        </option>
                                      ))}
                                    </select>

                                    {/* Operator Selector */}
                                    <select
                                      value={filter.operator}
                                      onChange={(e) => updateAdvancedFilter(filter.id, { operator: e.target.value })}
                                      className="operator-select"
                                    >
                                      <option value="equals">=</option>
                                      <option value="not_equals">≠</option>
                                      <option value="contains">contains</option>
                                      <option value="not_contains">!contains</option>
                                      <option value="starts_with">starts</option>
                                      <option value="ends_with">ends</option>
                                      <option value="greater_than">&gt;</option>
                                      <option value="less_than">&lt;</option>
                                      <option value="is_empty">empty</option>
                                      <option value="is_not_empty">!empty</option>
                                      <option value="is_true">true</option>
                                      <option value="is_false">false</option>
                                    </select>

                                    {/* Value Input/Select */}
                                    {!['is_empty', 'is_not_empty', 'is_true', 'is_false'].includes(filter.operator) && (
                                      uniqueValues.length > 0 && uniqueValues.length <= 20 ? (
                                        <select
                                          value={filter.value}
                                          onChange={(e) => updateAdvancedFilter(filter.id, { value: e.target.value })}
                                          className="value-select"
                                        >
                                          <option value="">Select...</option>
                                          {uniqueValues.map((uv, idx) => (
                                            <option key={idx} value={uv.display}>{uv.display}</option>
                                          ))}
                                        </select>
                                      ) : (
                                        <input
                                          type="text"
                                          value={filter.value}
                                          onChange={(e) => updateAdvancedFilter(filter.id, { value: e.target.value })}
                                          placeholder="Value..."
                                          className="value-input"
                                        />
                                      )
                                    )}

                                    {/* Remove Button */}
                                    <button
                                      className="remove-condition-btn"
                                      onClick={() => removeAdvancedFilter(filter.id)}
                                      title="Remove condition"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Add Condition Button */}
                          <button className="add-condition-btn" onClick={addAdvancedFilter}>
                            + Add Condition
                          </button>

                          {/* Query Preview */}
                          {fieldConfig.advancedFilters.some(f => f.fieldPath && f.value) && (
                            <div className="query-preview">
                              <strong>Results:</strong> {(() => {
                                const data = getMetricData();
                                if (!Array.isArray(data)) return '0 items';
                                const filtered = applyAdvancedFilters(data, fieldConfig.advancedFilters);
                                return `${filtered.length} of ${data.length} items`;
                              })()}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Group By Field Selection */}
                      {fieldConfig.filterMode === 'group' && (
                        <div className="sidebar-field">
                          <label>Group By</label>
                          <div className="sidebar-field-chips">
                            {[...fieldAnalysis.booleanFields, ...fieldAnalysis.categoryFields.map(f => f.key)].slice(0, 8).map(field => (
                              <button
                                key={field}
                                className={`field-chip ${fieldConfig.filterField === field ? 'active' : ''}`}
                                onClick={() => setFieldConfig(prev => ({ ...prev, filterField: field }))}
                              >
                                {field}
                              </button>
                            ))}
                          </div>
                          {fieldConfig.filterField && (
                            <div className="sidebar-preview">
                              {(() => {
                                const data = getMetricData();
                                if (!Array.isArray(data)) return '';
                                const grouped = {};
                                data.forEach(item => {
                                  if (item && fieldConfig.filterField in item) {
                                    const val = String(item[fieldConfig.filterField]);
                                    grouped[val] = (grouped[val] || 0) + 1;
                                  }
                                });
                                return Object.entries(grouped).map(([k, v]) => `${k}: ${v}`).join(' • ');
                              })()}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Filter Field Selection */}
                      {fieldConfig.filterMode === 'filter' && (
                        <>
                          <div className="sidebar-field">
                            <label>Filter By</label>
                            <select
                              value={fieldConfig.filterField}
                              onChange={(e) => setFieldConfig(prev => ({ ...prev, filterField: e.target.value, filterValue: '' }))}
                            >
                              <option value="">Select field...</option>
                              {[...fieldAnalysis.booleanFields, ...fieldAnalysis.textFields, ...fieldAnalysis.categoryFields.map(f => f.key)].map(field => (
                                <option key={field} value={field}>{field}</option>
                              ))}
                            </select>
                          </div>
                          {fieldConfig.filterField && (
                            <div className="sidebar-field">
                              <label>Value</label>
                              <select
                                value={fieldConfig.filterValue}
                                onChange={(e) => setFieldConfig(prev => ({ ...prev, filterValue: e.target.value }))}
                              >
                                <option value="">Select value...</option>
                                {getUniqueValues(fieldConfig.filterField).map((val, idx) => (
                                  <option key={idx} value={String(val)}>
                                    {typeof val === 'boolean' ? (val ? 'True' : 'False') : String(val)}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        </>
                      )}

                      {/* Label/Value Fields for non-grouped mode */}
                      {fieldConfig.filterMode === 'none' && !hasNoNumericValues && (
                        <>
                          <div className="sidebar-field">
                            <label>Label Field</label>
                            <select
                              value={fieldConfig.labelField}
                              onChange={(e) => setFieldConfig(prev => ({ ...prev, labelField: e.target.value }))}
                            >
                              <option value="">Auto-detect</option>
                              {fieldAnalysis.textFields.map(field => (
                                <option key={field} value={field}>{field}</option>
                              ))}
                            </select>
                          </div>
                          <div className="sidebar-field">
                            <label>Value Field</label>
                            <select
                              value={fieldConfig.valueField}
                              onChange={(e) => setFieldConfig(prev => ({ ...prev, valueField: e.target.value }))}
                            >
                              <option value="">Auto-detect</option>
                              {fieldAnalysis.numericFields.map(field => (
                                <option key={field} value={field}>{field}</option>
                              ))}
                            </select>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Step 4: Preview & Add */}
              {wizardStep === 4 && (
                <div className="sidebar-wizard-step-content">
                  <div className="step-header">
                    <h4>Ready to Add</h4>
                    <p>Review your widget</p>
                  </div>

                  <div className="sidebar-preview-card">
                    <div className="preview-icon-large">{widgetTypes.find(w => w.type === selectedWidgetType)?.icon}</div>
                    <div className="preview-info">
                      <div className="preview-title">{fieldConfig.title || selectedMetric?.label}</div>
                      <div className="preview-meta">{widgetTypes.find(w => w.type === selectedWidgetType)?.label}</div>
                      <div className="preview-source">{selectedMetric?.label}</div>
                      {fieldConfig.filterMode === 'group' && fieldConfig.filterField && (
                        <div className="preview-config">Grouped by: {fieldConfig.filterField}</div>
                      )}
                      {fieldConfig.filterMode === 'filter' && fieldConfig.filterField && (
                        <div className="preview-config">Filter: {fieldConfig.filterField} = {fieldConfig.filterValue}</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Wizard Footer - Fixed at bottom - Hide on Step 2 as it has its own footer */}
            {wizardStep !== 2 && (
              <div className="sidebar-wizard-footer">
                {wizardStep === 1 && (
                  <button className="btn btn-secondary btn-sm" onClick={() => closeWizard()}>
                    Cancel
                  </button>
                )}
                {wizardStep > 1 && (
                  <button className="btn btn-secondary btn-sm" onClick={() => wizardStep === 2 ? closeWizard() : setWizardStep(wizardStep - 1)}>
                    ← Back
                  </button>
                )}
                <div className="footer-spacer"></div>
                {wizardStep === 3 && (
                  <button className="btn btn-primary btn-sm" onClick={() => setWizardStep(4)}>
                    Next →
                  </button>
                )}
                {wizardStep === 4 && (
                  <button className="btn btn-success btn-sm" onClick={addWidgetFromWizard}>
                    ✓ Add Widget
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Normal Sidebar Content */}
            {activeTab === 'widgets' && (
              <div className="sidebar-widgets-tab">
                <div className="sidebar-section-header">
                  <h4>Add Widget</h4>
                  <p>Select a chart type to get started</p>
                </div>

                {/* Search Box */}
                <div className="widget-search-container" style={{ marginBottom: '12px', padding: '0 12px' }}>
                  <input
                    type="text"
                    placeholder="Search widgets..."
                    value={widgetSearchQuery}
                    onChange={(e) => setWidgetSearchQuery(e.target.value)}
                    className="widget-search-input"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                  />
                </div>

                <div className="sidebar-widgets-organized">
                  {/* Group widgets by category */}
                  {[
                    'Standard Charts',
                    'Findings & Security',
                    'External Risk Scan',
                    'Active Directory',
                    'Microsoft 365',
                    'Network',
                    'Localhost',
                    'Personally Identifiable Information',
                    'Backups',
                    'Admins',
                    'Accounts',
                    'Firewall',
                    'Services',
                    'Open Ports',
                    'Operating System',
                    'Cost Analysis',
                  ].map(staticCategory => {
                    const category = getDynamicCategoryName(staticCategory);
                    // Filter widgets by search query and category
                    let categoryWidgets = filteredWidgetTypes.filter(w => w.category === category);

                    // Apply search filter
                    if (widgetSearchQuery.trim()) {
                      const query = widgetSearchQuery.toLowerCase().trim();
                      categoryWidgets = categoryWidgets.filter(w =>
                        w.label.toLowerCase().includes(query) ||
                        w.description?.toLowerCase().includes(query) ||
                        w.type.toLowerCase().includes(query)
                      );
                    }

                    if (categoryWidgets.length === 0) return null;

                    const isCollapsed = collapsedCategories.has(category);
                    const toggleCategory = () => {
                      setCollapsedCategories(prev => {
                        const newSet = new Set(prev);
                        if (newSet.has(category)) {
                          newSet.delete(category);
                        } else {
                          newSet.add(category);
                        }
                        return newSet;
                      });
                    };

                    return (
                      <div key={category} className="widget-category-section">
                        <h4
                          className="widget-category-title"
                          onClick={toggleCategory}
                          style={{
                            cursor: 'pointer',
                            userSelect: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 12px',
                            margin: '8px 0 4px 0',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '4px',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#e8e8e8'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                        >
                          <span style={{ fontSize: '12px', transition: 'transform 0.2s', transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>
                            ▼
                          </span>
                          <span>{category}</span>
                          <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#666', fontWeight: 'normal' }}>
                            ({categoryWidgets.length})
                          </span>
                        </h4>
                        {!isCollapsed && (
                          <div className="sidebar-widget-grid">
                            {categoryWidgets.map(widget => (
                              <button
                                key={widget.type}
                                className="sidebar-widget-btn"
                                onClick={() => handleWidgetButtonClick(widget.type)}
                                title={widget.description}
                              >
                                <span className="widget-icon">{widget.icon}</span>
                                <span className="widget-label">{widget.label}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {activeTab === 'elements' && (
              <div className="sidebar-elements-tab">
                <div className="sidebar-section-header">
                  <h4>Add Element</h4>
                  <p>Add content to your report</p>
                </div>
                <div className="sidebar-widget-grid">
                  {elementTypes.map(element => (
                    <button
                      key={element.type}
                      className="sidebar-widget-btn"
                      onClick={() => onAddElement(element.type)}
                      title={element.description}
                    >
                      <span className="widget-icon">{element.icon}</span>
                      <span className="widget-label">{element.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'custom' && (
              <div className="sidebar-elements-tab">
                <div className="sidebar-section-header">
                  <h4>Custom Widgets</h4>
                  <p>Saved reusable blocks for this template</p>
                </div>
                {customWidgets.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                    <p style={{ fontSize: '14px' }}>No custom widgets yet.</p>
                    <p style={{ fontSize: '12px' }}>Right-click any block and select<br /><strong>"💾 Save as Custom Widget"</strong></p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '8px 12px' }}>
                    {customWidgets.map(cw => (
                      <div
                        key={cw.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '10px 12px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          backgroundColor: '#fff'
                        }}
                        onClick={() => onAddCustomWidget(cw.id)}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0f4ff'; e.currentTarget.style.borderColor = '#3b82f6'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
                        title={`Click to add "${cw.name}" to the current page`}
                      >
                        <span style={{ fontSize: '24px' }}>{cw.icon}</span>
                        <div style={{ flex: 1, overflow: 'hidden' }}>
                          <div style={{ fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cw.name}</div>
                          <div style={{ fontSize: '11px', color: '#999' }}>
                            {cw.isMultiBlock
                              ? `Multi-block • ${cw.blockData?.blocks?.length || 0} blocks`
                              : `${cw.blockData?.type === 'widget' ? 'Widget' : 'Element'} • ${cw.blockData?.widgetType || cw.blockData?.elementType || 'block'}`
                            }
                          </div>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete custom widget "${cw.name}"?`)) onDeleteCustomWidget(cw.id); }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#dc2626',
                            cursor: 'pointer',
                            fontSize: '16px',
                            padding: '4px',
                            borderRadius: '4px',
                            lineHeight: 1
                          }}
                          title="Delete custom widget"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Modern Toolbar Component
const Toolbar = ({
  pages,
  currentPageId,
  onPageChange,
  onAddPage,
  onInsertPage,
  onMovePage,
  onReorderPage,
  onRemovePage,
  onTogglePageExport,
  onToggleSidebar,
  onToggleProperties,
  sidebarVisible,
  propertiesVisible,
  theme,
  onThemeChange,
  showGrid,
  onToggleGrid,
  showGlobalVariablesPanel,
  setShowGlobalVariablesPanel,
  setShowStatusColorsModal,
  // Template Saving
  onSaveTemplate,
  savingTemplate,
  builderMode = 'report', // 'template' or 'report'
  currentTemplateId,
  handleUpdateTemplate,
  handleDeleteTemplate,
  onAIBuild,
  onRegenerateWithAI,
  isAiTemplate,
  onGenerateWrittenReport,
  // Export
  onExportPDF,
  filePath,
  orientation,
  handleOrientationToggle,
  readOnly = false,
  // v2 frame: the document actions move to BuilderHeaderV2, and this renders only the
  // canvas bar. The page navigator below is shared so both interfaces reorder, insert
  // and remove pages through the same code.
  variant = 'v1',
  zoomLevel = 1,
  onZoomIn,
  onZoomOut,
  onZoomReset
}) => {
  const [showPageDropdown, setShowPageDropdown] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const [draggedPageIndex, setDraggedPageIndex] = useState(null);
  const isDraggingRef = useRef(false); // Prevents click-outside from closing dropdown during drag
  const pageNavigationRef = useRef(null);
  const activeTabRef = useRef(null);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const MAX_VISIBLE_TABS = 8;

  const handleDropdownToggle = (e) => {
    e.stopPropagation();
    if (!showPageDropdown && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
    setShowPageDropdown(!showPageDropdown);
  };

  const currentPageIndex = pages.findIndex(p => p.id === currentPageId);
  const showCompactView = pages.length > MAX_VISIBLE_TABS;

  // Auto-scroll active tab into view
  useEffect(() => {
    if (activeTabRef.current && pageNavigationRef.current && !showCompactView) {
      const tab = activeTabRef.current;
      const container = pageNavigationRef.current;
      const tabLeft = tab.offsetLeft;
      const tabWidth = tab.offsetWidth;
      const containerWidth = container.offsetWidth;
      const scrollLeft = container.scrollLeft;

      // Check if tab is outside visible area
      if (tabLeft < scrollLeft) {
        container.scrollTo({ left: tabLeft - 10, behavior: 'smooth' });
      } else if (tabLeft + tabWidth > scrollLeft + containerWidth) {
        container.scrollTo({ left: tabLeft + tabWidth - containerWidth + 10, behavior: 'smooth' });
      }
    }
  }, [currentPageId, showCompactView]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // NEVER close the dropdown while dragging a page - this destroys the drop targets
      if (isDraggingRef.current) return;

      // Check if click is outside the toggle button AND outside the portal dropdown
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !event.target.closest('.page-dropdown')
      ) {
        setShowPageDropdown(false);
      }
    };

    if (showPageDropdown) {
      // Use 'click' instead of 'mousedown' so the handler fires AFTER a complete
      // mousedown+mouseup cycle. This prevents the dropdown from closing during
      // drag initiation (mousedown fires before dragstart, but click does NOT fire
      // during a drag operation since the mouseup happens on a different element).
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showPageDropdown]);

  // Keyboard navigation for compact view
  useEffect(() => {
    if (!showCompactView) {
      return;
    }

    const handleKeyDown = (event) => {
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        return; // Don't interfere with text input
      }

      if (event.key === 'ArrowLeft' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        if (currentPageIndex > 0) {
          onPageChange(pages[currentPageIndex - 1].id);
        }
      } else if (event.key === 'ArrowRight' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        if (currentPageIndex < pages.length - 1) {
          onPageChange(pages[currentPageIndex + 1].id);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showCompactView, currentPageIndex, pages, onPageChange]);

  const pageNav = (
    <div className="page-navigation-compact" ref={dropdownRef} style={{ marginLeft: 'auto', marginRight: 'auto', flex: '0 1 auto' }}>
      <button
        className="page-nav-arrow"
        onClick={() => onPageChange(pages[currentPageIndex - 1]?.id)}
        disabled={currentPageIndex <= 0}
        title="Previous Page"
      >
        ‹
      </button>

      <div className="page-selector-wrapper" ref={buttonRef} onClick={handleDropdownToggle}>
        <button
          className="page-selector-btn"
          onClick={(e) => e.preventDefault()}
        >
          <div className="page-selector-text">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span
                style={{
                  opacity: pages[currentPageIndex]?.isExcluded ? 0.6 : 1,
                  textDecoration: 'none'
                }}
              >
                {`Page ${currentPageIndex + 1}`}
                {pages[currentPageIndex]?.isExcluded && <span style={{ fontSize: '0.85em', fontStyle: 'italic', marginLeft: '6px' }}>(Excluded)</span>}
              </span>
            </div>
          </div>
          <span className="page-selector-count">{currentPageIndex + 1} / {pages.length}</span>
          <span className="page-selector-arrow">{showPageDropdown ? '▲' : '▼'}</span>
        </button>

        {showPageDropdown && (
          <div
            className="page-dropdown"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              top: dropdownPos.top,
              left: dropdownPos.left,
              maxWidth: '300px',
              position: 'fixed',
              zIndex: 99999
            }}
          >
            <div className="page-dropdown-header">
              <span>Pages ({pages.length})</span>
              {!readOnly && (
                <button
                  className="page-dropdown-close"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddPage();
                    setShowPageDropdown(false);
                  }}
                  title="Add New Page"
                >
                  +
                </button>
              )}
            </div>
            <div className="page-dropdown-list">
              {pages.map((page, index) => {
                const hasClonedBlocks = Array.isArray(page.blocks) && page.blocks.some(b => b.isClonedBlock);
                const isContinuationPage = page.isContinuationPage || hasClonedBlocks || !!page.continuationOf;

                return (
                  <div
                    key={page.id}
                    className={`page-dropdown-item ${currentPageId === page.id ? 'active' : ''} ${isContinuationPage ? 'continuation' : ''}`}
                    draggable={!readOnly && !isContinuationPage}
                    onDragStart={(e) => {
                      if (readOnly || isContinuationPage) return;
                      isDraggingRef.current = true;
                      setDraggedPageIndex(index);
                      e.dataTransfer.effectAllowed = 'move';
                      // Store the page ID (not index!) for cross-filter-safe reordering
                      e.dataTransfer.setData('text/plain', page.id);
                    }}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                    }}
                    onDragOver={(e) => {
                      e.preventDefault(); // Necessary to allow dropping
                      e.dataTransfer.dropEffect = 'move';
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      // Get the source page ID from dataTransfer
                      let sourcePageId = e.dataTransfer.getData('text/plain');

                      // Fallback: if dataTransfer failed, use the draggedPageIndex state to get the page ID
                      if (!sourcePageId && draggedPageIndex !== null) {
                        sourcePageId = pages[draggedPageIndex]?.id;
                      }

                      if (!sourcePageId || sourcePageId === page.id) {
                        setDraggedPageIndex(null);
                        return;
                      }

                      // Pass page IDs (not indices!) to reorderPage
                      onReorderPage(sourcePageId, page.id);
                      setDraggedPageIndex(null);
                    }}
                    onDragEnd={() => {
                      isDraggingRef.current = false; // Re-enable dropdown close
                      setDraggedPageIndex(null);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onPageChange(page.id);
                      setShowPageDropdown(false);
                    }}
                    style={{
                      opacity: draggedPageIndex === index ? 0.3 : 1, // Feedback during active drag
                      transition: 'opacity 0.2s',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {!readOnly && !isContinuationPage && (
                      <span
                        className="page-drag-handle"
                        style={{
                          cursor: 'grab',
                          marginRight: '6px',
                          opacity: 0.5,
                          display: 'inline-block',
                          padding: '0 2px'
                        }}
                        title="Drag to reorder"
                      >
                        ⋮⋮
                      </span>
                    )}
                    <span className="page-dropdown-number">{index + 1}</span>
                    <span className="page-dropdown-name" style={{
                      opacity: page.isExcluded ? 0.6 : 1,
                      textDecoration: 'none',
                      flex: 1
                    }}>
                      {`Page ${index + 1}`}
                      {page.isExcluded && <span style={{ fontSize: '0.85em', fontStyle: 'italic', marginLeft: '6px' }}>(Excluded)</span>}
                    </span>
                    {isContinuationPage && <span className="page-dropdown-badge">Continuation</span>}

                    {!isContinuationPage && !readOnly && pages.length > 1 && (
                      <div className="page-dropdown-actions" style={{ display: 'flex', gap: '2px', marginLeft: 'auto', alignItems: 'center' }}>
                        {/* Move Up button */}
                        <button
                          className="page-dropdown-action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (index > 0) {
                              // Pass page IDs, not filtered indices
                              onReorderPage(page.id, pages[index - 1].id);
                            }
                          }}
                          disabled={index === 0}
                          title="Move Page Up"
                          style={{
                            border: 'none', background: 'none', cursor: index === 0 ? 'default' : 'pointer',
                            opacity: index === 0 ? 0.25 : 0.7, padding: '0 3px', fontSize: '12px', lineHeight: 1,
                            color: 'inherit'
                          }}
                        >
                          ▲
                        </button>
                        {/* Move Down button */}
                        <button
                          className="page-dropdown-action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (index < pages.length - 1) {
                              // Pass page IDs, not filtered indices
                              onReorderPage(page.id, pages[index + 1].id);
                            }
                          }}
                          disabled={index === pages.length - 1}
                          title="Move Page Down"
                          style={{
                            border: 'none', background: 'none', cursor: index === pages.length - 1 ? 'default' : 'pointer',
                            opacity: index === pages.length - 1 ? 0.25 : 0.7, padding: '0 3px', fontSize: '12px', lineHeight: 1,
                            color: 'inherit'
                          }}
                        >
                          ▼
                        </button>
                        <button
                          className="page-dropdown-action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onInsertPage(index);
                            setShowPageDropdown(false);
                          }}
                          title="Insert New Page Above"
                          style={{ border: 'none', background: 'none', cursor: 'pointer', opacity: 0.7, padding: '0 4px', fontWeight: 'bold' }}
                        >
                          +
                        </button>
                        <button
                          className="page-dropdown-remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemovePage(page.id);
                          }}
                          title="Remove Page"
                          style={{ marginLeft: '4px' }}
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <button
        className="page-nav-arrow"
        onClick={() => onPageChange(pages[currentPageIndex + 1]?.id)}
        disabled={currentPageIndex >= pages.length - 1}
        title="Next Page"
      >
        ›
      </button>

      {!readOnly && (
        <button
          className={variant === 'v2' ? 'v2-btn rb2-addpage' : 'toolbar-btn primary'}
          // style={{ marginLeft: '10px' }}
          onClick={(e) => {
            e.stopPropagation();
            onAddPage();
            setShowPageDropdown(false);
          }}
          title="Add New Page"
        >
          {variant === 'v2' && <TbPlus size={14} />}
          <span className="btn-text">{variant === 'v2' ? 'Add page' : 'Add Page'}</span>
        </button>
      )}

    </div>
  );

  if (variant === 'v2') {
    return (
      <CanvasBarV2
        pageNav={pageNav}
        readOnly={readOnly}
        orientation={orientation}
        onOrientationToggle={handleOrientationToggle}
        showGrid={showGrid}
        onToggleGrid={onToggleGrid}
        zoomLevel={zoomLevel}
        onZoomIn={onZoomIn}
        onZoomOut={onZoomOut}
        onZoomReset={onZoomReset}
        propertiesVisible={propertiesVisible}
        onToggleProperties={onToggleProperties}
      />
    );
  }

  return (
    <div className="toolbar">
      <div className="toolbar-section">
        {!readOnly && (
          <button
            className="toolbar-btn primary"
            onClick={onToggleSidebar}
            title="Toggle Sidebar"
          >
            <span className="btn-icon">➕</span>
            <span className="btn-text">Add Content</span>
          </button>
        )}
        {!readOnly && (
          <button
            className="toolbar-btn secondary"
            style={{ marginLeft: '10px' }}
            onClick={handleOrientationToggle}
            title={`Switch to ${orientation === 'portrait' ? 'Landscape' : 'Portrait'}`}
          >
            <span className="btn-icon">{orientation === 'portrait' ? '↔️' : '↕️'}</span>
            <span className="btn-text">{orientation === 'portrait' ? 'Landscape' : 'Portrait'}</span>
          </button>
        )}

        {/* Unified Page Navigation Dropdown */}
        <div className="toolbar-section" style={{ flex: 1, overflow: 'visible', position: 'relative' }}>
          {pageNav}
        </div>
      </div>

      <div className="toolbar-section">
        <button
          className={`toolbar-btn ${showGrid ? 'active' : ''}`}
          onClick={onToggleGrid}
          title="Toggle Grid"
        >
          <span className="btn-icon">⊞</span>
          <span className="btn-text">Grid</span>
        </button>

        {!readOnly && (
          <button
            className="toolbar-btn"
            onClick={onToggleProperties}
            title="Toggle Properties"
          >
            <span className="btn-icon">⚙️</span>
            <span className="btn-text">Properties</span>
          </button>
        )}

        {/* Save Template button - show if handler provided, regardless of mode */}
        {(!readOnly && onSaveTemplate) && (
          currentTemplateId ? (
            <div className="d-flex align-items-center gap-2" style={{ position: 'relative', zIndex: 1000 }}>
              <SplitButton
                key="save-split-button"
                id="save-split-button"
                variant="primary"
                title={savingTemplate ? 'Saving...' : 'Save Changes'}
                onClick={handleUpdateTemplate}
                disabled={savingTemplate}
                className="save-template-split-btn"
                drop="down"
                align="end"
                menuAlign="right"
              >
                <Dropdown.Item onClick={onSaveTemplate} disabled={savingTemplate}>Save as New...</Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={handleDeleteTemplate} className="text-danger" disabled={savingTemplate}>Delete Template</Dropdown.Item>
              </SplitButton>
            </div>
          ) : (
            <button
              className="toolbar-btn"
              onClick={onSaveTemplate}
              disabled={savingTemplate}
              title="Save Template"
            >
              <span className="btn-icon">💾</span>
              <span className="btn-text">{savingTemplate ? 'Saving...' : 'Save Template'}</span>
            </button>
          )
        )}

        {!readOnly && onAIBuild && (
          <button
            className="toolbar-btn primary"
            onClick={isAiTemplate && onRegenerateWithAI ? onRegenerateWithAI : onAIBuild}
            title={isAiTemplate ? 'Re-run the AI analysis on the currently selected scan data — refreshes charts, scorecards, scores, and reviews' : 'AI Build'}
          >
            <span className="btn-text">{isAiTemplate ? '✨ Regenerate with AI' : 'AI Build (Beta)'}</span>
          </button>
        )}



        {/* Export PDF button - only show in report mode */}
        {builderMode === 'report' && (
          <button
            className="toolbar-btn success"
            onClick={onExportPDF}
            title="Export Report"
          >
            <span className="btn-icon">📥</span>
            <span className="btn-text">Export Report</span>
          </button>
        )}
      </div>
    </div>
  );
};

// Page Canvas Component
const PageCanvas = ({
  page,
  pageIndex = 0,
  totalPages = 1,
  selectedBlock,
  selectedBlocks = [],
  onBlockSelect,
  onBlockMove,
  onBlockResize,
  onBlockRemove,
  getBlockData,
  showGrid,
  gridConfig,
  editingBlocks,
  onToggleEditMode,
  onUpdateCustomData,
  onDataConfigUpdate,
  onConfigUpdate,
  isExporting,
  readOnly,
  pages,
  currentPageId,
  onPageChange,
  checkWidgetDataAvailability,
  metricsData,
  builderMode,
  effectiveScanData,
  headerElements = [],
  footerElements = [],
  onHeaderFooterSelect = null,
  onHeaderFooterMove = null,
  onHeaderFooterResize = null,
  onHeaderFooterRemove = null,
  onAddSection = null,
  scanGroups,
  allCategories,

  handleContextMenu = null,
  mspInfo,
  clientInfo,
  previewData,
  orientation,
  theme
}) => {
  const canvasRef = useRef(null);

  // Recursive function to render layout nodes (Section -> Row -> Column -> Block)
  const renderLayoutNode = (node, parentContext = {}) => {
    // Determine if this node is a header or footer, OR if it's already inside one
    const isHeaderFooter = (node.type === 'element' || node.type === 'section') && (node.elementType === 'header' || node.elementType === 'footer');
    const isNestedInHeaderFooter = parentContext.isNestedInHeaderFooter || isHeaderFooter;

    // If it's a leaf node (widget/element) that is NOT a container
    if (node.type !== 'section' && node.type !== 'row' && node.type !== 'column') {
      return (
        <Block
          key={`block-${node.id}`}
          block={node}
          isSelected={selectedBlock === node.id || (typeof selectedBlock === 'string' && selectedBlock.startsWith(node.id + '.'))}
          isMultiSelected={selectedBlocks.includes(node.id)}
          selectedBlock={selectedBlock}
          isHidden={!isExporting && false} // Layout items are usually visible. Logic for visibility might need enhancement.
          pageNumber={pageIndex + 1}
          onContextMenu={handleContextMenu}
          onSelect={onHeaderFooterSelect || onBlockSelect}
          onMove={onHeaderFooterMove || onBlockMove}
          onResize={onHeaderFooterResize || onBlockResize}
          onRemove={onHeaderFooterRemove || onBlockRemove}
          getBlockData={getBlockData}
          gridConfig={gridConfig}
          isEditing={editingBlocks.has(node.id)}
          onToggleEditMode={onToggleEditMode}
          orientation={orientation}
          onUpdateCustomData={onUpdateCustomData}
          onDataConfigUpdate={onDataConfigUpdate}
          onConfigUpdate={onConfigUpdate}
          isExporting={isExporting}
          readOnly={readOnly}
          page={page}
          checkWidgetDataAvailability={checkWidgetDataAvailability}
          metricsData={metricsData}
          builderMode={builderMode}
          effectiveScanData={effectiveScanData}
          pages={pages}
          currentPageId={currentPageId}
          onPageChange={onPageChange}
          mspInfo={mspInfo}
          clientInfo={clientInfo}
          previewData={previewData}
          isNestedInHeaderFooter={parentContext.isNestedInHeaderFooter} // Pass explicit flag for children of H/F
          theme={theme}
          scanGroups={scanGroups}
          allCategories={allCategories}
        >
          {node.children && node.children.length > 0 && node.children.map(child => renderLayoutNode(child, { isNestedInHeaderFooter }))}
        </Block>
      );
    }

    // Render children recursively
    const renderChildren = (children) => {
      return children && children.map(child => renderLayoutNode(child, { isNestedInHeaderFooter }));
    };

    const commonProps = {
      node,
      onSelect: onHeaderFooterSelect || onBlockSelect,
      selectedId: selectedBlock,
      readOnly,
      renderChildren
    };

    if (node.type === 'section') return <Section key={node.id} {...commonProps} />;
    if (node.type === 'row') return <Row key={node.id} {...commonProps} />;
    if (node.type === 'column') return <Column key={node.id} {...commonProps} />;

    return null;
  };

  // Helper function to check if header/footer should be visible on this page
  const shouldShowOnPage = (block, currentPageIndex, totalPageCount) => {
    const pageRange = block.config?.layout?.pageRange;
    if (!pageRange) return true; // No range specified, show on all pages

    const currentPage = currentPageIndex + 1; // Convert to 1-based
    const startPage = pageRange.startPage || 1;

    // Calculate end page
    let endPage;
    if (pageRange.endPage === 'last') {
      endPage = totalPageCount;
    } else if (pageRange.endPage === 'last-1') {
      endPage = totalPageCount - 1;
    } else if (pageRange.endPage === 'last-2') {
      endPage = totalPageCount - 2;
    } else {
      endPage = parseInt(pageRange.endPage) || totalPageCount;
    }

    // Check if current page is within range
    return currentPage >= startPage && currentPage <= endPage;
  };

  const handleCanvasClick = (e) => {
    // Deselect block when clicking on canvas, page background, or page content (but not on blocks)
    const target = e.target;

    // Check if click is on a block or its controls - if so, don't deselect
    const isBlock = target.closest('.block');
    const isResizeHandle = target.classList.contains('resize-handle');
    const isRemoveButton = target.classList.contains('block-remove-btn');

    // Only deselect if NOT clicking on a block or its controls
    if (!isBlock && !isResizeHandle && !isRemoveButton) {
      // Deselect - click is outside any block
      onBlockSelect(null);
    }
  };

  return (
    <div
      className="page-canvas"
      ref={canvasRef}
      onClick={handleCanvasClick}
    >
      <div
        className="page-background"
        data-page-id={page.id}
        onClick={handleCanvasClick}
        onContextMenu={(e) => {
          if (handleContextMenu) {
            handleContextMenu(e, null);
          }
        }}
        style={{
          width: `${gridConfig.pageWidth}px`,
          height: `${gridConfig.pageHeight}px`,
          minHeight: `${gridConfig.pageHeight}px`
        }}
      >
        <div
          className="page-content"
          onClick={handleCanvasClick}
          style={{
            width: `${gridConfig.pageWidth}px`,
            height: `${gridConfig.pageHeight}px`,
            minHeight: `${gridConfig.pageHeight}px`
          }}
        >
          {/* Page number (document-level "Show page numbers" setting). Rendered in the
              footer band of every page — including the cover — and captured in export.
              The number is this page's position in the report (pageIndex + 1). */}
          {theme?.pageNumbers?.enabled && (() => {
            const pos = theme.pageNumbers.position || 'center';
            const fmt = theme.pageNumbers.format || 'page_x';
            const n = pageIndex + 1;
            const label = fmt === 'x_of_y' ? `${n} of ${totalPages}` : fmt === 'x' ? `${n}` : `Page ${n}`;
            return (
              <div
                className="page-number"
                style={{
                  position: 'absolute',
                  bottom: '14px',
                  left: pos === 'left' ? '24px' : (pos === 'center' ? '0' : 'auto'),
                  right: pos === 'right' ? '24px' : (pos === 'center' ? '0' : 'auto'),
                  textAlign: pos,
                  width: pos === 'center' ? '100%' : 'auto',
                  fontSize: '11px',
                  color: '#64748b',
                  pointerEvents: 'none',
                  zIndex: 1
                }}
              >
                {label}
              </div>
            );
          })()}

          {/* Grid Overlay - positioned inside page-content to align with blocks */}
          {showGrid && (
            <div className="grid-overlay" onClick={handleCanvasClick}>
              {Array.from({ length: gridConfig.columns + 1 }, (_, i) => (
                <div
                  key={`v-${i}`}
                  className="grid-line vertical"
                  style={{
                    left: `${i * gridConfig.cellWidth}px`,
                    width: '1px',
                    height: `${gridConfig.pageHeight}px`
                  }}
                />
              ))}
              {Array.from({ length: gridConfig.rows + 1 }, (_, i) => (
                <div
                  key={`h-${i}`}
                  className="grid-line horizontal"
                  style={{
                    top: `${i * gridConfig.cellHeight}px`,
                    height: '1px',
                    width: `${gridConfig.pageWidth}px`
                  }}
                />
              ))}
            </div>
          )}
          {/* Render Header elements at the top of the page, inside page-content aligned with grid at (0, 0) */}
          {headerElements && headerElements.length > 0 && (
            <>
              {headerElements.map(headerBlock => {
                // Check if this header should be visible on this page
                const isVisibleOnThisPage = shouldShowOnPage(headerBlock, pageIndex, totalPages);

                return (
                  <div
                    key={`header-${headerBlock.id}-page-${page.id}`}
                    style={{
                      position: 'relative',
                      // height: Removed to let inner block control height
                      width: '100%',
                      opacity: isVisibleOnThisPage ? 1 : (isExporting ? 0 : 0.4),
                      pointerEvents: isVisibleOnThisPage ? 'auto' : 'auto', // Keep clickable for editing
                      display: (!isVisibleOnThisPage && isExporting) ? 'none' : 'block' // Hide completely when exporting
                    }}
                  >
                    {renderLayoutNode(headerBlock)}
                  </div>
                );
              })}
            </>
          )}

          {/* Add Header Section Button */}
          {builderMode === 'edit' && onAddSection && (
            <div
              className="add-section-btn-container header-add-btn"
              style={{
                width: '100%',
                padding: '10px',
                display: 'flex',
                justifyContent: 'center',
                borderBottom: '1px dashed #e5e7eb',
                marginBottom: '10px',
                position: 'relative',
                zIndex: 5
              }}
            >
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddSection('header');
                }}
                title="Add New Header Section"
              >
                + Add Header Section
              </button>
            </div>
          )}

          {/* Render all blocks (excluding Header and Footer elements that are rendered separately) */}
          {page.blocks
            .filter(block => {
              // 1. Skip Header/Footer elements (handled separately)
              if ((block.type === 'element' || block.type === 'section') && (block.elementType === 'header' || block.elementType === 'footer')) {
                return false;
              }

              // 2. Dependency Logic: Hide element if parent widget is missing or empty
              if (block.config?.dependentBlockId && !page.isPlaceholder && !page.isContinuationPage) {
                // Find parent widget across ALL pages
                let parentWidget = null;

                // Search all pages for the parent widget
                if (pages && Array.isArray(pages)) {
                  for (const p of pages) {
                    if (p.blocks) {
                      const found = p.blocks.find(b => b.id === block.config.dependentBlockId);
                      if (found) {
                        parentWidget = found;
                        break;
                      }
                    }
                  }
                }

                // If parent widget doesn't exist anywhere (deleted), hide this element
                if (!parentWidget) return false;

                // Check parent availability using getBlockData
                const parentData = getBlockData(parentWidget);
                if (parentData && parentData[PLACEHOLDER_FLAG]) {
                  return false;
                }
              }

              return true;
            })
            .map(block => renderLayoutNode(block))}

          {page.blocks.length === 0 &&
            (!headerElements || headerElements.filter(h => shouldShowOnPage(h, pageIndex, totalPages)).length === 0) &&
            (!footerElements || footerElements.filter(f => shouldShowOnPage(f, pageIndex, totalPages)).length === 0) &&
            !isExporting && (
              <div className="empty-canvas">
                <div className="empty-state">
                  <div className="empty-icon">📊</div>
                  <h3>Start Building Your Report</h3>
                  <p>Add widgets and elements from the sidebar to create your security report</p>
                  <button
                    className="btn-primary"
                    onClick={() => document.querySelector('.toolbar-btn.primary')?.click()}
                  >
                    Add Your First Element
                  </button>
                </div>
              </div>
            )}

          {/* Render Footer elements at the bottom of the page */}
          {footerElements && footerElements.length > 0 && (
            <>
              {footerElements.map(footerBlock => {
                // Check if this footer should be visible on this page
                const isVisibleOnThisPage = shouldShowOnPage(footerBlock, pageIndex, totalPages);

                return (
                  <div
                    key={`footer-${footerBlock.id}-page-${page.id}`}
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      // height: Removed to let inner block control height
                      width: '100%',
                      opacity: isVisibleOnThisPage ? 1 : (isExporting ? 0 : 0.4),
                      pointerEvents: isVisibleOnThisPage ? 'auto' : 'auto',
                      display: (!isVisibleOnThisPage && isExporting) ? 'none' : 'block',
                      zIndex: 10 // Ensure footer stays on top
                    }}
                  >
                    {renderLayoutNode(footerBlock)}
                  </div>
                );
              })}
            </>
          )}
          {/* Add Footer Section Button */}
          {builderMode === 'edit' && onAddSection && (
            <div
              className="add-section-btn-container footer-add-btn"
              style={{
                width: '100%',
                padding: '10px',
                display: 'flex',
                justifyContent: 'center',
                borderTop: '1px dashed #e5e7eb',
                marginTop: '10px',
                position: 'absolute',
                bottom: '60px', // Place above the absolute footer area
                left: 0,
                zIndex: 5
              }}
            >
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddSection('footer');
                }}
                title="Add New Footer Section"
              >
                + Add Footer Section
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Enhanced Block Component with Working Drag & Resize
const Block = ({
  block,
  isSelected,
  onSelect,
  onMove,
  onResize,
  onRemove,
  getBlockData,
  gridConfig,
  isEditing,
  onToggleEditMode,
  globalVariables,
  onUpdateCustomData,
  onDataConfigUpdate,
  onConfigUpdate,
  isExporting,
  readOnly,
  page,
  checkWidgetDataAvailability,
  metricsData,
  builderMode,
  effectiveScanData,
  pages,
  currentPageId,
  onPageChange,
  onContextMenu,
  mspInfo,
  clientInfo,
  selectedBlock,
  previewData,
  isHidden,
  pageNumber,
  children,
  isNestedInHeaderFooter, // received from renderLayoutNode
  orientation,
  theme,
  isMultiSelected = false,
  scanGroups = [],
  allCategories = []
}) => {
  const blockRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Check if any child of this block is selected
  const isChildSelected = selectedBlock && block.children && findBlockById(block.children, selectedBlock.includes('.') ? selectedBlock.split('.')[0] : selectedBlock);

  // Compute widget data with useMemo to ensure reactivity when block config changes
  // The key insight: JSON.stringify the tableColumns to create a stable dependency that changes when columns change
  const tableColumnsKey = block.widgetType === 'table'
    ? JSON.stringify(block.dataConfig?.tableColumns || [])
    : null;

  const data = useMemo(() => {
    if (block.type !== 'widget') return null;
    const result = getBlockData(block);


    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id, block.dataConfig, tableColumnsKey, getBlockData]);

  // Auto-size block height based on content for widgets (including cloned blocks on continuation pages)
  const contentRef = useRef(null);
  const lastResizedHeightRef = useRef(null);
  const isResizingRef = useRef(false);

  useEffect(() => {
    const shouldAutoSize = block.type === 'widget' || (block.type === 'element' && (block.elementType === 'header' || block.elementType === 'footer'));

    // Fix: If block was manually resized, DO NOT auto-size it (prevents shrinking/jumping)
    // Also skip auto-sizing for static clones on continuation pages to prevent infinite update loops
    if (block.manualResize || block.isStaticClone) {
      return;
    }

    if (shouldAutoSize && contentRef.current && !isResizingRef.current) {
      // Use a timeout to ensure content is rendered
      const timeoutId = setTimeout(() => {
        const contentElement = contentRef.current;
        if (!contentElement || isResizingRef.current) return;

        // Get the actual content height
        let actualHeight = contentElement.scrollHeight;

        // CRITICAL FIX: For Header/Footer blocks, since children are absolute-positioned, 
        // they don't contribute to scrollHeight. We must manually calculate the required 
        // height to contain all children.
        if (isHeaderFooter && block.children && block.children.length > 0) {
          const childrenMaxBottom = block.children.reduce((max, child) => {
            const childBottom = (child.position?.y || 0) + (child.size?.height || 0);
            return Math.max(max, childBottom);
          }, 0);

          // Use the larger of the text content height or the children height
          actualHeight = Math.max(actualHeight, childrenMaxBottom);
        }

        // Only update if height has changed significantly (more than 10px difference)
        const currentHeight = block.size.height;

        // Skip if we just resized to this height (prevent infinite loop)
        if (lastResizedHeightRef.current === currentHeight && Math.abs(actualHeight - currentHeight) < 20) {
          return;
        }

        if (actualHeight > 0 && Math.abs(actualHeight - currentHeight) > 10) {
          // Snap to grid
          const snappedHeight = Math.ceil(actualHeight / gridConfig.cellHeight) * gridConfig.cellHeight;
          // Add some padding (half a cell)
          const newHeight = Math.max(snappedHeight, gridConfig.cellHeight);

          // Update block size if different
          // Allow resizing for both regular blocks and cloned blocks on continuation pages
          if (Math.abs(newHeight - currentHeight) > 10) {
            isResizingRef.current = true;
            lastResizedHeightRef.current = newHeight;

            onResize(block.id, {
              width: block.size.width,
              height: newHeight
            }, page.id);

            // Reset flag after a delay to allow the resize to complete
            setTimeout(() => {
              isResizingRef.current = false;
            }, 200);
          }
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id, block.size?.height, block.size?.width, data, gridConfig.cellHeight, block.config]);

  // Fix: Force height !important for Header/Footer blocks
  const isHeaderFooter = (block.type === 'element' || block.type === 'section') && (block.elementType === 'header' || block.elementType === 'footer');
  useLayoutEffect(() => {
    if (isHeaderFooter && blockRef.current) {
      const heightVal = block.size?.height || 100;
      blockRef.current.style.setProperty('height', `${heightVal}px`, 'important');
    }
  }, [isHeaderFooter, block.size?.height]);

  // Enhanced drag functionality with cross-page support
  const handleMouseDown = (e) => {
    if (e.target.classList.contains('resize-handle')) return;

    // Right-click handling: skip selection clearing if already selected
    if (e.button === 2 && (isSelected || isMultiSelected)) {
      e.stopPropagation();
      return; // Return without starting drag, letting onContextMenu fire natively
    }

    // If Ctrl/Shift/Meta is held, just toggle multi-select — don't start drag
    if (e.ctrlKey || e.metaKey || e.shiftKey) {
      e.stopPropagation();
      e.preventDefault();
      onSelect(block.id, e);
      return; // Don't start drag
    }

    e.stopPropagation();
    e.preventDefault();
    onSelect(block.id, e);
    setIsDragging(true);

    const startX = e.clientX;
    const startY = e.clientY;

    // Guard against missing position (e.g. for nested blocks)
    const startBlockX = block.position?.x || 0;
    const startBlockY = block.position?.y || 0;
    const pageContainer = blockRef.current?.closest('.page-container');
    const pageContainerRect = pageContainer?.getBoundingClientRect();
    const pageIndex = pages.findIndex(p => p.id === page.id);

    // Find parent container if nested in header/footer to enforce bounds
    let parentHeaderFooter = null;
    if (isNestedInHeaderFooter) {
      // Helper to find parent
      const findParent = (blocks) => {
        for (const b of blocks) {
          if ((b.elementType === 'header' || b.elementType === 'footer') && b.children) {
            // Check if this block is a direct child
            if (b.children.some(child => child.id === block.id)) {
              return b;
            }
            // If deep nesting is allowed, recursive check would go here, 
            // but for now assume direct children of Header/Footer
          }
        }
        return null;
      };
      // Check current page first
      parentHeaderFooter = findParent(page.blocks);
      // If not found (e.g. global header defined on another page?), check all pages
      if (!parentHeaderFooter && pages) {
        for (const p of pages) {
          parentHeaderFooter = findParent(p.blocks);
          if (parentHeaderFooter) break;
        }
      }
    }

    console.log('Drag started for block:', block.id, 'at position:', { x: startBlockX, y: startBlockY }, 'Parent:', parentHeaderFooter?.id);

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;

      const newX = startBlockX + deltaX;
      const newY = startBlockY + deltaY;

      // Snap to grid
      const gridX = Math.round(newX / gridConfig.cellWidth) * gridConfig.cellWidth;
      const gridY = Math.round(newY / gridConfig.cellHeight) * gridConfig.cellHeight;

      // Detect which page the block is being dragged to
      let targetPageId = page.id;
      let constrainedX = gridX;
      let constrainedY = gridY;

      if (pages && pageContainerRect) {
        const mouseY = e.clientY;
        const scrollY = window.scrollY;
        const pageTop = pageContainerRect.top + scrollY;
        const pageBottom = pageTop + pageContainerRect.height;
        const absoluteMouseY = mouseY + scrollY;

        // Check if dragging above current page (Disabled for nested Header/Footer elements)
        if (!isNestedInHeaderFooter && absoluteMouseY < pageTop && pageIndex > 0) {
          const prevPage = pages[pageIndex - 1];
          if (prevPage) {
            const prevPageElement = document.querySelector(`[data-page-id="${prevPage.id}"]`);
            if (prevPageElement) {
              const prevPageRect = prevPageElement.getBoundingClientRect();
              const prevPageTop = prevPageRect.top + scrollY;
              const relativeY = absoluteMouseY - prevPageTop;
              targetPageId = prevPage.id;
              constrainedX = Math.max(0, Math.min(gridConfig.pageWidth - block.size.width, gridX));
              constrainedY = Math.max(0, Math.min(gridConfig.pageHeight - block.size.height, relativeY));
            }
          }
        }
        // Check if dragging below current page (Disabled for nested Header/Footer elements)
        else if (!isNestedInHeaderFooter && absoluteMouseY > pageBottom && pageIndex < pages.length - 1) {
          const nextPage = pages[pageIndex + 1];
          if (nextPage) {
            const nextPageElement = document.querySelector(`[data-page-id="${nextPage.id}"]`);
            if (nextPageElement) {
              const nextPageRect = nextPageElement.getBoundingClientRect();
              const nextPageTop = nextPageRect.top + scrollY;
              const relativeY = absoluteMouseY - nextPageTop;
              targetPageId = nextPage.id;

              constrainedX = Math.max(0, Math.min(gridConfig.pageWidth - block.size.width, gridX));
              constrainedY = Math.max(0, Math.min(gridConfig.pageHeight - block.size.height, relativeY));
            }
          }
        }
        // Within current page bounds
        // Within current page bounds
        else {
          if (isNestedInHeaderFooter && parentHeaderFooter) {
            // CONSTRAINED MOVEMENT FOR NESTED BLOCKS:
            // Force stay within parent dimensions
            const parentWidth = parentHeaderFooter.size?.width || gridConfig.pageWidth;
            const parentHeight = parentHeaderFooter.size?.height || 100;

            constrainedX = Math.max(0, Math.min(parentWidth - block.size.width, gridX));
            constrainedY = Math.max(0, Math.min(parentHeight - block.size.height, gridY));
          } else if (isNestedInHeaderFooter) {
            // Fallback if parent missing but it is a nested block (should stay in its parent area)
            // We'll try to use the pageWidth as x boundary and cellHeight*5 as a safe y boundary if parent is truly unknown
            constrainedX = Math.max(0, Math.min(gridConfig.pageWidth - block.size.width, gridX));
            constrainedY = Math.max(0, Math.min(100 - block.size.height, gridY));
          } else {
            constrainedX = Math.max(0, Math.min(gridConfig.pageWidth - block.size.width, gridX));
            constrainedY = Math.max(0, Math.min(gridConfig.pageHeight - block.size.height, gridY));
          }
        }
      } else {
        // Fallback: constrain to current page bounds
        if (isNestedInHeaderFooter && parentHeaderFooter) {
          const parentWidth = parentHeaderFooter.size?.width || gridConfig.pageWidth;
          const parentHeight = parentHeaderFooter.size?.height || 100;
          constrainedX = Math.max(0, Math.min(parentWidth - block.size.width, gridX));
          constrainedY = Math.max(0, Math.min(parentHeight - block.size.height, gridY));
        } else if (isNestedInHeaderFooter) {
          constrainedX = Math.max(0, Math.min(gridConfig.pageWidth - block.size.width, gridX));
          constrainedY = Math.max(0, Math.min(100 - block.size.height, gridY));
        } else {
          constrainedX = Math.max(0, Math.min(gridConfig.pageWidth - block.size.width, gridX));
          constrainedY = Math.max(0, Math.min(gridConfig.pageHeight - block.size.height, gridY));
        }
      }

      console.log('Moving block to:', { x: constrainedX, y: constrainedY }, 'targetPage:', targetPageId);
      onMove(block.id, { x: constrainedX, y: constrainedY }, targetPageId);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Enhanced resize functionality
  const handleResizeMouseDown = (e, direction) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);

    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = block.size.width;
    const startHeight = block.size.height;

    // Guard against missing position
    const startBlockX = block.position?.x || 0;
    const startBlockY = block.position?.y || 0;

    // Find parent container if nested in header/footer to enforce bounds
    let parentHeaderFooter = null;
    if (isNestedInHeaderFooter) {
      const findParent = (blocks) => {
        for (const b of blocks) {
          if ((b.elementType === 'header' || b.elementType === 'footer') && b.children) {
            if (b.children.some(child => child.id === block.id)) return b;
          }
        }
        return null;
      };
      parentHeaderFooter = findParent(page.blocks);
      if (!parentHeaderFooter && pages) {
        for (const p of pages) {
          parentHeaderFooter = findParent(p.blocks);
          if (parentHeaderFooter) break;
        }
      }
    }

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;

      let newWidth = startWidth;
      let newHeight = startHeight;
      let newX = startBlockX;
      let newY = startBlockY;

      const minWidth = gridConfig.cellWidth;
      const minHeight = gridConfig.cellHeight;
      const pageWidth = gridConfig.pageWidth;
      const pageHeight = gridConfig.pageHeight;

      const pWidth = (isNestedInHeaderFooter && parentHeaderFooter) ? (parentHeaderFooter.size?.width || pageWidth) : pageWidth;
      const pHeight = (isNestedInHeaderFooter && parentHeaderFooter) ? (parentHeaderFooter.size?.height || 100) : pageHeight;

      switch (direction) {
        case 'e': // East - resize right
          newWidth = Math.max(minWidth, startWidth + deltaX);
          newWidth = Math.min(newWidth, pWidth - newX);
          break;

        case 'w': // West - resize left
          newWidth = Math.max(minWidth, startWidth - deltaX);
          const deltaWidth = startWidth - newWidth;
          newX = isNestedInHeaderFooter ? (startBlockX + deltaWidth) : Math.max(0, startBlockX + deltaWidth);
          if (isNestedInHeaderFooter && newX < 0) {
            const diff = -newX;
            newX = 0;
            newWidth -= diff;
          }
          newWidth = Math.min(newWidth, startBlockX + startWidth);
          break;

        case 's': // South - resize down
          newHeight = Math.max(minHeight, startHeight + deltaY);
          newHeight = Math.min(newHeight, pHeight - newY);
          break;

        case 'n': // North - resize up
          newHeight = Math.max(minHeight, startHeight - deltaY);

          if (isHeaderFooter && block.elementType === 'footer') {
            newHeight = Math.min(newHeight, pageHeight);
            newY = startBlockY;
          } else {
            const deltaHeight = startHeight - newHeight;
            newY = isNestedInHeaderFooter ? (startBlockY + deltaHeight) : Math.max(0, startBlockY + deltaHeight);
            if (isNestedInHeaderFooter && newY < 0) {
              const diff = -newY;
              newY = 0;
              newHeight -= diff;
            }
            newHeight = Math.min(newHeight, startBlockY + startHeight);
          }
          break;

        case 'se': // South-east - resize down-right
          newWidth = Math.max(minWidth, startWidth + deltaX);
          newHeight = Math.max(minHeight, startHeight + deltaY);
          newWidth = Math.min(newWidth, pWidth - newX);
          newHeight = Math.min(newHeight, pHeight - newY);
          break;

        case 'sw': // South-west - resize down-left
          newWidth = Math.max(minWidth, startWidth - deltaX);
          newHeight = Math.max(minHeight, startHeight + deltaY);
          const deltaWidthSW = startWidth - newWidth;
          newX = isNestedInHeaderFooter ? (startBlockX + deltaWidthSW) : Math.max(0, startBlockX + deltaWidthSW);
          if (isNestedInHeaderFooter && newX < 0) {
            const diff = -newX;
            newX = 0;
            newWidth -= diff;
          }
          newWidth = Math.min(newWidth, startBlockX + startWidth);
          newHeight = Math.min(newHeight, pHeight - newY);
          break;

        case 'ne': // North-east - resize up-right
          newWidth = Math.max(minWidth, startWidth + deltaX);
          newWidth = Math.min(newWidth, pWidth - newX);
          newHeight = Math.max(minHeight, startHeight - deltaY);
          const deltaHeightNE = startHeight - newHeight;
          newY = isNestedInHeaderFooter ? (startBlockY + deltaHeightNE) : Math.max(0, startBlockY + deltaHeightNE);
          if (isNestedInHeaderFooter && newY < 0) {
            const diff = -newY;
            newY = 0;
            newHeight -= diff;
          }
          newHeight = Math.min(newHeight, startBlockY + startHeight);
          break;

        case 'nw': // North-west - resize up-left
          newWidth = Math.max(minWidth, startWidth - deltaX);
          newHeight = Math.max(minHeight, startHeight - deltaY);
          const deltaWidthNW = startWidth - newWidth;
          const deltaHeightNW = startHeight - newHeight;
          newX = isNestedInHeaderFooter ? (startBlockX + deltaWidthNW) : Math.max(0, startBlockX + deltaWidthNW);
          newY = isNestedInHeaderFooter ? (startBlockY + deltaHeightNW) : Math.max(0, startBlockY + deltaHeightNW);

          if (isNestedInHeaderFooter) {
            if (newX < 0) {
              const dw = -newX;
              newX = 0;
              newWidth -= dw;
            }
            if (newY < 0) {
              const dh = -newY;
              newY = 0;
              newHeight -= dh;
            }
          }

          newWidth = Math.min(newWidth, startBlockX + startWidth);
          newHeight = Math.min(newHeight, startBlockY + startHeight);
          break;

        default:
          break;
      }

      // Snap to grid
      const snappedWidth = Math.max(minWidth, Math.round(newWidth / gridConfig.cellWidth) * gridConfig.cellWidth);
      const snappedHeight = Math.max(minHeight, Math.round(newHeight / gridConfig.cellHeight) * gridConfig.cellHeight);
      const snappedX = Math.round(newX / gridConfig.cellWidth) * gridConfig.cellWidth;
      const snappedY = Math.round(newY / gridConfig.cellHeight) * gridConfig.cellHeight;

      // Update position if changed
      if (snappedX !== (block.position?.x || 0) || snappedY !== (block.position?.y || 0)) {
        onMove(block.id, { x: snappedX, y: snappedY });
      }

      // Update size
      onResize(block.id, {
        width: snappedWidth,
        height: snappedHeight
      }, page.id);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // NoDataFallback component for widgets with missing data
  const NoDataFallback = ({ scanType, message, widgetType }) => {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        minHeight: '150px',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        border: '2px dashed #dee2e6',
        borderRadius: '8px',
        color: '#6c757d',
        textAlign: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '10px' }}>📊</div>
        <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: '#495057' }}>
          {scanType} Required
        </div>
        <div style={{ fontSize: '14px', color: '#868e96', maxWidth: '300px', lineHeight: '1.5' }}>
          {message || `This widget requires ${scanType} data which is not available in the current scan file.`}
        </div>
        <div style={{
          fontSize: '11px',
          marginTop: '12px',
          color: '#adb5bd',
          padding: '4px 8px',
          backgroundColor: '#e9ecef',
          borderRadius: '4px',
          fontFamily: 'monospace'
        }}>
          Widget: {widgetType}
        </div>
      </div>
    );
  };

  // Memoize resolved style to recalculate when globalVariables or block.style changes
  const baseResolvedStyle = useMemo(() => {
    const { style } = block;
    if (!style) return {};

    const resolved = { ...style };

    // List of all color properties that need resolution
    const colorProperties = [
      'backgroundColor',
      'textColor',
      'borderColor',
      'tableHeaderBackground',
      'tableHeaderTextColor',
      'tableDataTextColor',
      'valueColor',
      'labelColor',
      'chartColors' // This is an array, handled separately
    ];

    // Resolve all color properties
    colorProperties.forEach(prop => {
      if (resolved[prop] !== undefined && resolved[prop] !== null) {
        // Special handling for chartColors array
        if (prop === 'chartColors' && Array.isArray(resolved[prop])) {
          resolved[prop] = resolved[prop].map(color =>
            resolveStyleValue(color, globalVariables) || color
          );
        } else {
          const resolvedValue = resolveStyleValue(resolved[prop], globalVariables);
          if (resolvedValue !== undefined) {
            resolved[prop] = resolvedValue;
          }
        }
      }
    });

    return resolved;
  }, [block.id, block.style, globalVariables]);

  const resolvedStyle = useMemo(() => {
    const style = { ...baseResolvedStyle };
    // CRITICAL: Strip container styles from resolvedStyle so widgets don't double-render backgrounds/borders.
    // The parent .block wrapper already applies these styles based on block.style.
    // We keep them for 'shape' elements because they handle their own container rendering.
    if (!(block.type === 'element' && block.elementType === 'shape')) {
      delete style.backgroundColor;
      delete style.borderWidth;
      delete style.borderStyle;
      delete style.borderColor;
      delete style.borderRadius;
      delete style.boxShadow;
    }
    return style;
  }, [baseResolvedStyle, block.type, block.elementType]);

  const renderContent = () => {
    const config = {
      ...block.config,
      // Merge properties from dataConfig so custom table widgets can apply them
      ...(block.dataConfig?.tableFilters ? { tableFilters: block.dataConfig.tableFilters } : {}),
      ...(block.dataConfig?.tableSort ? { tableSort: block.dataConfig.tableSort } : {}),
      ...(block.dataConfig?.lookoutField ? { lookoutField: block.dataConfig.lookoutField } : {})
    };


    // Deep-clone and resolve dynamic status colors for all widget labels
    if (config.labelStyles) {
      const resolvedLabelStyles = { ...config.labelStyles };
      Object.keys(resolvedLabelStyles).forEach(target => {
        const targetStyles = { ...resolvedLabelStyles[target] };

        ['color', 'backgroundColor'].forEach(prop => {
          if (typeof targetStyles[prop] === 'string' && targetStyles[prop].startsWith('dynamic:statusColor:')) {
            const category = targetStyles[prop].split(':')[2];
            targetStyles[prop] = resolveCategoryStatusColor(category, effectiveScanData, theme);
          }
        });

        resolvedLabelStyles[target] = targetStyles;
      });
      config.labelStyles = resolvedLabelStyles;
    }

    const editingProps = {
      isEditing,
      onToggleEditMode: () => onToggleEditMode(block.id),
      onUpdateCustomData: (customData) => onUpdateCustomData(block.id, customData),
      onConfigUpdate: onConfigUpdate ? (newConfig) => onConfigUpdate(block.id, newConfig) : undefined,
      customData: block.customData || {}
    };

    const chartProps = {
      ...editingProps,
      isExporting,
      dataContext: effectiveScanData,
      theme
    };

    // Helper function to handle unit cost changes for cost summary widgets
    const handleUnitCostChange = (itemName, newUnitCost) => {
      if (!onDataConfigUpdate) return;

      const currentCustomUnitCosts = block.dataConfig?.customUnitCosts || {};
      onDataConfigUpdate(block.id, {
        ...block.dataConfig,
        customUnitCosts: {
          ...currentCustomUnitCosts,
          [itemName]: newUnitCost
        }
      });
    };

    // Get custom unit cost for this widget's item
    const getCustomUnitCost = (itemName) => {
      return block.dataConfig?.customUnitCosts?.[itemName] ?? null;
    };

    // ===== DATA AVAILABILITY CHECK =====
    // Check if this widget has required data before rendering
    if (block.type === 'widget') {
      // CRITICAL: In template mode, metricsData is null, so use effectiveScanData instead
      // CRITICAL: In report mode, use effectiveScanData because it contains the MERGED external data
      // metricsData is just the raw API response and might miss the external scan data we manually merged
      const dataToCheck = effectiveScanData || metricsData;
      const dataCheck = checkWidgetDataAvailability(block.widgetType, dataToCheck);

      // If data not available, show fallback component
      // EXCEPT for table widgets - they handle their own "No data" state with headers visible
      const isTableWidget = block.widgetType === 'table' ||
        block.widgetType.endsWith('-table') ||
        (typeof PAGINATED_WIDGETS !== 'undefined' && PAGINATED_WIDGETS.includes(block.widgetType));

      if (!dataCheck.available && !isTableWidget) {
        console.log(`[renderContent] Widget ${block.widgetType} missing required data (${dataCheck.scanType})`);
        return (
          <NoDataFallback
            scanType={dataCheck.scanType}
            message={dataCheck.message}
            widgetType={block.widgetType}
          />
        );
      }
    }

    // Calculate pagination props for paginated widgets
    let paginationProps = {};

    if (block.type === 'widget' && PAGINATED_WIDGETS.includes(block.widgetType) && block.pagination) {
      // Use the currentPage from the block's pagination object
      // For original blocks, currentPage = 0
      // For cloned blocks on continuation pages, currentPage = 1, 2, 3, etc.
      // CRITICAL: If this is a cloned block, currentPage MUST be > 0 (continuation pages are 1-indexed)
      let currentPage = block.pagination.currentPage;
      if (block.isClonedBlock) {
        // For cloned blocks, currentPage must be > 0
        // If it's 0 or undefined, default to 1 (first continuation page)
        if (!currentPage || currentPage <= 0) {
          console.warn('[Block Render] ⚠️ Continuation block has invalid currentPage, defaulting to 1:', {
            blockId: block.id,
            currentPage: block.pagination.currentPage,
            isClonedBlock: block.isClonedBlock
          });
          currentPage = 1;
        }
      } else {
        // For original blocks, currentPage should be 0
        currentPage = currentPage || 0;
      }

      // Check dataConfig first (user-set values), then pagination object, then default
      const itemsPerPageFirstPage = block.dataConfig?.itemsPerPageFirstPage
        || block.pagination?.itemsPerPageFirstPage
        || block.dataConfig?.itemsPerPage
        || block.pagination?.itemsPerPage
        || 15;
      const itemsPerPageContinuation = block.dataConfig?.itemsPerPageContinuation
        || block.pagination?.itemsPerPageContinuation
        || block.dataConfig?.itemsPerPage
        || block.pagination?.itemsPerPage
        || 25;

      // Calculate correct itemsPerPage and startIndex based on currentPage
      let itemsPerPage, startIndex;
      if (currentPage === 0) {
        // First page: use itemsPerPageFirstPage, start at 0
        itemsPerPage = itemsPerPageFirstPage;
        startIndex = 0;
      } else {
        // Continuation pages: use itemsPerPageContinuation
        itemsPerPage = itemsPerPageContinuation;
        // Start index = items from first page + items from previous continuation pages
        startIndex = itemsPerPageFirstPage + (currentPage - 1) * itemsPerPageContinuation;
      }

      paginationProps = {
        pagination: {
          currentPage: currentPage,
          itemsPerPage: itemsPerPage,
          itemsPerPageFirstPage: itemsPerPageFirstPage,
          itemsPerPageContinuation: itemsPerPageContinuation,
          startIndex: startIndex, // Add startIndex for correct slicing
          endIndex: Math.min(startIndex + itemsPerPage, block.pagination.totalItems || 0), // CRITICAL: Add endIndex for correct slicing
          totalItems: block.pagination.totalItems || 0,
          totalPages: block.pagination.totalPages || 1,
          isContinuationPage: block.isClonedBlock || false
        }
      };

      // Log pagination props for debugging (for all paginated widgets)
      if (PAGINATED_WIDGETS.includes(block.widgetType)) {
        const startIdx = paginationProps.pagination.startIndex;
        const itemsPerPg = paginationProps.pagination.itemsPerPage;
        const endIdx = startIdx + itemsPerPg;
        console.log('[Block Render] ========================================');
        console.log('[Block Render] Widget Type:', block.widgetType);
        console.log('[Block Render] Block ID:', block.id);
        console.log('[Block Render] Is Cloned:', block.isClonedBlock);
        console.log('[Block Render] Pagination Props:', {
          currentPage: paginationProps.pagination.currentPage,
          itemsPerPage: itemsPerPg,
          itemsPerPageFirstPage: paginationProps.pagination.itemsPerPageFirstPage,
          itemsPerPageContinuation: paginationProps.pagination.itemsPerPageContinuation,
          startIndex: startIdx,
          totalItems: paginationProps.pagination.totalItems,
          totalPages: paginationProps.pagination.totalPages,
          expectedRange: `items ${startIdx + 1} to ${endIdx} (indices ${startIdx} to ${endIdx - 1})`
        });
        console.log('[Block Render] ========================================');
      }
    }

    if (block.type === 'widget') {
      switch (block.widgetType) {
        case 'numeric':
          return <NumericWidget data={data} config={config} style={resolvedStyle} {...editingProps} />;
        case 'bar':
          return <BarChartWidget data={data} config={config} style={resolvedStyle} {...chartProps} />;
        case 'line':
          return <LineChartWidget data={data} config={config} style={resolvedStyle} {...chartProps} />;
        case 'pie':
          return <PieChartWidget data={data} config={config} style={resolvedStyle} {...chartProps} />;
        case 'donut':
          return <DonutChartWidget data={data} config={config} style={resolvedStyle} {...chartProps} />;
        case 'radar':
          return <RadarChartWidget data={data} config={config} style={resolvedStyle} {...chartProps} />;
        case 'polar':
          return <PolarAreaChartWidget data={data} config={config} style={resolvedStyle} {...chartProps} />;
        case 'bubble':
          return <BubbleChartWidget data={data} config={config} style={resolvedStyle} {...chartProps} />;
        case 'scatter':
          return <ScatterChartWidget data={data} config={config} style={resolvedStyle} {...chartProps} />;
        case 'table':
          return <TableWidget data={data} config={config} style={resolvedStyle} theme={theme} scanData={effectiveScanData || metricsData} {...editingProps} {...paginationProps} />;
        case 'network-diagram':
          return <NetworkDiagramWidget config={config} style={resolvedStyle} scanData={effectiveScanData || metricsData} />;
        case 'gauge':
          return <GaugeWidget data={data} config={config} style={resolvedStyle} {...editingProps} />;
        case 'statcard':
          return <StatCardWidget data={data} config={config} style={resolvedStyle} {...editingProps} />;
        // ===== AUTO WIDGETS - TEMPLATE SIGNATURE =====
        case 'nested-rectangle-findings':
          return <NestedRectangleFindingsWidget data={data} config={config} style={resolvedStyle} />;
        case 'company-at-a-glance':
          return <CompanyAtAGlanceWidget data={data} config={config} style={resolvedStyle} />;
        case 'category-score-bar':
          return <CategoryScoreBarWidget data={data} config={config} style={resolvedStyle} />;
        case 'financial-impact':
          return <FinancialImpactWidget data={data} config={config} style={resolvedStyle} />;
        case 'category-description':
          return <CategoryDescriptionWidget data={data} config={config} style={resolvedStyle} />;

        // ===== AUTO WIDGETS - FINDINGS & SECURITY =====
        case 'findings-status':
          return <FindingsStatusWidget data={data} config={config} style={resolvedStyle} theme={theme} orientation={orientation} />;
        case 'single-finding-status':
          const allReportFindings = calculateAllReportFindings(data, scanGroups, allCategories);
          return <SingleFindingStatusWidget
            data={data}
            config={config}
            style={resolvedStyle}
            theme={theme}
            onConfigChange={editingProps.onConfigUpdate}
            scanGroups={scanGroups}
            availableCategories={allCategories}
            allPossibleFindings={allReportFindings}
          />;
        case 'infrastructure-scan-grid':
          return <InfrastructureScanGridWidget
            data={data}
            config={config}
            style={resolvedStyle}
            theme={theme}
            onConfigChange={editingProps.onConfigUpdate}
          />;
        case 'managed-service-experience':
          return <ManagedServiceScanGridWidget
            data={data}
            config={config}
            style={resolvedStyle}
            theme={theme}
            onConfigChange={editingProps.onConfigUpdate}
          />;
        case 'security-scan-grid':
          return <SecurityScanGridWidget
            data={data}
            config={config}
            style={resolvedStyle}
            theme={theme}
            onConfigChange={editingProps.onConfigUpdate}
          />;
        case 'cyber-security-threat-exposure':
          return <CyberSecurityGridWidget
            data={data}
            config={config}
            style={resolvedStyle}
            theme={theme}
            onConfigChange={editingProps.onConfigUpdate}
          />;
        case 'combined-scan-grid':
          return <CombinedScanGridWidget
            data={data}
            config={config}
            style={resolvedStyle}
            theme={theme}
            onConfigChange={editingProps.onConfigUpdate}
            scanGroups={scanGroups}
            availableCategories={allCategories}
          />;
        case 'advanced-scan-grid':
          return <AdvancedScanGridWidget
            data={data}
            config={config}
            style={resolvedStyle}
            theme={theme}
            onConfigChange={editingProps.onConfigUpdate}
            scanGroups={scanGroups}
            availableCategories={allCategories}
          />;
        case 'key-findings-advanced':
          return <KeyFindingsAdvancedWidget data={data} config={config} style={resolvedStyle} theme={theme} />;
        case 'key-findings':
          // Include sorting config for finding list widgets
          return <KeyFindingsSummaryWidget
            data={data}
            config={{
              ...config,
              sortField: block.dataConfig?.sortField,
              sortDirection: block.dataConfig?.sortDirection
            }}
            style={resolvedStyle}
            theme={theme}
            {...paginationProps}
          />;
        case 'open-ports':
          // Include sorting config for finding list widgets
          return <OpenPortsWidget
            data={data}
            config={{
              ...config,
              sortField: block.dataConfig?.sortField,
              sortDirection: block.dataConfig?.sortDirection
            }}
            style={resolvedStyle}
            {...paginationProps}
          />;
        case 'findings-grid':
          // Include sorting config for finding list widgets
          return <FindingsGridWidget
            data={data}
            config={{
              ...config,
              sortField: block.dataConfig?.sortField,
              sortDirection: block.dataConfig?.sortDirection
            }}
            style={resolvedStyle}
            theme={theme}
            {...paginationProps}
          />;
        case 'open-ports-summary':
          return <OpenPortsFindingsSummaryWidget data={data} config={config} style={resolvedStyle} />;
        case 'darkweb-summary':
          return <DarkWebSummaryWidget data={data} config={config} style={resolvedStyle} />;
        case 'accounts-summary':
          return <AccountsFindingsSummaryWidget data={data} config={{ ...config, type: 'password' }} style={resolvedStyle} />;
        case 'accounts-admin-summary':
          return <AccountsFindingsSummaryWidget data={data} config={{ ...config, type: 'admin' }} style={resolvedStyle} />;
        case 'firewall-summary':
          return <FirewallFindingsSummaryWidget data={data} config={config} style={resolvedStyle} />;
        case 'localhost-firewall-findings-summary':
          return <LocalhostFirewallFindingsSummaryWidget data={data} />;
        case 'localhost-firewall-cost-summary':
          return <LocalhostFirewallCostSummaryWidget data={data} />;
        case 'localhost-service-risk-summary':
          return <LocalhostServiceRiskSummaryWidget data={data} />;
        case 'localhost-service-cost-summary':
          return <LocalhostServiceCostSummaryWidget data={data} />;
        case 'localhost-os-cost-summary':
          return <LocalhostOSCostSummaryWidget data={data} />;
        case 'localhost-open-ports-cost-summary':
          return <LocalhostOpenPortsCostSummaryWidget data={data} />;
        case 'service-risk-summary':
          return <ServiceRiskSummaryWidget data={data} config={config} style={resolvedStyle} />;
        case 'overall-risk-score':
          return <OverallRiskScoreWidget
            data={data}
            config={config}
            style={{
              ...resolvedStyle,
              width: block.size?.width,
              height: block.size?.height
            }}
            isExporting={isExporting}
            orientation={orientation}
            theme={theme}
          />;

        // ===== CHARTS - LOCALHOST =====
        case 'user-password-status':
          return <UserPasswordStatusChart data={data} config={config} style={resolvedStyle} />;
        case 'disk-free-used':
          return <DiskFreeVsUsedChart data={data} config={config} style={resolvedStyle} />;
        case 'firewall-status':
          return <FirewallStatusChart data={data} config={config} style={resolvedStyle} />;
        case 'service-start-modes':
          return <ServiceStartModesChart data={data} config={config} style={resolvedStyle} />;
        case 'admin-password-status':
          return <AdminPasswordStatusChart data={data} config={config} style={resolvedStyle} />;
        case 'password-strength':
          return <PasswordStrengthChart data={data} config={config} style={resolvedStyle} />;
        case 'hibp-breach-status':
          return <HIBPBreachStatusChart data={data} config={config} style={resolvedStyle} />;
        case 'windows-version':
          return <WindowsVersionChart data={data} config={config} style={resolvedStyle} />;
        case 'network-firewall-status-chart':
          return <NetworkFirewallStatusChart data={data} config={config} style={resolvedStyle} theme={theme} />;
        case 'common-software':
          return <CommonSoftwareStatusChart data={data} config={config} style={resolvedStyle} />;
        case 'hibp-status':
          return <HIBPStatusChart data={data} config={config} style={resolvedStyle} />;
        case 'rdp-status':
          return <RDPStatusChart data={data} config={config} style={resolvedStyle} />;
        case 'browser-status':
          return <BrowserStatusChart data={data} config={config} style={resolvedStyle} />;
        case 'system-age':
          return <SystemAgeDistributionChart data={data} config={config} style={resolvedStyle} />;

        // ===== CHARTS - NETWORK =====
        case 'open-ports-chart':
          return <OpenPortsChart data={data} config={config} style={resolvedStyle} />;
        case 'device-type':
          return <DeviceTypeChart data={data} config={config} style={resolvedStyle} />;
        case 'network-sensitive-ports':
          return <NetworkSensitivePortsChart data={data} config={config} style={resolvedStyle} />;

        // ===== CHARTS - ACTIVE DIRECTORY =====
        case 'ad-object-types':
          return <ADObjectTypesChart data={data} config={config} style={resolvedStyle} />;
        case 'ad-objects-pie-chart':
          return <ADObjectsPieChart data={data} config={config} style={resolvedStyle} />;
        case 'fsmo-roles':
          return <FSMORolesChart data={data} config={config} style={resolvedStyle} />;
        case 'active-inactive-users':
          return <ActiveInactiveUsersChart data={data} config={config} style={resolvedStyle} />;
        case 'password-expiry':
          return <PasswordExpiryChart data={data} config={config} style={resolvedStyle} />;
        case 'active-inactive-computers':
          return <ActiveInactiveComputersChart data={data} config={config} style={resolvedStyle} />;
        case 'group-types':
          return <GroupTypesChart data={data} config={config} style={resolvedStyle} />;
        case 'ou-membership':
          return <OUMembershipChart data={data} config={config} style={resolvedStyle} />;
        case 'gpo-timeline':
          return <GPOTimelineChart data={data} config={config} style={resolvedStyle} />;

        // ===== PROOF TABLES - ACTIVE DIRECTORY =====
        case 'ad-all-users-table':
          return <ADAllUsersTableWidget data={data} config={config} style={resolvedStyle} theme={theme} />;
        case 'ad-inactive-users-table':
          return <ADInactiveUsersTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'ad-all-computers-table':
          return <ADAllComputersTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'ad-workstation-age-table':
          return <ADWorkstationAgeTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'ad-inactive-computers-table':
          return <ADInactiveComputersTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'ad-groups-risk-table':
          return <ADGroupsRiskTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'ad-ous-risk-table':
          return <ADOUsRiskTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'ad-fsmo-roles-table':
          return <ADFSMORolesTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'ad-gpo-analysis-table':
          return <ADGPOAnalysisTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'ad-users-audit-table':
          return <ADUsersAuditTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'ad-computers-audit-table':
          return <ADComputersAuditTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'ad-devices-audit-table':
          return <ADDevicesAuditTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'ad-groups-audit-table':
          return <ADGroupsAuditTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'ad-ous-audit-table':
          return <ADOUsAuditTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'ad-domain-controllers-audit-table':
          return <ADDomainControllersAuditTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'ad-gpo-policies-audit-table':
          return <ADGPOPoliciesAuditTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'ad-event-logs-audit-table':
          return <ADEventLogsAuditTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;

        // ===== PROOF TABLES - NETWORK =====
        case 'network-all-users-password-table':
          return <NetworkAllUsersPasswordTableWidget data={data} config={config} style={resolvedStyle} theme={theme} />;
        case 'network-non-expiring-passwords-table':
          return <NetworkNonExpiringPasswordsTableWidget data={data} config={config} style={resolvedStyle} theme={theme} />;
        case 'network-admin-daily-work-table':
          return <NetworkAdminDailyWorkTableWidget data={data} config={config} style={resolvedStyle} theme={theme} />;
        case 'network-disk-usage-table':
          return <NetworkDiskUsageTableWidget data={data} config={config} style={resolvedStyle} theme={theme} />;
        case 'network-backup-status-table':
          return <NetworkBackupStatusTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'network-device-age-table':
          return <NetworkDeviceAgeTableWidget data={data} config={config} style={resolvedStyle} theme={theme} />;
        case 'network-installed-software-table':
          return <NetworkInstalledSoftwareTableWidget data={data} config={config} style={resolvedStyle} theme={theme} />;
        case 'software-inventory-table':
          return <SoftwareInventoryTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'local-administrators-table':
          return <LocalAdministratorsTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'ad-admins-table':
          return <ADAdminsTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'network-os-versions-table':
          return <NetworkOSVersionsTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'network-users-no-expiring-password-table':
          return <NetworkUsersNoExpiringPasswordTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'network-users-expiring-password-table':
          return <NetworkUsersExpiringPasswordTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'network-admins-no-expiring-password-table':
          return <NetworkAdminsNoExpiringPasswordTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'network-admins-expiring-password-table':
          return <NetworkAdminsExpiringPasswordTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'network-admins-daily-work-table':
          return <NetworkAdminsDailyWorkTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'network-non-default-services-table':
          return <NetworkNonDefaultServicesTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'network-all-open-ports-table':
          return <NetworkAllOpenPortsTableWidget data={data} config={config} style={resolvedStyle} theme={theme} />;
        case 'network-sensitive-open-ports-table':
          return <NetworkSensitiveOpenPortsTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'network-antivirus-status-table':
          return <NetworkAntivirusStatusTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'network-encryption-status-table':
          return <NetworkEncryptionStatusTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'network-firewall-status-table':
          return <NetworkFirewallStatusTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'network-detailed-services-table':
          return <NetworkDetailedServicesTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'network-services-risk-breakdown-table':
          return <NetworkServicesRiskBreakdownTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'network-high-privilege-services-table':
          return <NetworkHighPrivilegeServicesTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'network-logical-disks-table':
          return <NetworkLogicalDisksTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;

        // ===== PROOF TABLES - LOCALHOST =====
        case 'localhost-all-users-password-table':
          return <LocalhostAllUsersPasswordTableWidget data={data} config={config} style={resolvedStyle} theme={theme} />;
        case 'localhost-non-expiring-passwords-table':
          return <LocalhostNonExpiringPasswordsTableWidget data={data} config={config} style={resolvedStyle} theme={theme} />;
        case 'localhost-disk-usage-table':
          return <LocalhostDiskUsageTableWidget data={data} config={config} style={resolvedStyle} theme={theme} />;
        case 'localhost-firewall-status-table':
          return <FirewallFindingsSummaryWidget data={data} config={config} style={resolvedStyle} theme={theme} />;
        case 'localhost-os-version-table':
          return <LocalhostOSVersionTableWidget data={data} config={config} style={resolvedStyle} theme={theme} />;
        case 'localhost-sensitive-open-ports-table':
          return <LocalhostSensitiveOpenPortsTableWidget data={data} config={config} style={resolvedStyle} theme={theme} />;
        case 'localhost-services-risk-breakdown-table':
          return <LocalhostServicesRiskBreakdownTableWidget data={data} config={config} style={resolvedStyle} theme={theme} />;
        case 'localhost-high-privilege-services-table':
          return <LocalhostHighPrivilegeServicesTableWidget data={data} config={config} style={resolvedStyle} theme={theme} pagination={paginationProps} />;
        case 'localhost-installed-software-table':
          return <LocalhostInstalledSoftwareTableWidget data={data} config={config} style={resolvedStyle} theme={theme} />;
        case 'localhost-non-default-services-table':
          return <LocalhostNonDefaultServicesTableWidget data={data} config={config} style={resolvedStyle} theme={theme} />;
        case 'localhost-open-ports-table':
          return <LocalhostOpenPortsTableWidget data={data} config={config} style={resolvedStyle} theme={theme} />;
        case 'localhost-browser-passwords-table':
          return <LocalhostBrowserPasswordsTableWidget data={data} config={config} style={resolvedStyle} theme={theme} />;
        case 'localhost-hardware-information-table':
          return <LocalhostHardwareInformationTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'localhost-system-information-table':
          return <LocalhostSystemInformationTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'localhost-installed-applications-table':
          return <LocalhostInstalledApplicationsTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'localhost-event-logs-table':
          return <LocalhostEventLogsTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'localhost-open-ports-audit-table':
          return <LocalhostOpenPortsAuditTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;

        // ===== PROOF TABLES - MS365 =====
        case 'ms365-inactive-users-table':
          return <MS365InactiveUsersTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'ms365-all-users-mfa-table':
          return <MS365AllUsersMFATableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'ms365-users-without-mfa-table':
          return <MS365UsersWithoutMFATableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'ms365-policy-config-table':
          return <MS365PolicyConfigTableWidget data={data} config={config} style={resolvedStyle} theme={theme} />;
        case 'ms365-scan-proof-table':
          return <MS365ScanProofTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'microsoft-policies-table':
          return <MicrosoftPoliciesTableWidget data={data} config={config} style={resolvedStyle} theme={theme} {...paginationProps} />;
        case 'ms365-users-pie-chart':
          return <MSUsersPieChartWidget data={data} config={config} style={resolvedStyle} />;

        case 'ms365-mfa-pie-chart':
          return <MSMFAPieChartWidget data={data} config={config} style={resolvedStyle} />;

        case 'ms365-policy-pie-chart':
          return <MSPolicyPieChartWidget data={data} config={config} style={resolvedStyle} />;

        case 'pii-detection-pie-chart':
          return <PIIDetectionPieChartWidget data={data} config={config} style={resolvedStyle} />;

        case 'pii-finding-summary':
          return <PIIFindingSummaryWidget data={data} config={config} style={resolvedStyle} />;

        case 'pii-cost-summary':
          return <PIICostSummaryWidget
            data={data}
            config={config} style={resolvedStyle}
            customUnitCost={getCustomUnitCost('PII')}
            onUnitCostChange={handleUnitCostChange}
          />;

        case 'inactive-ms-users-summary':
          return <InactiveMSUsersSummaryWidget data={data} config={config} style={resolvedStyle} />;

        case 'inactive-ms-users-cost-summary':
          return <InactiveMSUsersCostSummaryWidget data={data} config={config} style={resolvedStyle} />;

        case 'mfa-summary':
          return <MFASummaryWidget data={data} config={config} />;

        case 'mfa-cost-summary':
          return <MFACostSummaryWidget
            data={data}
            config={config} style={resolvedStyle}
            customUnitCost={getCustomUnitCost('MFA')}
            onUnitCostChange={handleUnitCostChange}
          />;

        case 'policy-configuration-cost-summary':
          return <PolicyConfigurationCostSummaryWidget data={data} config={config} style={resolvedStyle} />;

        // ===== PROOF TABLES - HIBP/DARK WEB =====
        case 'hibp-breached-credentials-table':
          return <HIBPBreachedCredentialsTableWidget data={data} config={config} style={resolvedStyle} />;
        case 'hibp-exposure-by-platform-table':
          return <HIBPExposureByPlatformTableWidget data={data} config={config} style={resolvedStyle} />;

        // ===== PROOF TABLES - PII =====
        case 'pii-detected-table':
          return <PIIDetectedTableWidget data={data} config={config} style={resolvedStyle} pagination={paginationProps?.pagination} />;
        case 'pii-type-summary-table':
          return <PIITypeSummaryTableWidget data={data} config={config} style={resolvedStyle} />;

        // ===== SUMMARY FINDINGS - BACKUPS =====
        case 'backups-summary':
          return <BackupsSummaryWidget data={data} config={config} style={resolvedStyle} />;
        case 'backups-cost-summary':
          return <BackupsCostSummaryWidget
            data={data}
            config={config} style={resolvedStyle}
            customUnitCost={getCustomUnitCost('Backups')}
            onUnitCostChange={handleUnitCostChange}
          />;

        // ===== SUMMARY FINDINGS - ADMINS =====
        case 'admins-password-pie-chart':
          return <AdminsPasswordStatusPieChartWidget data={data} config={config} style={resolvedStyle} />;
        case 'admins-finding-summary':
          return <AdminsFindingSummaryWidget data={data} config={config} style={resolvedStyle} />;
        case 'admins-cost-summary':
          return <AdminsCostSummaryWidget
            data={data}
            config={config} style={resolvedStyle}
            customUnitCost={getCustomUnitCost('Admin Accounts')}
            onUnitCostChange={handleUnitCostChange}
          />;

        // ===== SUMMARY FINDINGS - ACCOUNTS =====
        case 'accounts-password-pie-chart':
          return <AccountsPasswordStatusPieChartWidget data={data} config={config} style={resolvedStyle} />;
        case 'accounts-finding-summary':
          return <AccountsFindingSummaryWidget data={data} config={config} style={resolvedStyle} />;
        case 'accounts-cost-summary':
          return <AccountsCostSummaryWidget
            data={data}
            config={config} style={resolvedStyle}
            customUnitCost={getCustomUnitCost('Accounts')}
            onUnitCostChange={handleUnitCostChange}
          />;

        // ===== SUMMARY FINDINGS - FIREWALL =====
        case 'firewall-finding-summary':
          return <FirewallFindingSummaryWidget data={data} config={config} style={resolvedStyle} />;
        case 'firewall-cost-summary':
          return <FirewallCostSummaryWidget data={data} config={config} style={resolvedStyle} />;
        case 'network-encryption-cost-summary':
          return <EncryptionCostSummaryWidget
            data={data}
            config={config} style={resolvedStyle}
            customUnitCost={getCustomUnitCost('Encryption')}
            onUnitCostChange={handleUnitCostChange}
          />;
        case 'network-firewall-findings-summary':
          return <NetworkFirewallFindingsSummaryWidget data={data} config={config} style={resolvedStyle} />;
        case 'network-firewall-cost-summary':
          return <NetworkFirewallCostSummaryWidget data={data} config={config} style={resolvedStyle} />;
        case 'network-service-risk-summary':
          return <NetworkServiceRiskSummaryWidget data={data} config={config} style={resolvedStyle} />;
        case 'network-service-cost-summary':
          return <NetworkServiceCostSummaryWidget data={data} config={config} style={resolvedStyle} />;
        case 'network-open-ports-cost-summary':
          return <NetworkOpenPortsCostSummaryWidget data={data} config={config} style={resolvedStyle} />;
        case 'ad-computer-cost-summary':
          return <ADComputerCostSummaryWidget data={data} config={config} style={resolvedStyle} />;
        case 'ad-users-cost-summary':
          return <ADUsersCostSummaryWidget data={data} config={config} style={resolvedStyle} />;
        case 'ad-groups-cost-summary':
          return <ADGroupsCostSummaryWidget data={data} config={config} style={resolvedStyle} />;
        case 'ou-cost-summary':
          return <OUCostSummaryWidget data={data} config={config} style={resolvedStyle} />;
        case 'ad-admins-cost-summary':
          return <ADAdminsCostSummaryWidget
            data={data}
            config={config} style={resolvedStyle}
            customUnitCost={getCustomUnitCost('Admin Accounts')}
            onUnitCostChange={handleUnitCostChange}
          />;
        case 'ad-admins-status-pie':
          return <ADAdminsStatusPieChartWidget data={data} config={config} style={resolvedStyle} />;
        case 'fsmo-cost-summary':
          return <FSMOCostSummaryWidget
            data={data}
            customUnitCost={getCustomUnitCost('FSMO')}
            onUnitCostChange={handleUnitCostChange}
            config={config} style={resolvedStyle}
          />;
        case 'gpo-cost-summary':
          return <GPOCostSummaryWidget
            data={data}
            customUnitCost={getCustomUnitCost('GPO')}
            onUnitCostChange={handleUnitCostChange}
            config={config} style={resolvedStyle}
          />;

        // ===== SUMMARY FINDINGS - OS =====
        case 'os-risk-summary':
          return <OSRiskSummaryWidget data={data} config={config} style={resolvedStyle} />;
        case 'os-cost-summary':
          return <OSCostSummaryWidget data={data} config={config} style={resolvedStyle} />;

        // ===== SUMMARY FINDINGS - TOTAL COST =====
        case 'total-cost-calculator':
          return <TotalCostCalculatorWidget data={data} pages={pages} config={config} style={resolvedStyle} />;

        // ===== EXTERNAL RISK SCAN WIDGETS =====
        case 'external-risk-score':
          return <ExternalRiskScoreWidget data={data} config={config} style={resolvedStyle} />;
        case 'external-domain-info':
          return <ExternalDomainInfoWidget data={data} config={config} style={resolvedStyle} />;
        case 'external-risk-overview':
          return <ExternalRiskOverviewWidget data={data} config={config} style={resolvedStyle} />;
        case 'external-category-breakdown':
          return <ExternalCategoryBreakdownWidget data={data} config={config} style={resolvedStyle} />;
        case 'external-all-findings-table':
          return <ExternalAllFindingsTableWidget data={data} config={config} style={resolvedStyle} pagination={paginationProps?.pagination} />;
        case 'external-warnings-table':
          return <ExternalWarningsTableWidget data={data} config={config} style={resolvedStyle} pagination={paginationProps?.pagination} />;
        case 'external-passed-table':
          return <ExternalPassedTableWidget data={data} config={config} style={resolvedStyle} pagination={paginationProps?.pagination} />;
        case 'external-severity-pie-chart':
          return <ExternalSeverityPieChartWidget data={data} config={config} style={resolvedStyle} />;
        case 'external-status-pie-chart':
          return <ExternalStatusPieChartWidget data={data} config={config} style={resolvedStyle} />;
        case 'external-category-bar-chart':
          return <ExternalCategoryBarChartWidget data={data} config={config} style={resolvedStyle} />;
        case 'external-compliance-summary':
          return <ExternalComplianceSummaryWidget data={data} config={config} style={resolvedStyle} />;
        case 'external-compliance-table':
          return <ExternalComplianceTableWidget data={data} config={config} style={resolvedStyle} pagination={paginationProps?.pagination} />;
        case 'external-dns-findings':
          return <ExternalDNSFindingsWidget data={data} config={config} style={resolvedStyle} />;
        case 'external-email-findings':
          return <ExternalEmailFindingsWidget data={data} config={config} style={resolvedStyle} />;
        case 'external-ssl-findings':
          return <ExternalSSLFindingsWidget data={data} config={config} style={resolvedStyle} />;
        case 'external-headers-findings':
          return <ExternalHeadersFindingsWidget data={data} config={config} style={resolvedStyle} />;
        case 'external-infrastructure-findings':
          return <ExternalInfrastructureFindingsWidget data={data} config={config} style={resolvedStyle} />;
        case 'external-breach-findings':
          return <ExternalBreachFindingsWidget data={data} config={config} style={resolvedStyle} />;

        default:
          return <div>Unknown widget</div>;
      }
    } else if (block.type === 'element') {
      switch (block.elementType) {
        case 'title':
          return <TitleElement
            config={config}
            style={resolvedStyle}
            organizationName={metricsData?.organizationName}
            scanDate={metricsData?.scanDate}
            mspInfo={mspInfo}
            clientInfo={clientInfo}
            {...editingProps}
          />;
        case 'text':
          return <TextElement
            config={config}
            style={resolvedStyle}
            organizationName={metricsData?.organizationName}
            scanDate={metricsData?.scanDate}
            mspInfo={mspInfo}
            clientInfo={clientInfo}
            scanData={effectiveScanData || metricsData}
            {...editingProps}
          />;
        case 'image':
          return <ImageElement config={config} style={resolvedStyle} {...editingProps} mspInfo={mspInfo} />;
        case 'divider':
          return <DividerElement config={config} style={resolvedStyle} {...editingProps} />;
        case 'embed':
          return <EmbedElement config={config} style={resolvedStyle} {...editingProps} />;
        case 'shape':
          return <ShapeElement config={config} style={resolvedStyle} data={{ scanData: effectiveScanData || metricsData, ...metricsData }} theme={theme} {...editingProps} />;
        case 'icon':
          return <IconElement config={config} style={resolvedStyle} {...editingProps} />;
        case 'header':
          return <HeaderElement
            blockId={block.id}
            config={config}
            style={resolvedStyle}
            organizationName={metricsData?.organizationName || previewData.organizationName}
            scanDate={metricsData?.scanDate || previewData.scanDate}
            mspInfo={mspInfo || previewData.mspInfo}
            clientInfo={clientInfo || previewData.clientInfo}
            selectedBlock={selectedBlock}
            onSelect={onSelect}
            children={children} // Pass children to HeaderElement
            {...editingProps}
          />;
        case 'footer':
          return <FooterElement
            blockId={block.id}
            config={config}
            style={resolvedStyle}
            organizationName={metricsData?.organizationName || previewData.organizationName}
            scanDate={metricsData?.scanDate || previewData.scanDate}
            mspInfo={mspInfo || previewData.mspInfo}
            clientInfo={clientInfo || previewData.clientInfo}
            selectedBlock={selectedBlock}
            onSelect={onSelect}
            children={children} // Pass children to FooterElement
            {...editingProps}
          />;
        default:
          return <div>Unknown element</div>;
      }
    }
  };

  // Check if this is a block with relative positioning (likely nested in a column)
  const isRelative = block.style?.position === 'relative';

  return (
    <div
      ref={blockRef}
      className={`block ${isSelected ? 'selected' : ''} ${isMultiSelected ? 'multi-selected' : ''} ${isDragging ? 'dragging' : ''} ${isHeaderFooter ? 'header-footer-block' : ''} ${block.groupId ? 'grouped' : ''} ${block.groupRole || ''}`}
      data-element-type={block.type === 'element' ? block.elementType : undefined}
      style={{
        position: isHeaderFooter ? 'relative' : (isRelative ? 'relative' : 'absolute'),
        left: (isHeaderFooter || isRelative) ? undefined : `${block.position?.x || 0}px`,
        top: (isHeaderFooter || isRelative) ? undefined : `${block.position?.y || 0}px`,
        width: isHeaderFooter ? '100%' : `${block.size?.width || gridConfig.cellWidth}px`,
        // Fix: Use explicit height for Header/Footer blocks instead of 100%, as the parent wrapper now lets content dictate height
        // IMPORTANT: For Header/Footer, we set height via useLayoutEffect with !important to ensure it overrides any other styles
        height: isHeaderFooter ? undefined : `${block.size?.height || 'auto'}px`,
        // Fix: Always allow overflow specificially for Header/Footer to ensure child resize handles (which are outside the box) are visible
        overflow: 'visible',
        boxSizing: 'border-box',
        // For footers, constrain max height to prevent overflow beyond page bottom
        maxHeight: undefined, // Removed footer max-height constraint as we now rely on parent container size
        backgroundColor: (block.type === 'element' && block.elementType === 'shape')
          ? 'transparent'
          : resolveStyleValue(block.style.backgroundColor, globalVariables),
        color: resolveStyleValue(block.style.textColor, globalVariables),
        borderWidth: (block.type === 'element' && block.elementType === 'shape') ? '0px' : (block.style.borderWidth !== undefined ? `${block.style.borderWidth}px` : '0px'),
        borderStyle: (block.type === 'element' && block.elementType === 'shape') ? 'none' : ((block.style.borderWidth && block.style.borderWidth > 0) ? (block.style.borderStyle || 'solid') : 'none'),
        borderColor: resolveStyleValue(block.style.borderColor, globalVariables),
        borderRadius: block.style.borderRadius !== undefined ? `${block.style.borderRadius}px` : (block.type === 'element' && block.elementType === 'shape' ? '0px' : '8px'),
        boxShadow: block.style.shadow === 'small' ? '0 1px 3px rgba(0,0,0,0.1)' :
          block.style.shadow === 'medium' ? '0 4px 6px rgba(0,0,0,0.1)' :
            block.style.shadow === 'large' ? '0 10px 25px rgba(0,0,0,0.1)' : 'none',
        // padding: `${block.style.padding}px`,
        fontSize: ((block.type === 'element' || block.type === 'section') && (block.elementType === 'title' || block.elementType === 'text' || block.elementType === 'header' || block.elementType === 'footer'))
          ? 'inherit' // Let elements control their own font size
          : (block.style.fontSize === 'small' ? '0.875rem' :
            block.style.fontSize === 'large' ? '1.125rem' : '1rem'),
        cursor: isDragging ? 'grabbing' : (isHeaderFooter ? 'pointer' : 'grab'), // Changed from 'default' to 'pointer' for headers/footers
        userSelect: 'none',
        zIndex: isSelected
          ? 1000
          : ((typeof block.style?.zIndex === 'number' && block.style.zIndex > 0)
            ? block.style.zIndex
            : (isHeaderFooter ? 100 : (block.type === 'element' && block.elementType === 'shape' ? 1 : 2))), // Shape gets 1, others get 2
        pointerEvents: (isExporting || readOnly) ? 'none' : 'auto',
        transition: isHeaderFooter ? 'box-shadow 0.2s ease, opacity 0.2s ease' : undefined,
        opacity: isHeaderFooter && !isSelected ? 0.9 : 1
      }}
      onMouseDown={(isHeaderFooter || isRelative || readOnly) ? undefined : handleMouseDown}
      onClick={(e) => {
        if (readOnly) return;
        // Select block when clicking on it and prevent deselection
        e.stopPropagation();
        // Skip if modifier keys held — handleMouseDown already handled multi-select
        if (e.ctrlKey || e.metaKey || e.shiftKey) return;
        onSelect(block.id, e);
      }}
      onMouseEnter={(e) => {
        // Add hover effect for headers/footers
        if (isHeaderFooter && !isSelected) {
          e.currentTarget.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.5)';
          e.currentTarget.style.opacity = '1';
        }
      }}
      onMouseLeave={(e) => {
        // Remove hover effect for headers/footers
        if (isHeaderFooter && !isSelected) {
          e.currentTarget.style.boxShadow = block.style.shadow === 'small' ? '0 1px 3px rgba(0,0,0,0.1)' :
            block.style.shadow === 'medium' ? '0 4px 6px rgba(0,0,0,0.1)' :
              block.style.shadow === 'large' ? '0 10px 25px rgba(0,0,0,0.1)' : 'none';
          e.currentTarget.style.opacity = '0.9';
        }
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        // Allow context menu on any block
        onContextMenu(e, block.id);
      }}
    >

      {/* Widget Group Badge */}
      {block.groupId && block.groupRole && (
        <div
          className={`widget-group-badge ${block.groupRole}`}
          title={`${block.groupRole === 'leader' ? 'Leader' : 'Follower'} of group`}
        >
          {block.groupRole === 'leader' ? '👑' : '👥'}
          <span style={{ fontSize: '0.55rem' }}>
            {block.groupRole === 'leader' ? 'Leader' : 'Follower'}
          </span>
        </div>
      )}

      {/* Hidden Page Indicator Overlay (Internal for precise alignment) */}
      {isHidden && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
            border: '2px dashed #94a3b8',
            borderRadius: block.style.borderRadius !== undefined ? `${block.style.borderRadius}px` : '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001, // High z-index to show above everything
            pointerEvents: 'none',
            fontSize: '11px',
            color: '#64748b',
            fontWeight: '500',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}
        >
          Hidden on this page (Page {pageNumber})
        </div>
      )}

      <div
        ref={contentRef}
        className="block-content"
        style={{
          width: '100%',
          minHeight: '100%',
          height: (block.type === 'widget' || ((block.type === 'element' || block.type === 'section') && (block.elementType === 'header' || block.elementType === 'footer'))) ? '100%' : '100%',
          pointerEvents: 'none', // Prevent content from interfering with drag
          overflow: 'hidden',
          borderRadius: 'inherit',
          boxSizing: 'border-box',
          textAlign: ((block.type === 'element' || block.type === 'section') && (block.elementType === 'title' || block.elementType === 'text' || block.elementType === 'header' || block.elementType === 'footer'))
            ? 'inherit' // Let elements control their own text alignment
            : undefined
        }}
      >
        {renderContent()}

        {/* Selection Indicator Overlay - moved after content to ensure visibility */}
        {isSelected && !readOnly && (
          <div
            className="block-selection-indicator"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              pointerEvents: 'none',
              border: '2px solid #3b82f6',
              boxShadow: '0 0 0 1px rgba(59, 130, 246, 0.5), 0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              borderRadius: `${block.style?.borderRadius || 8}px`,
              zIndex: 100
            }}
          />
        )}
      </div>

      {/* Render children (nested blocks) if any */}
      {/* Render children (nested blocks) if any - BUT skip for header/footer as they render their own children */}
      {!isHeaderFooter && children}

      {isSelected && !readOnly && (
        <>
          {/* Resize handles */}
          {(isHeaderFooter
            ? (block.elementType === 'header' ? ['s'] : ['n'])
            : ['n', 'e', 's', 'w', 'ne', 'nw', 'se', 'sw']
          ).map(dir => (
            <div
              key={dir}
              className={`resize-handle ${dir}`}
              onMouseDown={(e) => handleResizeMouseDown(e, dir)}
              style={{
                position: 'absolute',
                width: '12px', // Slightly larger for easier grabbing
                height: '12px',
                backgroundColor: '#3b82f6',
                border: '2px solid #ffffff',
                borderRadius: '50%',
                zIndex: 1000, // Matching selected block z-index
                pointerEvents: 'auto', // Explicitly enable pointer events
                cursor: dir === 'n' || dir === 's' ? 'ns-resize' : (dir === 'e' || dir === 'w' ? 'ew-resize' : (dir === 'nw' || dir === 'se' ? 'nwse-resize' : 'nesw-resize')),
                ...getResizeHandlePosition(dir)
              }}
            />
          ))}

          <button
            className="block-remove-btn"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(block.id);
            }}
            style={{
              position: 'absolute',
              top: '-18px',
              right: '-18px',
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              backgroundColor: '#ef4444',
              color: 'white',
              border: '3px solid white',
              boxShadow: '0 3px 6px rgba(0, 0, 0, 0.3)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              fontWeight: 'bold',
              zIndex: 1000, // Matching selected block z-index
              padding: 0,
              transition: 'all 0.2s ease',
              pointerEvents: 'auto'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#dc2626';
              e.currentTarget.style.transform = 'scale(1.2)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ef4444';
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 3px 6px rgba(0, 0, 0, 0.3)';
            }}
            title="Remove widget"
          >
            ✕
          </button>
        </>
      )}

      {/* Remove button for headers/footers (shown when selected) */}
      {isSelected && isHeaderFooter && (
        <button
          className="block-remove-btn"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(block.id);
          }}
          style={{
            position: 'absolute',
            top: '-18px',
            right: '-18px',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            backgroundColor: '#ef4444',
            color: 'white',
            border: '3px solid white',
            boxShadow: '0 3px 6px rgba(0, 0, 0, 0.3)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            fontWeight: 'bold',
            zIndex: 1000,
            padding: 0,
            transition: 'all 0.2s ease',
            pointerEvents: 'auto'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#dc2626';
            e.currentTarget.style.transform = 'scale(1.2)';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#ef4444';
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 3px 6px rgba(0, 0, 0, 0.3)';
          }}
          title="Remove header/footer"
        >
          ✕
        </button>
      )}
    </div>
  );
};

// Helper function to position resize handles
const getResizeHandlePosition = (direction) => {
  const positions = {
    n: { top: '-4px', left: '50%', transform: 'translateX(-50%)', cursor: 'n-resize' },
    e: { top: '50%', right: '-4px', transform: 'translateY(-50%)', cursor: 'e-resize' },
    s: { bottom: '-4px', left: '50%', transform: 'translateX(-50%)', cursor: 's-resize' },
    w: { top: '50%', left: '-4px', transform: 'translateY(-50%)', cursor: 'w-resize' },
    ne: { top: '-4px', right: '-4px', cursor: 'ne-resize' },
    nw: { top: '-4px', left: '-4px', cursor: 'nw-resize' },
    se: { bottom: '-4px', right: '-4px', cursor: 'se-resize' },
    sw: { bottom: '-4px', left: '-4px', cursor: 'sw-resize' }
  };
  return positions[direction] || {};
};



// Widget Components with ChartJS - Including New Chart Types
const NumericWidget = ({ data, config, style, customData }) => {
  // Check if this is placeholder data
  const isPlaceholder = data && data[PLACEHOLDER_FLAG];

  // Value always comes from data source (scan results), not from customData
  // Only use label from customData if it exists, otherwise use label from data
  const displayValue = data?.value ?? '--';
  const displayLabel = customData?.label || data?.label;

  // New styling system
  const valueStyle = getLabelStyle(config, 'value', {
    fontSize: style.valueFontSize ? `${style.valueFontSize}px` : '24px',
    color: style.valueColor || style.textColor || '#000000',
    fontWeight: 'bold',
    fontStyle: 'normal',
    textAlign: 'center'
  }, 'numeric');

  const labelStyle = getLabelStyle(config, 'label', {
    fontSize: style.labelFontSize ? `${style.labelFontSize}px` : '14px',
    color: style.labelColor || '#666666',
    fontWeight: 'normal',
    fontStyle: 'normal',
    textAlign: 'center'
  }, 'numeric');

  // Show placeholder message if no real data
  if (isPlaceholder) {
    return (
      <div
        className="numeric-widget placeholder-widget"
        style={{
          display: 'flex',
          flexDirection: config.titlePosition === 'bottom' ? 'column-reverse' :
            config.titlePosition === 'left' ? 'row' :
              config.titlePosition === 'right' ? 'row-reverse' : 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          height: '100%',
          pointerEvents: 'none'
        }}
      >
        {config.title && (
          <div
            className="widget-title"
            style={getLabelStyle(config, 'title', {
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#000000',
              textAlign: 'center'
            }, 'numeric')}
          >
            {config.title}
          </div>
        )}
        <div className="placeholder-message">
          <div className="placeholder-icon">🔢</div>
          <div className="placeholder-text">{data?.reason || 'Select a data source'}</div>
          <div className="placeholder-hint">Use the wizard to configure data</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="numeric-widget"
      style={{
        display: 'flex',
        flexDirection: config.titlePosition === 'bottom' ? 'column-reverse' :
          config.titlePosition === 'left' ? 'row' :
            config.titlePosition === 'right' ? 'row-reverse' : 'column',
        alignItems: (config.titlePosition === 'left' || config.titlePosition === 'right') ? 'center' : 'stretch',
        justifyContent: (config.titlePosition === 'left' || config.titlePosition === 'right') ? 'flex-start' : 'center',
        gap: '10px',
        height: '100%'
      }}
    >
      {config.title && (
        <div
          className="widget-title"
          style={getLabelStyle(config, 'title', {
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#000000',
            textAlign: 'left'
          }, 'numeric')}
        >
          {config.title}
        </div>
      )}
      <div
        className="numeric-value"
        style={{
          ...valueStyle,
          textAlign: valueStyle.textAlign || 'center'
        }}
      >
        {typeof displayValue === 'number' ? displayValue.toLocaleString() : displayValue}
      </div>
      {displayLabel && (
        <div
          className="numeric-label"
          style={{
            ...labelStyle,
            textAlign: labelStyle.textAlign || 'center'
          }}
        >
          {displayLabel}
        </div>
      )}
    </div>
  );
};

const BarChartWidget = ({ data, config, style, isEditing, onToggleEditMode, onUpdateCustomData, customData, isExporting }) => {
  const [editLabels, setEditLabels] = useState([]);
  const [editValues, setEditValues] = useState([]);

  // Check if this is placeholder data
  const isPlaceholder = data && data[PLACEHOLDER_FLAG];

  // Initialize form data when entering edit mode
  useEffect(() => {
    if (isEditing) {
      const displayData = customData.labels ? customData : data;
      setEditLabels(displayData?.labels || []);
      setEditValues(displayData?.values || []);
    }
  }, [isEditing, data, customData]);

  const handleSave = () => {
    onUpdateCustomData({
      labels: editLabels,
      values: editValues.map(v => parseFloat(v) || 0)
    });
    onToggleEditMode();
  };

  const displayData = customData.labels ? customData : data;

  // Show placeholder message (setup mode)
  if (isPlaceholder) {
    return (
      <div className="chart-widget placeholder-widget">
        {config.title && (
          <div
            className="widget-title"
            style={getLabelStyle(config, 'title', {
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#000000',
              textAlign: 'center'
            }, 'bar')}
          >
            {config.title}
          </div>
        )}
        <div className="placeholder-message">
          <div className="placeholder-icon">📊</div>
          <div className="placeholder-text">{data?.reason || 'Select a data source'}</div>
          <div className="placeholder-hint">Use the wizard to configure data</div>
        </div>
      </div>
    );
  }

  // Show empty state if configured but no data
  if (!displayData?.labels?.length && !displayData?.values?.length) {
    // Show ghost chart with message if configured but no data
    const ghostData = {
      labels: ['', '', '', '', ''],
      datasets: [{
        data: [0, 0, 0, 0, 0],
        backgroundColor: '#f3f4f6',
        borderWidth: 0
      }]
    };

    const ghostOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        datalabels: { display: false },
        title: {
          display: !!config.title,
          text: config.title,
          position: config.titlePosition || 'top',
          ...(() => {
            const s = getLabelStyle(config, 'title', {
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#6b7280',
              textAlign: 'center'
            }, 'bar');
            return {
              align: s.textAlign,
              color: s.color,
              font: {
                size: parseInt(s.fontSize),
                weight: s.fontWeight,
                style: s.fontStyle || 'normal'
              }
            };
          })()
        }
      },
      scales: {
        x: { display: true, grid: { display: true } },
        y: { display: true, grid: { display: true }, max: 10, ticks: { display: false } }
      },
      interaction: { mode: null },
      events: []
    };

    return (
      <div className="chart-widget empty-state" style={{ position: 'relative', height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ width: '100%', height: '100%' }}>
          <Bar data={ghostData} options={ghostOptions} />
        </div>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          color: style?.textColor || '#6b7280',
          fontStyle: 'italic',
          width: '100%',
          pointerEvents: 'none',
          zIndex: 10
        }}>
          {config.emptyStateMessage || "No data to render"}
        </div>
      </div>
    );
  }

  // Get custom chart colors from style, or use defaults
  const customColors = style.chartColors && Array.isArray(style.chartColors) && style.chartColors.length > 0
    ? style.chartColors
    : null;

  const defaultBackgroundColors = [
    'rgba(239, 68, 68, 0.8)',
    'rgba(249, 115, 22, 0.8)',
    'rgba(234, 179, 8, 0.8)',
    'rgba(34, 197, 94, 0.8)'
  ];

  const defaultBorderColors = [
    'rgb(239, 68, 68)',
    'rgb(249, 115, 22)',
    'rgb(234, 179, 8)',
    'rgb(34, 197, 94)'
  ];

  // Convert hex colors to rgba/rgb format for Chart.js
  const convertColorForChart = (hexColor, isBackground = true) => {
    if (!hexColor) return null;
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return isBackground ? `rgba(${r}, ${g}, ${b}, 0.8)` : `rgb(${r}, ${g}, ${b})`;
  };

  const generateColorsForData = (dataLength) => {
    if (customColors && customColors.length > 0) {
      // Use custom colors, repeating if necessary
      const backgrounds = [];
      const borders = [];
      for (let i = 0; i < dataLength; i++) {
        const color = customColors[i % customColors.length];
        backgrounds.push(convertColorForChart(color, true));
        borders.push(convertColorForChart(color, false));
      }
      return { backgrounds, borders };
    }
    // Use defaults, repeating if necessary
    const backgrounds = [];
    const borders = [];
    for (let i = 0; i < dataLength; i++) {
      backgrounds.push(defaultBackgroundColors[i % defaultBackgroundColors.length]);
      borders.push(defaultBorderColors[i % defaultBorderColors.length]);
    }
    return { backgrounds, borders };
  };

  if (isEditing) {
    return (
      <div className="chart-widget editing">
        {config.title && (
          <div
            className="widget-title"
            style={getLabelStyle(config, 'title', {
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#000000',
              textAlign: 'center'
            }, 'bar')}
          >
            {config.title}
          </div>
        )}
        <div className="edit-controls">
          <div className="edit-field">
            <label>Labels (comma-separated):</label>
            <input
              type="text"
              value={editLabels.join(', ')}
              onChange={(e) => setEditLabels(e.target.value.split(',').map(s => s.trim()))}
              style={{ width: '100%', marginBottom: '8px' }}
            />
          </div>
          <div className="edit-field">
            <label>Values (comma-separated):</label>
            <input
              type="text"
              value={editValues.join(', ')}
              onChange={(e) => setEditValues(e.target.value.split(',').map(s => s.trim()))}
              style={{ width: '100%', marginBottom: '8px' }}
            />
          </div>
          <div className="edit-actions">
            <button onClick={handleSave} className="btn btn-sm btn-success">Save</button>
            <button onClick={onToggleEditMode} className="btn btn-sm btn-secondary">Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  const values = displayData?.values || [];
  const labels = displayData?.labels || [];
  // Handle segment colors if provided in data
  let backgroundColors, borderColors;
  if (data?.colors && Array.isArray(data.colors)) {
    backgroundColors = data.colors.map(c => convertColorForChart(c, true));
    borderColors = data.colors.map(c => convertColorForChart(c, false));
  } else {
    const generated = generateColorsForData(values.length);
    backgroundColors = customColors ? generated.backgrounds : 'rgba(59, 130, 246, 0.8)';
    borderColors = customColors ? generated.borders : 'rgb(59, 130, 246)';
  }

  const chartData = {
    labels: labels,
    datasets: [
      {
        label: data?.seriesLabel || config.title || 'Security Findings',
        data: values,
        backgroundColor: backgroundColors,
        borderColor: borderColors,
        borderWidth: 1
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: isExporting ? false : {
      duration: 1000,
      easing: 'easeInOutQuart'
    },
    interaction: {
      intersect: false,
    },
    plugins: {
      legend: {
        display: config.legendPosition !== 'none',
        position: config.legendPosition || 'top',
      },
      title: {
        display: !!config.title,
        text: config.title,
        position: config.titlePosition || 'top',
        ...(() => {
          const s = getLabelStyle(config, 'title', {
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#000000',
            textAlign: 'center'
          }, 'bar');
          return {
            align: s.textAlign,
            color: s.color,
            font: {
              size: parseInt(s.fontSize),
              weight: s.fontWeight,
              style: s.fontStyle || 'normal'
            }
          };
        })()
      },
      datalabels: {
        display: style.showDataLabels !== false,
        anchor: 'end',
        align: 'top',
        formatter: (value) => value.toLocaleString(),
        font: {
          weight: 'bold',
          size: 11
        },
        color: style.textColor || '#6b7280'
      }
    }
  };

  return (
    <div className="chart-widget" style={{ pointerEvents: 'none' }}>
      <Bar data={chartData} options={options} />
    </div>
  );
};

const LineChartWidget = ({ data, config, style, isExporting }) => {
  // Check if this is placeholder data
  const isPlaceholder = data && data[PLACEHOLDER_FLAG];

  // Show placeholder message (setup mode)
  if (isPlaceholder) {
    return (
      <div className="chart-widget placeholder-widget">
        {config.title && (
          <div
            className="widget-title"
            style={getLabelStyle(config, 'title', {
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#000000',
              textAlign: 'center'
            }, 'line')}
          >
            {config.title}
          </div>
        )}
        <div className="placeholder-message">
          <div className="placeholder-icon">📈</div>
          <div className="placeholder-text">{data?.reason || 'Select a data source'}</div>
          <div className="placeholder-hint">Use the wizard to configure data</div>
        </div>
      </div>
    );
  }

  // Show empty state if configured but no data
  if (!data?.labels?.length && !data?.values?.length) {
    // Show ghost chart with message if configured but no data
    const ghostData = {
      labels: ['', '', '', '', ''],
      datasets: [{
        data: [0, 0, 0, 0, 0],
        borderColor: '#e5e7eb',
        borderWidth: 2,
        pointRadius: 0,
        fill: false
      }]
    };

    const ghostOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        datalabels: { display: false },
        title: {
          display: !!config.title,
          text: config.title,
          position: config.titlePosition || 'top',
          ...(() => {
            const s = getLabelStyle(config, 'title', {
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#6b7280',
              textAlign: 'center'
            }, 'line');
            return {
              align: s.textAlign,
              color: s.color,
              font: {
                size: parseInt(s.fontSize),
                weight: s.fontWeight,
                style: s.fontStyle || 'normal'
              }
            };
          })()
        }
      },
      scales: {
        x: { display: true, grid: { display: true } },
        y: { display: true, grid: { display: true }, max: 10, ticks: { display: false } }
      },
      interaction: { mode: null },
      events: []
    };

    return (
      <div className="chart-widget empty-state" style={{ position: 'relative', height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ width: '100%', height: '100%' }}>
          <Line data={ghostData} options={ghostOptions} />
        </div>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          color: style?.textColor || '#6b7280',
          fontStyle: 'italic',
          width: '100%',
          pointerEvents: 'none',
          zIndex: 10
        }}>
          {config.emptyStateMessage || "No data to render"}
        </div>
      </div>
    );
  }

  const chartData = {
    labels: data?.labels || [],
    datasets: [
      {
        label: config.title || 'Data',
        data: data?.values || [],
        fill: true,
        backgroundColor: 'rgba(59, 130, 246, 0.2)',
        borderColor: 'rgb(59, 130, 246)',
        tension: 0.4
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: isExporting ? false : {
      duration: 1000,
      easing: 'easeInOutQuart'
    },
    plugins: {
      legend: {
        display: config.legendPosition !== 'none',
        position: config.legendPosition || 'top',
      },
      title: {
        display: !!config.title,
        text: config.title,
        position: config.titlePosition || 'top',
        ...(() => {
          const s = getLabelStyle(config, 'title', {
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#000000',
            textAlign: 'center'
          }, 'line');
          return {
            align: s.textAlign,
            color: s.color,
            font: {
              size: parseInt(s.fontSize),
              weight: s.fontWeight,
              style: s.fontStyle || 'normal'
            }
          };
        })()
      },
      datalabels: {
        display: style.showDataLabels !== false,
        anchor: 'end',
        align: 'top',
        formatter: (value) => value.toLocaleString(),
        font: {
          weight: 'bold',
          size: 11
        },
        color: style.textColor || '#6b7280'
      }
    }
  };

  return (
    <div className="chart-widget" style={{ pointerEvents: 'none' }}>
      <Line data={chartData} options={options} />
    </div>
  );
};

const PieChartWidget = ({ data, config, style, isExporting }) => {
  // Check if this is placeholder data
  const isPlaceholder = data && data[PLACEHOLDER_FLAG];

  // Show placeholder message (setup mode)
  if (isPlaceholder) {
    return (
      <div className="chart-widget placeholder-widget">
        {config.title && <div className="widget-title">{config.title}</div>}
        <div className="placeholder-message">
          <div className="placeholder-icon">🥧</div>
          <div className="placeholder-text">{data?.reason || 'Select a data source'}</div>
          <div className="placeholder-hint">Use the wizard to configure data</div>
        </div>
      </div>
    );
  }

  // Show empty state if configured but no data
  if (!data?.labels?.length && !data?.values?.length) {
    // Show ghost chart with message if configured but no data
    const ghostData = {
      labels: ['Empty'],
      datasets: [{
        data: [1],
        backgroundColor: ['#f3f4f6'],
        borderWidth: 0
      }]
    };

    const ghostOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        datalabels: { display: false },
        title: {
          display: !!config.title,
          text: config.title,
          position: config.titlePosition || 'top',
          ...(() => {
            const s = getLabelStyle(config, 'title', {
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#6b7280',
              textAlign: 'center'
            }, 'pie');
            return {
              align: s.textAlign,
              color: s.color,
              font: {
                size: parseInt(s.fontSize),
                weight: s.fontWeight,
                style: s.fontStyle || 'normal'
              }
            };
          })()
        }
      },
      interaction: { mode: null },
      events: []
    };

    return (
      <div className="chart-widget empty-state" style={{ position: 'relative', height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ width: '100%', height: '100%' }}>
          <Pie data={ghostData} options={ghostOptions} />
        </div>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          color: style?.textColor || '#6b7280',
          fontStyle: 'italic',
          width: '100%',
          pointerEvents: 'none',
          zIndex: 10
        }}>
          {config.emptyStateMessage || "No data to render"}
        </div>
      </div>
    );
  }

  // Process labels for legend if requested
  const totalValue = (data?.values || []).reduce((a, b) => a + Number(b), 0);
  const processedLabels = (data?.labels || []).map((label, i) => {
    if (style.legendMode === 'label-value') {
      const val = data.values[i] || 0;
      return `${label}: ${val.toLocaleString()}`;
    }
    if (style.legendMode === 'label-percent') {
      const val = data.values[i] || 0;
      const pct = totalValue > 0 ? Math.round((val / totalValue) * 100) : 0;
      return `${label}: ${pct}%`;
    }
    return label;
  });

  const chartData = {
    labels: processedLabels,
    datasets: [
      {
        label: data?.seriesLabel || config.title || 'Data',
        data: data?.values || [],
        backgroundColor: (data?.colors && Array.isArray(data.colors)) ? data.colors : ((style?.chartColors?.length > 0) ? style.chartColors : [
          'rgba(59, 130, 246, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(139, 92, 246, 0.8)',
          'rgba(156, 163, 175, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(234, 179, 8, 0.8)',
          'rgba(236, 72, 153, 0.8)',
          'rgba(14, 165, 233, 0.8)',
          'rgba(168, 162, 158, 0.8)',
          'rgba(249, 115, 22, 0.8)'
        ]),
        borderWidth: 1
      }
    ]
  };

  return (
    <div className="chart-widget" style={{ pointerEvents: 'none' }}>
      <Pie data={chartData} options={{
        maintainAspectRatio: false,
        animation: isExporting ? false : {
          duration: 1000,
          easing: 'easeInOutQuart'
        },
        plugins: {
          legend: {
            display: config.legendPosition !== 'none',
            position: config.legendPosition || 'top',
            labels: {
              boxWidth: 12,
              padding: 10,
              font: { size: 10 }
            }
          },
          title: {
            display: !!config.title,
            text: config.title,
            position: config.titlePosition || 'top',
            ...(() => {
              const s = getLabelStyle(config, 'title', {
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#000000',
                textAlign: 'center'
              }, 'pie');
              return {
                align: s.textAlign,
                color: s.color,
                font: {
                  size: parseInt(s.fontSize),
                  weight: s.fontWeight,
                  style: s.fontStyle || 'normal'
                }
              };
            })()
          },
          datalabels: {
            color: '#fff',
            font: {
              weight: 'bold',
              size: 11
            },
            formatter: (value) => value.toLocaleString(),
            anchor: 'center',
            align: 'center',
            display: (context) => (style.showDataLabels !== false) && context.dataset.data[context.dataIndex] > 0
          }
        }
      }} />
    </div>
  );
};

const DonutChartWidget = ({ data, config, style, isExporting }) => {
  // Check if this is placeholder data
  const isPlaceholder = data && data[PLACEHOLDER_FLAG];

  // Show placeholder message (setup mode)
  if (isPlaceholder) {
    return (
      <div className="chart-widget placeholder-widget">
        {config.title && (
          <div
            className="widget-title"
            style={getLabelStyle(config, 'title', {
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#000000',
              textAlign: 'center'
            }, 'donut')}
          >
            {config.title}
          </div>
        )}
        <div className="placeholder-message">
          <div className="placeholder-icon">🍩</div>
          <div className="placeholder-text">{data?.reason || 'Select a data source'}</div>
          <div className="placeholder-hint">Use the wizard to configure data</div>
        </div>
      </div>
    );
  }

  // Show empty state if configured but no data
  if (!data?.labels?.length && !data?.values?.length) {
    // Show ghost chart with message if configured but no data
    const ghostData = {
      labels: ['Empty'],
      datasets: [{
        data: [1],
        backgroundColor: ['#f3f4f6'],
        borderWidth: 0
      }]
    };

    const ghostOptions = {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '60%',
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        datalabels: { display: false },
        title: {
          display: !!config.title,
          text: config.title,
          position: config.titlePosition || 'top',
          ...(() => {
            const s = getLabelStyle(config, 'title', {
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#6b7280',
              textAlign: 'center'
            }, 'donut');
            return {
              align: s.textAlign,
              color: s.color,
              font: {
                size: parseInt(s.fontSize),
                weight: s.fontWeight,
                style: s.fontStyle || 'normal'
              }
            };
          })()
        }
      },
      interaction: { mode: null },
      events: []
    };

    return (
      <div className="chart-widget empty-state" style={{ position: 'relative', height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ width: '100%', height: '100%' }}>
          <Doughnut data={ghostData} options={ghostOptions} />
        </div>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          color: style?.textColor || '#6b7280',
          fontStyle: 'italic',
          width: '100%',
          pointerEvents: 'none',
          zIndex: 10
        }}>
          {config.emptyStateMessage || "No data to render"}
        </div>
      </div>
    );
  }

  // Process labels for legend if requested
  const totalValueValue = (data?.values || []).reduce((a, b) => a + Number(b), 0);
  const processedDonutLabels = (data?.labels || []).map((label, i) => {
    if (style.legendMode === 'label-value') {
      const val = data.values[i] || 0;
      return `${label}: ${val.toLocaleString()}`;
    }
    if (style.legendMode === 'label-percent') {
      const val = data.values[i] || 0;
      const pct = totalValueValue > 0 ? Math.round((val / totalValueValue) * 100) : 0;
      return `${label}: ${pct}%`;
    }
    return label;
  });

  const chartData = {
    labels: processedDonutLabels,
    datasets: [
      {
        label: data?.seriesLabel || config.title || 'Data',
        data: data?.values || [],
        backgroundColor: (data?.colors && Array.isArray(data.colors)) ? data.colors : ((style?.chartColors?.length > 0) ? style.chartColors : [
          'rgba(34, 197, 94, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(234, 179, 8, 0.8)',
          'rgba(59, 130, 246, 0.8)',
          'rgba(139, 92, 246, 0.8)',
          'rgba(236, 72, 153, 0.8)',
          'rgba(14, 165, 233, 0.8)',
          'rgba(168, 162, 158, 0.8)',
          'rgba(249, 115, 22, 0.8)',
          'rgba(16, 185, 129, 0.8)'
        ]),
        borderWidth: 1
      }
    ]
  };

  return (
    <div className="chart-widget" style={{ pointerEvents: 'none' }}>
      <Doughnut data={chartData} options={{
        maintainAspectRatio: false,
        animation: isExporting ? false : {
          duration: 1000,
          easing: 'easeInOutQuart'
        },
        plugins: {
          legend: {
            display: config.legendPosition !== 'none',
            position: config.legendPosition || 'top',
            labels: {
              boxWidth: 12,
              padding: 10,
              font: { size: 10 }
            }
          },
          title: {
            display: !!config.title,
            text: config.title,
            position: config.titlePosition || 'top',
            ...(() => {
              const s = getLabelStyle(config, 'title', {
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#000000',
                textAlign: 'center'
              }, 'donut');
              return {
                align: s.textAlign,
                color: s.color,
                font: {
                  size: parseInt(s.fontSize),
                  weight: s.fontWeight,
                  style: s.fontStyle || 'normal'
                }
              };
            })()
          },
          datalabels: {
            color: '#fff',
            font: {
              weight: 'bold',
              size: 11
            },
            formatter: (value) => value.toLocaleString(),
            anchor: 'center',
            align: 'center',
            display: (context) => (style.showDataLabels !== false) && context.dataset.data[context.dataIndex] > 0
          }
        }
      }} />
    </div>
  );
};

const RadarChartWidget = ({ data, config, style, isExporting }) => {
  // Check if this is placeholder data
  const isPlaceholder = data && data[PLACEHOLDER_FLAG];

  // Show placeholder message (setup mode)
  if (isPlaceholder) {
    return (
      <div className="chart-widget placeholder-widget">
        {config.title && (
          <div
            className="widget-title"
            style={getLabelStyle(config, 'title', {
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#000000',
              textAlign: 'center'
            }, 'radar')}
          >
            {config.title}
          </div>
        )}
        <div className="placeholder-message">
          <div className="placeholder-icon">📡</div>
          <div className="placeholder-text">{data?.reason || 'Select a data source'}</div>
          <div className="placeholder-hint">Use the wizard to configure data</div>
        </div>
      </div>
    );
  }

  // Show empty state if configured but no data
  if (!data?.labels?.length && !data?.values?.length) {
    // Show ghost chart with message if configured but no data
    const ghostData = {
      labels: ['', '', '', '', ''],
      datasets: [{
        data: [10, 10, 10, 10, 10],
        backgroundColor: '#f3f4f6',
        borderColor: '#e5e7eb',
        borderWidth: 1
      }]
    };

    const ghostOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        datalabels: { display: false },
        title: {
          display: !!config.title,
          text: config.title,
          position: config.titlePosition || 'top',
          ...(() => {
            const s = getLabelStyle(config, 'title', {
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#6b7280',
              textAlign: 'center'
            }, 'radar');
            return {
              align: s.textAlign,
              color: s.color,
              font: {
                size: parseInt(s.fontSize),
                weight: s.fontWeight,
                style: s.fontStyle || 'normal'
              }
            };
          })()
        }
      },
      scales: {
        r: {
          ticks: { display: false },
          pointLabels: { display: false }
        }
      },
      interaction: { mode: null },
      events: []
    };

    return (
      <div className="chart-widget empty-state" style={{ position: 'relative', height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ width: '100%', height: '100%' }}>
          <Radar data={ghostData} options={ghostOptions} />
        </div>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          color: style?.textColor || '#6b7280',
          fontStyle: 'italic',
          width: '100%',
          pointerEvents: 'none',
          zIndex: 10
        }}>
          {config.emptyStateMessage || "No data to render"}
        </div>
      </div>
    );
  }

  const chartData = {
    labels: data?.labels || [],
    datasets: [
      {
        label: config.title || 'Data',
        data: data?.values || [],
        backgroundColor: 'rgba(59, 130, 246, 0.2)',
        borderColor: 'rgb(59, 130, 246)',
        pointBackgroundColor: Array.isArray(data?.colors) ? data.colors : 'rgb(59, 130, 246)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgb(59, 130, 246)'
      }
    ]
  };

  return (
    <div className="chart-widget" style={{ pointerEvents: 'none' }}>
      <Radar data={chartData} options={{
        maintainAspectRatio: false,
        animation: isExporting ? false : {
          duration: 1000,
          easing: 'easeInOutQuart'
        },
        plugins: {
          legend: {
            display: config.legendPosition !== 'none',
            position: config.legendPosition || 'top',
          },
          title: {
            display: !!config.title,
            text: config.title,
            position: config.titlePosition || 'top',
            ...(() => {
              const s = getLabelStyle(config, 'title', {
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#000000',
                textAlign: 'center'
              }, 'radar');
              return {
                align: s.textAlign,
                color: s.color,
                font: {
                  size: parseInt(s.fontSize),
                  weight: s.fontWeight,
                  style: s.fontStyle || 'normal'
                }
              };
            })()
          },
          datalabels: {
            color: '#fff',
            font: {
              weight: 'bold',
              size: 11
            },
            formatter: (value) => value.toLocaleString(),
            anchor: 'center',
            align: 'center',
            display: (context) => context.dataset.data[context.dataIndex] > 0
          }
        }
      }} />
    </div>
  );
};

const PolarAreaChartWidget = ({ data, config, style, isExporting }) => {
  // Check if this is placeholder data
  const isPlaceholder = data && data[PLACEHOLDER_FLAG];

  // Show placeholder message (setup mode)
  if (isPlaceholder) {
    return (
      <div className="chart-widget placeholder-widget">
        {config.title && (
          <div
            className="widget-title"
            style={getLabelStyle(config, 'title', {
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#000000',
              textAlign: 'center'
            }, 'polar')}
          >
            {config.title}
          </div>
        )}
        <div className="placeholder-message">
          <div className="placeholder-icon">🎯</div>
          <div className="placeholder-text">{data?.reason || 'Select a data source'}</div>
          <div className="placeholder-hint">Use the wizard to configure data</div>
        </div>
      </div>
    );
  }

  // Show empty state if configured but no data
  if (!data?.labels?.length && !data?.values?.length) {
    // Show ghost chart with message if configured but no data
    const ghostData = {
      labels: ['Empty'],
      datasets: [{
        data: [1],
        backgroundColor: ['#f3f4f6'],
        borderWidth: 0
      }]
    };

    const ghostOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        datalabels: { display: false },
        title: {
          display: !!config.title,
          text: config.title,
          position: config.titlePosition || 'top',
          ...(() => {
            const s = getLabelStyle(config, 'title', {
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#6b7280',
              textAlign: 'center'
            }, 'polar');
            return {
              align: s.textAlign,
              color: s.color,
              font: {
                size: parseInt(s.fontSize),
                weight: s.fontWeight,
                style: s.fontStyle || 'normal'
              }
            };
          })()
        }
      },
      interaction: { mode: null },
      events: []
    };

    return (
      <div className="chart-widget empty-state" style={{ position: 'relative', height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ width: '100%', height: '100%' }}>
          <PolarArea data={ghostData} options={ghostOptions} />
        </div>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          color: style?.textColor || '#6b7280',
          fontStyle: 'italic',
          width: '100%',
          pointerEvents: 'none',
          zIndex: 10
        }}>
          {config.emptyStateMessage || "No data to render"}
        </div>
      </div>
    );
  }

  const chartData = {
    labels: data?.labels || [],
    datasets: [
      {
        data: data?.values || [],
        backgroundColor: (data?.colors && Array.isArray(data.colors)) ? data.colors : [
          'rgba(255, 99, 132, 0.8)',
          'rgba(75, 192, 192, 0.8)',
          'rgba(255, 205, 86, 0.8)',
          'rgba(201, 203, 207, 0.8)',
          'rgba(54, 162, 235, 0.8)',
          'rgba(139, 92, 246, 0.8)',
          'rgba(236, 72, 153, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(249, 115, 22, 0.8)',
          'rgba(168, 162, 158, 0.8)'
        ]
      }
    ]
  };

  return (
    <div className="chart-widget" style={{ pointerEvents: 'none' }}>
      <PolarArea data={chartData} options={{
        maintainAspectRatio: false,
        animation: isExporting ? false : {
          duration: 1000,
          easing: 'easeInOutQuart'
        },
        plugins: {
          legend: {
            display: config.legendPosition !== 'none',
            position: config.legendPosition || 'top',
          },
          title: {
            display: !!config.title,
            text: config.title,
            position: config.titlePosition || 'top',
            ...(() => {
              const s = getLabelStyle(config, 'title', {
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#000000',
                textAlign: 'center'
              }, 'polar');
              return {
                align: s.textAlign,
                color: s.color,
                font: {
                  size: parseInt(s.fontSize),
                  weight: s.fontWeight,
                  style: s.fontStyle || 'normal'
                }
              };
            })()
          },
          datalabels: {
            color: '#fff',
            font: {
              weight: 'bold',
              size: 11
            },
            formatter: (value) => value.toLocaleString(),
            anchor: 'center',
            align: 'center',
            display: (context) => context.dataset.data[context.dataIndex] > 0
          }
        }
      }} />
    </div>
  );
};

const BubbleChartWidget = ({ data, config, style, isExporting, dataContext, theme }) => {
  const chartRef = useRef(null);
  const isPlaceholder = data && data[PLACEHOLDER_FLAG];

  if (isPlaceholder) {
    return (
      <div className="chart-widget placeholder-widget">
        {config.title && (
          <div
            className="widget-title"
            style={getLabelStyle(config, 'title', {
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#000000',
              textAlign: 'center'
            }, 'bubble')}
          >
            {config.title}
          </div>
        )}
        <div className="placeholder-message">
          <div className="placeholder-icon">🫧</div>
          <div className="placeholder-text">{data?.reason || 'Select a data source'}</div>
          <div className="placeholder-hint">Use the wizard to configure data</div>
        </div>
      </div>
    );
  }

  if (!data?.values?.length) {
    return (
      <div className="chart-widget empty-state"
        style={{
          backgroundColor: style?.backgroundColor || 'transparent',
          color: style?.textColor || undefined,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: '100px',
          border: '1px solid #efefef',
          borderRadius: '4px'
        }}
      >
        {config.title && (
          <div
            className="widget-title"
            style={{
              padding: '8px 12px',
              fontSize: config.labelStyles?.title?.fontSize || (config.titleFontSize ? `${config.titleFontSize}px` : undefined),
              fontWeight: config.labelStyles?.title?.fontWeight || config.titleFontWeight || 'bold',
              fontStyle: config.labelStyles?.title?.fontStyle || config.titleFontStyle || 'normal',
              color: config.labelStyles?.title?.color || config.titleColor || undefined,
              textAlign: config.labelStyles?.title?.textAlign || config.titleAlignment || 'left',
              borderBottom: '1px solid #eee'
            }}
          >
            {config.title}
          </div>
        )}
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          color: style?.textColor || '#888',
          fontStyle: 'italic',
          textAlign: 'center'
        }}>
          {config.emptyStateMessage || "No data to render"}
        </div>
      </div>
    );
  }

  const chartData = {
    datasets: (data?.values || []).map((val, i) => ({
      label: data.labels?.[i] || `Item ${i + 1}`,
      data: [val],
      backgroundColor: style.mainChartColor ? (style.mainChartColor + 'CC') : 'rgba(255, 99, 132, 0.7)',
      borderColor: style.mainChartColor || 'rgba(255, 99, 132, 1)',
      borderWidth: 1,
    }))
  };

  const legendStyle = getLabelStyle(config, 'legend', { color: '#666' }, 'bubble', dataContext);

  const isLegendTop = config.legendPosition === 'top' || !config.legendPosition;
  const isLegendBottom = config.legendPosition === 'bottom';
  const showLegend = config.legendPosition !== 'none';

  return (
    <div className="chart-widget" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {showLegend && isLegendTop && (
        <CustomChartLegend
          chartRef={chartRef}
          config={config}
          dataContext={dataContext}
          chartType="bubble"
          data={data}
        />
      )}

      <div style={{ flex: 1, width: '100%', position: 'relative', minHeight: '300px' }}>
        <Bubble ref={chartRef} data={chartData} options={{
          maintainAspectRatio: false,
          animation: isExporting ? false : {
            duration: 1000,
            easing: 'easeInOutQuart'
          },
          plugins: {
            legend: {
              display: false // Using custom React legend
            },
            datalabels: {
              display: style.showDataLabels === true ? 'auto' : false,
              formatter: (value, context) => {
                return context.dataset.label || '';
              },
              color: getLabelStyle(config, 'legend', { color: '#333' }, 'bubble', dataContext).color,
              backgroundColor: getLabelStyle(config, 'legend', {}, 'bubble', dataContext).backgroundColor,
              borderRadius: 3,
              font: {
                weight: getLabelStyle(config, 'legend', { fontWeight: 'bold' }, 'bubble', dataContext).fontWeight,
                size: parseInt(getLabelStyle(config, 'legend', { fontSize: '11px' }, 'bubble', dataContext).fontSize)
              },
              padding: 4,
              clip: false,
              anchor: 'end',
              align: 'top',
              offset: 4
            },
            tooltip: {
              backgroundColor: getLabelStyle(config, 'tooltip', { backgroundColor: 'rgba(0,0,0,0.8)' }, 'bubble', dataContext).backgroundColor,
              titleColor: getLabelStyle(config, 'tooltip', { color: '#fff' }, 'bubble', dataContext).color,
              bodyColor: getLabelStyle(config, 'tooltip', { color: '#fff' }, 'bubble', dataContext).color,
              titleFont: {
                size: parseInt(getLabelStyle(config, 'tooltip', { fontSize: '12px' }, 'bubble', dataContext).fontSize),
                weight: getLabelStyle(config, 'tooltip', { fontWeight: 'bold' }, 'bubble', dataContext).fontWeight
              },
              bodyFont: {
                size: parseInt(getLabelStyle(config, 'tooltip', { fontSize: '12px' }, 'bubble', dataContext).fontSize),
                weight: getLabelStyle(config, 'tooltip', { fontWeight: 'normal' }, 'bubble', dataContext).fontWeight
              },
              callbacks: {
                label: (context) => {
                  const label = context.dataset.label || '';
                  const x = context.raw.x;
                  const y = context.raw.y;
                  const r = context.raw.r;
                  return `${label}: (X: ${x}, Y: ${y}, Size: ${r})`;
                }
              }
            },
            title: {
              display: !!config.title,
              text: config.title,
              position: 'top',
              padding: { top: 10, bottom: 20 },
              ...(() => {
                const s = getLabelStyle(config, 'title', {
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: '#000000',
                  textAlign: 'center'
                }, 'bubble');
                return {
                  align: s.textAlign,
                  color: s.color,
                  font: {
                    size: parseInt(s.fontSize),
                    weight: s.fontWeight,
                    style: s.fontStyle || 'normal'
                  }
                };
              })()
            }
          },
          layout: {
            padding: {
              top: 20,
              right: 40,
              bottom: 20,
              left: 20
            }
          },
          scales: {
            x: {
              grace: '10%',
              ticks: {
                color: getLabelStyle(config, "axisLabels", { color: "#666" }, "bubble", dataContext).color,
                font: {
                  size: parseInt(getLabelStyle(config, "axisLabels", { fontSize: "11px" }, "bubble", dataContext).fontSize),
                  weight: getLabelStyle(config, "axisLabels", { fontWeight: "normal" }, "bubble", dataContext).fontWeight
                }
              },
              title: {
                display: true,
                text: config.xLabel || config.dataConfig?.xField || "X Axis",
                color: getLabelStyle(config, "axisLabels", { color: "#666" }, "bubble", dataContext).color,
                font: {
                  size: parseInt(getLabelStyle(config, "axisLabels", { fontSize: "11px" }, "bubble", dataContext).fontSize),
                  weight: "bold"
                }
              }
            },
            y: {
              grace: '10%',
              ticks: {
                color: getLabelStyle(config, "axisLabels", { color: "#666" }, "bubble", dataContext).color,
                font: {
                  size: parseInt(getLabelStyle(config, "axisLabels", { fontSize: "11px" }, "bubble", dataContext).fontSize),
                  weight: getLabelStyle(config, "axisLabels", { fontWeight: "normal" }, "bubble", dataContext).fontWeight
                }
              },
              title: {
                display: true,
                text: config.yLabel || config.dataConfig?.yField || config.dataConfig?.valueField || "Y Axis",
                color: getLabelStyle(config, "axisLabels", { color: "#666" }, "bubble", dataContext).color,
                font: {
                  size: parseInt(getLabelStyle(config, "axisLabels", { fontSize: "11px" }, "bubble", dataContext).fontSize),
                  weight: "bold"
                }
              }
            }
          }
        }} />
      </div>

      {showLegend && isLegendBottom && (
        <CustomChartLegend
          chartRef={chartRef}
          config={config}
          dataContext={dataContext}
          chartType="bubble"
          data={data}
        />
      )}
    </div>
  );
};

const ScatterChartWidget = ({ data, config, style, isExporting, dataContext, theme }) => {
  const chartRef = useRef(null);
  const isPlaceholder = data && data[PLACEHOLDER_FLAG];

  if (isPlaceholder) {
    return (
      <div className="chart-widget placeholder-widget">
        {config.title && (
          <div
            className="widget-title"
            style={getLabelStyle(config, 'title', {
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#000000',
              textAlign: 'center'
            }, 'scatter')}
          >
            {config.title}
          </div>
        )}
        <div className="placeholder-message">
          <div className="placeholder-icon">⛯</div>
          <div className="placeholder-text">{data?.reason || 'Select a data source'}</div>
          <div className="placeholder-hint">Use the wizard to configure data</div>
        </div>
      </div>
    );
  }

  if (!data?.values?.length) {
    return (
      <div className="chart-widget empty-state" style={{ backgroundColor: style?.backgroundColor || 'transparent', color: style?.textColor || undefined, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100px', border: '1px solid #efefef', borderRadius: '4px' }}>
        {config.title && <div className="widget-title" style={{ padding: '8px 12px', fontSize: config.labelStyles?.title?.fontSize || (config.titleFontSize ? `${config.titleFontSize}px` : undefined), fontWeight: config.labelStyles?.title?.fontWeight || config.titleFontWeight || 'bold', fontStyle: config.labelStyles?.title?.fontStyle || config.titleFontStyle || 'normal', color: config.labelStyles?.title?.color || config.titleColor || undefined, textAlign: config.labelStyles?.title?.textAlign || config.titleAlignment || 'left', borderBottom: '1px solid #eee' }}>{config.title}</div>}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', color: style?.textColor || '#888', fontStyle: 'italic', textAlign: 'center' }}>{config.emptyStateMessage || "No data to render"}</div>
      </div>
    );
  }

  const chartData = {
    datasets: [
      {
        label: config.title || 'Data',
        data: data?.values || [],
        backgroundColor: 'rgba(255, 99, 132, 1)'
      }
    ]
  };

  const legendStyle = getLabelStyle(config, 'legend', { color: '#666' }, 'scatter', dataContext);

  const isLegendTop = config.legendPosition === 'top' || !config.legendPosition;
  const isLegendBottom = config.legendPosition === 'bottom';
  const showLegend = config.legendPosition !== 'none';

  return (
    <div className="chart-widget" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {showLegend && isLegendTop && (
        <CustomChartLegend
          chartRef={chartRef}
          config={config}
          dataContext={dataContext}
          chartType="scatter"
          data={data}
        />
      )}

      <div style={{ flex: 1, width: '100%', position: 'relative', minHeight: '300px' }}>
        <Scatter ref={chartRef} data={chartData} options={{
          maintainAspectRatio: false,
          animation: isExporting ? false : {
            duration: 1000,
            easing: 'easeInOutQuart'
          },
          plugins: {
            legend: {
              display: false // Using custom React legend
            },
            datalabels: {
              display: style.showDataLabels === false ? false : 'auto',
              formatter: (value, context) => {
                return context.dataset.label || '';
              },
              color: getLabelStyle(config, 'legend', { color: '#333' }, 'scatter', dataContext).color,
              backgroundColor: getLabelStyle(config, 'legend', {}, 'scatter', dataContext).backgroundColor,
              borderRadius: 3,
              font: {
                weight: getLabelStyle(config, 'legend', { fontWeight: 'bold' }, 'scatter', dataContext).fontWeight,
                size: parseInt(getLabelStyle(config, 'legend', { fontSize: '11px' }, 'scatter', dataContext).fontSize)
              },
              padding: 4,
              clip: false,
              anchor: 'end',
              align: 'top',
              offset: 4
            },
            tooltip: {
              backgroundColor: getLabelStyle(config, 'tooltip', { backgroundColor: 'rgba(0,0,0,0.8)' }, 'scatter', dataContext).backgroundColor,
              titleColor: getLabelStyle(config, 'tooltip', { color: '#fff' }, 'scatter', dataContext).color,
              bodyColor: getLabelStyle(config, 'tooltip', { color: '#fff' }, 'scatter', dataContext).color,
              titleFont: {
                size: parseInt(getLabelStyle(config, 'tooltip', { fontSize: '12px' }, 'scatter', dataContext).fontSize),
                weight: getLabelStyle(config, 'tooltip', { fontWeight: 'bold' }, 'scatter', dataContext).fontWeight
              },
              bodyFont: {
                size: parseInt(getLabelStyle(config, 'tooltip', { fontSize: '12px' }, 'scatter', dataContext).fontSize),
                weight: getLabelStyle(config, 'tooltip', { fontWeight: 'normal' }, 'scatter', dataContext).fontWeight
              },
              callbacks: {
                label: (context) => {
                  const label = context.dataset.label || '';
                  const x = context.raw.x;
                  const y = context.raw.y;
                  return `${label}: (X: ${x}, Y: ${y})`;
                }
              }
            },
            title: {
              display: !!config.title,
              text: config.title,
              position: 'top',
              padding: { top: 10, bottom: 20 },
              ...(() => {
                const s = getLabelStyle(config, 'title', {
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: '#000000',
                  textAlign: 'center'
                }, 'scatter');
                return {
                  align: s.textAlign,
                  color: s.color,
                  font: {
                    size: parseInt(s.fontSize),
                    weight: s.fontWeight,
                    style: s.fontStyle || 'normal'
                  }
                };
              })()
            }
          },
          layout: {
            padding: {
              top: 20,
              right: 40,
              bottom: 20,
              left: 20
            }
          },
          scales: {
            x: {
              grace: '10%',
              ticks: {
                color: getLabelStyle(config, "axisLabels", { color: "#666" }, "scatter", dataContext).color,
                font: {
                  size: parseInt(getLabelStyle(config, "axisLabels", { fontSize: "11px" }, "scatter", dataContext).fontSize),
                  weight: getLabelStyle(config, "axisLabels", { fontWeight: "normal" }, "scatter", dataContext).fontWeight
                }
              },
              title: {
                display: true,
                text: config.xLabel || config.dataConfig?.xField || "X Axis",
                color: getLabelStyle(config, "axisLabels", { color: "#666" }, "scatter", dataContext).color,
                font: {
                  size: parseInt(getLabelStyle(config, "axisLabels", { fontSize: "11px" }, "scatter", dataContext).fontSize),
                  weight: "bold"
                }
              }
            },
            y: {
              grace: '10%',
              ticks: {
                color: getLabelStyle(config, "axisLabels", { color: "#666" }, "scatter", dataContext).color,
                font: {
                  size: parseInt(getLabelStyle(config, "axisLabels", { fontSize: "11px" }, "scatter", dataContext).fontSize),
                  weight: getLabelStyle(config, "axisLabels", { fontWeight: "normal" }, "scatter", dataContext).fontWeight
                }
              },
              title: {
                display: true,
                text: config.yLabel || config.dataConfig?.yField || config.dataConfig?.valueField || "Y Axis",
                color: getLabelStyle(config, "axisLabels", { color: "#666" }, "scatter", dataContext).color,
                font: {
                  size: parseInt(getLabelStyle(config, "axisLabels", { fontSize: "11px" }, "scatter", dataContext).fontSize),
                  weight: "bold"
                }
              }
            }
          }
        }} />
      </div>

      {showLegend && isLegendBottom && (
        <CustomChartLegend
          chartRef={chartRef}
          config={config}
          dataContext={dataContext}
          chartType="scatter"
          data={data}
        />
      )}
    </div>
  );
};

// Network topology diagram: hub-and-spoke SVG built live from the scan's network_devices.
// Reusable across scan data (reads the array at render time). Each device node is
// color-coded by risk — red if it exposes open ports, gray otherwise — and labeled
// with its IP/hostname. Recomputes automatically when the loaded scan changes.
const NetworkDiagramWidget = ({ config, style, scanData }) => {
  const sourcePath = config?.sourcePath || 'network_devices';
  const devices = useMemo(() => {
    const raw = getScanPathValue(scanData, sourcePath);
    const arr = Array.isArray(raw) ? raw : [];
    // Prefer devices that are "interesting" (have open ports / system info); fall back to all.
    // Field mapping is generic so this works for any device-like array — network_devices
    // (ip/open_ports), AD computers at "Ad Scan.Computers" (name/Host/os), localhost, etc.
    const mapped = arr.map((d, i) => {
      const ip = d.ip || d.ip_address || d.address || d.host || d.Host || '';
      const hostname = d.hostname || d.host_name || d.name || d.Name || d.dnshostname || d.DNSHostName || d.Host || '';
      const deviceType = d.device_type || d.type || d.os || d.OperatingSystem || d['os version'] || '';
      const openPorts = d.open_ports && typeof d.open_ports === 'object' ? Object.keys(d.open_ports) : (Array.isArray(d.open_ports) ? d.open_ports : []);
      const portCount = openPorts.length;
      return { id: i, ip: String(ip), hostname: String(hostname), deviceType: String(deviceType), portCount, ports: openPorts.slice(0, 6) };
    }).filter(d => d.ip || d.hostname);
    const withPorts = mapped.filter(d => d.portCount > 0);
    const list = (withPorts.length ? withPorts : mapped);
    return list.slice(0, 16); // cap for readability
  }, [scanData, sourcePath]);

  // Hub label adapts to the topology kind (e.g. "Network", "Active Directory").
  const hubLabel = config?.hubLabel || 'Network';
  // Whether any node exposes ports — if none do (e.g. an AD computer topology), we omit
  // the "no open ports" line entirely so the cards read cleanly.
  const anyPorts = devices.some(d => d.portCount > 0);
  // SVG <text> doesn't wrap or clip, so long device names (e.g. FQDNs like
  // "HOST-01.corp.local") overflow the fixed-width node card. Truncate with an ellipsis
  // to a char budget that fits the card at the given font size.
  const fitLabel = (str, maxChars) => {
    const s = String(str || '');
    return s.length > maxChars ? `${s.slice(0, Math.max(1, maxChars - 1))}…` : s;
  };

  if (devices.length === 0) {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        {config?.title && <div style={{ fontWeight: 700, fontSize: '16px', marginBottom: 8, color: style?.textColor || '#0f172a' }}>{config.title}</div>}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontStyle: 'italic' }}>
          No devices to diagram.
        </div>
      </div>
    );
  }

  // Layout: hub in the center, devices on as many concentric rings as needed. Each ring
  // is filled ONLY to its angular capacity (cards spaced >= arcMin apart) and rings are
  // spaced >= ringGap apart, so cards can never overlap regardless of device count.
  const cardW = 112, cardH = 46;
  const hubR = 40;
  const arcMin = cardW + 18;       // minimum center-to-center spacing along a ring
  const ringGap = cardH + 30;      // radial distance between consecutive rings
  const rings = [];
  let remaining = devices.slice();
  let r = hubR + ringGap;          // first ring radius (clear of the hub)
  while (remaining.length) {
    const capacity = Math.max(1, Math.floor((2 * Math.PI * r) / arcMin));
    rings.push({ r, items: remaining.splice(0, capacity) });
    r += ringGap;
  }
  const outerR = rings.length ? rings[rings.length - 1].r : hubR + ringGap;
  const pad = cardW / 2 + 20;
  // Stretch the rings horizontally into an ellipse so the diagram fills wide report
  // blocks (which are much wider than tall) instead of rendering small and centered
  // with large side margins. This widens the viewBox to a landscape-friendly aspect,
  // so "preserveAspectRatio meet" scales the whole diagram up to use the available width.
  const xStretch = 1.6;
  const outerRx = outerR * xStretch;
  const W = Math.ceil(2 * (outerRx + pad));
  const H = Math.ceil(2 * (outerR + cardH / 2 + 16));
  const cx = W / 2, cy = H / 2;
  const riskColor = (d) => d.portCount === 0 ? { fill: '#eef2f7', stroke: '#94a3b8', text: '#475569' }
    : d.portCount <= 3 ? { fill: '#fdf6da', stroke: '#e6c252', text: '#7a5a12' }
    : { fill: '#fce7e7', stroke: '#e89090', text: '#8c3030' };

  // Offset alternate rings by half a step so adjacent rings' cards don't line up radially.
  const nodes = rings.flatMap((ring, ringIdx) => {
    const offset = (ringIdx % 2) * (Math.PI / ring.items.length);
    return ring.items.map((d, i) => {
      const angle = (i / ring.items.length) * 2 * Math.PI - Math.PI / 2 + offset;
      return { ...d, x: cx + ring.r * xStretch * Math.cos(angle), y: cy + ring.r * Math.sin(angle) };
    });
  });

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {config?.title && <div style={{ fontWeight: 700, fontSize: '16px', marginBottom: 6, color: style?.textColor || '#0f172a' }}>{config.title}</div>}
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: '100%' }} preserveAspectRatio="xMidYMid meet">
        {/* spokes */}
        {nodes.map(n => (
          <line key={`l-${n.id}`} x1={cx} y1={cy} x2={n.x} y2={n.y} stroke="#cbd5e1" strokeWidth="1.5" />
        ))}
        {/* center hub */}
        <circle cx={cx} cy={cy} r="40" fill="#093070" />
        <text x={cx} y={cy - 2} textAnchor="middle" fill="#ffffff" fontSize="13" fontWeight="700">{hubLabel}</text>
        <text x={cx} y={cy + 14} textAnchor="middle" fill="#a8c4ff" fontSize="10">{devices.length} {devices.length === 1 ? 'device' : 'devices'}</text>
        {/* device nodes */}
        {nodes.map(n => {
          const c = riskColor(n);
          return (
            <g key={`n-${n.id}`}>
              <title>{[n.ip || n.hostname, n.hostname && n.ip ? n.hostname : n.deviceType].filter(Boolean).join(' — ')}</title>
              <rect x={n.x - cardW / 2} y={n.y - cardH / 2} width={cardW} height={cardH} rx="8" fill={c.fill} stroke={c.stroke} strokeWidth="2" />
              <text x={n.x} y={n.y - 8} textAnchor="middle" fill={c.text} fontSize="11" fontWeight="700">{fitLabel(n.ip || n.hostname, 16)}</text>
              <text x={n.x} y={n.y + 6} textAnchor="middle" fill={c.text} fontSize="9">{fitLabel(n.hostname && n.ip ? n.hostname : n.deviceType, 18)}</text>
              {(anyPorts || n.portCount > 0) && (
                <text x={n.x} y={n.y + 18} textAnchor="middle" fill={c.text} fontSize="8.5">{n.portCount ? `${n.portCount} open port${n.portCount > 1 ? 's' : ''}` : 'no open ports'}</text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

const TableWidget = ({ data, config, style, pagination, theme, scanData }) => {
  const containerRef = useRef(null);
  const tableRef = useRef(null);

  // Check if this is placeholder data
  const isPlaceholder = data && data[PLACEHOLDER_FLAG];

  // Check if data has table structure with headers and rows
  const hasTableStructure = data && data.headers && Array.isArray(data.headers) && Array.isArray(data.rows);

  // Default to empty - DO NOT use sample data that looks real
  let headers = [];
  let rows = [];

  if (hasTableStructure) {
    headers = data.headers || [];
    rows = data.rows || [];
  } else if (data && Array.isArray(data)) {
    // If data is an array of objects, try to infer structure
    if (data.length > 0 && typeof data[0] === 'object') {
      // Filter out internal tracking fields from headers
      headers = Object.keys(data[0]).filter(key => !TABLE_INTERNAL_FIELDS.includes(key));
      rows = data.map(item => headers.map(header => {
        const value = item[header];
        if (value === null || value === undefined) return '';
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
      }));
    } else if (data.length > 0) {
      // Array of primitives
      headers = ['Value'];
      rows = data.map(item => [String(item)]);
    }
  } else if (data && typeof data === 'object') {
    // Single object - show as key-value pairs
    headers = ['Property', 'Value'];
    rows = Object.entries(data)
      .filter(([key]) => !TABLE_INTERNAL_FIELDS.includes(key) && !key.startsWith('_'))
      .map(([key, value]) => [key, typeof value === 'object' ? JSON.stringify(value) : String(value)]);
  }

  // Apply pagination if configured
  if (pagination && pagination.itemsPerPage > 0 && Array.isArray(rows)) {
    const { startIndex, endIndex } = pagination;
    // CRITICAL: Slice the rows to show only the items for the current page
    // console.log(`[TableWidget] Slicing rows: ${startIndex} to ${endIndex} (Total: ${rows.length})`);
    rows = rows.slice(startIndex, endIndex);
  }

  // Get label styles using standardized system
  const headerStyle = getLabelStyle(config, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, 'table');

  const rowStyle = getLabelStyle(config, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, 'table');

  // Legacy fallbacks for backwards compatibility with style tab properties
  const tableFontSize = rowStyle.fontSize || (style?.tableFontSize ? `${style.tableFontSize}px` : '14px');
  const headerBackground = headerStyle.backgroundColor || style?.tableHeaderBackground || '#f5f5f5';
  const headerTextColor = headerStyle.color || style?.tableHeaderTextColor || '#333333';
  const dataTextColor = rowStyle.color || style?.tableDataTextColor || '#212529';
  const rowBackground = rowStyle.backgroundColor || 'transparent';
  const widgetBackground = style?.backgroundColor || 'transparent';
  const widgetTextColor = style?.textColor || undefined;

  const isEmpty = !rows || rows.length === 0;

  return (
    <div
      className="table-widget"
      ref={containerRef}
      style={{
        backgroundColor: widgetBackground,
        color: widgetTextColor,
        display: 'flex',
        flexDirection: config.titlePosition === 'bottom' ? 'column-reverse' :
          config.titlePosition === 'left' ? 'row' :
            config.titlePosition === 'right' ? 'row-reverse' : 'column',
        alignItems: (config.titlePosition === 'left' || config.titlePosition === 'right') ? 'flex-start' : 'stretch',
        gap: '10px',
        height: '100%'
      }}
    >
      {config.title && (
        <div
          className="widget-title"
          style={{
            ...getLabelStyle(config, 'title', {
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#000000',
              textAlign: 'left'
            }, 'table'),
            padding: '4px 0'
          }}
        >
          {config.title}
        </div>
      )}
      <div className="table-container-no-scroll">
        <table ref={tableRef} style={{ fontSize: tableFontSize, width: '100%' }}>
          <thead style={{ backgroundColor: headerBackground, position: 'sticky', top: 0, zIndex: 1 }}>
            <tr>
              {headers.map((header, idx) => (
                <th
                  key={idx}
                  style={{
                    backgroundColor: headerBackground,
                    color: headerTextColor,
                    padding: '8px',
                    textAlign: headerStyle.textAlign || 'left',
                    fontWeight: headerStyle.fontWeight || 600,
                    fontStyle: headerStyle.fontStyle || 'normal'
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isEmpty ? (
              <tr>
                <td
                  colSpan={headers.length || 1}
                  style={{
                    padding: '30px 20px',
                    textAlign: 'center',
                    color: style?.tableDataTextColor || '#6c757d',
                    fontStyle: 'italic',
                    verticalAlign: 'middle'
                  }}
                >
                  {config.emptyStateMessage || "No data to render"}
                </td>
              </tr>
            ) : (
              rows.map((row, rowIdx) => (
                <tr key={rowIdx}>
                  {row.map((cell, cellIdx) => (
                    <td
                      key={cellIdx}
                      style={{
                        color: dataTextColor,
                        backgroundColor: rowBackground,
                        padding: '8px',
                        fontWeight: rowStyle.fontWeight || 'normal',
                        fontStyle: rowStyle.fontStyle || 'normal',
                        textAlign: rowStyle.textAlign || 'left'
                      }}
                    >
                      {(() => {
                        if (typeof cell !== 'string' || !cell) return cell;

                        // Resolve live scan-data tokens (e.g. {{ scan:localhost.Hostname | value }})
                        // so AI-generated table cells show real values, not raw token syntax.
                        if (cell.includes('{{')) cell = resolveScanTokens(cell, scanData);

                        // ONLY apply automatic status badges if the column is explicitly 'conditional'
                        // This prevents regular data fields from being turned into badges unintentionally
                        const colConfig = config?.columns?.[cellIdx];
                        if (colConfig?.columnType !== 'conditional') return cell;

                        const lower = cell.toLowerCase();
                        let badgeColor = null;

                        // Status Keyword Mapping (Vibrant Badges)
                        if (lower.includes('fully enabled') || lower === 'yes' || lower === 'healthy' || lower === 'success' || lower === 'enabled' || lower === 'good' || lower === 'stable') {
                          badgeColor = theme?.statusColors?.green || '#28a745';
                        } else if (lower.includes('partially') || lower === 'warning' || lower === 'moderate' || lower === 'average') {
                          badgeColor = theme?.statusColors?.yellow || '#ffc107';
                        } else if (lower.includes('disabled') || lower === 'no' || lower === 'critical' || lower === 'fail' || lower.includes('needs review') || lower === 'danger' || lower === 'critical') {
                          badgeColor = theme?.statusColors?.red || '#dc3545';
                        }

                        if (badgeColor) {
                          return (
                            <span style={{
                              backgroundColor: badgeColor,
                              color: 'white',
                              padding: '2px 10px',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              display: 'inline-block',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                            }}>
                              {cell}
                            </span>
                          );
                        }

                        return cell;
                      })()}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const GaugeWidget = ({ data, config, style }) => {
  // Check if this is placeholder data
  const isPlaceholder = data && data[PLACEHOLDER_FLAG];

  // Show placeholder message if no real data
  if (isPlaceholder) {
    return (
      <div
        className="gauge-widget placeholder-widget"
        style={{
          display: 'flex',
          flexDirection: config.titlePosition === 'bottom' ? 'column-reverse' :
            config.titlePosition === 'left' ? 'row' :
              config.titlePosition === 'right' ? 'row-reverse' : 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          height: '100%'
        }}
      >
        {config.title && (
          <div
            className="widget-title"
            style={getLabelStyle(config, 'title', {
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#000000',
              textAlign: 'center'
            }, 'gauge')}
          >
            {config.title}
          </div>
        )}
        <div className="placeholder-message">
          <div className="placeholder-icon">🎚️</div>
          <div className="placeholder-text">{data?.reason || 'Select a data source'}</div>
          <div className="placeholder-hint">Use the wizard to configure data</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="gauge-widget"
      style={{
        display: 'flex',
        flexDirection: config.titlePosition === 'bottom' ? 'column-reverse' :
          config.titlePosition === 'left' ? 'row' :
            config.titlePosition === 'right' ? 'row-reverse' : 'column',
        alignItems: (config.titlePosition === 'left' || config.titlePosition === 'right') ? 'center' : 'stretch',
        justifyContent: (config.titlePosition === 'left' || config.titlePosition === 'right') ? 'flex-start' : 'center',
        gap: '20px',
        height: '100%'
      }}
    >
      {config.title && (
        <div
          className="widget-title"
          style={getLabelStyle(config, 'title', {
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#000000',
            textAlign: 'left'
          }, 'gauge')}
        >
          {config.title}
        </div>
      )}
      <div className="gauge-placeholder">
        <div
          className="gauge-value"
          style={getLabelStyle(config, 'value', {
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#212529',
            textAlign: 'center'
          }, 'gauge')}
        >
          {data?.value ?? 0}%
        </div>
        <div
          className="gauge-label"
          style={getLabelStyle(config, 'label', {
            fontSize: '14px',
            fontWeight: 'normal',
            color: '#666666',
            textAlign: 'center'
          }, 'gauge')}
        >
          {data?.label || 'Value'}
        </div>
      </div>
    </div>
  );
};

const StatCardWidget = ({ data, config, style, isEditing, onToggleEditMode, onUpdateCustomData, customData }) => {
  const [editValue, setEditValue] = useState('');
  const [editLabel, setEditLabel] = useState('');

  // Check if this is placeholder data
  const isPlaceholder = data && data[PLACEHOLDER_FLAG];

  // Initialize form data when entering edit mode
  useEffect(() => {
    if (isEditing) {
      const displayData = customData.value !== undefined ? customData : data;
      setEditValue(displayData?.value || 0);
      setEditLabel(customData?.label !== undefined ? customData.label : config.title || 'Statistic');
    }
  }, [isEditing, data, customData, config.title]);

  const handleSave = () => {
    onUpdateCustomData({
      value: parseFloat(editValue) || 0,
      label: editLabel
    });
    onToggleEditMode();
  };

  const displayData = customData.value !== undefined ? customData : data;
  const displayLabel = customData.label !== undefined ? customData.label : config.title;

  // Show placeholder message if no real data
  if (isPlaceholder && !customData.value) {
    return (
      <div
        className="stat-card-widget placeholder-widget"
        style={{
          display: 'flex',
          flexDirection: config.titlePosition === 'bottom' ? 'column-reverse' :
            config.titlePosition === 'left' ? 'row' :
              config.titlePosition === 'right' ? 'row-reverse' : 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          height: '100%'
        }}
      >
        {config.title && (
          <div
            className="widget-title"
            style={getLabelStyle(config, 'title', {
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#000000',
              textAlign: 'center'
            }, 'numeric')}
          >
            {config.title}
          </div>
        )}
        <div className="placeholder-message">
          <div className="placeholder-icon">📈</div>
          <div className="placeholder-text">{data?.reason || 'Select a data source'}</div>
          <div className="placeholder-hint">Use the wizard to configure data</div>
        </div>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="stat-card-widget editing">
        <div className="edit-controls">
          <div className="edit-field">
            <label>Value:</label>
            <input
              type="number"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              style={{ width: '100%', marginBottom: '8px' }}
            />
          </div>
          <div className="edit-field">
            <label>Label:</label>
            <input
              type="text"
              value={editLabel}
              onChange={(e) => setEditLabel(e.target.value)}
              style={{ width: '100%', marginBottom: '8px' }}
            />
          </div>
          <div className="edit-actions">
            <button onClick={handleSave} className="btn btn-sm btn-success">Save</button>
            <button onClick={onToggleEditMode} className="btn btn-sm btn-secondary">Cancel</button>
          </div>
        </div>
      </div>
    );
  }



  // New styling system
  const valueStyle = getLabelStyle(config, 'value', {
    fontSize: style.valueFontSize ? `${style.valueFontSize}px` : '24px',
    color: style.valueColor || style.textColor || '#000000',
    fontWeight: 'bold',
    fontStyle: 'normal',
    textAlign: 'inherit'
  }, 'numeric');

  const labelStyle = getLabelStyle(config, 'label', {
    fontSize: style.labelFontSize ? `${style.labelFontSize}px` : '14px',
    color: style.labelColor || '#666666',
    fontWeight: 'normal',
    fontStyle: 'normal',
    textAlign: 'inherit'
  }, 'numeric');

  return (
    <div
      className="stat-card-widget"
      style={{
        display: 'flex',
        flexDirection: config.titlePosition === 'bottom' ? 'column-reverse' :
          config.titlePosition === 'left' ? 'row' :
            config.titlePosition === 'right' ? 'row-reverse' : 'column',
        alignItems: (config.titlePosition === 'left' || config.titlePosition === 'right') ? 'center' : 'stretch',
        justifyContent: (config.titlePosition === 'left' || config.titlePosition === 'right') ? 'flex-start' : 'center',
        gap: '10px',
        height: '100%'
      }}
    >
      {config.title && (
        <div
          className="widget-title"
          style={getLabelStyle(config, 'title', {
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#000000',
            textAlign: 'left'
          }, 'numeric')}
        >
          {config.title}
        </div>
      )}
      <div className="stat-content-inner" style={{ display: 'flex', flexDirection: 'column', alignItems: 'inherit' }}>
        <div
          className="stat-value"
          style={{
            ...valueStyle,
            textAlign: valueStyle.textAlign || 'inherit'
          }}
        >
          {typeof displayData?.value === 'number' ? displayData.value.toLocaleString() : (displayData?.value ?? '--')}
        </div>
        <div
          className="stat-label"
          style={{
            ...labelStyle,
            textAlign: labelStyle.textAlign || 'inherit'
          }}
        >
          {displayLabel || 'Statistic'}
        </div>
      </div>
    </div>
  );
};

// ============================================
// SHARED HELPER: Variable Rendering
// ============================================
// Renders template variables with styled placeholders or actual values
const renderVariables = (content, organizationName, scanDate, mspInfo, clientInfo) => {
  if (!content) return [content];  // Return as array for consistency

  // Updated pattern: matches variables with optional pipe arguments (including quoted strings)
  // FIXED: Regex now captures the variable content more flexibly to allow custom formats like | "YYYY-MM-DD"
  const variablePattern = /(\{\{\s*(?:date_today|organization_name|tenant_name|tenant\.name|date_of_scan|MSP\.user\.first_name|MSP\.user\.last_name|MSP\.logo|MSP\.large_logo|client\.name|client\.email|client\.phone_number|client\.domain)(?:\s*\|\s*(?:[^}]+))?\s*\}\})/g;
  const parts = content.split(variablePattern);

  // Helper function to format date based on pipe
  const formatDate = (dateStr, formatOrCustom) => {
    if (!dateStr) return dateStr;

    try {
      // Parse the date string (assuming DD-MM-YYYY or similar format)
      const dateParts = dateStr.split(/[-\/]/);
      let date;

      // Try to parse in different formats
      if (dateParts.length === 3) {
        // Assume DD-MM-YYYY format
        date = new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);
      } else {
        date = new Date(dateStr);
      }

      if (isNaN(date.getTime())) return dateStr; // Return original if invalid

      // Handle custom format string (e.g., "YYYY MM dd")
      // Remove quotes if present
      let format = formatOrCustom;
      if (format && (format.startsWith('"') || format.startsWith("'"))) {
        format = format.slice(1, -1); // Remove quotes

        // Custom formatting logic
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const monthsLong = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        return format
          .replace(/YYYY/g, year)
          .replace(/YY/g, String(year).slice(-2))
          .replace(/MMMM/g, monthsLong[month - 1])
          .replace(/MMM/g, monthsShort[month - 1])
          .replace(/MM/g, String(month).padStart(2, '0'))
          .replace(/M/g, month)
          .replace(/dd/g, String(day).padStart(2, '0'))
          .replace(/d/g, day);
      }

      // Handle standard presets
      switch (format) {
        case 'short':
          // 1/1/26
          return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear().toString().slice(-2)}`;
        case 'medium':
          // Jan 1, 2026
          const monthShort = date.toLocaleDateString('en-US', { month: 'short' });
          return `${monthShort} ${date.getDate()}, ${date.getFullYear()}`;
        case 'long':
          // January 1, 2026
          const monthLong = date.toLocaleDateString('en-US', { month: 'long' });
          return `${monthLong} ${date.getDate()}, ${date.getFullYear()}`;
        default:
          // Default format with dashes: 1-1-26
          return `${date.getMonth() + 1}-${date.getDate()}-${date.getFullYear().toString().slice(-2)}`;
      }
    } catch (e) {
      return dateStr; // Return original on error
    }
  };

  return parts.map((part, index) => {
    // Safety check: skip undefined, null, or non-string parts
    if (!part || typeof part !== 'string') {
      return part;
    }

    // Handle standard variables
    if (part.match(/^\{\{\s*date_today/)) {
      // Current date
      let format = 'default';

      // Parse pipe argument
      const pipeMatch = part.match(/\|\s*(.+?)\s*\}\}/);
      if (pipeMatch) {
        const pipeContent = pipeMatch[1];
        // Check for quoted string (custom format)
        // Matches: "YYYY-MM-DD" or long:"YYYY-MM-DD"
        const quoteMatch = pipeContent.match(/["']([^"']+)["']/);
        if (quoteMatch) {
          // Keep quotes for formatDate to detect it's a custom format
          format = `"${quoteMatch[1]}"`;
        } else {
          // Standard preset (short, medium, long)
          format = pipeContent.trim();
        }
      }

      const today = new Date().toLocaleDateString('en-GB'); // Use user's locale or standard format
      return <span key={index}>{formatDate(today, format)}</span>;
    } else if (part.match(/^\{\{\s*organization_name/)) {
      // If we have actual organization name (report mode), show it
      if (organizationName) {
        return <span key={index}>{organizationName}</span>;
      }
      // Otherwise show styled placeholder (template mode)
      return (
        <span
          key={index}
          style={{
            backgroundColor: 'rgba(13, 110, 253, 0.15)',
            color: '#0d6efd',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '0.9em',
            fontWeight: '500',
            border: '1px dashed rgba(13, 110, 253, 0.4)'
          }}
          title="Will be replaced with organization name when generating report"
        >
          🏢 Organization Name
        </span>
      );
    } else if (part.match(/^\{\{\s*(?:tenant_name|tenant\.name)/)) {
      if (mspInfo?.tenant_name) {
        return <span key={index}>{mspInfo.tenant_name}</span>;
      }
      return (
        <span
          key={index}
          style={{
            backgroundColor: 'rgba(102, 16, 242, 0.15)',
            color: '#6610f2',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '0.9em',
            fontWeight: '500',
            border: '1px dashed rgba(102, 16, 242, 0.4)'
          }}
          title="Will be replaced with the tenant name when generating report"
        >
          🏢 Tenant Name
        </span>
      );
    } else if (part.match(/^\{\{\s*date_of_scan/)) {
      // Extract format from the part if present
      let format = 'default';
      const pipeMatch = part.match(/\|\s*(.+?)\s*\}\}/);
      if (pipeMatch) {
        const pipeContent = pipeMatch[1];
        const quoteMatch = pipeContent.match(/["']([^"']+)["']/);
        if (quoteMatch) {
          format = `"${quoteMatch[1]}"`;
        } else {
          format = pipeContent.trim();
        }
      } else {
        // Legacy/Fallback check if regex didn't catch it above (though it should)
        const formatMatch = part.match(/\|\s*(short|medium|long)/);
        if (formatMatch) format = formatMatch[1];
      }

      // If we have actual scan date (report mode), show it formatted
      if (scanDate) {
        const formattedDate = formatDate(scanDate, format);
        return <span key={index}>{formattedDate}</span>;
      }
      // Otherwise show styled placeholder (template mode)
      const placeholderText = format === 'short' ? '📅 Date (1/1/26)' :
        format === 'medium' ? '📅 Date (Jan 1, 2026)' :
          format === 'long' ? '📅 Date (January 1, 2026)' :
            '📅 Date (1-1-26)'; // Default when no pipe;
      return (
        <span
          key={index}
          style={{
            backgroundColor: 'rgba(25, 135, 84, 0.15)',
            color: '#198754',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '0.9em',
            fontWeight: '500',
            border: '1px dashed rgba(25, 135, 84, 0.4)'
          }}
          title={`Will be replaced with scan date in ${format} format when generating report`}
        >
          {placeholderText}
        </span>
      );
    } else if (part.match(/^\{\{\s*MSP\.user\.first_name/)) {
      const firstName = mspInfo?.name ? mspInfo.name.split(' ')[0] : null;
      if (firstName) {
        return <span key={index}>{firstName}</span>;
      }
      return (
        <span
          key={index}
          style={{
            backgroundColor: 'rgba(102, 16, 242, 0.15)',
            color: '#6610f2',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '0.9em',
            fontWeight: '500',
            border: '1px dashed rgba(102, 16, 242, 0.4)'
          }}
          title="Will be replaced with MSP user first name"
        >
          👤 MSP First Name
        </span>
      );
    } else if (part.match(/^\{\{\s*MSP\.user\.last_name/)) {
      const lastName = mspInfo?.name ? mspInfo.name.split(' ').slice(1).join(' ') : null;
      if (lastName) {
        return <span key={index}>{lastName}</span>;
      }
      return (
        <span
          key={index}
          style={{
            backgroundColor: 'rgba(102, 16, 242, 0.15)',
            color: '#6610f2',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '0.9em',
            fontWeight: '500',
            border: '1px dashed rgba(102, 16, 242, 0.4)'
          }}
          title="Will be replaced with MSP user last name"
        >
          👤 MSP Last Name
        </span>
      );
    } else if (part.match(/^\{\{\s*MSP\.logo/)) {
      if (mspInfo?.tenant_logo || mspInfo?.logo) {
        return <img key={index} src={mspInfo.tenant_logo || mspInfo.logo} alt="MSP Logo" style={{ maxHeight: '50px', maxWidth: '150px', verticalAlign: 'middle' }} />;
      }
      return <img key={index} src="/images/no-image-found.png" alt="MSP Logo Fallback" style={{ maxHeight: '50px', maxWidth: '150px', verticalAlign: 'middle', opacity: 0.7 }} />;
    } else if (part.match(/^\{\{\s*MSP\.large_logo/)) {
      if (mspInfo?.tenant_large_logo || mspInfo?.large_logo) {
        return <img key={index} src={mspInfo.tenant_large_logo || mspInfo.large_logo} alt="MSP Large Logo" style={{ maxHeight: '120px', maxWidth: '400px', verticalAlign: 'middle' }} />;
      }
      // Fallback to regular logo for large logo placeholder if large is not set
      if (mspInfo?.tenant_logo || mspInfo?.logo) {
        return <img key={index} src={mspInfo.tenant_logo || mspInfo.logo} alt="MSP Logo Fallback" style={{ maxHeight: '120px', maxWidth: '400px', verticalAlign: 'middle' }} />;
      }
      return <img key={index} src="/images/no-image-found.png" alt="MSP Large Logo Fallback" style={{ maxHeight: '120px', maxWidth: '400px', verticalAlign: 'middle', opacity: 0.7 }} />;
    } else if (part.match(/^\{\{\s*client\.name/)) {
      if (clientInfo?.name) return <span key={index}>{clientInfo.name}</span>;
      return <span key={index} style={{ color: '#dc3545' }}>[Client Name Not Set]</span>;
    } else if (part.match(/^\{\{\s*client\.email/)) {
      if (clientInfo?.email) return <span key={index}>{clientInfo.email}</span>;
      return <span key={index} style={{ color: '#dc3545' }}>[Client Email Not Set]</span>;
    } else if (part.match(/^\{\{\s*client\.phone_number/)) {
      if (clientInfo?.phone) return <span key={index}>{clientInfo.phone}</span>;
      return <span key={index} style={{ color: '#dc3545' }}>[Client Phone Not Set]</span>;
    } else if (part.match(/^\{\{\s*client\.domain/)) {
      if (clientInfo?.domain) return <span key={index}>{clientInfo.domain}</span>;
      return <span key={index} style={{ color: '#dc3545' }}>[Client Domain Not Set]</span>;
    } else if (part === 'organization_name' || part === 'date_of_scan' ||
      part === 'tenant_name' || part === 'tenant.name' ||
      part === 'MSP.user.first_name' || part === 'MSP.user.last_name' || part === 'MSP.logo' || part === 'MSP.large_logo' ||
      part === 'short' || part === 'medium' || part === 'long' || (part && part.match(/^\s*\|\s*(short|medium|long)\s*$/))) {
      // Skip the captured group names and pipe syntax
      return null;
    }
    return part;
  });
};

// Element Components
const TitleElement = ({ config, style, isEditing, onToggleEditMode, onUpdateCustomData, customData, onConfigUpdate, organizationName, scanDate, mspInfo, clientInfo }) => {
  const [editContent, setEditContent] = useState('');
  const [editSubtitle, setEditSubtitle] = useState('');

  // Initialize form data when entering edit mode
  useEffect(() => {
    if (isEditing) {
      const displayContent = customData.content !== undefined ? customData.content : config.content;
      const displaySubtitle = customData.subtitle !== undefined ? customData.subtitle : config.subtitle;
      setEditContent(displayContent || 'Title');
      setEditSubtitle(displaySubtitle || '');
    }
  }, [isEditing, config.content, config.subtitle, customData]);

  const handleSave = () => {
    // Update both customData and config to ensure persistence
    onUpdateCustomData({
      content: editContent,
      subtitle: editSubtitle
    });
    if (onConfigUpdate) {
      onConfigUpdate({
        content: editContent,
        subtitle: editSubtitle
      });
    }
    onToggleEditMode();
  };

  // Check both customData, config.content, and config.title (for backwards compatibility)
  const displayContent = customData.content !== undefined
    ? customData.content
    : (config.content !== undefined ? config.content : (config.title || 'Title'));
  const displaySubtitle = customData.subtitle !== undefined ? customData.subtitle : config.subtitle;

  if (isEditing) {
    return (
      <div className="title-element editing">
        <div className="edit-controls">
          <div className="edit-field">
            <label>Title:</label>
            <input
              type="text"
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              style={{ width: '100%', marginBottom: '8px' }}
            />
          </div>
          <div className="edit-field">
            <label>Subtitle:</label>
            <input
              type="text"
              value={editSubtitle}
              onChange={(e) => setEditSubtitle(e.target.value)}
              style={{ width: '100%', marginBottom: '8px' }}
            />
          </div>
          <div className="edit-actions">
            <button onClick={handleSave} className="btn btn-sm btn-success">Save</button>
            <button onClick={onToggleEditMode} className="btn btn-sm btn-secondary">Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  const textAlign = config.align || 'left';
  const verticalAlign = config.verticalAlign || 'top';

  // Map vertical alignment to flexbox justifyContent
  const getJustifyContent = () => {
    switch (verticalAlign) {
      case 'middle':
        return 'center';
      case 'bottom':
        return 'flex-end';
      default: // 'top'
        return 'flex-start';
    }
  };

  return (
    <div
      className="title-element"
      style={{
        color: style.textColor || undefined,
        fontSize: style.labelFontSize ? `${style.labelFontSize}px` : (style.fontSize === 'small' ? '0.875rem' : style.fontSize === 'large' ? '1.125rem' : '1.5rem'),
        fontWeight: style.fontWeight || undefined,
        backgroundColor: style.backgroundColor || 'transparent',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: getJustifyContent(),
        height: '100%',
        width: '100%',
        padding: style.backgroundColor && style.backgroundColor !== 'transparent' ? '10px' : '0',
        borderRadius: style.borderRadius !== undefined ? `${style.borderRadius}px` : '4px'
      }}
    >
      <h1 style={{ fontSize: 'inherit', margin: 0, textAlign: textAlign, width: '100%', fontWeight: 'inherit' }}>
        {renderVariables(displayContent || 'Title', organizationName, scanDate, mspInfo, clientInfo)}
      </h1>
      {displaySubtitle && (
        <p className="subtitle" style={{ fontSize: '0.6em', margin: '4px 0 0 0', textAlign: textAlign, width: '100%', opacity: 0.8, fontWeight: 'normal' }}>
          {renderVariables(displaySubtitle, organizationName, scanDate, mspInfo, clientInfo)}
        </p>
      )}
    </div>
  );
};

// Animated status indicator shown while the AI generates a report layout.
// A rounded box with pulsing dots, an orbiting ring, and cycling status text.
const AI_GENERATION_MESSAGES = [
  'Reading your scan data…',
  'Analyzing findings…',
  'Thinking…',
  'Scoring risk categories…',
  'Writing the narrative…',
  'Designing report sections…',
  'Building tables & charts…',
  'Generating layout…',
  'Placing widgets on pages…',
  'Polishing the report…'
];

const AIGeneratingIndicator = () => {
  const [msgIndex, setMsgIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const containerRef = useRef(null);

  useEffect(() => {
    // Bring the indicator into view when it appears, so the user sees it
    // immediately after clicking Generate Layout regardless of scroll position
    containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setMsgIndex(i => (i + 1) % AI_GENERATION_MESSAGES.length);
        setVisible(true);
      }, 250);
    }, 2400);
    return () => clearInterval(timer);
  }, []);

  return (
    <div ref={containerRef} style={{
      display: 'flex', alignItems: 'center', gap: '14px', padding: '16px 18px',
      borderRadius: '12px', background: 'linear-gradient(135deg, #f0f4ff, #f8f5ff)',
      border: '1px solid #dbe4ff', position: 'relative', overflow: 'hidden'
    }}>
      <style>{`
        @keyframes pns-ai-orbit { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pns-ai-dot { 0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; } 40% { transform: scale(1); opacity: 1; } }
        @keyframes pns-ai-shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }
      `}</style>
      {/* Box with pulsing dots and an orbiting ring around it */}
      <div style={{ position: 'relative', width: '46px', height: '46px', flexShrink: 0 }}>
        <div style={{
          position: 'absolute', inset: 0, borderRadius: '12px',
          border: '2px solid transparent', borderTopColor: '#4f6ef7', borderRightColor: '#a78bfa',
          animation: 'pns-ai-orbit 1.2s linear infinite'
        }} />
        <div style={{
          position: 'absolute', inset: '6px', borderRadius: '9px', background: '#ffffff',
          border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px'
        }}>
          {[0, 1, 2].map(i => (
            <span key={i} style={{
              width: '6px', height: '6px', borderRadius: '50%', background: '#4f6ef7',
              animation: `pns-ai-dot 1.2s ease-in-out ${i * 0.18}s infinite`
            }} />
          ))}
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: '14px', color: '#1e293b' }}>AI is building your report</div>
        <div style={{
          fontSize: '13px', color: '#4f6ef7', marginTop: '2px',
          transition: 'opacity 0.25s ease', opacity: visible ? 1 : 0
        }}>
          {AI_GENERATION_MESSAGES[msgIndex]}
        </div>
      </div>
      {/* Shimmer sweep across the card */}
      <div style={{
        position: 'absolute', top: 0, bottom: 0, left: 0, width: '35%',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)',
        animation: 'pns-ai-shimmer 2.2s ease-in-out infinite', pointerEvents: 'none'
      }} />
    </div>
  );
};

// Resolve live scan-data tokens in text content so AI-generated templates stay
// reusable across different scan data. Supported syntax:
//   {{ scan:path.to.value }}            -> raw value (arrays render their length)
//   {{ scan:path.to.array | count }}    -> number of items
//   {{ scan:path.to.array | join }}     -> comma-separated list
//   {{ scan:path.to.numbers | sum }}    -> numeric sum
//   {{ scan:path.to.numbers | avg }}    -> numeric average
//   {{ scan:path.to.array | first }}    -> first item
const getScanPathValue = (root, path) => {
  if (!root || !path) return undefined;
  let cur = root;
  for (const part of String(path).trim().split('.')) {
    if (cur === null || cur === undefined) return undefined;
    cur = cur[/^\d+$/.test(part) ? Number(part) : part];
  }
  return cur;
};

const SCAN_TOKEN_RE = /\{\{\s*scan:([^}|]+?)\s*(?:\|\s*(count|sum|avg|join|first|value))?\s*(?:\|\s*([^}]*?)\s*)?\}\}/;

// Resolve a single scan token to its string value (empty string if the path has no data).
// Placeholder shown when a token's path has no data on the current scan. We never
// invent a real value (no stale fallback), but we DO label the absence clearly so the
// reader sees "0" / "None" / "N/A" instead of a confusing blank.
//   count/sum/avg -> "0"  (a numeric measure of nothing is zero)
//   join          -> "None"  (an empty list)
//   value/first   -> "N/A"  (a single value that isn't present)
const scanTokenEmptyPlaceholder = (op) => {
  if (op === 'count' || op === 'sum' || op === 'avg') return '0';
  if (op === 'join') return 'None';
  return 'N/A';
};

const resolveOneScanToken = (path, op, scanData) => {
  const val = scanData ? getScanPathValue(scanData, String(path).trim()) : undefined;
  // Treat undefined/null/empty-string as "not present" — show a clear absence label.
  if (val === undefined || val === null || val === '') return scanTokenEmptyPlaceholder(op);
  if (op === 'count') return String(Array.isArray(val) ? val.length : (typeof val === 'object' ? Object.keys(val).length : 1));
  if (op === 'sum' && Array.isArray(val)) return String(val.reduce((a, b) => a + (Number(b) || 0), 0));
  if (op === 'avg' && Array.isArray(val) && val.length > 0) return String(Math.round(val.reduce((a, b) => a + (Number(b) || 0), 0) / val.length * 10) / 10);
  if (op === 'join' && Array.isArray(val)) return val.length ? val.map(v => (v && typeof v === 'object') ? (v.name || v.username || v.label || v.hostname || JSON.stringify(v)) : String(v)).join(', ') : scanTokenEmptyPlaceholder(op);
  if (op === 'first' && Array.isArray(val)) return val.length ? String((val[0] && typeof val[0] === 'object') ? JSON.stringify(val[0]) : val[0]) : scanTokenEmptyPlaceholder(op);
  if (Array.isArray(val)) return val.length ? String(val.length) : scanTokenEmptyPlaceholder(op);
  if (typeof val === 'object') return JSON.stringify(val).slice(0, 120);
  return String(val);
};

const resolveScanTokens = (text, scanData) => {
  if (!text || !String(text).includes('{{')) return text;
  let out = String(text);
  // Pass 1: tokens wrapped in emphasis (**{{token}}** or *{{token}}*). If the token
  // resolves to empty, drop the wrapping markers too so no bare "**" is left behind.
  // This is precise — it only touches emphasis whose entire content is one scan token,
  // never adjacent real bold spans.
  out = out.replace(/(\*{1,3})\s*(\{\{\s*scan:[^}]*\}\})\s*\1/g, (m, stars, token) => {
    const inner = token.match(SCAN_TOKEN_RE);
    const resolved = inner ? resolveOneScanToken(inner[1], inner[2], scanData) : '';
    return resolved === '' ? '' : `${stars}${resolved}${stars}`;
  });
  // Pass 2: any remaining standalone tokens.
  out = out.replace(new RegExp(SCAN_TOKEN_RE.source, 'g'), (match, path, op) => resolveOneScanToken(path, op, scanData));
  return out;
};

// ─── Computed bindings: recompute AI scores/charts from live scan data (no AI) ──────
// An AI report can store a "recipe" (data path + op + thresholds) for each category
// score, scorecard card, and donut category. These evaluators run that recipe against
// whatever scan data is loaded, so charts/scorecards update themselves on reuse.

// Run a metric op over the value at a scan path.
const evalBindingMetric = (binding, scanData) => {
  if (!binding || !binding.sourcePath) return null;
  const val = getScanPathValue(scanData, binding.sourcePath);
  if (val === undefined || val === null) return null;
  const op = binding.op || 'count';
  const field = binding.field;
  const arr = Array.isArray(val) ? val : (typeof val === 'object' ? Object.values(val) : null);
  switch (op) {
    case 'value': return Number(val) || 0;
    case 'count': return arr ? arr.length : (typeof val === 'object' ? Object.keys(val).length : 1);
    case 'sum': return arr ? arr.reduce((a, b) => a + (Number(field ? (b && b[field]) : b) || 0), 0) : (Number(val) || 0);
    case 'avg': return arr && arr.length ? arr.reduce((a, b) => a + (Number(field ? (b && b[field]) : b) || 0), 0) / arr.length : 0;
    case 'presentCount': return arr ? arr.filter(it => it && field && it[field] !== undefined && it[field] !== null && it[field] !== '').length : 0;
    case 'flagTrueCount': return arr ? arr.filter(it => {
      const f = field ? (it && it[field]) : it;
      return Array.isArray(f) ? f.length > 0 : (f && typeof f === 'object' ? Object.keys(f).length > 0 : !!f);
    }).length : 0;
    case 'flagFalseCount': return arr ? arr.filter(it => {
      const f = field ? (it && it[field]) : it;
      return Array.isArray(f) ? f.length === 0 : (f && typeof f === 'object' ? Object.keys(f).length === 0 : !f);
    }).length : 0;
    default: return arr ? arr.length : (Number(val) || 0);
  }
};

// Map a metric through ascending {max, score} thresholds -> 0-100 score.
const evalBindingScore = (binding, scanData) => {
  const metric = evalBindingMetric(binding, scanData);
  if (metric === null || metric === undefined) return null;
  const thresholds = Array.isArray(binding.thresholds) ? binding.thresholds : null;
  if (!thresholds || thresholds.length === 0) return null;
  for (const t of thresholds) {
    if (t.max === undefined || t.max === null) return Number(t.score) || 0; // fallback entry
    if (metric <= t.max) return Number(t.score) || 0;
  }
  const last = thresholds[thresholds.length - 1];
  return Number(last.score) || 0;
};

// Score -> traffic-light status/colors (matches the backend RISK_PALETTE buckets).
const SCORE_STATUS = {
  green:  { status: 'GOOD',     fill: 'linear-gradient(135deg, #eaf7ef 0%, #d4eede 100%)', accent: '#7cc796', text: '#266748', bar: '#7cc796' },
  yellow: { status: 'MODERATE', fill: 'linear-gradient(135deg, #fdf6da 0%, #f8ecb6 100%)', accent: '#e6c252', text: '#7a5a12', bar: '#e6c252' },
  red:    { status: 'HIGH',     fill: 'linear-gradient(135deg, #fce7e7 0%, #f6d2d2 100%)', accent: '#e89090', text: '#8c3030', bar: '#e89090' }
};
const scoreToStatus = (score) => {
  if (score >= 70) return SCORE_STATUS.green;
  if (score >= 40) return SCORE_STATUS.yellow;
  return SCORE_STATUS.red;
};

// Count scan items per category by matching their names against keyword patterns.
const evalCategoryCounts = (binding, scanData) => {
  if (!binding || !binding.categorySource || !Array.isArray(binding.categories)) return null;
  const src = getScanPathValue(scanData, binding.categorySource);
  if (!src) return null;
  // Names: object keys, or array of strings / objects with a name-ish field.
  let names = [];
  if (Array.isArray(src)) {
    names = src.map(it => (it && typeof it === 'object') ? String(it.name || it.label || it.application || it.title || '') : String(it || ''));
  } else if (typeof src === 'object') {
    names = Object.keys(src);
  }
  names = names.filter(Boolean).map(n => n.toLowerCase());
  const labels = binding.categories.map(c => c.label);
  const values = binding.categories.map(c => {
    const pats = (c.patterns || []).map(p => String(p).toLowerCase());
    return names.filter(n => pats.some(p => p && n.includes(p))).length;
  });
  return { labels, values };
};

const TextElement = ({ config, style, isEditing, onToggleEditMode, onUpdateCustomData, customData, onConfigUpdate, organizationName, scanDate, mspInfo, clientInfo, scanData }) => {
  const [editContent, setEditContent] = useState('');
  const [editFontSize, setEditFontSize] = useState('');
  const textareaRef = useRef(null);

  useEffect(() => {
    if (isEditing) {
      const displayContent = customData.content !== undefined ? customData.content : config.content;
      setEditContent(displayContent || 'Enter your text here...');
      setEditFontSize(style.labelFontSize ? String(style.labelFontSize) : '');
    }
  }, [isEditing, config.content, customData, style.labelFontSize]);

  const handleSave = () => {
    onUpdateCustomData({ content: editContent });
    if (onConfigUpdate) {
      const fontUpdate = editFontSize ? { labelFontSize: Number(editFontSize) } : {};
      onConfigUpdate({ content: editContent, ...fontUpdate });
    }
    onToggleEditMode();
  };

  // Insert markdown syntax around selection or at cursor
  const applyFormat = (prefix, suffix) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = editContent.slice(start, end);
    const suf = suffix !== undefined ? suffix : prefix;
    const newContent = editContent.slice(0, start) + prefix + selected + suf + editContent.slice(end);
    setEditContent(newContent);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + prefix.length, end + prefix.length);
    }, 0);
  };

  const applyLinePrefix = (prefix) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const lineStart = editContent.lastIndexOf('\n', start - 1) + 1;
    const currentLine = editContent.slice(lineStart, editContent.indexOf('\n', start) === -1 ? editContent.length : editContent.indexOf('\n', start));
    // Toggle: if line already starts with prefix, remove it; otherwise add it
    let newContent;
    let offset;
    if (currentLine.startsWith(prefix)) {
      newContent = editContent.slice(0, lineStart) + currentLine.slice(prefix.length) + editContent.slice(lineStart + currentLine.length);
      offset = -prefix.length;
    } else {
      newContent = editContent.slice(0, lineStart) + prefix + editContent.slice(lineStart);
      offset = prefix.length;
    }
    setEditContent(newContent);
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start + offset, start + offset); }, 0);
  };

  let displayContent = customData.content !== undefined ? customData.content : config.content;
  // Scorecard card label/status with a binding: recompute the status from live scan data.
  let scoreBindingTextColor = null;
  if (config.scoreBinding && scanData) {
    const score = evalBindingScore(config.scoreBinding, scanData);
    if (score !== null && score !== undefined) {
      const st = scoreToStatus(score);
      displayContent = `**${config.scoreLabel || ''}**\n${st.status}`;
      scoreBindingTextColor = st.text;
    }
  }

  const btnStyle = {
    padding: '2px 7px', fontSize: '11px', fontWeight: '600', border: '1px solid #d1d5db',
    borderRadius: '3px', cursor: 'pointer', background: '#f9fafb', color: '#374151',
    lineHeight: '1.4', fontFamily: 'inherit'
  };

  if (isEditing) {
    return (
      <div className="text-element editing" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* WYSIWYG-style toolbar */}
        <div style={{ display: 'flex', gap: '3px', padding: '4px 6px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc', flexWrap: 'wrap', alignItems: 'center' }}>
          <button style={{ ...btnStyle, fontWeight: '700' }} title="Bold (Ctrl+B)" onMouseDown={(e) => { e.preventDefault(); applyFormat('**'); }}>B</button>
          <button style={{ ...btnStyle, fontStyle: 'italic' }} title="Italic" onMouseDown={(e) => { e.preventDefault(); applyFormat('*'); }}>I</button>
          <div style={{ width: '1px', height: '16px', background: '#d1d5db', margin: '0 2px' }} />
          <button style={btnStyle} title="Heading 1" onMouseDown={(e) => { e.preventDefault(); applyLinePrefix('# '); }}>H1</button>
          <button style={btnStyle} title="Heading 2" onMouseDown={(e) => { e.preventDefault(); applyLinePrefix('## '); }}>H2</button>
          <button style={btnStyle} title="Heading 3" onMouseDown={(e) => { e.preventDefault(); applyLinePrefix('### '); }}>H3</button>
          <div style={{ width: '1px', height: '16px', background: '#d1d5db', margin: '0 2px' }} />
          <button style={btnStyle} title="Bullet list" onMouseDown={(e) => { e.preventDefault(); applyLinePrefix('- '); }}>• List</button>
          <button style={btnStyle} title="Numbered list" onMouseDown={(e) => { e.preventDefault(); applyLinePrefix('1. '); }}>1. List</button>
          <div style={{ width: '1px', height: '16px', background: '#d1d5db', margin: '0 2px' }} />
          <label style={{ fontSize: '11px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '3px' }}>
            Size:
            <select value={editFontSize} onChange={(e) => setEditFontSize(e.target.value)} style={{ fontSize: '11px', padding: '1px 3px', border: '1px solid #d1d5db', borderRadius: '3px' }}>
              <option value="">Default</option>
              {[9,10,11,12,13,14,16,18,20,22,24,28,32].map(s => <option key={s} value={s}>{s}px</option>)}
            </select>
          </label>
        </div>
        <textarea
          ref={textareaRef}
          value={editContent}
          onChange={(e) => setEditContent(e.target.value)}
          style={{ flex: 1, width: '100%', padding: '8px', border: 'none', outline: 'none', resize: 'none', fontFamily: 'monospace', fontSize: '12px', lineHeight: '1.5' }}
        />
        <div style={{ display: 'flex', gap: '6px', padding: '6px', borderTop: '1px solid #e5e7eb', background: '#f8fafc' }}>
          <button onClick={handleSave} className="btn btn-sm btn-success" style={{ fontSize: '12px' }}>Save</button>
          <button onClick={onToggleEditMode} className="btn btn-sm btn-secondary" style={{ fontSize: '12px' }}>Cancel</button>
        </div>
      </div>
    );
  }

  const verticalAlign = config.verticalAlign || 'top';
  const textAlign = config.align || 'left';

  const getJustifyContent = () => {
    switch (verticalAlign) {
      case 'middle': return 'center';
      case 'bottom': return 'flex-end';
      default: return 'flex-start';
    }
  };

  // Always render through markdown — handles both AI-generated markdown and plain text gracefully.
  // NOTE: renderVariables returns an ARRAY of strings/React elements, so markdown structure is
  // parsed on the raw text first, and variables are resolved inside each text segment.
  const renderMarkdown = (text) => {
    if (!text) return null;

    const lines = String(text).split('\n');
    const elements = [];
    let i = 0;

    const parseLine = (line) => {
      const parts = line.split(/(\*\*[^*\n]+\*\*|\*[^*\n]+\*)/g);
      return parts.map((part, idx) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={idx}>{renderVariables(part.slice(2, -2), organizationName, scanDate, mspInfo, clientInfo)}</strong>;
        }
        if (part.startsWith('*') && part.endsWith('*')) {
          return <em key={idx}>{renderVariables(part.slice(1, -1), organizationName, scanDate, mspInfo, clientInfo)}</em>;
        }
        if (!part) return null;
        return <span key={idx}>{renderVariables(part, organizationName, scanDate, mspInfo, clientInfo)}</span>;
      });
    };

    while (i < lines.length) {
      const line = lines[i];
      const trimmed = line.trim();

      if (/^[-_]{3,}$/.test(trimmed)) {
        elements.push(<hr key={i} style={{ border: 'none', borderTop: '1px solid currentColor', opacity: 0.25, margin: '6px 0' }} />);
        i++; continue;
      }

      if (/^[-*•]\s/.test(trimmed)) {
        const items = [];
        while (i < lines.length && /^[-*•]\s/.test(lines[i].trim())) {
          items.push(<li key={i} style={{ marginBottom: '2px', lineHeight: '1.5' }}>{parseLine(lines[i].trim().replace(/^[-*•]\s/, ''))}</li>);
          i++;
        }
        elements.push(<ul key={`ul-${i}`} style={{ paddingLeft: '1.4em', margin: '3px 0 4px', listStyleType: 'disc' }}>{items}</ul>);
        continue;
      }

      if (/^\d+[.)]\s/.test(trimmed)) {
        const items = [];
        while (i < lines.length && /^\d+[.)]\s/.test(lines[i].trim())) {
          items.push(<li key={i} style={{ marginBottom: '2px', lineHeight: '1.5' }}>{parseLine(lines[i].trim().replace(/^\d+[.)]\s/, ''))}</li>);
          i++;
        }
        elements.push(<ol key={`ol-${i}`} style={{ paddingLeft: '1.4em', margin: '3px 0 4px' }}>{items}</ol>);
        continue;
      }

      const headingMatch = trimmed.match(/^(#{1,3})\s+(.+)/);
      if (headingMatch) {
        const level = headingMatch[1].length;
        const sizes = ['1.2em', '1.05em', '0.95em'];
        const margins = ['12px', '8px', '6px'];
        elements.push(
          <div key={i} style={{ fontWeight: 700, fontSize: sizes[level - 1], marginTop: margins[level - 1], marginBottom: '4px', borderBottom: level <= 2 ? '1px solid currentColor' : 'none', opacity: 0.95, lineHeight: '1.3' }}>
            {parseLine(headingMatch[2])}
          </div>
        );
        i++; continue;
      }

      if (trimmed === '') {
        elements.push(<div key={i} style={{ height: '5px' }} />);
        i++; continue;
      }

      elements.push(<div key={i} style={{ marginBottom: '2px', lineHeight: '1.5' }}>{parseLine(line)}</div>);
      i++;
    }
    return elements;
  };

  const resolvedFontSize = style.labelFontSize
    ? `${style.labelFontSize}px`
    : style.fontSize === 'small' ? '0.85rem' : style.fontSize === 'large' ? '1.1rem' : '0.9rem';

  const bgVal = style.backgroundColor || 'transparent';
  const isGradientBg = typeof bgVal === 'string' && bgVal.includes('gradient');

  return (
    <div
      className="text-element"
      style={{
        color: scoreBindingTextColor || style.textColor || undefined,
        fontSize: resolvedFontSize,
        fontWeight: style.fontWeight || undefined,
        // Gradient backgrounds must use `background`, not `backgroundColor`.
        ...(isGradientBg ? { background: bgVal } : { backgroundColor: bgVal }),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: getJustifyContent(),
        height: '100%',
        width: '100%',
        padding: style.padding !== undefined ? `${style.padding}px` : (bgVal !== 'transparent' ? '10px' : '4px'),
        borderRadius: style.borderRadius !== undefined ? `${style.borderRadius}px` : '4px',
        // Never clip report text — generous height estimates keep blocks from overlapping.
        overflow: 'visible'
      }}
    >
      <div style={{ textAlign, width: '100%', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
        {renderMarkdown(resolveScanTokens(displayContent || '', scanData))}
      </div>
    </div>
  );
};

const ImageElement = ({ config, style, isEditing, onToggleEditMode, onUpdateCustomData, customData, mspInfo }) => {
  const [editSrc, setEditSrc] = useState('');
  const [editAlt, setEditAlt] = useState('');
  const imgRef = useRef(null);

  // Initialize form data when entering edit mode
  useEffect(() => {
    if (isEditing) {
      const displaySrc = customData.src !== undefined ? customData.src : config.src;
      const displayAlt = customData.alt !== undefined ? customData.alt : config.alt;
      setEditSrc(displaySrc || 'https://via.placeholder.com/400x200');
      setEditAlt(displayAlt || 'Image');
    }
  }, [isEditing, config.src, config.alt, customData]);

  const handleSave = () => {
    onUpdateCustomData({
      src: editSrc,
      alt: editAlt
    });
    onToggleEditMode();
  };

  const displaySrc = customData.src !== undefined ? customData.src : config.src;
  const displayAlt = customData.alt !== undefined ? customData.alt : config.alt;

  // Resolve MSP Logo variables
  let resolvedSrc = displaySrc;
  if (displaySrc === '{{ MSP.large_logo }}') {
    resolvedSrc = mspInfo?.tenant_large_logo || mspInfo?.large_logo || mspInfo?.tenant_logo || mspInfo?.logo || displaySrc;
  } else if (displaySrc === '{{ MSP.logo }}') {
    resolvedSrc = mspInfo?.tenant_logo || mspInfo?.logo || displaySrc;
  }

  // Detect an unconfigured image: no src set by the user, or an MSP logo variable
  // that could not be resolved. Instead of showing a broken/"no image found" graphic,
  // we render a clean "No image configured" placeholder below.
  const noImageConfigured = resolvedSrc === '{{ MSP.logo }}' ||
    resolvedSrc === '{{ MSP.large_logo }}' ||
    !resolvedSrc ||
    (typeof resolvedSrc === 'string' && resolvedSrc.trim() === '');

  // Debug log for logo resolution (only if contains MSP logo variables)
  useEffect(() => {
    if (displaySrc === '{{ MSP.logo }}' || displaySrc === '{{ MSP.large_logo }}') {
      console.log('[ImageElement] Logo resolution:', {
        variable: displaySrc,
        resolvedTo: resolvedSrc,
        mspInfoPresent: !!mspInfo,
        hasLargeLogo: !!mspInfo?.tenant_large_logo
      });
    }
  }, [displaySrc, resolvedSrc, mspInfo]);

  // Force !important styles on the image tag to override any global/browser defaults
  useEffect(() => {
    if (imgRef.current) {
      const heightVal = config.imageHeight || '100%';
      const widthVal = config.imageWidth || '100%';
      const fitVal = config.objectFit || 'contain';
      imgRef.current.style.setProperty('height', heightVal, 'important');
      imgRef.current.style.setProperty('min-height', heightVal, 'important');
      imgRef.current.style.setProperty('width', widthVal, 'important');
      imgRef.current.style.setProperty('min-width', widthVal, 'important');
      imgRef.current.style.setProperty('max-height', 'none', 'important');
      imgRef.current.style.setProperty('max-width', 'none', 'important');
      imgRef.current.style.setProperty('object-fit', fitVal, 'important');
    }
  }, [config.imageHeight, config.imageWidth, config.objectFit, resolvedSrc]);

  if (isEditing) {
    return (
      <div className="image-element editing">
        <div className="edit-controls">
          <div className="edit-field">
            <label>Image URL:</label>
            <input
              type="url"
              value={editSrc}
              onChange={(e) => setEditSrc(e.target.value)}
              style={{ width: '100%', marginBottom: '8px' }}
            />
          </div>
          <div className="edit-field">
            <label>Alt Text:</label>
            <input
              type="text"
              value={editAlt}
              onChange={(e) => setEditAlt(e.target.value)}
              style={{ width: '100%', marginBottom: '8px' }}
            />
          </div>
          <div className="edit-actions">
            <button onClick={handleSave} className="btn btn-sm btn-success">Save</button>
            <button onClick={onToggleEditMode} className="btn btn-sm btn-secondary">Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  // Flexbox alignment values for the image container - only used if not filling
  const justifyContent = config.align === 'right' ? 'flex-end' : config.align === 'center' ? 'center' : 'flex-start';
  const alignItems = config.verticalAlign === 'bottom' ? 'flex-end' : config.verticalAlign === 'middle' ? 'center' : 'flex-start';

  // Object-position mapping
  const hPos = config.align || 'center';
  const vPos = config.verticalAlign === 'middle' ? 'center' : (config.verticalAlign === 'top' ? 'top' : (config.verticalAlign === 'bottom' ? 'bottom' : 'center'));

  // No image set by the user (or an unresolved logo variable): show a neutral
  // "No image configured" placeholder instead of a broken/"no image found" graphic.
  if (noImageConfigured) {
    return (
      <div className="image-element image-element-empty" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        border: '1px dashed #cbd5e1',
        borderRadius: style.borderRadius || 6,
        backgroundColor: '#f8fafc',
        color: '#94a3b8',
        fontSize: '13px',
        fontWeight: 500,
        textAlign: 'center',
        padding: '8px',
        boxSizing: 'border-box'
      }}>
        <span style={{ fontSize: '22px', lineHeight: 1 }} aria-hidden="true">🖼️</span>
        <span>No image configured</span>
      </div>
    );
  }

  return (
    <div className="image-element" style={{
      display: 'block', // Changed from flex to block for more predictable 100% height
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      position: 'relative',
      backgroundColor: 'transparent' // Ensure no background leaks through
    }}>
      <img
        ref={imgRef}
        src={resolvedSrc}
        alt={displayAlt || 'Image'}
        style={{
          borderRadius: style.borderRadius,
          width: config.imageWidth || '100%',
          height: config.imageHeight || '100%',
          minWidth: config.imageWidth || '100%',
          minHeight: config.imageHeight || '100%',
          maxWidth: 'none',
          maxHeight: 'none',
          objectFit: config.objectFit || 'contain',
          objectPosition: `${hPos} ${vPos}`,
          flexShrink: 0,
          display: 'block',
          margin: 0,
          padding: 0,
          boxSizing: 'border-box'
        }}
      />
    </div>
  );
};

const DividerElement = ({ config, style }) => (
  <div className="divider-element">
    <hr style={{
      borderColor: style.borderColor || '#e5e7eb',
      borderWidth: config.thickness || '2px',
      borderStyle: config.style || 'solid'
    }} />
  </div>
);

const EmbedElement = ({ config, style }) => (
  <div className="embed-element">
    <div className="embed-placeholder">
      <span>🔗</span>
      <p>Embed: {config.url || 'Add URL'}</p>
    </div>
  </div>
);

const ShapeElement = ({ config, style, data, theme }) => {
  const tColors = theme?.statusColors || {};
  const statusColors = {
    'green': style.auditColorGreen || tColors.green || '#16a34a',
    'yellow': style.auditColorYellow || tColors.yellow || '#eab308',
    'red': style.auditColorRed || tColors.red || '#dc2626',
    'gray': style.auditColorGray || tColors.gray || '#9ca3af',
  };

  let effectiveColor = style.backgroundColor;
  let effectiveBorderColor = style.borderColor;
  if (config.statusSyncCategory && data) {
    const summaries = extractKeyFindingsSummary(data);
    const categorySummary = summaries.find(s => s.category === config.statusSyncCategory);
    if (categorySummary && categorySummary.statusColor) {
      effectiveColor = statusColors[categorySummary.statusColor] || effectiveColor;
    }
  }
  // Scorecard card with a computed binding: recompute its score from the live scan
  // data and recolor the card (fill + border) by the resulting traffic-light status.
  if (config.scoreBinding && data) {
    const scan = data.scanData || data;
    const score = evalBindingScore(config.scoreBinding, scan);
    if (score !== null && score !== undefined) {
      const st = scoreToStatus(score);
      effectiveColor = st.fill;
      effectiveBorderColor = st.accent;
    }
  }

  const borderWidth = style.borderWidth || 0;
  const borderType = style.borderStyle || 'solid';
  const borderStyle = borderWidth > 0
    ? `${borderWidth}px ${borderType} ${effectiveBorderColor || '#e5e7eb'}`
    : 'none';

  // Flexbox alignment values
  const justifyContent = config.align === 'right' ? 'flex-end' : config.align === 'center' ? 'center' : 'flex-start';
  const alignItems = config.verticalAlign === 'bottom' ? 'flex-end' : config.verticalAlign === 'middle' ? 'center' : 'flex-start';

  return (
    <div
      style={{
        display: 'flex',
        width: '100%',
        height: '100%',
        justifyContent,
        alignItems
      }}
    >
      <div
        className="shape-element"
        style={{
          // Gradient strings must go through `background`, not `backgroundColor`.
          ...(typeof effectiveColor === 'string' && effectiveColor.includes('gradient')
            ? { background: effectiveColor }
            : { backgroundColor: effectiveColor || '#CBD5E1' }),
          border: borderStyle,
          borderRadius: config.shape === 'circle' ? '50%' : (style.borderRadius || 0),
          width: style.width || '100%',
          height: style.height || '100%'
        }}
      />
    </div>
  );
};

const IconElement = ({ config, style }) => {
  // Flexbox alignment values
  const justifyContent = config.align === 'right' ? 'flex-end' : config.align === 'center' ? 'center' : 'flex-start';
  const alignItems = config.verticalAlign === 'bottom' ? 'flex-end' : config.verticalAlign === 'middle' ? 'center' : 'flex-start';

  // Map icon name to Lucide component
  const getIconComponent = (iconName) => {
    switch (iconName) {
      case 'heart': return <Heart size={style.fontSize === 'small' ? 32 : style.fontSize === 'large' ? 64 : 48} color={style.textColor || '#ef4444'} />;
      case 'alert': return <AlertTriangle size={style.fontSize === 'small' ? 32 : style.fontSize === 'large' ? 64 : 48} color={style.textColor || '#f59e0b'} />;
      case 'check': return <CheckCircle size={style.fontSize === 'small' ? 32 : style.fontSize === 'large' ? 64 : 48} color={style.textColor || '#10b981'} />;
      case 'info': return <Info size={style.fontSize === 'small' ? 32 : style.fontSize === 'large' ? 64 : 48} color={style.textColor || '#3b82f6'} />;
      case 'user': return <User size={style.fontSize === 'small' ? 32 : style.fontSize === 'large' ? 64 : 48} color={style.textColor || '#64748b'} />;
      case 'settings': return <Settings size={style.fontSize === 'small' ? 32 : style.fontSize === 'large' ? 64 : 48} color={style.textColor || '#64748b'} />;
      case 'menu': return <Menu size={style.fontSize === 'small' ? 32 : style.fontSize === 'large' ? 64 : 48} color={style.textColor || '#64748b'} />;
      case 'search': return <Search size={style.fontSize === 'small' ? 32 : style.fontSize === 'large' ? 64 : 48} color={style.textColor || '#64748b'} />;
      case 'star':
      default: return <Star size={style.fontSize === 'small' ? 32 : style.fontSize === 'large' ? 64 : 48} color={style.textColor || '#f59e0b'} fill={style.textColor || '#f59e0b'} />;
    }
  };

  return (
    <div
      className="icon-element"
      style={{
        display: 'flex',
        width: '100%',
        height: '100%',
        justifyContent,
        alignItems,
        color: style.textColor,
        fontSize: '48px'
      }}
    >
      {getIconComponent(config.icon || 'star')}
    </div>
  );
};

// Header and Footer Elements are now imported from separate files:
// - ./byor/elements/HeaderElement.jsx
// - ./byor/elements/FooterElement.jsx


const FILTER_PRESETS = {
  networkDevices: [
    {
      id: 'status_up',
      name: 'Active Devices Only',
      description: 'Show only network devices with status "Up"',
      icon: '🟢',
      filterMode: 'query',
      advancedFilters: [
        { id: 'status_up_1', fieldPath: 'status', operator: 'equals', value: 'Up', logic: 'AND' }
      ]
    },
    {
      id: 'status_down',
      name: 'Inactive Devices',
      description: 'Show only network devices with status "Down"',
      icon: '🔴',
      filterMode: 'query',
      advancedFilters: [
        { id: 'status_down_1', fieldPath: 'status', operator: 'equals', value: 'Down', logic: 'AND' }
      ]
    },
    {
      id: 'windows_11',
      name: 'Windows 11 Devices',
      description: 'Filter devices running Windows 11',
      icon: '🪟',
      filterMode: 'query',
      advancedFilters: [
        { id: 'win11_1', fieldPath: 'system.OS', operator: 'contains', value: 'Windows 11', logic: 'AND' }
      ]
    },
    {
      id: 'windows_10',
      name: 'Windows 10 Devices',
      description: 'Filter devices running Windows 10',
      icon: '🪟',
      filterMode: 'query',
      advancedFilters: [
        { id: 'win10_1', fieldPath: 'system.OS', operator: 'contains', value: 'Windows 10', logic: 'AND' }
      ]
    },
    {
      id: 'has_system_info',
      name: 'Devices with System Info',
      description: 'Only devices where WMI scan succeeded',
      icon: '💻',
      filterMode: 'query',
      advancedFilters: [
        { id: 'sysinfo_1', fieldPath: 'system.Computer Name', operator: 'is_not_empty', value: '', logic: 'AND' }
      ]
    },
    {
      id: 'high_ram_usage',
      name: 'High RAM Usage (>70%)',
      description: 'Devices with RAM usage over 70%',
      icon: '⚠️',
      filterMode: 'query',
      advancedFilters: [
        { id: 'ram_1', fieldPath: 'system.RAM Used', operator: 'greater_than', value: '70', logic: 'AND' }
      ]
    },
    {
      id: 'rdp_enabled',
      name: 'RDP Enabled Devices',
      description: 'Devices with Remote Desktop enabled',
      icon: '🖥️',
      filterMode: 'query',
      advancedFilters: [
        { id: 'rdp_1', fieldPath: 'system.RDP Enabled', operator: 'is_true', value: '', logic: 'AND' }
      ]
    },
    {
      id: 'windows_type',
      name: 'Group by Windows Type',
      description: 'Group devices by their OS type',
      icon: '📊',
      filterMode: 'group',
      filterField: 'device_type',
      groupByField: 'device_type'
    },
    {
      id: 'by_manufacturer',
      name: 'Group by Manufacturer',
      description: 'Group devices by hardware manufacturer',
      icon: '🏭',
      compareMode: true,
      comparisonField: 'system.Manufacturer',
      deviceMode: 'compare'
    },
    {
      id: 'compare_ram',
      name: 'Compare RAM Usage',
      description: 'Compare RAM usage across all devices',
      icon: '📈',
      compareMode: true,
      comparisonField: 'system.RAM Used',
      deviceMode: 'compare'
    },
    {
      id: 'compare_cpu',
      name: 'Compare CPU Usage',
      description: 'Compare CPU usage across all devices',
      icon: '📉',
      compareMode: true,
      comparisonField: 'system.CPU Usage',
      deviceMode: 'compare'
    },
    {
      id: 'compare_age',
      name: 'Compare Device Age',
      description: 'Compare age of devices',
      icon: '📅',
      compareMode: true,
      comparisonField: 'system.Age',
      deviceMode: 'compare'
    }
  ],
  nestedArrays: {
    'system.Local Users': [
      {
        id: 'users_enabled',
        name: 'Enabled Users Only',
        description: 'Filter to show only enabled user accounts',
        icon: '✅',
        filterMode: 'query',
        advancedFilters: [
          { id: 'enabled_1', fieldPath: 'Disabled', operator: 'is_false', value: '', logic: 'AND' }
        ]
      },
      {
        id: 'users_disabled',
        name: 'Disabled Users Only',
        description: 'Filter to show only disabled user accounts',
        icon: '🚫',
        filterMode: 'query',
        advancedFilters: [
          { id: 'disabled_1', fieldPath: 'Disabled', operator: 'is_true', value: '', logic: 'AND' }
        ]
      },
      {
        id: 'users_by_status',
        name: 'Group by Enabled/Disabled',
        description: 'Count users by their enabled status',
        icon: '📊',
        filterMode: 'group',
        filterField: 'Disabled'
      },
      {
        id: 'users_by_type',
        name: 'Group by Account Type',
        description: 'Count users by account type',
        icon: '👥',
        filterMode: 'group',
        filterField: 'Account Type'
      },
      {
        id: 'users_password_expires',
        name: 'Password Expiry Status',
        description: 'Group by password expiration setting',
        icon: '🔐',
        filterMode: 'group',
        filterField: 'Password Expires'
      }
    ],
    'system.Administrators': [
      {
        id: 'admins_enabled',
        name: 'Active Admins Only',
        description: 'Show only enabled administrator accounts',
        icon: '👤',
        filterMode: 'query',
        advancedFilters: [
          { id: 'admin_enabled_1', fieldPath: 'Disabled', operator: 'is_false', value: '', logic: 'AND' }
        ]
      },
      {
        id: 'admins_by_type',
        name: 'Group by Account Type',
        description: 'Count admins by account type',
        icon: '📊',
        filterMode: 'group',
        filterField: 'Account Type'
      }
    ],
    'system.Non-Default Services': [
      {
        id: 'services_running',
        name: 'Running Services Only',
        description: 'Show only services that are currently running',
        icon: '▶️',
        filterMode: 'query',
        advancedFilters: [
          { id: 'svc_running_1', fieldPath: 'State', operator: 'equals', value: 'Running', logic: 'AND' }
        ]
      },
      {
        id: 'services_stopped',
        name: 'Stopped Services Only',
        description: 'Show only services that are stopped',
        icon: '⏹️',
        filterMode: 'query',
        advancedFilters: [
          { id: 'svc_stopped_1', fieldPath: 'State', operator: 'equals', value: 'Stopped', logic: 'AND' }
        ]
      },
      {
        id: 'services_by_state',
        name: 'Group by State',
        description: 'Count services by their running state',
        icon: '📊',
        filterMode: 'group',
        filterField: 'State'
      },
      {
        id: 'services_by_startmode',
        name: 'Group by Start Mode',
        description: 'Count services by start mode (Auto, Manual, etc.)',
        icon: '⚙️',
        filterMode: 'group',
        filterField: 'Start Mode'
      }
    ],
    'system.Disk Drives': [
      {
        id: 'disks_ok',
        name: 'Healthy Disks Only',
        description: 'Show only disks with OK status',
        icon: '✅',
        filterMode: 'query',
        advancedFilters: [
          { id: 'disk_ok_1', fieldPath: 'Status', operator: 'equals', value: 'OK', logic: 'AND' }
        ]
      },
      {
        id: 'disks_by_type',
        name: 'Group by Interface Type',
        description: 'Count disks by interface (SATA, NVMe, etc.)',
        icon: '💾',
        filterMode: 'group',
        filterField: 'InterfaceType'
      }
    ],
    'system.Logical Disks': [
      {
        id: 'logical_high_usage',
        name: 'High Usage Disks (>80%)',
        description: 'Disks with more than 80% usage',
        icon: '⚠️',
        filterMode: 'query',
        advancedFilters: [
          { id: 'disk_high_1', fieldPath: 'Percent Used', operator: 'greater_than', value: '80', logic: 'AND' }
        ]
      }
    ],
    'system.Antivirus': [
      {
        id: 'av_by_product',
        name: 'Group by Product',
        description: 'Count antivirus by product name',
        icon: '🛡️',
        filterMode: 'group',
        filterField: '0' // First element of antivirus array is product name
      }
    ]
  }
};

// Helper to get filter presets for a given metric path
const getFilterPresetsForMetric = (metricPath, nestedArrayPath = null) => {
  const presets = [];

  // Check if this is network_devices
  if (metricPath === 'network_devices') {
    presets.push(...FILTER_PRESETS.networkDevices);
  }

  // Check for nested array presets
  if (nestedArrayPath && FILTER_PRESETS.nestedArrays[nestedArrayPath]) {
    presets.push(...FILTER_PRESETS.nestedArrays[nestedArrayPath]);
  }

  return presets;
};

// Helper functions (keep the same as before)

/**
 * Analyze metric data to determine its structure and characteristics
 */
const analyzeMetricData = (metric) => {
  if (!metric || !metric.path) {
    return {
      type: 'unknown',
      itemCount: 0,
      hasNumericValues: false,
      hasBooleanFields: false,
      categoryFieldCount: 0,
      numericFieldCount: 0,
      isObjectArray: false
    };
  }

  const data = metric.data;
  const type = metric.type;

  const analysis = {
    type: type || 'unknown',
    itemCount: 0,
    hasNumericValues: false,
    hasBooleanFields: false,
    categoryFieldCount: 0,
    numericFieldCount: 0,
    isObjectArray: false
  };

  if (type === 'number') {
    analysis.hasNumericValues = true;
    analysis.numericFieldCount = 1;
    analysis.itemCount = 1;
    return analysis;
  }

  if (type === 'boolean') {
    analysis.hasBooleanFields = true;
    analysis.itemCount = 1;
    return analysis;
  }

  if (type === 'array' && Array.isArray(data)) {
    analysis.itemCount = data.length;

    if (data.length > 0) {
      const firstItem = data[0];

      if (typeof firstItem === 'object' && firstItem !== null && !Array.isArray(firstItem)) {
        analysis.isObjectArray = true;

        // Analyze fields in the object
        const fields = Object.keys(firstItem);
        fields.forEach(field => {
          const value = firstItem[field];
          if (typeof value === 'number') {
            analysis.hasNumericValues = true;
            analysis.numericFieldCount++;
          } else if (typeof value === 'boolean') {
            analysis.hasBooleanFields = true;
          } else if (typeof value === 'string') {
            analysis.categoryFieldCount++;
          }
        });
      } else if (typeof firstItem === 'number') {
        analysis.hasNumericValues = true;
        analysis.numericFieldCount = 1;
      }
    }
  }

  return analysis;
};

/**
 * Dynamically discovers metrics from ANY JSON structure
 * No hardcoded keys - works with any JSON file
 */
const processScanDataForMetrics = (scanData) => {
  if (!scanData || typeof scanData !== 'object') {
    return [];
  }

  const metrics = [];
  const seenPaths = new Set();

  const humanizeKey = (key = '') => {
    if (!key) return '';
    return key
      .toString()
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map(word => {
        if (word === word.toUpperCase()) {
          return word;
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  };

  const isPlainObject = (value) => {
    return Object.prototype.toString.call(value) === '[object Object]';
  };

  const addMetric = (category, label, pathSegments, type, description, options = {}) => {
    const path = pathSegments.join('.');
    if (!path || seenPaths.has(path)) return;

    metrics.push({
      category,
      label,
      path,
      type,
      description,
      options
    });
    seenPaths.add(path);
  };

  const addNumericArrayMetric = (category, label, basePathSegments, dataArray, description) => {
    if (!Array.isArray(dataArray) || dataArray.length === 0) return;

    const baseLabel = humanizeKey(label);
    addMetric(
      category,
      `${baseLabel} (All Values)`,
      basePathSegments,
      'array',
      description || `Dataset for ${baseLabel}`
    );

    dataArray.forEach((value, index) => {
      if (typeof value !== 'number') return;
      addMetric(
        category,
        `${baseLabel} (Value ${index + 1})`,
        [...basePathSegments, String(index)],
        'number',
        description || `Entry ${index + 1} for ${baseLabel}`,
        { parentPath: basePathSegments.join('.'), index }
      );
    });
  };

  // Recursive function to collect metrics from any object structure
  const collectMetrics = (data, category, pathSegments, parentLabel = '') => {
    if (data === null || data === undefined) return;

    if (Array.isArray(data)) {
      const label = parentLabel || humanizeKey(pathSegments[pathSegments.length - 1]);

      if (data.every(item => typeof item === 'number')) {
        addNumericArrayMetric(category, label, pathSegments, data, `Numeric values for ${label}`);
        return;
      }

      if (data.length > 0 && data.every(item => isPlainObject(item))) {
        addMetric(
          category,
          humanizeKey(label),
          pathSegments,
          'array',
          `Dataset for ${humanizeKey(label)} (${data.length} items)`,
          { elementShape: 'object' }
        );
        return;
      }

      addMetric(
        category,
        humanizeKey(label),
        pathSegments,
        'array',
        `Collection for ${humanizeKey(label)}`
      );
      return;
    }

    if (typeof data === 'number') {
      const label = parentLabel || humanizeKey(pathSegments[pathSegments.length - 1]);
      addMetric(
        category,
        humanizeKey(label),
        pathSegments,
        'number',
        `Numeric value from ${category}`
      );
      return;
    }

    if (typeof data === 'boolean') {
      const label = parentLabel || humanizeKey(pathSegments[pathSegments.length - 1]);
      addMetric(
        category,
        humanizeKey(label),
        pathSegments,
        'boolean',
        `Boolean from ${category}`,
        { treatAsNumeric: true }
      );
      return;
    }

    if (typeof data === 'string') {
      const label = parentLabel || humanizeKey(pathSegments[pathSegments.length - 1]);
      addMetric(
        category,
        humanizeKey(label),
        pathSegments,
        'string',
        `Text value from ${category}`
      );
      return;
    }

    if (!isPlainObject(data)) {
      return;
    }

    // Recurse into object properties
    Object.entries(data).forEach(([key, value]) => {
      const currentPath = [...pathSegments, key];
      const label = humanizeKey(key);

      if (value === null || value === undefined) {
        return;
      }

      if (Array.isArray(value)) {
        if (value.every(item => typeof item === 'number')) {
          addNumericArrayMetric(
            category,
            label,
            currentPath,
            value,
            `Numeric values for ${label}`
          );
        } else if (value.length > 0 && value.every(item => isPlainObject(item))) {
          addMetric(
            category,
            label,
            currentPath,
            'array',
            `Dataset for ${label} (${value.length} items)`,
            { elementShape: 'object' }
          );
        } else {
          addMetric(
            category,
            label,
            currentPath,
            'array',
            `Collection for ${label}`
          );
        }
        return;
      }

      if (typeof value === 'number') {
        addMetric(category, label, currentPath, 'number', `Numeric value`);
        return;
      }

      if (typeof value === 'boolean') {
        addMetric(category, label, currentPath, 'boolean', `Boolean value`, { treatAsNumeric: true });
        return;
      }

      if (typeof value === 'string') {
        addMetric(category, label, currentPath, 'string', `Text value`);
        return;
      }

      if (isPlainObject(value)) {
        collectMetrics(value, category, currentPath, label);
      }
    });
  };

  // Dynamically discover ALL top-level keys in the JSON
  // Each top-level key becomes a category
  Object.entries(scanData).forEach(([key, value]) => {
    if (value === null || value === undefined) return;

    const category = humanizeKey(key);

    // For top-level objects/arrays, add them as metrics and recurse
    if (isPlainObject(value)) {
      // Add the object itself as a browsable metric
      addMetric(
        category,
        `${category} (All Data)`,
        [key],
        'object',
        `Complete ${category} data`
      );
      // Recurse into the object
      collectMetrics(value, category, [key]);
    } else if (Array.isArray(value)) {
      collectMetrics(value, category, [key]);
    } else {
      // Primitive values at top level
      collectMetrics(value, category, [key]);
    }
  });

  // Sort alphabetically by category, then by label
  metrics.sort((a, b) => {
    if (a.category !== b.category) {
      return a.category.localeCompare(b.category);
    }
    return a.label.localeCompare(b.label);
  });

  return metrics;
};

const getDefaultWidgetSize = (widgetType, gridConfig = GRID_CONFIG) => {
  const cellWidth = gridConfig.cellWidth;
  const cellHeight = gridConfig.cellHeight;

  const sizes = {
    numeric: { width: cellWidth * 4, height: cellHeight * 4 }, // 4x4 (was 2x2)
    statcard: { width: cellWidth * 4, height: cellHeight * 4 }, // 4x4 (was 2x2)
    gauge: { width: cellWidth * 6, height: cellHeight * 8 }, // 6x8 (was 3x4)
    bar: { width: cellWidth * 8, height: cellHeight * 12 }, // 8x12 (was 4x6)
    line: { width: cellWidth * 8, height: cellHeight * 12 }, // 8x12 (was 4x6)
    pie: { width: cellWidth * 6, height: cellHeight * 12 }, // 6x12 (was 3x6)
    donut: { width: cellWidth * 6, height: cellHeight * 12 }, // 6x12 (was 3x6)
    'ms365-users-pie-chart': { width: cellWidth * 6, height: cellHeight * 12 }, // 6x12
    'ms365-mfa-pie-chart': { width: cellWidth * 6, height: cellHeight * 12 }, // 6x12
    'ms365-policy-pie-chart': { width: cellWidth * 6, height: cellHeight * 12 }, // 6x12
    'pii-detection-pie-chart': { width: cellWidth * 6, height: cellHeight * 12 }, // 6x12
    'pii-finding-summary': { width: cellWidth * 8, height: cellHeight * 6 }, // 8x6 (was 4x3)
    'pii-cost-summary': { width: cellWidth * gridConfig.columns, height: cellHeight * 6 },
    'inactive-ms-users-cost-summary': { width: cellWidth * gridConfig.columns, height: cellHeight * 6 },
    'mfa-cost-summary': { width: cellWidth * gridConfig.columns, height: cellHeight * 6 },
    'policy-configuration-cost-summary': { width: cellWidth * gridConfig.columns, height: cellHeight * 6 },
    radar: { width: cellWidth * 8, height: cellHeight * 12 }, // 8x12
    polar: { width: cellWidth * 6, height: cellHeight * 12 }, // 6x12
    bubble: { width: cellWidth * 8, height: cellHeight * 12 }, // 8x12
    scatter: { width: cellWidth * 8, height: cellHeight * 12 }, // 8x12
    // Paginated Widgets - Full width based on columns
    table: { width: cellWidth * gridConfig.columns, height: cellHeight * 20 }, // Full width, ~20 rows (was 10)
    'key-findings': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'backups-cost-summary': { width: cellWidth * gridConfig.columns, height: cellHeight * 6 },
    'network-encryption-cost-summary': { width: cellWidth * gridConfig.columns, height: cellHeight * 6 },
    'admins-cost-summary': { width: cellWidth * gridConfig.columns, height: cellHeight * 6 },
    'accounts-cost-summary': { width: cellWidth * gridConfig.columns, height: cellHeight * 6 },
    'network-firewall-cost-summary': { width: cellWidth * gridConfig.columns, height: cellHeight * 6 },
    'os-cost-summary': { width: cellWidth * gridConfig.columns, height: cellHeight * 6 },
    'total-cost-calculator': { width: cellWidth * gridConfig.columns, height: cellHeight * 10 },
    'open-ports': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'ad-inactive-users-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'ad-inactive-computers-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'ad-groups-risk-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'ad-ous-risk-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'ad-fsmo-roles-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'ad-gpo-analysis-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'ad-users-audit-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'ad-computers-audit-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'ad-devices-audit-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'ad-groups-audit-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'ad-ous-audit-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'ad-domain-controllers-audit-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'ad-gpo-policies-audit-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'ad-event-logs-audit-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'ms365-scan-proof-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'microsoft-policies-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'localhost-hardware-information-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'localhost-system-information-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'localhost-installed-applications-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'localhost-event-logs-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'localhost-open-ports-audit-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'network-backup-status-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'network-os-versions-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'network-firewall-status-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'network-firewall-status-chart': { width: cellWidth * 8, height: cellHeight * 8 },
    'network-encryption-status-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'network-detailed-services-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'network-non-default-services-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'network-services-risk-breakdown-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'network-high-privilege-services-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'network-users-no-expiring-password-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'network-users-expiring-password-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'network-admins-no-expiring-password-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'network-admins-expiring-password-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'network-admins-daily-work-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'findings-grid': { width: cellWidth * gridConfig.columns, height: cellHeight * 24 }, // 24 rows
    'findings-status': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'single-finding-status': { width: cellWidth * 4, height: cellHeight * 6 },
    'infrastructure-scan-grid': {
      width: cellWidth * gridConfig.columns,
      // Landscape (shorter page) needs less height to fit. 
      // Reduced to 14 rows for better fit.
      height: cellHeight * (gridConfig.orientation === 'landscape' ? 14 : 20)
    },
    'managed-service-experience': {
      width: cellWidth * gridConfig.columns,
      // Landscape (shorter page) needs less height to fit. 
      // Reduced to 14 rows for better fit.
      height: cellHeight * (gridConfig.orientation === 'landscape' ? 14 : 20)
    },
    'security-scan-grid': {
      width: cellWidth * gridConfig.columns,
      height: cellHeight * (gridConfig.orientation === 'landscape' ? 14 : 20)
    },
    'cyber-security-threat-exposure': {
      width: cellWidth * gridConfig.columns,
      height: cellHeight * (gridConfig.orientation === 'landscape' ? 14 : 20)
    },
    'combined-scan-grid': {
      width: cellWidth * gridConfig.columns,
      height: cellHeight * (gridConfig.orientation === 'landscape' ? 14 : 20)
    },
    'advanced-scan-grid': {
      width: cellWidth * gridConfig.columns,
      height: cellHeight * (gridConfig.orientation === 'landscape' ? 14 : 20)
    },

    // Summary Widgets - scaled dimensions
    'open-ports-summary': { width: cellWidth * 8, height: cellHeight * 6 }, // 8x6 (was 4x3)
    'darkweb-summary': { width: cellWidth * 8, height: cellHeight * 6 },
    'accounts-summary': { width: cellWidth * 8, height: cellHeight * 6 },
    'accounts-admin-summary': { width: cellWidth * 10, height: cellHeight * 8 }, // 10x8 (was 5x4)
    'firewall-summary': { width: cellWidth * 8, height: cellHeight * 6 },
    'localhost-firewall-findings-summary': { width: cellWidth * 8, height: cellHeight * 6 },
    'localhost-firewall-cost-summary': { width: cellWidth * gridConfig.columns, height: cellHeight * 6 },
    'localhost-service-cost-summary': { width: cellWidth * gridConfig.columns, height: cellHeight * 6 },
    'localhost-os-cost-summary': { width: cellWidth * gridConfig.columns, height: cellHeight * 6 },
    'localhost-open-ports-cost-summary': { width: cellWidth * gridConfig.columns, height: cellHeight * 6 },
    'network-service-cost-summary': { width: cellWidth * gridConfig.columns, height: cellHeight * 6 },
    'network-open-ports-cost-summary': { width: cellWidth * gridConfig.columns, height: cellHeight * 6 },
    'ad-computer-cost-summary': { width: cellWidth * gridConfig.columns, height: cellHeight * 6 },
    'ad-users-cost-summary': { width: cellWidth * gridConfig.columns, height: cellHeight * 6 },
    'ad-groups-cost-summary': { width: cellWidth * gridConfig.columns, height: cellHeight * 6 },

    'ms365-inactive-users-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'ms365-users-without-mfa-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'ms365-policy-config-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'localhost-os-version-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'localhost-sensitive-open-ports-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'localhost-services-risk-breakdown-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'localhost-high-privilege-services-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'pii-detected-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 }, // Full width
    'ou-cost-summary': { width: cellWidth * gridConfig.columns, height: cellHeight * 6 },
    'fsmo-cost-summary': { width: cellWidth * gridConfig.columns, height: cellHeight * 6 },
    'gpo-cost-summary': { width: cellWidth * gridConfig.columns, height: cellHeight * 6 },
    'overall-risk-score': { width: cellWidth * 12, height: cellHeight * 16 }, // 12x16 (was 6x8)
    // External Risk Scan table widgets - Full width tables
    'external-all-findings-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'external-warnings-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'external-passed-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 },
    'external-compliance-table': { width: cellWidth * gridConfig.columns, height: cellHeight * 20 }
  };

  const size = sizes[widgetType] || { width: cellWidth * 3, height: cellHeight * 4 };

  // Ensure paginated widgets don't exceed max page height
  if (PAGINATED_WIDGETS.includes(widgetType) && size.height > gridConfig.maxWidgetHeight) {
    size.height = gridConfig.maxWidgetHeight;
  }

  return size;
};

const getDefaultElementSize = (elementType, gridConfig = GRID_CONFIG) => {
  const cellWidth = gridConfig.cellWidth;
  const cellHeight = gridConfig.cellHeight;

  const sizes = {
    title: { width: cellWidth * 8, height: cellHeight * 4 }, // 8x4 (was 4x2)
    text: { width: cellWidth * 8, height: cellHeight * 6 }, // 8x6 (was 4x3)
    image: { width: cellWidth * 6, height: cellHeight * 8 }, // 6x8 (was 3x4)
    divider: { width: cellWidth * 8, height: cellHeight * 2 }, // 8x2 (was 4x1)
    embed: { width: cellWidth * 8, height: cellHeight * 8 }, // 8x8 (was 4x4)
    shape: { width: cellWidth * 2, height: cellHeight * 4 }, // 2x4 (was 1x2)
    icon: { width: cellWidth * 2, height: cellHeight * 2 }, // 2x2 (was 1x1)
    header: { width: gridConfig.pageWidth, height: cellHeight * 2 }, // Full page width, 2 rows (was 1)
    footer: { width: gridConfig.pageWidth, height: cellHeight * 2 } // Full page width, 2 rows (was 1)
  };
  return sizes[elementType] || { width: cellWidth * 2, height: cellHeight * 2 };
};

const getDefaultWidgetConfig = (widgetType) => ({
  title: getDefaultTitle(widgetType),
  showTitle: true
});

const getDefaultElementConfig = (elementType) => ({
  content: elementType === 'title' ? 'Title' :
    elementType === 'text' ? 'Enter your text here...' :
      elementType === 'header' ? 'Header' :
        elementType === 'footer' ? 'Footer' : '',
  // Shapes and Icons default to center/middle, others to left/top
  align: (elementType === 'shape' || elementType === 'icon') ? 'center' : 'left',
  verticalAlign: (elementType === 'shape' || elementType === 'icon') ? 'middle' : 'top',
  // Specific defaults
  shape: elementType === 'shape' ? 'rounded' : undefined,
  icon: elementType === 'icon' ? 'star' : undefined,
  fromPage: elementType === 'header' || elementType === 'footer' ? 1 : undefined, // Default to page 1
  toPage: elementType === 'header' || elementType === 'footer' ? 'last' : undefined // Default to last page
});

const getDefaultTitle = (widgetType) => {
  const titles = {
    numeric: 'Numeric Value',
    bar: 'Bar Chart',
    line: 'Line Chart',
    pie: 'Pie Chart',
    donut: 'Donut Chart',
    radar: 'Radar Chart',
    polar: 'Polar Area Chart',
    bubble: 'Bubble Chart',
    scatter: 'Scatter Plot',
    table: 'Data Table',
    gauge: 'Gauge',
    statcard: 'Stat Card',
    // Findings Widgets
    'findings-status': 'Security Findings Status',
    'single-finding-status': 'Finding Status',
    'key-findings': 'Key Findings Summary',
    'open-ports': 'Sensitive Open Ports',
    'ad-inactive-users-table': 'Inactive AD Users',
    'ad-inactive-computers-table': 'Inactive AD Computers',
    'ad-gpo-analysis-table': 'GPO Analysis',
    'localhost-os-version-table': 'Localhost OS Version',
    'localhost-sensitive-open-ports-table': 'Sensitive Open Ports - Localhost',
    'pii-detected-table': 'PII Detected',
    'localhost-services-risk-breakdown-table': 'Non-Default Services Risk Breakdown by State',
    'localhost-high-privilege-services-table': 'High-Privilege Services',
    'network-backup-status-table': 'Backup Status by Device',
    'network-os-versions-table': 'OS Versions',
    'network-firewall-status-table': 'Firewall Status by Device',
    'network-firewall-status-chart': 'Network Firewall Status',
    'network-detailed-services-table': 'Detailed Non-Default Services Found',
    'network-non-default-services-table': 'Non-Default Services Risk Breakdown by State',
    'network-services-risk-breakdown-table': 'Non-Default Services Risk Breakdown by State',
    'network-high-privilege-services-table': 'High-Privilege Services',
    'network-users-no-expiring-password-table': 'Users with no Expiring Password',
    'network-users-expiring-password-table': 'Users with Expiring Password',
    'network-admins-no-expiring-password-table': 'Admins with no Expiring Password',
    'network-admins-expiring-password-table': 'Admins with Expiring Password',
    'network-admins-daily-work-table': 'Admins Used for Daily Work',
    'findings-grid': 'Findings Overview',
    'infrastructure-scan-grid': 'Infrastructure Scan Grid',
    'managed-service-experience': 'Managed IT / Service Experience',
    'security-scan-grid': 'Security Scan Grid',
    'cyber-security-threat-exposure': 'Cyber Security (Threat & Exposure)',
    // Summary Widgets
    'open-ports-summary': 'Open Ports Findings Summary',
    'darkweb-summary': 'Dark Web Scanning Summary',
    'accounts-summary': 'Accounts Finding Summary',
    'accounts-admin-summary': 'Admins Summary',
    'firewall-summary': 'Firewall Findings Summary',
    'localhost-firewall-findings-summary': 'Localhost Firewall Findings Summary',
    'localhost-firewall-cost-summary': 'Localhost Firewall Cost Summary',
    'localhost-service-risk-summary': 'Localhost Service Risk Summary',
    'localhost-service-cost-summary': 'Localhost Service Cost Summary',
    'localhost-os-cost-summary': 'Localhost OS Cost Summary',
    'localhost-open-ports-cost-summary': 'Localhost Open Ports Cost Summary',
    'service-risk-summary': 'Service Risk Summary',
    'network-service-risk-summary': 'Network Service Risk Summary',
    'network-service-cost-summary': 'Network Service Cost',
    'network-open-ports-cost-summary': 'Open Ports Cost Summary',
    'ad-computer-cost-summary': 'AD Computer Cost Summary',
    'ad-users-cost-summary': 'AD Users Cost Summary',
    'ad-groups-cost-summary': 'AD Groups Cost Summary',
    'ou-cost-summary': 'OU Cost Summary',
    'fsmo-cost-summary': 'FSMO Cost Summary',
    'gpo-cost-summary': 'GPO Cost Summary',
    'overall-risk-score': 'Overall Risk Score',

    // Charts - Localhost
    'user-password-status': 'User Password Status',
    'disk-free-used': 'Disk Free vs Used',
    'firewall-status': 'Firewall Status',
    'service-start-modes': 'Service Start Modes',
    'admin-password-status': 'Admin Password Status',
    'password-strength': 'Password Strength',
    'hibp-breach-status': 'HIBP Breach Status',
    'windows-version': 'Windows Version',
    'common-software': 'Common Software',
    'hibp-status': 'HIBP Status',
    'rdp-status': 'RDP Status',
    'browser-status': 'Browser Status',
    'system-age': 'System Age',

    // Charts - Network
    'open-ports-chart': 'Open Ports Chart',
    'device-type': 'Device Type',
    'network-sensitive-ports': 'Sensitive Ports',

    // Charts - MS365
    'ms365-users-pie-chart': 'MS Users',
    'ms365-mfa-pie-chart': 'MS MFA',
    'ms365-policy-pie-chart': 'MS Policy',
    'pii-detection-pie-chart': 'PII Detection',
    'pii-finding-summary': 'PII Finding Summary',
    'pii-cost-summary': 'PII Cost Summary',
    'inactive-ms-users-summary': 'Inactive MS Users Summary',
    'inactive-ms-users-cost-summary': 'Inactive MS Users Cost Summary',
    'mfa-summary': 'MFA Summary',
    'mfa-cost-summary': 'MFA Cost Summary',
    'policy-configuration-cost-summary': 'Policy Configuration Cost Summary',

    // Charts - Active Directory
    'ad-object-types': 'AD Object Types',
    'ad-objects-pie-chart': 'AD Objects Pie Chart',
    'fsmo-roles': 'FSMO Roles',
    'active-inactive-users': 'Active/Inactive Users',
    'password-expiry': 'Password Expiry',
    'active-inactive-computers': 'Active/Inactive Computers',
    'group-types': 'Group Types',
    'ou-membership': 'OU Membership',
    'gpo-timeline': 'GPO Timeline',

    // External Risk Scan Widgets
    'external-risk-score': 'External Risk Score',
    'external-domain-info': '🌐 Domain Information',
    'external-risk-overview': 'External Risk Overview',
    'external-category-breakdown': 'External Category Breakdown',
    'external-severity-pie': 'External Severity Pie Chart',
    'external-status-pie': 'External Status Pie Chart',
    'external-category-bar': 'External Category Bar Chart',
    'external-compliance-summary': 'External Compliance Summary',
    'external-all-findings-table': 'External All Findings',
    'external-warnings-table': 'External Warnings',
    'external-passed-table': 'External Passed Checks',
    'external-compliance-table': 'External Compliance Mapping',
    'external-dns-findings': '🔗 Domain & DNS Configuration',
    'external-email-findings': '📧 Email Security',
    'external-ssl-findings': '🔒 SSL/TLS',
    'external-headers-findings': '🛡️ Security Headers',
    'external-infrastructure-findings': '☁️ Infrastructure',
    'external-breach-findings': '🔓 Breach Intelligence'
  };

  if (titles[widgetType]) {
    return titles[widgetType];
  }

  // Just capitalise the first letter of each word in the widget type
  return widgetType
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  // return titles[widgetType] || 'Widget';
};


const aggregateData = (data, aggregation, filters) => {
  if (!data) return null;

  if (typeof data === 'object' && data !== null) {
    if (Array.isArray(data.labels) && Array.isArray(data.values)) {
      return data;
    }
  }

  if (Array.isArray(data)) {
    switch (aggregation) {
      case 'count':
        return { value: data.length, label: 'Count' };
      case 'sum':
        return { value: data.reduce((sum, item) => sum + (item.value || 0), 0), label: 'Sum' };
      case 'average':
        const sum = data.reduce((s, item) => s + (item.value || 0), 0);
        return { value: data.length > 0 ? sum / data.length : 0, label: 'Average' };
      case 'min':
        return { value: Math.min(...data.map(item => item.value || 0)), label: 'Minimum' };
      case 'max':
        return { value: Math.max(...data.map(item => item.value || 0)), label: 'Maximum' };
      default:
        return { value: data.length, label: 'Count' };
    }
  }

  if (typeof data === 'number') {
    return { value: data, label: 'Value' };
  }

  if (typeof data === 'string') {
    return { value: data, label: 'Text' };
  }

  return { value: Object.keys(data).length, label: 'Count' };
};

// Flag to indicate placeholder/sample data
const PLACEHOLDER_FLAG = '__isPlaceholder__';

const getSampleData = (widgetType, reason = 'No data source selected') => {
  // Return placeholder data with a flag so widgets can show appropriate UI
  const samples = {
    numeric: { value: 42, label: 'Sample Value' },
    bar: { values: [12, 19, 3, 5], labels: ['Critical', 'High', 'Medium', 'Low'] },
    line: { values: [65, 59, 80, 81, 56, 55], labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'] },
    pie: { values: [45, 25, 20, 10], labels: ['Windows', 'Linux', 'macOS', 'Other'] },
    donut: { values: [70, 20, 10], labels: ['Passed', 'Failed', 'Warning'] },
    radar: { values: [65, 59, 90, 81, 56], labels: ['Performance', 'Security', 'Reliability', 'Usability', 'Compatibility'] },
    polar: { values: [11, 16, 7, 3, 14], labels: ['Critical', 'High', 'Medium', 'Low', 'Info'] },
    bubble: {
      values: [
        { x: 20, y: 30, r: 15 },
        { x: 40, y: 10, r: 10 },
        { x: 30, y: 20, r: 20 }
      ]
    },
    scatter: {
      values: [
        { x: -10, y: 0 },
        { x: 0, y: 10 },
        { x: 10, y: 5 }
      ]
    },
    gauge: { value: 75, label: 'Security Score' },
    statcard: { value: 42, label: 'Sample Stat' },
    table: {
      headers: ['Category', 'Value'],
      rows: [
        ['Critical', '12'],
        ['High', '19'],
        ['Medium', '3'],
        ['Low', '5']
      ]
    },
    'network-encryption-cost-summary': {
      item: 'Encryption',
      totalCount: 10,
      count: 2,
      unitCost: 50,
      itemCost: 100,
      riskPercent: '20.00%',
      hasEncryptionData: true
    },
    'network-logical-disks-table': {
      headers: ['No.', 'Device', 'Computer Name', 'Drive Name', 'File System', 'Size (GB)', 'Used (GB)', 'Free (GB)', 'Disk Usage %'],
      rows: [
        ['1', '192.168.1.10', 'WORKSTATION-01', 'C:', 'NTFS', '500', '150', '350', '30.00%'],
        ['2', '192.168.1.10', 'WORKSTATION-01', 'D:', 'NTFS', '1000', '200', '800', '20.00%'],
        ['3', '192.168.1.50', 'SERVER-DB', 'C:', 'NTFS', '250', '100', '150', '40.00%']
      ]
    }
  };
  return samples[widgetType] || { value: 'No Data', label: 'Configure Data Source', [PLACEHOLDER_FLAG]: true, reason };
};

// Evaluate a single condition
const evaluateCondition = (itemValue, operator, targetValue) => {
  // Normalize values for comparison
  const normalize = (val) => String(val).toLowerCase();

  if (operator === 'is_empty') {
    return itemValue === undefined || itemValue === null || itemValue === '' || (Array.isArray(itemValue) && itemValue.length === 0) || (typeof itemValue === 'object' && itemValue !== null && !Array.isArray(itemValue) && Object.keys(itemValue).length === 0);
  }
  if (operator === 'is_not_empty') {
    return itemValue !== undefined && itemValue !== null && itemValue !== '' && !(Array.isArray(itemValue) && itemValue.length === 0) && !(typeof itemValue === 'object' && itemValue !== null && !Array.isArray(itemValue) && Object.keys(itemValue).length === 0);
  }
  if (operator === 'is_true') {
    return itemValue === true || itemValue === 'true' || itemValue === 1;
  }
  if (operator === 'is_false') {
    return itemValue === false || itemValue === 'false' || itemValue === 0;
  }

  if (operator === 'in_list') {
    // Match against predefined lists: targetValue is a list key (e.g., 'sensitive_ports')
    // or comma-separated values
    const predefined = typeof PREDEFINED_LISTS !== 'undefined' && PREDEFINED_LISTS[targetValue];
    const listValues = predefined ? predefined.values : String(targetValue).split(',').map(v => v.trim());
    return listValues.includes(String(itemValue));
  }
  if (operator === 'not_in_list') {
    const predefined = typeof PREDEFINED_LISTS !== 'undefined' && PREDEFINED_LISTS[targetValue];
    const listValues = predefined ? predefined.values : String(targetValue).split(',').map(v => v.trim());
    return !listValues.includes(String(itemValue));
  }

  // Handle special "(Empty)" value from UI filters
  let sTarget = normalize(targetValue ?? '');
  if (sTarget === '(empty)' || sTarget === 'empty') {
    sTarget = '';
  }

  // Handle Arrays specially for contains/not_contains (Exact Item Match)
  if (Array.isArray(itemValue)) {
    if (operator === 'contains') {
      return itemValue.some(v => normalize(v) === sTarget);
    }
    if (operator === 'not_contains') {
      return !itemValue.some(v => normalize(v) === sTarget);
    }
    // Fallthrough for other operators (will stringify)
  }

  // For other operators, we need safe values
  const sItem = normalize(itemValue ?? '');

  switch (operator) {
    case 'equals':
      return sItem === sTarget;
    case 'not_equals':
      return sItem !== sTarget;
    case 'contains':
      return sItem.includes(sTarget);
    case 'not_contains':
      return !sItem.includes(sTarget);
    case 'starts_with':
      return sItem.startsWith(sTarget);
    case 'ends_with':
      return sItem.endsWith(sTarget);
    case 'greater_than':
      return parseFloat(itemValue) > parseFloat(targetValue);
    case 'greater_than_or_equal':
      return parseFloat(itemValue) >= parseFloat(targetValue);
    case 'less_than':
      return parseFloat(itemValue) < parseFloat(targetValue);
    case 'less_than_or_equal':
      return parseFloat(itemValue) <= parseFloat(targetValue);
    default:
      return true;
  }
};

// Filter data based on device/parent filters
// Filter data based on device/parent filters
// Supports array-traversing paths using arrayQuantifier (any_match, all_match, none_match)
// AND implements "Scoped Array Filtering": consecutive AND filters on the same array 
// are evaluated against the SAME item.
const filterTableData = (data, filters) => {
  if (!Array.isArray(data) || !filters || filters.length === 0) return data;

  // 1. Group filters to handle array scoping
  // Consecutive filters with 'AND' logic that share the same parent path
  // should be evaluated as a group against the same array item.
  const steps = [];
  let currentGroup = null;

  for (let i = 0; i < filters.length; i++) {
    const f = filters[i];
    // Determine potential parent path (up to last dot)
    const lastDot = f.fieldPath.lastIndexOf('.');
    const prefix = lastDot > -1 ? f.fieldPath.substring(0, lastDot) : null;

    // Check if valid to continue current group
    // - Must be AND logic (except for the very first filter of the list)
    // - Must share the same prefix
    const isAnd = (i === 0) || (f.logic === 'AND');

    if (currentGroup && isAnd && prefix && prefix === currentGroup.prefix) {
      currentGroup.filters.push(f);
    } else {
      // Push finished group/filter
      if (currentGroup) steps.push(currentGroup);

      // Start new group or single item
      if (prefix) {
        currentGroup = {
          type: 'group',
          prefix: prefix,
          logic: f.logic || 'AND', // Logic connecting this group to previous result
          filters: [f]
        };
      } else {
        currentGroup = null;
        steps.push({
          type: 'single',
          logic: f.logic || 'AND',
          filter: f
        });
      }
    }
  }
  if (currentGroup) steps.push(currentGroup);

  // 2. Evaluation Helper
  const evaluateFilter = (item, filter) => {
    const fieldValue = getNestedField(item, filter.fieldPath);
    // If array traversal, apply quantifier
    if (Array.isArray(fieldValue) && fieldValue.__isArrayTraversal) {
      const quantifier = filter.arrayQuantifier || 'any_match';
      switch (quantifier) {
        case 'all_match': return fieldValue.length > 0 && fieldValue.every(v => evaluateCondition(v, filter.operator, filter.value));
        case 'none_match': return fieldValue.every(v => !evaluateCondition(v, filter.operator, filter.value));
        case 'any_match': default: return fieldValue.some(v => evaluateCondition(v, filter.operator, filter.value));
      }
    }
    return evaluateCondition(fieldValue, filter.operator, filter.value);
  };

  const evaluateGroup = (item, group) => {
    // Get the parent object/array
    const parentValue = getNestedField(item, group.prefix);

    if (Array.isArray(parentValue)) {
      // Scoped Array Check:
      // Does ANY item in the array satisfy ALL filters in the group?
      // (Using 'any_match' logic implicitly for the group itself)
      return parentValue.some(arrItem => {
        return group.filters.every(f => {
          // Resolve path relative to the array item
          const relPath = f.fieldPath.substring(group.prefix.length + 1);
          // We use getNestedField again to handle deeper nesting or simple keys
          const val = getNestedField(arrItem, relPath);
          return evaluateCondition(val, f.operator, f.value);
        });
      });
    } else {
      // Scalar/Object Check:
      // Just check if the item satisfies all filters (standard AND logic)
      return group.filters.every(f => evaluateFilter(item, f));
    }
  };

  return data.filter(item => {
    if (steps.length === 0) return true;

    // Evaluate first step
    let result = steps[0].type === 'group'
      ? evaluateGroup(item, steps[0])
      : evaluateFilter(item, steps[0].filter);

    // Evaluate subsequent steps
    for (let i = 1; i < steps.length; i++) {
      const step = steps[i];
      const match = step.type === 'group'
        ? evaluateGroup(item, step)
        : evaluateFilter(item, step.filter);

      if (step.logic === 'OR') {
        result = result || match;
      } else {
        result = result && match;
      }
    }
    return result;
  });
};

// Process table data with column mappings
// Built-in lookup tables for computed/lookup columns
const PORT_TO_SERVICE_LOOKUP = {
  '20': 'FTP Data', '21': 'FTP', '22': 'SSH', '23': 'Telnet', '25': 'SMTP',
  '53': 'DNS', '67': 'DHCP', '68': 'DHCP', '69': 'TFTP', '80': 'HTTP',
  '110': 'POP3', '119': 'NNTP', '123': 'NTP', '135': 'MS RPC', '137': 'NetBIOS',
  '138': 'NetBIOS', '139': 'NetBIOS', '143': 'IMAP', '161': 'SNMP',
  '162': 'SNMP Trap', '389': 'LDAP', '443': 'HTTPS', '445': 'SMB',
  '465': 'SMTPS', '514': 'Syslog', '587': 'SMTP', '636': 'LDAPS',
  '993': 'IMAPS', '995': 'POP3S', '1433': 'MSSQL', '1434': 'MSSQL',
  '1521': 'Oracle', '1723': 'PPTP', '3306': 'MySQL', '3389': 'RDP',
  '5432': 'PostgreSQL', '5900': 'VNC', '5985': 'WinRM', '5986': 'WinRM',
  '6379': 'Redis', '8080': 'HTTP Proxy', '8443': 'HTTPS Alt', '8888': 'HTTP Alt',
  '27017': 'MongoDB'
};

// Sensitive ports - matches the backend's sensetive_ports from Network.js
const SENSITIVE_PORT_TO_SERVICE = {
  '21': 'FTP', '22': 'SSH', '23': 'Telnet', '25': 'SMTP', '53': 'DNS',
  '80': 'HTTP', '139': 'NetBIOS', '143': 'SMB', '3306': 'MySQL',
  '3389': 'RDP', '8080': 'HTTP'
};

// Predefined value lists for the 'in_list' filter operator
const PREDEFINED_LISTS = {
  sensitive_ports: {
    label: 'Sensitive Ports (21, 22, 23, 25, 53, 80, 139, 143, 3306, 3389, 8080)',
    values: Object.keys(SENSITIVE_PORT_TO_SERVICE)
  }
};

// Resolve a lookup column value using built-in lookup tables
const resolveLookupValue = (lookupSource, inputValue) => {
  if (!lookupSource || inputValue === null || inputValue === undefined) return '';
  if (lookupSource === 'port_to_service') {
    return PORT_TO_SERVICE_LOOKUP[String(inputValue)] || 'Unknown';
  }
  if (lookupSource === 'sensitive_port_to_service') {
    return SENSITIVE_PORT_TO_SERVICE[String(inputValue)] || '';
  }
  return '';
};

// Rule evaluation logic with smart numeric parsing
const evaluateRule = (itemValue, operator, matchValue) => {
  if (itemValue === null || itemValue === undefined) return false;

  const sItemVal = String(itemValue).toLowerCase();
  const sMatchVal = String(matchValue ?? '').toLowerCase();

  // Numeric extraction for numeric operators
  const isNumericOp = ['greater_than', 'less_than', 'greater_than_equal', 'less_than_equal'].includes(operator);

  if (isNumericOp) {
    // Extract number from strings like "16 GB" or "80%" 
    const extractNum = (str) => {
      if (typeof str === 'number') return str;
      const match = String(str).match(/[-+]?[0-9]*\.?[0-9]+/);
      return match ? parseFloat(match[0]) : NaN;
    };

    const nItemVal = extractNum(itemValue);
    const nMatchVal = parseFloat(matchValue);

    if (isNaN(nItemVal) || isNaN(nMatchVal)) return false;

    switch (operator) {
      case 'greater_than': return nItemVal > nMatchVal;
      case 'less_than': return nItemVal < nMatchVal;
      case 'greater_than_equal': return nItemVal >= nMatchVal;
      case 'less_than_equal': return nItemVal <= nMatchVal;
      default: return false;
    }
  }

  // String & Boolean operators
  switch (operator) {
    case 'not_equals':
      return sItemVal !== sMatchVal;
    case 'contains':
      return sItemVal.includes(sMatchVal);
    case 'equals':
    default:
      // Robust equality check (handling booleans/Yes-No)
      return sItemVal === sMatchVal ||
        (sMatchVal === 'true' && (itemValue === true || sItemVal === 'yes')) ||
        (sMatchVal === 'false' && (itemValue === false || sItemVal === 'no')) ||
        (sMatchVal === 'yes' && (itemValue === true || sItemVal === 'true')) ||
        (sMatchVal === 'no' && (itemValue === false || sItemVal === 'false'));
  }
};

// Resolve a conditional status value
const resolveConditionalValue = (col, item, nestedArrayPath) => {
  const fields = col.conditionalFields || [];
  const allRes = col.allMatchResult || 'Fully Enabled';
  const someRes = col.someMatchResult || 'Partially Enabled';
  const noneRes = col.noneMatchResult || 'Fully Disabled';

  let matchCount = 0;
  let validFieldsCount = 0;

  fields.forEach(fieldData => {
    // Support both simple string paths and object-based rules
    const isObject = fieldData && typeof fieldData === 'object';
    const fieldPath = isObject ? fieldData.fieldPath : fieldData;
    const operator = (isObject ? fieldData.operator : 'equals') || 'equals';
    const matchVal = (isObject && fieldData.matchValue !== undefined)
      ? fieldData.matchValue
      : (col.matchValue || '');

    if (!fieldPath) return;
    validFieldsCount++;
    const val = getNestedField(item, fieldPath, nestedArrayPath);

    if (evaluateRule(val, operator, matchVal)) {
      matchCount++;
    }
  });

  if (validFieldsCount === 0) return '(configure fields)';
  if (matchCount === validFieldsCount) return allRes;
  if (matchCount > 0) return someRes;
  return noneRes;
};

// Process table data with column mappings
// Built-in lookup tables for computed/lookup columns
// ...
const processTableData = (data, tableColumns, metricPath, tableSort = null, nestedArrayPath = null, returnEmptyIfNoData = false) => {
  console.log('[processTableData] Called with:', {
    hasData: !!data,
    dataLength: Array.isArray(data) ? data.length : 'not-array',
    tableColumnsCount: tableColumns?.length || 0,
    tableColumnHeaders: tableColumns?.map(c => c.header),
    tableColumnIds: tableColumns?.map(c => c.id),
    metricPath,
    tableSort,
    nestedArrayPath,
    returnEmptyIfNoData
  });
  if (!data) {
    if (returnEmptyIfNoData) {
      // If we have columns configured, return them as headers with empty rows
      if (tableColumns && tableColumns.length > 0) {
        return {
          headers: tableColumns.map(col => col.header || col.fieldPath || 'Column'),
          rows: []
        };
      }
      return { headers: [], rows: [] };
    }
    return getSampleData('table', 'No data at selected path');
  }
  if (!tableColumns || tableColumns.length === 0) {
    // If no columns configured, try to auto-generate from data
    if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
      const autoHeaders = Object.keys(data[0]).filter(key => !TABLE_INTERNAL_FIELDS.includes(key));
      if (autoHeaders.length > 0) {
        const autoRows = data.map(item => autoHeaders.map(header => {
          const value = item[header];
          return formatTableValue(value);
        }));
        return { headers: autoHeaders, rows: autoRows };
      }
    }
    if (returnEmptyIfNoData) return { headers: [], rows: [] };
    return getSampleData('table', 'Configure table columns in properties panel');
  }

  // Extract headers from column configuration
  const headers = tableColumns.map(col => col.header || col.fieldPath || 'Column');

  // Process rows based on data structure
  let rows = [];

  if (Array.isArray(data)) {
    // If data is an array, each item is a row
    console.log('[processTableData] Processing array data:', data.length, 'items, sample item:', data[0]);
    // First, create rows with original data for sorting
    let dataWithRows = data.map((item, index) => ({
      originalItem: item,
      rowIndex: index,
      row: tableColumns.map(col => {
        // Handle lookup columns
        if (col.columnType === 'lookup' && col.lookupSource) {
          // Get the source value from another column's fieldPath
          const sourceValue = col.sourceField ? getNestedField(item, col.sourceField, nestedArrayPath) : null;
          return resolveLookupValue(col.lookupSource, sourceValue);
        }
        // Handle conditional status columns
        if (col.columnType === 'conditional') {
          return resolveConditionalValue(col, item, nestedArrayPath);
        }
        // Handle lookout field columns
        if (col.columnType === 'lookout') {
          if (item._flattenedItem && typeof item._flattenedItem === 'object') {
            const subItem = item._flattenedItem;
            // Handle both sub-field mode and whole-item mode
            const flattenField = nestedArrayPath || '';
            const colPath = col.fieldPath || '';

            // Try to resolve the field path on the expanded sub-item
            // getNestedField will strip the flattenField prefix if it matches
            const val = getNestedField(subItem, colPath, flattenField);

            // If we resolved a single primitive value (e.g. "C:"), show it.
            // This handles both absolute paths and relative paths.
            if (val !== null && val !== undefined && typeof val !== 'object') {
              return formatTableValue(val);
            }

            // If we resolved a value but it's an array (and not the whole item), just return it
            if (Array.isArray(val) && val.length > 0 && colPath !== flattenField) {
              return formatTableValue(val);
            }

            // Whole-item mode: show all primitive key-value pairs from _flattenedItem
            const parts = Object.keys(subItem)
              .filter(k => !k.startsWith('_') && typeof subItem[k] !== 'object')
              .map(k => `"${k}": "${subItem[k]}"`);
            return parts.length > 0 ? parts.join(', ') : '(empty item)';
          }
          // No flattened item — row has no matching nested array entry
          return '';
        }
        // Skip unconfigured columns (no fieldPath set) — show placeholder
        if (!col.fieldPath) {
          return '(select field)';
        }
        // Handle flattened data special fields
        if (col.fieldPath === '_flattenedKey' && item._flattenedKey !== undefined) {
          return formatTableValue(item._flattenedKey);
        }
        if (col.fieldPath === '_flattenedValue' && item._flattenedValue !== undefined) {
          return formatTableValue(item._flattenedValue);
        }
        // For flattened items, also check the _flattenedItem for nested fields
        if (item._flattenedItem && typeof item._flattenedItem === 'object') {
          const fromFlattened = getNestedField(item._flattenedItem, col.fieldPath, nestedArrayPath);
          if (fromFlattened !== null && fromFlattened !== undefined) {
            return formatTableValue(fromFlattened);
          }
        }
        // Pass nestedArrayPath to getNestedField for proper path resolution
        let fieldPath = col.fieldPath;
        let datePipe = col.dateFormat; // Use dedicated property if available

        if (!datePipe && fieldPath && fieldPath.includes('|')) {
          const parts = fieldPath.split('|').map(p => p.trim());
          fieldPath = parts[0];
          datePipe = parts[1]?.toLowerCase();
        }

        const value = getNestedField(item, fieldPath, nestedArrayPath);

        if (value === null || value === undefined) {
          console.warn(`[processTableData] Field "${fieldPath}" not found in item ${index}`);
          return '';
        }

        // Apply formatting pipe if present
        if (datePipe) {
          try {
            const dateObj = typeof value === 'string' ? parseISO(value) : new Date(value);
            if (isValid(dateObj)) {
              if (datePipe === 'date') return format(dateObj, 'yyyy-MM-dd');
              if (datePipe === 'datetime') return format(dateObj, 'yyyy-MM-dd HH:mm:ss');
              if (datePipe === 'time') return format(dateObj, 'HH:mm:ss');
              // Fallback to generic date-fns format if it looks like a format string
              return format(dateObj, datePipe);
            }
          } catch (e) {
            console.warn(`[processTableData] Failed to format date for pipe "${datePipe}":`, e);
          }
        }

        return formatTableValue(value);
      })
    }));

    // Apply sorting if configured
    if (tableSort && tableSort.fieldPath) {
      const sortColumn = tableColumns.find(col => col.fieldPath === tableSort.fieldPath);
      if (sortColumn) {
        const sortIndex = tableColumns.findIndex(col => col.fieldPath === tableSort.fieldPath);
        dataWithRows.sort((a, b) => {
          const aValue = getNestedField(a.originalItem, sortColumn.fieldPath, nestedArrayPath);
          const bValue = getNestedField(b.originalItem, sortColumn.fieldPath, nestedArrayPath);

          // Handle null/undefined values
          if (aValue === null || aValue === undefined) return tableSort.direction === 'asc' ? 1 : -1;
          if (bValue === null || bValue === undefined) return tableSort.direction === 'asc' ? -1 : 1;

          // Compare values
          let comparison = 0;
          if (typeof aValue === 'number' && typeof bValue === 'number') {
            comparison = aValue - bValue;
          } else if (typeof aValue === 'string' && typeof bValue === 'string') {
            comparison = aValue.localeCompare(bValue);
          } else if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
            comparison = aValue === bValue ? 0 : (aValue ? 1 : -1);
          } else {
            // Fallback: convert to string and compare
            comparison = String(aValue).localeCompare(String(bValue));
          }

          return tableSort.direction === 'asc' ? comparison : -comparison;
        });
      }
    }

    // Extract just the rows after sorting
    rows = dataWithRows.map(item => item.row);
  } else if (typeof data === 'object') {
    // Special case: If columns are 'key' and 'value', convert object to key-value pairs
    const hasKeyValueColumns = tableColumns.length === 2 &&
      tableColumns.some(col => col.fieldPath === 'key') &&
      tableColumns.some(col => col.fieldPath === 'value');

    if (hasKeyValueColumns) {
      console.log('[processTableData] Converting object to key-value pairs');
      const TABLE_INTERNAL_FIELDS = ['_parent', '_parentIp', '_parentHostname', '__isPlaceholder__'];
      rows = Object.entries(data)
        .filter(([key]) => !TABLE_INTERNAL_FIELDS.includes(key) && !key.startsWith('_'))
        .map(([key, value]) => {
          return tableColumns.map(col => {
            if (col.fieldPath === 'key') {
              return formatTableValue(key);
            } else if (col.fieldPath === 'value') {
              return formatTableValue(value);
            }
            return '';
          });
        });
    } else {
      // If data is an object, create rows from object values
      // For objects with nested arrays, create multiple rows
      const firstArrayField = tableColumns.find(col => {
        const fieldData = getNestedField(data, col.fieldPath, nestedArrayPath);
        return Array.isArray(fieldData);
      });

      if (firstArrayField) {
        const arrayData = getNestedField(data, firstArrayField.fieldPath, nestedArrayPath);
        if (Array.isArray(arrayData) && arrayData.length > 0) {
          rows = arrayData.map(item => {
            return tableColumns.map(col => {
              if (col.fieldPath === firstArrayField.fieldPath) {
                return formatTableValue(item);
              }
              // Handle conditional status
              if (col.columnType === 'conditional') {
                return resolveConditionalValue(col, item, nestedArrayPath);
              }
              // For other columns, try to get from the item or parent data
              const value = getNestedField(item, col.fieldPath, nestedArrayPath) || getNestedField(data, col.fieldPath, nestedArrayPath);
              return formatTableValue(value);
            });
          });
        }
      } else {
        // Single row from object properties
        rows = [tableColumns.map(col => {
          if (col.columnType === 'conditional') {
            return resolveConditionalValue(col, data, nestedArrayPath);
          }
          const value = getNestedField(data, col.fieldPath, nestedArrayPath);
          return formatTableValue(value);
        })];
      }
    }
  }

  return {
    headers,
    rows: (rows.length > 0 || returnEmptyIfNoData) ? rows : getSampleData('table').rows
  };
};

// Get nested field from object using dot notation or direct key
// Enhanced to support nested array expansion scenarios:
// - Direct field access: "Model" on expanded nested item
// - Full path access: "system.Disk Drives.Model" 
// - Relative path from nested array: When nestedArrayPath is "system.Disk Drives", 
//   the path "system.Disk Drives.Model" should resolve to "Model" on the expanded item
// - Array traversal: When mid-path encounters an array (e.g. "system.Non-Default Services.Display Name"),
//   returns all values from array items with a __isArrayTraversal marker
const getNestedField = (obj, path, nestedArrayPath = null) => {
  if (!obj || !path) return null;

  // Handle nested array context - strip the parent array path from the field path
  // so paths like "system.Logical Disks.Drive Name" work on a "Logical Disks" item
  let resolvedPath = path;
  if (nestedArrayPath) {
    const lowerPath = path.toLowerCase();
    const lowerPrefix = (nestedArrayPath + '.').toLowerCase();
    if (lowerPath.startsWith(lowerPrefix)) {
      resolvedPath = path.slice(nestedArrayPath.length + 1);
    }
  }

  // If path is a direct key after stripping parent array (e.g. "Drive Name" on a Logical Disks item)
  if (obj[resolvedPath] !== undefined) {
    return obj[resolvedPath];
  }

  // Try dot notation lookup with the resolved path
  try {
    const parts = resolvedPath.split('.');
    let current = obj;

    for (let i = 0; i < parts.length; i++) {
      const key = parts[i];
      if (current === null || current === undefined) return null;

      if (typeof current === 'object' && !Array.isArray(current)) {
        // Try exact match first
        if (current[key] !== undefined) {
          current = current[key];
        } else {
          // Try case-insensitive match
          const matchingKey = Object.keys(current).find(k => k.toLowerCase() === key.toLowerCase());
          if (matchingKey !== undefined) {
            current = current[matchingKey];
          } else {
            // Check for multi-word keys with dots (e.g. "Disk Drives")
            let combinedKey = key;
            let foundCombined = false;
            for (let j = i + 1; j < parts.length; j++) {
              combinedKey += '.' + parts[j];
              if (current[combinedKey] !== undefined) {
                current = current[combinedKey];
                i = j;
                foundCombined = true;
                break;
              }
              const spacedKey = combinedKey.replace(/\./g, ' ');
              if (current[spacedKey] !== undefined) {
                current = current[spacedKey];
                i = j;
                foundCombined = true;
                break;
              }
            }
            if (!foundCombined) return null;
          }
        }

        // After resolving the current key, check if we landed on an array
        // and there are remaining path parts — traverse the array
        if (Array.isArray(current) && i < parts.length - 1) {
          const remainingPath = parts.slice(i + 1).join('.');
          const values = current
            .filter(item => item !== null && item !== undefined)
            .map(item => getNestedField(item, remainingPath));
          // Mark as array traversal so filterTableData can apply quantifier
          values.__isArrayTraversal = true;
          return values;
        }
      } else if (Array.isArray(current) && /^\d+$/.test(key)) {
        current = current[parseInt(key, 10)];
      } else if (Array.isArray(current)) {
        // Array with non-numeric key — traverse all items
        const remainingPath = parts.slice(i).join('.');
        const values = current
          .filter(item => item !== null && item !== undefined)
          .map(item => getNestedField(item, remainingPath));
        values.__isArrayTraversal = true;
        return values;
      } else {
        return null;
      }
    }
    return current;
  } catch {
    return null;
  }
};

// Expand nested fields into multiple rows for table display AND filter out devices without the field
// This function acts as BOTH an expander AND a filter:
// - Devices WITHOUT the field are EXCLUDED from the results
// - Devices WITH arrays get one row per array item
// - Devices WITH primitives get one row
// - Optional nestedFilter can further filter array items (e.g., Status = "OK")
// Example: If devices have "system.Disk Drives" with 2 drives, device 192.168.1.144 becomes 2 rows
// Row 1: 192.168.1.144 | SQL2020.Beacon.local | Virtual HD ATA Device | null | OK
// Row 2: 192.168.1.144 | SQL2020.Beacon.local | Microsoft Virtual Disk | null | OK
const expandNestedArraysToRows = (dataArray, nestedFieldPath, nestedFilter = null) => {
  if (!Array.isArray(dataArray) || !nestedFieldPath) return dataArray;

  const expandedRows = [];

  // Helper function to check if an item matches the nested filter
  const itemMatchesNestedFilter = (item) => {
    if (!nestedFilter || !nestedFilter.fieldPath) return true;

    const { fieldPath, operator, value } = nestedFilter;
    const itemValue = item[fieldPath];

    switch (operator) {
      case 'equals':
        return String(itemValue).toLowerCase() === String(value).toLowerCase();
      case 'not_equals':
        return String(itemValue).toLowerCase() !== String(value).toLowerCase();
      case 'contains':
        return String(itemValue).toLowerCase().includes(String(value).toLowerCase());
      case 'starts_with':
        return String(itemValue).toLowerCase().startsWith(String(value).toLowerCase());
      case 'greater_than':
        return parseFloat(itemValue) > parseFloat(value);
      case 'less_than':
        return parseFloat(itemValue) < parseFloat(value);
      case 'is_empty':
        return itemValue === undefined || itemValue === null || itemValue === '' || (Array.isArray(itemValue) && itemValue.length === 0) || (typeof itemValue === 'object' && itemValue !== null && !Array.isArray(itemValue) && Object.keys(itemValue).length === 0);
      case 'is_not_empty':
        return itemValue !== undefined && itemValue !== null && itemValue !== '' && !(Array.isArray(itemValue) && itemValue.length === 0) && !(typeof itemValue === 'object' && itemValue !== null && !Array.isArray(itemValue) && Object.keys(itemValue).length === 0);
      case 'in_list': {
        const predefined = typeof PREDEFINED_LISTS !== 'undefined' && PREDEFINED_LISTS[value];
        const listVals = predefined ? predefined.values : String(value).split(',').map(v => v.trim());
        return listVals.includes(String(itemValue));
      }
      case 'not_in_list': {
        const predefined2 = typeof PREDEFINED_LISTS !== 'undefined' && PREDEFINED_LISTS[value];
        const listVals2 = predefined2 ? predefined2.values : String(value).split(',').map(v => v.trim());
        return !listVals2.includes(String(itemValue));
      }
      default:
        return true;
    }
  };

  for (const parentItem of dataArray) {
    if (!parentItem || typeof parentItem !== 'object') {
      continue; // Skip non-objects
    }

    // Get the nested field value using the path
    const nestedValue = getNestedField(parentItem, nestedFieldPath, null);

    // FILTER: Skip devices that don't have this field (undefined, null, or error strings)
    if (nestedValue === null || nestedValue === undefined) {
      continue; // Device doesn't have this field - exclude it
    }

    // Also skip if the value is an error string (common in scan results)
    if (typeof nestedValue === 'string' && (
      nestedValue.toLowerCase().includes('error') ||
      nestedValue.toLowerCase().includes('wmi error') ||
      nestedValue.toLowerCase().includes('unable to') ||
      nestedValue.toLowerCase().includes('failed to')
    )) {
      continue; // Device has an error for this field - exclude it
    }

    if (Array.isArray(nestedValue)) {
      if (nestedValue.length === 0) {
        // Empty array - exclude this device
        continue;
      }
      // EXPAND: create one row for each item in the nested array
      for (const nestedItem of nestedValue) {
        // Apply nested filter if provided
        if (typeof nestedItem === 'object' && nestedItem !== null) {
          if (!itemMatchesNestedFilter(nestedItem)) {
            continue; // Skip items that don't match the filter
          }
        }

        // Create a new row that combines parent fields with nested item fields
        const expandedRow = {
          // First, include important parent fields like IP and Hostname
          ...(parentItem.ip && { ip: parentItem.ip }),
          ...(parentItem.IP && { IP: parentItem.IP }),
          ...(parentItem.hostname && { hostname: parentItem.hostname }),
          ...(parentItem.Hostname && { Hostname: parentItem.Hostname }),
          // Then spread all the nested item fields
          ...(typeof nestedItem === 'object' && nestedItem !== null ? nestedItem : { value: nestedItem }),
          // Add parent reference for accessing parent fields (stored for column access)
          _parent: parentItem,
          _parentIp: parentItem.ip || parentItem.IP,
          _parentHostname: parentItem.hostname || parentItem.Hostname
        };
        expandedRows.push(expandedRow);
      }
    } else if (typeof nestedValue === 'object' && nestedValue !== null) {
      // Object field - include parent fields plus all object properties
      const expandedRow = {
        ...(parentItem.ip && { ip: parentItem.ip }),
        ...(parentItem.IP && { IP: parentItem.IP }),
        ...(parentItem.hostname && { hostname: parentItem.hostname }),
        ...(parentItem.Hostname && { Hostname: parentItem.Hostname }),
        ...nestedValue,
        _parent: parentItem,
        _parentIp: parentItem.ip || parentItem.IP,
        _parentHostname: parentItem.hostname || parentItem.Hostname
      };
      expandedRows.push(expandedRow);
    } else {
      // Primitive field (string, number, boolean) - create a single row with the value
      const fieldName = nestedFieldPath.split('.').pop(); // Get last part of path
      const expandedRow = {
        ...(parentItem.ip && { ip: parentItem.ip }),
        ...(parentItem.IP && { IP: parentItem.IP }),
        ...(parentItem.hostname && { hostname: parentItem.hostname }),
        ...(parentItem.Hostname && { Hostname: parentItem.Hostname }),
        [fieldName]: nestedValue, // Add the primitive value with its field name
        _parent: parentItem,
        _parentIp: parentItem.ip || parentItem.IP,
        _parentHostname: parentItem.hostname || parentItem.Hostname
      };
      expandedRows.push(expandedRow);
    }
  }

  return expandedRows;
};

// Format value for table display
const formatTableValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      if (value.length === 0) return '';
      // Check if array contains arrays (e.g. Antivirus: [["Name", "Path"], ...])
      if (Array.isArray(value[0])) {
        const displayValues = value.slice(0, 5).map(item => {
          // Use first element as label if it's a string or number
          if (item.length > 0 && (typeof item[0] === 'string' || typeof item[0] === 'number')) {
            return String(item[0]);
          }
          return JSON.stringify(item);
        });
        const suffix = value.length > 5 ? `, ... (${value.length} total)` : '';
        return displayValues.join(', ') + suffix;
      }

      // Check if array contains objects
      if (typeof value[0] === 'object' && value[0] !== null) {
        // Array of objects - try to find meaningful fields to display
        const firstItem = value[0];
        const keys = Object.keys(firstItem);

        // Look for common identifying fields
        const nameField = keys.find(k => ['name', 'title', 'label', 'model', 'Model', 'Name', 'Title'].includes(k));
        const statusField = keys.find(k => ['status', 'Status', 'state', 'State'].includes(k));

        if (nameField || statusField) {
          // Display array with meaningful field values
          const displayValues = value.slice(0, 3).map(item => {
            const parts = [];
            if (nameField && item[nameField]) parts.push(String(item[nameField]));
            if (statusField && item[statusField]) parts.push(`(${String(item[statusField])})`);
            return parts.join(' ');
          });
          const suffix = value.length > 3 ? `, ... (${value.length} total)` : '';
          return displayValues.join(', ') + suffix;
        }

        // Fallback: show count with sample keys
        const sampleKeys = keys.slice(0, 2).join(', ');
        return `[${value.length} items: ${sampleKeys}...]`;
      }
      // Array of primitives - join with comma (limit to first 5)
      const displayValues = value.slice(0, 5).map(v => {
        if (v === null || v === undefined) return '';
        if (typeof v === 'object') return JSON.stringify(v);
        return String(v);
      }).filter(v => v !== '');
      const suffix = value.length > 5 ? `, ... (${value.length} total)` : '';
      return displayValues.join(', ') + suffix;
    }
    // Plain object — render human-readably instead of raw JSON.
    // A map of primitives (e.g. open_ports {"80":"http","443":"https"}) reads best as
    // a list of its keys ("80, 443") since the keys are the meaningful values.
    const keys = Object.keys(value);
    if (keys.length === 0) return '';
    const valuesArePrimitive = keys.every(k => {
      const v = value[k];
      return v === null || v === undefined || typeof v !== 'object';
    });
    if (valuesArePrimitive) {
      // If keys look like the meaningful labels (e.g. port numbers), show the keys.
      const keysAreLabels = keys.every(k => /^[\w.\-:]+$/.test(k));
      const shown = keys.slice(0, 12);
      const suffix = keys.length > 12 ? `, +${keys.length - 12} more` : '';
      if (keysAreLabels) return shown.join(', ') + suffix;
      // Otherwise show key: value pairs.
      return shown.map(k => `${k}: ${value[k]}`).join('; ') + suffix;
    }
    try {
      return JSON.stringify(value).slice(0, 160);
    } catch {
      return `{${keys.slice(0, 3).join(', ')}${keys.length > 3 ? '…' : ''}}`;
    }
  }
  return String(value);
};

// Context Menu Component
const ContextMenu = ({
  x, y, blockId, block,
  onEdit, onBringToFront, onSendToBack, onDelete, onClose,
  // Copy Paste props
  onCopy, onPaste, canPaste,
  // Save as Custom Widget
  onSaveAsCustomWidget,
  // Group management props
  onCreateGroup, onAddToGroup, onRemoveFromGroup,
  widgetGroups, blockGroupInfo,
  onAddWidget, // New prop
  availableHeaders = [],
  availableFooters = [],
  onAddToHeader,
  onAddToFooter,
  hasPlaceholder,
  onTogglePlaceholder,
  selectedBlocksCount = 0,
  isContinuationContext = false
}) => {
  const menuRef = useRef(null);
  const [showGroupSubmenu, setShowGroupSubmenu] = useState(false);
  const [showHeaderSubmenu, setShowHeaderSubmenu] = useState(false);
  const [showFooterSubmenu, setShowFooterSubmenu] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  if (!block && !canPaste) return null; // Show if we have a block OR we can paste (assuming we support canvas right click later, but for now context menu is usually triggered on block)

  const blockType = block ? (block.type === 'widget' ? 'Widget' : 'Element') : 'Item';
  const isInGroup = !!blockGroupInfo;
  const availableGroups = Object.entries(widgetGroups || {}).filter(
    ([, group]) => group.leaderId !== blockId && !group.followerIds.includes(blockId)
  );

  const isHeaderFooter = block && block.type === 'element' && (block.elementType === 'header' || block.elementType === 'footer');

  return (
    <div
      ref={menuRef}
      className="context-menu"
      style={{
        position: 'fixed',
        left: `${x}px`,
        top: `${y}px`,
        zIndex: 10000
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {block && (
        <>
          {isHeaderFooter && (
            <>
              <button className="context-menu-item" onClick={onAddWidget}>
                ➕ Add Widget / Element
              </button>
              <div className="context-menu-divider" />
            </>
          )}

          {!isHeaderFooter && availableHeaders.length > 0 && (
            availableHeaders.length === 1 ? (
              <button
                className="context-menu-item"
                onClick={() => {
                  onAddToHeader(blockId, availableHeaders[0].id);
                  onClose();
                }}
              >
                ⬆️ Add to Header
              </button>
            ) : (
              <div
                className="context-menu-item context-menu-submenu"
                onMouseEnter={() => setShowHeaderSubmenu(true)}
                onMouseLeave={() => setShowHeaderSubmenu(false)}
              >
                ⬆️ Add to Header →
                {showHeaderSubmenu && (
                  <div className="context-menu-submenu-items" style={{
                    position: 'absolute',
                    left: '100%',
                    top: 0,
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    minWidth: '150px',
                    zIndex: 10001
                  }}>
                    {availableHeaders.map((h, idx) => (
                      <button
                        key={h.id}
                        className="context-menu-item"
                        onClick={() => {
                          onAddToHeader(blockId, h.id);
                          onClose();
                        }}
                      >
                        Header {idx + 1}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )
          )}

          {!isHeaderFooter && availableFooters.length > 0 && (
            availableFooters.length === 1 ? (
              <button
                className="context-menu-item"
                onClick={() => {
                  onAddToFooter(blockId, availableFooters[0].id);
                  onClose();
                }}
              >
                ⬇️ Add to Footer
              </button>
            ) : (
              <div
                className="context-menu-item context-menu-submenu"
                onMouseEnter={() => setShowFooterSubmenu(true)}
                onMouseLeave={() => setShowFooterSubmenu(false)}
              >
                ⬇️ Add to Footer →
                {showFooterSubmenu && (
                  <div className="context-menu-submenu-items" style={{
                    position: 'absolute',
                    left: '100%',
                    top: 0,
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    minWidth: '150px',
                    zIndex: 10001
                  }}>
                    {availableFooters.map((f, idx) => (
                      <button
                        key={f.id}
                        className="context-menu-item"
                        onClick={() => {
                          onAddToFooter(blockId, f.id);
                          onClose();
                        }}
                      >
                        Footer {idx + 1}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )
          )}

          {(!isHeaderFooter && (availableHeaders.length > 0 || availableFooters.length > 0)) && (
            <div className="context-menu-divider" />
          )}

          {/* Only show Add/Remove Continuation Placeholder if not already in a continuation context */}
          {blockType === 'Widget' && !isContinuationContext && (
            <button className="context-menu-item" onClick={() => {
              if (onTogglePlaceholder) onTogglePlaceholder(blockId);
              onClose();
            }}>
              {hasPlaceholder ? '➖ Remove Continuation Placeholder' : '➕ Add Continuation Placeholder'}
            </button>
          )}

          <button className="context-menu-item" onClick={onEdit}>
            Edit {blockType}
          </button>

          <div className="context-menu-divider" />

          <button className="context-menu-item" onClick={onCopy}>
            📋 Copy Widget
          </button>

          {/* <button className="context-menu-item" onClick={() => {
            if (onSaveAsCustomWidget) onSaveAsCustomWidget(blockId);
          }}>
            💾 {selectedBlocksCount > 1 ? `Save ${selectedBlocksCount} Blocks as Custom Widget` : 'Save as Custom Widget'}
          </button> */}

          <div className="context-menu-divider" />
        </>
      )}

      {/* Paste is available even if not on a block */}
      <button
        className="context-menu-item"
        onClick={onPaste}
        disabled={!canPaste}
        style={{ opacity: canPaste ? 1 : 0.5, cursor: canPaste ? 'pointer' : 'not-allowed' }}
      >
        🖌️ Paste Widget
      </button>

      {block && (
        <>
          <div className="context-menu-divider" />
          <button className="context-menu-item" onClick={onBringToFront}>
            Bring to Front
          </button>
          <button className="context-menu-item" onClick={onSendToBack}>
            Send to Back
          </button>
          <div className="context-menu-divider" />

          {/* Group Management Section */}
          {!isInGroup ? (
            <>
              <button
                className="context-menu-item"
                onClick={() => {
                  const groupName = prompt('Enter group name:', `${block.widgetType || block.elementType || 'Widget'} Group`);
                  if (groupName) {
                    onCreateGroup(blockId, groupName);
                    onClose();
                  }
                }}
              >
                🔗 Create Group (as Leader)
              </button>
              {availableGroups.length > 0 && (
                <div
                  className="context-menu-item context-menu-submenu"
                  onMouseEnter={() => setShowGroupSubmenu(true)}
                  onMouseLeave={() => setShowGroupSubmenu(false)}
                >
                  📎 Add to Group →
                  {showGroupSubmenu && (
                    <div className="context-menu-submenu-items" style={{
                      position: 'absolute',
                      left: '100%',
                      top: 0,
                      backgroundColor: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      minWidth: '150px',
                      zIndex: 10001
                    }}>
                      {availableGroups.map(([groupId, group]) => (
                        <button
                          key={groupId}
                          className="context-menu-item"
                          onClick={() => {
                            onAddToGroup(blockId, groupId);
                            onClose();
                          }}
                        >
                          {group.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <button
              className="context-menu-item"
              onClick={() => {
                onRemoveFromGroup(blockId);
                onClose();
              }}
            >
              🔓 Remove from Group ({blockGroupInfo.groupName})
            </button>
          )}

          {isInGroup && (
            <div className="context-menu-item context-menu-item-info" style={{
              fontSize: '11px',
              color: '#6b7280',
              cursor: 'default',
              padding: '4px 12px'
            }}>
              {blockGroupInfo.role === 'leader' ? '👑 Leader' : '👥 Follower'} of "{blockGroupInfo.groupName}"
            </div>
          )}

          <div className="context-menu-divider" />
          <button className="context-menu-item context-menu-item-danger" onClick={onDelete}>
            Delete
          </button>
        </>
      )}

      {!block && (
        <button
          className="context-menu-item"
          onClick={() => {
            const addBtn = document.querySelector('.toolbar-btn.primary');
            if (addBtn) addBtn.click();
            onClose();
          }}
        >
          ➕ Add Content
        </button>
      )}
    </div>
  );
};

// Internal fields to exclude from display and suggestions (used for drill-down tracking)
const TABLE_INTERNAL_FIELDS = ['_parentIndex', '_parentIp', '_parentHostname', '_index'];

// Helper to recursively add a block to a container (column)
const addBlockToContainer = (blocks, containerId, newBlock) => {
  let found = false;

  const traverse = (nodes) => {
    return nodes.map(node => {
      if (node.id === containerId) {
        // Can add to column OR freeform element container OR Header/Footer
        // Support both old 'section' type and new 'element' type for Headers/Footers
        const isHeaderFooter = (node.type === 'element' || node.type === 'section') &&
          (node.elementType === 'header' || node.elementType === 'footer');

        if (node.type === 'column' || node.type === 'layout-column' ||
          (node.type === 'element' && node.config?.layout?.type === 'freeform') ||
          isHeaderFooter) {
          found = true;

          let blockToInsert;

          if (node.type === 'column' || node.type === 'layout-column') {
            // Modify newBlock to be relative for flex layout in columns
            blockToInsert = {
              ...newBlock,
              position: { x: 0, y: 0 },
              style: {
                ...newBlock.style,
                position: 'relative',
                width: '100%',
                marginBottom: '10px'
              }
            };
          } else {
            // Freeform container (Header/Footer or generic freeform element) - keep absolute positioning
            // For Headers/Footers, we want the item to appear at the top-left of the container by default
            // if it looks like it's inheriting a page-relative position
            const isDefaultPos = newBlock.position?.x === 0 && newBlock.position?.y === 0;

            blockToInsert = {
              ...newBlock,
              position: isHeaderFooter ? { x: 0, y: 0 } : (newBlock.position || { x: 10, y: 10 }),
              style: {
                ...newBlock.style,
                position: 'absolute'
              }
            };
          }

          return {
            ...node,
            children: [...(node.children || []), blockToInsert]
          };
        }
        return node;
      }

      if (node.children && node.children.length > 0) {
        const newChildren = traverse(node.children);
        if (found) {
          return {
            ...node,
            children: newChildren
          };
        }
      }
      return node;
    });
  };

  const newBlocks = traverse(blocks);
  return { updatedBlocks: newBlocks, success: found };
};



export default PipsqueakReportBuilder;

// Helper to resolve the target container ID (Column) from a selected block ID
// Handles selecting Section -> Row -> Column auto-resolution
const resolveTargetContainer = (pages, selectedId) => {
  let targetId = null;

  const find = (nodes) => {
    if (!nodes) return false;
    for (const node of nodes) {
      if (node.id === selectedId) {
        // Direct selection of column
        if (node.type === 'column' || node.type === 'layout-column') {
          targetId = node.id;
          return true;
        }
        // Direct selection of freeform element (Header/Footer)
        else if ((node.type === 'element' && node.config?.layout?.type === 'freeform') ||
          (node.type === 'element' && (node.elementType === 'header' || node.elementType === 'footer'))) {
          targetId = node.id;
          return true;
        }
        // Selection of row -> find first column
        else if (node.type === 'row' && node.children?.length > 0) {
          const firstCol = node.children.find(c => c.type === 'column' || c.type === 'layout-column');
          if (firstCol) {
            targetId = firstCol.id;
            return true;
          }
        }
        // Selection of section -> find first row -> first column
        else if (node.type === 'section' && node.children?.length > 0) {
          const firstRow = node.children.find(r => r.type === 'row');
          if (firstRow && firstRow.children?.length > 0) {
            const firstCol = firstRow.children.find(c => c.type === 'column' || c.type === 'layout-column');
            if (firstCol) {
              targetId = firstCol.id;
              return true;
            }
          }
        }

        // If it's a valid container, set it as targetId anyway (fallback)
        if (node.type === 'column' || node.type === 'layout-column' ||
          (node.type === 'element' && node.config?.layout?.type === 'freeform') ||
          ((node.type === 'element' || node.type === 'section') && (node.elementType === 'header' || node.elementType === 'footer'))) {
          targetId = node.id;
        }

        return true;
      }

      if (node.children) {
        if (find(node.children)) {
          // If we found the selectedId inside this node, and this node is a container,
          // we should target THIS node for the new block if targetId hasn't been set yet
          // by a deeper container.
          if (!targetId && (
            node.type === 'column' || node.type === 'layout-column' ||
            (node.type === 'element' && node.config?.layout?.type === 'freeform') ||
            ((node.type === 'element' || node.type === 'section') && (node.elementType === 'header' || node.elementType === 'footer'))
          )) {
            targetId = node.id;
          }
          return true;
        }
      }
    }
    return false;
  };

  for (const page of pages) {
    if (find(page.blocks)) break;
  }

  return targetId;
};
